import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import {

  AlertTriangle,
  Bell,

  Briefcase,
  Calendar,
  Check,

  ChevronDown,
  ChevronRight,

  Clock3,

  ExternalLink,

  FileText,
  Flag,

  Loader,

  LogOut,

  Mail,

  MapPin,

  MessageSquare,
  Mic,

  Phone,
  PlayCircle,

  Plus,

  Search,

  Share2,

  ShieldCheck,

  Globe,

  Sparkles,
  Square,

  Trash2,

  Upload,

  User,

  Download,

  X,
  Zap,

} from 'lucide-react';

import { downloadXlsxReport, exportPipelineCsv, exportFunnelCsv, exportSourceQualityCsv, exportSourceRoiCsv, exportActivityMixCsv, exportActivityTrendCsv, exportRecruiterLeaderboardCsv } from './services/exportReport';

import { arrayUnion, collection, deleteDoc, doc, getDocs, getDoc, limit, onSnapshot, orderBy, query, runTransaction, serverTimestamp, setDoc, writeBatch } from 'firebase/firestore';

import { deleteUser } from 'firebase/auth';

import { getFunctions, httpsCallable } from 'firebase/functions';

import { getDownloadURL, ref as storageRef, uploadBytes } from 'firebase/storage';

import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

import pdfjsWorker from 'pdfjs-dist/build/pdf.worker.min?url';

import CandidateFullPanel from './CandidateFullPanel';
import ConfirmModal from './components/ConfirmModal';
import AccountPanel from './AccountPanel';
import ContactPanel from './ContactPanel';
import CrmAutomationPanel, { BUILTIN_CADENCES, AUTOMATION_TRIGGERS } from './CrmAutomationPanel';
import ShortlistPortal from './ShortlistPortal';
import VisionDemoLandingPage from './VisionDemoLandingPage';

const PUBLIC_PRIVACY_NOTICE = 'Information submitted through this Vision link is sent to the recruitment agency that shared the link for recruitment, screening, shortlist, and client-vacancy handling purposes.';
const PUBLIC_LAWFUL_USE_NOTICE = 'The agency is responsible for having a lawful basis or consent to process personal information and must not use Vision for unlawful discrimination, spam, scraping abuse, or unauthorized data sharing.';


import { app, appId, auth, db, storage, useAuth } from './contexts/AuthContext';



if (GlobalWorkerOptions && pdfjsWorker) {

  GlobalWorkerOptions.workerSrc = pdfjsWorker;

}

const INITIAL_CANDIDATES = [];

const INITIAL_AGENCIES = [];

const INITIAL_JOBS = [];

function getPublicAppBaseUrl() {
  const configuredUrl = String(import.meta.env?.VITE_PUBLIC_APP_URL || '').trim().replace(/\/+$/, '');
  if (configuredUrl && !/\/\/vision-grid\.(web\.app|firebaseapp\.com)$/i.test(configuredUrl)) return configuredUrl;
  return 'https://vision-team.xyz';
}

function getPublicAppHost() {
  return getPublicAppBaseUrl().replace(/^https?:\/\//i, '');
}

function cleanJobBoardText(value, fallback = '') {
  return String(value || fallback || '').replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
}

function RequirementsContent({ value, emptyText = 'No requirements have been captured for this role yet.' }) {
  const text = cleanJobBoardText(value, '');
  if (!text) return <div className="text-sm leading-6 text-slate-400">{emptyText}</div>;

  const lines = text.split('\n').map((line) => line.trim());
  const hasMultipleContentLines = lines.filter(Boolean).length > 1;

  return (
    <div className="requirements-content text-sm leading-6 text-slate-200">
      {lines.map((line, index) => {
        if (!line) return <div key={`spacer-${index}`} className="h-3" aria-hidden="true" />;

        const bulletMatch = line.match(/^[-*•▪◦]\s+(.*)$/);
        const content = bulletMatch ? bulletMatch[1] : line;
        const isHeading = content.length <= 48
          && (/^[A-Z][A-Z\s&/+-]*$/.test(content) || /^(education|experience|skills and capabilities)$/i.test(content));
        const labelMatch = content.match(/^([^:]{2,40}):\s*(.+)$/);
        const isListItem = Boolean(bulletMatch) || (hasMultipleContentLines && !isHeading);

        if (isHeading) {
          return (
            <div key={`heading-${index}`} className="mt-2 font-semibold text-slate-100 first:mt-0">
              {content}
            </div>
          );
        }

        return (
          <div key={`line-${index}`} className={isListItem ? 'relative pl-5' : undefined}>
            {isListItem && <span className="absolute left-1.5 top-[0.72em] h-1.5 w-1.5 rounded-full bg-slate-400" aria-hidden="true" />}
            {labelMatch ? (
              <>
                <strong className="font-bold text-slate-100">{labelMatch[1]}:</strong> {labelMatch[2]}
              </>
            ) : content}
          </div>
        );
      })}
    </div>
  );
}

function buildJobBoardExport(job = {}, urls = {}, agencyName = '') {
  const title = cleanJobBoardText(job.title, 'Untitled role');
  const company = cleanJobBoardText(getJobCompanyName(job) || job.companyName || job.company || agencyName, 'Confidential client');
  const location = cleanJobBoardText(job.location, 'Remote');
  const jobType = cleanJobBoardText(job.type || job.jobType || job.employmentType, 'Permanent');
  const salary = cleanJobBoardText(job.salary, 'Compensation on request');
  const summary = cleanJobBoardText(job.summary || job.description, `${title} opportunity at ${company}.`);
  const requirements = cleanJobBoardText(job.requirements, '');
  const applyUrl = cleanJobBoardText(urls.jobUrl, '');
  const careersUrl = cleanJobBoardText(urls.careersPageUrl, '');
  const fullAdvert = [
    title,
    '',
    `Company: ${company}`,
    `Location: ${location}`,
    `Job type: ${jobType}`,
    `Compensation: ${salary}`,
    '',
    'Job description',
    summary,
    requirements ? ['', 'Requirements', requirements] : [],
    '',
    'Apply here',
    applyUrl,
  ].flat().join('\n');

  return {
    title,
    company,
    location,
    jobType,
    salary,
    applyUrl,
    careersUrl,
    summary,
    requirements,
    fullAdvert,
  };
}



const PIPELINE_STAGES = [

  { id: 'applied', label: 'New Applied', color: 'bg-blue-500' },

  { id: 'screening', label: 'Screening', color: 'bg-violet-500' },

  { id: 'interview', label: 'Interview', color: 'bg-yellow-500' },

  { id: 'offer', label: 'Offer Sent', color: 'bg-green-500' },

  { id: 'hired', label: 'Hired', color: 'bg-emerald-500' },

  { id: 'rejected', label: 'Not Selected', color: 'bg-rose-500' },

];



const OFFER_DECLINE_CATEGORY_LABELS = {
  compensation:     'Compensation too low',
  competing_offer:  'Accepted competing offer',
  role_fit:         'Role / culture fit',
  location:         'Location / commute',
  timeline:         'Start date / notice',
  personal:         'Personal reasons',
  other:            'Other',
};

const PIPELINE_REJECT_REASON_LABELS = {
  experience_gap:      'Needed more relevant experience',
  missing_skill:       'Missing required skill',
  industry_background: 'Different industry background needed',
  cv_clarity:          'CV did not show the requirement clearly',
  salary_mismatch:     'Salary expectations',
  location:            'Location / availability mismatch',
  stronger_matches:    'Client chose stronger matches',
  role_filled:         'Role filled or closed',
  candidate_withdrew:  'Candidate withdrew',
  no_response:         'No response from candidate',
  other:               'Other recruiter reason',
};

const PIPELINE_REJECT_EMAIL_TEMPLATES = {
  experience_gap:      (name, role) => `Hi ${name},\n\nThank you for applying for the ${role} role.\n\nAfter recruiter review, we will not be moving forward at this stage because this role needs more directly relevant experience in the areas the client is prioritising.\n\nWe appreciate the time you took to apply and encourage you to apply again if a closer-fit role opens up.\n\nBest regards`,
  missing_skill:       (name, role) => `Hi ${name},\n\nThank you for applying for the ${role} role.\n\nAfter recruiter review, we will not be moving forward at this stage because the role requires a specific skill or capability that was not clear enough in your application.\n\nWe appreciate your time and wish you success with your job search.\n\nBest regards`,
  industry_background: (name, role) => `Hi ${name},\n\nThank you for your interest in the ${role} opportunity.\n\nAfter recruiter review, the client is prioritising candidates with a different industry background for this specific role, so we will not be moving forward at this stage.\n\nWe appreciate your time and will keep your profile in mind for better-aligned opportunities.\n\nBest regards`,
  cv_clarity:          (name, role) => `Hi ${name},\n\nThank you for applying for the ${role} role.\n\nAfter recruiter review, we could not clearly confirm the required experience from your CV for this specific position. For future applications, it may help to make the most relevant duties, tools, and results easier to see.\n\nWe appreciate your time and wish you every success.\n\nBest regards`,
  salary_mismatch:     (name, role) => `Hi ${name},\n\nThank you for your application for the ${role} position.\n\nAfter recruiter review, we are unable to proceed because the salary expectations do not align with the current range for this role.\n\nWe appreciate your time and wish you all the best in your search.\n\nBest regards`,
  location:            (name, role) => `Hi ${name},\n\nThank you for your interest in the ${role} role.\n\nAfter recruiter review, we are unable to progress your application at this stage because the location, working arrangement, or availability requirements do not align with the role.\n\nWe appreciate your time and wish you success.\n\nBest regards`,
  stronger_matches:    (name, role) => `Hi ${name},\n\nThank you for applying for the ${role} role.\n\nAfter recruiter review, we will not be moving forward at this stage because the client selected candidates whose experience more closely matched the current requirements.\n\nWe appreciate your time and encourage you to apply again if a more suitable role opens up.\n\nBest regards`,
  role_filled:         (name, role) => `Hi ${name},\n\nThank you for your interest in the ${role} opportunity.\n\nWe wanted to let you know that this position has now been filled or closed, so we will not be progressing additional applications for this role.\n\nWe appreciate your time and will keep your details on file for future opportunities.\n\nBest regards`,
  candidate_withdrew:  (name, role) => `Hi ${name},\n\nThank you for letting us know about your application for the ${role} role.\n\nWe have updated our records to show that you are no longer proceeding with this opportunity.\n\nWe wish you all the best with your next steps.\n\nBest regards`,
  no_response:         (name, role) => `Hi ${name},\n\nWe attempted to reach you regarding the ${role} opportunity but were unable to connect.\n\nAfter recruiter review, we have had to move forward with other candidates at this stage. Please feel free to reach out if you would like to be considered for future roles.\n\nBest regards`,
  other:               (name, role) => `Hi ${name},\n\nThank you for your interest in the ${role} opportunity.\n\nAfter recruiter review, we have decided not to move forward with your application for this specific role at this stage.\n\nWe appreciate your time and wish you every success.\n\nBest regards`,
};

const SCREENING_WINDOW_MINUTES = 15;

const SCREENING_QUESTIONS = [

  'Why are you a strong fit for this role and company?',

  'Which recent project best shows your ability to perform in this job?',

  'What is your current notice period and when can you start?',

  'What compensation range are you targeting for this opportunity?',

];



function getPipelineStageMeta(stageId) {

  return PIPELINE_STAGES.find((stage) => stage.id === String(stageId || '').toLowerCase()) || PIPELINE_STAGES[0];

}



function getCandidatePipelineWindow(candidate = {}) {

  const stageMeta = getPipelineStageMeta(candidate?.status || 'applied');

  const hasScreeningSubmission = Boolean(candidate?.screeningSubmittedAt || candidate?.screeningCompletedAt || candidate?.screeningResponseId);

  const windows = {

    applied: {

      windowLabel: 'initial CV review window',

      detail: 'The recruitment team is checking fit against the vacancy brief before shortlisting the next group of candidates.',

    },

    screening: hasScreeningSubmission ? {

      windowLabel: 'screening review window',

      detail: 'Your screening responses are being reviewed so the recruiter can decide on the next shortlist move.',

    } : {

      windowLabel: 'screening completion window',

      detail: 'Your CV has progressed into screening, and the recruiter is waiting to complete that review window before the next decision.',

    },

    interview: {

      windowLabel: 'interview review window',

      detail: 'Your profile is in the interview review window while the team coordinates or reviews interview decisions.',

    },

    offer: {

      windowLabel: 'offer decision window',

      detail: 'Your application is in the final offer decision window while the recruiter aligns on the closing steps.',

    },

    hired: {

      windowLabel: 'placement confirmation window',

      detail: 'Your application is in the placement confirmation window while the final hiring details are being closed out.',

    },

  };

  const selected = windows[stageMeta.id] || windows.applied;

  return {

    stageId: stageMeta.id,

    stageLabel: stageMeta.label,

    windowLabel: selected.windowLabel,

    detail: selected.detail,

  };

}



function normalizeScreeningQuestions(questions) {

  const source = Array.isArray(questions) ? questions : [];

  return SCREENING_QUESTIONS.map((defaultQuestion, index) => {

    const customQuestion = String(source[index] || '').trim();

    return customQuestion || defaultQuestion;

  });

}



function buildScreeningAnswerDraft(questions, answers) {

  const normalizedQuestions = normalizeScreeningQuestions(questions);

  const sourceAnswers = Array.isArray(answers) ? answers : [];

  return normalizedQuestions.map((_question, index) => String(sourceAnswers[index] || ''));

}



function buildAiScreeningSuggestions(candidate) {

  const role = String(candidate?.role || 'this role').trim();

  const company = String(candidate?.company || 'the company').trim();

  const location = String(candidate?.location || '').trim();

  const roleSignals = ROLE_KEYWORDS[role] || [];

  const toolFocus = roleSignals.slice(0, 2).join(' and ');

  return [

    `Which recent result best shows your ability to succeed as a ${role} with ${company}, and what measurable outcome did you own?`,

    toolFocus

      ? `What hands-on experience do you have with ${toolFocus}, and how have you used it to deliver business impact?`

      : `Which tools, systems, or methods do you rely on most in your work, and what outcomes have they helped you drive?`,
    `What would you need clarified early to feel confident moving forward with ${company}, and how does that affect your interest level?`,

    location

      ? `How does ${location} affect your availability, work setup, or relocation plans for this opportunity?`

      : 'What work setup, timezone, or location constraints should the recruiter know before moving you to the next stage?',

  ];

}


const DEFAULT_RECRUITER_PROFILE = {

  name: '',

  location: '',

  agency: '',

  title: 'Recruiter',

  phone: '',

  agencySecret: '',

  agencyKey: '',

  accountRole: 'agent',

  seatAssigned: false,

};

function buildDemoWorkspaceSeed(seatCount = 5) {
  const now = Date.now();
  const isoDaysAgo = (days) => new Date(now - days * 86400000).toISOString();
  const seats = Math.min(20, Math.max(1, Number(seatCount || 5)));
  const members = ['Aisha Patel', 'Daniel Meyer', 'Mia Jacobs', 'Noah Williams', 'Priya Naidoo', 'Liam Carter', 'Zoe Smith', 'Ethan Brown']
    .slice(0, seats)
    .map((name, index) => ({
      id: `demo-seat-${index + 1}`,
      uid: `demo-seat-${index + 1}`,
      name,
      email: `${name.toLowerCase().replace(/\s+/g, '.')}@demo.vision`,
      role: index === 0 ? 'owner' : 'agent',
      seatAssigned: true,
      seatStatus: 'active',
    }));
  const job = {
    id: 'demo-role-1',
    title: 'Senior Java Developer',
    companyName: 'Northstar Fintech',
    department: 'Engineering',
    type: 'Permanent',
    status: 'Active',
    location: 'Johannesburg / Hybrid',
    salary: 'ZAR 950k - 1.2m',
    recruiter: members[0]?.name || 'Demo Recruiter',
    clientContact: 'Lerato Mokoena',
    relationshipStatus: 'Active client',
    lastOutreach: isoDaysAgo(1),
    summary: 'Client needs a senior backend engineer for a payments modernization project.',
    requirements: 'Java, Spring Boot, APIs, cloud deployment, financial services exposure.',
    stage: 'active',
    createdAtIso: isoDaysAgo(8),
    updatedAtIso: isoDaysAgo(1),
  };
  const candidateNames = ['Jordan Blake', 'Thabo Mokoena', 'Emily Chen', 'Riley Adams', 'Nadia Khan', 'Michael Dlamini', 'Sofia Martins', 'Arjun Singh', 'Grace Taylor', 'Neo Maseko', 'Hannah Wilson', 'Leo van Zyl'];
  const statuses = ['applied', 'applied', 'screening', 'screening', 'interview', 'interview', 'offer', 'hired', 'screening', 'applied', 'interview', 'offer'];
  const candidates = candidateNames.map((name, index) => {
    const status = statuses[index] || 'applied';
    const owner = members[index % Math.max(members.length, 1)] || members[0] || {};
    const createdAtIso = isoDaysAgo(7 - Math.min(index, 6));
    return {
      id: `demo-candidate-${index + 1}`,
      recruiterId: owner.id || '',
      ownerRecruiterId: owner.id || '',
      ownerRecruiterName: owner.name || 'Demo Recruiter',
      name,
      email: `${name.toLowerCase().replace(/\s+/g, '.')}@example.com`,
      phone: `+27 82 000 ${String(1000 + index).slice(-4)}`,
      role: job.title,
      matchedJobId: job.id,
      matchedJobTitle: job.title,
      company: job.companyName,
      location: index % 2 ? 'Cape Town' : 'Johannesburg',
      skills: ['Java', 'Spring Boot', 'REST APIs', index % 2 ? 'AWS' : 'Azure'],
      status,
      rating: 92 - index * 3,
      yearsExperience: 5 + (index % 6),
      source: ['LinkedIn', 'CV upload', 'Referral', 'Talent pool'][index % 4],
      submittedAt: createdAtIso,
      createdAtIso,
      lastActivityAt: isoDaysAgo(Math.max(0, 6 - index)),
      screeningLinkId: `demo-screening-${index + 1}`,
      stageHistory: PIPELINE_STAGES.slice(0, Math.max(1, PIPELINE_STAGES.findIndex((stage) => stage.id === status) + 1)).map((stage, stageIndex) => ({
        stageId: stage.id,
        stageLabel: stage.label,
        movedAt: isoDaysAgo(7 - stageIndex),
        source: 'demo_workspace',
      })),
      crmTimeline: [
        { id: `demo-event-${index}`, type: 'intake_accepted', label: `${name} added to ${job.title}.`, createdAtIso },
      ],
    };
  });
  const screeningResponses = candidates
    .filter((candidate) => ['screening', 'interview', 'offer', 'hired'].includes(candidate.status))
    .reduce((acc, candidate, index) => {
      acc[candidate.id] = {
        id: candidate.id,
        candidateId: candidate.id,
        candidateName: candidate.name,
        role: candidate.role,
        company: candidate.company,
        submittedAt: isoDaysAgo(Math.max(0, 4 - index)),
        answers: [
          'Led backend delivery on a production system with measurable uptime improvements.',
          'Hands-on with Java, Spring Boot, API design, and cloud deployment.',
          'Available for a first interview this week.',
          'Hybrid work in Johannesburg is workable.',
        ],
      };
      return acc;
    }, {});
  const submissions = candidates
    .filter((candidate) => ['interview', 'offer', 'hired'].includes(candidate.status))
    .map((candidate, index) => ({
      id: `demo-submission-${index + 1}`,
      candidateId: candidate.id,
      candidateName: candidate.name,
      jobId: job.id,
      jobTitle: job.title,
      clientName: job.companyName,
      submittedBy: candidate.ownerRecruiterName,
      submittedAt: isoDaysAgo(Math.max(0, 3 - index)),
      submittedAtIso: isoDaysAgo(Math.max(0, 3 - index)),
      outcome: index === 0 ? 'interview_requested' : 'pending',
    }));
  return {
    profile: {
      ...DEFAULT_RECRUITER_PROFILE,
      name: 'Demo Agency Owner',
      email: 'demo@vision.local',
      agency: 'Vision Demo Agency',
      agencyKey: 'vision-demo-agency',
      location: 'Johannesburg, South Africa',
      title: 'Agency Owner',
      accountRole: 'owner',
      seatAssigned: true,
      lawfulUseAccepted: true,
    },
    agencyDetails: {
      agencyKey: 'vision-demo-agency',
      agencyName: 'Vision Demo Agency',
      ownerUid: '',
      seatLimit: seats,
      activeSeatCount: seats,
      membershipRequired: false,
      subscriptionStatus: 'demo',
      status: 'active',
      compliance: { lawfulUseAccepted: true },
    },
    agencyMembers: members,
    jobs: [job],
    candidates,
    screeningResponses,
    submissions,
    companies: [{ id: 'northstar-fintech', name: job.companyName, relationshipStatus: 'Active client', openRoles: 1, lastInteractionAtIso: isoDaysAgo(1) }],
    contacts: [{ id: 'northstar-fintech-lerato', companyId: 'northstar-fintech', companyName: job.companyName, name: 'Lerato Mokoena', role: 'Engineering Director', email: 'lerato@example.com', sentiment: 'Active' }],
    clientOpportunities: [{ id: 'demo-opportunity-1', companyName: job.companyName, title: job.title, stage: 'Closed Won', weightedValue: 180000, jobId: job.id, createdAtIso: isoDaysAgo(10) }],
    placements: candidates.filter((candidate) => candidate.status === 'hired').map((candidate) => ({ id: 'demo-placement-1', candidateId: candidate.id, candidateName: candidate.name, jobId: job.id, jobTitle: job.title, companyName: job.companyName, feeAmount: 165000, status: 'placed', placedAtIso: isoDaysAgo(1) })),
    activityLog: [
      { id: 'demo-activity-1', type: 'job_created', message: `${job.title} role intake completed.`, createdAtIso: isoDaysAgo(7) },
      { id: 'demo-activity-2', type: 'cv_intake_batch', message: '12 CVs processed and matched to the live role.', createdAtIso: isoDaysAgo(5) },
      { id: 'demo-activity-3', type: 'screening_submitted', message: 'Screening responses received for shortlist review.', createdAtIso: isoDaysAgo(3) },
      { id: 'demo-activity-4', type: 'client_submission', message: 'Client-ready shortlist submitted to Northstar Fintech.', createdAtIso: isoDaysAgo(1) },
    ],
  };
}

const DEFAULT_AGENCY_BRAND = {
  logoUrl: '',
  heroImageUrl: '',
  aboutImageUrl: '',
  teamImageUrl: '',
  brandColor: '#6366f1',
  portalTheme: 'midnight',
  portalHeadline: '',
  tagline: '',
  agencyAbout: '',
  candidatePromise: '',
  hiringProcess: '',
  ownedAudienceEnabled: true,
  talentHubHeadline: '',
  talentHubIntro: '',
  contactEmail: '',
  linkedInUrl: '',
  agencyWebsite: '',
  agencySlug: '',
};

const DEFAULT_SEQUENCE_CONTROLS = {

  hourlyEmailCap: 8,

  dailyEmailCap: 40,

  minDomainCooldownMinutes: 5,

  whatsappTemplateName: '',

  whatsappTemplateLanguage: 'en_US',

};



const SOLO_RECRUITER_PRICE_USD = 25;
const PRICE_PER_USER_USD = 60;
const AVAILABLE_SEAT_OPTIONS = Array.from({ length: 25 }, (_, idx) => idx + 1);

const MEMBERSHIP_PLANS = AVAILABLE_SEAT_OPTIONS.map((seats) => ({

  seats,

  monthlyPrice: seats === 1
    ? `USD $${SOLO_RECRUITER_PRICE_USD.toFixed(2)} / month`
    : `USD $${(seats * PRICE_PER_USER_USD).toFixed(2)} / month`,

  originalPrice: '',

  title: seats === 1 ? 'Solo Recruiter Membership' : `${seats} Users Membership`,

  summary: seats === 1
    ? `Solo recruiter launch pricing at USD $${SOLO_RECRUITER_PRICE_USD.toFixed(2)} per month.`
    : `Flat pricing at USD $${PRICE_PER_USER_USD.toFixed(2)} per user per month.`,

}));



const DEFAULT_JOB_FORM = {

  title: '',

  companyName: '',

  department: '',

  type: 'Full-time',

  status: 'Active',

  location: '',

  salary: '',

  recruiter: '',

  clientContact: '',

  relationshipStatus: 'Active client',

  lastOutreach: '',

  summary: '',

  requirements: '',

  booleanQuery: '',

  customScreeningQuestions: [],

  ownerName: '',
  lifecycleStage: 'brief',
  targetFillDate: '',
  fee: '',
  isBlocked: false,
  blockedReason: '',
  publishStatus: 'Live',
  applicationRoutingMode: 'pipeline',
  applicationRoutingOwner: '',
  showSalaryPublic: true,
  showClientNamePublic: true,
  trackingCampaign: '',

};



const DEFAULT_SOURCE_LEAD_FORM = {

  linkedInUrl: '',

  name: '',

  headline: '',

  location: '',

  notes: '',

};



const DEFAULT_CLIENT_JOB_FORM = {

  title: '',

  companyName: '',

  location: '',

  clientContact: '',

  summary: '',

  requirements: '',

  type: 'Full-time',

};



const DEFAULT_CANDIDATE_APPLICATION_FORM = {

  name: '',

  email: '',

  phone: '',

  location: '',

};



const DEFAULT_SCRAPED_JOB_SEARCH = {

  niche: '',

  location: '',

};

const DEFAULT_CLIENT_OPPORTUNITY_FORM = {
  companyName: '',
  contactName: '',
  contactEmail: '',
  title: '',
  stage: 'Lead',
  fee: '',
  jobType: 'Permanent',
  location: '',
  salary: '',
  billingTerms: '',
  complianceNeeds: '',
  requirements: '',
  sourceEmail: '',
};

const CLIENT_OPPORTUNITY_STAGES = ['Lead', 'Qualified', 'Discovery', 'Proposal', 'Verbal Commit', 'Closed-Won', 'Closed-Lost'];
const LEGACY_CLIENT_OPPORTUNITY_STAGE_MAP = { Opportunity: 'Qualified' };

const DEFAULT_PLACEMENT_DRAFT = {
  directFee: '',
  offerSalary: '',
  feePercent: '',
  startDate: '',
  usePercent: false,
  marginPercent: '',
  commissionSplit: '',
  invoiceNumber: '',
  invoiceDate: '',
  invoiceDueDays: '30',
  invoiceStatus: 'pending',
  paymentStatus: 'unpaid',
  billedBy: '',
  currency: 'ZAR',
  taxPercent: '',
};

const PLACEMENT_CURRENCIES = [
  { code: 'ZAR', symbol: 'R', label: 'South African Rand' },
  { code: 'USD', symbol: '$', label: 'US Dollar' },
  { code: 'GBP', symbol: '£', label: 'British Pound' },
  { code: 'EUR', symbol: '€', label: 'Euro' },
  { code: 'AUD', symbol: 'A$', label: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', label: 'Canadian Dollar' },
  { code: 'AED', symbol: 'د.إ', label: 'UAE Dirham' },
];

const WORKSPACE_PAGE_CONFIG = {
  pipeline: {
    path: '/workspace/pipeline',
    label: 'Pipeline',
    title: 'Pipeline',
    description: 'Track candidates, stages, screening, and recruiter activity.',
  },
  jobs: {
    path: '/workspace/jobs',
    label: 'Jobs',
    title: 'Jobs Portfolio',
    description: 'Create job cards, publish roles, source prospects, and manage client vacancies.',
  },
  revenue: {
    path: '/workspace/revenue',
    label: 'Revenue',
    title: 'Revenue & Finance',
    description: 'Placement fees, margins, commission splits, invoice and payment status, revenue by client, contact, and recruiter.',
  },
  agency: {
    path: '/workspace/agency',
    label: 'Agency',
    title: 'Agency Settings',
    description: 'Manage profile, brand, launch checks, reporting, team, support, and governance.',
  },
  automations: {
    path: '/workspace/automations',
    label: 'Automate',
    title: 'Automation Engine',
    description: 'Configure reminders, follow-ups, stale-role handling, and recruiter workflow automation.',
  },
};

const WORKSPACE_PATH_TO_TAB = Object.fromEntries(Object.entries(WORKSPACE_PAGE_CONFIG).map(([tab, config]) => [config.path, tab]));

const RBAC_ROLE_PRESETS = {
  owner: {
    label: 'Owner',
    description: 'Full agency control, billing, roles, data, exports, and governance.',
    permissions: ['*'],
  },
  admin: {
    label: 'Admin',
    description: 'Runs workspace operations and team settings without ownership takeover.',
    permissions: ['pipeline.read', 'pipeline.write', 'pipeline.delete', 'pipeline.export', 'jobs.read', 'jobs.write', 'jobs.delete', 'jobs.publish', 'clients.read', 'clients.write', 'clients.delete', 'deals.read', 'deals.write', 'revenue.read', 'revenue.write', 'revenue.export', 'settings.read', 'settings.write', 'team.read', 'team.editRoles', 'audit.read', 'automation.read', 'automation.write', 'integrations.manage'],
  },
  recruiter: {
    label: 'Recruiter',
    description: 'Owns candidates, job workflow, screening, submissions, and outreach.',
    permissions: ['pipeline.read', 'pipeline.write', 'jobs.read', 'jobs.write', 'clients.read', 'deals.read', 'settings.read', 'automation.read'],
  },
  sales_manager: {
    label: 'Sales / Account Manager',
    description: 'Owns companies, contacts, opportunities, account activity, and client queue.',
    permissions: ['jobs.read', 'clients.read', 'clients.write', 'deals.read', 'deals.write', 'pipeline.read', 'settings.read', 'automation.read', 'automation.write'],
  },
  client_reviewer: {
    label: 'Client Reviewer',
    description: 'Limited client-side review of submitted candidates and shortlists.',
    permissions: ['pipeline.read', 'clients.read'],
  },
  finance: {
    label: 'Finance / Bookkeeper',
    description: 'Placement revenue, invoices, taxes, payment status, and exports only.',
    permissions: ['revenue.read', 'revenue.write', 'revenue.export', 'settings.read', 'audit.read'],
  },
  limited: {
    label: 'Limited Access',
    description: 'Restricted workspace access configured per member.',
    permissions: ['pipeline.read', 'jobs.read'],
  },
};

const RBAC_PAGE_PERMISSIONS = {
  pipeline: 'pipeline.read',
  jobs: 'jobs.read',
  revenue: 'revenue.read',
  agency: 'settings.read',
  automations: 'automation.read',
};

const RBAC_ACTION_LABELS = {
  'pipeline.delete': 'Delete candidates',
  'pipeline.export': 'Export candidate data',
  'jobs.delete': 'Delete jobs',
  'jobs.publish': 'Publish job boards',
  'clients.delete': 'Delete clients',
  'revenue.write': 'Edit placements and invoices',
  'revenue.export': 'Export revenue',
  'team.editRoles': 'Edit roles',
  'billing.write': 'Billing changes',
  'integrations.manage': 'Manage integrations',
};

const FLAGSHIP_OPERATING_LIMITS = {
  starter: { label: 'Starter desk', candidates: 2500, jobs: 120, clients: 250, contacts: 750 },
  launch: { label: 'Small agency', candidates: 20000, jobs: 1000, clients: 2000, contacts: 8000 },
  scale: { label: 'Agency scale', candidates: 50000, jobs: 2500, clients: 5000, contacts: 20000 },
  flagship: { label: 'Flagship agency', candidates: 250000, jobs: 10000, clients: 25000, contacts: 100000 },
};

function fmtLimit(n) {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${+(n / 1_000).toFixed(1)}K`;
  return String(n);
}

const DEFAULT_GOVERNANCE_CONTROLS = {
  operatingTier: 'flagship',
  maxCandidates: '250000',
  maxJobs: '10000',
  maxClients: '25000',
  maxContacts: '100000',
  requireCandidateOwner: true,
  requireJobOwner: true,
  requireClientOwner: true,
  staleClientDays: '30',
  maxActiveJobsPerRecruiter: '35',
  maxActiveCandidatesPerRecruiter: '750',
  minOwnerCoveragePct: '95',
};

const MIGRATION_SOURCE_PRESETS = {
  bullhorn: {
    label: 'Bullhorn',
    accent: 'cyan',
    entities: ['candidates', 'jobs', 'companies', 'contacts', 'placements', 'activities'],
    stageMap: {
      submitted: 'submitted',
      client_submission: 'submitted',
      client_submittal: 'submitted',
      interview: 'interview',
      offer: 'offer',
      placement: 'hired',
      placed: 'hired',
      rejected: 'rejected',
    },
    fieldAliases: {
      candidateName: ['candidate_name', 'name', 'full_name', 'first_and_last_name'],
      email: ['email', 'email_address', 'candidate_email'],
      phone: ['phone', 'mobile', 'mobile_phone'],
      role: ['job_title', 'occupation', 'title', 'desired_title'],
      company: ['clientcorporation', 'client_corporation', 'company', 'company_name'],
      stage: ['status', 'candidate_status', 'submission_status', 'stage'],
      jobTitle: ['job_order_title', 'job_title', 'title', 'position'],
      contactName: ['client_contact', 'contact_name', 'hiring_manager'],
      contactEmail: ['client_contact_email', 'contact_email', 'hiring_manager_email'],
      fee: ['fee', 'placement_fee', 'bill_rate', 'gross_margin'],
      legacyId: ['id', 'bullhorn_id', 'candidate_id', 'job_order_id'],
    },
  },
  recruitly: {
    label: 'Recruitly',
    accent: 'emerald',
    entities: ['candidates', 'jobs', 'companies', 'contacts', 'deals', 'placements'],
    stageMap: {
      lead: 'lead',
      qualified: 'screening',
      cv_sent: 'submitted',
      submitted: 'submitted',
      interview: 'interview',
      offered: 'offer',
      placed: 'hired',
    },
    fieldAliases: {
      candidateName: ['candidate', 'candidate_name', 'name', 'full_name'],
      email: ['email', 'candidate_email'],
      phone: ['phone', 'mobile'],
      role: ['role', 'job_title', 'position'],
      company: ['company', 'organisation', 'organization', 'client'],
      stage: ['stage', 'status', 'pipeline_stage'],
      jobTitle: ['vacancy', 'job', 'job_title', 'role'],
      contactName: ['company_contact', 'contact', 'contact_name'],
      contactEmail: ['contact_email', 'company_contact_email'],
      fee: ['fee', 'placement_fee', 'revenue'],
      legacyId: ['id', 'recruitly_id', 'record_id'],
    },
  },
  manatal: {
    label: 'Manatal',
    accent: 'violet',
    entities: ['candidates', 'jobs', 'companies', 'contacts'],
    stageMap: {
      applied: 'lead',
      sourced: 'lead',
      screening: 'screening',
      interview: 'interview',
      offer: 'offer',
      hired: 'hired',
      rejected: 'rejected',
    },
    fieldAliases: {
      candidateName: ['candidate', 'candidate_name', 'full_name', 'name'],
      email: ['email', 'email_address'],
      phone: ['phone', 'mobile'],
      role: ['position', 'job', 'job_title', 'title'],
      company: ['organization', 'organisation', 'company', 'client'],
      stage: ['stage', 'status', 'application_status'],
      jobTitle: ['job', 'job_title', 'position'],
      contactName: ['contact', 'hiring_manager', 'contact_name'],
      contactEmail: ['contact_email', 'hiring_manager_email'],
      fee: ['fee', 'revenue'],
      legacyId: ['id', 'manatal_id'],
    },
  },
  breezy: {
    label: 'Breezy HR',
    accent: 'sky',
    entities: ['candidates', 'jobs', 'companies', 'activities'],
    stageMap: {
      applied: 'lead',
      phone_screen: 'screening',
      interview: 'interview',
      assessment: 'screening',
      offer: 'offer',
      hired: 'hired',
      disqualified: 'rejected',
    },
    fieldAliases: {
      candidateName: ['candidate_name', 'name', 'full_name'],
      email: ['email', 'candidate_email'],
      phone: ['phone', 'mobile'],
      role: ['position', 'job_title', 'job'],
      company: ['company', 'department', 'client'],
      stage: ['stage', 'status'],
      jobTitle: ['position', 'job', 'job_title'],
      contactName: ['hiring_manager', 'contact_name'],
      contactEmail: ['hiring_manager_email', 'contact_email'],
      fee: ['fee'],
      legacyId: ['id', 'breezy_id'],
    },
  },
  greenhouse: {
    label: 'Greenhouse',
    accent: 'lime',
    entities: ['candidates', 'jobs', 'companies', 'activities'],
    stageMap: {
      application_review: 'screening',
      recruiter_screen: 'screening',
      hiring_manager_review: 'submitted',
      interview: 'interview',
      offer: 'offer',
      hired: 'hired',
      rejected: 'rejected',
    },
    fieldAliases: {
      candidateName: ['candidate_name', 'name', 'full_name'],
      email: ['email', 'email_addresses'],
      phone: ['phone', 'phone_numbers'],
      role: ['job', 'job_title', 'opening'],
      company: ['company', 'office', 'department'],
      stage: ['stage', 'current_stage', 'application_status'],
      jobTitle: ['job', 'job_title', 'opening'],
      contactName: ['coordinator', 'recruiter', 'hiring_manager'],
      contactEmail: ['coordinator_email', 'recruiter_email'],
      fee: ['fee'],
      legacyId: ['id', 'greenhouse_id', 'candidate_id'],
    },
  },
  workable: {
    label: 'Workable',
    accent: 'amber',
    entities: ['candidates', 'jobs', 'companies', 'activities'],
    stageMap: {
      sourced: 'lead',
      applied: 'lead',
      phone_screen: 'screening',
      interview: 'interview',
      offer: 'offer',
      hired: 'hired',
      disqualified: 'rejected',
    },
    fieldAliases: {
      candidateName: ['candidate', 'candidate_name', 'name', 'full_name'],
      email: ['email', 'candidate_email'],
      phone: ['phone', 'mobile'],
      role: ['job', 'job_title', 'position'],
      company: ['company', 'client', 'department'],
      stage: ['stage', 'status'],
      jobTitle: ['job', 'job_title', 'position'],
      contactName: ['hiring_manager', 'contact_name'],
      contactEmail: ['hiring_manager_email', 'contact_email'],
      fee: ['fee'],
      legacyId: ['id', 'workable_id'],
    },
  },
  generic: {
    label: 'Generic CSV',
    accent: 'slate',
    entities: ['candidates', 'jobs', 'companies', 'contacts', 'placements'],
    stageMap: {},
    fieldAliases: {
      candidateName: ['candidate_name', 'candidate', 'name', 'full_name'],
      email: ['email', 'email_address'],
      phone: ['phone', 'mobile'],
      role: ['role', 'title', 'job_title', 'position'],
      company: ['company', 'company_name', 'client', 'organization', 'organisation'],
      stage: ['stage', 'status', 'pipeline_stage'],
      jobTitle: ['job_title', 'job', 'role', 'position', 'title'],
      contactName: ['contact', 'contact_name', 'hiring_manager', 'client_contact'],
      contactEmail: ['contact_email', 'hiring_manager_email', 'client_contact_email'],
      fee: ['fee', 'placement_fee', 'revenue'],
      legacyId: ['id', 'record_id', 'legacy_id'],
    },
  },
};

const MIGRATION_ENTITY_OPTIONS = [
  { id: 'candidates', label: 'Candidates', permission: 'pipeline.write' },
  { id: 'jobs', label: 'Jobs', permission: 'jobs.write' },
  { id: 'companies', label: 'Companies', permission: 'clients.write' },
  { id: 'contacts', label: 'Contacts', permission: 'clients.write' },
  { id: 'placements', label: 'Placements', permission: 'revenue.write' },
];

function normalizeWorkspaceRole(role) {
  const normalized = String(role || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
  if (normalized === 'account_manager' || normalized === 'sales') return 'sales_manager';
  if (normalized === 'bookkeeper') return 'finance';
  if (RBAC_ROLE_PRESETS[normalized]) return normalized;
  if (normalized === 'agent') return 'recruiter';
  return 'recruiter';
}

function getRolePermissions(role, customPermissions = []) {
  const normalizedRole = normalizeWorkspaceRole(role);
  const preset = RBAC_ROLE_PRESETS[normalizedRole] || RBAC_ROLE_PRESETS.recruiter;
  return Array.from(new Set([...(preset.permissions || []), ...(Array.isArray(customPermissions) ? customPermissions : [])]));
}

function hasPermission(role, permission, customPermissions = []) {
  const permissions = getRolePermissions(role, customPermissions);
  if (permissions.includes('*')) return true;
  return permissions.includes(permission);
}

function getFirstAllowedWorkspaceTab(role, customPermissions = []) {
  return Object.keys(WORKSPACE_PAGE_CONFIG).find((tabId) => hasPermission(role, RBAC_PAGE_PERMISSIONS[tabId], customPermissions)) || 'pipeline';
}

function getWorkspaceTabFromPath(pathname = '') {
  const normalizedPath = String(pathname || '').replace(/\/+$/, '') || '/';
  if (normalizedPath === '/workspace') return 'pipeline';
  return WORKSPACE_PATH_TO_TAB[normalizedPath] || '';
}

function getInitialWorkspaceTab() {
  if (typeof window === 'undefined') return 'pipeline';
  return getWorkspaceTabFromPath(window.location.pathname) || 'pipeline';
}



const DEFAULT_TIME_MANAGEMENT = {

  calendarDate: '',

  scheduleItems: '',

  reminders: '',

  dailyGoals: '',

};



const SCRAPED_JOBS_CACHE_TTL_MS = 15 * 60 * 1000;

const SCRAPED_JOBS_RATE_LIMIT_MS = 45 * 1000;

const OUTREACH_TONES = ['formal', 'concise', 'sales'];

const PIPELINE_VIEW_OPTIONS = ['board', 'table'];



const ROLE_KEYWORDS = {

  'Senior Frontend Engineer': ['react', 'typescript', 'javascript', 'frontend', 'ui', 'node'],

  'Product Designer': ['figma', 'ux', 'ui', 'prototype', 'design', 'research'],

  'Head of Growth': ['growth', 'marketing', 'analytics', 'seo', 'performance', 'retention'],

};

const PLACEHOLDER_EMAIL_DOMAINS = new Set(['example.com', 'example.org', 'example.net', 'test.com', 'mailinator.com']);

const SEQUENCE_TASK_OUTCOME_OPTIONS = {

  linkedin_task: [

    { id: 'sent', label: 'Sent' },

    { id: 'replied', label: 'Replied' },

    { id: 'no_response', label: 'No Response' },

    { id: 'invalid_profile', label: 'Invalid Profile' },

    { id: 'not_interested', label: 'Not Interested' },

    { id: 'skipped', label: 'Skip' },

  ],

  whatsapp_task: [

    { id: 'sent', label: 'Sent' },

    { id: 'replied', label: 'Replied' },

    { id: 'no_response', label: 'No Response' },

    { id: 'wrong_number', label: 'Wrong Number' },

    { id: 'opted_out', label: 'Opted Out' },

    { id: 'skipped', label: 'Skip' },

  ],

  call_task: [

    { id: 'connected', label: 'Connected' },

    { id: 'voicemail', label: 'Voicemail' },

    { id: 'no_answer', label: 'No Answer' },

    { id: 'wrong_number', label: 'Wrong Number' },

    { id: 'not_interested', label: 'Not Interested' },

    { id: 'skipped', label: 'Skip' },

  ],

};



function normalizeText(value) {

  return String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

}

function rgbToHex(red, green, blue) {
  return `#${[red, green, blue].map((channel) => channel.toString(16).padStart(2, '0')).join('')}`;
}

function getLogoColorSuggestions(file) {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve([]);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const size = 64;
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext('2d', { willReadFrequently: true });
        if (!context) {
          resolve([]);
          return;
        }
        context.drawImage(image, 0, 0, size, size);
        const pixels = context.getImageData(0, 0, size, size).data;
        const buckets = new Map();
        for (let index = 0; index < pixels.length; index += 4) {
          const alpha = pixels[index + 3];
          if (alpha < 180) continue;
          const red = Math.round(pixels[index] / 32) * 32;
          const green = Math.round(pixels[index + 1] / 32) * 32;
          const blue = Math.round(pixels[index + 2] / 32) * 32;
          const brightness = (red + green + blue) / 3;
          if (brightness < 18 || brightness > 245) continue;
          const key = `${red},${green},${blue}`;
          buckets.set(key, (buckets.get(key) || 0) + 1);
        }
        const suggestions = [...buckets.entries()]
          .sort((left, right) => right[1] - left[1])
          .map(([key]) => key.split(',').map(Number))
          .map(([red, green, blue]) => rgbToHex(Math.min(red, 255), Math.min(green, 255), Math.min(blue, 255)))
          .filter((color, index, colors) => colors.every((existing, existingIndex) => existingIndex === index || existing !== color))
          .slice(0, 5);
        resolve(suggestions);
      } catch (error) {
        reject(error);
      } finally {
        URL.revokeObjectURL(objectUrl);
      }
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('The logo image could not be read.'));
    };
    image.src = objectUrl;
  });
}

function cleanLinkedInUrl(url) {
  try {
    const u = new URL(String(url || ''));
    return `${u.origin}${u.pathname.replace(/\/$/, '')}/`;
  } catch (_) {
    return String(url || '');
  }
}

function slugifyKey(value, fallback = 'record') {

  const slug = normalizeText(value).replace(/\s+/g, '-');

  return slug || `${fallback}-${Date.now()}`;

}

function getJobPublishStatus(job = {}) {
  const explicit = String(job.publishStatus || '').trim();
  if (explicit) return explicit;
  const status = String(job.status || 'Active').trim().toLowerCase();
  if (['closed', 'inactive', 'archived', 'removed'].includes(status)) return 'Closed';
  if (status === 'paused') return 'Paused';
  if (status === 'draft') return 'Draft';
  return 'Live';
}

function isJobPubliclyLive(job = {}) {
  return getJobPublishStatus(job).toLowerCase() === 'live' && !['closed', 'inactive', 'archived', 'removed'].includes(String(job.status || '').toLowerCase());
}

function getCareersIdentifier(agencyBrand = {}, profile = {}, recruiterUser = {}) {
  return agencyBrand.agencySlug || recruiterUser?.uid || slugifyKey(profile.agency || profile.agencyKey || 'agency', 'agency');
}

function getPublicJobUrl(job = {}, agencyBrand = {}, profile = {}, recruiterUser = {}) {
  const ownerId = recruiterUser?.uid || getCareersIdentifier(agencyBrand, profile, recruiterUser);
  return `${getPublicAppBaseUrl()}/job/${encodeURIComponent(ownerId)}/${encodeURIComponent(job.id || '')}`;
}

function getPublicCareerUrl(agencyBrand = {}, profile = {}, recruiterUser = {}) {
  const identifier = getCareersIdentifier(agencyBrand, profile, recruiterUser);
  return `${getPublicAppBaseUrl()}/careers/${encodeURIComponent(identifier)}`;
}

function getClientBoardUrl(job = {}, agencyBrand = {}, profile = {}, recruiterUser = {}) {
  const identifier = getCareersIdentifier(agencyBrand, profile, recruiterUser);
  const clientSlug = slugifyKey(getJobCompanyName(job) || job.companyName || 'client', 'client');
  return `${getPublicAppBaseUrl()}/careers/${encodeURIComponent(identifier)}?client=${encodeURIComponent(clientSlug)}`;
}

function buildJobPublishingHealth(job = {}, snapshot = null, agencyBrand = {}) {
  const checks = [
    { id: 'title', label: 'Searchable title', ok: Boolean(String(job.title || '').trim()) },
    { id: 'company', label: 'Client/company connected', ok: Boolean(getJobCompanyName(job)) },
    { id: 'location', label: 'Location indexed', ok: Boolean(String(job.location || '').trim()) },
    { id: 'description', label: 'Real job description', ok: String(job.summary || job.requirements || '').trim().length >= 80 },
    { id: 'questions', label: 'Screening questions', ok: Array.isArray(job.customScreeningQuestions) && job.customScreeningQuestions.filter(Boolean).length > 0 },
    { id: 'routing', label: 'Pipeline routing owner', ok: Boolean(String(job.applicationRoutingOwner || job.ownerName || job.recruiter || '').trim()) },
    { id: 'slug', label: 'Agency careers slug', ok: Boolean(String(agencyBrand.agencySlug || '').trim()) },
    { id: 'apply', label: 'Application intake active', ok: isJobPubliclyLive(job) },
  ];
  const passed = checks.filter((check) => check.ok).length;
  const score = Math.round((passed / checks.length) * 100);
  const sourceCounts = (snapshot?.applications || []).reduce((accumulator, application) => {
    const key = String(application.utmSource || application.source || application.referrer || 'Direct').trim() || 'Direct';
    accumulator[key] = (accumulator[key] || 0) + 1;
    return accumulator;
  }, {});
  return {
    score,
    label: score >= 88 ? 'Publish-ready' : score >= 65 ? 'Needs polish' : 'At risk',
    checks,
    sourceCounts,
  };
}

function createCompanyRecordId(companyName) {

  return `company-${slugifyKey(companyName, 'company')}`;

}

function createContactRecordId(companyName, contactValue) {

  return `contact-${slugifyKey(companyName, 'company')}-${slugifyKey(contactValue, 'contact')}`;

}

function createSubmissionRecordId(candidateId, jobId, companyName = '') {

  return `submission-${slugifyKey(jobId || companyName, 'job')}-${slugifyKey(candidateId, 'candidate')}`;

}

function extractEmailFromText(value) {

  const match = String(value || '').match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);

  return match ? match[0].toLowerCase() : '';

}

function inferContactVibe(contact, touchpoints = [], submissions = []) {

  const haystack = normalizeText([
    contact?.notes,
    contact?.preferenceSignal,
    contact?.vibe,
    ...touchpoints.map((item) => `${item.summary || ''} ${item.type || ''}`),
    ...submissions.map((candidate) => `${candidate.notes || ''} ${candidate.summary || ''} ${candidate.screeningSummary || ''}`),
  ].join(' '));

  if (/(metric|metrics|quantified|kpi|scale|scalable|data|score|scorecard|revenue|conversion|performance)/.test(haystack)) return 'Tier 1 Vibe';

  if (/(narrative|story|storytelling|portfolio|agency|creative|context|culture|voice|writing)/.test(haystack)) return 'Agency Vibe';

  return 'Balanced Vibe';

}

function inferContactSentiment(touchpoints = []) {

  if (touchpoints.some((item) => /(send_candidates|book_call|meeting|connected|reply|response)/i.test(`${item.type || ''} ${item.summary || ''}`))) return 'Highly Responsive';

  const mostRecent = touchpoints
    .map((item) => new Date(item.createdAtIso || 0).getTime())
    .filter(Boolean)
    .sort((left, right) => right - left)[0] || 0;

  if (!mostRecent) return 'Passive';

  const daysSinceTouch = Math.floor((Date.now() - mostRecent) / 86400000);

  if (daysSinceTouch >= 45) return 'Ghosted';

  if (daysSinceTouch >= 21) return 'Passive';

  return 'Highly Responsive';

}

// ─── Tag Color Palette ────────────────────────────────────────────────────────
const TAG_PALETTE = [
  'bg-cyan-500/15 border-cyan-400/25 text-cyan-200',
  'bg-violet-500/15 border-violet-400/25 text-violet-200',
  'bg-emerald-500/15 border-emerald-400/25 text-emerald-200',
  'bg-amber-500/15 border-amber-400/25 text-amber-200',
  'bg-rose-500/15 border-rose-400/25 text-rose-200',
  'bg-sky-500/15 border-sky-400/25 text-sky-200',
  'bg-orange-500/15 border-orange-400/25 text-orange-200',
  'bg-pink-500/15 border-pink-400/25 text-pink-200',
  'bg-teal-500/15 border-teal-400/25 text-teal-200',
  'bg-violet-500/15 border-violet-400/25 text-violet-200',
];
function getTagColor(tag) {
  let h = 0;
  for (let i = 0; i < tag.length; i++) h = (h * 31 + tag.charCodeAt(i)) >>> 0;
  return TAG_PALETTE[h % TAG_PALETTE.length];
}

// ─── Deduplication Engine ─────────────────────────────────────────────────────
function _editDistance(a, b) {
  const m = a.length, n = b.length;
  if (m === 0) return n; if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, (_, i) => [i, ...Array(n).fill(0)]);
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) for (let j = 1; j <= n; j++)
    dp[i][j] = a[i - 1] === b[j - 1] ? dp[i - 1][j - 1] : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
  return dp[m][n];
}
function _nameSim(a, b) {
  if (!a || !b) return 0;
  const na = a.toLowerCase().trim(), nb = b.toLowerCase().trim();
  if (na === nb) return 100;
  const longer = Math.max(na.length, nb.length);
  if (longer === 0) return 100;
  return Math.round((1 - _editDistance(na, nb) / longer) * 100);
}
function computeContactDuplicates(contacts, dismissedKeys) {
  const groups = [];
  const dismissed = new Set(dismissedKeys || []);
  for (let i = 0; i < contacts.length; i++) {
    for (let j = i + 1; j < contacts.length; j++) {
      const a = contacts[i], b = contacts[j];
      const key = [a.id, b.id].sort().join('__');
      if (dismissed.has(key)) continue;
      let score = 0;
      const emailA = String(a.email || '').toLowerCase().trim();
      const emailB = String(b.email || '').toLowerCase().trim();
      if (emailA && emailB && emailA === emailB) score = 100;
      else if (a.linkedIn && b.linkedIn && a.linkedIn === b.linkedIn) score = 95;
      else {
        const phoneA = String(a.phone || '').replace(/\D/g, '');
        const phoneB = String(b.phone || '').replace(/\D/g, '');
        if (phoneA.length >= 7 && phoneA === phoneB) score = Math.max(score, 85);
        const nameSim = _nameSim(a.name, b.name);
        const sameCompany = normalizeText(a.companyName || '') === normalizeText(b.companyName || '') && a.companyName;
        if (nameSim >= 90 && sameCompany) score = Math.max(score, 88);
        else if (nameSim >= 85) score = Math.max(score, 72);
        else if (nameSim >= 70 && sameCompany) score = Math.max(score, 65);
      }
      if (score >= 60) groups.push({ primary: a, secondary: b, score, key });
    }
  }
  return groups.sort((a, b) => b.score - a.score).slice(0, 30);
}
function computeCandidateDuplicates(candidates, dismissedKeys) {
  const groups = [];
  const dismissed = new Set(dismissedKeys || []);
  for (let i = 0; i < candidates.length; i++) {
    for (let j = i + 1; j < candidates.length; j++) {
      const a = candidates[i], b = candidates[j];
      const key = [a.id, b.id].sort().join('__');
      if (dismissed.has(key)) continue;
      let score = 0;
      const emailA = String(a.email || '').toLowerCase().trim();
      const emailB = String(b.email || '').toLowerCase().trim();
      if (emailA && emailB && emailA === emailB) score = 100;
      else {
        const phoneA = String(a.phone || '').replace(/\D/g, '');
        const phoneB = String(b.phone || '').replace(/\D/g, '');
        if (phoneA.length >= 7 && phoneA === phoneB) score = Math.max(score, 85);
        const nameSim = _nameSim(a.name, b.name);
        const sameRole = normalizeText(a.role || '') === normalizeText(b.role || '') && a.role;
        const sameCompany = normalizeText(a.company || '') === normalizeText(b.company || '') && a.company;
        if (nameSim >= 90) score = Math.max(score, sameRole || sameCompany ? 88 : 75);
        else if (nameSim >= 80 && (sameRole || sameCompany)) score = Math.max(score, 68);
      }
      if (score >= 60) groups.push({ primary: a, secondary: b, score, key });
    }
  }
  return groups.sort((a, b) => b.score - a.score).slice(0, 30);
}

// ─── Relationship Intelligence Engine ────────────────────────────────────────
function computeRelationshipIntelligence(client, allContacts, allCrmTasks, allJobs) {
  const today = new Date().toISOString().slice(0, 10);
  const now = Date.now();

  // ── 1. Recency (0–35) ──────────────────────────────────────────────────────
  const lastTouchMs = client.lastOutreach ? new Date(client.lastOutreach).getTime() : 0;
  const daysSinceTouch = lastTouchMs ? Math.floor((now - lastTouchMs) / 86400000) : 999;
  const recencyPoints =
    !lastTouchMs     ? 35
    : daysSinceTouch >= 60 ? 35
    : daysSinceTouch >= 45 ? 28
    : daysSinceTouch >= 30 ? 22
    : daysSinceTouch >= 21 ? 15
    : daysSinceTouch >= 14 ? 8
    : daysSinceTouch >= 7  ? 3
    : 0;

  // ── 2. Touch velocity (0–15) — last 90 days ────────────────────────────────
  const ninetyDaysAgo = now - 90 * 86400000;
  const thirtyDaysAgo = now - 30 * 86400000;
  const sixtyDaysAgo  = now - 60 * 86400000;
  const recentTouches = client.touchpoints.filter((tp) => new Date(tp.createdAtIso || 0).getTime() > ninetyDaysAgo).length;
  const velocityPoints = recentTouches === 0 ? 15 : recentTouches === 1 ? 10 : recentTouches === 2 ? 5 : 0;

  // ── 3. Contact responsiveness (0–15) ───────────────────────────────────────
  const _clientKey = normalizeText(client.companyName || '');
  const _clientLinkedInUrl = (client.linkedInUrl || '').toLowerCase().replace(/\/$/, '');
  const clientContacts = allContacts.filter((c) => {
    if (c.type === 'company') return false; // skip company records — only person contacts
    const cKey = normalizeText(c.company || c.companyName || '');
    // Match by company name (fuzzy)
    if (cKey && _clientKey) {
      if (cKey === _clientKey || cKey.includes(_clientKey) || _clientKey.includes(cKey)) return true;
    }
    // Match by company LinkedIn URL — catches contacts where company name was empty
    if (_clientLinkedInUrl && c.companyLinkedInUrl) {
      const cUrl = c.companyLinkedInUrl.toLowerCase().replace(/\/$/, '');
      if (cUrl === _clientLinkedInUrl || cUrl.includes(_clientLinkedInUrl) || _clientLinkedInUrl.includes(cUrl)) return true;
    }
    return false;
  });
  let responsivenessPoints = clientContacts.length === 0 ? 8 : 0;
  if (clientContacts.length > 0) {
    const sentiments = clientContacts.map((c) => c.sentiment || inferContactSentiment(c.touchpoints || []));
    const ghosted = sentiments.filter((s) => s === 'Ghosted').length;
    const passive = sentiments.filter((s) => s === 'Passive').length;
    const responsive = sentiments.filter((s) => s === 'Highly Responsive').length;
    if (ghosted >= 1) responsivenessPoints += 10;
    if (passive > responsive) responsivenessPoints += 5;
    if (responsive > 0 && ghosted === 0) responsivenessPoints = Math.max(0, responsivenessPoints - 5);
  }
  responsivenessPoints = Math.min(15, responsivenessPoints);

  // ── 4. Tier multiplier ─────────────────────────────────────────────────────
  const isTopTier = client.totalJobOrders >= 3 || client.hires >= 1 || client.weightedPipeline >= 15000;
  const isMidTier = !isTopTier && (client.totalJobOrders >= 1 || client.weightedPipeline >= 5000);
  const tierMultiplier = isTopTier ? 1.3 : isMidTier ? 1.1 : 0.9;

  // ── 5. Pipeline momentum (0–10) ────────────────────────────────────────────
  const momentumPoints =
    client.submittedCandidates === 0 && client.totalJobOrders > 0 ? 10
    : client.hires === 0 && client.submittedCandidates >= 5 ? 5
    : 0;

  // ── 6. Blocked / stuck jobs (0–10) ─────────────────────────────────────────
  const clientJobs = allJobs.filter((j) => normalizeText(getJobCompanyName(j) || '') === normalizeText(client.companyName || ''));
  const blockedJobs = clientJobs.filter((j) => j.isBlocked);
  const blockedPoints = Math.min(10, blockedJobs.length * 5);

  // ── 7. Overdue CRM tasks (0–10) ────────────────────────────────────────────
  const overdueTasks = allCrmTasks.filter((t) => normalizeText(t.companyName || '') === normalizeText(client.companyName || '') && t.status !== 'done' && t.dueDate && t.dueDate < today);
  const overduePoints = Math.min(10, overdueTasks.length * 4);

  // ── Risk score ─────────────────────────────────────────────────────────────
  const rawScore = recencyPoints + velocityPoints + responsivenessPoints + momentumPoints + blockedPoints + overduePoints;
  const riskScore = Math.min(100, Math.round(rawScore * tierMultiplier));

  // ── Risk level ─────────────────────────────────────────────────────────────
  const riskLevel =
    riskScore <= 19 ? 'Healthy'
    : riskScore <= 39 ? 'Watch'
    : riskScore <= 59 ? 'Needs attention'
    : riskScore <= 79 ? 'At risk'
    : 'Critical';

  // ── Drivers ────────────────────────────────────────────────────────────────
  const drivers = [];
  if (recencyPoints >= 22) drivers.push({ label: daysSinceTouch >= 999 ? 'No outreach on record' : `Silent for ${daysSinceTouch} days`, impact: recencyPoints >= 28 ? 'high' : 'medium', icon: 'clock' });
  else if (recencyPoints >= 8) drivers.push({ label: `Last touch ${daysSinceTouch}d ago`, impact: 'low', icon: 'clock' });
  if (velocityPoints >= 10) drivers.push({ label: recentTouches === 0 ? 'No touches in 90 days' : `Only ${recentTouches} touch${recentTouches !== 1 ? 'es' : ''} in 90 days`, impact: velocityPoints >= 15 ? 'high' : 'medium', icon: 'zap' });
  if (clientContacts.length === 0) drivers.push({ label: 'No contacts on file', impact: 'medium', icon: 'user' });
  else {
    const ghosted = clientContacts.filter((c) => (c.sentiment || inferContactSentiment(c.touchpoints || [])) === 'Ghosted');
    if (ghosted.length > 0) drivers.push({ label: `${ghosted.length} contact${ghosted.length !== 1 ? 's' : ''} ghosted`, impact: 'high', icon: 'user' });
  }
  if (momentumPoints >= 8) drivers.push({ label: client.submittedCandidates === 0 ? 'No CVs submitted yet' : 'Many submittals, no placement', impact: 'medium', icon: 'briefcase' });
  if (blockedPoints > 0) drivers.push({ label: `${blockedJobs.length} job${blockedJobs.length !== 1 ? 's' : ''} blocked`, impact: blockedPoints >= 8 ? 'high' : 'medium', icon: 'flag' });
  if (overduePoints > 0) drivers.push({ label: `${overdueTasks.length} overdue task${overdueTasks.length !== 1 ? 's' : ''}`, impact: overduePoints >= 8 ? 'high' : 'medium', icon: 'alert' });
  if (isTopTier) drivers.push({ label: 'Top-tier account', impact: 'info', icon: 'star' });

  // ── Health signals ─────────────────────────────────────────────────────────
  const healthSignals = [];
  if (recencyPoints <= 3) healthSignals.push('Active engagement');
  if (client.hires > 0) healthSignals.push(`${client.hires} placement${client.hires !== 1 ? 's' : ''}`);
  const last30count = client.touchpoints.filter((tp) => new Date(tp.createdAtIso || 0).getTime() > thirtyDaysAgo).length;
  if (last30count >= 3) healthSignals.push('High cadence');
  const responsiveCount = clientContacts.filter((c) => (c.sentiment || inferContactSentiment(c.touchpoints || [])) === 'Highly Responsive').length;
  if (responsiveCount > 0) healthSignals.push(`${responsiveCount} responsive contact${responsiveCount !== 1 ? 's' : ''}`);
  if (client.weightedPipeline >= 10000) healthSignals.push(`R${Math.round(client.weightedPipeline / 1000)}k pipeline`);
  if (clientJobs.some((j) => ['offer', 'placed'].includes(j.lifecycleStage || ''))) healthSignals.push('Late-stage deal active');

  // ── Next best action ───────────────────────────────────────────────────────
  const nextBestAction =
    riskLevel === 'Critical'         ? `Call ${client.hiringContact || 'key contact'} today — account is critically at risk.`
    : riskLevel === 'At risk'        ? `Re-engage with a personalised email referencing ${client.openRoles} open role${client.openRoles !== 1 ? 's' : ''}.`
    : riskLevel === 'Needs attention' ? (overdueTasks.length > 0 ? `Clear ${overdueTasks.length} overdue task${overdueTasks.length !== 1 ? 's' : ''}, then schedule a call.` : 'Schedule a check-in within 7 days.')
    : riskLevel === 'Watch'          ? 'Log a touchpoint this week to maintain momentum.'
    : client.openRoles > 0           ? `Send candidates for ${client.openRoles} open role${client.openRoles !== 1 ? 's' : ''}.`
    : 'Relationship is healthy. Monitor for new roles.';

  // ── Trend ──────────────────────────────────────────────────────────────────
  const last30 = client.touchpoints.filter((tp) => new Date(tp.createdAtIso || 0).getTime() > thirtyDaysAgo).length;
  const prev30 = client.touchpoints.filter((tp) => { const t = new Date(tp.createdAtIso || 0).getTime(); return t > sixtyDaysAgo && t <= thirtyDaysAgo; }).length;
  const riskTrend = last30 > prev30 ? 'improving' : last30 < prev30 ? 'declining' : 'stable';

  // ── salesQueueScore ────────────────────────────────────────────────────────
  const salesQueueScore = riskScore * 2 + client.weightedPipeline / 500 + client.openRoles * 10 + (isTopTier ? 50 : 0);

  return { riskScore, riskLevel, riskDrivers: drivers, healthSignals, nextBestAction, riskTrend, salesQueueScore, daysSinceTouch, contacts: clientContacts };
}

function getDepartmentMix(jobs = []) {

  return jobs.reduce((accumulator, job) => {
    const department = String(job.department || job.type || 'General').trim() || 'General';
    accumulator[department] = (accumulator[department] || 0) + 1;
    return accumulator;
  }, {});

}

function getRoleVelocity(jobs = []) {

  const now = Date.now();
  const recentJobs = jobs.filter((job) => {
    const createdMs = new Date(job.createdAtIso || job.postedAt || job.lastOutreach || 0).getTime();
    return createdMs && now - createdMs <= 90 * 86400000;
  }).length;

  return Math.round((recentJobs / 3) * 10) / 10;

}



function isLikelyPlaceholderValue(value) {

  const normalized = normalizeText(value);

  if (!normalized) return true;

  return ['fake', 'test', 'dummy', 'example', 'unknown', 'none', 'not real', 'placeholder'].some((token) => normalized.includes(token));

}



function getSequenceLocationCountryHint(location) {

  const normalized = normalizeText(location);

  if (normalized.includes('south africa') || normalized.includes(' johannesburg ') || normalized.endsWith(' za') || normalized.includes(' cape town ')) return 'za';

  if (normalized.includes('united states') || normalized.includes(' usa ') || normalized.includes('new york') || normalized.includes('san francisco')) return 'us';

  if (normalized.includes('united kingdom') || normalized.includes(' uk ') || normalized.includes('london')) return 'uk';

  return '';

}



function isValidSequenceEmail(value) {

  const email = String(value || '').trim().toLowerCase();

  if (!email || isLikelyPlaceholderValue(email)) return '';

  const parts = email.split('@');

  if (parts.length !== 2) return '';

  const [localPart, domain] = parts;

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return '';

  if (!localPart || PLACEHOLDER_EMAIL_DOMAINS.has(domain)) return '';

  return email;

}



function normalizeSequencePhone(value, location = '') {

  const raw = String(value || '').trim();

  if (!raw || isLikelyPlaceholderValue(raw)) return '';

  let compact = raw.replace(/[^\d+]/g, '');

  if (!compact) return '';

  if (compact.startsWith('00')) compact = `+${compact.slice(2)}`;

  if (compact.startsWith('+')) {

    const digits = compact.replace(/\D/g, '');

    return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : '';

  }

  const digits = compact.replace(/\D/g, '');

  const countryHint = getSequenceLocationCountryHint(location);

  if (countryHint === 'za' && digits.length === 10 && digits.startsWith('0')) return `+27${digits.slice(1)}`;

  if (countryHint === 'us' && digits.length === 10) return `+1${digits}`;

  if (countryHint === 'uk' && digits.length >= 10 && digits.startsWith('0')) return `+44${digits.slice(1)}`;

  return digits.length >= 10 && digits.length <= 15 ? `+${digits}` : '';

}



function normalizeLinkedInProfileUrl(value) {

  const raw = String(value || '').trim();

  if (!raw || isLikelyPlaceholderValue(raw)) return '';

  const prefixed = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;

  try {

    const url = new URL(prefixed);

    const hostname = url.hostname.toLowerCase();

    const pathname = url.pathname.toLowerCase();

    if (!hostname.endsWith('linkedin.com')) return '';

    if (!(pathname.startsWith('/in/') || pathname.startsWith('/pub/'))) return '';

    return url.toString();

  } catch {

    return '';

  }

}



function buildSequenceContactReadiness(target = {}) {

  const email = isValidSequenceEmail(target.email);

  const linkedInUrl = normalizeLinkedInProfileUrl(target.linkedInUrl);

  const phone = normalizeSequencePhone(target.phone, target.location);

  const emailReady = Boolean(email);

  const linkedInReady = Boolean(linkedInUrl);

  const phoneReady = Boolean(phone);

  const channels = {

    email: {

      id: 'email',

      isUsable: emailReady,

      value: email,

      message: emailReady ? 'Email looks usable for outreach.' : 'Email is missing or looks like placeholder/test data.',

    },

    linkedin: {

      id: 'linkedin',

      isUsable: linkedInReady,

      value: linkedInUrl,

      message: linkedInReady ? 'LinkedIn profile URL is ready.' : 'LinkedIn profile is missing or invalid.',

    },

    phone: {

      id: 'phone',

      isUsable: phoneReady,

      value: phone,

      message: phoneReady ? 'Phone number is normalized for WhatsApp and call tasks.' : 'Phone number is missing, fake, or not normalized enough for outreach.',

    },

  };

  const unusableChannels = Object.values(channels).filter((channel) => !channel.isUsable).map((channel) => channel.id);

  return {

    channels,

    unusableChannels,

    warningSummary: unusableChannels.length > 0 ? `Skipped invalid channels: ${unusableChannels.join(', ')}.` : '',

    normalizedTarget: {

      ...target,

      email,

      linkedInUrl,

      phone,

    },

  };

}



function getSequenceTaskOutcomeOptions(channel) {

  return SEQUENCE_TASK_OUTCOME_OPTIONS[channel] || [{ id: 'skipped', label: 'Skip' }];

}



function getSequenceTaskActionLabel(channel) {

  if (channel === 'linkedin_task') return 'Open LinkedIn';

  if (channel === 'whatsapp_task') return 'Open WhatsApp';

  if (channel === 'call_task') return 'Call now';

  return 'Open task';

}



function getSequenceTaskProviderStatusLabel(task = {}) {

  const status = String(task?.providerDispatchStatus || '').toLowerCase();

  if (status === 'queued') return 'Queued';

  if (status === 'sent') return 'Sent';

  if (status === 'delivered') return 'Delivered';

  if (status === 'read') return 'Read';

  if (status === 'replied') return 'Replied';

  if (status === 'failed' || status === 'undelivered') return 'Failed';

  return '';

}



function createStorageSafeFileName(value, fallback = 'cv-upload.pdf') {

  const trimmed = String(value || '').trim();

  const sanitized = trimmed.replace(/[^a-zA-Z0-9._-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

  return sanitized || fallback;

}



function getCandidateResumeUrl(candidate = {}) {

  const candidateUrls = [
    candidate?.resumeUrl,
    candidate?.resume_url,
    candidate?.cvPdfUrl,
    candidate?.cvUrl,
    candidate?.resume?.url,
    candidate?.applicant?.resumeUrl,
    candidate?.raw?.resume?.url,
    candidate?.raw?.applicant?.resumeUrl,
  ];

  return candidateUrls.find((value) => typeof value === 'string' && value.trim()) || '';

}



function getCandidateResumeFileName(candidate = {}, resumeUrl = '') {

  if (typeof candidate?.fileName === 'string' && candidate.fileName.trim()) return candidate.fileName.trim();

  if (typeof candidate?.resume?.fileName === 'string' && candidate.resume.fileName.trim()) return candidate.resume.fileName.trim();

  if (typeof candidate?.raw?.resume?.fileName === 'string' && candidate.raw.resume.fileName.trim()) return candidate.raw.resume.fileName.trim();

  if (typeof candidate?.source === 'string' && /\.pdf(?:$|\?)/i.test(candidate.source.trim())) return candidate.source.trim();

  if (!resumeUrl) return '';

  try {

    const parsedUrl = new URL(resumeUrl);

    const pathParts = parsedUrl.pathname.split('/').filter(Boolean);

    const lastPathPart = pathParts[pathParts.length - 1] || '';

    return decodeURIComponent(lastPathPart).trim();

  } catch (_error) {

    return '';

  }

}



function isAgencySubscriptionActive(agencyData) {

  const status = String(agencyData?.subscriptionStatus || agencyData?.status || '').toLowerCase();

  return ['active', 'renewing', 'valid', 'trialing', 'paid', 'completed', 'complete', 'success', 'succeeded'].includes(status);

}



function clampSeatLimit(value, fallback = 1, minimum = 1) {

  const parsed = Number(value);

  const safeValue = Number.isFinite(parsed) ? parsed : fallback;

  return Math.max(Math.min(safeValue, 250), minimum, 1);

}



function getJobCompanyName(job) {

  const companyName = String(job?.companyName || '').replace(/\s+/g, ' ').trim();

  if (normalizeText(companyName) && !['company', 'unknown company', 'vision agency'].includes(normalizeText(companyName))) return companyName;

  return INITIAL_AGENCIES.find((agency) => agency.id === job?.agencyId)?.name || '';

}



const GENERIC_JOB_TITLE_PREFIXES = [

  'build a great career',

  'experience the team spirit',

  'lead with your ambition',

  'enjoy global',

  'join us in shaping',

  'we invest in your growth',

  'preferably in',

  'compensation on request',

  'no matter your background',

  'no matter where',

];



const JOB_ROLE_HINT_PATTERN = /\b(manager|designer|engineer|developer|specialist|lead|director|coordinator|analyst|consultant|executive|associate|assistant|architect|recruiter|marketer|strategist|scientist|administrator|officer|intern|head|principal|producer|editor|writer|sales|account|product|project|operations|crm|paid media|brand|retention|marketing)\b/i;



const JOB_STATUS_MAP = {

  active: 'Active',

  listed: 'Active',

  launch: 'Active',

  open: 'Active',

  live: 'Active',

  closed: 'Closed',

  inactive: 'Closed',

  paused: 'Paused',

  draft: 'Draft',

  removed: 'Removed',

  deleted: 'Removed',

};

const JOB_LIFECYCLE_STAGES = [
  { id: 'brief',     label: 'Brief',     desc: 'Requirements gathering',   dot: 'bg-slate-400',   textColor: 'text-slate-300',   border: 'border-slate-500/40'   },
  { id: 'search',    label: 'Search',    desc: 'Active sourcing',           dot: 'bg-blue-400',    textColor: 'text-blue-300',    border: 'border-blue-500/40'    },
  { id: 'shortlist', label: 'Shortlist', desc: 'Candidates shortlisted',    dot: 'bg-violet-400',  textColor: 'text-violet-300',  border: 'border-violet-500/40'  },
  { id: 'submitted', label: 'Submitted', desc: 'CVs sent to client',        dot: 'bg-amber-400',   textColor: 'text-amber-300',   border: 'border-amber-500/40'   },
  { id: 'interview', label: 'Interview', desc: 'Client interviews running', dot: 'bg-orange-400',  textColor: 'text-orange-300',  border: 'border-orange-500/40'  },
  { id: 'offer',     label: 'Offer',     desc: 'Offer being negotiated',    dot: 'bg-green-400',   textColor: 'text-green-300',   border: 'border-green-500/40'   },
  { id: 'placed',    label: 'Placed',    desc: 'Placement confirmed ✓',     dot: 'bg-emerald-400', textColor: 'text-emerald-300', border: 'border-emerald-500/40' },
];

const RISK_LEVEL_STYLES = {
  Healthy:          { border: 'border-emerald-400/20', bg: 'bg-emerald-500/5',  badge: 'border-emerald-300/30 bg-emerald-500/10 text-emerald-200',  bar: 'bg-emerald-400', label: 'Healthy'          },
  Watch:            { border: 'border-sky-400/20',     bg: 'bg-sky-500/5',      badge: 'border-sky-300/30 bg-sky-500/10 text-sky-200',             bar: 'bg-sky-400',     label: 'Watch'            },
  'Needs attention':{ border: 'border-amber-400/25',   bg: 'bg-amber-500/5',    badge: 'border-amber-300/30 bg-amber-500/10 text-amber-100',        bar: 'bg-amber-400',   label: 'Needs attention'  },
  'At risk':        { border: 'border-orange-400/30',  bg: 'bg-orange-500/8',   badge: 'border-orange-300/30 bg-orange-500/10 text-orange-100',     bar: 'bg-orange-400',  label: 'At risk'          },
  Critical:         { border: 'border-rose-400/35',    bg: 'bg-rose-500/10',    badge: 'border-rose-300/30 bg-rose-500/10 text-rose-100',           bar: 'bg-rose-400',    label: 'Critical'         },
};

const COMPANY_GARBAGE_PATTERN = /\b(and|with|for|through|aligned|execute|campaign|strategy|team|operations|plans|free bets|email|collaborating|revenue growth|manage|develop|content)\b/i;



function cleanJobText(value) {

  return String(value || '')

    .replace(/\u00a0/g, ' ')

    .replace(/[\r\n]+/g, ' ')

    .replace(/\s+/g, ' ')

    .trim();

}



function isLinkedInSearchResultsUrl(value) {

  const normalized = cleanJobText(value).toLowerCase();

  return normalized.includes('linkedin.com/jobs/search') || normalized.includes('linkedin.com/jobs/search-results');

}



function getJobSourceUrlCandidates(rawJob) {

  if (!rawJob) return [];

  return [

    rawJob.sourceUrl,

    rawJob.url,

    rawJob.job_url,

    rawJob.detail_url,

    rawJob.detailUrl,

    rawJob.applyUrl,

    rawJob.apply_url,

    rawJob.job_apply_link,

    rawJob.job_google_link,

    rawJob.job_posted_link,

    rawJob.job_offer_link,

    rawJob.aboutlink,

    rawJob.about_link,

    rawJob.link,

  ]

    .map((value) => cleanJobText(value))

    .filter(Boolean);

}



function getJobSourceLinkMeta(job) {

  const href = cleanJobText(job?.sourceUrl || '');

  if (!href) return null;

  return {

    href,

    label: isLinkedInSearchResultsUrl(href) ? 'Search LinkedIn roles' : 'Open original vacancy',

  };

}



function normalizeTitleCase(value) {

  return cleanJobText(value)

    .split(/\s+/)

    .filter(Boolean)

    .map((word) => word.length <= 3 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1))

    .join(' ');

}



function isGenericJobTitle(value) {

  const title = cleanJobText(value);

  if (!title) return true;

  const normalized = normalizeText(title);

  if (!normalized) return true;

  if (GENERIC_JOB_TITLE_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return true;

  if (normalized === 'company' || normalized === 'active') return true;

  if (title.length > 90) return true;

  const wordCount = title.split(/\s+/).filter(Boolean).length;

  if (wordCount > 8) return true;

  if (!JOB_ROLE_HINT_PATTERN.test(title)) return true;

  return false;

}



function extractJobTitleFromDescription(description) {

  const text = cleanJobText(description);

  if (!text) return '';

  const patterns = [

    /we are currently seeking an? ([A-Za-z0-9&\-\/(),+ ]{3,80}?)(?: to| who| for|\.)/i,

    /we are seeking an? ([A-Za-z0-9&\-\/(),+ ]{3,80}?)(?: to| who| for|\.)/i,

    /as an? ([A-Za-z0-9&\-\/(),+ ]{3,80}?)[, ]/i,

    /the ([A-Za-z0-9&\-\/(),+ ]{3,80}?) role is/i,

    /job description\s*[-:]?\s*([A-Za-z0-9&\-\/(),+ ]{3,80})/i,

  ];

  for (const pattern of patterns) {

    const match = text.match(pattern);

    if (match) {

      const candidate = cleanJobText(match[1]);

      if (candidate && !isGenericJobTitle(candidate) && JOB_ROLE_HINT_PATTERN.test(candidate)) return normalizeTitleCase(candidate);

    }

  }

  const sentences = text.split(/(?<=[.!?])\s+/).map((sentence) => cleanJobText(sentence));

  const fallback = sentences.find((sentence) => sentence.length <= 90 && JOB_ROLE_HINT_PATTERN.test(sentence) && !isGenericJobTitle(sentence));

  return fallback ? normalizeTitleCase(fallback) : '';

}



function extractJobSummaryFromDescription(description, fallbackTitle = '') {

  const text = cleanJobText(description);

  if (!text) return '';

  const sentences = text

    .split(/(?<=[.!?])\s+/)

    .map((sentence) => cleanJobText(sentence))

    .filter(Boolean);

  const filtered = sentences.filter((sentence) => {

    const normalized = normalizeText(sentence);

    if (!normalized) return false;

    if (fallbackTitle && normalized === normalizeText(fallbackTitle)) return false;

    if (GENERIC_JOB_TITLE_PREFIXES.some((prefix) => normalized.startsWith(prefix))) return false;

    return sentence.length >= 40;

  });

  return cleanJobText(filtered.slice(0, 2).join(' ')).slice(0, 420);

}



function extractJobRequirementsFromDescription(description) {

  const text = String(description || '');

  const sectionPatterns = [

    /qualifications?[:\s]+([\s\S]{40,500})/i,

    /requirements?[:\s]+([\s\S]{40,500})/i,

    /skills[,\s\w-]*[:\s]+([\s\S]{40,500})/i,

    /experience[:\s]+([\s\S]{40,500})/i,

  ];

  for (const pattern of sectionPatterns) {

    const match = text.match(pattern);

    if (match) return cleanJobText(match[1]).slice(0, 420);

  }

  return extractJobSummaryFromDescription(description).slice(0, 420);

}



function sanitizeJobRecord(rawJob, fallbackCompanyName = '') {

  if (!rawJob) return null;

  const sourceLabel = normalizeText(rawJob.sourceLabel || '');

  const isLinkedInExtensionJob = sourceLabel === 'linkedin extension';
  const isManualJob = sourceLabel === 'manual job' || sourceLabel === 'manual';

  const rawTitle = cleanJobText(rawJob.title || rawJob.role || rawJob.job_title || '');

  const rawDescription = cleanJobText(rawJob.summary || rawJob.description || rawJob.primary_description || rawJob.requirements || rawJob.insight || rawJob.notes || '');

  const companyName = cleanJobText(rawJob.companyName || rawJob.company_name || rawJob.company || rawJob.organization || rawJob.primary_description || fallbackCompanyName || (isManualJob ? 'Client' : ''));

  const title = isManualJob
    ? (rawTitle || 'Untitled role')
    : (isLinkedInExtensionJob ? (rawTitle || extractJobTitleFromDescription(rawDescription)) : (!isGenericJobTitle(rawTitle) ? rawTitle : extractJobTitleFromDescription(rawDescription)));

  if (!title || (!isManualJob && !isLinkedInExtensionJob && isGenericJobTitle(title))) return null;



  const location = cleanJobText(rawJob.location || rawJob.city || 'Remote') || 'Remote';

  const type = cleanJobText(rawJob.type || rawJob.employment_type || 'Full-time') || 'Full-time';

  const salary = cleanJobText(rawJob.salary || 'Compensation on request') || 'Compensation on request';

  const linkedInJobId = cleanJobText(rawJob.linkedInJobId || rawJob.jobId || rawJob.job_id || '');

  const normalizedCompany = normalizeText(companyName);

  const normalizedStatus = normalizeText(rawJob.status || rawJob.job_state || 'active');

  const linkedInFallbackSummary = title + ' opportunity at ' + (companyName || 'this company') + ' in ' + location + '. Imported from LinkedIn extension.';

  const manualFallbackSummary = `${title} role at ${companyName || 'the client'} in ${location}.`;

  const summary = cleanJobText(rawJob.summary || extractJobSummaryFromDescription(rawDescription, title) || (isLinkedInExtensionJob ? linkedInFallbackSummary : '') || (isManualJob ? manualFallbackSummary : ''));

  const requirements = cleanJobText(rawJob.requirements || extractJobRequirementsFromDescription(rawDescription) || summary || (isLinkedInExtensionJob ? (type + ' | ' + location) : '') || (isManualJob ? summary : ''));

  const sourceUrlCandidates = getJobSourceUrlCandidates(rawJob);

  const directSourceUrl = sourceUrlCandidates.find((url) => !isLinkedInSearchResultsUrl(url)) || '';

  const linkedInViewUrl = linkedInJobId ? `https://www.linkedin.com/jobs/view/${encodeURIComponent(linkedInJobId)}/` : '';

  const sourceUrl = directSourceUrl

    || linkedInViewUrl

    || sourceUrlCandidates[0]

    || (isLinkedInExtensionJob ? ('https://www.linkedin.com/jobs/search/?keywords=' + encodeURIComponent([title, companyName].filter(Boolean).join(' '))) : '');



  if (!isManualJob && (!companyName || ['company', 'unknown company'].includes(normalizedCompany))) return null;

  if (!isLinkedInExtensionJob && !isManualJob && (!summary || summary.length < 30)) return null;

  if (!isLinkedInExtensionJob && !isManualJob && (companyName.length > 60 || companyName.split(/\s+/).filter(Boolean).length > 6)) return null;

  if (!isLinkedInExtensionJob && !isManualJob && (COMPANY_GARBAGE_PATTERN.test(companyName) || JOB_ROLE_HINT_PATTERN.test(companyName))) return null;



  return {

    ...rawJob,

    title: normalizeTitleCase(title),

    companyName,

    summary: (summary || linkedInFallbackSummary).slice(0, 420),

    requirements: (requirements || summary || linkedInFallbackSummary).slice(0, 420),

    location,

    status: JOB_STATUS_MAP[normalizedStatus] || 'Active',

    type,

    salary,

    clientContact: cleanJobText(rawJob.clientContact || rawJob.hiring_contact || rawJob.client_contact || rawJob.poster_id || ''),

    notes: cleanJobText(rawJob.notes || rawJob.insight || ''),

    sourceUrl,

  };

}

function shouldBackfillLinkedInImportedJob(job) {

  const sourceLabel = normalizeText(job?.sourceLabel || '');

  if (sourceLabel !== 'linkedin extension') return false;

  const summary = cleanJobText(job?.summary || '');

  const requirements = cleanJobText(job?.requirements || '');

  const sourceUrl = cleanJobText(job?.sourceUrl || '');
  const requirementsSource = normalizeText(job?.requirementsSource || '');
  const applicationUrl = cleanJobText(job?.applyUrl || job?.apply_url || job?.job_apply_link || '');
  return !sourceUrl

    || !summary

    || summary.length < 30

    || /^imported from linkedin extension for /i.test(summary)

    || / role imported from linkedin extension\.$/i.test(summary)

    || !requirements
    || requirements.length < 30
    || (Boolean(applicationUrl) && !['application html', 'application rendered'].includes(requirementsSource));

}

function getJobDeduplicationKeys(job) {

  const keys = [];

  const linkedInJobId = normalizeText(job?.linkedInJobId || job?.jobId || job?.job_id || '');

  const sourceUrl = cleanJobText(job?.sourceUrl || '');

  const normalizedSourceUrl = sourceUrl.toLowerCase();

  const compositeKey = [

    normalizeText(job?.title || ''),

    normalizeText(getJobCompanyName(job) || job?.companyName || ''),

    normalizeText(job?.location || ''),

  ].filter(Boolean).join('|');



  if (linkedInJobId) keys.push('linkedin:' + linkedInJobId);

  if (sourceUrl && !isLinkedInSearchResultsUrl(sourceUrl)) keys.push('source:' + normalizedSourceUrl);

  if (compositeKey) keys.push('composite:' + compositeKey);



  return keys;

}

function getJobRecordQualityScore(job) {

  const summaryLength = cleanJobText(job?.summary || '').length;

  const requirementsLength = cleanJobText(job?.requirements || '').length;

  const notesLength = cleanJobText(job?.notes || '').length;



  return (

    summaryLength +

    requirementsLength +

    Math.min(notesLength, 120) +

    (job?.sourceUrl ? 120 : 0) +

    (job?.linkedInJobId ? 90 : 0) +

    (job?.clientContact ? 40 : 0) +

    (job?.salary ? 25 : 0) +

    (job?.department ? 20 : 0)

  );

}



function isPreferredJobRecord(candidateJob, currentJob) {

  const candidateScore = getJobRecordQualityScore(candidateJob);

  const currentScore = getJobRecordQualityScore(currentJob);

  if (candidateScore !== currentScore) return candidateScore > currentScore;



  const candidateCreatedAt = new Date(candidateJob?.createdAtIso || 0).getTime();

  const currentCreatedAt = new Date(currentJob?.createdAtIso || 0).getTime();

  if (candidateCreatedAt !== currentCreatedAt) return candidateCreatedAt > currentCreatedAt;



  return String(candidateJob?.id || '').localeCompare(String(currentJob?.id || '')) < 0;

}



function sanitizeJobPortfolio(jobPortfolio, fallbackCompanyName = '') {

  const sanitizedJobs = (Array.isArray(jobPortfolio) ? jobPortfolio : [])

    .map((job) => sanitizeJobRecord(job, fallbackCompanyName))

    .filter(Boolean);



  const dedupedJobs = [];

  const keyToIndex = new Map();



  sanitizedJobs.forEach((job) => {

    const dedupeKeys = getJobDeduplicationKeys(job);

    const matchingIndex = dedupeKeys

      .map((key) => keyToIndex.get(key))

      .find((index) => Number.isInteger(index));



    if (!Number.isInteger(matchingIndex)) {

      const nextIndex = dedupedJobs.length;

      dedupedJobs.push(job);

      dedupeKeys.forEach((key) => keyToIndex.set(key, nextIndex));

      return;

    }



    const existingJob = dedupedJobs[matchingIndex];

    const preferredJob = isPreferredJobRecord(job, existingJob) ? job : existingJob;

    dedupedJobs[matchingIndex] = preferredJob;

    getJobDeduplicationKeys(preferredJob).forEach((key) => keyToIndex.set(key, matchingIndex));

  });



  return dedupedJobs;

}



function serializeJobPortfolioForComparison(jobPortfolio) {

  return JSON.stringify((Array.isArray(jobPortfolio) ? jobPortfolio : []).map((job) => ({

    id: String(job?.id || ''),

    title: String(job?.title || ''),

    companyName: String(getJobCompanyName(job) || ''),

    summary: String(job?.summary || ''),

    requirements: String(job?.requirements || ''),

    status: String(job?.status || ''),

    location: String(job?.location || ''),

    type: String(job?.type || ''),

    sourceUrl: String(job?.sourceUrl || ''),

  })));

}



function createProspectQueryFromJob(job) {

  if (!job) return '';

  const requirementTokens = uniqueWords(job.requirements || '').slice(0, 6);

  const titleTerms = uniqueWords(job.title || '').slice(0, 4);

  const positiveTerms = Array.from(new Set([...titleTerms, ...requirementTokens])).filter(Boolean);

  const primaryTerms = positiveTerms.slice(0, 5).map((term) => `"${term}"`).join(' AND ');

  return [job.title ? `"${job.title}"` : '', primaryTerms].filter(Boolean).join(' AND ');

}



function createBooleanQueryFromJob(job) {

  if (!job) return '';

  return [createProspectQueryFromJob(job), getJobCompanyName(job) ? `"${getJobCompanyName(job)}"` : ''].filter(Boolean).join(' AND ');

}



function buildRecruiterCompanyMessage(listing, recruiterName, agencyName, tone = 'formal') {

  if (!listing) return '';

  const company = listing.company || 'your team';

  const role = listing.title || 'this vacancy';

  const location = listing.location ? ` in ${listing.location}` : '';

  const introName = recruiterName || 'a recruiter';

  const agency = agencyName || 'Vision';

  if (tone === 'concise') {

    return `Hi ${company} team,\n\nI saw your ${role} opening${location}. We already have qualified candidates through ${agency} who could fit this role quickly.\n\nIf the vacancy is still open, I can send a shortlist immediately.\n\nBest,\n${introName}`;

  }

  if (tone === 'sales') {

    return `Hi ${company} team,\n\nI noticed your ${role} vacancy${location} and wanted to reach out because ${agency} can help you move faster on this hire. We already work with candidates who match openings like this, which means we can shorten the time your team spends sourcing and screening.\n\nIf this role is still active, I can send across a focused shortlist and take the first screening step off your hands.\n\nWould you be open to a quick call this week?\n\nBest,\n${introName}`;

  }

  return `Hi ${company} team,\n\nI came across your ${role} opening${location} and wanted to reach out directly. I run recruitment through ${agency}, and we already have qualified candidates in our network who align with roles like this.\n\nIf you are still hiring for this vacancy, I can send across a shortlist quickly and handle screening to save your team time.\n\nWould you be open to a quick conversation about how we can support this hire?\n\nBest,\n${introName}`;

}



function buildJobClientOutreachMessage(job, recruiterName, agencyName) {

  if (!job) return '';

  const companyName = getJobCompanyName(job) || 'your team';

  const role = job.title || 'this vacancy';

  const location = job.location ? ` in ${job.location}` : '';

  const recruiter = recruiterName || 'a recruiter';

  const agency = agencyName || 'Vision';

  return `Hi ${companyName} team,



I'm ${recruiter} from ${agency}. I came across your ${role} vacancy${location} and wanted to reach out directly.



We support hiring teams by shortlisting qualified candidates quickly and handling the first layer of screening so the process moves faster without adding noise.



If this role is still open, I can share how we would approach the search and the type of candidates we can bring forward.



Best,

${recruiter}

${agency}`;

}



function createScrapedJobsCacheKey(userId, query, location) {

  return `vision-scraped-jobs:${userId}:${normalizeText(query)}:${normalizeText(location)}`;

}



function readCachedScrapedJobs(userId, query, location) {

  if (typeof window === 'undefined' || !userId) return null;

  try {

    const raw = window.localStorage.getItem(createScrapedJobsCacheKey(userId, query, location));

    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (!parsed || !Array.isArray(parsed.listings) || !parsed.cachedAt) return null;

    const ageMs = Date.now() - Number(parsed.cachedAt);

    if (!Number.isFinite(ageMs) || ageMs > SCRAPED_JOBS_CACHE_TTL_MS) return null;

    return parsed;

  } catch {

    return null;

  }

}



function writeCachedScrapedJobs(userId, query, location, payload) {

  if (typeof window === 'undefined' || !userId) return;

  try {

    window.localStorage.setItem(

      createScrapedJobsCacheKey(userId, query, location),

      JSON.stringify({

        ...payload,

        cachedAt: Date.now(),

      })

    );

  } catch {}

}



function readScrapedJobsRateLimit(userId) {

  if (typeof window === 'undefined' || !userId) return 0;

  try {

    return Number(window.localStorage.getItem(`vision-scraped-jobs-rate-limit:${userId}`) || 0);

  } catch {

    return 0;

  }

}



function writeScrapedJobsRateLimit(userId) {

  if (typeof window === 'undefined' || !userId) return;

  try {

    window.localStorage.setItem(`vision-scraped-jobs-rate-limit:${userId}`, String(Date.now()));

  } catch {}

}



function parseCsvLine(line) {

  const values = [];

  let current = '';

  let inQuotes = false;

  for (let index = 0; index < line.length; index += 1) {

    const character = line[index];

    const nextCharacter = line[index + 1];

    if (character === '"') {

      if (inQuotes && nextCharacter === '"') {

        current += '"';

        index += 1;

      } else {

        inQuotes = !inQuotes;

      }

      continue;

    }

    if (character === ',' && !inQuotes) {

      values.push(current);

      current = '';

      continue;

    }

    current += character;

  }

  values.push(current);

  return values.map((value) => value.trim());

}



function parseCsvText(text) {

  const rows = [];

  const input = String(text || '');

  let currentValue = '';

  let currentRow = [];

  let inQuotes = false;



  for (let index = 0; index < input.length; index += 1) {

    const character = input[index];

    const nextCharacter = input[index + 1];



    if (character === '"') {

      if (inQuotes && nextCharacter === '"') {

        currentValue += '"';

        index += 1;

      } else {

        inQuotes = !inQuotes;

      }

      continue;

    }



    if (character === ',' && !inQuotes) {

      currentRow.push(currentValue.trim());

      currentValue = '';

      continue;

    }



    if ((character === '\n' || character === '\r') && !inQuotes) {

      if (character === '\r' && nextCharacter === '\n') index += 1;

      if (currentValue.length || currentRow.length) {

        currentRow.push(currentValue.trim());

        rows.push(currentRow);

      }

      currentValue = '';

      currentRow = [];

      continue;

    }



    currentValue += character;

  }



  if (currentValue.length || currentRow.length) {

    currentRow.push(currentValue.trim());

    rows.push(currentRow);

  }



  if (!rows.length) return [];

  const headers = rows[0].map((header) => normalizeText(header).replace(/\s+/g, '_'));

  return rows.slice(1)

    .filter((row) => row.some((value) => String(value || '').trim()))

    .map((row) => headers.reduce((record, header, index) => {

      record[header] = row[index] || '';

      return record;

    }, {}));

}



function escapeCsvValue(value) {

  const normalized = String(value ?? '');

  if (/[",\n]/.test(normalized)) {

    return `"${normalized.replace(/"/g, '""')}"`;

  }

  return normalized;

}



function downloadCsv(filename, rows) {

  if (typeof window === 'undefined' || !rows?.length) return;

  const csvText = rows.map((row) => row.map((value) => escapeCsvValue(value)).join(',')).join('\n');

  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });

  const url = window.URL.createObjectURL(blob);

  const anchor = document.createElement('a');

  anchor.href = url;

  anchor.download = filename;

  anchor.click();

  window.URL.revokeObjectURL(url);

}

function normalizeMigrationKey(value) {
  return normalizeText(value).replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function getMigrationValue(row = {}, aliases = []) {
  const normalizedRow = Object.fromEntries(Object.entries(row || {}).map(([key, value]) => [normalizeMigrationKey(key), value]));
  const match = aliases.map(normalizeMigrationKey).find((alias) => Object.prototype.hasOwnProperty.call(normalizedRow, alias));
  return match ? String(normalizedRow[match] || '').trim() : '';
}

function getMigrationPreset(sourceId = 'generic') {
  return MIGRATION_SOURCE_PRESETS[sourceId] || MIGRATION_SOURCE_PRESETS.generic;
}

function inferMigrationFieldMap(rows = [], sourceId = 'generic') {
  const preset = getMigrationPreset(sourceId);
  const headers = Array.from(new Set(rows.flatMap((row) => Object.keys(row || {}).map(normalizeMigrationKey))));
  return Object.fromEntries(Object.entries(preset.fieldAliases).map(([field, aliases]) => {
    const match = aliases.map(normalizeMigrationKey).find((alias) => headers.includes(alias)) || '';
    return [field, match];
  }));
}

function normalizeMigrationStage(rawStage = '', sourceId = 'generic') {
  const normalized = normalizeMigrationKey(rawStage);
  const preset = getMigrationPreset(sourceId);
  const mapped = preset.stageMap?.[normalized] || preset.stageMap?.[normalizeText(rawStage).replace(/\s+/g, '_')];
  return normalizePipelineStage(mapped || rawStage);
}

function buildMigrationPreview(rows = [], sourceId = 'generic', entity = 'candidates', currentData = {}) {
  const preset = getMigrationPreset(sourceId);
  const aliases = preset.fieldAliases || {};
  const fieldMap = inferMigrationFieldMap(rows, sourceId);
  const nowIso = new Date().toISOString();
  const records = rows.map((row, index) => {
    const legacyId = getMigrationValue(row, aliases.legacyId) || `${sourceId}-${index + 1}`;
    const candidateName = getMigrationValue(row, aliases.candidateName);
    const email = extractEmailFromText(getMigrationValue(row, aliases.email)) || getMigrationValue(row, aliases.email);
    const phone = getMigrationValue(row, aliases.phone);
    const role = getMigrationValue(row, aliases.role) || getMigrationValue(row, aliases.jobTitle);
    const company = getMigrationValue(row, aliases.company);
    const stageRaw = getMigrationValue(row, aliases.stage);
    const stage = normalizeMigrationStage(stageRaw, sourceId);
    const jobTitle = getMigrationValue(row, aliases.jobTitle) || role;
    const contactName = getMigrationValue(row, aliases.contactName);
    const contactEmail = extractEmailFromText(getMigrationValue(row, aliases.contactEmail)) || getMigrationValue(row, aliases.contactEmail);
    const fee = getMigrationValue(row, aliases.fee);
    const base = {
      legacyId,
      legacySource: preset.label,
      migrationSource: sourceId,
      migrationRow: index + 1,
      legacyStage: stageRaw,
      stage,
      candidateName,
      email,
      phone,
      role,
      company,
      jobTitle,
      contactName,
      contactEmail,
      fee,
      raw: row,
    };
    if (entity === 'jobs') return { ...base, title: jobTitle, summary: row.summary || row.description || row.notes || `${jobTitle || 'Role'} imported from ${preset.label}.` };
    if (entity === 'companies') return { ...base, name: company || row.name || row.company_name || '' };
    if (entity === 'contacts') return { ...base, name: contactName || candidateName || row.name || '', companyName: company };
    if (entity === 'placements') return { ...base, candidateName: candidateName || row.candidate || '', clientName: company, placementFee: Number(String(fee).replace(/[^0-9.-]/g, '')) || 0 };
    return { ...base, name: candidateName, submittedAt: row.submitted_at || row.created_at || nowIso };
  }).filter((record) => {
    if (entity === 'jobs') return record.title && record.company;
    if (entity === 'companies') return record.name || record.company;
    if (entity === 'contacts') return record.name || record.email;
    if (entity === 'placements') return record.candidateName || record.clientName || record.placementFee;
    return record.name && (record.role || record.email);
  });

  const duplicateEmails = new Set((currentData.candidates || []).map((candidate) => normalizeText(candidate.email)).filter(Boolean));
  const duplicateCompanies = new Set((currentData.companies || []).map((company) => normalizeText(company.name || company.companyName)).filter(Boolean));
  const missing = {
    email: records.filter((record) => entity === 'candidates' && !record.email).length,
    company: records.filter((record) => ['jobs', 'contacts', 'placements', 'candidates'].includes(entity) && !record.company && !record.companyName && !record.clientName).length,
    stage: records.filter((record) => entity === 'candidates' && !record.stage).length,
    fee: records.filter((record) => entity === 'placements' && !record.placementFee).length,
  };
  const duplicates = records.filter((record) => (record.email && duplicateEmails.has(normalizeText(record.email))) || ((record.company || record.name) && duplicateCompanies.has(normalizeText(record.company || record.name)))).length;
  const exactDuplicates = records.filter((record) => record.email && duplicateEmails.has(normalizeText(record.email))).length;
  const likelyDuplicates = records.filter((record) => !record.email && record.name && (currentData.candidates || []).some((candidate) => normalizeText(candidate.name) === normalizeText(record.name) && normalizeText(candidate.company || candidate.matchedJobTitle) === normalizeText(record.company || record.role))).length;
  const attachmentFields = ['resume_url', 'cv_url', 'file_url', 'attachment_url', 'resume_file', 'cv_file', 'file_name', 'filename'];
  const attachmentRows = records.filter((record) => attachmentFields.some((field) => String(record.raw?.[field] || '').trim()));
  const attachmentRecovered = attachmentRows.filter((record) => String(record.raw?.resume_url || record.raw?.cv_url || record.raw?.file_url || record.raw?.attachment_url || '').trim()).length;
  const attachmentNeedsReview = Math.max(0, attachmentRows.length - attachmentRecovered);
  const mappedFields = Object.values(fieldMap).filter(Boolean).length;
  const fieldReadiness = Math.min(100, Math.round((mappedFields / Math.max(1, Object.keys(aliases).length)) * 100));
  const duplicateRisk = records.length ? Math.min(100, Math.round(((exactDuplicates + likelyDuplicates * 0.7) / records.length) * 100)) : 0;
  const attachmentReadiness = attachmentRows.length ? Math.round((attachmentRecovered / attachmentRows.length) * 100) : 100;
  const relationshipReadiness = records.length ? Math.max(0, 100 - Math.round(((missing.company + missing.stage + missing.fee) / records.length) * 22)) : 0;
  const readiness = Math.max(35, Math.min(99, Math.round(
    fieldReadiness * 0.34
    + relationshipReadiness * 0.28
    + attachmentReadiness * 0.18
    + (100 - duplicateRisk) * 0.2
  )));
  const blockers = [
    !records.length ? 'No importable rows detected.' : '',
    entity === 'candidates' && missing.email > Math.max(3, records.length * 0.35) ? 'Too many candidates are missing email addresses.' : '',
    entity === 'jobs' && missing.company > 0 ? 'Some jobs are missing client companies.' : '',
    entity === 'placements' && missing.fee > 0 ? 'Some placements are missing fee values.' : '',
  ].filter(Boolean);

  return {
    sourceId,
    sourceLabel: preset.label,
    entity,
    rowsTotal: rows.length,
    records,
    fieldMap,
    missing,
    duplicates,
    duplicateBreakdown: {
      exact: exactDuplicates,
      likely: likelyDuplicates,
      possible: Math.max(0, duplicates - exactDuplicates - likelyDuplicates),
    },
    attachmentRecovery: {
      detected: attachmentRows.length,
      recovered: attachmentRecovered,
      needsReview: attachmentNeedsReview,
      rate: attachmentReadiness,
    },
    readinessBreakdown: {
      fieldMapping: fieldReadiness,
      relationships: relationshipReadiness,
      attachments: attachmentReadiness,
      duplicateSafety: Math.max(0, 100 - duplicateRisk),
    },
    readiness,
    blockers,
    warnings: [
      duplicates ? `${duplicates} possible duplicate record${duplicates === 1 ? '' : 's'} will be merged or preserved with legacy IDs.` : '',
      missing.company ? `${missing.company} row${missing.company === 1 ? '' : 's'} missing company context.` : '',
      missing.email ? `${missing.email} candidate${missing.email === 1 ? '' : 's'} missing email.` : '',
    ].filter(Boolean),
  };
}

function getCurrencySymbol(currencyCode = 'ZAR') {
  return PLACEMENT_CURRENCIES.find((currency) => currency.code === currencyCode)?.symbol || currencyCode || 'R';
}

function formatMoney(value, currencyCode = 'ZAR') {
  const symbol = getCurrencySymbol(currencyCode);
  const amount = Number(value || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  return `${symbol} ${amount}`;
}

function summarizeMoneyByCurrency(items, field) {
  const totals = {};
  items.forEach((item) => {
    const currency = item.currency || 'ZAR';
    totals[currency] = (totals[currency] || 0) + Number(item[field] || 0);
  });
  const entries = Object.entries(totals).filter(([, amount]) => amount > 0);
  if (!entries.length) return formatMoney(0, 'ZAR');
  return entries.map(([currency, amount]) => formatMoney(amount, currency)).join(' · ');
}

function buildPlacementRevenueCsvRows(placements = []) {
  return [
    ['Invoice #', 'Candidate', 'Client', 'Contact', 'Role', 'Currency', 'Fee ex tax', 'Tax %', 'Tax amount', 'Total inc tax', 'Margin', 'Commission %', 'Invoice date', 'Due date', 'Invoice status', 'Payment status', 'Billed by'],
    ...placements.map((placement) => [
      placement.invoiceNumber || '',
      placement.candidateName || '',
      placement.clientName || '',
      placement.contactName || '',
      placement.jobTitle || '',
      placement.currency || 'ZAR',
      Number(placement.placementFee || 0),
      Number(placement.taxPercent || 0),
      Number(placement.taxAmount || 0),
      Number(placement.invoiceTotal || placement.placementFee || 0),
      Number(placement.margin || 0),
      Number(placement.commissionSplit || 0),
      placement.invoiceDate || '',
      placement.invoiceDueDate || '',
      placement.invoiceStatus || '',
      placement.paymentStatus || '',
      placement.billedBy || '',
    ]),
  ];
}

function createApplicationRecordId(candidateId, jobId, companyName = '') {
  return `app-${slugifyKey(jobId || companyName || 'job', 'job')}-${slugifyKey(candidateId || 'candidate', 'candidate')}`;
}

function getApplicationNextAction(application = {}) {
  const stage = String(application.stage || '').toLowerCase();
  if (!application.cvUrl && !application.resumeUrl) return 'Attach or parse CV';
  if (application.screeningStatus !== 'completed' && ['applied', 'screening'].includes(stage)) return 'Complete screening';
  if (!application.submittedAtIso && ['screening', 'interview', 'offer', 'hired'].includes(stage)) return 'Submit to client';
  if (application.submittedAtIso && !application.feedback) return 'Chase client feedback';
  if (stage === 'offer') return 'Confirm offer outcome';
  if (stage === 'hired' && Number(application.revenue || 0) <= 0) return 'Log placement revenue';
  return 'Keep warm';
}

function getApplicationRisk(application = {}) {
  const lastMs = new Date(application.updatedAtIso || application.submittedAtIso || application.createdAtIso || 0).getTime();
  const ageDays = lastMs ? Math.floor((Date.now() - lastMs) / 86400000) : 0;
  if (ageDays >= 7 && !['hired', 'rejected'].includes(String(application.stage || '').toLowerCase())) return { label: 'At risk', tone: 'rose' };
  if (application.screeningStatus !== 'completed' && ['screening', 'applied'].includes(String(application.stage || '').toLowerCase())) return { label: 'Needs screening', tone: 'amber' };
  if (application.submittedAtIso && !application.feedback) return { label: 'Waiting client', tone: 'cyan' };
  return { label: 'Healthy', tone: 'emerald' };
}

function buildApplicationRecordFromCandidate(candidate, jobs = [], screeningResponses = {}) {
  const matchedJob = jobs.find((job) => job.id === candidate.matchedJobId)
    || jobs.find((job) => normalizeText(job.title) === normalizeText(candidate.matchedJobTitle) && normalizeText(getJobCompanyName(job)) === normalizeText(candidate.company));
  const companyName = candidate.company || (matchedJob ? getJobCompanyName(matchedJob) : '');
  const jobId = candidate.matchedJobId || matchedJob?.id || '';
  if (!candidate?.id || (!jobId && !candidate.matchedJobTitle && !companyName)) return null;
  const latestSubmittal = Array.isArray(candidate.submittals) && candidate.submittals.length > 0 ? candidate.submittals[candidate.submittals.length - 1] : null;
  const screeningDone = Boolean(screeningResponses[candidate.id]?.answers?.length || candidate.screeningSubmittedAt);
  const cvUrl = candidate.cvUrl || candidate.resumeUrl || candidate.publicCvUrl || '';
  const createdAtIso = candidate.submittedAt || candidate.createdAtIso || candidate.createdAt || new Date().toISOString();
  const updatedAtIso = candidate.lastActivityAt || candidate.updatedAtIso || candidate.screeningSubmittedAt || createdAtIso;
  return {
    id: createApplicationRecordId(candidate.id, jobId || candidate.matchedJobTitle, companyName),
    candidateId: candidate.id,
    candidateName: candidate.name || 'Candidate',
    candidateEmail: candidate.email || '',
    jobId,
    jobTitle: candidate.matchedJobTitle || matchedJob?.title || candidate.role || '',
    companyName,
    contactId: latestSubmittal?.contactId || '',
    contactName: latestSubmittal?.contactName || matchedJob?.clientContact || '',
    source: candidate.source || 'pipeline',
    stage: candidate.status || 'applied',
    screeningStatus: screeningDone ? 'completed' : 'pending',
    screeningScore: Number(screeningResponses[candidate.id]?.score || candidate.screeningScore || 0),
    matchScore: Number(candidate.rating || candidate.matchScore || 0),
    cvUrl,
    submittedAtIso: latestSubmittal?.submittedAt || latestSubmittal?.createdAt || '',
    feedback: latestSubmittal?.feedback || '',
    outcome: latestSubmittal?.outcome || '',
    interviewDate: candidate.interviewDate || '',
    offerStatus: candidate.offerStatus || '',
    revenue: Number(candidate.placementFee || 0),
    createdAtIso,
    updatedAtIso,
  };
}

function normalizeClientOpportunityStage(stage) {
  const raw = String(stage || 'Lead').trim();
  return LEGACY_CLIENT_OPPORTUNITY_STAGE_MAP[raw] || (CLIENT_OPPORTUNITY_STAGES.includes(raw) ? raw : 'Lead');
}

function getOpportunityNextAction(opportunity = {}, account = null) {
  const stage = normalizeClientOpportunityStage(opportunity.stage);
  const lastTouchMs = new Date(opportunity.lastTouchAt || opportunity.updatedAtIso || opportunity.createdAtIso || 0).getTime();
  const daysSinceTouch = lastTouchMs ? Math.floor((Date.now() - lastTouchMs) / 86400000) : 999;
  if (stage === 'Lead') return 'Qualify hiring need';
  if (stage === 'Qualified') return 'Book discovery call';
  if (stage === 'Discovery') return 'Send proposal';
  if (stage === 'Proposal' && daysSinceTouch >= 5) return 'Chase proposal response';
  if (stage === 'Proposal') return 'Confirm decision date';
  if (stage === 'Verbal Commit') return 'Move to Closed-Won';
  if (stage === 'Closed-Won' && !opportunity.jobId) return 'Generate job order';
  if (stage === 'Closed-Lost') return 'Log loss reason';
  if (account?.riskLevel && ['At risk', 'Critical'].includes(account.riskLevel)) return 'Re-engage account owner';
  return 'Maintain momentum';
}

function getOpportunityForecastMonth(opportunity = {}) {
  const date = new Date(opportunity.expectedCloseDate || opportunity.closeDate || opportunity.expiryDate || opportunity.updatedAtIso || opportunity.createdAtIso || Date.now());
  if (Number.isNaN(date.getTime())) return 'Unscheduled';
  return date.toLocaleDateString([], { month: 'short', year: 'numeric' });
}

function contactIdentityMatches(contact = {}, value = '') {
  const haystack = normalizeText(value);
  if (!haystack) return false;
  const identityParts = [
    contact.id,
    contact.name,
    contact.email,
    contact.phone,
  ].map((part) => normalizeText(part)).filter(Boolean);
  return identityParts.some((part) => haystack === part || haystack.includes(part) || part.includes(haystack));
}

function contactCompanyMatches(contact = {}, companyName = '') {
  const contactCompany = normalizeText(contact.companyName || contact.company || contact.normalizedCompanyName);
  const targetCompany = normalizeText(companyName);
  if (!contactCompany || !targetCompany) return false;
  return contactCompany === targetCompany || contactCompany.includes(targetCompany) || targetCompany.includes(contactCompany);
}

function buildContactCommercialRollup(contact = {}, data = {}) {
  const opportunities = data.opportunities || [];
  const jobs = data.jobs || [];
  const submissions = data.submissions || [];
  const placements = data.placements || [];
  const touchpoints = data.touchpoints || [];
  const activityLog = data.activityLog || [];
  const candidates = data.candidates || [];
  const contactCompany = contact.companyName || contact.company || '';
  const contactNameOrEmail = contact.email || contact.name || '';

  const linkedOpportunities = opportunities.filter((opportunity) => (
    String(opportunity.contactId || '') === String(contact.id || '')
    || contactIdentityMatches(contact, opportunity.contactEmail)
    || contactIdentityMatches(contact, opportunity.contactName)
    || (contactCompanyMatches(contact, opportunity.companyName) && contactIdentityMatches(contact, `${opportunity.contactName || ''} ${opportunity.contactEmail || ''}`))
  ));

  const linkedJobs = jobs.filter((job) => (
    contactCompanyMatches(contact, getJobCompanyName(job))
    && (!job.clientContact || contactIdentityMatches(contact, job.clientContact) || contactIdentityMatches(contact, job.hiringContact))
  ));

  const linkedSubmissions = submissions.filter((submission) => (
    String(submission.contactId || '') === String(contact.id || '')
    || contactIdentityMatches(contact, submission.contactEmail)
    || contactIdentityMatches(contact, submission.contactName)
    || (contactCompanyMatches(contact, submission.companyName) && contactIdentityMatches(contact, `${submission.contactName || ''} ${submission.contactEmail || ''}`))
  ));

  const syntheticSubmissions = candidates
    .filter((candidate) => contactCompanyMatches(contact, candidate.company))
    .filter((candidate) => candidate.matchedJobId || candidate.matchedJobTitle || String(candidate.status || '').toLowerCase() === 'hired')
    .map((candidate) => ({
      id: `candidate-${candidate.id}`,
      candidateId: candidate.id,
      candidateName: candidate.name || 'Candidate',
      jobTitle: candidate.matchedJobTitle || candidate.role || '',
      companyName: candidate.company || contactCompany,
      stage: candidate.status || 'pipeline',
      submittedAtIso: candidate.submittedAt || candidate.createdAtIso || candidate.lastActivityAt || '',
      source: 'candidate_pipeline',
    }));

  const submissionRecords = Array.from(new Map([...linkedSubmissions, ...syntheticSubmissions].map((submission) => [submission.id || `${submission.candidateId}-${submission.jobId || submission.jobTitle}`, submission])).values());

  const linkedPlacements = placements.filter((placement) => (
    String(placement.contactId || '') === String(contact.id || '')
    || contactIdentityMatches(contact, placement.contactEmail)
    || contactIdentityMatches(contact, placement.contactName)
    || (contactCompanyMatches(contact, placement.clientName || placement.companyName) && contactIdentityMatches(contact, `${placement.contactName || ''} ${placement.contactEmail || ''}`))
  ));

  const linkedTouchpoints = touchpoints.filter((touchpoint) => (
    contactCompanyMatches(contact, touchpoint.companyName)
    && (!touchpoint.contactId || String(touchpoint.contactId) === String(contact.id || '') || contactIdentityMatches(contact, touchpoint.contactName || touchpoint.contactEmail || touchpoint.summary))
  ));

  const linkedActivity = activityLog.filter((activity) => {
    const meta = activity.metadata || {};
    return String(meta.contactId || '') === String(contact.id || '')
      || contactIdentityMatches(contact, meta.contactEmail || meta.contact || meta.contactName)
      || contactIdentityMatches(contact, activity.summary || activity.message || '')
      || (contactCompanyMatches(contact, meta.company || meta.clientCompany || meta.companyName) && contactIdentityMatches(contact, activity.summary || activity.message || ''));
  });

  const openOpportunities = linkedOpportunities.filter((opportunity) => !['Closed-Won', 'Closed-Lost'].includes(normalizeClientOpportunityStage(opportunity.stage)));
  const wonOpportunities = linkedOpportunities.filter((opportunity) => normalizeClientOpportunityStage(opportunity.stage) === 'Closed-Won');
  const openWeightedPipeline = openOpportunities.reduce((sum, opportunity) => sum + Number(opportunity.weightedValue || 0), 0);
  const revenueByCurrency = linkedPlacements.reduce((accumulator, placement) => {
    const currency = placement.currency || 'ZAR';
    accumulator[currency] = (accumulator[currency] || 0) + Number(placement.invoiceTotal || placement.placementFee || 0);
    return accumulator;
  }, {});
  const totalRevenue = linkedPlacements.reduce((sum, placement) => sum + Number(placement.invoiceTotal || placement.placementFee || 0), 0);
  const unpaidInvoices = linkedPlacements.filter((placement) => !['paid', 'settled'].includes(String(placement.paymentStatus || placement.invoiceStatus || '').toLowerCase()));
  const latestTouchMs = Math.max(
    0,
    ...linkedTouchpoints.map((item) => new Date(item.createdAtIso || item.updatedAtIso || 0).getTime()),
    ...linkedActivity.map((item) => new Date(item.createdAtIso || item.createdAt || item.updatedAtIso || 0).getTime()),
    new Date(contact.lastInteractionAtIso || contact.updatedAtIso || contact.createdAtIso || 0).getTime() || 0
  );
  const daysSinceTouch = latestTouchMs ? Math.floor((Date.now() - latestTouchMs) / 86400000) : 999;
  const awaitingFeedback = submissionRecords.filter((submission) => !submission.feedback && !['hired', 'rejected', 'declined'].includes(String(submission.stage || '').toLowerCase()));
  const activeJobs = linkedJobs.filter((job) => !['closed', 'removed'].includes(String(job.status || '').toLowerCase()));

  let health = 'Healthy';
  if (daysSinceTouch >= 45 || String(contact.sentiment || '').toLowerCase() === 'ghosted') health = 'At risk';
  if (daysSinceTouch >= 30 && (totalRevenue > 0 || openWeightedPipeline > 0)) health = 'Revenue risk';
  if (awaitingFeedback.length > 0) health = 'Needs feedback';
  if (daysSinceTouch <= 14 && (openOpportunities.length > 0 || activeJobs.length > 0)) health = 'Active';

  let nextAction = 'Maintain relationship';
  if (awaitingFeedback.length > 0) nextAction = `Chase feedback for ${awaitingFeedback.length} candidate${awaitingFeedback.length === 1 ? '' : 's'}`;
  else if (openOpportunities.some((opportunity) => normalizeClientOpportunityStage(opportunity.stage) === 'Proposal')) nextAction = 'Chase proposal response';
  else if (activeJobs.length > 0 && submissionRecords.length === 0) nextAction = 'Submit matched candidates';
  else if (daysSinceTouch >= 30) nextAction = 'Re-engage this contact';
  else if (openOpportunities.length > 0) nextAction = getOpportunityNextAction(openOpportunities[0]);
  else if (totalRevenue > 0) nextAction = 'Protect revenue with check-in';

  const labels = [];
  if (totalRevenue > 0) labels.push('Revenue contact');
  if (openWeightedPipeline > 0) labels.push('Open pipeline');
  if (submissionRecords.length > 0) labels.push('Submission owner');
  if (wonOpportunities.length > 0) labels.push('Closed-won buyer');
  if (awaitingFeedback.length > 0) labels.push('Feedback blocker');
  if (daysSinceTouch >= 30) labels.push('Re-engage');
  if (String(contact.role || '').toLowerCase().includes('manager') || String(contact.role || '').toLowerCase().includes('director') || String(contact.role || '').toLowerCase().includes('head')) labels.push('Decision maker');

  const timeline = [
    ...linkedTouchpoints.map((item) => ({ id: `touch-${item.id}`, type: item.type || 'touchpoint', title: item.summary || item.type || 'Touchpoint', iso: item.createdAtIso || item.updatedAtIso })),
    ...linkedActivity.map((item) => ({ id: `activity-${item.id}`, type: item.type || 'activity', title: item.summary || item.message || item.type || 'Activity', iso: item.createdAtIso || item.createdAt || item.updatedAtIso })),
    ...submissionRecords.map((item) => ({ id: `submission-${item.id}`, type: 'submission', title: `${item.candidateName || 'Candidate'} submitted${item.jobTitle ? ` for ${item.jobTitle}` : ''}`, iso: item.submittedAtIso || item.createdAtIso })),
    ...linkedPlacements.map((item) => ({ id: `placement-${item.id}`, type: 'placement', title: `${item.candidateName || 'Candidate'} placed${item.jobTitle ? ` for ${item.jobTitle}` : ''}`, iso: item.invoiceDate || item.createdAtIso || item.placedAtIso })),
    ...linkedOpportunities.map((item) => ({ id: `opportunity-${item.id}`, type: 'opportunity', title: `${item.title || 'Opportunity'} - ${normalizeClientOpportunityStage(item.stage)}`, iso: item.updatedAtIso || item.createdAtIso })),
  ].filter((item) => item.iso || item.title).sort((left, right) => new Date(right.iso || 0) - new Date(left.iso || 0));

  return {
    contactId: contact.id,
    contactName: contact.name || contactNameOrEmail || 'Contact',
    companyName: contactCompany,
    health,
    nextAction,
    daysSinceTouch,
    lastTouchAtIso: latestTouchMs ? new Date(latestTouchMs).toISOString() : '',
    openOpportunities,
    wonOpportunities,
    opportunities: linkedOpportunities,
    jobs: linkedJobs,
    activeJobs,
    submissions: submissionRecords,
    placements: linkedPlacements,
    touchpoints: linkedTouchpoints,
    activity: linkedActivity,
    awaitingFeedback,
    labels,
    timeline,
    openWeightedPipeline,
    totalRevenue,
    revenueByCurrency,
    unpaidInvoices,
  };
}

function openPlacementInvoicePrint(placement, agencySettings = {}) {
  if (typeof window === 'undefined' || !placement) return;
  const currency = placement.currency || 'ZAR';
  const fee = Number(placement.placementFee || 0);
  const taxPercent = Number(placement.taxPercent || 0);
  const taxAmount = Number(placement.taxAmount || (fee * taxPercent / 100));
  const total = Number(placement.invoiceTotal || (fee + taxAmount));
  const invoiceNumber = placement.invoiceNumber || `INV-${String(placement.id || Date.now()).slice(-6)}`;
  const html = `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${invoiceNumber}</title>
  <style>
    body { font-family: Inter, Arial, sans-serif; color: #0f172a; margin: 0; padding: 40px; background: #f8fafc; }
    .invoice { max-width: 820px; margin: 0 auto; background: white; border: 1px solid #e2e8f0; border-radius: 18px; padding: 40px; }
    .top { display: flex; justify-content: space-between; gap: 32px; border-bottom: 1px solid #e2e8f0; padding-bottom: 28px; }
    h1 { margin: 0; font-size: 36px; letter-spacing: -0.04em; }
    .muted { color: #64748b; font-size: 13px; line-height: 1.6; }
    .block { margin-top: 28px; }
    table { width: 100%; border-collapse: collapse; margin-top: 18px; }
    th { text-align: left; color: #64748b; font-size: 11px; text-transform: uppercase; letter-spacing: 0.12em; border-bottom: 1px solid #e2e8f0; padding: 12px 0; }
    td { padding: 14px 0; border-bottom: 1px solid #f1f5f9; font-size: 14px; }
    .right { text-align: right; }
    .totals { margin-left: auto; width: 320px; margin-top: 24px; }
    .totals div { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; }
    .grand { font-size: 22px !important; font-weight: 800; border-top: 1px solid #e2e8f0; margin-top: 8px; padding-top: 16px !important; }
    @media print { body { background: white; padding: 0; } .invoice { border: 0; border-radius: 0; } }
  </style>
</head>
<body>
  <main class="invoice">
    <section class="top">
      <div>
        <h1>Invoice</h1>
        <div class="muted">Placement fee invoice generated by Vision</div>
      </div>
      <div class="muted right">
        <strong>${invoiceNumber}</strong><br />
        Invoice date: ${placement.invoiceDate || '-'}<br />
        Due date: ${placement.invoiceDueDate || '-'}<br />
        Status: ${placement.invoiceStatus || 'pending'}
      </div>
    </section>
    <section class="block muted">
      <strong>${agencySettings.agencyName || 'Recruitment Agency'}</strong><br />
      Billed by: ${placement.billedBy || '-'}<br /><br />
      Bill to: <strong>${placement.clientName || 'Client'}</strong><br />
      Contact: ${placement.contactName || '-'}
    </section>
    <table>
      <thead><tr><th>Description</th><th class="right">Amount</th></tr></thead>
      <tbody>
        <tr>
          <td>Placement fee for ${placement.candidateName || 'candidate'}${placement.jobTitle ? ` - ${placement.jobTitle}` : ''}</td>
          <td class="right">${formatMoney(fee, currency)}</td>
        </tr>
      </tbody>
    </table>
    <section class="totals">
      <div><span>Subtotal</span><strong>${formatMoney(fee, currency)}</strong></div>
      <div><span>Tax / VAT (${taxPercent}%)</span><strong>${formatMoney(taxAmount, currency)}</strong></div>
      <div class="grand"><span>Total</span><span>${formatMoney(total, currency)}</span></div>
    </section>
  </main>
</body>
</html>`;
  const printWindow = window.open('', '_blank', 'noopener,noreferrer');
  if (!printWindow) return;
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  window.setTimeout(() => {
    try { printWindow.focus(); printWindow.print(); } catch (_error) {}
  }, 250);
}



function openCsvPicker() {

  if (typeof window === 'undefined') return Promise.resolve(null);

  return new Promise((resolve) => {

    const input = document.createElement('input');

    input.type = 'file';

    input.accept = '.csv,text/csv';

    input.onchange = () => resolve(input.files?.[0] || null);

    input.click();

  });

}



function normalizePipelineStage(value) {

  const normalized = normalizeText(String(value || ''));

  const directMatch = PIPELINE_STAGES.find((stage) => stage.id === normalized || normalizeText(stage.label) === normalized);

  return directMatch?.id || 'applied';

}



function truncateLongText(value, limit = 1200) {

  const normalized = String(value || '').trim();

  if (normalized.length <= limit) return normalized;

  return `${normalized.slice(0, limit).trim()}...`;

}



function buildCandidateSearchText(candidate) {

  return normalizeText([

    candidate.name,

    candidate.role,

    candidate.company,

    candidate.location,

    candidate.email,

    candidate.phone,

    candidate.matchedJobTitle,

    Array.isArray(candidate.skills) ? candidate.skills.join(' ') : '',

    candidate.parsedProfile?.seniority,

    candidate.parsedProfile?.scopeLabel,

    Array.isArray(candidate.parsedProfile?.skills) ? candidate.parsedProfile.skills.join(' ') : '',

    candidate.semanticSearchText,

    candidate.rawText,

  ].join(' '));

}



function parseBooleanQuery(query) {

  return String(query || '')

    .split(/\s+OR\s+/i)

    .map((group) => {

      const negatives = Array.from(group.matchAll(/(?:NOT|-)(?:\s+)?"([^"]+)"|(?:NOT|-)(?:\s+)?([^\s()]+)/gi)).map((match) => normalizeText(match[1] || match[2] || '')).filter(Boolean);

      const stripped = group.replace(/(?:NOT|-)(?:\s+)?"[^"]+"/gi, ' ').replace(/(?:NOT|-)(?:\s+)?[^\s()]+/gi, ' ').replace(/[()]/g, ' ');

      const positives = stripped.split(/\s+AND\s+/i).flatMap((part) => part.split(/\s+/)).map((part) => normalizeText(part.replace(/^"|"$/g, ''))).filter(Boolean);

      return { positives, negatives };

    })

    .filter((group) => group.positives.length || group.negatives.length);

}



function scoreBooleanMatch(candidate, query) {

  const haystack = buildCandidateSearchText(candidate);

  const groups = parseBooleanQuery(query);

  if (!groups.length) return { matched: true, score: Number(candidate.rating || 0), positiveHits: 0 };

  let bestScore = -1;

  let matched = false;

  for (const group of groups) {

    if (group.negatives.some((term) => term && haystack.includes(term))) continue;

    const positiveHits = group.positives.filter((term) => term && haystack.includes(term)).length;

    if (group.positives.length && positiveHits < group.positives.length) continue;

    matched = true;

    bestScore = Math.max(bestScore, Number(candidate.rating || 0) + positiveHits * 3);

  }

  return { matched, score: matched ? bestScore : 0 };

}



function scoreBooleanCoverage(candidate, query) {

  const haystack = buildCandidateSearchText(candidate);

  const groups = parseBooleanQuery(query);

  if (!groups.length) return { score: Number(candidate.rating || 0), positiveHits: 0, positiveCoverage: 0 };

  let best = { score: 0, positiveHits: 0, positiveCoverage: 0 };

  for (const group of groups) {

    if (group.negatives.some((term) => term && haystack.includes(term))) continue;

    const positiveHits = group.positives.filter((term) => term && haystack.includes(term)).length;

    const positiveCoverage = group.positives.length ? positiveHits / group.positives.length : 0;

    const score = Math.round((positiveCoverage * 100) * 0.7 + positiveHits * 4);

    if (score > best.score) {

      best = { score, positiveHits, positiveCoverage };

    }

  }

  return best;

}



function createLinkedInSourcingCommand(job, query) {

  const company = getJobCompanyName(job);

  return [company ? `"${company}"` : '', job?.title ? `"${job.title}"` : '', query].filter(Boolean).join(' AND ');

}



function extractLinkedInHandle(url) {

  const match = String(url || '').match(/linkedin\.com\/in\/([^/?#]+)/i);

  return match ? match[1].replace(/[-_]+/g, ' ').trim() : '';

}



function titleCaseWords(value) {

  return String(value || '').split(/\s+/).filter(Boolean).map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');

}



function buildSourceLeadProfile(lead, job) {

  const inferredName = lead.name || titleCaseWords(extractLinkedInHandle(lead.linkedInUrl));

  const inferredRole = lead.headline || job?.title || 'Sourced Candidate';

  const inferredCompany = getJobCompanyName(job) || 'Target Company';

  const inferredLocation = lead.location || 'Unknown location';

  const notesText = lead.notes || '';

  const skills = extractSkills(`${lead.headline || ''} ${notesText}`);

  const score = scoreAgainstJob({ role: inferredRole, company: inferredCompany, skills, rawText: `${lead.headline || ''} ${notesText}` }, job || { title: inferredRole, requirements: notesText, agencyId: job?.agencyId });

  return {

    name: inferredName || 'LinkedIn Lead',

    role: inferredRole,

    company: inferredCompany,

    location: inferredLocation,

    skills,

    rating: score.score || 60,

    rawText: notesText,

  };

}



function createCrmEvent(type, summary, metadata = {}) {

  const createdAtIso = new Date().toISOString();

  return {

    type,

    summary,

    metadata,

    createdAtIso,

    createdAtMs: Date.parse(createdAtIso),

  };

}



function uniqueWords(value) {

  return Array.from(new Set(normalizeText(value).split(/\s+/).filter(Boolean)));

}



function extractSeniorityTokens(value) {

  const haystack = normalizeText(value);

  const levels = ['intern', 'junior', 'mid', 'senior', 'lead', 'principal', 'head', 'director'];

  return levels.filter((level) => haystack.includes(level));

}



function detectRoleFamily(value) {

  const haystack = normalizeText(value);

  const families = [

    { name: 'design', terms: ['designer', 'design', 'ux', 'ui', 'product design', 'interaction'] },

    { name: 'engineering', terms: ['engineer', 'developer', 'frontend', 'backend', 'full stack', 'software'] },

    { name: 'growth', terms: ['growth', 'marketing', 'seo', 'acquisition', 'performance'] },

    { name: 'product', terms: ['product manager', 'product owner', 'product lead'] },

    { name: 'data', terms: ['data', 'analytics', 'analyst', 'scientist'] },

    { name: 'sales', terms: ['sales', 'account executive', 'business development'] },

    { name: 'recruiting', terms: ['recruiter', 'talent', 'sourcer'] },

  ];

  const match = families.find((family) => family.terms.some((term) => haystack.includes(term)));

  return match ? match.name : '';

}



function computeTitleCoverage(candidateRole, jobTitle) {

  const roleText = normalizeText(candidateRole);

  const jobTitleText = normalizeText(jobTitle);

  const titleWords = uniqueWords(jobTitleText);

  const exactPhrase = jobTitleText && roleText.includes(jobTitleText);

  const titleHits = titleWords.filter((word) => roleText.includes(word)).length;

  const titleCoverage = titleWords.length ? titleHits / titleWords.length : 0;

  const candidateSeniority = extractSeniorityTokens(candidateRole);

  const jobSeniority = extractSeniorityTokens(jobTitle);

  const seniorityMismatch = Boolean(candidateSeniority.length && jobSeniority.length && !candidateSeniority.some((token) => jobSeniority.includes(token)));

  const candidateFamily = detectRoleFamily(candidateRole);

  const jobFamily = detectRoleFamily(jobTitle);

  const familyMismatch = Boolean(candidateFamily && jobFamily && candidateFamily != jobFamily);

  const familyMatch = Boolean(candidateFamily && jobFamily && candidateFamily == jobFamily);

  return { exactPhrase, titleHits, titleCoverage, seniorityMismatch, familyMismatch, familyMatch };

}



function scoreAgainstJob(parsedCandidate, job) {

  const roleText = parsedCandidate.role || '';

  const rawText = normalizeText([

    parsedCandidate.role,

    parsedCandidate.skills.join(' '),

    parsedCandidate.rawText,

  ].join(' '));

  const requiredKeywords = ROLE_KEYWORDS[job.title] || uniqueWords(`${job.title} ${job.requirements}`);

  const title = computeTitleCoverage(roleText, job.title);

  const skillHits = requiredKeywords.filter((word) => rawText.includes(word)).length;

  const skillCoverage = requiredKeywords.length ? skillHits / requiredKeywords.length : 0;

  let score = 10;

  if (title.exactPhrase) score += 55;

  score += title.titleCoverage * 28;

  score += skillCoverage * 20;

  if (title.familyMatch) score += 18;

  if (title.familyMismatch) score -= 42;

  if (title.seniorityMismatch) score -= 18;

  if (title.titleHits === 0 && skillHits === 0) score -= 12;

  return {

    score: Math.max(0, Math.min(99, Math.round(score))),

    titleHits: title.titleHits,

    titleCoverage: title.titleCoverage,

    skillHits,

    skillCoverage,

    seniorityMismatch: title.seniorityMismatch,

    familyMismatch: title.familyMismatch,

    familyMatch: title.familyMatch,

    exactPhrase: title.exactPhrase,

  };

}



function findBestProspectJob(profile, jobs) {

  const activeJobs = (Array.isArray(jobs) ? jobs : []).filter((job) => !['closed', 'removed'].includes(String(job?.status || '').toLowerCase()));

  const ranked = activeJobs

    .map((job) => {

      const score = scoreAgainstJob({

        role: profile.role || '',

        company: '',

        skills: Array.isArray(profile.skills) ? profile.skills : [],

        rawText: profile.rawText || '',

      }, job);

      return { job, score };

    })

    .sort((left, right) => right.score.score - left.score.score);

  const best = ranked[0];

  if (!best) {

    return {

      matchedJobId: '',

      matchedJobTitle: '',

      matchedCompanyName: '',

      rating: Number(profile.rating || 0),

      fitScore: Number(profile.rating || 0),

    };

  }

  return {

    matchedJobId: best.job.id,

    matchedJobTitle: best.job.title,

    matchedCompanyName: getJobCompanyName(best.job),

    rating: best.score.score,

    fitScore: best.score.score,

  };

}



function rankCvJobsLocally(candidateProfile, jobs) {

  const activeJobs = (Array.isArray(jobs) ? jobs : []).filter((job) => String(job?.status || '').toLowerCase() === 'active');

  return activeJobs

    .map((job) => {

      const aiScore = scoreAgainstJob(candidateProfile, job);

      const booleanScore = scoreBooleanCoverage(candidateProfile, job.booleanQuery || createBooleanQueryFromJob(job));

      const blendedScore = Math.round((aiScore.score * 0.82) + (booleanScore.score * 0.18));

      return {

        job,

        aiScore,

        booleanScore,

        blendedScore,

      };

    })

    .sort((left, right) => right.blendedScore - left.blendedScore || right.aiScore.score - left.aiScore.score || right.booleanScore.score - left.booleanScore.score);

}



function findBestCvJobMatch(candidateProfile, jobs) {

  return rankCvJobsLocally(candidateProfile, jobs)[0] || null;

}



function getCallableErrorMessage(error, fallback) {

  const message = String(error?.message || '').trim();

  const code = String(error?.code || '').trim().toLowerCase();

  if (!message && !code) return fallback;

  if (message.toUpperCase() === 'INTERNAL' || code.includes('internal')) {

    return 'Vision could not complete that step right now. Retry once, and if it persists, check that email sending is configured for this workspace.';

  }

  return message || fallback;

}



function extractEmail(text) {

  const match = text.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);

  return match ? match[0] : '';

}



function extractPhone(text) {

  const match = text.match(/(?:\+?\d[\d\s().-]{7,}\d)/);

  return match ? match[0].trim() : '';

}



function titleCaseFromEmail(email) {

  const local = String(email || '').split('@')[0] || '';

  if (!local) return '';

  return local

    .replace(/[._-]+/g, ' ')

    .split(/\s+/)

    .filter(Boolean)

    .slice(0, 4)

    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())

    .join(' ');

}



function sanitizeCandidateName(value) {

  return String(value || '')

    .replace(/\b(email|phone|location|professional summary|summary|skills|experience|education|projects|tools)\b.*$/i, '')

    .replace(/[^A-Za-z' -]/g, ' ')

    .replace(/\s+/g, ' ')

    .trim();

}



function stripTrailingRoleWords(value) {

  const roleSuffixes = [

    'product designer',

    'senior product designer',

    'frontend engineer',

    'senior frontend engineer',

    'backend engineer',

    'marketing lead',

    'designer',

    'engineer',

    'developer',

    'manager',

    'lead',

    'specialist',

    'analyst',

  ];

  let output = String(value || '').trim();

  for (const suffix of roleSuffixes) {

    const pattern = new RegExp(`\\s+${suffix.replace(/\s+/g, '\\s+')}$$`, 'i');

    if (pattern.test(output)) {

      output = output.replace(pattern, '').trim();

      break;

    }

  }

  return output;

}



function extractName(text) {

  const lines = text.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  for (const line of lines.slice(0, 8)) {

    const cleaned = stripTrailingRoleWords(sanitizeCandidateName(line));

    if (/^[A-Z][a-zA-Z'-]+(?:\s+[A-Z][a-zA-Z'-]+){1,3}$/.test(cleaned)) {

      return cleaned;

    }

  }



  const firstChunk = String(text || '')

    .split(/\b(?:Email|Phone|Location|Professional Summary|Summary|Skills|Experience|Education|Projects|Tools)\b/i)[0]

    .trim();

  const cleanedChunk = stripTrailingRoleWords(sanitizeCandidateName(firstChunk));

  const words = cleanedChunk.split(/\s+/).filter(Boolean);

  const leadingName = words.slice(0, 4).join(' ');

  if (/^[A-Z][a-zA-Z'-]+(?:\s+[A-Z][a-zA-Z'-]+){1,3}$/.test(leadingName)) {

    return leadingName;

  }



  const inlineMatch = String(text || '').match(/\b([A-Z][a-zA-Z'-]+(?:\s+[A-Z][a-zA-Z'-]+){1,3})\b/);

  if (inlineMatch) {

    const cleaned = stripTrailingRoleWords(sanitizeCandidateName(inlineMatch[1]));

    if (cleaned) return cleaned;

  }



  const email = extractEmail(text);

  const fromEmail = titleCaseFromEmail(email);

  if (fromEmail) return fromEmail;



  return 'Unknown Candidate';

}



function extractYearsExperience(text) {

  const explicit = text.match(/(\d+)\+?\s+years?/i);

  if (explicit) return Number(explicit[1]);

  const years = Array.from(text.matchAll(/20(0\d|1\d|2\d|3\d|4\d|5\d)/g)).map((match) => Number(`20${match[1]}`));

  if (years.length >= 2) {

    const minYear = Math.min(...years);

    return Math.max(1, new Date().getFullYear() - minYear);

  }

  return 0;

}



function extractSkills(text) {

  const keywords = ['react', 'typescript', 'javascript', 'node', 'go', 'postgres', 'docker', 'figma', 'ux', 'seo', 'analytics', 'leadership', 'research', 'prototyping', 'marketing'];

  const haystack = normalizeText(text);

  return keywords.filter((keyword) => haystack.includes(keyword)).map((keyword) => {

    if (keyword === 'ux') return 'UX';

    if (keyword === 'seo') return 'SEO';

    return keyword.charAt(0).toUpperCase() + keyword.slice(1);

  });

}



function sanitizeLocation(value) {

  return String(value || '')

    .replace(/^location\s*[:.-]?\s*/i, '')

    .replace(/^address\s*[:.-]?\s*/i, '')

    .replace(/\b(email|phone|professional summary|summary|skills|experience|education|projects|tools)\b.*$/i, '')

    .replace(/\s+/g, ' ')

    .trim()

    .replace(/^[,\-\s]+|[,\-\s]+$/g, '');

}



function extractLocation(text) {

  const lines = String(text || '').split(/\r?\n/).map((line) => line.trim()).filter(Boolean);

  for (const line of lines.slice(0, 12)) {

    if (/^location\s*[:.-]?/i.test(line) || /^address\s*[:.-]?/i.test(line)) {

      const cleaned = sanitizeLocation(line);

      if (cleaned) return cleaned;

    }

  }



  const inlineMatch = String(text || '').match(/\bLocation\s*[:.-]?\s*([^\n|]+(?:,\s*[^\n|]+){0,2})/i);

  if (inlineMatch) {

    const cleaned = sanitizeLocation(inlineMatch[1]);

    if (cleaned) return cleaned;

  }



  const cityCountryMatch = String(text || '').match(/\b([A-Z][a-zA-Z.'-]+(?:\s+[A-Z][a-zA-Z.'-]+)*,\s*[A-Z][a-zA-Z.'-]+(?:\s+[A-Z][a-zA-Z.'-]+)*)\b/);

  if (cityCountryMatch) {

    const cleaned = sanitizeLocation(cityCountryMatch[1]);

    if (cleaned && cleaned.length <= 60) return cleaned;

  }



  return '';

}

function getCandidateDetailMismatches(form, cvDetails) {
  const fields = [
    ['name', 'Name', (value) => normalizeText(value)],
    ['email', 'Email', (value) => normalizeText(value)],
    ['phone', 'Phone number', (value) => String(value || '').replace(/\D/g, '')],
    ['location', 'Location', (value) => normalizeText(value)],
  ];

  return fields
    .map(([field, label, normalize]) => {
      const entered = String(form?.[field] || '').trim();
      const fromCv = String(cvDetails?.[field] || '').trim();
      if (!entered || !fromCv || normalize(entered) === normalize(fromCv)) return '';
      return `${label}: entered "${entered}", but your CV says "${fromCv}".`;
    })
    .filter(Boolean);
}



function findExplicitJobMatches(text, jobs) {

  const haystack = normalizeText(text);

  return jobs.filter((job) => {

    const companyName = getJobCompanyName(job);

    return haystack.includes(normalizeText(job.title)) && haystack.includes(normalizeText(companyName));

  });

}



function ensureScreeningDeadline(candidate) {

  if (candidate?.screeningDeadlineAt) return candidate.screeningDeadlineAt;

  return new Date(Date.now() + SCREENING_WINDOW_MINUTES * 60 * 1000).toISOString();

}



function formatTimeRemaining(deadlineAt) {

  if (!deadlineAt) return 'Not scheduled';

  const remainingMs = new Date(deadlineAt).getTime() - Date.now();

  if (remainingMs <= 0) return 'Expired';

  const totalSeconds = Math.floor(remainingMs / 1000);

  const minutes = Math.floor(totalSeconds / 60);

  const seconds = totalSeconds % 60;

  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

}



function buildScreeningLink(candidateId) {

  if (typeof window === 'undefined') return `?screening=${candidateId}`;

  const url = new URL(window.location.href);

  url.searchParams.set('screening', candidateId);

  return url.toString();

}



async function extractPdfText(file) {

  const buffer = await file.arrayBuffer();

  const pdf = await getDocument({ data: buffer }).promise;

  const pages = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {

    const page = await pdf.getPage(pageNumber);

    const content = await page.getTextContent();

    pages.push(content.items.map((item) => item.str || '').join(' '));

  }

  return pages.join('\n');

}



function PipelineTableView({
  candidates,
  jobs,
  sortKey,
  filters,
  onSortChange,
  onFilterChange,
  onStageChange,
  onOpenScreeningCandidate,
  onDeleteCandidate,
  onDeclineOffer,
  onReassignJob,
}) {
  const [reassignOpenId, setReassignOpenId] = React.useState('');

  const activeJobs = React.useMemo(
    () => (Array.isArray(jobs) ? jobs : []).filter((j) => !['closed', 'removed'].includes(String(j.status || '').toLowerCase())),
    [jobs],
  );

  const getRankedJobsForCandidate = (candidate) => {
    const profile = {
      role: candidate.role || candidate.matchedJobTitle || '',
      company: candidate.company || '',
      skills: candidate.skills || [],
      rawText: candidate.rawText || candidate.semanticSearchText || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      location: candidate.location || '',
    };
    return rankCvJobsLocally(profile, activeJobs);
  };
  const SORT_OPTIONS = [
    { key: 'stage', label: 'Stage' },
    { key: 'fit', label: 'Fit Score' },
    { key: 'activity', label: 'Activity' },
  ];

  return (
    <div className="overflow-x-auto">
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <select
          id="pipeline-table-stage-filter"
          name="pipeline-table-stage-filter"
          value={filters.stage}
          onChange={(e) => onFilterChange('stage', e.target.value)}
          className="rounded-full border border-white/10 bg-slate-900/80 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400"
        >
          <option value="">All Stages</option>
          {PIPELINE_STAGES.map((s) => (
            <option key={s.id} value={s.id}>{s.label}</option>
          ))}
        </select>
        <input
          id="pipeline-table-company-filter"
          name="pipeline-table-company-filter"
          type="text"
          placeholder="Filter company…"
          value={filters.company}
          onChange={(e) => onFilterChange('company', e.target.value)}
          className="rounded-full border border-white/10 bg-slate-900/80 px-3 py-2 text-xs text-white outline-none placeholder:text-slate-500 focus:border-cyan-400"
        />
        <input
          id="pipeline-table-role-filter"
          name="pipeline-table-role-filter"
          type="text"
          placeholder="Filter role…"
          value={filters.role}
          onChange={(e) => onFilterChange('role', e.target.value)}
          className="rounded-full border border-white/10 bg-slate-900/80 px-3 py-2 text-xs text-white outline-none placeholder:text-slate-500 focus:border-cyan-400"
        />
        <div className="ml-auto flex items-center gap-1 rounded-full border border-white/10 bg-slate-950/60 p-1">
          {SORT_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              onClick={() => onSortChange(opt.key)}
              className={`rounded-full px-3 py-1.5 text-micro font-black uppercase tracking-[0.16em] transition ${sortKey === opt.key ? 'bg-cyan-500 text-slate-950' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <table className="min-w-full text-left text-sm">
        <thead>
          <tr className="border-b border-white/10 text-micro uppercase tracking-[0.2em] text-slate-500">
            <th className="px-3 py-2">Name</th>
            <th className="px-3 py-2">Role</th>
            <th className="px-3 py-2">Company</th>
            <th className="px-3 py-2">Stage</th>
            <th className="px-3 py-2">Location</th>
            <th className="px-3 py-2">Fit</th>
            <th className="px-3 py-2">Last Activity</th>
            <th className="px-3 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {candidates.length === 0 && (
            <tr>
              <td colSpan={8} className="px-3 py-6 text-center text-xs text-slate-500">
                No candidates match the current filters.
              </td>
            </tr>
          )}
          {candidates.map((candidate) => {
            const stageMeta = getPipelineStageMeta(candidate.status);
            const isScreening = candidate.status === 'screening';
            return (
              <tr key={candidate.id} className="border-b border-white/5 text-slate-200 transition hover:bg-white/5">
                <td className="px-3 py-3 font-bold text-white">{candidate.name}</td>
                <td className="px-3 py-3 text-xs text-slate-300">{candidate.role}</td>
                <td className="px-3 py-3 text-xs text-slate-300">{candidate.company || 'Unknown'}</td>
                <td className="px-3 py-3">
                  <select
                    id={`stage-select-${candidate.id}`}
                    name={`stage-select-${candidate.id}`}
                    value={candidate.status}
                    onChange={(e) => onStageChange(candidate.id, e.target.value)}
                    className="rounded-ctl border border-white/10 bg-slate-900/80 px-2 py-1.5 text-xs text-white outline-none focus:border-cyan-400"
                  >
                    {PIPELINE_STAGES.map((s) => (
                      <option key={s.id} value={s.id}>{s.label}</option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-3 text-xs text-slate-400">{candidate.location || '—'}</td>
                <td className="px-3 py-3">
                  <span className="rounded-full bg-emerald-500/10 px-2 py-1 text-micro font-bold uppercase tracking-[0.14em] text-emerald-300">
                    {candidate.rating ?? '—'}
                  </span>
                </td>
                <td className="px-3 py-3 text-xs text-slate-400">
                  {candidate.lastActivityAt
                    ? new Date(candidate.lastActivityAt).toLocaleString()
                    : 'No activity yet'}
                </td>
                <td className="px-3 py-3">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => onOpenScreeningCandidate(candidate.id)}
                      disabled={!isScreening}
                      className="rounded-full border border-violet-400/20 bg-violet-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-violet-200 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      Screening
                    </button>
                    {candidate.status === 'offer' && onDeclineOffer && (
                      <button
                        type="button"
                        onClick={() => onDeclineOffer(candidate.id)}
                        className="rounded-full border border-rose-400/20 bg-rose-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-rose-200"
                      >
                        Decline Offer
                      </button>
                    )}
                    {typeof onDeleteCandidate === 'function' && (
                      <button
                        type="button"
                        onClick={() => onDeleteCandidate(candidate.id)}
                        className="rounded-full border border-red-400/20 bg-red-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-red-200"
                      >
                        Delete
                      </button>
                    )}
                    {typeof onReassignJob === 'function' && (
                      <button
                        type="button"
                        onClick={() => setReassignOpenId((prev) => (prev === candidate.id ? '' : candidate.id))}
                        className="rounded-full border border-violet-400/20 bg-violet-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-violet-200"
                      >
                        Pair job
                      </button>
                    )}
                  </div>
                  {reassignOpenId === candidate.id && typeof onReassignJob === 'function' && (() => {
                    const ranked = getRankedJobsForCandidate(candidate);
                    return (
                      <div className="mt-2 rounded-ctl border border-violet-400/20 bg-slate-950/90 p-2 shadow-lg">
                        <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-violet-300">Pair to vacancy — ranked by fit</div>
                        <div className="max-h-48 overflow-y-auto space-y-1">
                          {ranked.length === 0 && (
                            <div className="text-micro text-slate-500 px-1">No active vacancies.</div>
                          )}
                          {ranked.map(({ job, blendedScore }) => (
                            <button
                              key={job.id}
                              type="button"
                              onClick={() => { onReassignJob(candidate.id, job.id); setReassignOpenId(''); }}
                              className={`w-full flex items-center justify-between gap-2 rounded-ctl px-2.5 py-1.5 text-left text-micro transition hover:bg-violet-500/15 ${candidate.matchedJobId === job.id ? 'border border-violet-400/30 bg-violet-500/10 text-violet-100' : 'text-slate-200'}`}
                            >
                              <span className="min-w-0 truncate font-bold">{job.title}<span className="ml-1 font-normal text-slate-400">· {getJobCompanyName(job) || 'Client'}</span></span>
                              <span className={`shrink-0 rounded-full px-2 py-0.5 text-micro font-black ${blendedScore >= 70 ? 'bg-emerald-500/20 text-emerald-300' : blendedScore >= 45 ? 'bg-amber-500/20 text-amber-300' : 'bg-slate-700/60 text-slate-400'}`}>{blendedScore}</span>
                            </button>
                          ))}
                        </div>
                        <button type="button" onClick={() => setReassignOpenId('')} className="mt-2 w-full rounded-ctl border border-white/10 bg-white/5 py-1 text-micro font-black uppercase tracking-[0.12em] text-slate-400 hover:text-white">Cancel</button>
                      </div>
                    );
                  })()}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StageInterviewRecorder({ candidate, onSaveInterviewCapture }) {
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const recognitionRef = useRef(null);
  const startedAtRef = useRef(0);
  const transcriptRef = useRef('');
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [transcriptSource, setTranscriptSource] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { transcriptRef.current = transcript; }, [transcript]);
  useEffect(() => () => {
    try { recognitionRef.current?.stop?.(); } catch (_error) {}
    try { recorderRef.current?.state !== 'inactive' && recorderRef.current?.stop?.(); } catch (_error) {}
    try { streamRef.current?.getTracks?.().forEach((track) => track.stop()); } catch (_error) {}
  }, []);

  const stopTracks = () => {
    try { streamRef.current?.getTracks?.().forEach((track) => track.stop()); } catch (_error) {}
    streamRef.current = null;
  };

  const startTranscript = () => {
    const Recognition = typeof window !== 'undefined' ? (window.SpeechRecognition || window.webkitSpeechRecognition) : null;
    if (!Recognition) {
      setTranscriptSource('manual');
      setNotice('Live transcript is unavailable in this browser. Audio will still save.');
      return;
    }
    const recognition = new Recognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      let finalText = '';
      for (let index = event.resultIndex; index < event.results.length; index += 1) {
        if (event.results[index].isFinal) finalText += `${event.results[index]?.[0]?.transcript || ''} `;
      }
      if (finalText) setTranscript((current) => `${current} ${finalText}`.replace(/\s+/g, ' ').trim());
    };
    recognition.onerror = () => setNotice('Live transcript paused. Recording continues.');
    recognition.onend = () => {
      if (recorderRef.current?.state === 'recording') {
        try { recognition.start(); } catch (_error) {}
      }
    };
    recognitionRef.current = recognition;
    recognition.start();
    setTranscriptSource('browser_speech');
  };

  const saveCapture = async (blob) => {
    setIsSaving(true);
    setError('');
    try {
      const result = await onSaveInterviewCapture?.(candidate.id, {
        blob,
        consentConfirmed,
        transcript: transcriptRef.current,
        transcriptSource,
        durationMs: startedAtRef.current ? Date.now() - startedAtRef.current : 0,
      });
      setNotice(result?.aiNote ? 'Saved. AI note added to Notes and this interview card.' : 'Recording saved.');
      setTranscript('');
      transcriptRef.current = '';
      setConsentConfirmed(false);
    } catch (saveError) {
      setError(saveError?.message || 'Failed to save interview recording.');
    } finally {
      setIsSaving(false);
    }
  };

  const startRecording = async () => {
    if (!consentConfirmed || isSaving || !onSaveInterviewCapture) return;
    setError('');
    setNotice('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported?.('audio/webm;codecs=opus') ? 'audio/webm;codecs=opus' : '';
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => { if (event.data?.size) chunksRef.current.push(event.data); };
      recorder.onstop = () => {
        stopTracks();
        void saveCapture(new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' }));
      };
      recorder.start(1000);
      startedAtRef.current = Date.now();
      startTranscript();
      setIsRecording(true);
      setNotice('Recording.');
    } catch (recordError) {
      stopTracks();
      setError(recordError?.message || 'Microphone access is required.');
    }
  };

  const stopRecording = () => {
    try { recognitionRef.current?.stop?.(); } catch (_error) {}
    recognitionRef.current = null;
    if (recorderRef.current?.state && recorderRef.current.state !== 'inactive') recorderRef.current.stop();
    setIsRecording(false);
  };

  const recordings = Array.isArray(candidate.interviewRecordings) ? candidate.interviewRecordings.slice().reverse() : [];
  const aiNotesByRecordingId = (Array.isArray(candidate.candidateNotes) ? candidate.candidateNotes : [])
    .filter((note) => note?.recordingId && note?.authorName === 'Vision AI Notetaker')
    .reduce((notes, note) => ({ ...notes, [note.recordingId]: note }), {});
  return (
    <div className="mt-2 space-y-2 rounded-ctl border border-amber-300/15 bg-slate-950/80 p-2.5" onClick={(event) => event.stopPropagation()}>
      <label className="flex items-start gap-2 text-micro leading-4 text-slate-300">
        <input type="checkbox" checked={consentConfirmed} disabled={isRecording || isSaving} onChange={(event) => setConsentConfirmed(event.target.checked)} className="mt-0.5 h-3.5 w-3.5 accent-cyan-400" />
        <span>Recording and AI notes consent confirmed.</span>
      </label>
      {!isRecording ? (
        <button type="button" disabled={!consentConfirmed || isSaving || !onSaveInterviewCapture} onClick={startRecording} className="flex w-full items-center justify-center gap-1.5 rounded-ctl border border-cyan-400/25 bg-cyan-500/15 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-cyan-100 disabled:cursor-not-allowed disabled:opacity-50">
          {isSaving ? <Loader size={11} className="animate-spin" /> : <Mic size={11} />} Record interview
        </button>
      ) : (
        <button type="button" onClick={stopRecording} className="flex w-full items-center justify-center gap-1.5 rounded-ctl bg-rose-400 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-950">
          <Square size={10} /> Stop and save
        </button>
      )}
      <textarea value={transcript} disabled={isRecording || isSaving} onChange={(event) => { setTranscript(event.target.value); setTranscriptSource('manual'); }} rows={3} className="w-full resize-y rounded-ctl border border-white/10 bg-slate-900 px-2 py-1.5 text-meta leading-4 text-white outline-none focus:border-cyan-400 disabled:opacity-80" placeholder="Interview transcript" />
      {recordings.slice(0, 2).map((recording) => {
        const aiNote = aiNotesByRecordingId[recording.id];
        return (
          <div key={recording.id} className="rounded-ctl border border-white/10 bg-white/5 p-2 text-micro leading-4 text-slate-300">
            <div className="flex items-center gap-2">
              <span className="font-bold text-white">Recording</span>
              {recording.url && <a href={recording.url} target="_blank" rel="noreferrer" className="ml-auto text-cyan-200">Open</a>}
            </div>
            {recording.transcript && <div className="mt-1 line-clamp-3 whitespace-pre-wrap text-slate-400">{recording.transcript}</div>}
            {aiNote?.text && (
              <div className="mt-2 rounded-md border border-cyan-300/15 bg-cyan-400/10 p-1.5">
                <div className="font-black uppercase tracking-[0.12em] text-cyan-100">AI notes</div>
                <div className="mt-1 line-clamp-5 whitespace-pre-wrap text-slate-200">{aiNote.text}</div>
              </div>
            )}
          </div>
        );
      })}
      {notice && <div className="text-micro leading-4 text-cyan-100">{notice}</div>}
      {error && <div className="text-micro leading-4 text-rose-200">{error}</div>}
    </div>
  );
}

function StageColumn({

  stage,

  items,

  onDragOverStage,

  onDropStage,

  onDragStartCard,

  onDeleteCandidate,

  onDeclineOffer,

  onOpenScreeningCandidate,

  onStageChange,

  isActiveDropTarget,

  agingThresholdDays = 14,

  onOpenCandidate,

  onSetInterviewDetails,
  onSaveInterviewCapture,

  onSubmitToClient,

  onSendOffer,

  onAcceptOffer,

  onRejectCandidate,

  onLogPlacement,

  loggedCandidateIds,
  directDemoGuideTargets = {},

}) {
  const [interviewEditId, setInterviewEditId] = React.useState(null);
  const [interviewDraft, setInterviewDraft] = React.useState({ interviewDate: '', interviewType: '', interviewerName: '' });
  const directDemoDropTarget = directDemoGuideTargets.screeningDrop || directDemoGuideTargets.interviewDrop || directDemoGuideTargets.offerDrop;
  // Expanded by default — user can collapse to a stack
  const [isCollapsed, setIsCollapsed] = React.useState(false);

  const daysInStage = (candidate) => {
    const history = Array.isArray(candidate.stageHistory) ? candidate.stageHistory : [];
    const hit = [...history].reverse().find((e) => e.stageId === stage.id);
    const movedMs = hit ? new Date(hit.movedAt || 0).getTime() : new Date(candidate.submittedAt || 0).getTime();
    if (!Number.isFinite(movedMs) || movedMs <= 0) return -1;
    return (Date.now() - movedMs) / 86400000;
  };

  return (
    <div
      data-direct-demo-target={directDemoGuideTargets.screeningDrop ? 'pipeline-screening-column' : directDemoGuideTargets.interviewDrop ? 'pipeline-interview-column' : directDemoGuideTargets.offerDrop ? 'pipeline-offer-column' : undefined}
      onDragOver={(event) => onDragOverStage(event, stage.id)}
      onDrop={(event) => onDropStage(event, stage.id)}
      className={`flex flex-col rounded-shell border bg-white/5 p-4 transition sm:p-5 ${isActiveDropTarget ? 'border-cyan-400/70 ring-2 ring-cyan-400/20' : 'border-white/10'} ${directDemoDropTarget ? 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]' : ''}`}
    >

      {/* Column header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`h-2.5 w-2.5 rounded-full ${stage.color}`} />
          <h3 className="text-xs font-black uppercase tracking-[0.2em] text-white">{stage.label}</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-400">{items.length}</span>
          {items.length > 1 && (
            <button
              type="button"
              title={isCollapsed ? 'Expand' : 'Collapse'}
              onClick={() => setIsCollapsed((v) => !v)}
              className="rounded-ctl p-1 text-slate-400 transition hover:bg-white/10 hover:text-white"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
                className={`h-4 w-4 transition-transform duration-200 ${isCollapsed ? '' : 'rotate-180'}`}>
                <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06z" clipRule="evenodd" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Empty drop zone */}
      {items.length === 0 && (
        <div className="mt-3 rounded-card border border-dashed border-white/10 px-4 py-5 text-xs text-slate-500">
          Drop a candidate here.
        </div>
      )}

      {/* Collapsed stack — cards fanned, still draggable */}
      {isCollapsed && items.length > 0 && (
        <div
          className="relative mt-3"
          style={{ height: Math.min(items.length, 5) * 10 + 52 }}
        >
          {items.map((candidate, index) => (
            <div
              key={candidate.id}
              draggable
              onDragStart={(event) => onDragStartCard(event, candidate.id)}
              style={{
                top: index * 10,
                zIndex: items.length - index,
                transform: `rotate(${(index % 2 === 0 ? 1 : -1) * index * 0.4}deg)`,
              }}
              className="absolute left-0 right-0 cursor-grab rounded-card border border-white/10 bg-slate-900/90 px-4 py-3 shadow transition active:cursor-grabbing"
            >
              <div className="truncate text-sm font-bold text-white">{candidate.name}</div>
              <div className="mt-0.5 truncate text-meta text-slate-400">{candidate.company || 'Unknown company'}</div>
              <div className="mt-0.5 truncate text-micro text-cyan-300/70">{candidate.matchedJobTitle || candidate.role || 'No vacancy'}</div>
            </div>
          ))}
        </div>
      )}

      {/* Expanded list — all cards fully visible */}
      {!isCollapsed && items.length > 0 && (
        <div className="mt-3 flex flex-col gap-3">
          {items.map((candidate) => (
            <div
              key={candidate.id}
              draggable
              onDragStart={(event) => onDragStartCard(event, candidate.id)}
              onClick={() => onOpenCandidate && onOpenCandidate(candidate.id)}
              className={"cursor-grab rounded-card border px-4 py-3 transition hover:bg-slate-900 active:cursor-grabbing " + (
                stage.id === 'hired' && loggedCandidateIds && !loggedCandidateIds.has(candidate.id)
                  ? 'border-emerald-400/60 bg-emerald-950/20 hover:border-emerald-400/80'
                  : daysInStage(candidate) >= agingThresholdDays
                    ? 'border-amber-500/60 bg-amber-950/30 hover:border-amber-400/80'
                    : 'border-white/10 bg-slate-900/70 hover:border-cyan-400/40'
              )}
            >
              <div className="truncate text-sm font-bold text-white">{candidate.name}</div>
              <div className="mt-1 truncate text-xs text-slate-400">{candidate.company || 'Unknown company'}</div>
              <div className="mt-1 truncate text-meta text-cyan-300/80">{candidate.matchedJobTitle || candidate.role || 'No vacancy'}</div>
              {daysInStage(candidate) >= agingThresholdDays && (
                <div className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-amber-500/50 bg-amber-500/10 px-2 py-0.5 text-micro font-bold text-amber-300">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-2.5 w-2.5"><path fillRule="evenodd" d="M8 1a7 7 0 1 0 0 14A7 7 0 0 0 8 1zm.75 4a.75.75 0 0 0-1.5 0v3.25c0 .234.109.454.294.596l2.25 1.682a.75.75 0 0 0 .912-1.194L8.75 7.93V5z" clipRule="evenodd"/></svg>
                  {Math.floor(daysInStage(candidate))}d in stage
                </div>
              )}
              {stage.id === 'hired' && (() => {
                const isLogged = loggedCandidateIds && loggedCandidateIds.has(candidate.id);
                return isLogged ? (
                  <div className="mt-2 flex items-center gap-1.5 rounded-ctl border border-emerald-400/25 bg-emerald-500/10 px-3 py-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3 shrink-0 text-emerald-400"><path fillRule="evenodd" d="M12.416 3.376a.75.75 0 0 1 .208 1.04l-5 7.5a.75.75 0 0 1-1.154.114l-3-3a.75.75 0 0 1 1.06-1.06l2.353 2.353 4.493-6.74a.75.75 0 0 1 1.04-.207Z" clipRule="evenodd"/></svg>
                    <span className="text-micro font-black text-emerald-300">Placement Logged</span>
                  </div>
                ) : (
                  <button
                    type="button"
                    data-direct-demo-target={directDemoGuideTargets.placement ? 'candidate-offer-placement' : undefined}
                    onClick={(e) => { e.stopPropagation(); onLogPlacement && onLogPlacement(candidate); }}
                    className={`mt-2 w-full rounded-ctl border border-emerald-400/40 bg-emerald-500/15 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-emerald-200 transition hover:bg-emerald-500/25 ${directDemoGuideTargets.placement ? 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]' : ''}`}
                  >
                    Log Placement
                  </button>
                );
              })()}
              {stage.id === 'screening' && (
                <button
                  type="button"
                  data-direct-demo-target={directDemoGuideTargets.screening ? 'candidate-screening-button' : undefined}
                  onClick={(e) => { e.stopPropagation(); onOpenScreeningCandidate(candidate.id); }}
                  className={`mt-3 w-full rounded-ctl border border-violet-400/30 bg-violet-500/10 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-violet-200 transition hover:bg-violet-500/20 ${directDemoGuideTargets.screening ? 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]' : ''}`}
                >
                  Screening
                </button>
              )}
              {stage.id === 'interview' && (() => {
                if (interviewEditId === candidate.id) {
                  return (
                    <div className="mt-3 rounded-ctl border border-cyan-400/20 bg-slate-950 p-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                      <input type="datetime-local" value={interviewDraft.interviewDate} onChange={(e) => setInterviewDraft((d) => ({ ...d, interviewDate: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-2 py-1.5 text-meta text-white focus:border-cyan-500/50 focus:outline-none" />
                      <input type="text" placeholder="Type (e.g. Phone, Video, Panel)" value={interviewDraft.interviewType} onChange={(e) => setInterviewDraft((d) => ({ ...d, interviewType: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-2 py-1.5 text-meta text-white placeholder-slate-600 focus:border-cyan-500/50 focus:outline-none" />
                      <input type="text" placeholder="Interviewer name" value={interviewDraft.interviewerName} onChange={(e) => setInterviewDraft((d) => ({ ...d, interviewerName: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-2 py-1.5 text-meta text-white placeholder-slate-600 focus:border-cyan-500/50 focus:outline-none" />
                      <div className="flex gap-2 pt-1">
                        <button type="button" onClick={() => { onSetInterviewDetails && onSetInterviewDetails(candidate.id, interviewDraft); setInterviewEditId(null); }} className="flex-1 rounded-ctl bg-cyan-500 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-950 hover:bg-cyan-400">Save</button>
                        <button type="button" onClick={() => setInterviewEditId(null)} className="rounded-ctl border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-400 hover:text-white">Cancel</button>
                      </div>
                    </div>
                  );
                }
                const hasDetails = candidate.interviewDate || candidate.interviewType || candidate.interviewerName;
                return (
                  <div className="mt-2">
                    {hasDetails && (
                      <div className="mb-2 space-y-1 rounded-ctl border border-white/10 bg-slate-950/60 px-3 py-2">
                        {candidate.interviewDate && <div className="text-micro text-cyan-300/80">{new Date(candidate.interviewDate).toLocaleString([], {month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'})}</div>}
                        {candidate.interviewType && <div className="text-micro text-slate-400">{candidate.interviewType}</div>}
                        {candidate.interviewerName && <div className="text-micro text-slate-400">with <span className="text-white">{candidate.interviewerName}</span></div>}
                      </div>
                    )}
                    <button type="button" onClick={(e) => { e.stopPropagation(); setInterviewEditId(candidate.id); setInterviewDraft({ interviewDate: candidate.interviewDate || '', interviewType: candidate.interviewType || '', interviewerName: candidate.interviewerName || '' }); }} className="w-full rounded-ctl border border-cyan-400/20 bg-cyan-500/10 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-cyan-200 transition hover:bg-cyan-500/20">
                      {hasDetails ? 'Edit Interview' : 'Set Interview'}
                    </button>
                    <StageInterviewRecorder candidate={candidate} onSaveInterviewCapture={onSaveInterviewCapture} />
                  </div>
                );
              })()}

              {stage.id === 'offer' && (() => {
                const off = candidate.offer;
                const isExpired = off?.expiryDate && new Date(off.expiryDate) < new Date();
                return (
                  <div className="mt-3 space-y-2">
                    {/* Offer summary card */}
                    {off && (
                      <div className={`rounded-ctl border px-3 py-2 space-y-1 ${
                        off.status === 'accepted' ? 'border-emerald-400/30 bg-emerald-500/10'
                        : off.status === 'declined' ? 'border-rose-400/30 bg-rose-500/10'
                        : isExpired ? 'border-amber-400/30 bg-amber-500/10'
                        : 'border-green-400/20 bg-green-500/[0.08]'
                      }`}>
                        <div className="flex items-center justify-between">
                          <span className="text-micro font-black uppercase tracking-[0.12em] text-slate-400">Offer</span>
                          <span className={`text-micro font-bold uppercase tracking-[0.1em] ${
                            off.status === 'accepted' ? 'text-emerald-300'
                            : off.status === 'declined' ? 'text-rose-300'
                            : isExpired ? 'text-amber-300'
                            : 'text-green-300'
                          }`}>{off.status === 'accepted' ? '✓ Accepted' : off.status === 'declined' ? '✗ Declined' : isExpired ? 'Expired' : 'Pending'}</span>
                        </div>
                        {off.amount && <div className="text-sm font-black text-white">{off.currency} {Number(off.amount).toLocaleString()}</div>}
                        {off.sentDate && <div className="text-micro text-slate-500">Sent {new Date(off.sentDate).toLocaleDateString([], { day: 'numeric', month: 'short' })}</div>}
                        {off.expiryDate && off.status === 'pending' && <div className={`text-micro font-semibold ${isExpired ? 'text-amber-300' : 'text-slate-400'}`}>Expires {new Date(off.expiryDate).toLocaleDateString([], { day: 'numeric', month: 'short' })}</div>}
                      </div>
                    )}
                    {/* Actions */}
                    {(!off || off.status === 'pending') && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); onSendOffer && onSendOffer(candidate.id, candidate.offer); }}
                        data-direct-demo-target={directDemoGuideTargets.placement ? 'candidate-offer-placement' : undefined}
                        className={`w-full rounded-ctl border border-green-400/30 bg-green-500/10 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-green-200 transition hover:bg-green-500/20 ${directDemoGuideTargets.placement ? 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]' : ''}`}>
                        {off ? 'Edit Offer' : 'Send Offer'}
                      </button>
                    )}
                    {off && off.status === 'pending' && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); onAcceptOffer && onAcceptOffer(candidate.id); }}
                        className="w-full rounded-ctl border border-emerald-400/30 bg-emerald-500/10 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-emerald-200 transition hover:bg-emerald-500/20">
                        Mark Accepted
                      </button>
                    )}
                    {off && off.status === 'pending' && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); onDeclineOffer && onDeclineOffer(candidate.id); }}
                        className="w-full rounded-ctl border border-rose-400/30 bg-rose-500/10 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-rose-200 transition hover:bg-rose-500/20">
                        Decline Offer
                      </button>
                    )}
                    {!off && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); onDeclineOffer && onDeclineOffer(candidate.id); }}
                        className="w-full rounded-ctl border border-rose-400/30 bg-rose-500/10 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-rose-200 transition hover:bg-rose-500/20">
                        Decline Offer
                      </button>
                    )}
                  </div>
                );
              })()}
              {/* Submittal history badge */}
              {Array.isArray(candidate.submittals) && candidate.submittals.length > 0 && (() => {
                const last = candidate.submittals[candidate.submittals.length - 1];
                const OUTCOME_BADGE = { interview_requested: { label: 'Interview', cls: 'text-cyan-300' }, shortlisted: { label: 'Shortlisted', cls: 'text-violet-300' }, rejected: { label: 'Rejected', cls: 'text-rose-300' }, no_response: { label: 'No response', cls: 'text-slate-400' }, placed: { label: 'Placed', cls: 'text-emerald-300' } };
                const badge = OUTCOME_BADGE[last.outcome];
                return (
                  <div className="mt-2 flex items-center gap-1.5 rounded-ctl border border-violet-400/20 bg-violet-500/10 px-3 py-1.5">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3 shrink-0 text-violet-300"><path d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c.026.009.051.02.076.032L7.674 8.51c.206.1.446.1.652 0l6.598-3.185A.755.755 0 0 1 15 5.293V4.5A1.5 1.5 0 0 0 13.5 3h-11Z"/><path d="M15 6.954 8.978 9.86a2.25 2.25 0 0 1-1.956 0L1 6.954V11.5A1.5 1.5 0 0 0 2.5 13h11a1.5 1.5 0 0 0 1.5-1.5V6.954Z"/></svg>
                    <span className="text-micro font-bold text-violet-200">Submitted {candidate.submittals.length}×</span>
                    {badge ? <span className={`ml-auto text-micro font-semibold ${badge.cls}`}>{badge.label}</span> : <span className="ml-auto truncate text-micro text-violet-300/60">{last.clientName}</span>}
                  </div>
                );
              })()}
              {/* Send to Client */}
              {onSubmitToClient && (
                <button
                  type="button"
                  data-direct-demo-target={directDemoGuideTargets.sendClient ? 'candidate-send-client-button' : undefined}
                  onClick={(e) => { e.stopPropagation(); onSubmitToClient(candidate.id); }}
                  className={`mt-2 w-full rounded-ctl border border-violet-400/30 bg-violet-500/10 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-violet-200 transition hover:bg-violet-500/20 ${directDemoGuideTargets.sendClient ? 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]' : ''}`}
                >
                  Send to Client
                </button>
              )}
              {/* Reject from pipeline */}
              {onRejectCandidate && !candidate.pipelineRejected && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onRejectCandidate(candidate.id); }}
                  className="mt-2 w-full rounded-ctl border border-rose-400/20 bg-rose-500/[0.08] py-1.5 text-micro font-black uppercase tracking-[0.14em] text-rose-300/70 transition hover:bg-rose-500/15 hover:text-rose-200"
                >
                  Mark Not Selected
                </button>
              )}
              {candidate.pipelineRejected && (
                <div className="mt-2 rounded-ctl border border-rose-400/20 bg-rose-500/[0.06] px-3 py-2">
                  <div className="flex items-center gap-1.5">
                    <span className="text-micro font-bold text-rose-300/80">Not selected</span>
                    {candidate.pipelineRejectReason && <span className="ml-auto truncate text-micro text-slate-400">{candidate.pipelineRejectReasonLabel || PIPELINE_REJECT_REASON_LABELS[candidate.pipelineRejectReason] || candidate.pipelineRejectReason}</span>}
                  </div>
                  <div className="mt-1 text-micro font-semibold uppercase tracking-[0.12em] text-slate-500">
                    {candidate.pipelineFeedbackChannel === 'email' ? 'Candidate email prepared' : 'Internal reason saved'}
                  </div>
                </div>
              )}
              {typeof onDeleteCandidate === 'function' && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDeleteCandidate(candidate.id); }}
                  className="mt-2 w-full rounded-ctl border border-red-400/20 bg-red-500/10 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-red-200 transition hover:bg-red-500/20"
                >
                  Delete
                </button>
              )}
            </div>
          ))}
        </div>
      )}

    </div>

  );
}





// ─── JobLifecycleBar ─────────────────────────────────────────────────────────
function JobLifecycleBar({ job, onStageChange, onToggleBlocked }) {
  const [blockInput, setBlockInput] = React.useState('');
  const [showBlockForm, setShowBlockForm] = React.useState(false);

  const currentIdx = Math.max(0, JOB_LIFECYCLE_STAGES.findIndex((s) => s.id === (job.lifecycleStage || 'brief')));
  const currentStage = JOB_LIFECYCLE_STAGES[currentIdx];
  const prevStage = JOB_LIFECYCLE_STAGES[currentIdx - 1];
  const nextStage = JOB_LIFECYCLE_STAGES[currentIdx + 1];

  const stageAgeDays = job.stageUpdatedAt
    ? Math.floor((Date.now() - new Date(job.stageUpdatedAt).getTime()) / 86400000)
    : null;
  const stageAgeLabel = stageAgeDays === null ? '' : stageAgeDays === 0 ? 'today' : stageAgeDays === 1 ? '1 day' : `${stageAgeDays} days`;

  const daysToFill = job.targetFillDate
    ? Math.ceil((new Date(job.targetFillDate).getTime() - Date.now()) / 86400000)
    : null;

  return (
    <div className={`rounded-shell border p-5 sm:p-6 ${job.isBlocked ? 'border-rose-500/30 bg-rose-500/5' : 'border-white/10 bg-white/5'}`}>
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-meta font-black uppercase tracking-[0.18em] text-white">Job Lifecycle</div>
        <div className="flex flex-wrap items-center gap-2">
          {job.isBlocked && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-rose-300">
              <Flag size={9} /> Blocked{job.blockedReason ? ` · ${job.blockedReason}` : ''}
            </span>
          )}
          {daysToFill !== null && (
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-micro font-black uppercase tracking-[0.12em] ${daysToFill < 0 ? 'border-rose-500/30 bg-rose-500/10 text-rose-300' : daysToFill <= 7 ? 'border-amber-500/30 bg-amber-500/10 text-amber-300' : 'border-white/10 bg-white/5 text-slate-300'}`}>
              <Clock3 size={9} /> {daysToFill < 0 ? `${Math.abs(daysToFill)}d overdue` : daysToFill === 0 ? 'Fill due today' : `${daysToFill}d to target`}
            </span>
          )}
          <button
            type="button"
            onClick={() => { if (job.isBlocked) { onToggleBlocked(false, ''); } else { setShowBlockForm((v) => !v); } }}
            className={`rounded-full border px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] transition ${job.isBlocked ? 'border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20' : 'border-white/10 bg-white/5 text-slate-400 hover:text-white'}`}
          >
            <Flag size={9} className="inline mr-1" />{job.isBlocked ? 'Unblock' : 'Block'}
          </button>
        </div>
      </div>

      {/* Block reason input */}
      {showBlockForm && !job.isBlocked && (
        <div className="mt-3 flex items-center gap-2">
          <input
            value={blockInput}
            onChange={(e) => setBlockInput(e.target.value)}
            placeholder="Reason for blocking (optional)"
            className="flex-1 rounded-card border border-white/10 bg-slate-950/70 px-4 py-2 text-sm text-white outline-none focus:border-rose-400"
          />
          <button
            type="button"
            onClick={() => { onToggleBlocked(true, blockInput); setShowBlockForm(false); setBlockInput(''); }}
            className="rounded-full bg-rose-500 px-4 py-2 text-micro font-black uppercase tracking-[0.12em] text-white transition hover:bg-rose-400"
          >
            Confirm
          </button>
        </div>
      )}

      {/* Stage stepper */}
      <div className="mt-5 flex items-center">
        {JOB_LIFECYCLE_STAGES.map((stage, idx) => {
          const isActive = idx === currentIdx;
          const isPast = idx < currentIdx;
          const isLast = idx === JOB_LIFECYCLE_STAGES.length - 1;
          return (
            <React.Fragment key={stage.id}>
              <button
                type="button"
                onClick={() => onStageChange(stage.id)}
                title={`${stage.label} — ${stage.desc}`}
                className={`flex flex-col items-center gap-1.5 px-1 sm:px-2 transition-opacity ${isActive ? 'opacity-100' : isPast ? 'opacity-60 hover:opacity-90' : 'opacity-30 hover:opacity-60'}`}
              >
                <div className={`h-3 w-3 rounded-full transition-all ${isActive || isPast ? stage.dot : 'bg-slate-600'} ${isActive ? 'scale-125' : ''}`} />
                <div className={`hidden sm:block text-micro font-black uppercase tracking-[0.1em] ${isActive ? stage.textColor : isPast ? 'text-slate-500' : 'text-slate-700'}`}>{stage.label}</div>
              </button>
              {!isLast && <div className={`h-px flex-1 min-w-[8px] ${isPast ? 'bg-slate-500' : 'bg-slate-700/50'}`} />}
            </React.Fragment>
          );
        })}
      </div>

      {/* Current stage label + navigation */}
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className={`inline-flex items-center gap-2 rounded-full border bg-white/5 px-3 py-1.5 text-meta font-black uppercase tracking-[0.12em] ${currentStage.border} ${currentStage.textColor}`}>
            <div className={`h-2 w-2 rounded-full ${currentStage.dot}`} />
            {currentStage.label}
          </div>
          {stageAgeLabel && <div className="mt-1.5 text-micro text-slate-500">In this stage · {stageAgeLabel}</div>}
        </div>
        <div className="flex items-center gap-2">
          {prevStage && (
            <button
              type="button"
              onClick={() => onStageChange(prevStage.id)}
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.12em] text-slate-300 transition hover:border-white/20 hover:text-white"
            >
              ← {prevStage.label}
            </button>
          )}
          {nextStage && (
            <button
              type="button"
              onClick={() => onStageChange(nextStage.id)}
              className="inline-flex items-center gap-1.5 rounded-full bg-cyan-500 px-4 py-2 text-micro font-black uppercase tracking-[0.12em] text-slate-950 transition hover:bg-cyan-400"
            >
              {nextStage.label} <ChevronRight size={11} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function JobPublishingConsole({ job, snapshot, publishing, canPublish, onUpdatePublishing }) {
  if (!job || !publishing) return null;
  const status = getJobPublishStatus(job);
  const health = publishing.health || buildJobPublishingHealth(job, snapshot, {});
  const sourceRows = Object.entries(health.sourceCounts || {}).sort((a, b) => b[1] - a[1]).slice(0, 4);
  const scoreTone = health.score >= 88 ? 'text-emerald-200 border-emerald-400/25 bg-emerald-500/10' : health.score >= 65 ? 'text-amber-100 border-amber-400/25 bg-amber-500/10' : 'text-rose-100 border-rose-400/25 bg-rose-500/10';
  const copyUrl = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      onUpdatePublishing({}, `${label} copied.`);
    } catch (_error) {
      onUpdatePublishing({}, `Copy failed. Open ${label.toLowerCase()} and copy from the browser.`);
    }
  };

  return (
    <div className="mt-6 rounded-shell border border-emerald-400/15 bg-emerald-500/[0.055] p-5 sm:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <div className="text-sm font-black uppercase tracking-[0.18em] text-emerald-100">Publishing Command Center</div>
          <div className="mt-2 text-sm text-slate-400">Public board, SEO readiness, application routing, and source tracking stay connected to this job record.</div>
        </div>
        <div className={`rounded-full border px-4 py-2 text-micro font-black uppercase tracking-[0.16em] ${scoreTone}`}>{health.score}% · {health.label}</div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {[
          { label: 'Agency board', url: publishing.careersUrl },
          { label: 'Client board', url: publishing.clientBoardUrl },
          { label: 'Job page', url: publishing.jobUrl },
        ].map((item) => (
          <div key={item.label} className="rounded-card border border-white/10 bg-slate-950/60 p-4">
            <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{item.label}</div>
            <a href={item.url} target="_blank" rel="noreferrer" className="mt-2 block truncate text-xs font-semibold text-cyan-200 underline decoration-cyan-400/30">{item.url}</a>
            <button type="button" onClick={() => copyUrl(item.url, item.label)} className="mt-3 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-300 transition hover:border-white/20 hover:text-white">Copy link</button>
          </div>
        ))}
      </div>

      <div className="mt-5 grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
          <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Controls</div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Publish status</span>
              <select value={status} disabled={!canPublish} onChange={(event) => onUpdatePublishing({ publishStatus: event.target.value })} className="mt-2 w-full rounded-card border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400 disabled:opacity-60">
                {['Live', 'Draft', 'Paused', 'Closed'].map((option) => <option key={option} value={option}>{option}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Routing owner</span>
              <input value={job.applicationRoutingOwner || job.ownerName || job.recruiter || ''} disabled={!canPublish} onChange={(event) => onUpdatePublishing({ applicationRoutingOwner: event.target.value })} className="mt-2 w-full rounded-card border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400 disabled:opacity-60" placeholder="Recruiter or account manager" />
            </label>
            <label className="block">
              <span className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Application route</span>
              <select value={job.applicationRoutingMode || 'pipeline'} disabled={!canPublish} onChange={(event) => onUpdatePublishing({ applicationRoutingMode: event.target.value })} className="mt-2 w-full rounded-card border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400 disabled:opacity-60">
                <option value="pipeline">Pipeline + screening</option>
                <option value="client_review">Client review shortlist</option>
                <option value="sales_owner">Sales owner triage</option>
              </select>
            </label>
            <label className="block">
              <span className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Campaign tag</span>
              <input value={job.trackingCampaign || ''} disabled={!canPublish} onChange={(event) => onUpdatePublishing({ trackingCampaign: event.target.value })} className="mt-2 w-full rounded-card border border-white/10 bg-slate-950 px-3 py-2 text-sm text-white outline-none focus:border-emerald-400 disabled:opacity-60" placeholder="linkedin-may, client-board" />
            </label>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {[
              ['showSalaryPublic', 'Show salary'],
              ['showClientNamePublic', 'Show client'],
            ].map(([field, label]) => (
              <button key={field} type="button" disabled={!canPublish} onClick={() => onUpdatePublishing({ [field]: job[field] === false })} className={`rounded-full border px-3 py-2 text-micro font-black uppercase tracking-[0.12em] transition disabled:opacity-60 ${job[field] === false ? 'border-white/10 bg-white/5 text-slate-400' : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100'}`}>{label}</button>
            ))}
          </div>
        </div>

        <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">SEO + routing checks</div>
            <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-400">{snapshot?.applications?.length || 0} applications tracked</div>
          </div>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {health.checks.map((check) => (
              <div key={check.id} className={`rounded-card border px-3 py-2 text-xs font-semibold ${check.ok ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-100' : 'border-amber-400/20 bg-amber-500/10 text-amber-100'}`}>
                {check.ok ? '✓' : '!'} {check.label}
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-card border border-white/10 bg-white/[0.04] p-3">
            <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Source tracking</div>
            <div className="mt-2 space-y-2">
              {sourceRows.length ? sourceRows.map(([source, count]) => (
                <div key={source} className="flex items-center justify-between gap-3 text-xs text-slate-300">
                  <span className="truncate">{source}</span>
                  <span className="font-black text-white">{count}</span>
                </div>
              )) : <div className="text-xs text-slate-500">No tracked applications yet. Public applies will flow into this job and pipeline.</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function JobCard({ job, snapshot, onOpen, onCopyAiMessage, onDistribute, jobContacts = [], directDemoGuideHighlightOpen = false }) {

  const agency = INITIAL_AGENCIES.find((item) => item.id === job.agencyId);

  const companyName = getJobCompanyName(job);

  const stats = [

    { label: 'Candidates', value: snapshot?.matchedCandidatesCount || 0 },

    { label: 'Prospects', value: snapshot?.prospectsCount || 0 },

    { label: 'Screenings', value: snapshot?.screeningCompletedCount || 0 },

  ];

  const detailItems = [

    { label: 'Location', value: job.location || 'Remote' },

    { label: 'Type', value: job.type || 'Full-time' },

    { label: 'Compensation', value: job.salary || 'Compensation on request' },

  ];

  return (

    <div className="rounded-shell border border-white/10 bg-white/5 p-5 text-left transition hover:border-cyan-400/30 hover:bg-white/[0.07] sm:p-6">

      <button type="button" onClick={() => onOpen(job.id)} className="w-full text-left">

        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">

          <div className="min-w-0">

            <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">ATS-ready role</div>

            <div className="mt-2 text-lg font-black leading-tight text-white">{job.title}</div>

            <div className="mt-2 flex items-center gap-2">

              {job.companyLogoUrl && (

                <img

                  src={job.companyLogoUrl}

                  alt=""

                  className="h-6 w-6 shrink-0 rounded object-contain bg-white/10"

                  onError={(e) => { e.currentTarget.style.display = 'none'; }}

                />

              )}

              <span className="text-sm text-slate-300">{companyName || (agency ? agency.name : 'Client not identified')}</span>

            </div>

            {job.ownerName && (
              <div className="mt-1.5 inline-flex items-center gap-1 rounded-full border border-violet-400/20 bg-violet-500/10 px-2.5 py-0.5 text-micro font-black uppercase tracking-[0.12em] text-violet-300">
                <User size={9} /> {job.ownerName}
              </div>
            )}
            {(() => {
              const ls = JOB_LIFECYCLE_STAGES.find((s) => s.id === (job.lifecycleStage || 'brief'));
              return ls ? (
                <div className={`mt-1.5 inline-flex items-center gap-1.5 rounded-full border bg-white/5 px-2.5 py-0.5 text-micro font-black uppercase tracking-[0.1em] ${ls.border} ${ls.textColor}`}>
                  <div className={`h-1.5 w-1.5 rounded-full ${ls.dot}`} />{ls.label}
                  {job.isBlocked && <Flag size={8} className="ml-0.5 text-rose-400" />}
                </div>
              ) : null;
            })()}

          </div>

          <span

            className="shrink-0 rounded-full px-3 py-1.5 text-micro font-bold uppercase tracking-widest text-white"

            style={{ backgroundColor: agency ? agency.color : '#334155' }}

          >

            {job.status}

          </span>

        </div>

        <div className="mt-5 rounded-shell border border-white/10 bg-slate-950/50 p-4 sm:p-5">

          <p className="text-sm leading-7 text-slate-300">{job.summary || 'Vision could not recover a clean description for this role.'}</p>

        </div>

        <div className={`mt-5 rounded-shell border p-4 sm:p-5 ${job.requirementsSource === 'unavailable' ? 'border-amber-400/25 bg-amber-500/[0.06]' : 'border-emerald-400/20 bg-emerald-500/[0.05]'}`}>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-400">Requirements</div>
            <span className={`rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] ${job.requirementsSource === 'application_html' || job.requirementsSource === 'application_rendered' ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200' : job.requirementsSource === 'unavailable' ? 'border-amber-400/25 bg-amber-500/10 text-amber-200' : 'border-white/10 bg-white/5 text-slate-300'}`}>
              {job.requirementsSource === 'application_html' || job.requirementsSource === 'application_rendered' ? 'Verified from application' : job.requirementsSource === 'unavailable' ? 'Not verified' : 'Captured'}
            </span>
          </div>
          <div className="mt-3 max-h-72 overflow-y-auto pr-2">
            <RequirementsContent value={job.requirements} />
          </div>
          {job.applyUrl && (
            <a href={job.applyUrl} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()} className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-200 underline decoration-cyan-400/30">
              <ExternalLink size={11} /> Open application source
            </a>
          )}
          {job.requirementsSource === 'unavailable' && (
            <div className="mt-2 text-xs leading-5 text-amber-100/80">Requirements were not verified from the employer application page.</div>
          )}
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">

          {stats.map((item) => (

            <div key={item.label} className="rounded-card border border-white/10 bg-white/[0.04] px-4 py-3">

              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{item.label}</div>

              <div className="mt-2 text-xl font-black text-white">{item.value}</div>

            </div>

          ))}

        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-3">

          {detailItems.map((item) => (

            <div key={item.label} className="rounded-card border border-white/10 bg-slate-950/50 px-4 py-3">

              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{item.label}</div>

              <div className="mt-2 text-sm font-semibold text-slate-200">{item.value}</div>

            </div>

          ))}

        </div>

      </button>

      <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-white/10 pt-5">

        <button type="button" data-direct-demo-target="job-open-button" onClick={() => onOpen(job.id)} className={`inline-flex items-center gap-2 rounded-full bg-cyan-500 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-400 ${directDemoGuideHighlightOpen ? 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]' : ''}`}>Open ATS panel</button>

        <button type="button" onClick={() => onCopyAiMessage(job)} className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-cyan-200 transition hover:border-cyan-300/40 hover:text-cyan-100">

          <Sparkles size={12} /> AI

        </button>

        <button type="button" onClick={() => onDistribute(job)} className="inline-flex items-center gap-2 rounded-full border border-violet-400/20 bg-violet-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-violet-200 transition hover:border-violet-300/40 hover:text-violet-100">

          <Share2 size={12} /> Distribute

        </button>

        {job.sourceUrl && (

          <a href={job.sourceUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white">

            <ExternalLink size={12} /> Open source

          </a>

        )}

      </div>

      {jobContacts.length > 0 && (
        <div className="mt-4 border-t border-white/10 pt-4 space-y-2">
          <div className="text-micro font-black uppercase tracking-[0.16em] text-emerald-400">Hiring contacts</div>
          {jobContacts.map((c) => (
            <div key={c.id} className="flex items-start gap-3 rounded-card border border-emerald-400/10 bg-emerald-500/5 px-4 py-3">
              <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-500/20 text-meta font-black text-emerald-300">
                {(c.name || '?')[0].toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-bold text-white leading-tight">{c.name || 'Unknown'}</div>
                {c.title && <div className="mt-0.5 text-meta text-slate-400">{c.title}{c.company ? ` · ${c.company}` : ''}</div>}
                <div className="mt-1.5 flex flex-wrap gap-2">
                  {c.email && (
                    <a href={`mailto:${c.email}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1 text-micro font-black text-cyan-300 hover:text-cyan-200">
                      <Mail size={10} /> {c.email}
                    </a>
                  )}
                  {c.phone && (
                    <a href={`tel:${c.phone}`} onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-full border border-violet-400/20 bg-violet-500/10 px-2.5 py-1 text-micro font-black text-violet-300 hover:text-violet-200">
                      <Phone size={10} /> {c.phone}
                    </a>
                  )}
                  {c.linkedInUrl && (
                    <a href={cleanLinkedInUrl(c.linkedInUrl)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="inline-flex items-center gap-1 rounded-full border border-blue-400/20 bg-blue-500/10 px-2.5 py-1 text-micro font-black text-blue-300 hover:text-blue-200">
                      <ExternalLink size={10} /> LinkedIn
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

    </div>

  );

}

// ─── CRM Task constants ────────────────────────────────────────────────────────
// ─── Notification Bell ────────────────────────────────────────────────────────
const NOTIF_TYPE_STYLES = {
  task_reminder:  { dot: 'bg-amber-400',  label: 'Task' },
  gmail_sync:     { dot: 'bg-cyan-400',   label: 'Gmail' },
  overdue_tasks:  { dot: 'bg-rose-400',   label: 'Overdue' },
  calendar:       { dot: 'bg-violet-400', label: 'Calendar' },
  assignment:     { dot: 'bg-emerald-400',label: 'Assigned' },
  team_message:   { dot: 'bg-violet-400', label: 'Team' },
  system:         { dot: 'bg-slate-400',  label: 'System' },
};

function NotificationBell({ notifications, onMarkRead, onMarkAllRead, onOpenNotification }) {
  const [open, setOpen] = React.useState(false);
  const unreadCount = notifications.filter((n) => !n.read).length;
  const notificationPanel = open ? (
    <div className="fixed right-3 top-16 z-[2147483647] w-[calc(100vw-1.5rem)] max-w-sm overflow-hidden rounded-card border border-white/10 bg-slate-950 shadow-2xl shadow-black/50 sm:right-6 sm:top-20">
      <div className="flex items-center justify-between border-b border-white/10 px-4 py-3">
        <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Notifications</div>
        <div className="flex items-center gap-3">
          {unreadCount > 0 && (
            <button type="button" onClick={() => { onMarkAllRead(); }} className="text-micro font-bold text-slate-400 hover:text-white">Mark all read</button>
          )}
          <button type="button" onClick={() => setOpen(false)} className="text-slate-500 hover:text-white"><X size={14} /></button>
        </div>
      </div>
      <div className="max-h-[420px] overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-slate-500">No notifications yet.</div>
        ) : (
          notifications.slice(0, 25).map((notif) => {
            const style = NOTIF_TYPE_STYLES[notif.type] || NOTIF_TYPE_STYLES.system;
            return (
              <div
                key={notif.id}
                onClick={() => {
                  if (!notif.read) onMarkRead(notif.id);
                  onOpenNotification?.(notif);
                  setOpen(false);
                }}
                className={`flex cursor-pointer gap-3 border-b border-white/5 px-4 py-3 transition hover:bg-white/5 ${notif.read ? 'opacity-55' : ''}`}
              >
                <div className="mt-1 flex-shrink-0">
                  <span className={`block h-2 w-2 rounded-full ${style.dot}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="truncate text-xs font-bold text-white">{notif.title || style.label}</div>
                    <div className="flex-shrink-0 text-micro text-slate-500">{notif.createdAtIso ? new Date(notif.createdAtIso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}</div>
                  </div>
                  <div className="mt-0.5 text-meta leading-4 text-slate-400">{notif.body || ''}</div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  ) : null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition hover:border-cyan-400/30 hover:text-white"
        aria-label="Notifications"
      >
        <Bell size={16} />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-cyan-400 text-micro font-black text-slate-950">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {typeof document !== 'undefined' && notificationPanel ? createPortal(notificationPanel, document.body) : null}
    </div>
  );
}

const CRM_TASK_TYPES = [
  { id: 'follow_up_call',   label: 'Follow-up call',   Icon: 'Phone'    },
  { id: 'send_email',       label: 'Send email',        Icon: 'Mail'     },
  { id: 'send_cv',          label: 'Send CV',           Icon: 'FileText' },
  { id: 'schedule_meeting', label: 'Schedule meeting',  Icon: 'Calendar' },
  { id: 'check_in',         label: 'Check-in',          Icon: 'User'     },
  { id: 'get_feedback',     label: 'Get feedback',      Icon: 'MessageSquare' },
  { id: 'other',            label: 'Other',             Icon: 'Zap'      },
];

const CRM_TASK_PRIORITY = ['Low', 'Medium', 'High', 'Urgent'];

const CRM_TASK_PRIORITY_STYLES = {
  Low:    'border-slate-500/30 bg-slate-700/20 text-slate-400',
  Medium: 'border-blue-400/30 bg-blue-500/10 text-blue-200',
  High:   'border-amber-400/30 bg-amber-500/10 text-amber-200',
  Urgent: 'border-rose-400/30 bg-rose-500/10 text-rose-200',
};

// ─── AccountTasks component (inside JobAtsModal right column) ─────────────────
function AccountTasks({ companyName, tasks, isSaving, onAdd, onComplete, onSnooze, onRemove }) {
  const BLANK = { type: 'follow_up_call', title: '', dueDate: '', priority: 'Medium', note: '', contactName: '' };
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(BLANK);
  const resetForm = () => { setForm(BLANK); setShowForm(false); };

  const pending = (tasks || []).filter((t) => t.status !== 'done');
  const done    = (tasks || []).filter((t) => t.status === 'done').slice(0, 5);
  const today   = new Date().toISOString().slice(0, 10);

  const dueBadge = (dueDate) => {
    if (!dueDate) return null;
    const diff = Math.round((new Date(dueDate).getTime() - Date.now()) / 86400000);
    if (diff < 0)  return <span className="rounded-full border border-rose-400/30 bg-rose-500/10 px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] text-rose-300">Overdue {Math.abs(diff)}d</span>;
    if (diff === 0) return <span className="rounded-full border border-amber-400/30 bg-amber-500/10 px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] text-amber-200">Due today</span>;
    if (diff <= 3)  return <span className="rounded-full border border-blue-400/30 bg-blue-500/10 px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] text-blue-200">In {diff}d</span>;
    return <span className="rounded-full border border-white/10 bg-slate-800/50 px-2 py-0.5 text-micro text-slate-500">{new Date(dueDate).toLocaleDateString()}</span>;
  };

  return (
    <div className="rounded-shell border border-white/10 bg-white/5 p-5">
      <div className="flex items-center justify-between">
        <div className="text-sm font-black uppercase tracking-[0.18em] text-white">
          Follow-up Tasks
          {pending.length > 0 && <span className="ml-2 rounded-full border border-cyan-400/30 bg-cyan-500/15 px-2 py-0.5 text-micro font-black text-cyan-300">{pending.length}</span>}
        </div>
        <button type="button" onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.12em] text-slate-200 transition hover:border-cyan-300/30 hover:text-white">
          <Plus size={11} /> Task
        </button>
      </div>

      {pending.length === 0 && !showForm && (
        <div className="mt-4 text-sm text-slate-500">No open tasks for {companyName || 'this account'}. Add a follow-up to keep the deal moving.</div>
      )}

      {pending.length > 0 && (
        <div className="mt-4 space-y-2">
          {pending.sort((a, b) => {
            const priorityOrder = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
            const pDiff = (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
            if (pDiff !== 0) return pDiff;
            return new Date(a.dueDate || '9999').getTime() - new Date(b.dueDate || '9999').getTime();
          }).map((task) => {
            const typeInfo = CRM_TASK_TYPES.find((t) => t.id === task.type) || CRM_TASK_TYPES[0];
            const priorityStyle = CRM_TASK_PRIORITY_STYLES[task.priority] || CRM_TASK_PRIORITY_STYLES.Medium;
            return (
              <div key={task.id} className="rounded-card border border-white/10 bg-slate-950/60 p-3.5">
                <div className="flex items-start gap-3">
                  <button type="button" onClick={() => onComplete(task.id)} title="Mark done" className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/5 transition hover:border-cyan-300/50 hover:bg-cyan-500/10">
                    <Check size={10} className="text-slate-500" />
                  </button>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="text-sm font-bold text-white">{task.title || typeInfo.label}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] ${priorityStyle}`}>{task.priority}</span>
                      {dueBadge(task.dueDate)}
                    </div>
                    {task.contactName && <div className="mt-0.5 text-xs text-slate-400">{task.contactName}</div>}
                    {task.note && <div className="mt-1 text-xs leading-5 text-slate-500">{task.note}</div>}
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button type="button" onClick={() => onSnooze(task.id, 1)} className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-micro font-black uppercase tracking-[0.1em] text-slate-400 hover:text-white">+1d</button>
                      <button type="button" onClick={() => onSnooze(task.id, 3)} className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-micro font-black uppercase tracking-[0.1em] text-slate-400 hover:text-white">+3d</button>
                      <button type="button" onClick={() => onSnooze(task.id, 7)} className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-micro font-black uppercase tracking-[0.1em] text-slate-400 hover:text-white">+1w</button>
                      <button type="button" onClick={() => onRemove(task.id)} className="rounded-full border border-rose-400/20 bg-rose-500/5 px-2 py-1 text-micro font-black uppercase tracking-[0.1em] text-rose-400 hover:bg-rose-500/10">Remove</button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {done.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-600">Completed</div>
          {done.map((task) => (
            <div key={task.id} className="flex items-center gap-2 rounded-card border border-white/5 bg-slate-900/40 px-3 py-2 text-xs text-slate-600 line-through">{task.title || task.type}</div>
          ))}
        </div>
      )}

      {showForm && (
        <form onSubmit={(ev) => { ev.preventDefault(); if (!String(form.title || '').trim()) return; onAdd({ ...form }); resetForm(); }} className="mt-4 space-y-3 rounded-card border border-cyan-400/20 bg-cyan-500/5 p-4">
          <div className="text-micro font-black uppercase tracking-[0.16em] text-cyan-300">New task</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <select value={form.type} onChange={(ev) => setForm((p) => ({ ...p, type: ev.target.value, title: CRM_TASK_TYPES.find((t) => t.id === ev.target.value)?.label || p.title }))} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">
              {CRM_TASK_TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
            <select value={form.priority} onChange={(ev) => setForm((p) => ({ ...p, priority: ev.target.value }))} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">
              {CRM_TASK_PRIORITY.map((p) => <option key={p}>{p}</option>)}
            </select>
            <input value={form.title} onChange={(ev) => setForm((p) => ({ ...p, title: ev.target.value }))} required placeholder="Task title" className="sm:col-span-2 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
            <input type="date" value={form.dueDate} min={today} onChange={(ev) => setForm((p) => ({ ...p, dueDate: ev.target.value }))} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
            <input value={form.contactName} onChange={(ev) => setForm((p) => ({ ...p, contactName: ev.target.value }))} placeholder="Assigned contact (optional)" className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
            <textarea value={form.note} onChange={(ev) => setForm((p) => ({ ...p, note: ev.target.value }))} rows={2} placeholder="Notes (optional)" className="sm:col-span-2 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={isSaving} className="rounded-full bg-cyan-400 px-5 py-2.5 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-300 disabled:opacity-60">{isSaving ? 'Saving...' : 'Add task'}</button>
            <button type="button" onClick={resetForm} className="rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-black uppercase tracking-[0.16em] text-slate-300 transition hover:text-white">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── DailyTaskQueue — surfaced at top of Jobs dashboard ───────────────────────
function DailyTaskQueue({ tasks, onComplete, onSnooze }) {
  const today = new Date().toISOString().slice(0, 10);
  const overdue = tasks.filter((t) => t.status !== 'done' && t.dueDate && t.dueDate < today);
  const dueToday = tasks.filter((t) => t.status !== 'done' && t.dueDate === today);
  const upcoming = tasks.filter((t) => t.status !== 'done' && (!t.dueDate || t.dueDate > today)).slice(0, 5);

  const combined = [
    ...overdue.map((t) => ({ ...t, _bucket: 'overdue' })),
    ...dueToday.map((t) => ({ ...t, _bucket: 'today' })),
    ...upcoming.map((t) => ({ ...t, _bucket: 'upcoming' })),
  ];

  if (combined.length === 0) return null;

  return (
    <div className="rounded-shell border border-amber-400/20 bg-amber-500/[0.06] p-5 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.22em] text-amber-300">
            <Clock3 size={14} /> Daily Follow-up Queue
          </div>
          <div className="mt-1 text-sm text-slate-400">
            {overdue.length > 0 && <span className="mr-2 font-bold text-rose-300">{overdue.length} overdue</span>}
            {dueToday.length > 0 && <span className="mr-2 font-bold text-amber-200">{dueToday.length} due today</span>}
            {upcoming.length > 0 && <span className="text-slate-400">{upcoming.length} upcoming</span>}
          </div>
        </div>
        <div className="rounded-full border border-amber-400/20 bg-amber-500/10 px-4 py-2 text-meta font-black uppercase tracking-[0.16em] text-amber-200">{combined.length} open</div>
      </div>
      <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
        {combined.map((task) => {
          const bucketStyle = task._bucket === 'overdue'
            ? 'border-rose-400/20 bg-rose-500/[0.06]'
            : task._bucket === 'today'
              ? 'border-amber-400/20 bg-amber-500/[0.06]'
              : 'border-white/10 bg-slate-950/60';
          const priorityStyle = CRM_TASK_PRIORITY_STYLES[task.priority] || CRM_TASK_PRIORITY_STYLES.Medium;
          return (
            <div key={task.id} className={`rounded-card border p-3.5 ${bucketStyle}`}>
              <div className="flex items-start gap-2">
                <button type="button" onClick={() => onComplete(task.id)} title="Mark done" className="mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-white/20 bg-white/5 transition hover:border-cyan-300/50 hover:bg-cyan-500/10">
                  <Check size={10} className="text-slate-500" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="truncate text-sm font-bold text-white">{task.title || task.type}</span>
                    <span className={`rounded-full border px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] ${priorityStyle}`}>{task.priority}</span>
                  </div>
                  {task.companyName && <div className="mt-0.5 truncate text-xs text-slate-400">{task.companyName}{task.contactName ? ` · ${task.contactName}` : ''}</div>}
                  {task.jobTitle && <div className="mt-0.5 truncate text-micro text-slate-500">{task.jobTitle}</div>}
                  <div className="mt-2 flex gap-1.5">
                    <button type="button" onClick={() => onSnooze(task.id, 1)} className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-micro font-black uppercase tracking-[0.1em] text-slate-400 hover:text-white">+1d</button>
                    <button type="button" onClick={() => onSnooze(task.id, 3)} className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-micro font-black uppercase tracking-[0.1em] text-slate-400 hover:text-white">+3d</button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Schedule Meeting Modal ────────────────────────────────────────────────────
function ScheduleMeetingModal({ isOpen, companyName, jobId, prefillContactEmail, onSchedule, isBusy, onClose }) {
  const [form, setForm] = React.useState({ title: '', date: '', startTime: '09:00', endTime: '10:00', attendees: '', description: '' });
  const [notice, setNotice] = React.useState('');
  React.useEffect(() => {
    if (isOpen) {
      setForm({ title: companyName ? `Meeting with ${companyName}` : '', date: '', startTime: '09:00', endTime: '10:00', attendees: prefillContactEmail || '', description: '' });
      setNotice('');
    }
  }, [isOpen, companyName, prefillContactEmail]);

  if (!isOpen) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.date) { setNotice('Title and date are required.'); return; }
    const startIso = new Date(`${form.date}T${form.startTime}:00Z`).toISOString();
    const endIso = new Date(`${form.date}T${form.endTime}:00Z`).toISOString();
    if (new Date(endIso) <= new Date(startIso)) { setNotice('End time must be after start time.'); return; }
    const attendeeEmails = form.attendees.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => s.includes('@'));
    setNotice('');
    await onSchedule({ title: form.title.trim(), startIso, endIso, attendeeEmails, description: form.description.trim(), companyName, jobId });
    onClose();
  };
  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md overflow-hidden rounded-shell border border-white/10 bg-slate-900 shadow-2xl">
        <div className="flex items-center justify-between border-b border-white/10 px-6 py-4">
          <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Schedule Meeting</div>
          <button type="button" onClick={onClose} className="text-slate-500 hover:text-white"><X size={16} /></button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4 p-6">
          <label className="block">
            <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-500">Title *</div>
            <input type="text" value={form.title} onChange={set('title')} required className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400" placeholder="Discovery call, proposal review…" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            <label className="col-span-3 block sm:col-span-1">
              <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-500">Date *</div>
              <input type="date" value={form.date} onChange={set('date')} required className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400" />
            </label>
            <label className="block">
              <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-500">Start</div>
              <input type="time" value={form.startTime} onChange={set('startTime')} className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400" />
            </label>
            <label className="block">
              <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-500">End</div>
              <input type="time" value={form.endTime} onChange={set('endTime')} className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400" />
            </label>
          </div>
          <label className="block">
            <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-500">Attendee Emails</div>
            <input type="text" value={form.attendees} onChange={set('attendees')} className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400" placeholder="alice@company.com, bob@company.com" />
          </label>
          <label className="block">
            <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-500">Description</div>
            <textarea value={form.description} onChange={set('description')} rows={2} className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-3 py-2 text-sm text-white outline-none focus:border-cyan-400" placeholder="Agenda, notes…" />
          </label>
          {notice && <div className="rounded-ctl border border-amber-400/25 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">{notice}</div>}
          <div className="flex justify-end gap-3 pt-1">
            <button type="button" onClick={onClose} className="rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-slate-300 hover:text-white">Cancel</button>
            <button type="submit" disabled={isBusy} className="rounded-full bg-cyan-500 px-5 py-2.5 text-xs font-black uppercase tracking-[0.14em] text-slate-950 hover:bg-cyan-400 disabled:opacity-60">{isBusy ? 'Scheduling…' : 'Schedule'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const CONTACT_ROLES = ['Hiring Manager', 'Economic Buyer', 'HR Lead', 'Executive Sponsor', 'Technical Interviewer', 'Gatekeeper', 'Champion', 'Other'];

const CONTACT_ROLE_STYLES = {
  'Hiring Manager': 'border-cyan-400/30 bg-cyan-500/10 text-cyan-200',
  'Economic Buyer': 'border-violet-400/30 bg-violet-500/10 text-violet-200',
  'HR Lead': 'border-blue-400/30 bg-blue-500/10 text-blue-200',
  'Executive Sponsor': 'border-amber-400/30 bg-amber-500/10 text-amber-200',
  'Technical Interviewer': 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200',
  'Gatekeeper': 'border-rose-400/30 bg-rose-500/10 text-rose-200',
  'Champion': 'border-pink-400/30 bg-pink-500/10 text-pink-200',
  'Other': 'border-slate-400/20 bg-slate-500/10 text-slate-300',
};

const CONTACT_SENTIMENT_STYLES = {
  'Positive': 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200',
  'Neutral': 'border-slate-400/20 bg-slate-500/10 text-slate-300',
  'Ghosted': 'border-amber-400/30 bg-amber-500/10 text-amber-200',
  'Negative': 'border-rose-400/30 bg-rose-500/10 text-rose-200',
};

function AccountContacts({ companyName, contacts, isSaving, onAdd, onUpdate, onRemove, onLogActivity, onScheduleMeeting }) {
  const BLANK = { name: '', email: '', phone: '', role: 'Hiring Manager', linkedIn: '', notes: '' };
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(BLANK);

  const resetForm = () => { setForm(BLANK); setShowForm(false); setEditingId(null); };

  const handleSubmit = (ev) => {
    ev.preventDefault();
    if (!String(form.name || '').trim()) return;
    if (editingId) {
      onUpdate(editingId, { ...form });
    } else {
      onAdd({ ...form });
    }
    resetForm();
  };

  const startEdit = (contact) => {
    setForm({ name: contact.name || '', email: contact.email || '', phone: contact.phone || '', role: contact.role || 'Hiring Manager', linkedIn: contact.linkedIn || '', notes: contact.notes || '' });
    setEditingId(contact.id);
    setShowForm(true);
  };

  const manualContacts = (contacts || []).filter((c) => c.source !== 'job_order' && c.source !== 'opportunity');
  const inferredContacts = (contacts || []).filter((c) => c.source === 'job_order' || c.source === 'opportunity');
  const ordered = [...manualContacts, ...inferredContacts];

  return (
    <div className="rounded-shell border border-white/10 bg-white/5 p-5">
      <div className="flex items-center justify-between">
        <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Stakeholders <span className="ml-1 text-xs font-normal normal-case tracking-normal text-slate-500">{ordered.length > 0 ? `${ordered.length} contact${ordered.length !== 1 ? 's' : ''}` : ''}</span></div>
        <button type="button" onClick={() => { resetForm(); setShowForm(true); }} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.12em] text-slate-200 transition hover:border-cyan-300/30 hover:text-white">
          <Plus size={11} /> Add
        </button>
      </div>

      {ordered.length === 0 && !showForm && (
        <div className="mt-4 text-sm text-slate-500">No contacts recorded for {companyName || 'this account'}. Add stakeholders to track hiring manager relationships.</div>
      )}

      {ordered.length > 0 && (
        <div className="mt-4 space-y-3">
          {ordered.map((contact) => {
            const initials = String(contact.name || '?').split(' ').map((n) => n[0]).slice(0, 2).join('').toUpperCase();
            const roleStyle = CONTACT_ROLE_STYLES[contact.role] || CONTACT_ROLE_STYLES['Other'];
            const sentimentStyle = contact.sentiment ? (CONTACT_SENTIMENT_STYLES[contact.sentiment] || CONTACT_SENTIMENT_STYLES['Neutral']) : null;
            const isInferred = contact.source === 'job_order' || contact.source === 'opportunity';
            return (
              <div key={contact.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-slate-700 text-meta font-black text-slate-300">{initials}</div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-sm font-bold text-white">{contact.name || '—'}</span>
                      <span className={`rounded-full border px-2 py-0.5 text-micro font-black uppercase tracking-[0.12em] ${roleStyle}`}>{contact.role || 'Contact'}</span>
                      {sentimentStyle && <span className={`rounded-full border px-2 py-0.5 text-micro font-black uppercase tracking-[0.12em] ${sentimentStyle}`}>{contact.sentiment}</span>}
                      {isInferred && <span className="rounded-full border border-slate-600/30 bg-slate-700/20 px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] text-slate-500">inferred</span>}
                    </div>
                    {contact.email && <div className="mt-1 truncate text-xs text-slate-400">{contact.email}</div>}
                    {contact.phone && <div className="mt-0.5 text-xs text-slate-500">{contact.phone}</div>}
                    {contact.linkedIn && (
                      <a href={cleanLinkedInUrl(contact.linkedIn)} target="_blank" rel="noopener noreferrer" className="mt-0.5 inline-flex items-center gap-1 text-xs text-cyan-400 hover:underline"><ExternalLink size={10} /> LinkedIn</a>
                    )}
                    {contact.notes && <div className="mt-2 text-xs leading-5 text-slate-500">{contact.notes}</div>}
                    <div className="mt-3 flex flex-wrap gap-2">
                      {onLogActivity && <>
                        <button type="button" onClick={() => onLogActivity(contact, 'call')} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-300 transition hover:border-cyan-300/30 hover:text-white"><Phone size={10} /> Call</button>
                        <button type="button" onClick={() => onLogActivity(contact, 'email')} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-300 transition hover:border-cyan-300/30 hover:text-white"><Mail size={10} /> Email</button>
                        {onScheduleMeeting && <button type="button" onClick={() => onScheduleMeeting(contact)} className="inline-flex items-center gap-1 rounded-full border border-violet-400/25 bg-violet-500/10 px-2.5 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-violet-200 transition hover:bg-violet-500/20"><Calendar size={10} /> Meet</button>}
                      </>}
                      {!isInferred && <>
                        <button type="button" onClick={() => startEdit(contact)} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-300 transition hover:text-white">Edit</button>
                        <button type="button" onClick={() => onRemove(contact.id)} className="inline-flex items-center gap-1 rounded-full border border-rose-400/20 bg-rose-500/5 px-2.5 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-rose-300 transition hover:bg-rose-500/10">Remove</button>
                      </>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="mt-4 space-y-3 rounded-card border border-cyan-400/20 bg-cyan-500/5 p-4">
          <div className="text-micro font-black uppercase tracking-[0.16em] text-cyan-300">{editingId ? 'Edit contact' : 'New contact'}</div>
          <div className="grid gap-3 sm:grid-cols-2">
            <input value={form.name} onChange={(ev) => setForm((prev) => ({ ...prev, name: ev.target.value }))} required placeholder="Full name" className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
            <select value={form.role} onChange={(ev) => setForm((prev) => ({ ...prev, role: ev.target.value }))} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">
              {CONTACT_ROLES.map((r) => <option key={r}>{r}</option>)}
            </select>
            <input type="email" value={form.email} onChange={(ev) => setForm((prev) => ({ ...prev, email: ev.target.value }))} placeholder="Email address" className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
            <input value={form.phone} onChange={(ev) => setForm((prev) => ({ ...prev, phone: ev.target.value }))} placeholder="Phone number" className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
            <input value={form.linkedIn} onChange={(ev) => setForm((prev) => ({ ...prev, linkedIn: ev.target.value }))} placeholder="LinkedIn URL" className="sm:col-span-2 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
            <textarea value={form.notes} onChange={(ev) => setForm((prev) => ({ ...prev, notes: ev.target.value }))} rows={2} placeholder="e.g. Decision-maker, tough gatekeeper, champion for this hire" className="sm:col-span-2 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={isSaving} className="rounded-full bg-cyan-400 px-5 py-2.5 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-300 disabled:opacity-60">{isSaving ? 'Saving...' : editingId ? 'Update contact' : 'Add contact'}</button>
            <button type="button" onClick={resetForm} className="rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-xs font-black uppercase tracking-[0.16em] text-slate-300 transition hover:text-white">Cancel</button>
          </div>
        </form>
      )}
    </div>
  );
}

function AccountTimeline({ companyName, entries, isSaving, onAdd }) {
  const [entryType, setEntryType] = useState('note');
  const [entryText, setEntryText] = useState('');

  const ENTRY_TYPES = [
    { id: 'note', label: 'Note', Icon: FileText },
    { id: 'call', label: 'Call', Icon: Phone },
    { id: 'email', label: 'Email', Icon: Mail },
    { id: 'meeting', label: 'Meeting', Icon: Calendar },
  ];

  function getEntryMeta(entry) {
    const t = String(entry.type || '').toLowerCase();
    if (t.includes('call')) return { label: 'Call', iconEl: <Phone size={12} />, colorClass: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/20' };
    if (t.includes('email')) return { label: 'Email', iconEl: <Mail size={12} />, colorClass: 'bg-blue-500/15 text-blue-200 border-blue-500/20' };
    if (t.includes('meeting') || t === 'meeting_scheduled') return { label: 'Meeting', iconEl: <Calendar size={12} />, colorClass: 'bg-violet-500/15 text-violet-200 border-violet-500/20' };
    if (t.includes('note')) return { label: 'Note', iconEl: <FileText size={12} />, colorClass: 'bg-cyan-500/15 text-cyan-200 border-cyan-500/20' };
    if (t.includes('submission') || t.includes('candidate_added')) return { label: 'Submission', iconEl: <User size={12} />, colorClass: 'bg-amber-500/15 text-amber-200 border-amber-500/20' };
    if (t.includes('screening')) return { label: 'Screening', iconEl: <ShieldCheck size={12} />, colorClass: 'bg-violet-500/15 text-violet-200 border-violet-500/20' };
    if (t.includes('reply')) return { label: 'Reply', iconEl: <MessageSquare size={12} />, colorClass: 'bg-emerald-500/15 text-emerald-200 border-emerald-500/20' };
    if (t.includes('stage')) return { label: 'Stage move', iconEl: <Zap size={12} />, colorClass: 'bg-slate-500/15 text-slate-300 border-slate-500/20' };
    if (t.includes('sequence_started') || t.includes('sequence')) return { label: 'Sequence', iconEl: <Zap size={12} />, colorClass: 'bg-cyan-500/15 text-cyan-200 border-cyan-500/20' };
    return { label: 'Activity', iconEl: <Clock3 size={12} />, colorClass: 'bg-white/5 text-slate-300 border-white/10' };
  }

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!entryText.trim() || isSaving) return;
    onAdd(entryType, entryText.trim());
    setEntryText('');
  };

  return (
    <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Account Timeline</div>
          <div className="mt-1 text-xs text-slate-400">
            Every interaction, submission, screening, and next step for{' '}
            <span className="font-semibold text-slate-200">{companyName || 'this client'}</span> in one place.
          </div>
        </div>
        <div className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-300">{entries.length} events</div>
      </div>

      {/* ── Log-entry form ────────────────────────────────────── */}
      <form onSubmit={handleSubmit} className="mt-5 rounded-card border border-cyan-400/15 bg-cyan-500/5 p-4">
        <div className="mb-3 flex flex-wrap gap-2">
          {ENTRY_TYPES.map(({ id, label, Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setEntryType(id)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-micro font-black uppercase tracking-[0.14em] transition ${
                entryType === id
                  ? 'border-cyan-400/40 bg-cyan-500/20 text-cyan-100'
                  : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-slate-200'
              }`}
            >
              <Icon size={11} /> {label}
            </button>
          ))}
        </div>
        <textarea
          value={entryText}
          onChange={(event) => setEntryText(event.target.value)}
          rows={3}
          placeholder={
            entryType === 'note' ? 'Add a note about this client or role…' :
            entryType === 'call' ? 'Summarise the call — outcome, objections, next step…' :
            entryType === 'email' ? 'What was the email about? Any reply?' :
            'Meeting notes or agreed next steps…'
          }
          className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400"
        />
        <div className="mt-3 flex justify-end">
          <button
            type="submit"
            disabled={!entryText.trim() || isSaving}
            className="rounded-full bg-cyan-500 px-5 py-2.5 text-micro font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? 'Saving…' : 'Log entry'}
          </button>
        </div>
      </form>

      {/* ── Timeline feed ─────────────────────────────────────── */}
      <div className="mt-5 space-y-0">
        {entries.length === 0 && (
          <div className="rounded-card border border-dashed border-white/10 px-4 py-6 text-center text-sm text-slate-500">
            No activity logged yet. Use the form above to log the first note, call, email, or meeting.
          </div>
        )}
        {entries.map((entry, idx) => {
          const meta = getEntryMeta(entry);
          const isLast = idx === entries.length - 1;
          return (
            <div key={entry.id} className="relative flex gap-4 pb-4">
              {!isLast && (
                <div className="absolute left-[19px] top-10 bottom-0 w-px bg-white/10" />
              )}
              <div className={`relative z-10 mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border ${meta.colorClass}`}>
                {meta.iconEl}
              </div>
              <div className="flex-1 min-w-0 rounded-card border border-white/10 bg-slate-950/60 px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.14em] ${meta.colorClass}`}>
                    {meta.label}
                  </span>
                  <span className="text-micro text-slate-500">
                    {entry.createdAtIso
                      ? new Date(entry.createdAtIso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
                      : 'Now'}
                  </span>
                </div>
                <div className="mt-2 text-sm leading-6 text-slate-200">{entry.summary}</div>
                {entry.contact && (
                  <div className="mt-1 text-meta text-slate-500">{entry.contact}</div>
                )}
                {entry.metadata?.source === 'gmail_sync' && (
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 rounded-full border border-blue-400/25 bg-blue-500/10 px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] text-blue-300">
                      <Mail size={9} /> Gmail
                    </span>
                    {entry.metadata?.subject && <span className="text-micro text-slate-500 truncate max-w-[240px]">{entry.metadata.subject}</span>}
                    {entry.metadata?.snippet && <span className="text-micro text-slate-600 truncate max-w-[200px]">{entry.metadata.snippet}</span>}
                  </div>
                )}
                {entry.metadata?.source === 'google_calendar' && entry.metadata?.calendarEventLink && (
                  <div className="mt-2">
                    <a href={entry.metadata.calendarEventLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-micro text-violet-300 hover:underline">
                      <ExternalLink size={9} /> Open in Google Calendar
                    </a>
                  </div>
                )}
                {entry.type === 'meeting_scheduled' && entry.metadata?.source !== 'google_calendar' && entry.metadata?.startIso && (
                  <div className="mt-1 text-micro text-slate-500">
                    {new Date(entry.metadata.startIso).toLocaleString('en-GB', { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function JobAtsModal({

  job,

  snapshot,

  notesDraft,

  isSavingNotes,

  sequenceRuns,

  sequenceAnalytics,

  sequenceControls,

  sequenceSuppressions,

  sequenceTasks,

  sequenceTaskNotes,

  sequenceNotice,

  activeSequenceTargetKey,

  activeSequenceTaskId,

  activeSequenceApiTaskId,

  activeSequenceSuppressionId,

  isProcessingSequenceRuns,

  isSavingSequenceControls,

  hiddenLegacySequenceRunCount,
  directDemoGuideHighlight,

  onClose,

  onNotesChange,

  onSaveNotes,

  onPushProspect,

  onOpenPipeline,

  onOpenScreening,

  onExportCsv,

  onStartCandidateSequence,

  onStartProspectSequence,

  onCompleteSequenceTask,

  onSendSequenceWhatsAppTask,

  onSequenceTaskNoteChange,

  onSequenceControlsChange,

  onSaveSequenceControls,

  onClearSequenceSuppression,

  onProcessSequenceRuns,

  onOpenEeocEdit,

  accountTimeline,

  isSavingTimelineEntry,

  onAddTimelineEntry,

  companyContacts,

  isSavingCompanyContact,

  onAddContact,

  onUpdateContact,

  onRemoveContact,

  onLogContactActivity,

  crmTasks,

  isSavingCrmTask,

  onAddCrmTask,

  onCompleteCrmTask,

  onSnoozeCrmTask,

  onRemoveCrmTask,

  onScheduleMeeting,
  onSetJobStage,
  onToggleJobBlocked,
  publishing,
  canPublish,
  onUpdatePublishing,
  onRemoveJob,
  canRemoveJob,

}) {

  if (!job || !snapshot) return null;

  const companyName = getJobCompanyName(job);

  const openSequenceTaskCount = sequenceTasks.filter((task) => task.status === 'open').length;

  const stageEntries = PIPELINE_STAGES.map((stage) => ({

    ...stage,

    count: snapshot.pipelineCounts[stage.id] || 0,

  }));

  const headerStats = [

    { label: 'Applications', value: snapshot.applications?.length || snapshot.matchedCandidates.length },

    { label: 'Ready to submit', value: snapshot.readyToSubmitApplications?.length || 0 },

    { label: 'Awaiting feedback', value: snapshot.awaitingFeedbackApplications?.length || 0 },

    { label: 'Open tasks', value: openSequenceTaskCount },

  ];

  const overviewItems = [

    { label: 'Location', value: job.location || 'Remote' },

    { label: 'Type', value: job.type || 'Full-time' },

    { label: 'Compensation', value: job.salary || 'Compensation on request' },

    { label: 'Relationship', value: job.relationshipStatus || 'Active client' },

  ];

  const crmItems = [

    { label: 'Client company', value: companyName },

    { label: 'Hiring contact', value: job.clientContact || 'No contact recorded' },

    { label: 'Last outreach', value: job.lastOutreach ? new Date(job.lastOutreach).toLocaleString() : 'No outreach recorded' },

    { label: 'Open roles for client', value: snapshot.clientOpenRoles },

  ];

  const sourceLink = getJobSourceLinkMeta(job);

  return (

    <div className="fixed inset-0 z-50 flex min-h-screen items-stretch justify-center bg-slate-950/90 backdrop-blur-sm">

      <div className="flex min-h-screen w-full max-w-[96rem] flex-col overflow-y-auto p-4 sm:p-6 lg:p-8">

        <div className="rounded-shell border border-white/10 bg-slate-950/95 p-6 sm:p-8 lg:p-10">

          <div className="flex items-start justify-between gap-4">

            <div className="max-w-4xl">

              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Job ATS Workspace</div>

              <h2 className="mt-3 text-2xl font-black text-white sm:text-3xl">{job.title}</h2>

              <div className="mt-3 flex flex-wrap gap-2 text-meta text-slate-300">

                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">{companyName}</span>

                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">{job.location || 'Remote'}</span>

                <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5">{job.type || 'Full-time'}</span>

                <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1.5 text-cyan-100">{job.status}</span>

              </div>

              <div className="mt-5 max-w-3xl text-sm leading-7 text-slate-300">{job.summary}</div>

              <div className={`mt-5 rounded-card border p-4 ${job.requirementsSource === 'unavailable' ? 'border-amber-400/25 bg-amber-500/[0.08]' : 'border-emerald-400/20 bg-emerald-500/[0.06]'}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-400">Job requirements</div>
                  <span className={`rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] ${job.requirementsSource === 'application_html' || job.requirementsSource === 'application_rendered' ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200' : job.requirementsSource === 'unavailable' ? 'border-amber-400/25 bg-amber-500/10 text-amber-200' : 'border-white/10 bg-white/5 text-slate-300'}`}>
                    {job.requirementsSource === 'application_html' || job.requirementsSource === 'application_rendered' ? 'Verified from application' : job.requirementsSource === 'unavailable' ? 'Not verified' : 'Captured'}
                  </span>
                </div>
                <div className="mt-3 max-h-72 overflow-y-auto pr-2">
                  <RequirementsContent value={job.requirements} />
                </div>
                {job.applyUrl && (
                  <a href={job.applyUrl} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-cyan-200 underline decoration-cyan-400/30">
                    <ExternalLink size={11} /> Open application source
                  </a>
                )}
                {job.requirementsSource === 'unavailable' && (
                  <div className="mt-2 text-xs leading-5 text-amber-100/80">The employer application page was unavailable or did not expose a requirements section. Confirm the criteria before qualifying candidates.</div>
                )}
              </div>

              <div className="mt-5 flex flex-wrap gap-2">

                {sourceLink && (

                  <a href={sourceLink.href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white">

                    <ExternalLink size={12} /> {sourceLink.label}

                  </a>

                )}

              </div>

            </div>

            <div className="flex flex-col items-end gap-2">
              {canRemoveJob && (
                <button
                  type="button"
                  onClick={onRemoveJob}
                  className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/25 bg-rose-500/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-rose-400 transition hover:border-rose-400/50 hover:bg-rose-500/15"
                  title="Remove this role from the portfolio"
                >
                  <Trash2 size={9} /> Remove
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                className="rounded-full border border-white/10 bg-white/5 p-3 text-slate-300 transition hover:border-white/20 hover:text-white"
                aria-label="Close job ATS panel"
              >
                <X size={18} />
              </button>
            </div>

          </div>



          <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">

            {headerStats.map((item) => (

              <div key={item.label} className="rounded-shell border border-white/10 bg-white/5 px-4 py-4 sm:px-5">

                <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">{item.label}</div>

                <div className="mt-2 text-2xl font-black text-white">{item.value}</div>

              </div>

            ))}

          </div>

          {onSetJobStage && (
            <div className="mt-6">
              <JobLifecycleBar
                job={job}
                onStageChange={onSetJobStage}
                onToggleBlocked={onToggleJobBlocked || (() => {})}
              />
            </div>
          )}

          <JobPublishingConsole
            job={job}
            snapshot={snapshot}
            publishing={publishing}
            canPublish={canPublish}
            onUpdatePublishing={onUpdatePublishing || (() => {})}
          />

          <div className="mt-8 rounded-shell border border-cyan-400/15 bg-cyan-500/[0.06] p-5 sm:p-6">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <div className="text-sm font-black uppercase tracking-[0.18em] text-cyan-100">Recruitment Command Center</div>
                <div className="mt-2 text-sm text-slate-400">Applications connect this job to candidates, CVs, screening, client submissions, feedback, offers, placements, and revenue.</div>
              </div>
              <div className="rounded-full border border-cyan-400/20 bg-slate-950/60 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-cyan-100">Single source of truth</div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {[
                { label: 'Applications', value: snapshot.applications?.length || 0, detail: 'job-specific records' },
                { label: 'Submitted', value: snapshot.submittedApplications?.length || 0, detail: 'sent to client/contact' },
                { label: 'Feedback due', value: snapshot.awaitingFeedbackApplications?.length || 0, detail: 'waiting on client' },
                { label: 'Screening done', value: snapshot.screeningCompleted.length, detail: 'completed audits' },
              ].map((metric) => (
                <div key={metric.label} className="rounded-card border border-white/10 bg-slate-950/60 px-4 py-3">
                  <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{metric.label}</div>
                  <div className="mt-2 text-2xl font-black text-white">{metric.value}</div>
                  <div className="mt-1 text-micro text-slate-500">{metric.detail}</div>
                </div>
              ))}
            </div>

            <div className="mt-5 space-y-2">
              {(snapshot.applications || []).slice(0, 6).map((application) => {
                const risk = getApplicationRisk(application);
                const riskClass = risk.tone === 'rose' ? 'border-rose-400/25 bg-rose-500/10 text-rose-100' : risk.tone === 'amber' ? 'border-amber-400/25 bg-amber-500/10 text-amber-100' : risk.tone === 'cyan' ? 'border-cyan-400/25 bg-cyan-500/10 text-cyan-100' : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100';
                return (
                  <div key={application.id} className="grid gap-3 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 md:grid-cols-[minmax(0,1.2fr)_0.8fr_0.8fr_auto] md:items-center">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-black text-white">{application.candidateName}</div>
                      <div className="mt-1 truncate text-xs text-slate-500">{application.source || 'pipeline'} · {application.candidateEmail || 'no email'}</div>
                    </div>
                    <div>
                      <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-600">Stage</div>
                      <div className="mt-1 text-xs font-semibold text-slate-200">{application.stage || 'applied'}</div>
                    </div>
                    <div>
                      <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-600">Next action</div>
                      <div className="mt-1 text-xs font-semibold text-slate-200">{getApplicationNextAction(application)}</div>
                    </div>
                    <span className={`inline-flex justify-self-start rounded-full border px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] ${riskClass}`}>{risk.label}</span>
                  </div>
                );
              })}
              {(!snapshot.applications || snapshot.applications.length === 0) && (
                <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">Applications will appear here as candidates apply, are imported, or are matched to this role.</div>
              )}
            </div>
          </div>

          <div className="mt-8 grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">

            <div className="space-y-6">

              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">

                  <div>

                    <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Job Overview</div>

                    <div className="mt-2 text-sm text-slate-400">The core vacancy context recruiters need before they review candidates or run outreach.</div>

                  </div>

                  <div className="rounded-full border border-white/10 bg-slate-950/60 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-300">{companyName}</div>

                </div>

                <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">

                  {overviewItems.map((item) => (

                    <div key={item.label} className="rounded-card border border-white/10 bg-slate-950/60 px-4 py-3">

                      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{item.label}</div>

                      <div className="mt-2 text-sm font-semibold text-slate-200">{item.value}</div>

                    </div>

                  ))}

                </div>

                <div className="mt-5 grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">

                  <div className="rounded-shell border border-white/10 bg-slate-950/60 p-4 sm:p-5">

                    <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">Requirements</div>

                    <div className="mt-3 max-h-72 overflow-y-auto pr-2">
                      <RequirementsContent value={job.requirements} />
                    </div>

                  </div>

                  <div className="rounded-shell border border-white/10 bg-slate-950/60 p-4 sm:p-5">

                    <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">Client CRM</div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2 xl:grid-cols-1">

                      {crmItems.map((item) => (

                        <div key={item.label} className="rounded-card border border-white/10 bg-white/[0.04] px-4 py-3">

                          <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{item.label}</div>

                          <div className="mt-2 text-sm font-semibold text-slate-200">{item.value}</div>

                        </div>

                      ))}

                    </div>

                  </div>

                </div>

              </div>



              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

                <div className="flex flex-wrap items-center justify-between gap-3">

                  <div>

                    <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Matched Candidates + Sequence Tasks</div>

                    <div className="mt-1 text-xs text-slate-400">Run outreach from each candidate row, then complete the open LinkedIn, WhatsApp, and call work in this same ATS block.</div>

                  </div>

                  <div className="flex flex-wrap gap-2 text-meta text-slate-400">

                    <span className="rounded-full border border-white/10 bg-slate-950/60 px-3 py-2">{snapshot.matchedCandidates.length} candidates</span>

                    <span className="rounded-full border border-white/10 bg-slate-950/60 px-3 py-2">{sequenceRuns.length} runs</span>

                    <span className="rounded-full border border-white/10 bg-slate-950/60 px-3 py-2">{openSequenceTaskCount} open tasks</span>

                  </div>

                  <div className="flex flex-wrap gap-2">

                    <button type="button" onClick={onProcessSequenceRuns} disabled={isProcessingSequenceRuns} className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-cyan-100 transition hover:border-cyan-300/40 hover:text-cyan-50 disabled:cursor-not-allowed disabled:opacity-60">{isProcessingSequenceRuns ? 'Running...' : 'Run all sequences'}</button>

                    <button type="button" onClick={onExportCsv} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200">Export ATS CSV</button>

                    <button type="button" onClick={onOpenPipeline} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200">View all in pipeline</button>

                  </div>

                </div>

                {hiddenLegacySequenceRunCount > 0 && (

                  <div className="mt-4 rounded-card border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-xs text-amber-100">

                    Hidden {hiddenLegacySequenceRunCount} older Gmail login failure run{hiddenLegacySequenceRunCount === 1 ? '' : 's'} from this history view.

                  </div>

                )}

                {sequenceNotice && <div className="mt-4 rounded-card border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-xs uppercase tracking-[0.16em] text-cyan-200">{sequenceNotice}</div>}

                {(snapshot.rediscoveredCandidates || []).length > 0 && (
                  <div className="mt-4 rounded-shell border border-violet-400/20 bg-violet-500/10 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-3">
                      <div>
                        <div className="text-meta font-black uppercase tracking-[0.18em] text-violet-200">AI Rediscovery</div>
                        <div className="mt-1 text-xs text-slate-400">Strong candidates in your database that are not yet tied to this job.</div>
                      </div>
                      <div className="rounded-full border border-violet-400/20 bg-slate-950/60 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-violet-100">{snapshot.rediscoveredCandidates.length} suggested</div>
                    </div>
                    <div className="mt-3 grid gap-3 lg:grid-cols-2">
                      {snapshot.rediscoveredCandidates.slice(0, 4).map((candidate) => (
                        <div key={candidate.id} className="rounded-card border border-white/10 bg-slate-950/70 p-3">
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0">
                              <div className="truncate text-sm font-black text-white">{candidate.name}</div>
                              <div className="mt-1 truncate text-meta text-slate-400">{candidate.role || candidate.matchedJobTitle || 'Unknown role'} · {candidate.location || 'Unknown location'}</div>
                            </div>
                            <div className="rounded-full border border-violet-400/25 bg-violet-500/10 px-2.5 py-1 text-micro font-black text-violet-200">{candidate.aiMatch.overall}</div>
                          </div>
                          <div className="mt-2 text-meta leading-5 text-slate-300">{candidate.aiMatch.summary}</div>
                          <div className="mt-2 flex flex-wrap gap-1.5">
                            {Object.entries(candidate.aiMatch.dimensions).slice(0, 4).map(([label, value]) => (
                              <span key={label} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-micro font-bold text-slate-300">{label} {value}</span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="mt-4 space-y-3">

                  {snapshot.matchedCandidates.length === 0 && <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No candidates are tied to this role yet.</div>}

                  {snapshot.matchedCandidates.map((candidate) => {

                    const targetKey = `candidate:${candidate.id}`;

                    const isBusy = isProcessingSequenceRuns || activeSequenceTargetKey === targetKey;

                    const label = isBusy ? 'Working...' : 'Run sequence';

                    const aiMatch = candidate.aiMatch || null;

                    return (

                      <div key={candidate.id} className="rounded-shell border border-white/10 bg-slate-950/60 p-5">

                        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_auto]">

                          <div>

                            <div className="text-lg font-black text-white">{candidate.name}</div>

                            <div className="mt-2 text-sm text-slate-300">{candidate.role} · {candidate.location || 'Unknown location'}</div>

                            <div className="mt-4 grid gap-3 sm:grid-cols-3">

                              <div className="rounded-card border border-white/10 bg-white/[0.04] px-4 py-3">

                                <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Stage</div>

                                <div className="mt-2 text-sm font-semibold text-slate-200">{candidate.status}</div>

                              </div>

                              <div className="rounded-card border border-white/10 bg-white/[0.04] px-4 py-3">

                                <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Fit score</div>

                                <div className="mt-2 text-sm font-semibold text-slate-200">{candidate.rating || 0}</div>

                              </div>

                              <div className="rounded-card border border-white/10 bg-white/[0.04] px-4 py-3">

                                <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Last activity</div>

                                <div className="mt-2 text-sm font-semibold text-slate-200">{candidate.lastActivityAt ? new Date(candidate.lastActivityAt).toLocaleDateString() : 'No recent activity'}</div>

                              </div>

                            </div>

                            {aiMatch && (
                              <div className="mt-4 rounded-card border border-cyan-400/15 bg-cyan-500/[0.05] p-4">
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                  <div>
                                    <div className="text-micro font-black uppercase tracking-[0.16em] text-cyan-300">Explainable AI Match</div>
                                    <div className="mt-1 text-xs leading-5 text-slate-300">{aiMatch.summary}</div>
                                  </div>
                                  <div className="rounded-full border border-cyan-400/25 bg-cyan-500/10 px-3 py-1 text-micro font-black text-cyan-100">{aiMatch.overall}/99</div>
                                </div>
                                <div className="mt-3 grid gap-2 sm:grid-cols-5">
                                  {Object.entries(aiMatch.dimensions).map(([key, value]) => (
                                    <div key={key} className="rounded-ctl border border-white/10 bg-slate-950/60 px-3 py-2">
                                      <div className="text-micro font-black uppercase tracking-[0.12em] text-slate-500">{key}</div>
                                      <div className="mt-1 text-sm font-black text-white">{value}</div>
                                    </div>
                                  ))}
                                </div>
                                <div className="mt-3 grid gap-3 lg:grid-cols-2">
                                  <div className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-2">
                                    <div className="text-micro font-black uppercase tracking-[0.14em] text-emerald-300">Evidence</div>
                                    <div className="mt-1 text-meta leading-5 text-slate-300">{aiMatch.evidence?.[0] || 'No CV evidence available yet.'}</div>
                                  </div>
                                  <div className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-2">
                                    <div className="text-micro font-black uppercase tracking-[0.14em] text-amber-300">Next action</div>
                                    <div className="mt-1 text-meta leading-5 text-slate-300">{aiMatch.nextAction}</div>
                                  </div>
                                </div>
                              </div>
                            )}

                          </div>

                          <div className="flex flex-wrap gap-2 xl:max-w-[14rem] xl:justify-end">

                            <button type="button" onClick={() => onStartCandidateSequence(candidate)} disabled={isBusy} className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-cyan-100 transition hover:border-cyan-300/40 hover:text-cyan-50 disabled:cursor-not-allowed disabled:opacity-60">{label}</button>

                            {candidate.status === 'screening' && (

                              <button type="button" onClick={() => onOpenScreening(candidate.id)} className="rounded-full border border-violet-400/20 bg-violet-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-violet-100">Open screening</button>

                            )}

                            <button type="button" onClick={() => onOpenEeocEdit && onOpenEeocEdit(candidate.id, candidate.eeocSelfId || {})} className={candidate.eeocSelfId ? 'rounded-full border border-rose-400/30 bg-rose-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-rose-200 transition' : 'rounded-full border border-white/10 bg-white/5 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-slate-400 hover:border-white/20 transition'}>{candidate.eeocSelfId ? 'EEO: Done \u2713' : 'EEO ID'}</button>


                          </div>

                        </div>

                      </div>

                    );

                  })}

                </div>

                <div className="mt-5 space-y-3 border-t border-white/10 pt-5">

                  <div>

                    <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Open Sequence Tasks</div>

                    <div className="mt-1 text-xs text-slate-500">LinkedIn and call work stay manual here, while WhatsApp tasks can now send and track through the API when configured.</div>

                  </div>

                  {sequenceTasks.length === 0 && <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No sequence tasks are open for this vacancy yet. Run a sequence from a candidate or prospect row to create the first follow-up step.</div>}

                  {sequenceTasks.map((task) => (

                    <div key={task.id} className="rounded-shell border border-white/10 bg-slate-950/60 p-5">

                      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.2fr)_minmax(16rem,0.8fr)]">

                        <div className="min-w-0">

                          <div className="text-sm font-bold text-white">{task.title || 'Sequence task'}</div>

                          <div className="mt-2 text-xs text-slate-400">{task.target?.name || 'Unknown target'} · {formatSequenceChannelLabel(task.channel)}{task.validation?.contactValue ? ` · ${task.validation.contactValue}` : task.target?.email ? ` · ${task.target.email}` : ''}</div>

                          <div className="mt-4 rounded-card border border-white/10 bg-white/[0.04] px-4 py-4 text-sm leading-7 text-slate-300">{task.instructions || 'Complete the assigned outreach step.'}</div>

                          {task.validation?.message && (

                            <div className={`mt-3 inline-flex rounded-full border px-3 py-2 text-micro font-black uppercase tracking-[0.16em] ${task.validation.status === 'ready' ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/20 bg-amber-500/10 text-amber-100'}`}>

                              {task.validation.message}

                            </div>

                          )}

                          {task.draftMessage && (

                            <div className="mt-3 rounded-card border border-white/10 bg-white/5 p-3">

                              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Suggested script</div>

                              <div className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-300">{task.draftMessage}</div>

                            </div>

                          )}

                          {task.automation?.message && (

                            <div className={`mt-3 rounded-card border px-4 py-3 text-xs leading-6 ${task.automation?.isConfigured ? 'border-cyan-400/20 bg-cyan-500/10 text-cyan-100' : 'border-amber-400/20 bg-amber-500/10 text-amber-100'}`}>

                              {task.automation.message}

                            </div>

                          )}

                          {getSequenceTaskProviderStatusLabel(task) && (

                            <div className="mt-3 inline-flex rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-emerald-200">

                              WhatsApp API: {getSequenceTaskProviderStatusLabel(task)}

                            </div>

                          )}

                          <label className="mt-3 block">

                            <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">Outcome notes</div>

                            <textarea

                              value={sequenceTaskNotes[task.id] || ''}

                              onChange={(event) => onSequenceTaskNoteChange(task.id, event.target.value)}

                              rows={3}

                              placeholder="Add reply details, objections, or context for the next recruiter."

                              className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400"

                            />

                          </label>

                        </div>

                        <div className="flex flex-col gap-3 rounded-card border border-white/10 bg-white/[0.04] p-4">

                          <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Task actions</div>

                          {task.channel === 'whatsapp_task' && task.automation?.isConfigured && !task.providerMessageId && (

                            <button

                              type="button"

                              onClick={() => onSendSequenceWhatsAppTask(task)}

                              disabled={activeSequenceApiTaskId === task.id || activeSequenceTaskId === task.id}

                              className="inline-flex items-center justify-center rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-emerald-100 transition hover:border-emerald-300/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"

                            >

                              {activeSequenceApiTaskId === task.id ? 'Sending...' : 'Send via WhatsApp API'}

                            </button>

                          )}

                          {task.actionUrl && (

                            <a

                              href={task.actionUrl}

                              target={task.channel === 'call_task' ? undefined : '_blank'}

                              rel={task.channel === 'call_task' ? undefined : 'noreferrer'}

                              className="inline-flex items-center justify-center rounded-full border border-cyan-400/30 bg-cyan-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-cyan-100 transition hover:border-cyan-300/40 hover:text-white"

                            >

                              {getSequenceTaskActionLabel(task.channel)}

                            </a>

                          )}

                          <div className="flex flex-wrap gap-2">

                            {getSequenceTaskOutcomeOptions(task.channel).map((option) => (

                              <button

                                key={option.id}

                                type="button"

                                onClick={() => onCompleteSequenceTask(task, option.id)}

                                disabled={activeSequenceTaskId === task.id}

                                className={option.id === 'skipped'

                                  ? 'rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-60'

                                  : 'rounded-full bg-cyan-500 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60'}

                              >

                                {activeSequenceTaskId === task.id ? 'Saving...' : option.label}

                              </button>

                            ))}

                          </div>

                        </div>

                      </div>

                    </div>

                  ))}

                </div>

                <div className="mt-5 space-y-3 border-t border-white/10 pt-5">

                  <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Recent sequence runs</div>

                  {sequenceRuns.length === 0 && <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No sequence runs have started for this vacancy yet.</div>}

                  {sequenceRuns.slice(0, 4).map((run) => (

                    <div key={run.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                      <div className="flex flex-wrap items-center justify-between gap-3">

                        <div>

                          <div className="text-sm font-bold text-white">{run.target?.name || 'Unknown target'}</div>

                          <div className="mt-1 text-xs text-slate-400">{run.templateName || 'Sequence run'} | {run.startedAtIso ? new Date(run.startedAtIso).toLocaleString() : 'Pending start'}</div>

                          {run?.experiment && (

                            <div className="mt-2 text-xs text-slate-500">Experiment: {getSequenceExperimentLabel(run.experiment)}</div>

                          )}

                          {String(run.status || '').toLowerCase() === 'active' && run.nextStepAtIso && (

                            <div className="mt-2 text-xs text-slate-500">Next due: {new Date(run.nextStepAtIso).toLocaleString()}</div>

                          )}

                          {String(run.status || '').toLowerCase() === 'waiting_manual' && (

                            <div className="mt-2 text-xs text-slate-500">Waiting for the open manual outreach task to be completed.</div>

                          )}

                          {(run.replyDetectedAtIso || String(run.completedReason || '').toLowerCase() === 'reply_detected') && (

                            <div className="mt-2 text-xs text-emerald-300">

                              Reply detected{run.replyFrom ? ` from ${run.replyFrom}` : ''}{run.replyDetectedAtIso ? ` on ${new Date(run.replyDetectedAtIso).toLocaleString()}` : ''}.

                            </div>

                          )}

                          {String(run.status || '').toLowerCase() === 'failed' && run.blockedReason && (

                            <div className="mt-2 text-xs text-amber-300">Needs attention: {run.blockedReason}</div>

                          )}

                        </div>

                        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200">{run.status || 'unknown'}</span>

                      </div>

                    </div>

                  ))}

                </div>

              </div>



              <div data-direct-demo-target="sourced-prospects" className={`rounded-shell border border-white/10 bg-white/5 p-5 ${directDemoGuideHighlight ? 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]' : ''}`}>

                <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Sourced Prospects</div>

                <div className="mt-1 text-xs text-slate-400">Move strong sourced prospects into the live pipeline without leaving this job workspace.</div>

                <div className="mt-4 space-y-3">

                  {snapshot.prospects.length === 0 && <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No sourced prospects are attached to this vacancy yet.</div>}

                  {snapshot.prospects.map((prospect) => {

                    const targetKey = `prospect:${prospect.id}`;

                    const isBusy = isProcessingSequenceRuns || activeSequenceTargetKey === targetKey;

                    const label = isBusy ? 'Working...' : 'Run sequence';

                    return (

                      <div key={prospect.id} className="rounded-shell border border-white/10 bg-slate-950/60 p-5">

                        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_auto]">

                          <div>

                            <div className="text-lg font-black text-white">{prospect.name || 'Untitled prospect'}</div>

                            <div className="mt-2 text-sm text-slate-300">{prospect.headline || prospect.role || 'No headline captured'} · {prospect.location || 'Unknown location'}</div>

                            <div className="mt-4 grid gap-3 sm:grid-cols-2">

                              <div className="rounded-card border border-white/10 bg-white/[0.04] px-4 py-3">

                                <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Matched vacancy</div>

                                <div className="mt-2 text-sm font-semibold text-slate-200">{prospect.matchedJobTitle || job.title}</div>

                              </div>

                              <div className="rounded-card border border-white/10 bg-white/[0.04] px-4 py-3">

                                <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Fit score</div>

                                <div className="mt-2 text-sm font-semibold text-slate-200">{prospect.rating || 0}</div>

                              </div>

                            </div>

                          </div>

                          <div className="flex flex-wrap gap-2 xl:max-w-[16rem] xl:justify-end">

                            <button type="button" onClick={() => onStartProspectSequence(prospect)} disabled={isBusy} className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-cyan-100 transition hover:border-cyan-300/40 hover:text-cyan-50 disabled:cursor-not-allowed disabled:opacity-60">{label}</button>

                            <button type="button" onClick={() => onPushProspect(prospect)} className={`rounded-full bg-cyan-500 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-400 ${directDemoGuideHighlight ? 'ring-4 ring-cyan-200/80' : ''}`}>Move to pipeline</button>

                          </div>

                        </div>

                      </div>

                    );

                  })}

                </div>

              </div>

            </div>



            <div className="space-y-5">

              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Pipeline Counts By Stage</div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">

                  {stageEntries.map((stage) => (

                    <div key={stage.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                      <div className="text-meta uppercase tracking-[0.18em] text-slate-500">{stage.label}</div>

                      <div className="mt-2 text-2xl font-black text-white">{stage.count}</div>

                    </div>

                  ))}

                </div>

              </div>



              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Sequence Analytics</div>

                <div className="mt-1 text-xs text-slate-400">Track cadence health, reply performance, and channel-level outcomes for this vacancy.</div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Runs tracked</div>

                    <div className="mt-2 text-2xl font-black text-white">{sequenceAnalytics?.totalRuns || 0}</div>

                    <div className="mt-2 text-xs text-slate-500">{sequenceAnalytics?.activeRuns || 0} active | {sequenceAnalytics?.waitingManualRuns || 0} manual</div>

                  </div>

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Completion rate</div>

                    <div className="mt-2 text-2xl font-black text-white">{formatSequenceAnalyticsPercent(sequenceAnalytics?.completionRate)}</div>

                    <div className="mt-2 text-xs text-slate-500">{sequenceAnalytics?.completedRuns || 0} completed | {sequenceAnalytics?.failedRuns || 0} failed</div>

                  </div>

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Reply rate</div>

                    <div className="mt-2 text-2xl font-black text-white">{formatSequenceAnalyticsPercent(sequenceAnalytics?.replyRate)}</div>

                    <div className="mt-2 text-xs text-slate-500">{sequenceAnalytics?.replyDetectedRuns || 0} reply-detected runs | {sequenceAnalytics?.emailSentCount || 0} emails sent</div>

                  </div>

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Cadence pressure</div>

                    <div className="mt-2 text-2xl font-black text-white">{sequenceAnalytics?.openStepCount || 0}</div>

                    <div className="mt-2 text-xs text-slate-500">{sequenceAnalytics?.failedStepCount || 0} failed steps | Avg {Number(sequenceAnalytics?.averageCompletedStepsPerRun || 0).toFixed(1)} steps/run</div>

                  </div>

                </div>

                <div className="mt-4 grid gap-3 xl:grid-cols-[0.9fr_1.1fr]">

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Run Status Mix</div>

                    <div className="mt-3 space-y-3">

                      {(sequenceAnalytics?.statusSummaries || []).map((summary) => {

                        const ratio = (sequenceAnalytics?.totalRuns || 0) > 0 ? summary.count / sequenceAnalytics.totalRuns : 0;

                        return (

                          <div key={summary.id}>

                            <div className="flex items-center justify-between gap-3 text-xs text-slate-300">

                              <span>{summary.label}</span>

                              <span className="font-bold text-white">{summary.count}</span>

                            </div>

                            <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/5">

                              <div className={`h-full rounded-full ${summary.id === 'completed' ? 'bg-emerald-400' : summary.id === 'failed' ? 'bg-rose-400' : summary.id === 'waiting_manual' ? 'bg-amber-300' : 'bg-cyan-400'}`} style={{ width: `${Math.max(ratio * 100, summary.count > 0 ? 8 : 0)}%` }} />

                            </div>

                          </div>

                        );

                      })}

                    </div>

                    <div className="mt-4 rounded-card border border-white/10 bg-white/5 p-3 text-xs text-slate-400">

                      Engagement landed in {sequenceAnalytics?.engagedRuns || 0} run{sequenceAnalytics?.engagedRuns === 1 ? '' : 's'}.

                      {` `}{formatSequenceAnalyticsPercent(sequenceAnalytics?.engagedRate)} of tracked runs reached a terminal conversation signal.

                    </div>

                  </div>

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Recent Performance</div>

                    <div className="mt-3 grid gap-3 sm:grid-cols-2">

                      {(sequenceAnalytics?.recentWindows || []).map((window) => (

                        <div key={window.id} className="rounded-card border border-white/10 bg-white/5 p-4">

                          <div className="text-xs font-bold uppercase tracking-[0.16em] text-cyan-200">{window.label}</div>

                          <div className="mt-3 grid gap-2 grid-cols-2">

                            <div className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-3 text-xs text-slate-300">

                              <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Started</div>

                              <div className="mt-1 text-lg font-black text-white">{window.started}</div>

                            </div>

                            <div className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-3 text-xs text-slate-300">

                              <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Completed</div>

                              <div className="mt-1 text-lg font-black text-white">{window.completed}</div>

                            </div>

                            <div className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-3 text-xs text-slate-300">

                              <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Replies</div>

                              <div className="mt-1 text-lg font-black text-white">{window.replies}</div>

                            </div>

                            <div className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-3 text-xs text-slate-300">

                              <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Failed</div>

                              <div className="mt-1 text-lg font-black text-white">{window.failed}</div>

                            </div>

                          </div>

                        </div>

                      ))}

                    </div>

                    <div className="mt-4">

                      <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Top outcomes</div>

                      <div className="mt-3 flex flex-wrap gap-2">

                        {(sequenceAnalytics?.topOutcomes || []).length === 0 && (

                          <span className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-meta text-slate-400">No outcome data yet</span>

                        )}

                        {(sequenceAnalytics?.topOutcomes || []).map((outcome) => (

                          <span key={outcome.id} className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-meta text-slate-200">

                            {outcome.label}: <span className="font-bold text-white">{outcome.count}</span>

                          </span>

                        ))}

                      </div>

                    </div>

                  </div>

                </div>

                <div className="mt-4 grid gap-3 xl:grid-cols-2">

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Persona Performance</div>

                    <div className="mt-3 space-y-3">

                      {(sequenceAnalytics?.personaSummaries || []).length === 0 && (

                        <div className="rounded-card border border-dashed border-white/10 px-4 py-4 text-sm text-slate-500">No persona assignments have run yet.</div>

                      )}

                      {(sequenceAnalytics?.personaSummaries || []).map((summary) => (

                        <div key={summary.id} className="rounded-card border border-white/10 bg-white/5 p-3">

                          <div className="flex items-center justify-between gap-3">

                            <div className="text-sm font-bold text-white">{summary.label}</div>

                            <div className="text-micro uppercase tracking-[0.16em] text-slate-400">{summary.runs} runs</div>

                          </div>

                          <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">

                            <span>Reply rate <span className="font-bold text-white">{formatSequenceAnalyticsPercent(summary.replyRate)}</span></span>

                            <span>Completion <span className="font-bold text-white">{formatSequenceAnalyticsPercent(summary.completionRate)}</span></span>

                          </div>

                        </div>

                      ))}

                    </div>

                  </div>

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Variant Performance</div>

                    <div className="mt-3 space-y-3">

                      {(sequenceAnalytics?.variantSummaries || []).length === 0 && (

                        <div className="rounded-card border border-dashed border-white/10 px-4 py-4 text-sm text-slate-500">No variant assignments have run yet.</div>

                      )}

                      {(sequenceAnalytics?.variantSummaries || []).map((summary) => (

                        <div key={summary.id} className="rounded-card border border-white/10 bg-white/5 p-3">

                          <div className="flex items-center justify-between gap-3">

                            <div className="text-sm font-bold text-white">{summary.label}</div>

                            <div className="text-micro uppercase tracking-[0.16em] text-slate-400">{summary.runs} runs</div>

                          </div>

                          <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">

                            <span>Reply rate <span className="font-bold text-white">{formatSequenceAnalyticsPercent(summary.replyRate)}</span></span>

                            <span>Completion <span className="font-bold text-white">{formatSequenceAnalyticsPercent(summary.completionRate)}</span></span>

                          </div>

                        </div>

                      ))}

                    </div>

                  </div>

                </div>

                <div className="mt-4 grid gap-3 xl:grid-cols-[0.8fr_1.2fr]">

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Send Controls</div>

                    <div className="mt-3 grid gap-3">

                      <label className="block">

                        <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">Hourly email cap</div>

                        <input type="number" min="1" value={sequenceControls?.hourlyEmailCap || ''} onChange={(event) => onSequenceControlsChange('hourlyEmailCap', event.target.value)} className="w-full rounded-card border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />

                      </label>

                      <label className="block">

                        <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">Daily email cap</div>

                        <input type="number" min="1" value={sequenceControls?.dailyEmailCap || ''} onChange={(event) => onSequenceControlsChange('dailyEmailCap', event.target.value)} className="w-full rounded-card border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />

                      </label>

                      <label className="block">

                        <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">Domain cooldown minutes</div>

                        <input type="number" min="0" value={sequenceControls?.minDomainCooldownMinutes || ''} onChange={(event) => onSequenceControlsChange('minDomainCooldownMinutes', event.target.value)} className="w-full rounded-card border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />

                      </label>

                      <label className="block">

                        <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">WhatsApp template name</div>

                        <input type="text" value={sequenceControls?.whatsappTemplateName || ''} onChange={(event) => onSequenceControlsChange('whatsappTemplateName', event.target.value)} placeholder="approved_recruiter_intro" className="w-full rounded-card border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />

                      </label>

                      <label className="block">

                        <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">WhatsApp template language</div>

                        <input type="text" value={sequenceControls?.whatsappTemplateLanguage || ''} onChange={(event) => onSequenceControlsChange('whatsappTemplateLanguage', event.target.value)} placeholder="en_US" className="w-full rounded-card border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />

                      </label>

                      <div className="rounded-card border border-white/10 bg-white/5 px-4 py-3 text-xs leading-6 text-slate-400">

                        WhatsApp API uses the approved template body variables in this order: target first name, role title, company name, recruiter name.

                      </div>

                      <button type="button" onClick={onSaveSequenceControls} disabled={isSavingSequenceControls} className="rounded-full bg-cyan-500 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60">

                        {isSavingSequenceControls ? 'Saving...' : 'Save controls'}

                      </button>

                    </div>

                  </div>

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="flex flex-wrap items-center justify-between gap-3">

                      <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Active Suppressions</div>

                      <div className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-300">{sequenceSuppressions?.length || 0} linked to this vacancy</div>

                    </div>

                    <div className="mt-3 space-y-3">

                      {(sequenceSuppressions || []).length === 0 && (

                        <div className="rounded-card border border-dashed border-white/10 px-4 py-4 text-sm text-slate-500">No suppressions are currently linked to this vacancy.</div>

                      )}

                      {(sequenceSuppressions || []).map((suppression) => (

                        <div key={suppression.id} className="rounded-card border border-white/10 bg-white/5 p-3">

                          <div className="flex flex-wrap items-start justify-between gap-3">

                            <div>

                              <div className="text-sm font-bold text-white">{suppression.targetName || suppression.keyValue || 'Suppressed target'}</div>

                              <div className="mt-1 text-xs text-slate-400">{getSequenceSuppressionReasonLabel(suppression.reason)} | {Object.entries(suppression.channels || {}).filter(([, isSuppressed]) => isSuppressed).map(([channel]) => formatSequenceChannelLabel(channel)).join(', ') || 'No channels'}</div>

                              <div className="mt-2 text-xs text-slate-500">{suppression.keyType}: {suppression.keyValue}</div>

                            </div>

                            <button type="button" onClick={() => onClearSequenceSuppression(suppression.id)} disabled={activeSequenceSuppressionId === suppression.id} className="rounded-full border border-amber-400/20 bg-amber-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-amber-100 transition hover:border-amber-300/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-60">

                              {activeSequenceSuppressionId === suppression.id ? 'Clearing...' : 'Clear'}

                            </button>

                          </div>

                          <div className="mt-2 text-xs text-slate-500">Updated {suppression.updatedAtIso ? new Date(suppression.updatedAtIso).toLocaleString() : suppression.lastTriggeredAtIso ? new Date(suppression.lastTriggeredAtIso).toLocaleString() : 'recently'}</div>

                        </div>

                      ))}

                    </div>

                  </div>

                </div>

                <div className="mt-4 space-y-3">

                  {(sequenceAnalytics?.channelSummaries || []).map((summary) => (

                    <div key={summary.channel} className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                      <div className="flex flex-wrap items-center justify-between gap-3">

                        <div className="text-sm font-bold text-white">{summary.label}</div>

                        <div className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200">

                          {summary.totalTouches} touches

                        </div>

                      </div>

                      <div className="mt-3 grid gap-2 sm:grid-cols-5">

                        <div className="rounded-card border border-white/10 bg-white/5 px-3 py-3 text-xs text-slate-300">

                          <div className="text-micro uppercase tracking-[0.16em] text-slate-500">Completion</div>

                          <div className="mt-1 text-lg font-black text-white">{formatSequenceAnalyticsPercent(summary.completionRate)}</div>

                        </div>

                        <div className="rounded-card border border-white/10 bg-white/5 px-3 py-3 text-xs text-slate-300">

                          <div className="text-micro uppercase tracking-[0.16em] text-slate-500">Engagement</div>

                          <div className="mt-1 text-lg font-black text-white">{formatSequenceAnalyticsPercent(summary.engagementRate)}</div>

                        </div>

                        <div className="rounded-card border border-white/10 bg-white/5 px-3 py-3 text-xs text-slate-300">

                          <div className="text-micro uppercase tracking-[0.16em] text-slate-500">Open</div>

                          <div className="mt-1 text-lg font-black text-white">{summary.open}</div>

                        </div>

                        <div className="rounded-card border border-white/10 bg-white/5 px-3 py-3 text-xs text-slate-300">

                          <div className="text-micro uppercase tracking-[0.16em] text-slate-500">Skipped</div>

                          <div className="mt-1 text-lg font-black text-white">{summary.skipped}</div>

                        </div>

                        <div className="rounded-card border border-white/10 bg-white/5 px-3 py-3 text-xs text-slate-300">

                          <div className="text-micro uppercase tracking-[0.16em] text-slate-500">Failed</div>

                          <div className="mt-1 text-lg font-black text-white">{summary.failed}</div>

                        </div>

                      </div>

                      <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/5">

                        <div className="h-full rounded-full bg-cyan-400" style={{ width: `${Math.max(summary.completionRate * 100, summary.completed > 0 ? 6 : 0)}%` }} />

                      </div>

                      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">

                        <span>{summary.completed} completed | {summary.engaged} engaged</span>

                        <span>Top outcome: <span className="font-bold text-white">{summary.topOutcomeLabel}</span></span>

                      </div>

                    </div>

                  ))}

                </div>

              </div>



              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Screening Status</div>

                <div className="mt-4 space-y-3">

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Awaiting screening</div>

                    <div className="mt-2 text-2xl font-black text-white">{snapshot.screeningPending.length}</div>

                  </div>

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Screening completed</div>

                    <div className="mt-2 text-2xl font-black text-white">{snapshot.screeningCompleted.length}</div>

                  </div>

                  {snapshot.screeningCompleted.slice(0, 6).map((item) => (

                    <div key={item.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                      <div className="text-sm font-bold text-white">{item.name}</div>

                      <div className="mt-1 text-xs text-slate-400">Submitted {item.screeningSubmittedAt ? new Date(item.screeningSubmittedAt).toLocaleString() : 'recently'}</div>

                    </div>

                  ))}

                </div>

              </div>



              <AccountTasks
                companyName={companyName}
                tasks={crmTasks || []}
                isSaving={isSavingCrmTask}
                onAdd={(data) => onAddCrmTask(data)}
                onComplete={onCompleteCrmTask}
                onSnooze={onSnoozeCrmTask}
                onRemove={onRemoveCrmTask}
              />

              <AccountContacts
                companyName={companyName}
                contacts={companyContacts || []}
                isSaving={isSavingCompanyContact}
                onAdd={onAddContact}
                onUpdate={onUpdateContact}
                onRemove={onRemoveContact}
                onLogActivity={onLogContactActivity}
                onScheduleMeeting={onScheduleMeeting}
              />

              <AccountTimeline
                companyName={companyName}
                entries={accountTimeline || []}
                isSaving={isSavingTimelineEntry}
                onAdd={onAddTimelineEntry}
              />



              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Recruiter Notes</div>

                <textarea value={notesDraft} onChange={(event) => onNotesChange(event.target.value)} rows={7} className="mt-4 w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm leading-6 text-white outline-none transition focus:border-cyan-400" placeholder="Capture what matters for this vacancy: hiring manager context, outreach approach, blockers, or shortlist strategy." />

                <button type="button" onClick={onSaveNotes} disabled={isSavingNotes} className="mt-4 rounded-full bg-cyan-500 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-70">{isSavingNotes ? 'Saving notes...' : 'Save recruiter notes'}</button>

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

  );

}



function ScreeningReviewModal({ candidate, response, isSavingQuestions, onClose, onRenewLink, onSaveQuestions, directDemoGuideHighlightAi = false }) {

  const [draftQuestions, setDraftQuestions] = useState(() => normalizeScreeningQuestions(candidate?.screeningQuestions));

  const [questionNotice, setQuestionNotice] = useState('');

  const aiSuggestions = useMemo(() => buildAiScreeningSuggestions(candidate), [candidate]);



  useEffect(() => {

    setDraftQuestions(normalizeScreeningQuestions(candidate?.screeningQuestions));

    setQuestionNotice('');

  }, [candidate]);



  if (!candidate) return null;

  const screeningLink = buildScreeningLink(candidate.screeningLinkId || candidate.id);

  const deadlineAt = ensureScreeningDeadline(candidate);

  const screeningQuestions = normalizeScreeningQuestions(response?.questions || candidate?.screeningQuestions);

  return (

    <div className="fixed inset-0 z-[9995] flex min-h-screen items-stretch justify-center bg-[#020817]">

      <div className="flex min-h-screen w-full max-w-5xl flex-col overflow-y-auto p-4 sm:p-6 lg:p-8">

        <div className="rounded-shell border border-white/10 bg-[#020817] p-6 shadow-2xl shadow-black/60 sm:p-8">

          <div className="flex items-start justify-between gap-4">

            <div>

              <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Screening Review</div>

              <h2 className="mt-3 text-2xl font-black text-white sm:text-3xl">{candidate.name}</h2>

              <div className="mt-2 text-sm text-slate-300">{candidate.role} at {candidate.company}</div>

              <div className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-500">Location: {candidate.location || 'Unknown location'} · Fit: {candidate.rating}</div>

            </div>

            <button

              type="button"

              onClick={onClose}

              className="rounded-full border border-white/10 bg-white/5 p-3 text-slate-300 transition hover:border-white/20 hover:text-white"

              aria-label="Close screening review"

            >

              <X size={18} />

            </button>

          </div>



          <div className="mt-8 grid gap-5 lg:grid-cols-[1.1fr_0.9fr]">

            <div className="rounded-shell border border-white/10 bg-white/5 p-5">

              <div className="flex flex-wrap items-center justify-between gap-3">

                <div>

                  <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Screening Questions</div>

                  <div className="mt-1 text-xs text-slate-400">Edit the recruiter-facing default questions for this candidate before sharing the screening link.</div>

                </div>

                <button

                  type="button"

                  onClick={async () => {

                    setQuestionNotice('');

                    try {

                      await onSaveQuestions(candidate.id, draftQuestions);

                      setQuestionNotice('Screening questions saved to the recruiter workspace and candidate link.');

                    } catch (error) {

                      setQuestionNotice(error?.message || 'Failed to save screening questions.');

                    }

                  }}

                  disabled={isSavingQuestions}

                  className="rounded-full bg-violet-500 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-white transition hover:bg-violet-400 disabled:cursor-not-allowed disabled:opacity-70"

                >

                  {isSavingQuestions ? 'Saving...' : 'Save questions'}

                </button>

              </div>

              {questionNotice && <div className="mt-4 rounded-card border border-violet-400/20 bg-violet-500/10 px-4 py-3 text-xs text-violet-100">{questionNotice}</div>}

              <div className="mt-3 space-y-2">

                {draftQuestions.map((question, index) => {
                  const highlightAiSuggestion = directDemoGuideHighlightAi && index === 0;

                  return (
                  <div key={`screening-question-${index}`} className="rounded-card border border-white/10 bg-slate-950/60 px-4 py-4 text-sm text-slate-200">

                    <div className="text-micro font-black uppercase tracking-[0.18em] text-violet-300">Question {index + 1}</div>

                    <textarea

                      value={question}

                      onChange={(event) => {

                        const next = [...draftQuestions];

                        next[index] = event.target.value;

                        setDraftQuestions(next);

                      }}

                      rows={3}

                      className="mt-3 w-full rounded-card border border-white/10 bg-slate-950/80 px-4 py-3 text-sm leading-6 text-white outline-none transition focus:border-violet-400"

                    />

                    <div className="mt-3 rounded-card border border-violet-400/15 bg-violet-500/10 px-4 py-3">

                      <div className="flex items-center gap-2 text-micro font-black uppercase tracking-[0.18em] text-violet-200">

                        <Sparkles size={12} /> AI suggestion

                      </div>

                      <div className="mt-2 leading-6 text-violet-50">{aiSuggestions[index]}</div>

                      <button

                        type="button"
                        data-direct-demo-target={highlightAiSuggestion ? 'screening-ai-suggestion-button' : undefined}

                        onClick={async () => {

                          const next = [...draftQuestions];

                          next[index] = aiSuggestions[index];

                          setDraftQuestions(next);
                          if (highlightAiSuggestion) {
                            setQuestionNotice('');
                            try {
                              await onSaveQuestions(candidate.id, next);
                              setQuestionNotice('AI screening question saved.');
                            } catch (error) {
                              setQuestionNotice(error?.message || 'Failed to save AI screening question.');
                            }
                          }

                        }}

                        className={`mt-3 rounded-full border border-violet-300/20 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-violet-100 transition hover:border-violet-200/30 hover:text-white ${highlightAiSuggestion ? 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]' : ''}`}

                      >

                        Use AI suggestion

                      </button>

                    </div>

                  </div>

                  );
                })}

              </div>

            </div>



            <div className="space-y-5">

              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Candidate Link</div>

              <div className="mt-3 rounded-card border border-violet-500/20 bg-violet-500/10 px-4 py-3 text-sm text-violet-100">

                Candidate must submit within {SCREENING_WINDOW_MINUTES} minutes once the page opens.

              </div>

              <div className="mt-4 rounded-card border border-white/10 bg-slate-950/60 p-4">

                <div className="text-micro uppercase tracking-[0.18em] text-slate-500">Screening deadline</div>

                <div className="mt-2 text-lg font-bold text-white">{new Date(deadlineAt).toLocaleString()}</div>

                <div className="mt-2 break-all text-xs text-slate-400">{screeningLink}</div>

              </div>

              <div className="mt-4 flex flex-wrap items-center gap-3">

                <a

                  href={screeningLink}

                  target="_blank"

                  rel="noreferrer"

                  className="inline-flex items-center gap-2 rounded-full bg-violet-500 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-white transition hover:bg-violet-400"

                >

                  <ExternalLink size={14} /> Open candidate screening page

                </a>

                <button

                  type="button"

                  onClick={() => onRenewLink(candidate.id)}

                  className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-200"

                >

                  Renew link

                </button>

              </div>

              </div>



              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-sm font-black uppercase tracking-[0.18em] text-white">Submitted Answers</div>

                {!response?.submittedAt ? (

                  <div className="mt-4 rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">

                    No candidate response has been submitted yet.

                  </div>

                ) : (

                  <div className="mt-4 space-y-3">

                    <div className="rounded-card border border-emerald-500/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">

                      Submitted {new Date(response.submittedAt).toLocaleString()}

                    </div>

                    {screeningQuestions.map((question, index) => (

                      <div key={`${candidate.id}-${index}`} className="rounded-card border border-white/10 bg-slate-950/60 px-4 py-4">

                        <div className="text-micro font-black uppercase tracking-[0.18em] text-violet-300">Question {index + 1}</div>

                        <div className="mt-2 text-sm leading-6 text-white">{question}</div>

                        <div className="mt-3 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Answer</div>

                        <div className="mt-2 text-sm leading-6 text-slate-200">{response.answers?.[index] || 'No answer provided.'}</div>

                      </div>

                    ))}

                  </div>

                )}

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

  );

}



function ScreeningCandidatePage({ candidate, response, isSubmitting, isLoadingCandidate, onExit, onSubmit }) {

  const screeningQuestions = useMemo(() => normalizeScreeningQuestions(response?.questions || candidate?.screeningQuestions), [candidate, response]);

  const [answers, setAnswers] = useState(() => buildScreeningAnswerDraft(screeningQuestions, response?.answers));

  const [submitError, setSubmitError] = useState('');

  const [remaining, setRemaining] = useState(() => formatTimeRemaining(candidate?.screeningDeadlineAt || ensureScreeningDeadline(candidate || {})));



  useEffect(() => {

    if (!candidate) return undefined;

    const interval = setInterval(() => {

      setRemaining(formatTimeRemaining(candidate.screeningDeadlineAt || ensureScreeningDeadline(candidate)));

    }, 1000);

    return () => clearInterval(interval);

  }, [candidate]);



  useEffect(() => {

    setAnswers(buildScreeningAnswerDraft(screeningQuestions, response?.answers));

    setSubmitError('');

  }, [response, screeningQuestions]);



  if (!candidate) {

    return (

      <div className="min-h-screen bg-[radial-gradient(circle_at_top,#11335f_0%,#020617_42%,#020617_100%)] px-4 py-10 text-slate-100">

        <div className="mx-auto max-w-2xl rounded-shell border border-white/10 bg-slate-950/80 p-6 text-center">

          <div className="text-xl font-black text-white">Screening link invalid</div>

          <div className="mt-2 text-sm text-slate-400">This candidate screening link is missing or no longer available.</div>

        </div>

      </div>

    );

  }



  const isExpired = remaining === 'Expired';

  const isSubmitted = Boolean(response?.submittedAt);



  return (

    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#11335f_0%,#020617_42%,#020617_100%)] px-4 py-6 text-slate-100 sm:px-6 sm:py-8">

      <div className="mx-auto max-w-3xl rounded-shell border border-white/10 bg-slate-950/85 p-6 sm:p-8">

        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">

          <div>

            <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Vision Screening</div>

            <h1 className="mt-3 text-2xl font-black text-white sm:text-3xl">{candidate.role} at {candidate.company}</h1>

            <div className="mt-2 text-sm text-slate-300">Candidate: {candidate.name}</div>

          </div>

          <div className={`rounded-full px-4 py-2 text-xs font-black uppercase tracking-[0.18em] ${isExpired ? 'bg-red-500/10 text-red-300' : 'bg-violet-500/10 text-violet-200'}`}>

            Time left {remaining}

          </div>

        </div>



        {isSubmitted ? (

          <div className="mt-8 rounded-shell border border-emerald-500/20 bg-emerald-500/10 px-5 py-6 text-center">

            <div className="text-xl font-black text-white">Screening submitted</div>

            <div className="mt-2 text-sm text-emerald-100">Your responses have been captured for recruiter review.</div>

          </div>

        ) : (

          <form

            className="mt-8 space-y-4"

            onSubmit={async (event) => {

              event.preventDefault();

              if (isExpired || isSubmitting) return;

              setSubmitError('');

              try {

                await onSubmit(answers);

              } catch (error) {

                setSubmitError(error?.message || 'Failed to submit screening answers.');

              }

            }}

          >

            <div className="rounded-card border border-white/10 bg-white/5 px-4 py-3 text-xs leading-5 text-slate-400">{candidate?.privacyNotice || PUBLIC_PRIVACY_NOTICE} {candidate?.lawfulUseNotice || PUBLIC_LAWFUL_USE_NOTICE}</div>

            {screeningQuestions.map((question, index) => (

              <label key={`screening-page-question-${index}`} className="block rounded-shell border border-white/10 bg-white/5 p-4">

                <div className="text-micro font-black uppercase tracking-[0.18em] text-violet-300">Question {index + 1}</div>

                <div className="mt-2 text-sm leading-6 text-white">{question}</div>

                <textarea

                  value={answers[index]}

                  onChange={(event) => {

                    const next = [...answers];

                    next[index] = event.target.value;

                    setAnswers(next);

                  }}

                  disabled={isExpired || isSubmitting || isSubmitted}

                  className="mt-3 min-h-[120px] w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-violet-400"

                  placeholder="Enter your answer here"

                  required

                />

              </label>

            ))}

            {submitError && (

              <div className="rounded-card border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">

                {submitError}

              </div>

            )}

            <div className="flex flex-wrap items-center gap-3">

              <button

                type="submit"

                disabled={isExpired || isSubmitting || isSubmitted}

                className={`rounded-full px-5 py-3 text-xs font-black uppercase tracking-[0.18em] ${(isExpired || isSubmitted) ? 'bg-slate-800 text-slate-500' : 'bg-violet-500 text-white hover:bg-violet-400'}`}

              >

                {isSubmitting ? 'Submitting...' : 'Submit screening'}

              </button>

              <button

                type="button"

                onClick={onExit}

                className="rounded-full border border-white/10 bg-white/5 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-200"

              >

                Exit

              </button>

            </div>

          </form>

        )}

      </div>

    </div>

  );

}

function ClientJobIntakePage({ linkRecord, form, notice, isSubmitting, hasSubmitted, onChange, onSubmit, onExit }) {

  const isValidLink = Boolean(linkRecord?.recruiterId && linkRecord?.agencyName);

  const agencyName = linkRecord?.agencyName || 'the agency';

  const recruiterName = linkRecord?.recruiterName || 'the recruiter';

  return (

    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#11335f_0%,#020617_42%,#020617_100%)] px-4 py-8 text-slate-100 sm:px-6">

      <div className="mx-auto max-w-5xl space-y-6">

        <section className="overflow-hidden rounded-shell border border-cyan-400/20 bg-[linear-gradient(135deg,rgba(6,182,212,0.14),rgba(15,23,42,0.96)_45%,rgba(2,6,23,0.98))] p-6 shadow-[0_24px_80px_rgba(6,182,212,0.12)] backdrop-blur sm:p-8">

          <div className="flex flex-wrap items-start justify-between gap-4">

            <div>

              <div className="text-xs font-black uppercase tracking-[0.3em] text-cyan-300">{agencyName}</div>

              <h1 className="mt-4 max-w-3xl text-3xl font-black text-white sm:text-5xl">Client vacancy intake.</h1>

              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">Share your open role directly with {recruiterName} at {agencyName}. Vision will deliver this vacancy into the agency job portfolio for fast recruiter action.</p>

            </div>

            <button type="button" onClick={onExit} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-200 transition hover:border-white/20 hover:text-white">Close</button>

          </div>

          <div className="mt-6 grid gap-3 sm:grid-cols-3">

            <div className="rounded-card border border-white/10 bg-slate-950/45 p-4">

              <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Agency</div>

              <div className="mt-2 text-lg font-bold text-white">{agencyName}</div>

            </div>

            <div className="rounded-card border border-white/10 bg-slate-950/45 p-4">

              <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Recruiter contact</div>

              <div className="mt-2 text-lg font-bold text-white">{recruiterName}</div>

            </div>

            <div className="rounded-card border border-white/10 bg-slate-950/45 p-4">

              <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Delivery</div>

              <div className="mt-2 text-lg font-bold text-white">Direct to portfolio</div>

            </div>

          </div>

        </section>



        <section className="rounded-shell border border-white/10 bg-white/5 p-6 backdrop-blur sm:p-8">

          {!isValidLink ? (

            <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-400">This client vacancy link is missing or no longer available.</div>

          ) : hasSubmitted ? (

            <div className="rounded-shell border border-emerald-500/20 bg-emerald-500/10 p-6 text-center sm:p-8">

              <div className="text-xs font-black uppercase tracking-[0.28em] text-emerald-200">Vacancy Received</div>

              <div className="mt-3 text-3xl font-black text-white">Thank you. Your vacancy has been received.</div>

              <div className="mt-4 text-sm leading-7 text-emerald-50/90">{notice || `Your vacancy is now with ${agencyName}. ${recruiterName} can review it from the agency job portfolio and respond from there.`}</div>

              <div className="mt-6 flex flex-wrap justify-center gap-3">

                <button type="button" onClick={onExit} className="rounded-full bg-emerald-400 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-emerald-300">Close</button>

              </div>

            </div>

          ) : (

            <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>

              <div className="sm:col-span-2 rounded-card border border-cyan-400/15 bg-cyan-500/10 px-4 py-4 text-sm leading-6 text-cyan-50">Use this form once for each role you want {agencyName} to work on. Clear, complete vacancy details will help the recruiter move faster.</div>

              <div className="sm:col-span-2 rounded-card border border-white/10 bg-slate-950/50 px-4 py-3 text-xs leading-5 text-slate-400">{linkRecord?.privacyNotice || PUBLIC_PRIVACY_NOTICE} {linkRecord?.lawfulUseNotice || PUBLIC_LAWFUL_USE_NOTICE}</div>

              <label className="block sm:col-span-2">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Role title</div>

                <input name="title" value={form.title} onChange={onChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Senior Product Designer" required />

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Company</div>

                <input name="companyName" value={form.companyName} onChange={onChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder={agencyName} required />

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Location</div>

                <input name="location" value={form.location} onChange={onChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Cape Town or Remote" required />

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Hiring contact</div>

                <input name="clientContact" value={form.clientContact} onChange={onChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Hiring manager or HR lead" required />

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Type</div>

                <select name="type" value={form.type} onChange={onChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                  <option>Full-time</option>

                  <option>Contract</option>

                  <option>Part-time</option>

                </select>

              </label>

              <label className="block sm:col-span-2">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Summary</div>

                <textarea name="summary" value={form.summary} onChange={onChange} rows={4} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Explain what this role owns and why it matters." required />

              </label>

              <label className="block sm:col-span-2">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Requirements</div>

                <textarea name="requirements" value={form.requirements} onChange={onChange} rows={4} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Key skills, experience, domain knowledge, and must-haves." required />

              </label>

              {notice && <div className="sm:col-span-2 rounded-card border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">{notice}</div>}

              <div className="sm:col-span-2 flex flex-wrap gap-3">

                <button type="submit" disabled={isSubmitting} className="rounded-full bg-cyan-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-70">{isSubmitting ? 'Submitting vacancy...' : `Submit vacancy to ${agencyName}`}</button>

              </div>

            </form>

          )}

        </section>

      </div>

    </div>

  );

}





function CandidateBrandProvider({ config, children }) {
  const theme = config?.portalTheme || 'midnight';
  const themeTokens = {
    midnight: {
      background: '#080a0f',
      card: 'rgba(18, 22, 31, 0.72)',
      cardStrong: 'rgba(255,255,255,0.075)',
      text: '#f5f5f4',
      muted: '#a1a1aa',
    },
    light: {
      background: '#f8fafc',
      card: 'rgba(255,255,255,0.88)',
      cardStrong: 'rgba(15,23,42,0.06)',
      text: '#0f172a',
      muted: '#475569',
    },
    executive: {
      background: '#111827',
      card: 'rgba(15, 23, 42, 0.86)',
      cardStrong: 'rgba(255,255,255,0.09)',
      text: '#f8fafc',
      muted: '#cbd5e1',
    },
  }[theme] || null;
  const palette = themeTokens || {
    background: config?.background || '#080a0f',
    card: config?.card || 'rgba(18, 22, 31, 0.72)',
    cardStrong: config?.cardStrong || 'rgba(255,255,255,0.075)',
    text: config?.text || '#f5f5f4',
    muted: config?.muted || '#a1a1aa',
  };
  const tokens = {
    '--candidate-bg': palette.background,
    '--candidate-card': palette.card,
    '--candidate-card-strong': palette.cardStrong,
    '--candidate-text': palette.text,
    '--candidate-muted': palette.muted,
    '--candidate-accent': config?.accent || '#6ee7b7',
    '--candidate-radius-lg': config?.radiusLg || '24px',
    '--candidate-font': config?.fontFamily || "'Inter', 'Geist', 'Segoe UI', Arial, sans-serif",
  };

  return (
    <div style={tokens} className="candidate-portal-shell min-h-screen bg-[var(--candidate-bg)] text-[var(--candidate-text)] [font-family:var(--candidate-font)]">
      {children}
    </div>
  );
}

function AgencyLogo({ url, name, size = 'md' }) {
  const [failed, setFailed] = useState(false);
  const dimensions = {
    sm: 'h-9 w-9 rounded-lg text-xs',
    md: 'h-12 w-12 rounded-xl text-sm',
    lg: 'h-16 w-16 rounded-2xl text-lg',
  }[size] || 'h-12 w-12 rounded-xl text-sm';
  const initials = String(name || 'Agency')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  return (
    <div className={`${dimensions} grid shrink-0 place-items-center overflow-hidden border border-[var(--candidate-accent)]/30 bg-[var(--candidate-accent)]/10 font-black text-[var(--candidate-accent)]`} aria-label={`${name || 'Agency'} logo`}>
      {url && !failed ? (
        <img src={url} alt={`${name || 'Agency'} logo`} className="h-full w-full object-contain p-1.5" onError={() => setFailed(true)} />
      ) : initials}
    </div>
  );
}

function CandidateJobIntakePage({ linkRecord, form, notice, isSubmitting, hasSubmitted, selectedJobId, onJobSelect, onChange, onFileChange, onSubmit, onExit, screeningAnswers, onScreeningChange }) {

  const [jobSearch, setJobSearch] = useState('');
  const [selectedSchedule, setSelectedSchedule] = useState('');
  const [messageDraft, setMessageDraft] = useState('');
  const [sentMessages, setSentMessages] = useState([]);
  const [videoPlaying, setVideoPlaying] = useState(false);

  const publicJobs = Array.isArray(linkRecord?.jobs) ? linkRecord.jobs.filter((job) => !['closed', 'removed'].includes(String(job?.status || 'Active').toLowerCase())) : [];

  const filteredJobs = useMemo(() => {
    const query = normalizeText(jobSearch);
    if (!query) return publicJobs;
    return publicJobs.filter((job) => normalizeText([job.title, job.companyName, job.location, job.type, job.summary].join(' ')).includes(query));
  }, [jobSearch, publicJobs]);

  const selectedJob = filteredJobs.find((job) => job.id === selectedJobId) || publicJobs.find((job) => job.id === selectedJobId) || filteredJobs[0] || publicJobs[0] || null;

  const isValidLink = Boolean(linkRecord?.recruiterId && publicJobs.length);
  const agencyName = linkRecord?.agencyName || 'the agency';
  const recruiterName = linkRecord?.recruiterName || 'the recruiter';
  const accentColor = linkRecord?.brandColor || '#6ee7b7';
  const portalTheme = linkRecord?.portalTheme || 'midnight';
  const agencyLogoUrl = linkRecord?.logoUrl || '';
  const heroImageUrl = linkRecord?.heroImageUrl || '';
  const aboutImageUrl = linkRecord?.aboutImageUrl || '';
  const teamImageUrl = linkRecord?.teamImageUrl || '';
  const portalHeadline = linkRecord?.portalHeadline || `Career opportunities with ${agencyName}`;
  const agencyTagline = linkRecord?.tagline || 'Connecting great people with meaningful opportunities.';
  const applicationStatus = String(linkRecord?.applicationStatus || selectedJob?.applicationStatus || (hasSubmitted ? 'Screening' : 'Application_Draft'));

  const candidateState = {
    applicationStatus,
    currentStage: applicationStatus === 'Offer_Extended' ? 'Offer extended' : hasSubmitted ? 'Screening' : 'Apply',
    completedSteps: hasSubmitted ? ['Role selected', 'CV submitted', 'Recruiter notified'] : selectedJob ? ['Role selected'] : [],
    nextAction: applicationStatus === 'Offer_Extended'
      ? 'Review offer packet'
      : hasSubmitted
        ? 'Prep for your technical with these 3 tips'
        : 'Upload your CV and screening answers',
    selectedJob,
    agencyName,
    recruiterName,
    hiringManager: {
      name: linkRecord?.hiringManagerName || selectedJob?.hiringManagerName || recruiterName,
      role: linkRecord?.hiringManagerTitle || selectedJob?.hiringManagerTitle || 'Hiring partner',
      avatarUrl: linkRecord?.hiringManagerAvatarUrl || selectedJob?.hiringManagerAvatarUrl || '',
      videoUrl: linkRecord?.hiringManagerVideoUrl || selectedJob?.hiringManagerVideoUrl || '',
    },
    teamTags: Array.isArray(linkRecord?.teamTags) && linkRecord.teamTags.length
      ? linkRecord.teamTags
      : ['#AsyncFirst', '#ProductMinded', '#HighOwnership', '#CandidateRespect'],
  };

  const milestoneNodes = ['Discover', 'Apply', 'Screening', 'Interview', 'Offer'];
  const currentStageIndex = applicationStatus === 'Offer_Extended'
    ? 4
    : hasSubmitted
      ? 2
      : selectedJob
        ? 1
        : 0;

  const DEFAULT_QS = ['Why are you a strong fit for this role?', 'Describe a recent relevant project or achievement.', 'What is your current notice period?', 'What is your target compensation for this role?'];
  const screeningQuestions = selectedJob && Array.isArray(selectedJob.customScreeningQuestions) && selectedJob.customScreeningQuestions.length > 0 ? selectedJob.customScreeningQuestions : DEFAULT_QS;
  const resources = [
    { title: selectedJob?.title?.toLowerCase().includes('design') ? 'Portfolio Guide' : 'Engineering Handbook', meta: 'Stage-specific prep', icon: FileText },
    { title: 'Interview Scorecard', meta: 'What the team evaluates', icon: ShieldCheck },
    { title: 'Role Context Brief', meta: selectedJob?.companyName || agencyName, icon: Briefcase },
  ];
  const timeline = [
    { title: 'Career portal opened', meta: `${agencyName} shared available roles`, status: 'done' },
    { title: selectedJob ? `${selectedJob.title} selected` : 'Choose a role', meta: selectedJob?.companyName || 'Waiting for selection', status: selectedJob ? 'done' : 'active' },
    { title: hasSubmitted ? 'CV received' : 'CV upload', meta: hasSubmitted ? 'Application injected into recruiter pipeline' : 'PDF required before submission', status: hasSubmitted ? 'done' : 'active' },
    { title: 'Recruiter review', meta: hasSubmitted ? `${recruiterName} can review your application` : 'Unlocks after submission', status: hasSubmitted ? 'active' : 'upcoming' },
  ];

  const sendInlineMessage = () => {
    const trimmed = messageDraft.trim();
    if (!trimmed) return;
    setSentMessages((items) => [{ id: `msg-${Date.now()}`, body: trimmed, sentAt: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) }, ...items]);
    setMessageDraft('');
  };

  return (
    <CandidateBrandProvider config={{ accent: accentColor, portalTheme }}>
      <div className="relative isolate overflow-hidden px-4 py-6 sm:px-6 lg:px-8">
        <div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_20%_0%,rgba(110,231,183,0.12),transparent_34%),linear-gradient(180deg,rgba(255,255,255,0.04),transparent_38%)]" />

        <header className="mx-auto flex max-w-7xl items-center justify-between gap-4 pb-6">
          <div className="flex min-w-0 items-center gap-3">
            <AgencyLogo url={agencyLogoUrl} name={agencyName} size="md" />
            <div className="min-w-0">
              <div className="truncate text-lg font-black text-stone-50 sm:text-xl">{agencyName}</div>
              <div className="mt-1 truncate text-meta font-black uppercase tracking-[0.24em] text-[var(--candidate-accent)]">Careers</div>
            </div>
          </div>
          <button type="button" onClick={onExit} className="rounded-full border border-white/10 bg-white/[0.04] px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-stone-200 transition duration-200 hover:border-[var(--candidate-accent)]/60 hover:bg-white/[0.07] focus:outline-none focus:ring-2 focus:ring-[var(--candidate-accent)]/60">
            Close
          </button>
        </header>

        <main className="mx-auto grid max-w-7xl gap-4 lg:grid-cols-12">
          <section className="relative isolate overflow-hidden animate-[candidateRise_520ms_ease-out_both] rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 shadow-2xl shadow-black/30 backdrop-blur-xl transition duration-300 hover:border-[var(--candidate-accent)]/45 lg:col-span-7 lg:p-6">
            {heroImageUrl && <img src={heroImageUrl} alt="" aria-hidden="true" className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-20" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-transparent via-[var(--candidate-bg)]/40 to-[var(--candidate-bg)]/90" />
            <div className="relative z-10 flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full border border-[var(--candidate-accent)]/25 bg-[var(--candidate-accent)]/10 px-3 py-1 text-meta font-black uppercase tracking-[0.18em] text-[var(--candidate-accent)]">
                  <Sparkles size={13} /> {candidateState.currentStage}
                </div>
                <h2 className="mt-5 max-w-2xl text-3xl font-black tracking-normal text-stone-50 sm:text-5xl">{selectedJob?.title || portalHeadline}</h2>
                <p className="mt-4 max-w-2xl text-sm leading-7 text-zinc-400 sm:text-base">
                  {selectedJob ? `Select a role, upload your CV, and answer the screening questions for ${agencyName}.` : agencyTagline}
                </p>
              </div>
              <div className="rounded-card border border-white/10 bg-white/[0.045] px-4 py-3 text-right">
                <div className="text-micro font-black uppercase tracking-[0.2em] text-zinc-500">Open roles</div>
                <div className="mt-1 text-3xl font-black text-stone-50">{publicJobs.length}</div>
              </div>
            </div>

            <div className="mt-8 rounded-shell border border-white/10 bg-black/20 p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.18em] text-zinc-500">Milestone node graph</div>
                  <div className="mt-1 text-sm font-semibold text-stone-100">Next action: {candidateState.nextAction}</div>
                </div>
                <button type="button" className="rounded-full bg-[var(--candidate-accent)] px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-black transition duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--candidate-accent)]/70">
                  {hasSubmitted ? 'View prep' : 'Start packet'}
                </button>
              </div>
              <div className="mt-6 grid grid-cols-5 gap-2" role="list" aria-label="Application milestones">
                {milestoneNodes.map((node, index) => {
                  const active = index <= currentStageIndex;
                  return (
                    <div key={node} role="listitem" className="relative flex flex-col items-center gap-2">
                      {index < milestoneNodes.length - 1 && <span className={`absolute left-1/2 top-4 h-px w-full ${index < currentStageIndex ? 'bg-[var(--candidate-accent)]' : 'bg-white/10'}`} />}
                      <span className={`relative z-10 grid h-8 w-8 place-items-center rounded-full border text-micro font-black transition duration-300 ${active ? 'border-[var(--candidate-accent)] bg-[var(--candidate-accent)] text-black shadow-[0_0_22px_rgba(110,231,183,0.32)]' : 'border-white/15 bg-zinc-950 text-zinc-500'}`}>
                        {index + 1}
                      </span>
                      <span className={`text-center text-micro font-bold ${active ? 'text-stone-100' : 'text-zinc-600'}`}>{node}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </section>

          <aside className="animate-[candidateRise_620ms_ease-out_both] rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 backdrop-blur-xl transition duration-300 hover:border-[var(--candidate-accent)]/45 lg:col-span-5">
            <div className="relative z-10 flex min-h-20 items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs font-black uppercase tracking-[0.2em] text-zinc-500">Hiring manager</div>
                <div className="mt-2 truncate text-xl font-black text-stone-50">{candidateState.hiringManager.name}</div>
                <div className="truncate text-sm text-zinc-400">{candidateState.hiringManager.role}</div>
              </div>
              <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded-card border border-white/10 bg-white/[0.06]">
                {candidateState.hiringManager.avatarUrl ? <img src={candidateState.hiringManager.avatarUrl} alt="" className="h-full w-full object-cover" /> : <User size={24} className="text-[var(--candidate-accent)]" />}
              </div>
            </div>

            <div className="relative z-10 mt-5 overflow-hidden rounded-shell border border-white/10 bg-black/30">
              {candidateState.hiringManager.videoUrl && videoPlaying ? (
                <video src={candidateState.hiringManager.videoUrl} controls autoPlay className="aspect-video w-full object-cover" aria-label="Hiring manager video" />
              ) : (
                <button type="button" onClick={() => setVideoPlaying(true)} className="group relative block h-56 w-full overflow-hidden text-left focus:outline-none focus:ring-2 focus:ring-[var(--candidate-accent)]/70 sm:h-64">
                  {(teamImageUrl || candidateState.hiringManager.avatarUrl) && <img src={teamImageUrl || candidateState.hiringManager.avatarUrl} alt="" className="absolute inset-0 h-full w-full object-cover opacity-70" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(110,231,183,0.16),transparent_38%),linear-gradient(135deg,#18181b,#09090b)] opacity-80" />
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/55 px-3 py-2 text-xs font-black text-stone-100 backdrop-blur transition duration-200 group-hover:border-[var(--candidate-accent)]/60">
                      <span className="grid h-6 w-6 place-items-center rounded-full bg-[var(--candidate-accent)] text-black">▶</span>
                      Meet the team · 30 sec
                    </div>
                  </div>
                </button>
              )}
            </div>

            <div className="mt-5 flex flex-wrap gap-2" aria-label="Team vibe tags">
              {candidateState.teamTags.map((tag) => (
                <span key={tag} className="rounded-full border border-white/10 bg-white/[0.045] px-3 py-1.5 text-xs font-bold text-zinc-300 transition duration-200 hover:border-[var(--candidate-accent)]/50 hover:text-stone-50">{tag}</span>
              ))}
            </div>
          </aside>

          <section className="animate-[candidateRise_720ms_ease-out_both] rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 backdrop-blur-xl lg:col-span-7">
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.2em] text-zinc-500">Role selection</div>
                <h2 className="mt-2 text-2xl font-black text-stone-50">Choose one vacancy</h2>
              </div>
              <label className="relative block min-w-[220px] flex-1 sm:max-w-sm">
                <span className="sr-only">Search jobs</span>
                <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input value={jobSearch} onChange={(event) => setJobSearch(event.target.value)} className="w-full rounded-card border border-white/10 bg-black/25 py-3 pl-10 pr-4 text-sm text-stone-100 outline-none transition focus:border-[var(--candidate-accent)]/70 focus:ring-2 focus:ring-[var(--candidate-accent)]/15" placeholder="Search title, company, location" />
              </label>
            </div>

            {!isValidLink ? (
              <div className="mt-5 rounded-shell border border-dashed border-white/10 px-4 py-8 text-sm text-zinc-400">This candidate jobs link is missing or no longer available.</div>
            ) : (
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {filteredJobs.length === 0 ? (
                  <div className="rounded-shell border border-dashed border-white/10 px-4 py-8 text-sm text-zinc-400 sm:col-span-2">No jobs match that search right now.</div>
                ) : filteredJobs.map((job) => {
                  const isSelected = selectedJob && selectedJob.id === job.id;
                  return (
                    <button key={job.id} type="button" onClick={() => onJobSelect(job.id)} aria-pressed={isSelected} className={`group rounded-shell border p-4 text-left transition duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--candidate-accent)]/60 ${isSelected ? 'border-[var(--candidate-accent)]/70 bg-[var(--candidate-accent)]/10 shadow-[0_0_34px_rgba(110,231,183,0.18)]' : 'border-white/10 bg-white/[0.035] hover:border-white/25'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <div className="text-base font-black text-stone-50">{job.title}</div>
                          <div className="mt-1 text-xs font-semibold text-zinc-400">{job.companyName || agencyName}{job.location ? ` · ${job.location}` : ''}</div>
                        </div>
                        <span className={`rounded-full px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] ${isSelected ? 'bg-[var(--candidate-accent)] text-black' : 'bg-white/10 text-zinc-400'}`}>{job.type || 'Full-time'}</span>
                      </div>
                      <p className="mt-4 line-clamp-3 text-sm leading-6 text-zinc-400">{job.summary || 'No summary available for this role yet.'}</p>
                    </button>
                  );
                })}
              </div>
            )}
          </section>

          <section className="animate-[candidateRise_820ms_ease-out_both] rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 backdrop-blur-xl lg:col-span-5">
            <div className="text-xs font-black uppercase tracking-[0.2em] text-zinc-500">Transparent timeline</div>
            <div className="mt-5 space-y-4">
              {timeline.map((item, index) => (
                <div key={`${item.title}-${index}`} className="relative pl-8">
                  {index < timeline.length - 1 && <span className="absolute left-[9px] top-6 h-full w-px bg-white/10" />}
                  <span className={`absolute left-0 top-1 grid h-5 w-5 place-items-center rounded-full border ${item.status === 'done' ? 'border-[var(--candidate-accent)] bg-[var(--candidate-accent)]' : item.status === 'active' ? 'border-[var(--candidate-accent)] bg-black' : 'border-white/15 bg-black'}`} />
                  <div className="rounded-card border border-white/10 bg-white/[0.035] px-4 py-3 transition duration-200 hover:border-[var(--candidate-accent)]/40">
                    <div className="text-sm font-black text-stone-100">{item.title}</div>
                    <div className="mt-1 text-xs leading-5 text-zinc-500">{item.meta}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className="animate-[candidateRise_900ms_ease-out_both] rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 backdrop-blur-xl lg:col-span-8">
            {!isValidLink ? null : hasSubmitted ? (
              <div className="rounded-shell border border-[var(--candidate-accent)]/30 bg-[var(--candidate-accent)]/10 p-6 text-center">
                <div className="text-xs font-black uppercase tracking-[0.24em] text-[var(--candidate-accent)]">Application received</div>
                <div className="mt-3 text-3xl font-black text-stone-50">Your CV is in the recruiter pipeline.</div>
                <p className="mx-auto mt-4 max-w-xl text-sm leading-7 text-zinc-300">{notice || `Your application has been delivered to ${agencyName}. ${recruiterName} can review it directly from the recruiter pipeline.`}</p>
                <button type="button" onClick={onExit} className="mt-6 rounded-full bg-[var(--candidate-accent)] px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-black transition">Close</button>
              </div>
            ) : (
              <form className="grid gap-4 sm:grid-cols-2" onSubmit={onSubmit}>
                <div className="sm:col-span-2">
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-zinc-500">Application packet</div>
                  <h2 className="mt-2 text-2xl font-black text-stone-50">{selectedJob ? `Apply for ${selectedJob.title}` : 'Select a role to start'}</h2>
                  <p className="mt-2 text-sm leading-6 text-zinc-400">{linkRecord?.hiringProcess || `Your application will be reviewed by ${agencyName}. ${recruiterName} will contact you if your experience matches the role.`}</p>
                  <p className="mt-2 text-xs leading-5 text-zinc-500">{linkRecord?.privacyNotice || PUBLIC_PRIVACY_NOTICE} {linkRecord?.lawfulUseNotice || PUBLIC_LAWFUL_USE_NOTICE}</p>
                </div>

                <label className="group block sm:col-span-2">
                  <span className="mb-2 flex items-center gap-2 text-meta font-black uppercase tracking-[0.18em] text-zinc-500"><Upload size={14} /> CV / Resume PDF</span>
                  <input type="file" accept="application/pdf,.pdf" onChange={onFileChange} className="w-full rounded-shell border border-white/10 bg-black/30 px-4 py-4 text-sm text-stone-100 outline-none transition file:mr-4 file:rounded-full file:border-0 file:bg-[var(--candidate-accent)] file:px-4 file:py-2 file:text-xs file:font-black file:uppercase file:tracking-[0.14em] file:text-black group-hover:border-[var(--candidate-accent)]/50 focus:border-[var(--candidate-accent)]/70" required />
                </label>

                {[
                  ['name', 'Name override', 'Optional if your CV already includes this', 'text'],
                  ['email', 'Email override', 'Optional if your CV already includes this', 'email'],
                  ['phone', 'Phone override', 'Optional', 'text'],
                  ['location', 'Location override', 'Optional', 'text'],
                ].map(([fieldName, label, placeholder, type]) => (
                  <label key={fieldName} className="block">
                    <span className="mb-2 block text-meta font-black uppercase tracking-[0.18em] text-zinc-500">{label}</span>
                    <input name={fieldName} type={type} value={form[fieldName]} onChange={onChange} className="w-full rounded-card border border-white/10 bg-black/25 px-4 py-3 text-sm text-stone-100 outline-none transition hover:border-white/20 focus:border-[var(--candidate-accent)]/70 focus:ring-2 focus:ring-[var(--candidate-accent)]/15" placeholder={placeholder} />
                  </label>
                ))}

                {notice && <div className="sm:col-span-2 rounded-card border border-[var(--candidate-accent)]/25 bg-[var(--candidate-accent)]/10 px-4 py-3 text-sm text-stone-100">{notice}</div>}

                {selectedJob && (
                  <div className="sm:col-span-2 rounded-shell border border-white/10 bg-white/[0.035] p-4">
                    <div className="text-xs font-black uppercase tracking-[0.2em] text-zinc-500">Screening questions</div>
                    <div className="mt-4 grid gap-3">
                      {screeningQuestions.map((q) => (
                        <label key={q} className="block">
                          <span className="mb-1.5 block text-xs font-semibold leading-5 text-zinc-400">{q}</span>
                          <textarea rows={2} value={(screeningAnswers || {})[q] || ''} onChange={(e) => onScreeningChange && onScreeningChange(q, e.target.value)} placeholder="Your answer..." className="w-full resize-y rounded-card border border-white/10 bg-black/25 px-3 py-2 text-sm text-stone-100 outline-none transition hover:border-white/20 focus:border-[var(--candidate-accent)]/70" />
                        </label>
                      ))}
                    </div>
                  </div>
                )}

                <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                  <button type="submit" disabled={isSubmitting || !selectedJob} className="rounded-full bg-[var(--candidate-accent)] px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-black transition duration-200 disabled:cursor-not-allowed disabled:opacity-50">{isSubmitting ? 'Submitting CV...' : `Submit CV for ${selectedJob?.title || 'selected role'}`}</button>
                  <span className="text-xs text-zinc-500">No page refresh. Inline packet submission.</span>
                </div>
              </form>
            )}
          </section>

          <aside className="animate-[candidateRise_980ms_ease-out_both] grid gap-4 lg:col-span-4">
            {(linkRecord?.agencyAbout || linkRecord?.candidatePromise || linkRecord?.contactEmail || linkRecord?.agencyWebsite || linkRecord?.linkedInUrl) && (
              <section className="rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 backdrop-blur-xl">
                <div className="flex items-center gap-3">
                  <AgencyLogo url={agencyLogoUrl} name={agencyName} size="sm" />
                  <div className="min-w-0">
                    <div className="truncate text-sm font-black text-stone-100">{agencyName}</div>
                    <div className="truncate text-xs text-zinc-500">{agencyTagline}</div>
                  </div>
                </div>
                {aboutImageUrl && <img src={aboutImageUrl} alt={`${agencyName} story`} className="mt-4 h-32 w-full rounded-xl object-cover" onError={(event) => { event.currentTarget.style.display = 'none'; }} />}
                {linkRecord?.agencyAbout && <p className="mt-4 text-sm leading-6 text-zinc-400">{linkRecord.agencyAbout}</p>}
                {linkRecord?.candidatePromise && (
                  <div className="mt-4 rounded-card border border-[var(--candidate-accent)]/20 bg-[var(--candidate-accent)]/10 p-3 text-xs leading-5 text-zinc-300">
                    <div className="mb-1 text-meta font-black uppercase tracking-[0.16em] text-[var(--candidate-accent)]">Our candidate promise</div>
                    {linkRecord.candidatePromise}
                  </div>
                )}
                <div className="mt-4 flex flex-wrap gap-3 text-xs font-bold text-[var(--candidate-accent)]">
                  {linkRecord?.contactEmail && <a href={`mailto:${linkRecord.contactEmail}`} className="hover:underline">Contact us</a>}
                  {linkRecord?.agencyWebsite && <a href={linkRecord.agencyWebsite} target="_blank" rel="noreferrer" className="hover:underline">Website</a>}
                  {linkRecord?.linkedInUrl && <a href={linkRecord.linkedInUrl} target="_blank" rel="noreferrer" className="hover:underline">LinkedIn</a>}
                </div>
              </section>
            )}
            <section className="rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 backdrop-blur-xl">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-zinc-500">Resource vault</div>
              <div className="mt-4 grid gap-3">
                {resources.map(({ title, meta, icon: Icon }) => (
                  <button key={title} type="button" className="flex items-center gap-3 rounded-card border border-white/10 bg-white/[0.035] p-3 text-left transition duration-200 hover:border-[var(--candidate-accent)]/45 focus:outline-none focus:ring-2 focus:ring-[var(--candidate-accent)]/60">
                    <span className="grid h-10 w-10 place-items-center rounded-ctl bg-[var(--candidate-accent)]/12 text-[var(--candidate-accent)]"><Icon size={18} /></span>
                    <span>
                      <span className="block text-sm font-black text-stone-100">{title}</span>
                      <span className="text-xs text-zinc-500">{meta}</span>
                    </span>
                  </button>
                ))}
              </div>
            </section>

            <section className="rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 backdrop-blur-xl">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-zinc-500"><Calendar size={14} /> Inline scheduling</div>
              <div className="mt-4 grid gap-2">
                {['Tue 10:00', 'Wed 14:30', 'Fri 09:15'].map((slot) => (
                  <button key={slot} type="button" onClick={() => setSelectedSchedule(slot)} className={`rounded-card border px-3 py-2 text-left text-sm font-bold transition duration-200 ${selectedSchedule === slot ? 'border-[var(--candidate-accent)] bg-[var(--candidate-accent)]/10 text-stone-50' : 'border-white/10 bg-white/[0.035] text-zinc-300'}`}>{slot}</button>
                ))}
              </div>
            </section>

            <section className="rounded-[var(--candidate-radius-lg)] border border-white/10 bg-[var(--candidate-card)] p-5 backdrop-blur-xl">
              <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-zinc-500"><MessageSquare size={14} /> Message recruiter</div>
              <textarea value={messageDraft} onChange={(event) => setMessageDraft(event.target.value)} rows={3} className="mt-4 w-full resize-none rounded-card border border-white/10 bg-black/25 px-3 py-2 text-sm text-stone-100 outline-none transition focus:border-[var(--candidate-accent)]/70" placeholder={`Ask ${recruiterName} a question...`} />
              <button type="button" onClick={sendInlineMessage} className="mt-3 rounded-full border border-[var(--candidate-accent)]/40 bg-[var(--candidate-accent)]/10 px-4 py-2 text-xs font-black uppercase tracking-[0.14em] text-[var(--candidate-accent)] transition">Send inline</button>
              {sentMessages.length > 0 && (
                <div className="mt-4 space-y-2">
                  {sentMessages.slice(0, 2).map((message) => (
                    <div key={message.id} className="rounded-card border border-white/10 bg-white/[0.035] px-3 py-2 text-xs text-zinc-300">
                      <span className="font-bold text-stone-100">{message.sentAt}</span> {message.body}
                    </div>
                  ))}
                </div>
              )}
            </section>
          </aside>
        </main>
        <footer className="mx-auto mt-8 flex max-w-7xl items-center justify-between gap-3 border-t border-white/10 pt-5 text-xs text-zinc-500">
          <div className="flex min-w-0 items-center gap-2">
            <AgencyLogo url={agencyLogoUrl} name={agencyName} size="sm" />
            <span className="truncate">{agencyName} · Candidate careers</span>
          </div>
          <div className="flex shrink-0 gap-3">
            {linkRecord?.contactEmail && <a href={`mailto:${linkRecord.contactEmail}`} className="hover:text-[var(--candidate-accent)]">Contact</a>}
            {linkRecord?.agencyWebsite && <a href={linkRecord.agencyWebsite} target="_blank" rel="noreferrer" className="hover:text-[var(--candidate-accent)]">Website</a>}
          </div>
        </footer>
      </div>
    </CandidateBrandProvider>
  );

}



function sortCandidates(items) {

  return [...items].sort((left, right) => new Date(right.submittedAt || 0).getTime() - new Date(left.submittedAt || 0).getTime());

}



function createScreeningLinkId(recruiterId, candidateId) {

  return `screen-${recruiterId}-${candidateId}`;

}



function createJobIntakeLinkId(recruiterId) {

  return `job-intake-${recruiterId}`;

}



function createCandidateJobIntakeLinkId(recruiterId) {

  return `candidate-intake-${recruiterId}-${Date.now()}`;

}



function userDocRef(userId) {

  return doc(db, 'artifacts', appId, 'users', userId);

}



function recruiterCandidatesCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'candidates');

}



function recruiterCandidateDoc(userId, candidateId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'candidates', candidateId);

}

function recruiterCompaniesCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'companies');

}

function recruiterCompanyDoc(userId, companyId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'companies', companyId);

}

function recruiterContactsCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'contacts');

}

function recruiterContactDoc(userId, contactId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'contacts', contactId);

}

function recruiterCrmTasksCollection(userId) {
  return collection(db, 'artifacts', appId, 'users', userId, 'crmTasks');
}

function recruiterCrmTaskDoc(userId, taskId) {
  return doc(db, 'artifacts', appId, 'users', userId, 'crmTasks', taskId);
}

function recruiterCrmAutomationRulesCollection(userId) {
  return collection(db, 'artifacts', appId, 'users', userId, 'crmAutomationRules');
}
function recruiterCrmAutomationRuleDoc(userId, ruleId) {
  return doc(db, 'artifacts', appId, 'users', userId, 'crmAutomationRules', ruleId);
}
function recruiterCrmCadenceEnrollmentsCollection(userId) {
  return collection(db, 'artifacts', appId, 'users', userId, 'crmCadenceEnrollments');
}
function recruiterCrmCadenceEnrollmentDoc(userId, enrollId) {
  return doc(db, 'artifacts', appId, 'users', userId, 'crmCadenceEnrollments', enrollId);
}
function recruiterCrmEmailTrackingCollection(userId) {
  return collection(db, 'artifacts', appId, 'users', userId, 'crmEmailTracking');
}
function recruiterCrmEmailTrackingDoc(userId, token) {
  return doc(db, 'artifacts', appId, 'users', userId, 'crmEmailTracking', token);
}

function recruiterNotificationsCollection(userId) {
  return collection(db, 'artifacts', appId, 'users', userId, 'notifications');
}

function recruiterNotificationDoc(userId, notifId) {
  return doc(db, 'artifacts', appId, 'users', userId, 'notifications', notifId);
}

function recruiterSubmissionsCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'submissions');

}

function extractEvidenceSnippets(text = '', terms = [], limit = 3) {
  const lines = String(text || '')
    .split(/\n|\.|;/)
    .map((line) => line.trim())
    .filter((line) => line.length > 24);
  const normalizedTerms = terms.map((term) => normalizeText(term)).filter(Boolean);
  const matches = lines.filter((line) => {
    const haystack = normalizeText(line);
    return normalizedTerms.some((term) => haystack.includes(term));
  });
  return (matches.length ? matches : lines).slice(0, limit).map((line) => truncateLongText(line, 180));
}

function inferCandidateSeniority(candidate = {}) {
  const haystack = normalizeText([candidate.role, candidate.matchedJobTitle, candidate.rawText, candidate.parsedProfile?.seniority].join(' '));
  const years = Number(candidate.yearsExperience || candidate.parsedProfile?.yearsExperience || 0);
  if (haystack.includes('chief') || haystack.includes('vp ') || haystack.includes('vice president') || haystack.includes('director') || haystack.includes('head of')) return 'Executive';
  if (haystack.includes('principal') || haystack.includes('staff') || haystack.includes('lead') || years >= 10) return 'Lead';
  if (haystack.includes('senior') || years >= 6) return 'Senior';
  if (haystack.includes('junior') || haystack.includes('graduate') || years <= 2) return 'Junior';
  return 'Mid';
}

function inferCandidateScope(candidate = {}) {
  const text = String(candidate.rawText || candidate.notes || candidate.parsedProfile?.rawText || '');
  const haystack = normalizeText(text);
  const leadership = /managed|led|lead|mentored|hired|coached|people manager|pod|team/i.test(text);
  const quantified = /(\d+%|\$\s?\d+|r\s?\d+|£\s?\d+|€\s?\d+|\d+\s?(people|engineers|users|clients|revenue|budget|million|m|k))/i.test(text);
  const ownership = /owned|architected|delivered|launched|scaled|migrated|implemented|strategy|roadmap/i.test(text);
  return {
    leadership,
    quantified,
    ownership,
    label: leadership && quantified ? 'Quantified leadership' : leadership ? 'Leadership unclear' : quantified ? 'Impact quantified' : ownership ? 'Delivery ownership' : 'Scope light',
    needs: [
      !quantified ? 'Quantify impact, team size, budget, users, or revenue.' : '',
      leadership && !quantified ? 'Clarify whether leadership means people management, project ownership, or mentorship.' : '',
      !ownership ? 'Add ownership verbs such as owned, built, launched, scaled, or improved.' : '',
    ].filter(Boolean),
    tokens: uniqueWords(haystack).slice(0, 80),
  };
}

function buildCandidateIntelligenceProfile(candidate = {}, screeningResponse = null) {
  const rawText = String(candidate.rawText || candidate.parsedProfile?.rawText || '');
  const skills = Array.from(new Set([
    ...(Array.isArray(candidate.skills) ? candidate.skills : []),
    ...(Array.isArray(candidate.parsedProfile?.skills) ? candidate.parsedProfile.skills : []),
    ...extractSkills(rawText),
  ].filter(Boolean)));
  const seniority = inferCandidateSeniority(candidate);
  const scope = inferCandidateScope(candidate);
  const screeningText = Array.isArray(screeningResponse?.answers)
    ? screeningResponse.answers.map((answer) => `${answer.question || ''} ${answer.answer || ''}`).join(' ')
    : '';
  const searchText = [
    candidate.name,
    candidate.email,
    candidate.phone,
    candidate.role,
    candidate.matchedJobTitle,
    candidate.company,
    candidate.location,
    seniority,
    skills.join(' '),
    scope.label,
    rawText,
    screeningText,
    candidate.notes,
    Array.isArray(candidate.tags) ? candidate.tags.join(' ') : '',
  ].join(' ');
  return {
    seniority,
    skills,
    yearsExperience: Number(candidate.yearsExperience || candidate.parsedProfile?.yearsExperience || extractYearsExperience(rawText) || 0),
    scope,
    hasCv: Boolean(getCandidateResumeUrl(candidate) || rawText),
    hasScreening: Boolean(screeningResponse?.answers?.length || candidate.screeningSubmittedAt),
    parseabilityScore: Math.max(30, Math.min(98, Math.round(
      (rawText.length > 800 ? 25 : rawText.length > 200 ? 15 : 4)
      + (candidate.email ? 12 : 0)
      + (candidate.phone ? 8 : 0)
      + (skills.length >= 6 ? 18 : skills.length * 2)
      + (candidate.location ? 8 : 0)
      + (scope.quantified ? 14 : 0)
      + (scope.ownership ? 10 : 0)
    ))),
    searchText,
    searchTokens: uniqueWords(searchText),
  };
}

function scoreSemanticCandidateSearch(candidate = {}, query = '', screeningResponse = null) {
  const profile = buildCandidateIntelligenceProfile(candidate, screeningResponse);
  const queryTokens = uniqueWords(query).filter((token) => token.length > 1);
  if (!queryTokens.length) {
    return { score: Number(candidate.rating || 0), matched: true, profile, evidence: [], gaps: [] };
  }
  const haystack = normalizeText(profile.searchText);
  const hits = queryTokens.filter((token) => haystack.includes(token));
  const seniorityBoost = queryTokens.includes(normalizeText(profile.seniority)) ? 10 : 0;
  const skillHits = profile.skills.filter((skill) => normalizeText(query).includes(normalizeText(skill)) || haystack.includes(normalizeText(skill))).length;
  const score = Math.round((hits.length / queryTokens.length) * 72 + Math.min(skillHits * 4, 16) + seniorityBoost + Number(candidate.rating || 0) * 0.12);
  return {
    score: Math.max(0, Math.min(100, score)),
    matched: hits.length > 0 || skillHits > 0,
    profile,
    evidence: extractEvidenceSnippets(profile.searchText, hits.length ? hits : queryTokens, 3),
    gaps: queryTokens.filter((token) => !haystack.includes(token)).slice(0, 5),
  };
}

function scoreCandidateForJob(candidate = {}, job = {}, screeningResponse = null) {
  const profile = buildCandidateIntelligenceProfile(candidate, screeningResponse);
  const candidateForScore = {
    role: candidate.role || candidate.matchedJobTitle || '',
    skills: profile.skills,
    rawText: profile.searchText,
  };
  const fit = scoreAgainstJob(candidateForScore, job);
  const requirements = uniqueWords(`${job.title || ''} ${job.requirements || ''} ${job.summary || ''}`).filter((token) => token.length > 2).slice(0, 28);
  const haystack = normalizeText(profile.searchText);
  const met = requirements.filter((token) => haystack.includes(token));
  const missing = requirements.filter((token) => !haystack.includes(token)).slice(0, 7);
  const locationFit = !job.location || normalizeText(job.location).includes('remote') || !candidate.location
    ? 86
    : normalizeText(candidate.location).includes(normalizeText(job.location)) || normalizeText(job.location).includes(normalizeText(candidate.location)) ? 100 : 58;
  const seniorityFit = computeTitleCoverage(candidate.role || candidate.matchedJobTitle, job.title).seniorityMismatch ? 58 : 84;
  const skillsFit = requirements.length ? Math.round((met.length / requirements.length) * 100) : fit.score;
  const overall = Math.round((fit.score * 0.42) + (skillsFit * 0.24) + (seniorityFit * 0.14) + (locationFit * 0.1) + (profile.parseabilityScore * 0.1));
  const evidence = extractEvidenceSnippets(profile.searchText, met.length ? met : requirements, 3);
  let nextAction = 'Prepare submission note';
  if (!profile.hasCv) nextAction = 'Attach CV before submission';
  else if (missing.length > 3) nextAction = `Verify ${missing[0]} before submitting`;
  else if (!profile.scope.quantified) nextAction = 'Ask candidate to quantify scope';
  else if (!profile.hasScreening) nextAction = 'Send screening before client submission';
  return {
    overall: Math.max(0, Math.min(99, overall)),
    dimensions: {
      skills: Math.max(0, Math.min(100, skillsFit)),
      seniority: seniorityFit,
      location: locationFit,
      parseability: profile.parseabilityScore,
      scope: profile.scope.quantified ? 88 : profile.scope.leadership ? 66 : 54,
    },
    evidence,
    missing,
    profile,
    nextAction,
    summary: `${profile.seniority} profile with ${met.length}/${requirements.length || met.length || 1} job signals evidenced. ${profile.scope.label}.`,
  };
}

function buildJobMatchReason(overall, blendedScore, dimensions, _missing, evidence, profile, job, candidate) {
  const score = blendedScore || overall;
  const dim = dimensions || {};
  const skillCoverage = Math.max(0, Math.min(100, Math.round(dim.skills || 0)));
  const seniorityAligned = (dim.seniority || 0) >= 70;
  const locationClear = (dim.location || 0) >= 70;
  const cvReadable = (dim.parseability || 0) >= 50;
  const seniority = String(profile?.seniority || '');
  const candidateYears = Number(candidate?.yearsExperience || profile?.yearsExperience || 0);
  const candidateSkills = (candidate?.skills || profile?.skills || []).map((s) => normalizeText(String(s || '')));
  const candidateRole = normalizeText(String(candidate?.role || candidate?.matchedJobTitle || ''));
  const candidateLoc = String(candidate?.location || '').trim();
  const jobLoc = String(job?.location || '').trim();
  const jobText = [job?.title, job?.requirements, job?.summary].filter(Boolean).join(' ');
  const jobNorm = normalizeText(jobText);

  // --- Extract required years from job text ---
  const yearsRx = /(\d+)\s*(?:\+|to\s*\d+)?\s*years?(?:\s+of)?\s*(?:relevant\s+)?(?:experience|exp)?/i;
  const yearsMatch = yearsRx.exec(jobText);
  const requiredYears = yearsMatch ? parseInt(yearsMatch[1], 10) : null;

  // --- Identify job domain ---
  const DOMAINS = [
    { name: 'sales', keywords: ['sales', 'selling', 'revenue', 'quota', 'business development', 'account executive', 'account manager', 'bde', 'bdm'] },
    { name: 'software development', keywords: ['developer', 'software engineer', 'frontend', 'backend', 'full stack', 'react', 'node', 'python', 'java', 'coding', 'programming'] },
    { name: 'finance', keywords: ['finance', 'accounting', 'bookkeeping', 'payroll', 'audit', 'financial', 'cfo', 'accountant', 'creditors', 'debtors'] },
    { name: 'marketing', keywords: ['marketing', 'digital marketing', 'seo', 'sem', 'social media', 'brand', 'content', 'campaign'] },
    { name: 'operations', keywords: ['operations', 'logistics', 'supply chain', 'warehouse', 'procurement', 'fleet'] },
    { name: 'customer service', keywords: ['customer service', 'customer support', 'call centre', 'helpdesk', 'service desk', 'customer care'] },
    { name: 'HR', keywords: ['human resources', 'hr generalist', 'recruitment', 'talent acquisition', 'payroll', 'people operations'] },
  ];
  const jobDomain = DOMAINS.find((d) => d.keywords.some((k) => jobNorm.includes(k))) || null;
  const candidateInDomain = jobDomain
    ? (jobDomain.keywords.some((k) => candidateRole.includes(k)) || candidateSkills.some((s) => jobDomain.keywords.some((k) => s.includes(k) || k.includes(s))))
    : true;

  // --- Extract 2-3 named skills the job asks for that the candidate doesn't clearly have ---
  const NAMED_SKILLS = ['salesforce', 'sap', 'excel', 'powerpoint', 'power bi', 'tableau', 'google analytics', 'hubspot', 'zoho', 'microsoft office', 'pastel', 'sage', 'quickbooks', 'xero', 'autocad', 'solidworks', 'jira', 'git', 'aws', 'azure', 'sql', 'javascript', 'python', 'java', 'php', 'react', 'angular', 'vue', 'node', 'shopify', 'wordpress'];
  const jobAsksFor = NAMED_SKILLS.filter((sk) => jobNorm.includes(sk));
  const candidateMissing = jobAsksFor.filter((sk) => !candidateSkills.some((cs) => cs.includes(sk) || sk.includes(cs)));

  // --- Location gap ---
  let locationGap = null;
  if (!locationClear) {
    if (jobLoc && candidateLoc) locationGap = `the role is based in ${jobLoc} and the candidate is in ${candidateLoc}`;
    else if (jobLoc) locationGap = `the role requires presence in ${jobLoc}`;
    else locationGap = 'location may be incompatible with this role';
  }

  // --- Build ordered specific facts ---
  const facts = [];
  if (requiredYears && candidateYears > 0 && candidateYears < requiredYears) {
    facts.push(`the role requires ${requiredYears}+ years of experience and the candidate has ${candidateYears}`);
  } else if (requiredYears && !candidateYears && !cvReadable) {
    facts.push(`the role asks for ${requiredYears}+ years of experience but the CV does not confirm this`);
  }
  if (jobDomain && !candidateInDomain) {
    facts.push(`the role is ${jobDomain.name}-focused and the candidate has no ${jobDomain.name} background on record`);
  }
  if (candidateMissing.length > 0) {
    const listed = candidateMissing.slice(0, 2).join(' and ');
    facts.push(`the role specifically asks for ${listed} which is not evidenced in the candidate's profile`);
  }
  if (locationGap) facts.push(locationGap);
  if (!seniorityAligned && seniority) {
    facts.push(`the candidate is ${seniority.toLowerCase()} and this vacancy requires a different experience level`);
  }

  // --- Compose the sentence ---
  if (score >= 78) {
    const positives = [];
    if (skillCoverage >= 70) positives.push(`${skillCoverage}% of the vacancy requirements are evidenced in the CV`);
    if (requiredYears && candidateYears >= requiredYears) positives.push(`meets the ${requiredYears}+ years requirement with ${candidateYears} years`);
    if (seniorityAligned && seniority) positives.push(`${seniority} level is a strong match for this brief`);
    if (jobDomain && candidateInDomain) positives.push(`solid ${jobDomain.name} background aligns with the role`);
    return positives.length ? positives.slice(0, 2).join('; ') + '.' : 'Strong match across skills, experience level, and role requirements.';
  }

  if (score >= 62) {
    const concern = facts[0] || (skillCoverage < 58 ? `only ${skillCoverage}% of the role requirements are covered` : null) || (seniority ? `${seniority} experience may not fully meet the brief` : 'experience level may not fully meet the brief');
    return `Moderate fit \u2014 ${concern}.`;
  }

  if (score >= 42) {
    const primary = facts[0] || `only ${skillCoverage}% of the role requirements are evidenced in this CV`;
    const secondary = facts[1] || null;
    return secondary ? `Lower match \u2014 ${primary}; ${secondary}.` : `Lower match \u2014 ${primary}.`;
  }

  const primary = facts[0] || `only ${skillCoverage}% of the role requirements are present in the CV`;
  const secondary = facts[1] || null;
  return secondary ? `Not a fit \u2014 ${primary}; ${secondary}.` : `Not a fit \u2014 ${primary}.`;
}

function recruiterSubmissionDoc(userId, submissionId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'submissions', submissionId);

}

function recruiterApplicationsCollection(userId) {
  return collection(db, 'artifacts', appId, 'users', userId, 'applications');
}

function recruiterApplicationDoc(userId, applicationId) {
  return doc(db, 'artifacts', appId, 'users', userId, 'applications', applicationId);
}

function recruiterPlacementsCollection(userId) {
  return collection(db, 'artifacts', appId, 'users', userId, 'placements');
}

function recruiterPlacementDoc(userId, placementId) {
  return doc(db, 'artifacts', appId, 'users', userId, 'placements', placementId);
}



function recruiterResponsesCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'screeningResponses');

}



function recruiterReviewQueueCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'candidateReviewQueue');

}



function recruiterReviewQueueDoc(userId, reviewId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'candidateReviewQueue', reviewId);

}



function recruiterExternalProfilesCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'externalProfiles');

}



function recruiterResponseDoc(userId, candidateId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'screeningResponses', candidateId);

}



function publicScreeningLinkDoc(linkId) {

  return doc(db, 'artifacts', appId, 'public', 'data', 'screeningLinks', linkId);

}



function publicJobIntakeLinkDoc(linkId) {

  return doc(db, 'artifacts', appId, 'public', 'data', 'jobIntakeLinks', linkId);

}



function publicCandidateJobIntakeLinkDoc(linkId) {

  return doc(db, 'artifacts', appId, 'public', 'data', 'candidateJobIntakeLinks', linkId);

}

function publicShortlistDoc(token) {
  return doc(db, 'artifacts', appId, 'public', 'data', 'shortlists', token);
}



function recruiterActivityCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'activity');

}



function recruiterActivityDoc(userId, activityId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'activity', activityId);

}



function agencyDocRef(agencyKey) {

  return doc(db, 'artifacts', appId, 'agencies', agencyKey);

}



function agencyMembersCollection(agencyKey) {

  return collection(db, 'artifacts', appId, 'agencies', agencyKey, 'members');

}



function agencyMemberDoc(agencyKey, userId) {

  return doc(db, 'artifacts', appId, 'agencies', agencyKey, 'members', userId);

}

function agencyTeamMessagesCollection(agencyKey) {

  return collection(db, 'artifacts', appId, 'agencies', agencyKey, 'teamMessages');

}

function agencyTeamMessageDoc(agencyKey, messageId) {

  return doc(db, 'artifacts', appId, 'agencies', agencyKey, 'teamMessages', messageId);

}



function recruiterSequenceTemplatesCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'sequenceTemplates');

}



function recruiterSequenceRunsCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'sequenceRuns');

}



function recruiterSequenceTasksCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'sequenceTasks');

}



function recruiterSequenceSuppressionsCollection(userId) {

  return collection(db, 'artifacts', appId, 'users', userId, 'sequenceSuppressions');

}



function recruiterSequenceSuppressionDoc(userId, suppressionId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'sequenceSuppressions', suppressionId);

}



function recruiterSequenceTaskDoc(userId, taskId) {

  return doc(db, 'artifacts', appId, 'users', userId, 'sequenceTasks', taskId);

}



function formatSequenceChannelLabel(channel) {

  if (channel === 'email_send') return 'Email';

  if (channel === 'linkedin_task') return 'LinkedIn';

  if (channel === 'whatsapp_task') return 'WhatsApp';

  if (channel === 'call_task') return 'Call';

  return 'Sequence';

}



function isLegacySequenceAuthFailureMessage(value = '') {

  const normalized = normalizeText(value);

  return normalized.includes('535-5.7.8')

    || normalized.includes('username and password not accepted')

    || normalized.includes('badcredentials');

}



function isLegacySequenceAuthFailureRun(run) {

  return String(run?.status || '').toLowerCase() === 'failed'

    && isLegacySequenceAuthFailureMessage(run?.blockedReason || run?.lastError || '');

}



function isLegacySequenceAuthFailureActivity(item) {

  return String(item?.type || '').toLowerCase() === 'sequence_step_failed'

    && isLegacySequenceAuthFailureMessage(item?.metadata?.error || item?.summary || '');

}



function getSequenceRunStatus(run) {

  return String(run?.status || '').toLowerCase();

}



function isActionableSequenceRun(run) {

  const status = getSequenceRunStatus(run);

  if (status === 'waiting_manual') return true;

  if (status !== 'active') return false;

  const nextStepAtMs = Number(run?.nextStepAtMs || 0);

  return !nextStepAtMs || nextStepAtMs <= Date.now();

}



function findLatestSequenceRunForTarget(runs = [], jobId, targetId) {

  return runs

    .filter((run) => run?.jobSnapshot?.id === jobId && String(run?.target?.id || '') === String(targetId || ''))

    .sort((left, right) => Number(right?.updatedAtMs || right?.startedAtMs || right?.createdAtMs || 0) - Number(left?.updatedAtMs || left?.startedAtMs || left?.createdAtMs || 0))[0] || null;

}



function buildNoDueSequenceNotice(runs = [], tasks = []) {

  const openTasks = tasks.filter((task) => String(task?.status || '').toLowerCase() === 'open');

  if (openTasks.length > 0) {

    const nextTask = openTasks[0];

    return `No due steps were ready to advance. ${openTasks.length} manual task${openTasks.length === 1 ? ' is' : 's are'} already open for this vacancy. Complete or skip the ${formatSequenceChannelLabel(nextTask?.channel)} task for ${nextTask?.target?.name || 'the target'} first.`;

  }



  const activeRuns = runs

    .filter((run) => String(run?.status || '').toLowerCase() === 'active' && Number(run?.nextStepAtMs || 0) > Date.now())

    .sort((left, right) => Number(left?.nextStepAtMs || 0) - Number(right?.nextStepAtMs || 0));

  if (activeRuns.length > 0) {

    const nextRun = activeRuns[0];

    const dueLabel = nextRun?.nextStepAtIso ? new Date(nextRun.nextStepAtIso).toLocaleString() : 'later';

    return `No due steps were ready to advance. The next step for ${nextRun?.target?.name || 'this sequence'} is scheduled for ${dueLabel}.`;

  }



  const waitingManualRuns = runs.filter((run) => String(run?.status || '').toLowerCase() === 'waiting_manual');

  if (waitingManualRuns.length > 0) {

    const nextRun = waitingManualRuns[0];

    return `No due steps were ready to advance. ${nextRun?.target?.name || 'This sequence'} is waiting for a manual outreach task to be completed.`;

  }



  const blockedRuns = runs.filter((run) => String(run?.status || '').toLowerCase() === 'failed');

  if (blockedRuns.length > 0) {

    const blockedRun = blockedRuns[0];

    return `No due steps were ready to advance. ${blockedRun?.target?.name || 'A sequence'} needs attention${blockedRun?.blockedReason ? `: ${blockedRun.blockedReason}.` : '.'}`;

  }



  if (runs.some((run) => String(run?.status || '').toLowerCase() === 'completed')) {

    return 'No due steps were ready to advance. The current sequence runs for this vacancy are already completed.';

  }



  if (runs.length > 0) {

    return 'No due steps were ready to advance. The current sequence runs are not waiting on an automatic step right now.';

  }



  return 'No due sequence steps were ready to advance.';

}



function getSequenceTimestampMs(value) {

  if (!value) return 0;

  if (typeof value === 'number' && Number.isFinite(value)) return value;

  if (typeof value === 'string') {

    const parsed = Date.parse(value);

    return Number.isFinite(parsed) ? parsed : 0;

  }

  if (typeof value?.toDate === 'function') {

    return value.toDate().getTime();

  }

  if (typeof value?.seconds === 'number') {

    return (value.seconds * 1000) + Math.round(Number(value.nanoseconds || 0) / 1000000);

  }

  return 0;

}



function formatSequenceAnalyticsPercent(value) {

  return `${Math.round(Math.max(0, Number(value || 0)) * 100)}%`;

}



function getSequenceOutcomeLabel(outcome) {

  const normalized = String(outcome || '').toLowerCase();

  const labels = {

    email_sent: 'Email Sent',

    task_created: 'Task Created',

    sent: 'Sent',

    replied: 'Replied',

    reply_detected: 'Reply Detected',

    no_response: 'No Response',

    connected: 'Connected',

    no_answer: 'No Answer',

    voicemail: 'Voicemail',

    not_interested: 'Not Interested',

    wrong_number: 'Wrong Number',

    opted_out: 'Opted Out',

    invalid_profile: 'Invalid Profile',

    suppressed: 'Suppressed',

    throttled: 'Throttled',

    channel_unavailable: 'Channel Unavailable',

    missing_email: 'Missing Email',

    skipped: 'Skipped',

  };

  return labels[normalized] || (normalized ? normalized.replace(/_/g, ' ').replace(/\b\w/g, (character) => character.toUpperCase()) : 'No Outcome');

}



function getSequenceSuppressionReasonLabel(reason) {

  const normalized = String(reason || '').toLowerCase();

  if (!normalized) return 'Suppressed';

  return getSequenceOutcomeLabel(normalized);

}



function getSequenceExperimentLabel(experiment = {}) {

  const personaLabel = String(experiment?.personaLabel || experiment?.personaKey || '').trim();

  const variantLabel = String(experiment?.variantLabel || experiment?.variantKey || '').trim();

  if (personaLabel && variantLabel) return `${personaLabel} / ${variantLabel}`;

  return personaLabel || variantLabel || 'Default';

}



function hashSequenceExperimentSeed(value) {

  const input = String(value || '');

  let hash = 0;

  for (let index = 0; index < input.length; index += 1) {

    hash = ((hash << 5) - hash) + input.charCodeAt(index);

    hash |= 0;

  }

  return Math.abs(hash);

}



const SEQUENCE_PERSONAS = [

  {

    id: 'formal',

    label: 'Formal',

    intro: 'I am reaching out regarding',

    ask: 'If you are open to it, I would value a short conversation.',

    linkedInTone: 'Keep the note polished and recruiter-led.',

    whatsappTone: 'Keep the WhatsApp note respectful and concise.',

  },

  {

    id: 'consultative',

    label: 'Consultative',

    intro: 'I wanted to share',

    ask: 'If the timing is worth exploring, I can send the details.',

    linkedInTone: 'Lead with relevance and a consultative tone.',

    whatsappTone: 'Make it feel like a warm, insight-led follow-up.',

  },

  {

    id: 'direct',

    label: 'Direct',

    intro: 'Quick one about',

    ask: 'If this is worth a look, I can send the essentials right away.',

    linkedInTone: 'Keep the note crisp and outcome-oriented.',

    whatsappTone: 'Keep it short, direct, and easy to reply to.',

  },

];



const SEQUENCE_VARIANTS = [

  {

    id: 'a',

    label: 'Variant A',

    subject: ({ roleTitle, companyName }) => `Opportunity: ${roleTitle} with ${companyName}`,

    callToAction: 'Would you be open to hearing more?',

  },

  {

    id: 'b',

    label: 'Variant B',

    subject: ({ roleTitle, companyName }) => `${roleTitle} opportunity | ${companyName}`,

    callToAction: 'Should I send a short overview?',

  },

];



function selectStarterSequenceExperiment(target = {}, job = {}) {

  const seed = hashSequenceExperimentSeed(`${job?.id || 'job'}:${target?.id || target?.email || target?.name || 'target'}`);

  const persona = SEQUENCE_PERSONAS[seed % SEQUENCE_PERSONAS.length] || SEQUENCE_PERSONAS[0];

  const variant = SEQUENCE_VARIANTS[Math.floor(seed / SEQUENCE_PERSONAS.length) % SEQUENCE_VARIANTS.length] || SEQUENCE_VARIANTS[0];

  return {

    personaKey: persona.id,

    personaLabel: persona.label,

    variantKey: variant.id,

    variantLabel: variant.label,

  };

}



function buildSequenceAnalytics(runs = []) {

  const channelOrder = ['email_send', 'linkedin_task', 'whatsapp_task', 'call_task'];

  const channelSummaries = channelOrder.map((channel) => ({

    channel,

    label: formatSequenceChannelLabel(channel),

    totalTouches: 0,

    completed: 0,

    open: 0,

    skipped: 0,

    failed: 0,

    engaged: 0,

    outcomeCounts: {},

    engagementRate: 0,

    completionRate: 0,

    topOutcome: '',

  }));

  const channelSummaryMap = channelSummaries.reduce((accumulator, summary) => {

    accumulator[summary.channel] = summary;

    return accumulator;

  }, {});

  const statusSummaries = [

    { id: 'active', label: 'Active', count: 0 },

    { id: 'waiting_manual', label: 'Waiting Manual', count: 0 },

    { id: 'completed', label: 'Completed', count: 0 },

    { id: 'failed', label: 'Failed', count: 0 },

  ];

  const statusSummaryMap = statusSummaries.reduce((accumulator, summary) => {

    accumulator[summary.id] = summary;

    return accumulator;

  }, {});

  const recentWindows = [

    { id: '7d', label: 'Last 7 days', days: 7, started: 0, completed: 0, replies: 0, failed: 0 },

    { id: '30d', label: 'Last 30 days', days: 30, started: 0, completed: 0, replies: 0, failed: 0 },

  ];

  const analytics = {

    totalRuns: runs.length,

    activeRuns: 0,

    waitingManualRuns: 0,

    completedRuns: 0,

    failedRuns: 0,

    replyDetectedRuns: 0,

    engagedRuns: 0,

    emailSentCount: 0,

    openStepCount: 0,

    failedStepCount: 0,

    skippedStepCount: 0,

    averageCompletedStepsPerRun: 0,

    completionRate: 0,

    replyRate: 0,

    engagedRate: 0,

    statusSummaries,

    recentWindows,

    topOutcomes: [],

    personaSummaries: [],

    variantSummaries: [],

    channelSummaries,

  };

  const globalOutcomeCounts = {};

  const personaCounts = {};

  const variantCounts = {};

  const nowMs = Date.now();

  let completedStepsTotal = 0;



  runs.forEach((run) => {

    const status = String(run?.status || '').toLowerCase();

    if (status === 'active') analytics.activeRuns += 1;

    if (status === 'waiting_manual') analytics.waitingManualRuns += 1;

    if (status === 'completed') analytics.completedRuns += 1;

    if (status === 'failed') analytics.failedRuns += 1;

    if (statusSummaryMap[status]) statusSummaryMap[status].count += 1;

    if (run?.replyDetectedAtIso || String(run?.completedReason || '').toLowerCase() === 'reply_detected') {

      analytics.replyDetectedRuns += 1;

    }



    const startedMs = getSequenceTimestampMs(run?.startedAtIso || run?.createdAt || run?.updatedAt);

    const completedMs = status === 'completed'

      ? getSequenceTimestampMs(run?.replyDetectedAtIso || run?.replyDetectedAt || run?.completedAt || run?.updatedAt)

      : 0;

    const replyMs = getSequenceTimestampMs(run?.replyDetectedAtIso || run?.replyDetectedAt);

    const failedMs = status === 'failed'

      ? getSequenceTimestampMs(run?.updatedAt || run?.startedAtIso)

      : 0;

    recentWindows.forEach((window) => {

      const thresholdMs = nowMs - (window.days * 24 * 60 * 60 * 1000);

      if (startedMs && startedMs >= thresholdMs) window.started += 1;

      if (completedMs && completedMs >= thresholdMs) window.completed += 1;

      if (replyMs && replyMs >= thresholdMs) window.replies += 1;

      if (failedMs && failedMs >= thresholdMs) window.failed += 1;

    });



    const stepStates = Array.isArray(run?.stepStates) ? run.stepStates : [];

    const runHasEngagement = stepStates.some((step) => ['replied', 'connected', 'not_interested', 'opted_out', 'reply_detected'].includes(String(step?.outcome || '').toLowerCase()));

    if (runHasEngagement || replyMs) {

      analytics.engagedRuns += 1;

    }

    const experiment = run?.experiment || {};

    const personaKey = String(experiment?.personaKey || 'default').trim() || 'default';

    const variantKey = String(experiment?.variantKey || 'default').trim() || 'default';

    if (!personaCounts[personaKey]) {

      personaCounts[personaKey] = {

        id: personaKey,

        label: String(experiment?.personaLabel || personaKey || 'Default').trim(),

        runs: 0,

        replies: 0,

        completed: 0,

      };

    }

    if (!variantCounts[variantKey]) {

      variantCounts[variantKey] = {

        id: variantKey,

        label: String(experiment?.variantLabel || variantKey || 'Default').trim(),

        runs: 0,

        replies: 0,

        completed: 0,

      };

    }

    personaCounts[personaKey].runs += 1;

    variantCounts[variantKey].runs += 1;

    if (replyMs) {

      personaCounts[personaKey].replies += 1;

      variantCounts[variantKey].replies += 1;

    }

    if (status === 'completed') {

      personaCounts[personaKey].completed += 1;

      variantCounts[variantKey].completed += 1;

    }

    stepStates.forEach((step) => {

      const channelSummary = channelSummaryMap[String(step?.channel || '')];

      if (!channelSummary) return;

      const stepStatus = String(step?.status || '').toLowerCase();

      const outcome = String(step?.outcome || '').toLowerCase();

      channelSummary.totalTouches += 1;

      if (channelSummary.channel === 'email_send' && outcome === 'email_sent') {

        analytics.emailSentCount += 1;

      }

      if (stepStatus === 'completed') {

        channelSummary.completed += 1;

        completedStepsTotal += 1;

      } else if (stepStatus === 'pending' || stepStatus === 'awaiting_manual') {

        channelSummary.open += 1;

        analytics.openStepCount += 1;

      } else if (stepStatus === 'skipped') {

        channelSummary.skipped += 1;

        analytics.skippedStepCount += 1;

      } else if (stepStatus === 'failed') {

        channelSummary.failed += 1;

        analytics.failedStepCount += 1;

      }



      if (['replied', 'connected', 'not_interested', 'opted_out', 'reply_detected'].includes(outcome)) {

        channelSummary.engaged += 1;

      }

      if (outcome) {

        channelSummary.outcomeCounts[outcome] = (channelSummary.outcomeCounts[outcome] || 0) + 1;

        globalOutcomeCounts[outcome] = (globalOutcomeCounts[outcome] || 0) + 1;

      }

    });

  });



  analytics.averageCompletedStepsPerRun = analytics.totalRuns > 0 ? completedStepsTotal / analytics.totalRuns : 0;

  analytics.completionRate = analytics.totalRuns > 0 ? analytics.completedRuns / analytics.totalRuns : 0;

  analytics.replyRate = analytics.totalRuns > 0 ? analytics.replyDetectedRuns / analytics.totalRuns : 0;

  analytics.engagedRate = analytics.totalRuns > 0 ? analytics.engagedRuns / analytics.totalRuns : 0;



  analytics.topOutcomes = Object.entries(globalOutcomeCounts)

    .sort((left, right) => right[1] - left[1])

    .slice(0, 4)

    .map(([outcome, count]) => ({

      id: outcome,

      label: getSequenceOutcomeLabel(outcome),

      count,

    }));



  analytics.personaSummaries = Object.values(personaCounts)

    .sort((left, right) => right.runs - left.runs)

    .map((summary) => ({

      ...summary,

      replyRate: summary.runs > 0 ? summary.replies / summary.runs : 0,

      completionRate: summary.runs > 0 ? summary.completed / summary.runs : 0,

    }));

  analytics.variantSummaries = Object.values(variantCounts)

    .sort((left, right) => right.runs - left.runs)

    .map((summary) => ({

      ...summary,

      replyRate: summary.runs > 0 ? summary.replies / summary.runs : 0,

      completionRate: summary.runs > 0 ? summary.completed / summary.runs : 0,

    }));



  analytics.channelSummaries = channelSummaries.map((summary) => {

    const outcomeEntries = Object.entries(summary.outcomeCounts || {}).sort((left, right) => right[1] - left[1]);

    return {

      ...summary,

      engagementRate: summary.totalTouches > 0 ? summary.engaged / summary.totalTouches : 0,

      completionRate: summary.totalTouches > 0 ? summary.completed / summary.totalTouches : 0,

      topOutcome: outcomeEntries[0]?.[0] || '',

      topOutcomeLabel: outcomeEntries[0]?.[0] ? getSequenceOutcomeLabel(outcomeEntries[0][0]) : 'No Outcome',

    };

  });



  return analytics;

}



function buildStarterSequenceSteps(target, job, recruiterProfile, experiment = {}) {

  const companyName = getJobCompanyName(job) || target.company || 'the client';

  const roleTitle = job?.title || 'this role';

  const recruiterName = recruiterProfile?.name || 'Recruiter';

  const persona = SEQUENCE_PERSONAS.find((item) => item.id === experiment?.personaKey) || SEQUENCE_PERSONAS[0];

  const variant = SEQUENCE_VARIANTS.find((item) => item.id === experiment?.variantKey) || SEQUENCE_VARIANTS[0];

  const isCandidatePipelineUpdate = target?.workflowType === 'candidate_pipeline';

  const pipelineWindowLabel = target?.pipelineWindowLabel || 'current review window';

  const pipelineWindowDetail = target?.pipelineWindowDetail || 'The recruiter is progressing your profile through the current review step.';

  const steps = [];



  if (target.email) {

    steps.push({

      id: 'intro_email',

      channel: 'email_send',

      delayHours: 0,

      delayBusinessDays: 0,

      preferredHourLocal: 9,

      respectBusinessHours: true,

      subject: isCandidatePipelineUpdate ? `Update on your CV: ${roleTitle} | ${companyName}` : variant.subject({ roleTitle, companyName }),

      body: isCandidatePipelineUpdate

        ? `Hi ${target.firstName || target.name},\n\nI wanted to share a quick update on your CV for the ${roleTitle} role with ${companyName}${job?.location ? ` in ${job.location}` : ''}. Your application is currently in the ${pipelineWindowLabel}.\n\n${pipelineWindowDetail}\n\nIf you have any questions while we move through this stage, feel free to reply and I will keep you updated on the next step.\n\nBest,\n${recruiterName}`

        : `Hi ${target.firstName || target.name},\n\n${persona.intro} a ${roleTitle} opportunity with ${companyName}${job?.location ? ` in ${job.location}` : ''}. Your background looks relevant, and I think there could be a strong fit here.\n\n${variant.callToAction} ${persona.ask}\n\nBest,\n${recruiterName}`,

    });

  }



  if (target.linkedInUrl) {

    steps.push({

      id: 'linkedin_follow_up',

      channel: 'linkedin_task',

      delayHours: 0,

      delayBusinessDays: target.email ? 1 : 0,

      preferredHourLocal: 10,

      respectBusinessHours: true,

      taskNote: isCandidatePipelineUpdate

        ? `Send a LinkedIn update to ${target.name} confirming that their CV for ${roleTitle} is in the ${pipelineWindowLabel}. Keep the note clear and recruiter-led, and explain the current review step without overpromising timing.`

        : `Send a LinkedIn message to ${target.name} about the ${roleTitle} role with ${companyName}. ${persona.linkedInTone} Reference their background and ask whether they would be open to a short conversation.`,

    });

  }



  if (target.phone) {

    steps.push({

      id: 'whatsapp_follow_up',

      channel: 'whatsapp_task',

      delayHours: 0,

      delayBusinessDays: target.email || target.linkedInUrl ? 3 : 1,

      preferredHourLocal: 11,

      respectBusinessHours: true,

      taskNote: isCandidatePipelineUpdate

        ? `Send a WhatsApp update to ${target.name} letting them know their CV for ${roleTitle} is in the ${pipelineWindowLabel}. Keep it concise, confirm the current review step, and invite a reply if they want clarification.`

        : `Send a WhatsApp follow-up to ${target.name} about the ${roleTitle} opportunity with ${companyName}. ${persona.whatsappTone} Mention that you previously reached out and invite a quick reply if they want more detail.`,

    });

    steps.push({

      id: 'call_follow_up',

      channel: 'call_task',

      delayHours: 0,

      delayBusinessDays: target.email || target.linkedInUrl ? 4 : 2,

      preferredHourLocal: 15,

      respectBusinessHours: true,

      taskNote: isCandidatePipelineUpdate

        ? `Call ${target.name} with a CV status update for ${roleTitle}. Explain that their application is in the ${pipelineWindowLabel}, outline the current review step, answer questions, and capture the outcome clearly.`

        : `Call ${target.name} to discuss the ${roleTitle} opportunity with ${companyName}. Qualify interest, timing, and compensation expectations, then capture the outcome clearly.`,

    });

  }



  return steps;

}



function buildSequenceTargetFromCandidate(candidate) {

  const name = candidate?.name || 'Unnamed candidate';

  const pipelineWindow = getCandidatePipelineWindow(candidate);

  return {

    id: candidate?.id || normalizeText(name) || `candidate-${Date.now()}`,

    name,

    firstName: String(name).trim().split(/\s+/)[0] || name,

    email: candidate?.email || '',

    phone: candidate?.phone || '',

    linkedInUrl: candidate?.linkedInUrl || candidate?.linkedinUrl || '',

    company: candidate?.company || '',

    headline: candidate?.role || '',

    location: candidate?.location || '',

    workflowType: 'candidate_pipeline',

    pipelineStageId: pipelineWindow.stageId,

    pipelineStageLabel: pipelineWindow.stageLabel,

    pipelineWindowLabel: pipelineWindow.windowLabel,

    pipelineWindowDetail: pipelineWindow.detail,

  };

}



function buildSequenceTargetFromProspect(prospect) {

  const name = prospect?.name || 'Unnamed prospect';

  return {

    id: prospect?.id || normalizeText(name) || `prospect-${Date.now()}`,

    name,

    firstName: String(name).trim().split(/\s+/)[0] || name,

    email: prospect?.email || '',

    phone: prospect?.phone || '',

    linkedInUrl: prospect?.linkedInUrl || prospect?.linkedinUrl || '',

    company: prospect?.company || prospect?.matchedCompanyName || '',

    headline: prospect?.headline || prospect?.role || '',

    location: prospect?.location || '',

    workflowType: 'prospect_outreach',

  };

}

function buildDemoProspectForJob(job = {}) {
  const title = String(job.title || 'Sourced Candidate').trim() || 'Sourced Candidate';
  const companyName = getJobCompanyName(job) || job.companyName || 'Client';
  const requirements = String(job.requirements || '').trim();
  const skills = extractSkills(`${title} ${requirements}`).slice(0, 8);
  const createdAtIso = new Date().toISOString();

  return {
    id: `demo-prospect-${job.id || Date.now()}`,
    name: 'Maya Naidoo',
    headline: `${title} candidate with ${skills.slice(0, 3).join(', ') || 'matching role'} experience`,
    role: title,
    company: companyName,
    location: job.location || 'Johannesburg',
    skills,
    notes: `${title} prospect generated for the walkthrough. Matches ${companyName} requirements: ${requirements || 'role requirements'}.`,
    rating: 92,
    matchedJobId: job.id,
    matchedJobTitle: title,
    matchedCompanyName: companyName,
    linkedInUrl: 'https://www.linkedin.com/in/demo-vision-prospect',
    source: 'Vision demo direct sourcing',
    provider: 'demo',
    createdAtIso,
    addedAtIso: createdAtIso,
  };
}

const DEFAULT_DEMO_SCREENING_QUESTIONS = [
  'Why are you a strong fit for this role?',
  'Describe a recent relevant project or achievement.',
  'What is your current notice period?',
  'What is your target compensation for this role?',
];


const DEMO_DEFAULT_PIPELINE = 'Role intake, Sourced, Screened, Shortlist, Client submitted, Interview, Offer';

function parseDemoStages(value) {
  const stages = String(value || '')
    .replace(/→/g, ',')
    .split(/[,\n]/)
    .map((stage) => stage.trim())
    .filter(Boolean);
  return stages.length ? stages.slice(0, 8) : DEMO_DEFAULT_PIPELINE.split(',').map((stage) => stage.trim());
}

function buildWorkflowDemoData(form) {
  const seats = Math.min(20, Math.max(1, Number(form.seats || 1)));
  const jobTitle = String(form.jobTitle || '').trim() || 'Senior Java Developer';
  const niche = String(form.niche || '').trim() || 'software engineering';
  const agencyName = String(form.agencyName || '').trim() || 'Your agency';
  const stages = parseDemoStages(form.pipelineStages);
  const recruiterNames = ['Aisha Patel', 'Daniel Meyer', 'Mia Jacobs', 'Noah Williams', 'Priya Naidoo', 'Liam Carter', 'Zoe Smith', 'Ethan Brown'];
  const candidateNames = ['Jordan Blake', 'Thabo Mokoena', 'Emily Chen', 'Riley Adams', 'Nadia Khan', 'Michael Dlamini', 'Sofia Martins', 'Arjun Singh', 'Grace Taylor', 'Neo Maseko', 'Hannah Wilson', 'Leo van Zyl'];
  const demoLimit = Math.min(36, Math.max(12, seats * 8));
  const candidates = candidateNames.slice(0, Math.min(candidateNames.length, demoLimit)).map((name, index) => {
    const stage = stages[Math.min(stages.length - 1, Math.floor((index / Math.max(candidateNames.length - 1, 1)) * stages.length))] || stages[0];
    return {
      id: `demo-candidate-${index}`,
      name,
      title: index % 3 === 0 ? jobTitle : `${niche.replace(/\b\w/g, (c) => c.toUpperCase())} Candidate`,
      stage,
      owner: recruiterNames[index % seats],
      score: 92 - (index * 4),
      source: ['LinkedIn', 'Referral', 'CV upload', 'Talent pool'][index % 4],
    };
  });
  const countsByStage = stages.map((stage) => ({
    stage,
    count: candidates.filter((candidate) => candidate.stage === stage).length,
  }));
  const shortlistCount = candidates.filter((candidate) => /shortlist|submit|interview|offer/i.test(candidate.stage)).length;

  return {
    agencyName,
    niche,
    jobTitle,
    seats,
    stages,
    recruiters: recruiterNames.slice(0, seats).map((name, index) => ({
      id: `demo-seat-${index}`,
      name,
      role: index === 0 ? 'Owner / lead recruiter' : 'Recruiter',
      activeCandidates: candidates.filter((candidate) => candidate.owner === name).length,
    })),
    candidates,
    countsByStage,
    shortlistCount,
    demoLimit,
    metrics: [
      { label: 'Demo seats active', value: seats },
      { label: 'Candidates in demo', value: candidates.length },
      { label: 'Client-ready shortlist', value: shortlistCount },
      { label: 'Demo record cap', value: demoLimit },
    ],
  };
}

function WorkflowDemoModal({ isOpen, onClose, onRequestLead }) {
  const [form, setForm] = useState({
    agencyName: '',
    niche: '',
    jobTitle: '',
    seats: '3',
    monthlyVolume: '',
    pipelineStages: DEMO_DEFAULT_PIPELINE,
  });
  const [activeView, setActiveView] = useState('role');
  const [selectedStage, setSelectedStage] = useState('all');
  const [actionLog, setActionLog] = useState([
    'Demo workspace created with sample data only.',
    'Role intake brief connected to candidate pipeline.',
  ]);

  const demo = useMemo(() => buildWorkflowDemoData(form), [form]);

  if (!isOpen) return null;

  const updateForm = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: name === 'seats' ? String(Math.min(20, Math.max(1, Number(value || 1)))) : value }));
  };

  const addDemoAction = (message) => {
    setActionLog((current) => [message, ...current].slice(0, 6));
  };

  const visibleCandidates = demo.candidates.filter((candidate) => selectedStage === 'all' || candidate.stage === selectedStage);

  return createPortal(
    <div className="fixed inset-0 z-[90] overflow-y-auto bg-slate-950/85 px-4 py-6 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Workflow demo">
      <div className="mx-auto max-w-7xl overflow-hidden rounded-shell border border-cyan-400/20 bg-[#070d1a] shadow-2xl">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-white/10 p-5 sm:p-6">
          <div>
            <div className="text-meta font-black uppercase tracking-[0.22em] text-cyan-300">Workflow Demo</div>
            <h2 className="mt-2 text-2xl font-black text-white sm:text-3xl">Run Vision like a {demo.seats}-seat agency workspace.</h2>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">Use your job title, niche, and stages to see the execution layer from role intake to reporting. Demo actions are local, capped, and do not write production data.</p>
          </div>
          <button type="button" onClick={onClose} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-white/10 text-slate-300 transition hover:text-white" aria-label="Close workflow demo">
            <X size={17} />
          </button>
        </div>

        <div className="grid gap-0 lg:grid-cols-[360px_1fr]">
          <aside className="border-b border-white/10 bg-slate-950/70 p-5 lg:border-b-0 lg:border-r">
            <div className="grid gap-4">
              <label className="block">
                <div className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Agency</div>
                <input name="agencyName" value={form.agencyName} onChange={updateForm} className="w-full rounded-card border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400" placeholder="e.g. Apex Tech Search" />
              </label>
              <label className="block">
                <div className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Niche</div>
                <input name="niche" value={form.niche} onChange={updateForm} className="w-full rounded-card border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400" placeholder="e.g. SaaS sales, Java engineering" />
              </label>
              <label className="block">
                <div className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Live role</div>
                <input name="jobTitle" value={form.jobTitle} onChange={updateForm} className="w-full rounded-card border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400" placeholder="e.g. Senior Account Executive" />
              </label>
              <label className="block">
                <div className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Seats to simulate</div>
                <input name="seats" type="number" min="1" max="20" value={form.seats} onChange={updateForm} className="w-full rounded-card border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400" />
                <div className="mt-2 text-xs text-slate-500">Demo limit: 1-20 seats, up to {demo.demoLimit} sample records.</div>
              </label>
              <label className="block">
                <div className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Pipeline stages</div>
                <textarea name="pipelineStages" value={form.pipelineStages} onChange={updateForm} className="min-h-[92px] w-full rounded-card border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400" placeholder="Role intake, Sourced, Screened, Shortlist, Client submitted" />
              </label>
            </div>

            <div className="mt-5 rounded-card border border-amber-300/20 bg-amber-300/10 p-4 text-xs leading-5 text-amber-100">
              Realistic demo limits: no file upload, no email send, no ATS sync, and sample candidate records only. Guided demos can use the prospect's real workflow live.
            </div>
          </aside>

          <main className="p-5 sm:p-6">
            <div className="flex flex-wrap gap-2">
              {[
                { id: 'role', label: 'Role intake' },
                { id: 'pipeline', label: 'Candidate pipeline' },
                { id: 'screening', label: 'Screening' },
                { id: 'shortlist', label: 'Shortlist' },
                { id: 'submission', label: 'Client submission' },
                { id: 'reporting', label: 'Reporting' },
              ].map((view) => (
                <button key={view.id} type="button" onClick={() => setActiveView(view.id)} className={`rounded-full px-4 py-2 text-meta font-black uppercase tracking-[0.14em] transition ${activeView === view.id ? 'bg-cyan-400 text-slate-950' : 'border border-white/10 bg-white/5 text-slate-300 hover:text-white'}`}>
                  {view.label}
                </button>
              ))}
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {demo.metrics.map((metric) => (
                <div key={metric.label} className="rounded-card border border-white/10 bg-white/[0.04] p-4">
                  <div className="text-2xl font-black text-white">{metric.value}</div>
                  <div className="mt-1 text-micro font-black uppercase tracking-[0.16em] text-slate-500">{metric.label}</div>
                </div>
              ))}
            </div>

            <section className="mt-5 rounded-shell border border-white/10 bg-slate-950/55 p-5">
              {activeView === 'role' && (
                <div className="grid gap-5 xl:grid-cols-[1fr_0.85fr]">
                  <div>
                    <div className="text-micro font-black uppercase tracking-[0.18em] text-cyan-300">Client brief</div>
                    <h3 className="mt-2 text-2xl font-black text-white">{demo.jobTitle}</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-300">{demo.agencyName} is running a {demo.niche} search with a live brief, recruiter ownership, screening criteria, and shortlist expectations connected before candidates enter the pipeline.</p>
                    <div className="mt-4 grid gap-3 sm:grid-cols-3">
                      {['Must-have criteria', 'Target companies', 'Submission SLA'].map((item) => (
                        <div key={item} className="rounded-card border border-white/10 bg-white/5 p-4 text-sm font-bold text-slate-200">{item}</div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-card border border-white/10 bg-white/5 p-4">
                    <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">Active seats</div>
                    <div className="mt-3 space-y-2">
                      {demo.recruiters.map((recruiter) => (
                        <div key={recruiter.id} className="flex items-center justify-between rounded-ctl bg-slate-950/70 px-3 py-2">
                          <div>
                            <div className="text-sm font-bold text-white">{recruiter.name}</div>
                            <div className="text-xs text-slate-500">{recruiter.role}</div>
                          </div>
                          <div className="text-xs font-black text-cyan-200">{recruiter.activeCandidates} active</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeView === 'pipeline' && (
                <div>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h3 className="text-xl font-black text-white">Pipeline built around your stages</h3>
                    <select value={selectedStage} onChange={(event) => setSelectedStage(event.target.value)} className="rounded-full border border-white/10 bg-slate-900 px-4 py-2 text-xs text-white outline-none">
                      <option value="all">All stages</option>
                      {demo.stages.map((stage) => <option key={stage} value={stage}>{stage}</option>)}
                    </select>
                  </div>
                  <div className="grid gap-3 lg:grid-cols-3">
                    {visibleCandidates.map((candidate) => (
                      <article key={candidate.id} className="rounded-card border border-white/10 bg-white/[0.04] p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="font-black text-white">{candidate.name}</div>
                            <div className="mt-1 text-xs text-slate-400">{candidate.title}</div>
                          </div>
                          <div className="rounded-full bg-cyan-400/10 px-2 py-1 text-micro font-black text-cyan-200">{candidate.score}</div>
                        </div>
                        <div className="mt-3 text-xs text-slate-500">{candidate.stage} · {candidate.owner} · {candidate.source}</div>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {activeView === 'screening' && (
                <div className="grid gap-4 lg:grid-cols-3">
                  {['Screening link prepared', 'Responses normalized', 'Recruiter review required'].map((title, index) => (
                    <button key={title} type="button" onClick={() => addDemoAction(`${title} for ${demo.jobTitle}.`)} className="rounded-card border border-white/10 bg-white/[0.04] p-5 text-left transition hover:border-cyan-400/40">
                      <div className="text-micro font-black uppercase tracking-[0.18em] text-cyan-300">Step {index + 1}</div>
                      <div className="mt-2 text-lg font-black text-white">{title}</div>
                      <p className="mt-2 text-sm leading-6 text-slate-400">Click to simulate this demo action and update the activity trail.</p>
                    </button>
                  ))}
                </div>
              )}

              {activeView === 'shortlist' && (
                <div>
                  <h3 className="text-xl font-black text-white">Client-ready shortlist</h3>
                  <div className="mt-4 grid gap-3 lg:grid-cols-3">
                    {demo.candidates.slice(0, 6).map((candidate) => (
                      <article key={candidate.id} className="rounded-card border border-emerald-300/15 bg-emerald-300/10 p-4">
                        <div className="font-black text-white">{candidate.name}</div>
                        <div className="mt-1 text-xs text-emerald-100/80">{candidate.title}</div>
                        <div className="mt-3 text-sm text-slate-300">Fit score {candidate.score}. Owner: {candidate.owner}.</div>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {activeView === 'submission' && (
                <div className="grid gap-4 lg:grid-cols-[1fr_0.9fr]">
                  <div>
                    <h3 className="text-xl font-black text-white">Submission pack</h3>
                    <p className="mt-3 text-sm leading-7 text-slate-300">Vision turns shortlist context into a client-facing submission: candidate evidence, screening notes, recruiter rationale, and next steps stay attached to the role.</p>
                    <button type="button" onClick={() => addDemoAction(`Client submission draft generated for ${demo.jobTitle}.`)} className="mt-5 rounded-full bg-cyan-400 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 hover:bg-cyan-300">Generate demo submission</button>
                  </div>
                  <div className="rounded-card border border-white/10 bg-white/5 p-4">
                    <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">Submission includes</div>
                    <ul className="mt-3 space-y-2 text-sm text-slate-300">
                      {['Role brief', 'Shortlist rationale', 'Screening evidence', 'Client feedback tracking'].map((item) => <li key={item} className="flex gap-2"><Check size={15} className="mt-0.5 text-cyan-300" />{item}</li>)}
                    </ul>
                  </div>
                </div>
              )}

              {activeView === 'reporting' && (
                <div>
                  <h3 className="text-xl font-black text-white">Speed-to-shortlist reporting</h3>
                  <div className="mt-4 grid gap-3 lg:grid-cols-3">
                    {demo.countsByStage.map((item) => (
                      <div key={item.stage} className="rounded-card border border-white/10 bg-white/[0.04] p-4">
                        <div className="text-2xl font-black text-white">{item.count}</div>
                        <div className="mt-1 text-xs text-slate-400">{item.stage}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </section>

            <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_auto]">
              <div className="rounded-card border border-white/10 bg-white/[0.04] p-4">
                <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">Demo activity</div>
                <div className="mt-3 space-y-2">
                  {actionLog.map((item, index) => <div key={`${item}-${index}`} className="text-sm text-slate-300">{item}</div>)}
                </div>
              </div>
              <div className="flex flex-col justify-center gap-3">
                <button type="button" onClick={onRequestLead} className="rounded-full bg-cyan-400 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 hover:bg-cyan-300">Request guided demo</button>
                <button type="button" onClick={() => addDemoAction('Demo reset requested. Adjust the form fields to rerun with a different workflow.')} className="rounded-full border border-white/10 bg-white/5 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-200 hover:bg-white/10">Log demo action</button>
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>,
    document.body
  );
}



function RecruiterAuthPanel({

  mode,

  form,

  error,

  isSubmitting,

  onModeChange,

  onChange,

  onSubmit,
  onUseDemo,

}) {

  const proofPoints = [

    'Private recruiter-owned pipeline',

    'Timed screening review in context',

    'Operational reporting for every role',

  ];

  const featureRows = [

    {

      title: 'Pipeline control for active desks',

      body: 'Track live vacancies, shortlisted candidates, screening links, and recruiter decisions from one operating surface.',

    },

    {

      title: 'CV intake that keeps role context',

      body: 'Import resumes in batches, match them to open roles, and keep the vacancy brief attached through review.',

    },

    {

      title: 'Screening review without spreadsheet drift',

      body: 'Send structured screening links, review answers, and move candidates forward without rebuilding the shortlist.',

    },

  ];

  const faqRows = [

    {

      question: 'What is Vision for recruitment agencies?',

      answer: 'Vision is a recruiter workspace for agencies that need pipeline control, CV intake, screening, and recruiter-owned continuity.',

    },

    {

      question: 'How does Vision speed up hiring?',

      answer: 'It keeps CV intake, shortlist movement, candidate screening, and recruiter review in one workflow so teams move from intake to decision faster.',

    },

    {

      question: 'Is Vision designed for job seekers?',

      answer: 'No. Vision is agency-first software for recruitment teams, not a public job board or candidate marketplace.',

    },

  ];

  const whyChooseRows = [

    {

      title: 'Agency-first recruitment workflow',

      body: 'Vision Team recruitment software is designed for agency teams that need structured control over client roles, candidate stages, and recruiter accountability.',

    },

    {

      title: 'Faster shortlist movement',

      body: 'Bulk CV intake, auto-linked vacancies, and stage management reduce manual admin and help recruiters move from intake to shortlist faster.',

    },

    {

      title: 'Unified screening and review',

      body: 'Screening questions, candidate responses, and recruiter notes stay inside one workspace, making decisions easier and audit trails cleaner.',

    },

    {

      title: 'Built for recruiter execution',

      body: 'From sourcing to submission, Vision Team supports the day-to-day flow recruitment agencies actually run, rather than generic HR workflows.',

    },

  ];

  const howItWorksSteps = [

    {

      title: '1. Create client roles and active vacancies',

      body: 'Set up each client role with location, department, requirements, and hiring stage flow so every recruiter works from the same structure.',

    },

    {

      title: '2. Upload CVs and map candidates to jobs',

      body: 'Import resumes in bulk and map candidates to the right vacancy so your agency can process high volume without losing context.',

    },

    {

      title: '3. Run screening and shortlist decisions',

      body: 'Share screening links, review responses, and move candidates through the pipeline based on objective recruiter review.',

    },

    {

      title: '4. Present faster and track outcomes',

      body: 'Finalize shortlists, track stage conversion, and monitor performance metrics across roles and recruiters in one dashboard.',

    },

  ];

  const productPreviewStages = [
    { label: 'Applied', count: 48, tone: 'bg-slate-500' },
    { label: 'Screening', count: 22, tone: 'bg-cyan-400' },
    { label: 'Interview', count: 11, tone: 'bg-violet-400' },
    { label: 'Offer', count: 4, tone: 'bg-emerald-400' },
  ];

  const productMetrics = [
    { label: 'CVs processed', value: '286' },
    { label: 'Shortlist ready', value: '34' },
    { label: 'Avg. screen time', value: '18m' },
  ];

  const seoPageLinks = [

    { href: '/vision-grid-recruitment-software-guide.html', label: 'Vision Team recruitment software guide' },

    { href: '/why-choose-vision-grid-recruitment.html', label: 'Why choose Vision Team recruitment' },

    { href: '/how-vision-grid-recruitment-works.html', label: 'How Vision Team recruitment works' },

    { href: '/recruitment-crm-software-for-agencies.html', label: 'Recruitment CRM software for agencies' },

    { href: '/candidate-screening-software-for-recruiters.html', label: 'Candidate screening software for recruiters' },

    { href: '/recruitment-automation-software-for-agencies.html', label: 'Recruitment automation software for agencies' },

  ];

  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [isWorkflowDemoOpen, setIsWorkflowDemoOpen] = useState(false);
  const [isDemoChoiceOpen, setIsDemoChoiceOpen] = useState(false);

  const [isLeadSubmitting, setIsLeadSubmitting] = useState(false);

  const [leadNotice, setLeadNotice] = useState('');

  const [leadForm, setLeadForm] = useState({
    fullName: '',
    workEmail: '',
    agencyName: '',
    teamSize: '',
    monthlyVolume: '',
    message: '',
    website: '',
  });

  const demoInstructionChoices = [
    {
      id: 'direct',
      title: 'Use Vision to get new jobs and candidates directly',
      body: 'Start with the demo as if Vision is bringing new listings and candidate opportunities into the workspace for your recruiters to qualify.',
      instructions: [
        'Review the live role and candidate pipeline Vision creates.',
        'Use matching, screening, shortlist, and reporting views to see how new work is handled.',
      ],
      icon: Sparkles,
    },
    {
      id: 'import',
      title: 'Upload your jobs and candidates into Vision',
      body: 'Start with the demo as if you already receive jobs and applicants from your website, inbox, ATS, job boards, or spreadsheets.',
      instructions: [
        'Use the sample upload and intake flow as the model for your existing applicant sources.',
        'Map imported candidates to roles, then continue through screening and shortlist review.',
      ],
      icon: Upload,
    },
  ];

  const trackLeadEvent = (eventName, payload = {}) => {
    try {
      if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
        window.gtag('event', eventName, payload);
      }
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: eventName, ...payload });
      }
    } catch (_) {
      // Ignore analytics errors so lead submission still works.
    }
  };

  const openLeadModal = () => {
    setLeadNotice('');
    setIsLeadModalOpen(true);
    trackLeadEvent('vision_lead_modal_open', { page: 'homepage' });
  };

  const openDemoChoice = () => {
    setIsDemoChoiceOpen(true);
    trackLeadEvent('vision_demo_choice_open', { page: 'homepage' });
  };

  const closeDemoChoice = () => {
    setIsDemoChoiceOpen(false);
  };

  const startDemoFromChoice = (choiceId) => {
    setIsDemoChoiceOpen(false);
    trackLeadEvent('vision_demo_choice_select', { page: 'homepage', demo_path: choiceId });
    onUseDemo?.(choiceId);
  };

  const openWorkflowDemo = () => {
    setIsWorkflowDemoOpen(true);
    trackLeadEvent('vision_workflow_demo_open', { page: 'homepage' });
  };

  const closeWorkflowDemo = () => {
    setIsWorkflowDemoOpen(false);
  };

  const openGuidedDemoFromWorkflow = () => {
    setIsWorkflowDemoOpen(false);
    openLeadModal();
  };

  const closeLeadModal = () => {
    setIsLeadModalOpen(false);
  };

  const onLeadFormChange = (event) => {
    const { name, value } = event.target;
    setLeadForm((previous) => ({ ...previous, [name]: value }));
  };

  const onLeadFormSubmit = async (event) => {
    event.preventDefault();
    setLeadNotice('');
    if (isLeadSubmitting) return;

    try {
      setIsLeadSubmitting(true);
      const submitWebsiteLead = httpsCallable(getFunctions(app), 'submitWebsiteLead');
      const payload = {
        fullName: leadForm.fullName,
        workEmail: leadForm.workEmail,
        agencyName: leadForm.agencyName,
        teamSize: leadForm.teamSize,
        monthlyVolume: leadForm.monthlyVolume,
        message: leadForm.message,
        website: leadForm.website,
        sourcePage: typeof window !== 'undefined' ? window.location.pathname : '/',
      };

      await submitWebsiteLead(payload);

      trackLeadEvent('vision_lead_submit', {
        page: 'homepage',
        agency_name: leadForm.agencyName || 'unknown',
        team_size: leadForm.teamSize || 'unknown',
      });

      setLeadNotice('Thanks. Your request was received and the team will contact you shortly.');
      setLeadForm({
        fullName: '',
        workEmail: '',
        agencyName: '',
        teamSize: '',
        monthlyVolume: '',
        message: '',
        website: '',
      });
      setTimeout(() => setIsLeadModalOpen(false), 800);
    } catch (error) {
      setLeadNotice(error?.message || 'We could not submit your request right now. Please try again.');
    } finally {
      setIsLeadSubmitting(false);
    }
  };



  return (

    <div className="min-h-screen bg-[#050914] px-4 py-6 text-slate-100 sm:px-6 sm:py-8">

      <div className="mx-auto max-w-6xl space-y-5">

        <section className="overflow-hidden rounded-shell border border-white/10 bg-[#0a1020] shadow-2xl shadow-black/30 lg:grid lg:grid-cols-[1.08fr_0.92fr]">

          <div className="p-6 sm:p-8 lg:p-10">

            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-400/20 bg-cyan-400/10 px-3 py-1 text-meta font-bold uppercase tracking-[0.16em] text-cyan-200">
              <ShieldCheck size={14} /> Agency operating system
            </div>

            <h1 className="mt-5 max-w-3xl text-3xl font-black leading-tight text-white sm:text-5xl">Move CVs from intake to shortlist in one recruiter workspace.</h1>

            <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">

              Vision Team gives recruitment agencies a private workspace for live roles, candidate pipelines, screening links, and recruiter-owned decisions. It is built for teams that need control, speed, and a cleaner audit trail.

            </p>

            <div className="mt-7 grid gap-3 sm:grid-cols-3">

              {proofPoints.map((item) => (

                <div key={item} className="rounded-card border border-white/10 bg-white/[0.04] p-4 text-sm leading-6 text-slate-300">

                  {item}

                </div>

              ))}

            </div>

            <a
              href="https://youtu.be/KYWP-F92DyA"
              target="_blank"
              rel="noopener noreferrer"
              className="group mt-6 grid overflow-hidden rounded-card border border-white/10 bg-white/[0.04] transition hover:border-cyan-300/50 hover:bg-cyan-500/10 sm:grid-cols-[220px_1fr]"
              aria-label="Watch the Vision demo walkthrough on YouTube"
            >
              <div className="relative aspect-video overflow-hidden bg-slate-950 sm:aspect-auto">
                <img
                  src="https://img.youtube.com/vi/KYWP-F92DyA/hqdefault.jpg"
                  alt="Vision demo walkthrough video thumbnail"
                  className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-slate-950/20">
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-cyan-400 text-slate-950 shadow-lg shadow-cyan-950/40 transition group-hover:bg-cyan-300">
                    <PlayCircle size={26} />
                  </span>
                </div>
              </div>
              <div className="flex min-w-0 flex-col justify-center p-4">
                <div className="flex items-center gap-2 text-micro font-black uppercase tracking-[0.18em] text-cyan-300">
                  Demo walkthrough <ExternalLink size={13} />
                </div>
                <div className="mt-2 text-base font-black text-white">Watch Vision handle screening, shortlisting, and follow-up.</div>
                <p className="mt-2 text-sm leading-6 text-slate-300">See the recruiter workflow before opening the interactive demo.</p>
              </div>
            </a>

            <div className="mt-8 flex flex-wrap gap-3 text-meta font-bold">

              <button type="button" onClick={openDemoChoice} className="rounded-full bg-cyan-400 px-5 py-3 text-slate-950 shadow-lg shadow-cyan-950/40 transition hover:bg-cyan-300">Use demo</button>

              <a href="/vision-grid-recruitment-software-guide.html" className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-slate-200 transition hover:border-white/20 hover:text-white">View product guide</a>

              <a href="/recruiter-pipeline-software.html" className="rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-slate-200 transition hover:border-white/20 hover:text-white">Pipeline software</a>

            </div>

          </div>



          <div className="border-t border-white/10 bg-[#0f172a] p-5 sm:p-6 lg:border-l lg:border-t-0">

            <div className="mb-5 rounded-card border border-white/10 bg-slate-950/70 p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-meta font-bold uppercase tracking-[0.16em] text-slate-500">Live workspace preview</div>
                  <div className="mt-2 text-base font-black text-white">Senior Java Developer</div>
                  <div className="mt-1 text-xs text-slate-400">Active client role · Johannesburg</div>
                </div>
                <div className="rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-meta font-bold text-emerald-200">Active</div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                {productPreviewStages.map((stage) => (
                  <div key={stage.label} className="rounded-ctl border border-white/10 bg-white/[0.03] p-3">
                    <div className={`h-1.5 w-8 rounded-full ${stage.tone}`} />
                    <div className="mt-3 text-lg font-black text-white">{stage.count}</div>
                    <div className="mt-1 text-micro font-bold uppercase tracking-[0.1em] text-slate-500">{stage.label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 grid gap-2 sm:grid-cols-3">
                {productMetrics.map((metric) => (
                  <div key={metric.label} className="rounded-ctl bg-black/20 px-3 py-2">
                    <div className="text-sm font-black text-white">{metric.value}</div>
                    <div className="text-micro text-slate-500">{metric.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex rounded-full border border-white/10 bg-slate-950/60 p-1">

              {[

                { id: 'signin', label: 'Sign in' },

                { id: 'signup', label: 'Create account' },

              ].map((item) => (

                <button

                  key={item.id}

                  type="button"

                  onClick={() => onModeChange(item.id)}

                  className={`flex-1 rounded-full px-4 py-2 text-xs font-bold ${mode === item.id ? 'bg-cyan-400 text-slate-950' : 'text-slate-400'}`}

                >

                  {item.label}

                </button>

              ))}

            </div>



            <form className="mt-6 space-y-4" onSubmit={onSubmit} autoComplete="off">

              <label className="block">

                <div className="mb-2 text-xs font-bold text-slate-300">Agency</div>

                <input name="agency" value={form.agency} onChange={onChange} autoComplete="organization" className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Your agency name" required />

              </label>

              <label className="block">

                <div className="mb-2 text-xs font-bold text-slate-300">Agency access password</div>

                <input name="agencySecret" type="password" value={form.agencySecret} onChange={onChange} autoComplete="new-password" className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Enter your agency access password" required />

                <div className="mt-2 text-xs leading-5 text-slate-500">Your agency owner provides this shared access password. It keeps the workspace tied to the right agency.</div>

              </label>



              {mode === 'signup' && (

                <>

                  <label className="block">

                    <div className="mb-2 text-xs font-bold text-slate-300">Recruiter name</div>

                    <input name="name" value={form.name} onChange={onChange} autoComplete="name" className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Full name" required />

                  </label>

                  <label className="block">

                    <div className="mb-2 text-xs font-bold text-slate-300">Account role</div>

                    <select name="accountRole" value={form.accountRole} onChange={onChange} className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" required>

                      <option value="owner">Agency owner</option>

                      <option value="agent">Agency agent</option>

                    </select>

                    <div className="mt-2 text-xs leading-5 text-slate-500">Agency owners will manage membership and agency settings. Agents operate under the owner.</div>

                  </label>

                  <label className="block">

                    <div className="mb-2 text-xs font-bold text-slate-300">Coverage location</div>

                    <input name="location" value={form.location} onChange={onChange} autoComplete="off" className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Johannesburg, South Africa" required />

                  </label>

                </>

              )}



              <label className="block">

                <div className="mb-2 text-xs font-bold text-slate-300">Email</div>

                <input name="email" type="email" value={form.email} onChange={onChange} className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="you@agency.com" required />

              </label>

              <label className="block">

                <div className="mb-2 text-xs font-bold text-slate-300">Password</div>

                <input name="password" type="password" value={form.password} onChange={onChange} autoComplete={mode === 'signup' ? 'new-password' : 'off'} className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Enter password" required minLength={6} />

              </label>

              {mode === 'signup' && (
                <label className="flex gap-3 rounded-card border border-white/10 bg-slate-950/50 px-4 py-3 text-xs leading-5 text-slate-300">
                  <input name="lawfulUseAccepted" type="checkbox" checked={Boolean(form.lawfulUseAccepted)} onChange={onChange} className="mt-1 h-4 w-4 accent-cyan-400" required />
                  <span>I confirm this agency will use Vision only for lawful recruitment activity, with a lawful basis or consent for personal information, no unlawful discrimination, no spam, and no unauthorized data sharing.</span>
                </label>
              )}



              {error && (

                <div className="rounded-card border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">

                  {error}

                </div>

              )}



              <button type="submit" disabled={isSubmitting} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-4 py-3 text-xs font-black text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70">

                {isSubmitting && <Loader size={14} className="animate-spin" />}

                {mode === 'signup' ? 'Create recruiter account' : 'Sign in to workspace'}

              </button>

            </form>

          </div>

        </section>



        <section className="grid gap-5 lg:grid-cols-3">

          {featureRows.map((item) => (

            <article key={item.title} className="rounded-shell border border-white/10 bg-white/5 p-6">

              <h2 className="text-lg font-black text-white">{item.title}</h2>

              <p className="mt-3 text-sm leading-7 text-slate-300">{item.body}</p>

            </article>

          ))}

        </section>



        <section id="about" className="rounded-shell border border-white/10 bg-white/5 p-6 sm:p-8">

          <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">About Vision Team</div>

          <div className="mt-4 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">

            <div>

              <h2 className="text-2xl font-black text-white sm:text-3xl">Built for recruiter execution, not generic HR workflows.</h2>

              <p className="mt-4 text-sm leading-7 text-slate-300">

                Vision Team is a recruiter-first operating system for agencies that run fast-moving candidate pipelines.

                We focus on the real operational flow: bulk CV intake, structured stage movement, screening delivery,

                and accountable recruiter decision-making.

              </p>

              <p className="mt-4 text-sm leading-7 text-slate-300">

                Teams adopt Vision Team when they need tighter pipeline control, cleaner shortlist handoffs,

                and one workspace that remains stable across devices and sessions.

              </p>

            </div>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1">

              <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Focus</div>

                <div className="mt-2 text-sm font-bold text-white">Agency recruiting operations</div>

              </div>

              <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Outcome</div>

                <div className="mt-2 text-sm font-bold text-white">Faster CV-to-shortlist execution</div>

              </div>

              <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Core stack</div>

                <div className="mt-2 text-sm font-bold text-white">Pipeline, screening, recruiter workspace</div>

              </div>

            </div>

          </div>

        </section>



        <section id="pricing" className="rounded-shell border border-white/10 bg-white/5 p-6 sm:p-8">

          <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Pricing</div>

          <h2 className="mt-4 text-2xl font-black text-white sm:text-3xl">Start lean, look sharper, and grow into the agency you want to become.</h2>
          <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300">
            Vision is priced to help small recruitment teams feel organized from day one, then graduate into a bigger operating system as email sync, job-board distribution, and enterprise governance come online.
          </p>

          <div className="mt-6 grid gap-4 lg:grid-cols-4">

            <article className="relative overflow-hidden rounded-card border border-emerald-300/30 bg-[radial-gradient(circle_at_18%_0%,rgba(52,211,153,.2),transparent_34%),rgba(6,78,59,.13)] p-5 shadow-[0_22px_60px_rgba(16,185,129,.1)]">
              <div className="absolute right-4 top-4 rounded-full border border-emerald-300/25 bg-emerald-300/10 px-3 py-1 text-micro font-black uppercase tracking-[0.16em] text-emerald-200">Starter</div>

              <div className="text-micro font-black uppercase tracking-[0.22em] text-emerald-200">For one recruiter</div>
              <h3 className="mt-3 pr-20 text-xl font-black text-white">Solo Recruiter</h3>

              <div className="mt-4 text-3xl font-black text-white">USD $25<span className="text-base text-emerald-100"> / month</span></div>
              <p className="mt-3 text-sm leading-7 text-emerald-50/90">A low-risk way to run your desk professionally: jobs, CVs, candidates, clients, and follow-up in one focused workspace.</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-100">
                {['One recruiter workspace', 'AI CV intake, parsing, and screening support', 'Candidate pipeline, companies, contacts, and placements', 'Public career page and candidate application flow', 'Revenue tracking and CSV handoff for bookkeeping'].map((feature) => (
                  <li key={feature} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-emerald-200" /> <span>{feature}</span></li>
                ))}
              </ul>
              <div className="mt-4 border-t border-emerald-300/10 pt-3">
                <p className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-emerald-200/50">Operating limits</p>
                <div className="grid grid-cols-2 gap-1">
                  {[['Candidates', FLAGSHIP_OPERATING_LIMITS.starter.candidates], ['Jobs', FLAGSHIP_OPERATING_LIMITS.starter.jobs], ['Clients', FLAGSHIP_OPERATING_LIMITS.starter.clients], ['Contacts', FLAGSHIP_OPERATING_LIMITS.starter.contacts]].map(([label, n]) => (
                    <div key={label} className="flex items-center justify-between rounded bg-emerald-300/5 px-2 py-1">
                      <span className="text-micro text-emerald-100/50">{label}</span>
                      <span className="text-micro font-black text-emerald-200">{fmtLimit(n)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button type="button" onClick={openLeadModal} className="mt-6 w-full rounded-full bg-emerald-300 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-emerald-200">Start solo</button>

            </article>

            <article className="relative overflow-hidden rounded-card border border-cyan-400/30 bg-[radial-gradient(circle_at_20%_0%,rgba(103,232,249,.22),transparent_34%),rgba(8,145,178,.1)] p-5 shadow-[0_22px_60px_rgba(8,145,178,.12)]">
              <div className="absolute right-4 top-4 rounded-full border border-emerald-300/25 bg-emerald-300/10 px-3 py-1 text-micro font-black uppercase tracking-[0.16em] text-emerald-200">Live now</div>

              <div className="text-micro font-black uppercase tracking-[0.22em] text-cyan-200">For 2-20 recruiters</div>
              <h3 className="mt-3 text-xl font-black text-white">Small Agency Launch</h3>

              <div className="mt-4 text-3xl font-black text-white">USD $60<span className="text-base text-cyan-100"> / user / month</span></div>
              <p className="mt-3 text-sm leading-7 text-cyan-50/90">Walk into every client conversation with a cleaner pipeline, smarter candidate evidence, and a workspace that makes your agency feel more established.</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-100">
                {['AI CV intake, parsing, screening, and candidate scoring', 'Recruiter pipeline, job portfolio, companies, contacts, and placements', 'Client intake links, public career pages, candidate role-watch alerts', 'Revenue tracking, invoice-ready placement records, CSV exports', 'Team roles for owner, admin, recruiter, sales, finance, and limited users'].map((feature) => (
                  <li key={feature} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-cyan-200" /> <span>{feature}</span></li>
                ))}
              </ul>
              <div className="mt-4 border-t border-cyan-300/10 pt-3">
                <p className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-cyan-200/50">Operating limits</p>
                <div className="grid grid-cols-2 gap-1">
                  {[['Candidates', FLAGSHIP_OPERATING_LIMITS.launch.candidates], ['Jobs', FLAGSHIP_OPERATING_LIMITS.launch.jobs], ['Clients', FLAGSHIP_OPERATING_LIMITS.launch.clients], ['Contacts', FLAGSHIP_OPERATING_LIMITS.launch.contacts]].map(([label, n]) => (
                    <div key={label} className="flex items-center justify-between rounded bg-cyan-300/5 px-2 py-1">
                      <span className="text-micro text-cyan-100/50">{label}</span>
                      <span className="text-micro font-black text-cyan-200">{fmtLimit(n)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button type="button" onClick={openLeadModal} className="mt-6 w-full rounded-full bg-cyan-300 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-200">Build my agency workspace</button>

            </article>

            <article className="relative overflow-hidden rounded-card border border-white/10 bg-[radial-gradient(circle_at_80%_0%,rgba(34,211,238,.18),transparent_34%),rgba(15,23,42,.72)] p-5">
              <div className="absolute right-4 top-4 rounded-full border border-amber-300/25 bg-amber-300/10 px-3 py-1 text-micro font-black uppercase tracking-[0.16em] text-amber-200">Next wave</div>

              <div className="text-micro font-black uppercase tracking-[0.22em] text-slate-500">For bigger agencies</div>
              <h3 className="mt-3 pr-24 text-xl font-black text-white">Agency Scale</h3>

              <div className="mt-4 text-2xl font-black text-white">Custom quote</div>
              <p className="mt-3 text-sm leading-7 text-slate-300">For agencies preparing to move from organized execution into a sales-and-recruiting engine with email history and job distribution built in.</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                {['Everything in Small Agency Launch', 'Gmail and Outlook sync for email logging, replies, and client history', 'Automated job board posting and application return flow', 'Advanced team governance, sales ownership, and recruiter performance controls', 'Higher-volume candidate, job, and client operating limits'].map((feature) => (
                  <li key={feature} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-slate-400" /> <span>{feature}</span></li>
                ))}
              </ul>
              <div className="mt-4 border-t border-white/10 pt-3">
                <p className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-slate-400/70">Operating limits</p>
                <div className="grid grid-cols-2 gap-1">
                  {[['Candidates', FLAGSHIP_OPERATING_LIMITS.scale.candidates], ['Jobs', FLAGSHIP_OPERATING_LIMITS.scale.jobs], ['Clients', FLAGSHIP_OPERATING_LIMITS.scale.clients], ['Contacts', FLAGSHIP_OPERATING_LIMITS.scale.contacts]].map(([label, n]) => (
                    <div key={label} className="flex items-center justify-between rounded bg-white/5 px-2 py-1">
                      <span className="text-micro text-slate-400/60">{label}</span>
                      <span className="text-micro font-black text-slate-300">{fmtLimit(n)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button type="button" onClick={openLeadModal} className="mt-6 w-full rounded-full border border-cyan-300/25 bg-cyan-300/10 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-cyan-100 transition hover:border-cyan-300/50 hover:bg-cyan-300/15">Reserve scale access</button>

            </article>

            <article className="relative overflow-hidden rounded-card border border-white/10 bg-[radial-gradient(circle_at_50%_0%,rgba(148,163,184,.18),transparent_34%),rgba(2,6,23,.78)] p-5">
              <div className="absolute right-4 top-4 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-micro font-black uppercase tracking-[0.16em] text-slate-300">White glove</div>

              <div className="text-micro font-black uppercase tracking-[0.22em] text-slate-500">For enterprise agencies</div>
              <h3 className="mt-3 text-xl font-black text-white">Enterprise Operating System</h3>

              <div className="mt-4 text-2xl font-black text-white">Custom implementation</div>
              <p className="mt-3 text-sm leading-7 text-slate-300">For agencies that want to move years of client, candidate, and revenue history into a calmer, cleaner operating system with guided rollout.</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-300">
                {['Everything in Agency Scale', 'Migration command center for Bullhorn, Recruitly, Manatal, Breezy, and CSV exports', 'Enterprise RBAC, audit visibility, finance controls, and data export governance', 'Dedicated onboarding, data cleanup, rollout support, and success planning', 'Future-ready integration path for email, job boards, reporting, and account workflows'].map((feature) => (
                  <li key={feature} className="flex gap-2"><Check size={15} className="mt-0.5 shrink-0 text-slate-400" /> <span>{feature}</span></li>
                ))}
              </ul>
              <div className="mt-4 border-t border-white/10 pt-3">
                <p className="mb-2 text-micro font-black uppercase tracking-[0.18em] text-slate-400/70">Operating limits</p>
                <div className="grid grid-cols-2 gap-1">
                  {[['Candidates', FLAGSHIP_OPERATING_LIMITS.flagship.candidates], ['Jobs', FLAGSHIP_OPERATING_LIMITS.flagship.jobs], ['Clients', FLAGSHIP_OPERATING_LIMITS.flagship.clients], ['Contacts', FLAGSHIP_OPERATING_LIMITS.flagship.contacts]].map(([label, n]) => (
                    <div key={label} className="flex items-center justify-between rounded bg-white/5 px-2 py-1">
                      <span className="text-micro text-slate-400/60">{label}</span>
                      <span className="text-micro font-black text-slate-300">{fmtLimit(n)}</span>
                    </div>
                  ))}
                </div>
              </div>
              <button type="button" onClick={openLeadModal} className="mt-6 w-full rounded-full border border-white/15 bg-white/5 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-200 transition hover:border-cyan-300/40 hover:text-white">Plan the rollout</button>

            </article>

          </div>

          <div className="mt-5 text-sm text-slate-400">

            Not sure where you fit?{' '}
            <button type="button" onClick={openLeadModal} className="font-bold text-cyan-200 hover:text-cyan-100">
              Tell us your agency size
            </button>
            , and we will point you to the cleanest starting path.

          </div>

        </section>



        <section id="contact" className="rounded-shell border border-white/10 bg-white/5 p-6 sm:p-8">

          <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Contact</div>

          <h2 className="mt-4 text-2xl font-black text-white sm:text-3xl">Talk to the Vision Team.</h2>

          <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300">

            Share your agency size, typical monthly candidate volume, and current bottlenecks.

            We will map a rollout path for your pipeline, screening, and recruiter workspace.

          </p>

          <div className="mt-6 flex flex-wrap gap-3 text-xs font-black uppercase tracking-[0.16em]">

            <button type="button" onClick={openDemoChoice} className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-cyan-200 hover:bg-cyan-500/20">Use demo</button>

            <a href="/" className="rounded-full border border-white/20 bg-white/5 px-4 py-3 text-slate-200 hover:bg-white/10">Back to top</a>

          </div>

        </section>



        <section className="rounded-shell border border-white/10 bg-white/5 p-6 sm:p-8">

          <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Why Agencies Use Vision</div>

          <div className="mt-4 grid gap-5 lg:grid-cols-[1fr_1fr]">

            <div>

              <h2 className="text-2xl font-black text-white">A recruiter-first operating system for agency hiring.</h2>

          <section className="rounded-shell border border-white/10 bg-white/5 p-6 sm:p-8">

            <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Why Choose Vision Team Recruitment?</div>

            <h2 className="mt-4 text-2xl font-black text-white sm:text-3xl">Why recruitment agencies choose Vision Team</h2>

            <p className="mt-4 text-sm leading-7 text-slate-300">

              Vision Team recruitment gives agencies a practical system for handling candidate volume, pipeline progression, recruiter collaboration, and screening outcomes in one place.

            </p>

            <div className="mt-6 grid gap-4 md:grid-cols-2">

              {whyChooseRows.map((item) => (

                <article key={item.title} className="rounded-card border border-white/10 bg-slate-950/60 p-5">

                  <h3 className="text-base font-black text-white">{item.title}</h3>

                  <p className="mt-3 text-sm leading-7 text-slate-300">{item.body}</p>

                </article>

              ))}

            </div>

          </section>

          <section className="rounded-shell border border-white/10 bg-white/5 p-6 sm:p-8">

            <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">How It Works</div>

            <h2 className="mt-4 text-2xl font-black text-white sm:text-3xl">How Vision Team recruitment works for agencies</h2>

            <div className="mt-6 space-y-4">

              {howItWorksSteps.map((item) => (

                <article key={item.title} className="rounded-card border border-white/10 bg-slate-950/60 p-5">

                  <h3 className="text-base font-black text-white">{item.title}</h3>

                  <p className="mt-3 text-sm leading-7 text-slate-300">{item.body}</p>

                </article>

              ))}

            </div>

            <div className="mt-6 flex flex-wrap gap-3 text-meta font-black uppercase tracking-[0.16em] text-cyan-100">

              {seoPageLinks.map((item) => (

                <a key={item.href} href={item.href} className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-4 py-3">

                  {item.label}

                </a>

              ))}

            </div>

          </section>

              <p className="mt-4 text-sm leading-7 text-slate-300">

                Vision is built for agencies that need more control over candidate intake, faster shortlisting, and a cleaner recruiter workflow. Instead of acting like a public job board, the platform is positioned as internal agency software for recruiter productivity.

              </p>

              <p className="mt-4 text-sm leading-7 text-slate-300">

                Agencies can upload CVs in bulk, correlate candidates with active roles, launch screening links, and keep recruiter responses stored under the recruiter account so the workspace survives reloads, tab switches, and device changes.

              </p>

            </div>

            <div className="grid gap-3 sm:grid-cols-2">

              <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Category</div>

                <div className="mt-2 text-lg font-bold text-white">Recruitment agency software</div>

              </div>

              <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Core motion</div>

                <div className="mt-2 text-lg font-bold text-white">CV intake to screening</div>

              </div>

              <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Primary user</div>

                <div className="mt-2 text-lg font-bold text-white">Recruitment agencies</div>

              </div>

              <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Value</div>

                <div className="mt-2 text-lg font-bold text-white">Faster recruiter execution</div>

              </div>

            </div>

          </div>

        </section>



        <section className="rounded-shell border border-white/10 bg-white/5 p-6 sm:p-8">

          <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Frequently Asked Questions</div>

          <div className="mt-5 space-y-4">

            {faqRows.map((item) => (

              <article key={item.question} className="rounded-card border border-white/10 bg-slate-950/60 p-5">

                <h3 className="text-base font-black text-white">{item.question}</h3>

                <p className="mt-3 text-sm leading-7 text-slate-300">{item.answer}</p>

              </article>

            ))}

          </div>

        </section>

        <section className="overflow-hidden rounded-shell border border-cyan-400/15 bg-[radial-gradient(circle_at_20%_0%,rgba(34,211,238,.16),transparent_34%),rgba(15,23,42,.74)]">
          <div className="grid gap-0 lg:grid-cols-[0.92fr_1.08fr]">
            <div className="min-h-[320px] bg-slate-950">
              <img
                src="/images/kendall-seadras-owner.png"
                alt="Kendall Seadras, owner and creator of Vision Team Recruitment"
                className="h-full max-h-[620px] w-full object-cover object-[50%_18%]"
                loading="lazy"
              />
            </div>
            <div className="flex flex-col justify-center p-6 sm:p-8 lg:p-10">
              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Owner and Creator</div>
              <h2 className="mt-4 text-3xl font-black leading-tight text-white sm:text-4xl">Kendall Seadras</h2>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300 sm:text-base">
                Kendall Seadras is the owner and creator of Vision Team Recruitment. While pursuing a computer science degree, he is building Vision with a clear focus on practical, reliable software for recruitment agencies. His long-term commitment is to help recruiters work with more structure, speed, and confidence.
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                  <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Focus</div>
                  <div className="mt-2 text-sm font-bold text-white">Recruitment technology</div>
                </div>
                <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                  <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Contact</div>
                  <a href="tel:+27704367474" className="mt-2 block text-sm font-bold text-cyan-200 hover:text-cyan-100">+27 70 436 7474</a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Site footer */}
        <footer className="rounded-shell border border-white/10 bg-slate-950/80 px-6 py-10 sm:px-10">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">

            {/* Quick links */}
            <div>
              <div className="mb-4 text-meta font-black uppercase tracking-[0.22em] text-cyan-300">Quick Links</div>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/" className="hover:text-white transition-colors">Home</a></li>
                <li><a href="#about" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#contact" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>

            {/* SEO links */}
            <div>
              <div className="mb-4 text-meta font-black uppercase tracking-[0.22em] text-cyan-300">SEO Resource Hub</div>
              <ul className="space-y-3 text-sm text-slate-400">
                {seoPageLinks.map((item) => (
                  <li key={item.href}>
                    <a href={item.href} className="group block rounded-ctl border border-white/10 bg-white/5 px-3 py-2 transition hover:border-cyan-400/40 hover:bg-cyan-500/10">
                      <span className="text-slate-200 group-hover:text-white">{item.label}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <div className="mb-4 text-meta font-black uppercase tracking-[0.22em] text-cyan-300">Use Case Pages</div>
              <ul className="space-y-3 text-sm text-slate-400">
                <li><a href="/recruitment-agency-software.html" className="group block rounded-ctl border border-white/10 bg-white/5 px-3 py-2 transition hover:border-cyan-400/40 hover:bg-cyan-500/10"><span className="text-slate-200 group-hover:text-white">Recruitment agency software</span></a></li>
                <li><a href="/recruiter-pipeline-software.html" className="group block rounded-ctl border border-white/10 bg-white/5 px-3 py-2 transition hover:border-cyan-400/40 hover:bg-cyan-500/10"><span className="text-slate-200 group-hover:text-white">Recruiter pipeline software</span></a></li>
                <li><a href="/cv-intake-screening-software.html" className="group block rounded-ctl border border-white/10 bg-white/5 px-3 py-2 transition hover:border-cyan-400/40 hover:bg-cyan-500/10"><span className="text-slate-200 group-hover:text-white">CV intake and screening software</span></a></li>
              </ul>
            </div>

            {/* Compliance */}
            <div>
              <div className="mb-4 text-meta font-black uppercase tracking-[0.22em] text-cyan-300">Compliance</div>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="/application-privacy-policy.html" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="/application-terms-of-service.html" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>

          </div>

          <div className="mt-8 border-t border-white/10 pt-6 text-center text-xs text-slate-600">
            &copy; 2026 Vision Team Recruitment. All rights reserved.
          </div>
        </footer>

        {isLeadModalOpen && (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/80 px-4 py-8 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Lead form modal">
            <div className="relative w-full max-w-2xl rounded-shell border border-white/10 bg-slate-950 p-6 shadow-2xl sm:p-8">
              <button
                type="button"
                onClick={closeLeadModal}
                className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-slate-300 transition hover:text-white"
                aria-label="Close lead form"
              >
                <X size={16} />
              </button>

              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Contact Sales</div>
              <h3 className="mt-3 text-2xl font-black text-white sm:text-3xl">Request a Vision Team demo</h3>
              <p className="mt-3 text-sm leading-7 text-slate-300">Tell us about your agency and hiring volume. We will follow up with a rollout path tailored to your team.</p>

              <form className="mt-6 grid gap-4 sm:grid-cols-2" onSubmit={onLeadFormSubmit}>
                <label className="block sm:col-span-1">
                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Full name</div>
                  <input name="fullName" value={leadForm.fullName} onChange={onLeadFormChange} className="w-full rounded-card border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Full name" required />
                </label>

                <label className="block sm:col-span-1">
                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Work email</div>
                  <input name="workEmail" type="email" value={leadForm.workEmail} onChange={onLeadFormChange} className="w-full rounded-card border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="you@agency.com" required />
                </label>

                <label className="block sm:col-span-1">
                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Agency name</div>
                  <input name="agencyName" value={leadForm.agencyName} onChange={onLeadFormChange} className="w-full rounded-card border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Your agency" required />
                </label>

                <label className="block sm:col-span-1">
                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Recruiter team size</div>
                  <input name="teamSize" type="number" min="1" max="250" value={leadForm.teamSize} onChange={onLeadFormChange} className="w-full rounded-card border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="e.g. 8" required />
                </label>

                <label className="block sm:col-span-2">
                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Monthly candidate volume</div>
                  <input name="monthlyVolume" value={leadForm.monthlyVolume} onChange={onLeadFormChange} className="w-full rounded-card border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="e.g. 350 CVs/month" required />
                </label>

                <label className="block sm:col-span-2">
                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">What are you solving?</div>
                  <textarea name="message" value={leadForm.message} onChange={onLeadFormChange} className="min-h-[110px] w-full rounded-card border border-white/10 bg-slate-900/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Share your current bottlenecks in pipeline, screening, or shortlist execution." required />
                </label>

                <input name="website" value={leadForm.website} onChange={onLeadFormChange} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                {leadNotice && (
                  <div className="sm:col-span-2 rounded-card border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">
                    {leadNotice}
                  </div>
                )}

                <div className="sm:col-span-2 flex flex-wrap items-center gap-3">
                  <button type="submit" disabled={isLeadSubmitting} className="inline-flex items-center justify-center rounded-full bg-cyan-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-70">
                    {isLeadSubmitting ? 'Submitting...' : 'Submit lead request'}
                  </button>
                  <button type="button" onClick={closeLeadModal} className="inline-flex items-center justify-center rounded-full border border-white/20 bg-white/5 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-200 transition hover:bg-white/10">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isDemoChoiceOpen && (
          <div className="fixed inset-0 z-[85] overflow-y-auto bg-slate-950/85 px-4 py-6 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Choose demo instructions">
            <div className="mx-auto max-w-4xl rounded-shell border border-white/10 bg-[#0a1020] p-5 shadow-2xl shadow-black/50 sm:p-7">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-meta font-black uppercase tracking-[0.22em] text-cyan-300">Before you use the demo</div>
                  <h2 className="mt-2 text-2xl font-black text-white sm:text-3xl">Choose how Vision should be explained.</h2>
                  <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300">
                    Pick the route that matches how your agency wants to bring jobs and candidates into Vision. The demo opens with sample data after you choose.
                  </p>
                </div>
                <button type="button" onClick={closeDemoChoice} className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10 text-slate-300 transition hover:text-white" aria-label="Close demo instructions">
                  <X size={18} />
                </button>
              </div>

              <div className="mt-6 grid gap-4 md:grid-cols-2">
                {demoInstructionChoices.map((choice) => {
                  const ChoiceIcon = choice.icon;
                  return (
                    <article key={choice.id} className="rounded-card border border-white/10 bg-white/[0.04] p-5">
                      <div className="flex items-start gap-3">
                        <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-card border border-cyan-300/25 bg-cyan-300/10 text-cyan-200">
                          <ChoiceIcon size={20} />
                        </div>
                        <div>
                          <h3 className="text-base font-black text-white">{choice.title}</h3>
                          <p className="mt-2 text-sm leading-6 text-slate-300">{choice.body}</p>
                        </div>
                      </div>
                      <div className="mt-5 space-y-3">
                        {choice.instructions.map((instruction) => (
                          <div key={instruction} className="flex gap-3 text-sm leading-6 text-slate-300">
                            <Check size={16} className="mt-1 shrink-0 text-cyan-300" />
                            <span>{instruction}</span>
                          </div>
                        ))}
                      </div>
                      <button type="button" onClick={() => startDemoFromChoice(choice.id)} className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-cyan-400 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-300">
                        Start demo this way
                      </button>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        <WorkflowDemoModal
          isOpen={isWorkflowDemoOpen}
          onClose={closeWorkflowDemo}
          onRequestLead={openGuidedDemoFromWorkflow}
        />

      </div>

    </div>

  );

}

// ─── RelationshipRiskCard ─────────────────────────────────────────────────────
function RelationshipRiskCard({ client, selected, onSelect, onLogEmail, onLogCall, onLogMeeting, onSendCta, sendingCta, onDelete, onOpenAccount, onFetchLogo, fetchingLogo }) {
  const [expanded, setExpanded] = React.useState(false);
  const [emailOpen, setEmailOpen] = React.useState(false);
  const [callOpen, setCallOpen] = React.useState(false);
  const [manualEmail, setManualEmail] = React.useState('');
  const [manualPhone, setManualPhone] = React.useState('');
  const emailRef = React.useRef(null);
  const callRef = React.useRef(null);
  const styles = RISK_LEVEL_STYLES[client.riskLevel] || RISK_LEVEL_STYLES['Needs attention'];

  React.useEffect(() => {
    const handler = (e) => {
      if (emailRef.current && !emailRef.current.contains(e.target)) setEmailOpen(false);
      if (callRef.current && !callRef.current.contains(e.target)) setCallOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const contactWithEmail = (client.contacts || []).find((c) => c.email);
  const contactWithPhone = (client.contacts || []).find((c) => c.phone);
  const contactEmail = contactWithEmail?.email || extractEmailFromText(client.hiringContact || '');
  const contactPhone = contactWithPhone?.phone || '';
  const barWidth = `${client.riskScore || 0}%`;

  const driverIconEl = (icon) => {
    if (icon === 'clock')    return <Clock3 size={11} />;
    if (icon === 'zap')      return <Zap size={11} />;
    if (icon === 'user')     return <User size={11} />;
    if (icon === 'briefcase')return <Briefcase size={11} />;
    if (icon === 'flag')     return <Flag size={11} />;
    if (icon === 'alert')    return <AlertTriangle size={11} />;
    if (icon === 'star')     return <Sparkles size={11} />;
    return null;
  };

  const impactColor = (impact) => {
    if (impact === 'high')   return 'text-rose-300 bg-rose-500/10 border-rose-500/20';
    if (impact === 'medium') return 'text-amber-300 bg-amber-500/10 border-amber-500/20';
    if (impact === 'info')   return 'text-sky-300 bg-sky-500/10 border-sky-500/20';
    return 'text-slate-400 bg-slate-500/10 border-slate-500/20';
  };

  const trendIcon = client.riskTrend === 'improving' ? '↑' : client.riskTrend === 'declining' ? '↓' : '→';
  const trendColor = client.riskTrend === 'improving' ? 'text-emerald-400' : client.riskTrend === 'declining' ? 'text-rose-400' : 'text-slate-500';
  const initials = (client.companyName || '?').replace(/[^a-zA-Z0-9\s]/g, '').trim().split(/\s+/).slice(0, 2).map((w) => w[0].toUpperCase()).join('');
  const [logoFailed, setLogoFailed] = React.useState(false);

  return (
    <div className={`rounded-shell border p-4 transition-all ${styles.border} ${styles.bg}`}>
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <button
            type="button"
            onClick={() => onSelect && onSelect(client.id)}
            className={`shrink-0 h-4 w-4 rounded border flex items-center justify-center transition ${selected ? 'bg-cyan-500 border-cyan-400' : 'border-white/20 bg-white/5 hover:border-cyan-400/50'}`}
            aria-label={selected ? 'Deselect' : 'Select'}
          >
            {selected && <Check size={10} className="text-slate-950 font-black" />}
          </button>
          {/* Logo: real image when available, initials fallback always visible */}
          {client.companyLogoUrl && !logoFailed ? (
            <img
              src={client.companyLogoUrl}
              alt=""
              className="h-7 w-7 shrink-0 rounded-md object-contain bg-white/10"
              onError={() => setLogoFailed(true)}
            />
          ) : (
            <div className={`h-7 w-7 shrink-0 rounded-md flex items-center justify-center text-micro font-black ${styles.bg} border ${styles.border}`} title={client.companyLogoUrl ? 'Logo failed to load' : 'No logo captured yet'}>
              {initials}
            </div>
          )}
          <div className="text-sm font-bold text-white truncate">{client.companyName}</div>
          <span className={`${trendColor} text-sm font-black`} title={`Trend: ${client.riskTrend}`}>{trendIcon}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className={`rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.14em] ${styles.badge}`}>
            {styles.label}
          </span>
          {/* LinkedIn company page shortcut — clicking opens LinkedIn so extension can auto-capture */}
          <a
            href={client.linkedInUrl || `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(client.companyName)}`}
            target="_blank"
            rel="noopener noreferrer"
            title="Open company on LinkedIn to capture logo"
            className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-micro font-black text-slate-400 transition hover:border-cyan-400/30 hover:text-cyan-300 flex items-center gap-1"
          >
            <ExternalLink size={9} />
          </a>
          {/* Pulls the logo through the headless Vision LinkedIn scraper instead of requiring the browser extension */}
          {onFetchLogo && /linkedin\.com\/company\//i.test(client.linkedInUrl || '') && (
            <button
              type="button"
              onClick={() => onFetchLogo(client)}
              disabled={fetchingLogo}
              title="Fetch logo via LinkedIn scraper"
              className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-micro font-black text-slate-400 transition hover:border-cyan-400/30 hover:text-cyan-300 flex items-center gap-1 disabled:opacity-50"
            >
              {fetchingLogo ? <Loader size={9} className="animate-spin" /> : <Download size={9} />}
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] text-slate-400 transition hover:text-white"
          >
            {expanded ? 'Less' : 'Details'}
          </button>
        </div>
      </div>

      {/* Risk score bar */}
      <div className="mt-3">
        <div className="flex items-center justify-between mb-1.5">
          <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Relationship risk score</div>
          <div className="text-meta font-black text-white">{client.riskScore ?? '—'}<span className="text-slate-600 font-normal">/100</span></div>
        </div>
        <div className="h-2 w-full rounded-full bg-white/5 overflow-hidden">
          <div className={`h-full rounded-full transition-all ${styles.bar}`} style={{ width: barWidth }} />
        </div>
      </div>

      {/* Drivers row (always visible, compact) */}
      {client.riskDrivers && client.riskDrivers.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5">
          {client.riskDrivers.slice(0, expanded ? 99 : 3).map((driver, i) => (
            <span key={i} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-micro font-semibold ${impactColor(driver.impact)}`}>
              {driverIconEl(driver.icon)} {driver.label}
            </span>
          ))}
          {!expanded && client.riskDrivers.length > 3 && (
            <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-micro text-slate-500">+{client.riskDrivers.length - 3} more</span>
          )}
        </div>
      )}

      {/* Expanded section */}
      {expanded && (
        <div className="mt-4 space-y-4">
          {/* Health signals */}
          {client.healthSignals && client.healthSignals.length > 0 && (
            <div>
              <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-emerald-400">Positive signals</div>
              <div className="flex flex-wrap gap-1.5">
                {client.healthSignals.map((signal, i) => (
                  <span key={i} className="inline-flex items-center gap-1 rounded-full border border-emerald-400/20 bg-emerald-500/5 px-2 py-0.5 text-micro font-semibold text-emerald-300">
                    <Check size={9} /> {signal}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Next best action */}
          {client.nextBestAction && (
            <div className={`rounded-card border px-3 py-2.5 ${styles.border} bg-white/[0.03]`}>
              <div className="text-micro font-black uppercase tracking-[0.14em] text-cyan-400 mb-1">Recommended action</div>
              <div className="text-xs text-slate-200 leading-5">{client.nextBestAction}</div>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-2 gap-2 text-xs text-slate-300">
            <div><span className="text-slate-500">Owner:</span> {client.accountOwner || 'Unassigned'}</div>
            <div><span className="text-slate-500">Tier:</span> {client.accountTier || 'Prospect'}</div>
            <div><span className="text-slate-500">Last touch:</span> {client.daysSinceTouch >= 999 ? 'Never' : `${client.daysSinceTouch}d ago`}</div>
            <div><span className="text-slate-500">Job orders:</span> {client.totalJobOrders} total · {client.openRoles} open</div>
            <div><span className="text-slate-500">Submittal→hire:</span> {client.submittalToHireRatio}</div>
            <div><span className="text-slate-500">Pipeline:</span> R {Math.round(client.weightedPipeline || 0).toLocaleString()}</div>
            <div><span className="text-slate-500">Placements:</span> {client.placementCount || 0}</div>
            <div><span className="text-slate-500">Revenue:</span> R {Math.round(client.lifetimeRevenue || 0).toLocaleString()}</div>
          </div>

          {/* Contacts */}
          {(client.contacts || []).length > 0 ? (
            <div>
              <div className="mb-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-400">Contacts on file</div>
              <div className="space-y-1">
                {(client.contacts || []).slice(0, 4).map((c, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-ctl border border-white/5 bg-white/[0.03] px-2.5 py-1.5">
                    <User size={9} className="shrink-0 text-slate-500" />
                    <span className="text-micro font-semibold text-slate-200 truncate">{c.name || c.email || '—'}</span>
                    {c.title && <span className="text-micro text-slate-500 truncate">{c.title}</span>}
                    {c.email && <a href={`mailto:${c.email}`} className="ml-auto text-micro text-cyan-400 hover:underline shrink-0">{c.email}</a>}
                    {!c.email && c.phone && <span className="ml-auto text-micro text-emerald-400 shrink-0">{c.phone}</span>}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-ctl border border-amber-500/15 bg-amber-500/5 px-3 py-2.5">
              <div className="text-micro font-black uppercase tracking-[0.12em] text-amber-300 mb-1">No contacts on file</div>
              <p className="text-micro text-slate-400 leading-4 mb-2">Open a LinkedIn profile of someone at this company, then click the Vision extension to auto-save them.</p>
              <a
                href={`https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(client.companyName)}&origin=GLOBAL_SEARCH_HEADER`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2.5 py-1 text-micro font-black uppercase tracking-[0.1em] text-cyan-200 hover:bg-cyan-500/20 transition"
              >
                <User size={8} /> Find contacts on LinkedIn
              </a>
            </div>
          )}

          {/* Data Inspector */}
          <div className="rounded-ctl border border-white/5 bg-white/[0.02] px-3 py-2">
            <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-600 mb-1.5">Data inspector</div>
            <div className="space-y-0.5 text-micro text-slate-500 font-mono break-all">
              <div><span className="text-slate-600">logo: </span>{client.companyLogoUrl ? <span className="text-emerald-500">✓ {client.companyLogoUrl.slice(0, 60)}{client.companyLogoUrl.length > 60 ? '…' : ''}</span> : <span className="text-rose-500">✗ none stored</span>}</div>
              <div><span className="text-slate-600">logo status: </span>{client.companyLogoUrl ? (logoFailed ? <span className="text-rose-400">URL failed to load</span> : <span className="text-emerald-400">loaded OK</span>) : <span className="text-amber-400">not captured yet</span>}</div>
              <div><span className="text-slate-600">contacts: </span>{(client.contacts || []).length} found · {(client.contacts || []).filter(c => c.email).length} with email · {(client.contacts || []).filter(c => c.phone).length} with phone</div>
            </div>
          </div>
        </div>
      )}

      {/* Always-visible summary line */}
      {!expanded && (
        <div className="mt-2 text-micro text-slate-500 leading-5">{client.nextBestAction}</div>
      )}

      {/* Action buttons */}
      <div className="mt-3 flex flex-wrap gap-1.5">

        {/* Email dropdown */}
        <div className="relative" ref={emailRef}>
          <button
            type="button"
            onClick={() => { setEmailOpen((v) => !v); setCallOpen(false); }}
            className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-200 transition hover:border-cyan-300/30 hover:text-white"
          >
            <Mail size={10} /> Email <ChevronDown size={8} className={`transition-transform ${emailOpen ? 'rotate-180' : ''}`} />
          </button>
          {emailOpen && (
            <div className="absolute bottom-full left-0 mb-1.5 z-30 min-w-[200px] overflow-hidden rounded-ctl border border-white/10 bg-slate-900 shadow-2xl">
              {contactEmail ? (
                <a
                  href={`mailto:${contactEmail}`}
                  onClick={() => setEmailOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 text-micro font-semibold text-white hover:bg-white/10 transition"
                >
                  <Mail size={10} className="shrink-0 text-cyan-400" /> {contactEmail}
                </a>
              ) : (
                <div className="px-3 pt-2.5 pb-1 text-micro font-semibold uppercase tracking-[0.1em] text-slate-500">No email on file</div>
              )}
              <div className="px-2.5 pb-2 pt-1 border-t border-white/5">
                <div className="flex gap-1 items-center">
                  <input
                    type="email"
                    value={manualEmail}
                    onChange={(e) => setManualEmail(e.target.value)}
                    placeholder="Enter email…"
                    className="min-w-0 flex-1 rounded-ctl border border-white/10 bg-white/5 px-2 py-1.5 text-micro text-white placeholder-slate-500 focus:border-cyan-400/40 focus:outline-none"
                    onKeyDown={(e) => { if (e.key === 'Enter' && manualEmail) { window.location.href = `mailto:${manualEmail}`; setEmailOpen(false); } }}
                  />
                  <a
                    href={manualEmail ? `mailto:${manualEmail}` : undefined}
                    onClick={() => { if (manualEmail) setEmailOpen(false); }}
                    aria-disabled={!manualEmail}
                    className={`shrink-0 rounded-ctl border px-2 py-1.5 text-micro transition ${
                      manualEmail
                        ? 'border-cyan-400/30 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20 cursor-pointer'
                        : 'border-white/5 bg-white/5 text-slate-600 pointer-events-none'
                    }`}
                  >
                    <Mail size={10} />
                  </a>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { onLogEmail(client); setEmailOpen(false); }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-micro font-semibold text-slate-300 hover:bg-white/10 transition border-t border-white/5"
              >
                <Check size={10} className="shrink-0 text-emerald-400" /> Log email
              </button>
            </div>
          )}
        </div>

        {/* Call dropdown */}
        <div className="relative" ref={callRef}>
          <button
            type="button"
            onClick={() => { setCallOpen((v) => !v); setEmailOpen(false); }}
            className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-200 transition hover:border-cyan-300/30 hover:text-white"
          >
            <Phone size={10} /> Call <ChevronDown size={8} className={`transition-transform ${callOpen ? 'rotate-180' : ''}`} />
          </button>
          {callOpen && (
            <div className="absolute bottom-full left-0 mb-1.5 z-30 min-w-[200px] overflow-hidden rounded-ctl border border-white/10 bg-slate-900 shadow-2xl">
              {contactPhone ? (
                <a
                  href={`tel:${contactPhone}`}
                  onClick={() => setCallOpen(false)}
                  className="flex items-center gap-2 px-3 py-2.5 text-micro font-semibold text-white hover:bg-white/10 transition"
                >
                  <Phone size={10} className="shrink-0 text-emerald-400" /> {contactPhone}
                </a>
              ) : (
                <div className="px-3 pt-2.5 pb-1 text-micro font-semibold uppercase tracking-[0.1em] text-slate-500">No phone on file</div>
              )}
              <div className="px-2.5 pb-2 pt-1 border-t border-white/5">
                <div className="flex gap-1 items-center">
                  <input
                    type="tel"
                    value={manualPhone}
                    onChange={(e) => setManualPhone(e.target.value)}
                    placeholder="Enter number…"
                    className="min-w-0 flex-1 rounded-ctl border border-white/10 bg-white/5 px-2 py-1.5 text-micro text-white placeholder-slate-500 focus:border-emerald-400/40 focus:outline-none"
                    onKeyDown={(e) => { if (e.key === 'Enter' && manualPhone) { window.location.href = `tel:${manualPhone}`; setCallOpen(false); } }}
                  />
                  <a
                    href={manualPhone ? `tel:${manualPhone}` : undefined}
                    onClick={() => { if (manualPhone) setCallOpen(false); }}
                    aria-disabled={!manualPhone}
                    className={`shrink-0 rounded-ctl border px-2 py-1.5 text-micro transition ${
                      manualPhone
                        ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 cursor-pointer'
                        : 'border-white/5 bg-white/5 text-slate-600 pointer-events-none'
                    }`}
                  >
                    <Phone size={10} />
                  </a>
                </div>
              </div>
              <button
                type="button"
                onClick={() => { onLogCall(client); setCallOpen(false); }}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-micro font-semibold text-slate-300 hover:bg-white/10 transition border-t border-white/5"
              >
                <Check size={10} className="shrink-0 text-slate-400" /> Log call
              </button>
            </div>
          )}
        </div>

        <button type="button" onClick={() => onLogMeeting(client)} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-200 transition hover:border-cyan-300/30 hover:text-white"><Calendar size={10} /> Meeting</button>
        <button type="button" onClick={() => onSendCta(client)} disabled={sendingCta} className="inline-flex items-center gap-1 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-cyan-100 transition hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-60"><Mail size={10} /> {sendingCta ? 'Sending…' : 'Send CTA'}</button>
        {onOpenAccount && (
          <button type="button" onClick={() => onOpenAccount(client.id)} className="inline-flex items-center gap-1 rounded-full border border-violet-400/25 bg-violet-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-violet-300 transition hover:bg-violet-500/20"><ExternalLink size={9} /> Open account</button>
        )}
        {onDelete && (
          <button type="button" onClick={() => onDelete(client)} className="ml-auto inline-flex items-center gap-1 rounded-full border border-rose-500/20 bg-rose-500/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-rose-400 transition hover:bg-rose-500/15 hover:border-rose-400/40"><AlertTriangle size={10} /> Delete</button>
        )}
      </div>
    </div>
  );
}

function OtpGatePanel({
  email,
  code,
  error,
  notice,
  isSubmitting,
  isResending,
  onCodeChange,
  onSubmit,
  onResend,
  onLogout,
}) {
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#11335f_0%,#020617_42%,#020617_100%)] text-slate-100">
      <div className="mx-auto flex min-h-screen w-full max-w-3xl items-center justify-center px-4 py-8">
        <section className="w-full rounded-shell border border-white/10 bg-slate-950/75 p-6 shadow-2xl shadow-cyan-950/30 backdrop-blur sm:p-8">
          <div className="flex items-start gap-4">
            <div className="rounded-card border border-cyan-400/20 bg-cyan-500/10 p-3 text-cyan-200">
              <ShieldCheck size={24} />
            </div>
            <div>
              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Two-step verification</div>
              <h1 className="mt-2 text-2xl font-black text-white">Check your email</h1>
              <p className="mt-2 text-sm leading-6 text-slate-300">Enter the 6-digit code sent to {email || 'your account email'} to unlock this session.</p>
            </div>
          </div>

          <form className="mt-7 space-y-4" onSubmit={onSubmit}>
            <label className="block">
              <div className="mb-2 text-xs font-bold text-slate-300">Verification code</div>
              <input
                value={code}
                onChange={(event) => onCodeChange(event.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                pattern="[0-9]{6}"
                autoComplete="one-time-code"
                className="w-full rounded-ctl border border-white/10 bg-slate-950/70 px-4 py-3 text-center text-2xl font-black tracking-[0.4em] text-white outline-none transition focus:border-cyan-400"
                placeholder="000000"
                required
              />
            </label>

            {notice && <div className="rounded-card border border-cyan-500/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">{notice}</div>}
            {error && <div className="rounded-card border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">{error}</div>}

            <button type="submit" disabled={isSubmitting || code.length !== 6} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-400 px-4 py-3 text-xs font-black uppercase tracking-[0.14em] text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70">
              {isSubmitting && <Loader size={14} className="animate-spin" />}
              Verify and continue
            </button>
          </form>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 text-xs">
            <button type="button" onClick={onResend} disabled={isResending} className="font-bold text-cyan-200 transition hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-60">
              {isResending ? 'Sending new code...' : 'Resend code'}
            </button>
            <button type="button" onClick={onLogout} className="font-bold text-slate-400 transition hover:text-white">Use a different account</button>
          </div>
        </section>
      </div>
    </div>
  );
}

function WorkspacePrepSkeleton() {
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#11335f_0%,#020617_42%,#020617_100%)] text-slate-100">
      <div className="mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center px-4 py-8">
        <div className="w-full rounded-shell border border-white/10 bg-slate-950/70 p-6 shadow-2xl shadow-cyan-950/20 backdrop-blur">
          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
            <div className="text-center lg:text-left">
              <img src="/vision-official-logo.jpg" alt="Vision" className="mx-auto w-full max-w-[360px] rounded-shell shadow-2xl shadow-cyan-950/30 lg:mx-0" />
              <div className="mt-5 inline-flex items-center gap-3 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-cyan-100">
                <Loader size={18} className="animate-spin" /> Getting things prepared for you
              </div>
            </div>
            <div className="grid gap-4">
              <div className="h-24 animate-pulse rounded-shell border border-white/10 bg-white/[0.06]" />
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="h-28 animate-pulse rounded-shell border border-white/10 bg-white/[0.05]" />
                <div className="h-28 animate-pulse rounded-shell border border-white/10 bg-white/[0.05]" />
                <div className="h-28 animate-pulse rounded-shell border border-white/10 bg-white/[0.05]" />
              </div>
              <div className="grid gap-3 rounded-shell border border-white/10 bg-white/[0.04] p-4">
                <div className="h-3 w-2/3 animate-pulse rounded-full bg-white/10" />
                <div className="h-3 w-full animate-pulse rounded-full bg-white/10" />
                <div className="h-3 w-5/6 animate-pulse rounded-full bg-white/10" />
              </div>
              <div className="h-40 animate-pulse rounded-shell border border-white/10 bg-white/[0.05]" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function OwnerIntroWalkthrough({ agencyName, ownerName, step, onStep, onFinish, isFinishing }) {
  const steps = [
    {
      eyebrow: 'Start from zero',
      title: 'Go from empty workspace to active agency flow.',
      body: 'Vision is built so a new owner can begin without clients, add existing clients from previous software, or import company and contact history as the agency grows.',
      bullets: ['Create or import companies and contacts', 'Track ownership, terms, activity, and relationship health', 'Use client records as the start of jobs, placements, and revenue history'],
      accent: 'from-cyan-400 to-blue-500',
    },
    {
      eyebrow: 'Create demand',
      title: 'Turn clients into job orders and public roles.',
      body: 'Add a role, publish it to your Vision-hosted agency career page, and use Google indexing so candidates can discover your open roles. Job board integrations are coming soon.',
      bullets: ['Public job links for each role', 'Agency role board showing all open jobs', 'SEO-ready career pages and sitemap support'],
      accent: 'from-emerald-300 to-cyan-400',
    },
    {
      eyebrow: 'Capture candidates',
      title: 'Candidates apply through the job link, or recruiters inject CVs manually.',
      body: 'Applications come through the link attached to the job post. Recruiters can also upload CVs directly when they already have candidates from email, LinkedIn, referrals, or previous systems.',
      bullets: ['Candidate-facing application forms', 'Manual CV injection by recruiters', 'CV parsing into structured candidate records'],
      accent: 'from-violet-300 to-cyan-400',
    },
    {
      eyebrow: 'Run the desk',
      title: 'Parsed CVs move into the pipeline where recruiters make decisions.',
      body: 'Vision turns CVs and applications into pipeline-ready records. Recruiters can review candidate information, screening results, fit signals, CV files, notes, and stage history before moving candidates forward.',
      bullets: ['Drag candidates through pipeline stages', 'View CV, screening, fit, ownership, and activity in one panel', 'Submit candidates to clients, reject, shortlist, hire, and track revenue'],
      accent: 'from-amber-300 to-rose-400',
    },
  ];
  const optionalCapabilities = [
    'AI CV parsing, screening, and resume narrative audit',
    'Client CRM with companies, contacts, opportunities, and risk signals',
    'Revenue, placements, invoices, tax/VAT, CSV export, and multi-currency fields',
    'Team roles for owner, admin, recruiter, sales, finance, client reviewer, and limited access',
    'Candidate role-watch alerts and candidate tools powered by Vision',
    'Migration command center for CSV and previous ATS/CRM data',
  ];
  const current = steps[step] || steps[0];
  const progress = Math.round(((step + 1) / steps.length) * 100);

  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,#12385f_0%,#020617_45%,#020617_100%)] px-4 py-6 text-slate-100 sm:px-6 lg:px-8">
      <div className="mx-auto flex min-h-[calc(100vh-3rem)] w-full max-w-7xl items-center">
        <div className="grid w-full gap-5 lg:grid-cols-[0.95fr_1.05fr] lg:items-stretch">
          <section className="overflow-hidden rounded-shell border border-white/10 bg-slate-950/70 shadow-2xl shadow-cyan-950/20 backdrop-blur">
            <div className={`h-1.5 bg-gradient-to-r ${current.accent}`} style={{ width: `${progress}%` }} />
            <div className="p-6 sm:p-8">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="text-meta font-black uppercase tracking-[0.28em] text-cyan-300">Vision owner walkthrough</div>
                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-micro font-black uppercase tracking-[0.16em] text-slate-300">{step + 1} of {steps.length}</div>
              </div>
              <div className="mt-8 text-meta font-black uppercase tracking-[0.24em] text-slate-500">{current.eyebrow}</div>
              <h1 className="mt-3 text-3xl font-black leading-tight text-white sm:text-5xl">{current.title}</h1>
              <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-300">{current.body}</p>
              <div className="mt-7 grid gap-3">
                {current.bullets.map((item) => (
                  <div key={item} className="flex gap-3 rounded-card border border-white/10 bg-white/[0.04] p-4">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-cyan-400 text-slate-950"><Check size={13} strokeWidth={4} /></span>
                    <span className="text-sm font-semibold leading-6 text-slate-100">{item}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <button type="button" onClick={() => step < steps.length - 1 ? onStep(step + 1) : onFinish()} disabled={isFinishing} className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70">
                  {isFinishing ? 'Preparing...' : step < steps.length - 1 ? 'Continue' : 'Enter Vision'} <ChevronRight size={16} />
                </button>
                {step > 0 && (
                  <button type="button" onClick={() => onStep(step - 1)} disabled={isFinishing} className="rounded-full border border-white/10 bg-white/5 px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-200 transition hover:border-white/20 hover:text-white disabled:opacity-60">Back</button>
                )}
                <button type="button" onClick={onFinish} disabled={isFinishing} className="rounded-full border border-white/10 px-6 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-400 transition hover:border-white/20 hover:text-white disabled:opacity-60">Skip intro</button>
              </div>
            </div>
          </section>

          <aside className="grid gap-5">
            <div className="rounded-shell border border-white/10 bg-white/[0.04] p-6 backdrop-blur">
              <div className="flex items-center gap-4">
                <img src="/logo-vision.png" alt="Vision" className="h-14 w-14 rounded-card border border-cyan-300/20 bg-black object-cover" />
                <div>
                  <div className="text-micro font-black uppercase tracking-[0.22em] text-slate-400">Workspace</div>
                  <div className="mt-1 text-xl font-black text-white">{agencyName || 'Your agency'}</div>
                  <div className="mt-1 text-xs text-slate-400">Owner: {ownerName || 'Agency owner'}</div>
                </div>
              </div>
            </div>
            <div className="rounded-shell border border-white/10 bg-slate-950/60 p-6">
              <div className="text-meta font-black uppercase tracking-[0.24em] text-violet-300">Optional power tools</div>
              <div className="mt-4 grid gap-3">
                {optionalCapabilities.map((item) => (
                  <div key={item} className="rounded-card border border-white/10 bg-white/[0.035] px-4 py-3 text-sm font-semibold leading-6 text-slate-200">{item}</div>
                ))}
              </div>
            </div>
            <div className="rounded-shell border border-cyan-300/15 bg-cyan-500/10 p-6">
              <div className="flex items-center gap-3 text-cyan-100">
                <Sparkles size={18} />
                <div className="text-sm font-black">Beginner-friendly path</div>
              </div>
              <p className="mt-3 text-sm leading-7 text-slate-300">You can start with one client and one job, then add automation, governance, revenue, migration, and CRM depth only when the agency needs it.</p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

function DirectDemoWalkthrough({ isOpen, step, completedSteps, jobTitle, candidateCount, screeningReviewOpen = false, screeningCandidateReady = false, offerCandidateReady = false }) {
  const coachRef = useRef(null);
  const [coachPosition, setCoachPosition] = useState({ top: 24, left: null, right: 24, arrow: 'top' });

  const steps = [
    {
      target: 'nav-pipeline',
      eyebrow: 'Step 1',
      title: 'Start from the Pipeline.',
      body: 'Press the highlighted Pipeline button. This is where sourced candidates become active recruiter work.',
      press: ['Press Pipeline in the bottom navigation.', 'The guide moves forward only after the real app action happens.'],
    },
    {
      target: 'nav-jobs',
      eyebrow: 'Step 2',
      title: 'Open Jobs to create demand.',
      body: 'Jobs is the command center for new listings, role requirements, screening questions, publishing, and sourcing.',
      press: ['Press Jobs in the bottom navigation.', 'Vision will scroll to the job creation area next.'],
    },
    {
      target: 'job-title-input',
      eyebrow: 'Step 3',
      title: 'Create a job listing yourself.',
      body: 'Fill the highlighted Role title field, then press Add job offer. This teaches how new demand enters Vision.',
      press: ['Type a role title in the highlighted field.', 'Press Add job offer. The guide moves forward after the job is created.'],
    },
    {
      target: 'job-open-button',
      eyebrow: 'Step 4',
      title: 'Open the job workspace.',
      body: 'The job workspace connects the role to candidates, prospects, screening, publishing, and outreach controls.',
      press: ['Press Open ATS panel on a job card.', 'The guide moves forward after the job workspace opens.'],
    },
    {
      target: 'sourced-prospects',
      eyebrow: 'Step 5',
      title: 'Move a sourced prospect into the pipeline.',
      body: 'This is the direct-acquisition moment: a sourced prospect becomes a live pipeline candidate.',
      press: ['Find Sourced Prospects.', 'Press Move to pipeline on a strong prospect.'],
    },
    {
      target: 'nav-pipeline',
      eyebrow: 'Step 6',
      title: 'Return to the Pipeline.',
      body: 'The new candidate now belongs in the recruiter pipeline for screening, client submission, offer, and placement.',
      press: ['Press Pipeline in the bottom navigation.', 'Find the candidate card on the board.'],
    },
    {
      target: screeningReviewOpen ? 'screening-ai-suggestion-button' : screeningCandidateReady ? 'candidate-screening-button' : 'pipeline-screening-column',
      eyebrow: 'Step 7',
      title: screeningReviewOpen ? 'Use AI to shape screening.' : screeningCandidateReady ? 'Open the screening review.' : 'Drag the candidate into Screening.',
      body: screeningReviewOpen ? 'Choose an AI screening question so Vision saves a stronger candidate review before client submission.' : screeningCandidateReady ? 'The candidate is now in the Screening stage. Open the screening review from the card.' : 'The sourced candidate starts as New Applied. Move the card into Screening to start the recruiter review step.',
      press: screeningReviewOpen ? ['Press the highlighted Use AI suggestion button.', 'The guide advances only after the screening question is saved.'] : screeningCandidateReady ? ['Press Screening on the candidate card.', 'The screening review opens with AI question suggestions.'] : ['Drag the candidate card from New Applied into the highlighted Screening column.', 'Drop it inside Screening to reveal the Screening button.'],
    },
    {
      target: 'candidate-send-client-button',
      eyebrow: 'Step 8',
      title: 'Submit a qualified candidate to the client.',
      body: 'Vision records who was sent, when, to whom, and with what outcome.',
      press: ['Press Send to Client on the candidate card.', 'In Log Submittal, confirm details and press Log Submittal.'],
    },
    {
      target: 'pipeline-interview-column',
      eyebrow: 'Step 9',
      title: 'Move the candidate into Interview.',
      body: 'After client submission, move the candidate forward to Interview so the pipeline reflects the next stage.',
      press: ['Drag the candidate card from Screening into the highlighted Interview column.', 'Drop it inside Interview to continue.'],
    },
    {
      target: 'share-shortlist-button',
      eyebrow: 'Step 10',
      title: 'Create a client shortlist.',
      body: 'The shortlist turns selected candidates into a shareable client review pack.',
      press: ['Press Share Shortlist and then Create Shortlist.', 'Copy the shortlist link, then close the shortlist window.'],
    },
    {
      target: offerCandidateReady ? 'candidate-offer-placement' : 'pipeline-offer-column',
      eyebrow: 'Step 11',
      title: offerCandidateReady ? 'Send the offer packet.' : 'Move the candidate into Offer.',
      body: offerCandidateReady ? 'Vision records the offer packet and closes this direct-sourcing walkthrough once the packet is sent.' : 'Move the interviewed candidate into Offer so Vision can generate the final packet.',
      press: offerCandidateReady ? ['Press Send Offer on the candidate card.', 'In Send Offer, review the prefilled packet and press Send Offer Packet.'] : ['Drag the candidate card into the highlighted Offer column.', 'Drop it inside Offer to reveal Send Offer.'],
    },
  ];

  const current = steps[step] || steps[0];
  const progress = Math.round(((step + 1) / steps.length) * 100);
  const isComplete = completedSteps.includes(step);

  useEffect(() => {
    if (!isOpen || !current?.target || typeof document === 'undefined') return;

    let frameId = 0;
    let scrollTimer = 0;
    let didInitialScroll = false;

    const readTarget = () => document.querySelector(`[data-direct-demo-target="${current.target}"]`);
    const isBottomNavTarget = () => current.target === 'nav-pipeline' || current.target === 'nav-jobs';

    const updateCoachPosition = () => {
      const target = readTarget();
      if (!target) {
        setCoachPosition({ top: 24, left: null, right: 24, arrow: 'top' });
        return;
      }

      const rect = target.getBoundingClientRect();
      const coachRect = coachRef.current?.getBoundingClientRect();
      const width = Math.min(coachRect?.width || 400, window.innerWidth - 24);
      const coachHeight = Math.min(coachRect?.height || 255, window.innerHeight - 24);
      const gap = 22;
      let top = rect.bottom + gap;
      let left = rect.left + (rect.width / 2) - (width / 2);
      let arrow = 'top';

      left = Math.min(Math.max(12, left), window.innerWidth - width - 12);

      if (isBottomNavTarget()) {
        top = rect.top - coachHeight - gap;
        left = Math.min(Math.max(12, rect.left + (rect.width / 2) - (width / 2)), window.innerWidth - width - 12);
        arrow = 'bottom';
        if (top < 12) {
          top = 12;
          left = Math.min(Math.max(12, (window.innerWidth - width) / 2), window.innerWidth - width - 12);
          arrow = 'top';
        }
        setCoachPosition({ top, left, right: null, arrow });
        return;
      }

      if (top + coachHeight > window.innerHeight && rect.top > coachHeight + gap) {
        top = rect.top - coachHeight - gap;
        arrow = 'bottom';
      }

      if (window.innerWidth >= 980 && rect.right + width + gap < window.innerWidth) {
        left = rect.right + gap;
        top = Math.min(Math.max(12, rect.top - 12), window.innerHeight - coachHeight - 12);
        arrow = 'left';
      } else if (window.innerWidth >= 980 && rect.left - width - gap > 0) {
        left = rect.left - width - gap;
        top = Math.min(Math.max(12, rect.top - 12), window.innerHeight - coachHeight - 12);
        arrow = 'right';
      }

      if (window.innerWidth < 700) {
        left = 12;
        top = Math.min(Math.max(12, top), window.innerHeight - coachHeight - 12);
        arrow = top < rect.top ? 'bottom' : 'top';
      }

      const overlapsTarget = left < rect.right + gap
        && left + width > rect.left - gap
        && top < rect.bottom + gap
        && top + coachHeight > rect.top - gap;

      if (overlapsTarget && !isBottomNavTarget()) {
        if (rect.top > coachHeight + gap + 12) {
          top = rect.top - coachHeight - gap;
          left = Math.min(Math.max(12, rect.left + (rect.width / 2) - (width / 2)), window.innerWidth - width - 12);
          arrow = 'bottom';
        } else if (rect.bottom + coachHeight + gap < window.innerHeight - 12) {
          top = rect.bottom + gap;
          left = Math.min(Math.max(12, rect.left + (rect.width / 2) - (width / 2)), window.innerWidth - width - 12);
          arrow = 'top';
        }
      }

      setCoachPosition({ top, left, right: null, arrow });
    };

    const schedulePosition = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(updateCoachPosition);
    };

    const scrollTargetIntoView = () => {
      const target = readTarget();
      if (!target) {
        schedulePosition();
        return;
      }
      if (!isBottomNavTarget()) {
        target.scrollIntoView({ behavior: didInitialScroll ? 'auto' : 'smooth', block: 'center', inline: 'center' });
      }
      didInitialScroll = true;
      window.clearTimeout(scrollTimer);
      scrollTimer = window.setTimeout(schedulePosition, 360);
      schedulePosition();
    };

    scrollTargetIntoView();
    const retryTimer = window.setTimeout(scrollTargetIntoView, 450);

    window.addEventListener('scroll', schedulePosition, true);
    window.addEventListener('resize', schedulePosition);
    return () => {
      window.clearTimeout(retryTimer);
      window.clearTimeout(scrollTimer);
      window.cancelAnimationFrame(frameId);
      window.removeEventListener('scroll', schedulePosition, true);
      window.removeEventListener('resize', schedulePosition);
    };
  }, [current?.target, isOpen, step]);

  if (!isOpen) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[9997] text-white" role="dialog" aria-live="polite" aria-label="Vision direct sourcing walkthrough">
      <div
        ref={coachRef}
        className="pointer-events-none w-[min(25rem,calc(100vw-1.5rem))] overflow-visible rounded-card border border-cyan-300/55 bg-[#020817] shadow-[0_24px_90px_rgba(0,0,0,.88),0_0_0_1px_rgba(103,232,249,.18)]"
        style={{ position: 'fixed', top: coachPosition.top, left: coachPosition.left ?? 'auto', right: coachPosition.right ?? 'auto' }}
      >
        <div className={`absolute h-4 w-4 rotate-45 border border-cyan-300/55 bg-[#020817] ${
          coachPosition.arrow === 'bottom' ? '-bottom-2 left-1/2 -translate-x-1/2' : coachPosition.arrow === 'left' ? '-left-2 top-8' : coachPosition.arrow === 'right' ? '-right-2 top-8' : '-top-2 left-1/2 -translate-x-1/2'
        }`} />
        <div className="overflow-hidden rounded-card">
          <div className="h-1.5 bg-slate-800">
            <div className="h-full bg-cyan-400 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="relative p-4">
            <div className="text-micro font-black uppercase tracking-[0.2em] text-cyan-300">Live demo guide · {current.eyebrow}</div>
            <h2 className="mt-2 text-base font-black leading-tight text-white sm:text-lg">{current.title}</h2>
            <p className="mt-2 text-xs leading-5 text-slate-300 sm:text-sm">{current.body}</p>

            <div className="mt-3 rounded-card border border-cyan-300/25 bg-cyan-950/70 p-3">
              <div className="text-micro font-black uppercase tracking-[0.18em] text-cyan-200">Do this now</div>
              <div className="mt-2 grid gap-2">
                {current.press.map((item) => (
                  <div key={item} className="flex gap-2 text-xs font-semibold leading-5 text-slate-100">
                    <Check size={14} className="mt-0.5 shrink-0 text-cyan-300" />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className={`mt-3 rounded-card border px-3 py-2 text-meta font-semibold leading-5 ${isComplete ? 'border-emerald-300/20 bg-emerald-300/10 text-emerald-100' : 'border-amber-300/20 bg-amber-300/10 text-amber-100'}`}>
              {isComplete ? 'Done. Moving to the next step...' : 'Waiting for the highlighted app action.'}
            </div>

            <div className="mt-3 flex items-center justify-between gap-3 border-t border-white/10 pt-3 text-meta text-slate-500">
              <span className="truncate">{jobTitle || 'Demo job workspace'}</span>
              <span className="shrink-0">{step + 1}/{steps.length} · {candidateCount || 0} candidates</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ImportDemoWalkthrough({ isOpen, step, completedSteps, screeningReviewOpen = false, screeningCandidateReady = false, offerCandidateReady = false }) {
  const coachRef = useRef(null);
  const [coachPosition, setCoachPosition] = useState({ top: 24, left: null, right: 24, arrow: 'top' });

  const steps = [
    {
      target: 'nav-jobs',
      eyebrow: 'Step 1',
      title: 'Start by importing jobs.',
      body: 'This path is for agencies that already receive job specs from websites, email, spreadsheets, or older systems.',
      press: ['Press Jobs in the bottom navigation.', 'Vision will guide you to the job import control.'],
    },
    {
      target: 'import-jobs-csv-button',
      eyebrow: 'Step 2',
      title: 'Upload job listings into Vision.',
      body: 'Vision turns imported role rows into structured job cards with company, location, requirements, and matching context.',
      press: ['Press the highlighted Import CSV button.', 'In demo mode, Vision simulates a clean job import.'],
    },
    {
      target: 'nav-pipeline',
      eyebrow: 'Step 3',
      title: 'Move to candidate import.',
      body: 'Once jobs exist, imported candidates and CVs can be mapped to the right vacancy and moved through the pipeline.',
      press: ['Press Pipeline in the bottom navigation.', 'The candidate import controls are in this workspace.'],
    },
    {
      target: 'import-pipeline-csv-button',
      eyebrow: 'Step 4',
      title: 'Import candidate records.',
      body: 'Use CSV import for candidates you already have from another ATS, spreadsheet, applicant tracker, website export, or email list.',
      press: ['Press the highlighted Import CSV button.', 'Vision will add mapped demo candidates to the pipeline.'],
    },
    {
      target: 'bulk-cv-upload-button',
      eyebrow: 'Step 5',
      title: 'Upload CVs for parsing.',
      body: 'Use CV upload when applicants arrive as resumes from email, website forms, referrals, or external job boards.',
      press: ['Press Upload CV PDFs.', 'In demo mode, Vision simulates CV parsing and fit decisions.'],
    },
    {
      target: 'pipeline-screening-column',
      eyebrow: 'Step 6',
      title: 'Move an imported candidate to Screening.',
      body: 'Imported records become useful when recruiters stage them, screen them, and decide what is client-ready.',
      press: ['Drag an imported candidate from New Applied into the highlighted Screening column.', 'Drop it inside Screening to continue.'],
    },
    {
      target: screeningReviewOpen ? 'screening-ai-suggestion-button' : screeningCandidateReady ? 'candidate-screening-button' : 'pipeline-screening-column',
      eyebrow: 'Step 7',
      title: screeningReviewOpen ? 'Use AI to shape screening.' : screeningCandidateReady ? 'Open the screening review.' : 'Confirm Screening stage.',
      body: screeningReviewOpen ? 'Choose an AI screening question so Vision saves a stronger imported-candidate review before client submission.' : screeningCandidateReady ? 'The imported candidate is now in Screening. Open the review from the card.' : 'The imported candidate needs to be in Screening before the review can start.',
      press: screeningReviewOpen ? ['Press the highlighted Use AI suggestion button.', 'The guide advances only after the screening question is saved.'] : screeningCandidateReady ? ['Press Screening on the candidate card.', 'The screening review opens with AI question suggestions.'] : ['Drag the imported candidate into Screening if needed.', 'Drop it inside Screening to reveal the Screening button.'],
    },
    {
      target: 'candidate-send-client-button',
      eyebrow: 'Step 8',
      title: 'Submit the imported candidate to the client.',
      body: 'Vision records who was submitted, which job they were submitted for, and the client outcome.',
      press: ['Press Send to Client on the candidate card.', 'In Log Submittal, confirm details and press Log Submittal.'],
    },
    {
      target: 'pipeline-interview-column',
      eyebrow: 'Step 9',
      title: 'Move the imported candidate into Interview.',
      body: 'After submission, drag the candidate forward so the imported workflow becomes a live pipeline process.',
      press: ['Drag the candidate card from Screening into the highlighted Interview column.', 'Drop it inside Interview to continue.'],
    },
    {
      target: 'share-shortlist-button',
      eyebrow: 'Step 10',
      title: 'Create a client shortlist.',
      body: 'Turn imported candidate records into a shareable client review pack.',
      press: ['Press Share Shortlist and then Create Shortlist.', 'Copy the shortlist link, then close the shortlist window.'],
    },
    {
      target: offerCandidateReady ? 'candidate-offer-placement' : 'pipeline-offer-column',
      eyebrow: 'Step 11',
      title: offerCandidateReady ? 'Send the offer packet.' : 'Move the candidate into Offer.',
      body: offerCandidateReady ? 'Vision records the offer packet and closes this import walkthrough once the packet is sent.' : 'Move the interviewed imported candidate into Offer so Vision can generate the final packet.',
      press: offerCandidateReady ? ['Press Send Offer on the candidate card.', 'In Send Offer, review the prefilled packet and press Send Offer Packet.'] : ['Drag the candidate card into the highlighted Offer column.', 'Drop it inside Offer to reveal Send Offer.'],
    },
  ];

  const current = steps[step] || steps[0];
  const progress = Math.round(((step + 1) / steps.length) * 100);
  const isComplete = completedSteps.includes(step);

  useEffect(() => {
    if (!isOpen || !current?.target || typeof document === 'undefined') return;

    let frameId = 0;
    let scrollTimer = 0;

    const readTarget = () => document.querySelector(`[data-direct-demo-target="${current.target}"]`);

    const placeCoach = () => {
      const target = readTarget();
      const coach = coachRef.current;
      if (!target || !coach) return;

      const rect = target.getBoundingClientRect();
      const coachRect = coach.getBoundingClientRect();
      const margin = 14;
      const gap = 22;
      const viewportWidth = window.innerWidth || 1024;
      const viewportHeight = window.innerHeight || 768;
      const isBottomNav = current.target.startsWith('nav-');
      const coachWidth = Math.min(coachRect.width || 384, viewportWidth - margin * 2);
      const coachHeight = Math.min(coachRect.height || 260, viewportHeight - margin * 2);

      let top = rect.bottom + gap;
      let left = rect.left + rect.width / 2 - coachWidth / 2;
      let arrow = 'top';

      if (isBottomNav || top + coachHeight > viewportHeight - margin) {
        top = rect.top - coachHeight - gap;
        arrow = 'bottom';
      }

      if (top < margin) {
        top = Math.min(viewportHeight - coachHeight - margin, Math.max(margin, rect.bottom + gap));
        arrow = 'top';
      }

      if (!isBottomNav && viewportWidth >= 880 && rect.right + coachWidth + gap < viewportWidth - margin) {
        left = rect.right + gap;
        top = Math.min(Math.max(margin, rect.top - 10), viewportHeight - coachHeight - margin);
        arrow = 'left';
      } else if (!isBottomNav && viewportWidth >= 880 && rect.left - coachWidth - gap > margin) {
        left = rect.left - coachWidth - gap;
        top = Math.min(Math.max(margin, rect.top - 10), viewportHeight - coachHeight - margin);
        arrow = 'right';
      }

      left = Math.min(viewportWidth - coachWidth - margin, Math.max(margin, left));
      top = Math.min(viewportHeight - coachHeight - margin, Math.max(margin, top));

      const overlapsTarget = left < rect.right + margin
        && left + coachWidth > rect.left - margin
        && top < rect.bottom + margin
        && top + coachHeight > rect.top - margin;

      if (overlapsTarget) {
        if (rect.top > coachHeight + gap + margin) {
          top = rect.top - coachHeight - gap;
          left = Math.min(viewportWidth - coachWidth - margin, Math.max(margin, rect.left + rect.width / 2 - coachWidth / 2));
          arrow = 'bottom';
        } else if (rect.bottom + coachHeight + gap < viewportHeight - margin) {
          top = rect.bottom + gap;
          left = Math.min(viewportWidth - coachWidth - margin, Math.max(margin, rect.left + rect.width / 2 - coachWidth / 2));
          arrow = 'top';
        } else if (rect.left > coachWidth + gap + margin) {
          left = rect.left - coachWidth - gap;
          top = Math.min(viewportHeight - coachHeight - margin, Math.max(margin, rect.top - 10));
          arrow = 'right';
        } else if (rect.right + coachWidth + gap < viewportWidth - margin) {
          left = rect.right + gap;
          top = Math.min(viewportHeight - coachHeight - margin, Math.max(margin, rect.top - 10));
          arrow = 'left';
        }
      }

      setCoachPosition({ top, left, right: null, arrow });
    };

    const schedulePlace = () => {
      window.cancelAnimationFrame(frameId);
      frameId = window.requestAnimationFrame(placeCoach);
    };

    const target = readTarget();
    if (target && !current.target.startsWith('nav-')) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
      scrollTimer = window.setTimeout(schedulePlace, 420);
    } else {
      schedulePlace();
    }

    window.addEventListener('resize', schedulePlace);
    window.addEventListener('scroll', schedulePlace, true);

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(scrollTimer);
      window.removeEventListener('resize', schedulePlace);
      window.removeEventListener('scroll', schedulePlace, true);
    };
  }, [current?.target, isOpen]);

  if (!isOpen || !current) return null;

  return (
    <div className="pointer-events-none fixed inset-0 z-[9997] text-white" role="dialog" aria-live="polite" aria-label="Vision import walkthrough">
      <div
        ref={coachRef}
        style={{ position: 'fixed', top: coachPosition.top, left: coachPosition.left ?? 'auto', right: coachPosition.right ?? 'auto' }}
        className="w-[min(24rem,calc(100vw-2rem))] rounded-shell border border-cyan-300/55 bg-[#020817] p-4 shadow-[0_24px_90px_rgba(0,0,0,.88),0_0_0_1px_rgba(103,232,249,.18)]"
      >
        <div className={`absolute h-4 w-4 rotate-45 border-cyan-300/55 bg-[#020817] ${coachPosition.arrow === 'bottom' ? '-bottom-2 left-1/2 -translate-x-1/2 border-b border-r' : coachPosition.arrow === 'left' ? '-left-2 top-8 border-b border-l' : coachPosition.arrow === 'right' ? '-right-2 top-8 border-r border-t' : '-top-2 left-1/2 -translate-x-1/2 border-l border-t'}`} />
        <div className="relative">
          <div className="h-1 overflow-hidden rounded-full bg-slate-800">
            <div className="h-full rounded-full bg-cyan-300 transition-all" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-4 text-micro font-black uppercase tracking-[0.2em] text-cyan-300">Upload demo guide · {current.eyebrow}</div>
          <div className="mt-3 text-lg font-black leading-6 text-white">{current.title}</div>
          <div className="mt-3 text-sm leading-6 text-slate-300">{current.body}</div>
          <div className="mt-4 rounded-card border border-cyan-300/25 bg-cyan-950/70 p-3">
            <div className="text-micro font-black uppercase tracking-[0.18em] text-cyan-200">Do this now</div>
            <div className="mt-2 space-y-2">
              {current.press.map((item) => (
                <div key={item} className="flex gap-2 text-xs font-bold leading-5 text-slate-100">
                  <span className="text-cyan-300">✓</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div className={`mt-3 rounded-card border px-3 py-2 text-meta font-black ${isComplete ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-100' : 'border-amber-400/25 bg-amber-500/10 text-amber-100'}`}>
            {isComplete ? 'Step complete. Moving forward...' : 'Waiting for the highlighted app action.'}
          </div>
        </div>
      </div>
    </div>
  );
}
export default function App() {

  const auth = useAuth();
  const functionsClient = getFunctions(app, 'us-central1');
  const whopRedemptionAttempts = useRef(new Set());

  const [activeTab, setActiveTab] = useState(getInitialWorkspaceTab);

  const [profile, setProfile] = useState(DEFAULT_RECRUITER_PROFILE);
  const [agencyBrand, setAgencyBrand] = useState(DEFAULT_AGENCY_BRAND);
  const [agencyBrandSaving, setAgencyBrandSaving] = useState(false);
  const [agencyBrandMsg, setAgencyBrandMsg] = useState('');
  const [agencyLogoUploading, setAgencyLogoUploading] = useState(false);
  const [agencyLogoSuggestions, setAgencyLogoSuggestions] = useState([]);

  const [candidates, setCandidates] = useState([]);

  const [candidateReviewQueue, setCandidateReviewQueue] = useState([]);

  const [jobs, setJobs] = useState(INITIAL_JOBS);

  const [jobPortfolioCleanupDraft, setJobPortfolioCleanupDraft] = useState(null);
  const [removeJobConfirmId, setRemoveJobConfirmId] = useState('');

  const [linkedInBackfillRequested, setLinkedInBackfillRequested] = useState(false);

  const [isLoadingWorkspace, setIsLoadingWorkspace] = useState(true);
  const [ownerIntroCompleted, setOwnerIntroCompleted] = useState(true);
  const [ownerIntroStep, setOwnerIntroStep] = useState(0);
  const [ownerIntroPreparing, setOwnerIntroPreparing] = useState(false);
  const [ownerIntroSaving, setOwnerIntroSaving] = useState(false);

  const [draggedCandidateId, setDraggedCandidateId] = useState(null);

  const [dragOverStageId, setDragOverStageId] = useState(null);

  const [pipelineNotice, setPipelineNotice] = useState('');

  const [pipelineView, setPipelineView] = useState('board');

  const [pipelineSortKey, setPipelineSortKey] = useState('stage');

  const [pipelineFilters, setPipelineFilters] = useState({ stage: '', company: '', role: '' });

  const [isImporting, setIsImporting] = useState(false);

  const [importSummary, setImportSummary] = useState([]);

  const [screeningResponses, setScreeningResponses] = useState({});

  const [screeningLinks, setScreeningLinks] = useState({});

  const [isSavingScreeningQuestions, setIsSavingScreeningQuestions] = useState(false);

  const [activityLog, setActivityLog] = useState([]);

  const [agencyDetails, setAgencyDetails] = useState(null);
  const [agencyListenersKey, setAgencyListenersKey] = useState(0);
  const agencyPermissionRetries = useRef(0);

  const [agencyMembers, setAgencyMembers] = useState([]);
  const [teamMessages, setTeamMessages] = useState([]);
  const [teamMessageDraft, setTeamMessageDraft] = useState('');
  const [teamMessageSending, setTeamMessageSending] = useState(false);
  const [segmentAxis, setSegmentAxis] = useState('status');
  const [tthView, setTthView] = useState('stages');

  const [timeManagement, setTimeManagement] = useState(DEFAULT_TIME_MANAGEMENT);

  const [agencySettings, setAgencySettings] = useState({

    seatLimit: '5',

    billingContactEmail: '',

    reportingScheduleEnabled: false,

    reportingScheduleFrequency: 'weekly',

    reportingScheduleDay: 'monday',

    reportingScheduleRecipients: '',

    forecastMonthlyHireTarget: '10',

    agingThresholdDays: '14',

    costPerHireBudget: '0',

    monthlyRevenueTarget: '0',

    biExportEnabled: true,

    webhookUrl: '',
    webhookSecret: '',
    webhookEvents: [],
    lawfulUseAccepted: false,

    governanceAgentMetricKeys: ['candidate_changes', 'job_changes', 'stage_moves', 'screenings_done', 'time_to_fill', 'offer_acceptance', 'funnel_overview', 'velocity_overview', 'source_quality_overview', 'source_trend_wow', 'conversion_rates', 'dei_funnel', 'agency_performance', 'segmentation'],
    ...DEFAULT_GOVERNANCE_CONTROLS,

  });

  const [agencyNotice, setAgencyNotice] = useState('');

  const [agencySection, setAgencySection] = useState('profile');
  const [migrationSource, setMigrationSource] = useState('bullhorn');
  const [migrationEntity, setMigrationEntity] = useState('candidates');
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrationBusy, setMigrationBusy] = useState(false);
  const [migrationNotice, setMigrationNotice] = useState('');
  const [migrationBatches, setMigrationBatches] = useState([]);
  const [migrationFileName, setMigrationFileName] = useState('');

  const handleSetAgencySection = (section) => {
    setAgencySection(section);
    window.setTimeout(() => {
      try {
        document.getElementById('agency-section-body')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (_error) {}
    }, 0);
  };

  const [launchCheckResults, setLaunchCheckResults] = useState([]);

  const [isRunningLaunchChecks, setIsRunningLaunchChecks] = useState(false);

  const [supportRequestDraft, setSupportRequestDraft] = useState({ type: 'setup', priority: 'normal', message: '' });

  const [supportRequestState, setSupportRequestState] = useState({ status: 'idle', message: '' });

  const [reportingRangeDays, setReportingRangeDays] = useState(30);

  const [reportingRecruiterFilter, setReportingRecruiterFilter] = useState('all');
  const [biSnapshotSaveStatus, setBiSnapshotSaveStatus] = useState('idle');

  const [reportingUseCustomRange, setReportingUseCustomRange] = useState(false);

  const [reportingCustomStart, setReportingCustomStart] = useState('');

  const [reportingCustomEnd, setReportingCustomEnd] = useState('');

  const [reportingCompareEnabled, setReportingCompareEnabled] = useState(true);

  const [reportingKpiDrawerKey, setReportingKpiDrawerKey] = useState('');

  const [reportingChartHoverIdx, setReportingChartHoverIdx] = useState(null);

  const [customReportOpen, setCustomReportOpen] = useState(false);

  const [customReportSections, setCustomReportSections] = useState(['kpis', 'pipeline', 'sources', 'activity', 'forecast', 'aging', 'scorecards']);

  const [jobPortfolioNotice, setJobPortfolioNotice] = useState('');

  const [jobPortfolioFilters, setJobPortfolioFilters] = useState({ search: '', status: '', type: '', company: '', owner: '', stage: '', sort: 'newest' });

  const [isClientsBoardOpen, setIsClientsBoardOpen] = useState(false);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [clientCrmSearch, setClientCrmSearch] = useState('');
  const [clientCrmSelectedIds, setClientCrmSelectedIds] = useState(new Set());
  const [clientCrmDeleteConfirm, setClientCrmDeleteConfirm] = useState(false);
  const [clientCrmDeleting, setClientCrmDeleting] = useState(false);
  const [clientOpportunities, setClientOpportunities] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [applications, setApplications] = useState([]);
  const [clientOpportunityForm, setClientOpportunityForm] = useState(DEFAULT_CLIENT_OPPORTUNITY_FORM);
  const [clientCrmNotice, setClientCrmNotice] = useState('');
  const [fetchingLogoClientId, setFetchingLogoClientId] = useState(null);
  const [isSavingClientOpportunity, setIsSavingClientOpportunity] = useState(false);
  const [isSavingTimelineEntry, setIsSavingTimelineEntry] = useState(false);
  const [isSavingCompanyContact, setIsSavingCompanyContact] = useState(false);
  const [clientResponseEmailState, setClientResponseEmailState] = useState({});
  const [crmTasks, setCrmTasks] = useState([]);
  const [isSavingCrmTask, setIsSavingCrmTask] = useState(false);
  // ── CRM Automation ───────────────────────────────────────────────────────
  const [crmAutomationRules, setCrmAutomationRules] = useState([]);
  const [crmCadenceEnrollments, setCrmCadenceEnrollments] = useState([]);
  const [automationDismissed, setAutomationDismissed] = useState([]);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [showCrmAutomationPanel, setShowCrmAutomationPanel] = useState(false);
  const [crmEmailTracking, setCrmEmailTracking] = useState([]);
  const [smtpSettings, setSmtpSettings] = useState(null); // null = not loaded, {} = not configured
  const [smtpSettingsOpen, setSmtpSettingsOpen] = useState(false);
  const [smtpDraft, setSmtpDraft] = useState({ host: '', port: '587', secure: false, user: '', pass: '', fromName: '', fromEmail: '' });
  const [smtpSaving, setSmtpSaving] = useState(false);
  const [smtpTestSending, setSmtpTestSending] = useState(false);
  const [smtpNotice, setSmtpNotice] = useState('');
  const [notifications, setNotifications] = useState([]);
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [gmailIntegrationStatus, setGmailIntegrationStatus] = useState({ loading: false, ready: false, connected: false, pendingVerification: true, emailAddress: '', lastSyncAtIso: '' });
  const [gmailIntegrationBusy, setGmailIntegrationBusy] = useState(false);
  const [websiteLeads, setWebsiteLeads] = useState([]);
  const [websiteLeadsLoading, setWebsiteLeadsLoading] = useState(false);
  const [websiteLeadsLoaded, setWebsiteLeadsLoaded] = useState(false);
  const [websiteLeadsError, setWebsiteLeadsError] = useState('');
  const [billingRequests, setBillingRequests] = useState([]);
  const [billingRequestsLoading, setBillingRequestsLoading] = useState(false);
  const [billingRequestsLoaded, setBillingRequestsLoaded] = useState(false);
  const [billingRequestPayflexInputs, setBillingRequestPayflexInputs] = useState({});
  const [billingRequestSendingId, setBillingRequestSendingId] = useState(null);
  const [billingRequestsNotice, setBillingRequestsNotice] = useState('');
  // ── Contacts database: advanced search, saved views, tags ───────────────
  const [contactDbOpen, setContactDbOpen] = useState(false);
  const [contactDbSearch, setContactDbSearch] = useState('');
  const [contactDbFilters, setContactDbFilters] = useState({ company: '', role: '', sentiment: '', tag: '', source: '' });
  const [contactDbSort, setContactDbSort] = useState('name');
  const [savedContactViews, setSavedContactViews] = useState([]);
  const [saveViewModalOpen, setSaveViewModalOpen] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewTarget, setSaveViewTarget] = useState('contacts');
  const [tagEditContactId, setTagEditContactId] = useState(null);
  const [tagEditInput, setTagEditInput] = useState('');
  const [candidateDbOpen, setCandidateDbOpen] = useState(false);
  const [candidateDbSearch, setCandidateDbSearch] = useState('');
  const [candidateDbFilters, setCandidateDbFilters] = useState({ company: '', role: '', status: '', tag: '', source: '' });
  const [candidateDbSort, setCandidateDbSort] = useState('name');
  const [savedCandidateViews, setSavedCandidateViews] = useState([]);
  const [candidateDbSection, setCandidateDbSection] = useState('talent-bank');
  const [talentLists, setTalentLists] = useState([]);
  const [talentListDraft, setTalentListDraft] = useState({ name: '', description: '' });
  const [selectedTalentListId, setSelectedTalentListId] = useState('');
  const [candidateOwnerDrafts, setCandidateOwnerDrafts] = useState({});
  const [tagEditCandidateId, setTagEditCandidateId] = useState(null);
  const [tagEditCandidateInput, setTagEditCandidateInput] = useState('');
  // ── Deduplication ────────────────────────────────────────────────────────
  const [dedupOpen, setDedupOpen] = useState(false);
  const [dedupMergeModal, setDedupMergeModal] = useState(null); // { primary, secondary, entity: 'contacts'|'candidates', score }
  const [dedupMergeFieldChoices, setDedupMergeFieldChoices] = useState({}); // { fieldKey: 'primary'|'secondary' }
  const [dedupIsMerging, setDedupIsMerging] = useState(false);
  const [dedupDismissed, setDedupDismissed] = useState([]); // array of 'id1__id2' strings dismissed by user
  const [websiteLeadFilters, setWebsiteLeadFilters] = useState({ search: '', teamSize: '' });
  const [websiteLeadActionState, setWebsiteLeadActionState] = useState({});
  const [websiteLeadActionNotice, setWebsiteLeadActionNotice] = useState('');

  const [sourcingInbox, setSourcingInbox] = useState([]);
  const DEFAULT_AUTOMATION_SETTINGS = {
    leadFollowUp: { enabled: false, followUpDays: [3, 7, 14] },
    candidateAging: { enabled: false, stageThresholdDays: 21 },
    screeningReminder: { enabled: false, reminderDays: [2, 5] },
    clientReengagement: { enabled: false, dormantDays: 30 },
    weeklyDigest: { enabled: false },
    jobAutoClose: { enabled: false, inactiveDays: 60 },
    autoQualify: { enabled: false, scoreThreshold: 70 },
    autoRejectFeedback: { enabled: false, scoreThreshold: 40 },
    createAllFromLead: { enabled: false },
  };

  const LOCKED_AUTOMATION_FEATURES = new Set(['autoQualify', 'autoRejectFeedback']);

  const [automationSettings, setAutomationSettings] = useState(DEFAULT_AUTOMATION_SETTINGS);
  const [automationSettingsSaving, setAutomationSettingsSaving] = useState(false);
  const [automationSettingsLoaded, setAutomationSettingsLoaded] = useState(false);
  const [automationSettingsNotice, setAutomationSettingsNotice] = useState('');


  const [externalProfiles, setExternalProfiles] = useState([]);

  const [sequenceRuns, setSequenceRuns] = useState([]);

  const [sequenceTasks, setSequenceTasks] = useState([]);

  const [sequenceSuppressions, setSequenceSuppressions] = useState([]);

  const [sequenceControls, setSequenceControls] = useState(DEFAULT_SEQUENCE_CONTROLS);

  const [sequenceTaskNotes, setSequenceTaskNotes] = useState({});

  const [sequenceNotice, setSequenceNotice] = useState('');

  const [activeSequenceTargetKey, setActiveSequenceTargetKey] = useState('');

  const [activeSequenceTaskId, setActiveSequenceTaskId] = useState('');

  const [activeSequenceApiTaskId, setActiveSequenceApiTaskId] = useState('');

  const [activeSequenceSuppressionId, setActiveSequenceSuppressionId] = useState('');

  const [isProcessingSequenceRuns, setIsProcessingSequenceRuns] = useState(false);

  const [isSavingSequenceControls, setIsSavingSequenceControls] = useState(false);

  const [isUpdatingAgencySettings, setIsUpdatingAgencySettings] = useState(false);

  const [isSavingTimeManagement, setIsSavingTimeManagement] = useState(false);

  const [billingRequestPlanId, setBillingRequestPlanId] = useState(null);

  const [billingRequestSent, setBillingRequestSent] = useState(false);

  const [activeMemberActionId, setActiveMemberActionId] = useState('');

  const [isLoadingScreeningCandidate, setIsLoadingScreeningCandidate] = useState(false);

  const [isSubmittingScreening, setIsSubmittingScreening] = useState(false);

  const [isSavingProfile, setIsSavingProfile] = useState(false);

  const [isSavingJob, setIsSavingJob] = useState(false);

  const [isSavingSourceLead, setIsSavingSourceLead] = useState(false);

  const [isRankingSourceLeads, setIsRankingSourceLeads] = useState(false);

  const [selectedScreeningCandidateId, setSelectedScreeningCandidateId] = useState(null);

  const [selectedJobId, setSelectedJobId] = useState('');

  const [selectedJobPanelId, setSelectedJobPanelId] = useState('');

  const [jobNotesDraft, setJobNotesDraft] = useState('');

  const [isSavingJobNotes, setIsSavingJobNotes] = useState(false);
  const [isSavingEeoc, setIsSavingEeoc] = useState(false);
  const [offerDeclineCandidateId, setOfferDeclineCandidateId] = useState(null);
  const [offerDeclineCategory, setOfferDeclineCategory] = useState('');
  const [offerDeclineNote, setOfferDeclineNote] = useState('');
  const [isSavingOfferDecline, setIsSavingOfferDecline] = useState(false);
  const [rejectPipelineCandidateId, setRejectPipelineCandidateId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectSendEmail, setRejectSendEmail] = useState(false);
  const [rejectEmailBody, setRejectEmailBody] = useState('');
  const [isSavingReject, setIsSavingReject] = useState(false);
  const [sendOfferCandidateId, setSendOfferCandidateId] = useState(null);
  const [offerError, setOfferError] = useState('');
  const [offerDraft, setOfferDraft] = useState({
    amount: '',
    currency: 'USD',
    sentDate: '',
    expiryDate: '',
    note: '',
    clientEmail: '',
    clientName: '',
    clientLegalName: '',
    clientAddress: '',
    startDate: '',
    workLocation: '',
    employmentType: 'Permanent',
    workingHours: '',
    payFrequency: 'Monthly',
    benefits: '',
    leaveTerms: '',
    overtimeTerms: '',
    deductions: '',
    noticeTerms: '',
    sectorTerms: '',
    customNegotiation: '',
    emailBody: '',
  });
  const [isSavingOffer, setIsSavingOffer] = useState(false);
  const [isDraftingOfferEmail, setIsDraftingOfferEmail] = useState(false);
  const [submittalCandidateId, setSubmittalCandidateId] = useState(null);
  const [submittalClientName, setSubmittalClientName] = useState('');
  const [submittalContactId, setSubmittalContactId] = useState('');
  const [submittalContactName, setSubmittalContactName] = useState('');
  const [submittalJobId, setSubmittalJobId] = useState('');
  const [submittalSubmittedBy, setSubmittalSubmittedBy] = useState('');
  const [submittalOutcome, setSubmittalOutcome] = useState('pending');
  const [submittalFeedback, setSubmittalFeedback] = useState('');
  const [submittalNote, setSubmittalNote] = useState('');
  const [isSavingSubmittal, setIsSavingSubmittal] = useState(false);
  const [eeocEditCandidateId, setEeocEditCandidateId] = useState(null);
  // -- Distribute job modal --
  const [distributeJob, setDistributeJob] = useState(null);
  const [distributeCopied, setDistributeCopied] = useState('');
  const [indexingStatus, setIndexingStatus] = useState('idle'); // idle | submitting | done | partial | skipped | error
  const [indexingResults, setIndexingResults] = useState([]);
  const [eeocDraft, setEeocDraft] = useState({});
  const [deiTab, setDeiTab] = useState('passrate');
  const [pivotMetric, setPivotMetric] = useState('count');
  const [pivotRowDim, setPivotRowDim] = useState('source');
  const [pivotColDim, setPivotColDim] = useState('status');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [webhookPanelOpen, setWebhookPanelOpen] = useState(false);
  const [isSavingWebhook, setIsSavingWebhook] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [candidateJobMatchResults, setCandidateJobMatchResults] = useState({});
  const [candidateNoteDraft, setCandidateNoteDraft] = useState('');
  const [isSavingCandidateNote, setIsSavingCandidateNote] = useState(false);
  const [placementCandidateId, setPlacementCandidateId] = useState(null);
  const [placementDraft, setPlacementDraft] = useState(DEFAULT_PLACEMENT_DRAFT);
  const [isSavingPlacement, setIsSavingPlacement] = useState(false);
  const [placements, setPlacements] = useState([]);
  const [revenueInvoiceEditId, setRevenueInvoiceEditId] = useState(null);
  const [revenueInvoiceDraft, setRevenueInvoiceDraft] = useState({});
  const [isSavingRevenueInvoice, setIsSavingRevenueInvoice] = useState(false);
  const [isTestingWebhook, setIsTestingWebhook] = useState(false);
  const [webhookTestResult, setWebhookTestResult] = useState(null);
  const [funnelStageIdx, setFunnelStageIdx] = useState(-1);
  const [qohEditCandidateId, setQohEditCandidateId] = useState(null);
  const [qohDraft, setQohDraft] = useState({ score90: '', score180: '', notes: '' });
  const [isSavingQoh, setIsSavingQoh] = useState(false);
  const [savedReports, setSavedReports] = useState([]);
  const [savedReportDraft, setSavedReportDraft] = useState({ name: '', sections: ['kpis', 'pipeline', 'sources', 'activity', 'forecast', 'aging', 'scorecards'], schedule: { enabled: false, frequency: 'weekly', day: 'monday', recipients: '' } });
  const [savedReportEditId, setSavedReportEditId] = useState(null);
  const [savedReportsPanelOpen, setSavedReportsPanelOpen] = useState(false);
  const [isSavingSavedReport, setIsSavingSavedReport] = useState(false);
  const [savedReportCopied, setSavedReportCopied] = useState('');
  // -- Scheduled email digest system --
  const [reportSchedules, setReportSchedules] = useState([]);
  const [schedulesPanelOpen, setSchedulesPanelOpen] = useState(false);
  const [scheduleDraft, setScheduleDraft] = useState(null);
  const [scheduleEditId, setScheduleEditId] = useState(null);
  const [isSavingSchedule, setIsSavingSchedule] = useState(false);
  const [runningScheduleId, setRunningScheduleId] = useState(null);
  const [scheduleRunResult, setScheduleRunResult] = useState({});
  const [schedulesLoading, setSchedulesLoading] = useState(false);

  const [booleanSearchInput, setBooleanSearchInput] = useState('');

  const [jobForm, setJobForm] = useState(DEFAULT_JOB_FORM);
  const [jobFormErrors, setJobFormErrors] = useState({});
  const [isSuggestingQuestions, setIsSuggestingQuestions] = useState(false);

  const [sourceLeadForm, setSourceLeadForm] = useState(DEFAULT_SOURCE_LEAD_FORM);

  const [scrapedJobSearch, setScrapedJobSearch] = useState(DEFAULT_SCRAPED_JOB_SEARCH);

  const [scrapedJobs, setScrapedJobs] = useState([]);

  const [selectedScrapedJobId, setSelectedScrapedJobId] = useState('');

  const [outreachTone, setOutreachTone] = useState('formal');

  const [scrapedJobsStatus, setScrapedJobsStatus] = useState('idle');

  const [isScrapingJobs, setIsScrapingJobs] = useState(false);

  const [rankedSourceLeads, setRankedSourceLeads] = useState([]);

  const [sourcingRefreshNonce, setSourcingRefreshNonce] = useState(0);

  const [authMode, setAuthMode] = useState('signin');

  const [authForm, setAuthForm] = useState({

    name: '',

    agency: '',

    location: '',

    agencySecret: '',

    accountRole: 'owner',

    email: '',

    password: '',

    lawfulUseAccepted: false,

  });

  const [authError, setAuthError] = useState('');

  const [isSubmittingAuth, setIsSubmittingAuth] = useState(false);

  const [authOtpVerified, setAuthOtpVerified] = useState(false);
  const [authOtpCheckDone, setAuthOtpCheckDone] = useState(false);

  const [authOtpCode, setAuthOtpCode] = useState('');

  const [authOtpError, setAuthOtpError] = useState('');

  const [authOtpNotice, setAuthOtpNotice] = useState('');

  const [isVerifyingAuthOtp, setIsVerifyingAuthOtp] = useState(false);

  const [isResendingAuthOtp, setIsResendingAuthOtp] = useState(false);

  const [membershipPreviewEnabled, setMembershipPreviewEnabled] = useState(false);
  const [demoWorkspaceActive, setDemoWorkspaceActive] = useState(false);
  const [demoTabSwitchCount, setDemoTabSwitchCount] = useState(0);
  const [demoAccountAdOpen, setDemoAccountAdOpen] = useState(false);
  const [directDemoConfettiOpen, setDirectDemoConfettiOpen] = useState(false);
  const [postDemoFeaturePromptOpen, setPostDemoFeaturePromptOpen] = useState(false);
  const [postDemoFeatureDetailsOpen, setPostDemoFeatureDetailsOpen] = useState(false);
  const [directDemoWalkthroughOpen, setDirectDemoWalkthroughOpen] = useState(false);
  const [directDemoWalkthroughStep, setDirectDemoWalkthroughStep] = useState(0);
  const [directDemoCompletedSteps, setDirectDemoCompletedSteps] = useState([]);
  const [importDemoWalkthroughOpen, setImportDemoWalkthroughOpen] = useState(false);
  const [importDemoWalkthroughStep, setImportDemoWalkthroughStep] = useState(0);
  const [importDemoCompletedSteps, setImportDemoCompletedSteps] = useState([]);
  const [directDemoBaseline, setDirectDemoBaseline] = useState({ jobs: 0, candidates: 0, placements: 0 });
  const markDirectDemoStepComplete = (stepIndex) => {
    setDirectDemoCompletedSteps((current) => current.includes(stepIndex) ? current : [...current, stepIndex]);
    if (directDemoWalkthroughOpen) {
      window.setTimeout(() => {
        setDirectDemoWalkthroughStep((currentStep) => {
          if (currentStep !== stepIndex) return currentStep;
          if (currentStep >= 10) {
            setDirectDemoWalkthroughOpen(false);
            setDirectDemoConfettiOpen(true);
            window.setTimeout(() => {
              setDirectDemoConfettiOpen(false);
              setPostDemoFeaturePromptOpen(true);
            }, 1450);
            return currentStep;
          }
          return currentStep + 1;
        });
      }, 650);
    }
  };

  const markImportDemoStepComplete = (stepIndex) => {
    setImportDemoCompletedSteps((current) => current.includes(stepIndex) ? current : [...current, stepIndex]);
    if (importDemoWalkthroughOpen) {
      window.setTimeout(() => {
        setImportDemoWalkthroughStep((currentStep) => {
          if (currentStep !== stepIndex) return currentStep;
          if (currentStep >= 10) {
            setImportDemoWalkthroughOpen(false);
            setDirectDemoConfettiOpen(true);
            window.setTimeout(() => {
              setDirectDemoConfettiOpen(false);
              setPostDemoFeaturePromptOpen(true);
            }, 1450);
            return currentStep;
          }
          return currentStep + 1;
        });
      }, 650);
    }
  };

  const [screeningLinkId, setScreeningLinkId] = useState(() => {

    if (typeof window === 'undefined') return '';

    return new URLSearchParams(window.location.search).get('screening') || '';

  });

  const [jobIntakeLinkId, setJobIntakeLinkId] = useState(() => {

    if (typeof window === 'undefined') return '';

    return new URLSearchParams(window.location.search).get('job-intake') || '';

  });

  const [candidateJobIntakeLinkId, setCandidateJobIntakeLinkId] = useState(() => {

    if (typeof window === 'undefined') return '';

    return new URLSearchParams(window.location.search).get('candidate-intake') || '';

  });

  // ── Shortlist portal ─────────────────────────────────────────
  const [shortlistLinkId, setShortlistLinkId] = useState(() => {
    if (typeof window === 'undefined') return '';
    return new URLSearchParams(window.location.search).get('shortlist') || '';
  });
  const [shortlistModalOpen, setShortlistModalOpen] = useState(false);
  const [shortlistDraft, setShortlistDraft] = useState({ clientName: '', jobTitle: '', candidateIds: [] });
  const [shortlistSaving, setShortlistSaving] = useState(false);
  const [shortlistCreatedUrl, setShortlistCreatedUrl] = useState('');
  const [shortlistLinkCopied, setShortlistLinkCopied] = useState(false);
  // ── Demo data removal ────────────────────────────────────────
  const [isDeletingAllCandidates, setIsDeletingAllCandidates] = useState(false);

  const [publicJobIntakeLink, setPublicJobIntakeLink] = useState(null);

  const [publicCandidateJobIntakeLink, setPublicCandidateJobIntakeLink] = useState(null);

  const [clientJobForm, setClientJobForm] = useState(DEFAULT_CLIENT_JOB_FORM);

  const [candidateApplicationForm, setCandidateApplicationForm] = useState(DEFAULT_CANDIDATE_APPLICATION_FORM);

  const [candidateScreeningAnswers, setCandidateScreeningAnswers] = useState({});

  const [candidateCvFile, setCandidateCvFile] = useState(null);

  const [clientJobFormNotice, setClientJobFormNotice] = useState('');

  const [candidateApplicationNotice, setCandidateApplicationNotice] = useState('');

  const [isSubmittingClientJobForm, setIsSubmittingClientJobForm] = useState(false);

  const [isSubmittingCandidateApplication, setIsSubmittingCandidateApplication] = useState(false);

  const [hasSubmittedClientJobForm, setHasSubmittedClientJobForm] = useState(false);

  const [hasSubmittedCandidateApplication, setHasSubmittedCandidateApplication] = useState(false);

  const [selectedPublicCandidateJobId, setSelectedPublicCandidateJobId] = useState('');



  const rawRecruiterUser = auth?.user && !auth.user.isAnonymous ? auth.user : null;
  const isDemoWorkspace = demoWorkspaceActive;
  const demoRecruiterUser = isDemoWorkspace ? {
    uid: '',
    email: 'demo@vision.local',
    isDemo: true,
  } : null;
  const authOtpSessionKey = rawRecruiterUser?.uid ? `vision-auth-otp:${rawRecruiterUser.uid}` : '';
  const recruiterUser = isDemoWorkspace ? demoRecruiterUser : (authOtpVerified ? rawRecruiterUser : null);
  const currentAgencyMember = agencyMembers.find((member) => member.id === recruiterUser?.uid);
  const currentWorkspaceRole = normalizeWorkspaceRole(currentAgencyMember?.role || profile.accountRole);
  const currentWorkspacePermissions = getRolePermissions(currentWorkspaceRole, currentAgencyMember?.customPermissions);
  const canAccess = (permission) => hasPermission(currentWorkspaceRole, permission, currentAgencyMember?.customPermissions);
  const requireAccess = (permission, message = 'You do not have permission to perform this action.') => {
    if (canAccess(permission)) return true;
    setAgencyNotice(message);
    setPipelineNotice(message);
    setJobPortfolioNotice(message);
    return false;
  };
  const hasAcceptedLawfulUse = Boolean(profile.lawfulUseAccepted || agencyDetails?.compliance?.lawfulUseAccepted);

  useEffect(() => {
    if (!rawRecruiterUser?.uid) {
      setAuthOtpVerified(false);
      setAuthOtpCheckDone(true);
      setAuthOtpCode('');
      setAuthOtpError('');
      setAuthOtpNotice('');
      return;
    }
    try {
      setAuthOtpVerified(window.sessionStorage.getItem(`vision-auth-otp:${rawRecruiterUser.uid}`) === 'verified');
    } catch (_error) {
      setAuthOtpVerified(false);
    }
    setAuthOtpCheckDone(true);
    setAuthOtpCode('');
    setAuthOtpError('');
  }, [rawRecruiterUser?.uid]);

  const loadWebsiteLeads = async (force = false) => {
    if (!recruiterUser?.uid || websiteLeadsLoading) return;
    if (!force && websiteLeadsLoaded) return;

    setWebsiteLeadsLoading(true);
    setWebsiteLeadsError('');
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const getWebsiteLeads = httpsCallable(functionsClient, 'getWebsiteLeads');
      const result = await getWebsiteLeads({ appId, limit: 200 });
      const nextLeads = Array.isArray(result?.data?.leads) ? result.data.leads : [];
      setWebsiteLeads(nextLeads);
      setWebsiteLeadsLoaded(true);
    } catch (error) {
      setWebsiteLeadsError(error?.message || 'Failed to load website leads.');
    } finally {
      setWebsiteLeadsLoading(false);
    }
  };

  const loadBillingRequests = async (force = false) => {
    if (!recruiterUser?.uid || billingRequestsLoading) return;
    if (!force && billingRequestsLoaded) return;
    setBillingRequestsLoading(true);
    setBillingRequestsNotice('');
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const fn = httpsCallable(functionsClient, 'getBillingRequests');
      const result = await fn({ appId });
      setBillingRequests(Array.isArray(result?.data?.requests) ? result.data.requests : []);
      setBillingRequestsLoaded(true);
    } catch (err) {
      setBillingRequestsNotice(err?.message || 'Failed to load billing requests.');
    } finally {
      setBillingRequestsLoading(false);
    }
  };

  const handleSendPayflexInvoice = async (request) => {
    const payflexUrl = String(billingRequestPayflexInputs[request.id] || '').trim();
    if (!payflexUrl) {
      setBillingRequestsNotice('Paste the PayFlex link before sending.');
      return;
    }
    setBillingRequestSendingId(request.id);
    setBillingRequestsNotice('');
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const fn = httpsCallable(functionsClient, 'sendPayflexInvoiceToClient');
      const result = await fn({ appId, billingRequestId: request.id, payflexUrl });
      setBillingRequestsNotice(result?.data?.message || 'Invoice sent.');
      setBillingRequests((prev) => prev.map((r) => r.id === request.id ? { ...r, status: 'invoice_sent', payflexUrl } : r));
      setBillingRequestPayflexInputs((prev) => { const next = { ...prev }; delete next[request.id]; return next; });
    } catch (err) {
      setBillingRequestsNotice(err?.message || 'Failed to send invoice.');
    } finally {
      setBillingRequestSendingId(null);
    }
  };

    const loadAutomationSettings = async () => {
      if (automationSettingsLoaded || !recruiterUser?.uid) return;
      try {
        const functionsClient = getFunctions(app, 'us-central1');
        const fn = httpsCallable(functionsClient, 'getAutomationSettings');
        const result = await fn({ appId });
        if (result?.data?.settings && typeof result.data.settings === 'object') {
          setAutomationSettings({ ...DEFAULT_AUTOMATION_SETTINGS, ...result.data.settings });
        }
      } catch (err) {
        console.warn('[loadAutomationSettings]', err?.message);
      } finally {
        setAutomationSettingsLoaded(true);
      }
    };

    const saveAutomationSettingsFn = async () => {
      if (!recruiterUser?.uid) return;
      setAutomationSettingsSaving(true);
      setAutomationSettingsNotice('');
      try {
        const functionsClient = getFunctions(app, 'us-central1');
        const fn = httpsCallable(functionsClient, 'saveAutomationSettings');
        await fn({ appId, settings: automationSettings });
        setAutomationSettingsNotice('Settings saved successfully.');
        setTimeout(() => setAutomationSettingsNotice(''), 3000);
      } catch (err) {
        setAutomationSettingsNotice('Failed to save settings. Please try again.');
      } finally {
        setAutomationSettingsSaving(false);
      }
    };

  const handleWebsiteLeadAction = async (lead, action) => {
    if (!lead?.id || !recruiterUser?.uid) return;
    const actionKey = `${lead.id}:${action}`;
    if (websiteLeadActionState[actionKey]) return;

    const ownerMember = agencyMembers.find((member) => member.id === (agencyDetails?.ownerUid || '')) || agencyMembers.find((member) => member.role === 'owner');
    const ownerUid = ownerMember?.id || agencyDetails?.ownerUid || recruiterUser.uid;
    const ownerName = ownerMember?.name || ownerMember?.email || profile.name || 'Agency owner';

    setWebsiteLeadActionNotice('');
    setWebsiteLeadActionState((current) => ({ ...current, [actionKey]: true }));

    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const applyWebsiteLeadAction = httpsCallable(functionsClient, 'applyWebsiteLeadAction');
      const payload = {
        appId,
        leadId: lead.id,
        action,
        ownerUid,
        ownerName,
      };
      const response = await applyWebsiteLeadAction(payload);
      setWebsiteLeadActionNotice(response?.data?.message || 'Lead workflow action completed.');
      await loadWebsiteLeads(true);
      if ((action === 'create_job' || action === 'create_all') && response?.data?.jobId) {
        setSelectedJobId(response.data.jobId);
        setSelectedJobPanelId(response.data.jobId);
      }
    } catch (error) {
      setWebsiteLeadActionNotice(error?.message || 'Failed to apply workflow action.');
    } finally {
      setWebsiteLeadActionState((current) => {
        const next = { ...current };
        delete next[actionKey];
        return next;
      });
    }
  };

  const handleClientOpportunityFormChange = (event) => {
    const { name, value } = event.target;
    setClientOpportunityForm((current) => ({ ...current, [name]: value }));
  };

  const handleConvertEmailToOpportunity = () => {
    const raw = clientOpportunityForm.sourceEmail || '';
    if (!raw.trim()) return;
    const emailMatch = raw.match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    const subjectMatch = raw.match(/subject:\s*(.+)/i);
    const companyFromDomain = emailMatch ? emailMatch[0].split('@')[1].split('.')[0] : '';
    const inferredTitle = subjectMatch ? subjectMatch[1].trim().slice(0, 80) : '';
    setClientOpportunityForm((current) => ({
      ...current,
      contactEmail: current.contactEmail || (emailMatch ? emailMatch[0] : ''),
      companyName: current.companyName || titleCaseWords(companyFromDomain || 'Prospect company'),
      title: current.title || inferredTitle || 'New job order opportunity',
      stage: current.stage === 'Lead' ? 'Qualified' : current.stage,
      requirements: current.requirements || raw.slice(0, 900),
    }));
    setClientCrmNotice('Email converted into an editable CRM opportunity draft.');
  };

  const persistClientOpportunities = async (nextOpportunities, notice = '') => {
    if (!recruiterUser?.uid) return;
    await setDoc(userDocRef(recruiterUser.uid), {
      clientOpportunities: nextOpportunities,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    setClientOpportunities(nextOpportunities);
    if (notice) setClientCrmNotice(notice);
  };

  const upsertCompanyAndContact = async ({ companyName, contactName = '', contactEmail = '', job = null, opportunity = null, source = 'manual' }) => {
    if (!recruiterUser?.uid || !companyName) return;
    const companyId = createCompanyRecordId(companyName);
    const email = extractEmailFromText(contactEmail || contactName);
    const resolvedContactName = String(contactName || '').replace(email, '').trim() || email || '';
    const nowIso = new Date().toISOString();

    await setDoc(recruiterCompanyDoc(recruiterUser.uid, companyId), {
      id: companyId,
      name: companyName,
      normalizedName: normalizeText(companyName),
      relationshipStatus: job?.relationshipStatus || opportunity?.stage || 'Tracked',
      source,
      lastSynthesizedAtIso: nowIso,
      updatedAt: serverTimestamp(),
      createdAtIso: nowIso,
      synthesisSignals: arrayUnion({
        type: job ? 'job_order' : opportunity ? 'opportunity' : 'manual',
        jobId: job?.id || opportunity?.jobId || '',
        opportunityId: opportunity?.id || '',
        title: job?.title || opportunity?.title || '',
        department: job?.department || opportunity?.jobType || 'General',
        capturedAtIso: nowIso,
      }),
    }, { merge: true });

    if (resolvedContactName || email) {
      const contactKey = email || resolvedContactName;
      const contactId = createContactRecordId(companyName, contactKey);
      await setDoc(recruiterContactDoc(recruiterUser.uid, contactId), {
        id: contactId,
        companyId,
        companyName,
        normalizedCompanyName: normalizeText(companyName),
        name: resolvedContactName || email,
        email,
        role: 'Hiring Manager',
        preferenceSignal: '',
        vibe: 'Balanced Vibe',
        sentiment: 'Passive',
        source,
        lastInteractionAtIso: job?.lastOutreach || opportunity?.updatedAtIso || opportunity?.createdAtIso || '',
        updatedAt: serverTimestamp(),
        createdAtIso: nowIso,
      }, { merge: true });
    }
  };

  const createJobOrderFromOpportunity = async (opportunity) => {
    if (!recruiterUser?.uid || !opportunity) return null;
    const resolvedTitle = opportunity.title || 'Client job order';
    const resolvedCompanyName = opportunity.companyName || 'Client';
    const nextJob = {
      id: `job-${Date.now()}`,
      agencyId: '',
      companyName: resolvedCompanyName,
      title: resolvedTitle,
      department: opportunity.jobType || 'General',
      type: opportunity.jobType || 'Full-time',
      applicants: 0,
      status: 'Active',
      location: opportunity.location || 'Remote',
      salary: opportunity.salary || 'Compensation on request',
      recruiter: profile.name || 'Recruiter',
      clientContact: opportunity.contactName || opportunity.contactEmail || '',
      relationshipStatus: 'Active client',
      lastOutreach: new Date().toISOString(),
      summary: `${resolvedTitle} opened from a Closed-Won CRM opportunity.`,
      requirements: [
        opportunity.requirements,
        opportunity.billingTerms ? `Billing terms: ${opportunity.billingTerms}` : '',
        opportunity.complianceNeeds ? `Compliance needs: ${opportunity.complianceNeeds}` : '',
      ].filter(Boolean).join('\n\n') || 'Requirements can be added later.',
      billingTerms: opportunity.billingTerms || '',
      complianceNeeds: opportunity.complianceNeeds || '',
      crmOpportunityId: opportunity.id,
      booleanQuery: createBooleanQueryFromJob({ title: resolvedTitle, companyName: resolvedCompanyName, requirements: opportunity.requirements || '' }),
      createdAtIso: new Date().toISOString(),
      customScreeningQuestions: [],
      sourceLabel: 'CRM Closed-Won opportunity',
    };
    const nextJobs = sanitizeJobPortfolio([nextJob, ...jobs], profile.agency || '');
    await setDoc(userDocRef(recruiterUser.uid), {
      jobPortfolio: nextJobs,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    await recordActivity(recruiterUser.uid, 'crm_closed_won_job_order_created', `${resolvedTitle} job order created from ${resolvedCompanyName} opportunity.`, { jobId: nextJob.id, company: resolvedCompanyName, opportunityId: opportunity.id });
    await upsertCompanyAndContact({
      companyName: resolvedCompanyName,
      contactName: opportunity.contactName || opportunity.contactEmail || '',
      contactEmail: opportunity.contactEmail || '',
      job: nextJob,
      opportunity,
      source: 'closed_won_opportunity',
    });
    setJobs(nextJobs);
    setSelectedJobId(nextJob.id);
    setSelectedJobPanelId(nextJob.id);
    setBooleanSearchInput(nextJob.booleanQuery);
    return nextJob;
  };

  const handleSaveClientOpportunity = async (event) => {
    event.preventDefault();
    if (!recruiterUser?.uid) return;
    setIsSavingClientOpportunity(true);
    setClientCrmNotice('');
    try {
      const fee = Number(String(clientOpportunityForm.fee || '').replace(/[^0-9.]/g, '')) || 0;
      const probability = getHistoricalPlacementProbability(clientOpportunityForm.jobType || clientOpportunityForm.title);
      const opportunity = {
        ...clientOpportunityForm,
        id: `opp-${Date.now()}`,
        fee,
        probability,
        weightedValue: Math.round(fee * probability),
        ownerUid: recruiterUser.uid,
        ownerName: profile.name || recruiterUser.email || 'Recruiter',
        createdAtIso: new Date().toISOString(),
        updatedAtIso: new Date().toISOString(),
      };
      const nextOpportunities = [opportunity, ...clientOpportunities];
      await persistClientOpportunities(nextOpportunities, `${opportunity.title || 'Opportunity'} saved to Client CRM.`);
      await upsertCompanyAndContact({
        companyName: opportunity.companyName,
        contactName: opportunity.contactName,
        contactEmail: opportunity.contactEmail,
        opportunity,
        source: 'opportunity_capture',
      });
      await recordActivity(recruiterUser.uid, 'crm_opportunity_created', `${opportunity.title || 'Opportunity'} created for ${opportunity.companyName || 'client'}.`, { company: opportunity.companyName, opportunityId: opportunity.id, weightedValue: opportunity.weightedValue });
      setClientOpportunityForm(DEFAULT_CLIENT_OPPORTUNITY_FORM);
    } catch (error) {
      console.error('Failed to save client opportunity', error);
      setClientCrmNotice('Failed to save client opportunity.');
    } finally {
      setIsSavingClientOpportunity(false);
    }
  };

  const handleMoveClientOpportunityStage = async (opportunityId, stage) => {
    const opportunity = clientOpportunities.find((item) => item.id === opportunityId);
    if (!opportunity || !recruiterUser?.uid) return;
    try {
      const normalizedStage = normalizeClientOpportunityStage(stage);
      let jobId = opportunity.jobId || '';
      if (normalizedStage === 'Closed-Won' && !jobId) {
        const createdJob = await createJobOrderFromOpportunity({ ...opportunity, stage: normalizedStage });
        jobId = createdJob?.id || '';
      }
      const nextOpportunities = clientOpportunities.map((item) => item.id === opportunityId ? {
        ...item,
        stage: normalizedStage,
        jobId,
        closedWonAtIso: normalizedStage === 'Closed-Won' ? (item.closedWonAtIso || new Date().toISOString()) : item.closedWonAtIso,
        closedLostAtIso: normalizedStage === 'Closed-Lost' ? (item.closedLostAtIso || new Date().toISOString()) : item.closedLostAtIso,
        updatedAtIso: new Date().toISOString(),
      } : item);
      await persistClientOpportunities(nextOpportunities, normalizedStage === 'Closed-Won' ? 'Closed-Won opportunity generated an ATS job order.' : 'Opportunity stage updated.');
      await recordActivity(recruiterUser.uid, 'crm_opportunity_stage_changed', `${opportunity.title || 'Opportunity'} moved to ${normalizedStage}.`, { company: opportunity.companyName, opportunityId, stage: normalizedStage, jobId });
    } catch (error) {
      console.error('Failed to move client opportunity', error);
      setClientCrmNotice('Failed to update opportunity stage.');
    }
  };

  const handleLogClientTouchpoint = async (client, type, note = null) => {
    if (!recruiterUser?.uid || !client?.companyName) return;
    const createdAtIso = new Date().toISOString();
    const touchpointLabel = type === 'email' ? 'Email' : type === 'meeting' ? 'Meeting' : 'Call';
    try {
      const nextJobs = jobs.map((job) => normalizeText(getJobCompanyName(job)) === normalizeText(client.companyName) ? { ...job, lastOutreach: createdAtIso } : job);
      await setDoc(userDocRef(recruiterUser.uid), {
        jobPortfolio: nextJobs,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      const logMessage = note ? `${touchpointLabel} — ${client.companyName}: ${note.slice(0, 200)}` : `${touchpointLabel} logged for ${client.companyName}.`;
      await recordActivity(recruiterUser.uid, `client_${type}_logged`, logMessage, { company: client.companyName, clientCompany: client.companyName, contact: client.hiringContact || '' });
      setJobs(nextJobs);
      setClientCrmNotice(`${touchpointLabel} logged. Account moved to recent outreach.`);
    } catch (error) {
      setClientCrmNotice('Failed to log client touchpoint.');
    }
  };

  const handleFetchClientLogo = async (client) => {
    if (!recruiterUser?.uid || !client?.companyName) return;
    const linkedInUrl = String(client.linkedInUrl || '').trim();
    if (!/linkedin\.com\/company\//i.test(linkedInUrl)) {
      setClientCrmNotice('Open the client\'s LinkedIn company page first so a direct company URL is on file.');
      return;
    }
    setFetchingLogoClientId(client.id);
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const fetchClientCompanyLogo = httpsCallable(functionsClient, 'fetchClientCompanyLogo');
      const response = await fetchClientCompanyLogo({ appId, companyName: client.companyName, linkedInUrl });
      setClientCrmNotice(response?.data?.companyLogoUrl
        ? `Logo fetched for ${client.companyName}.`
        : `No logo found on that LinkedIn page for ${client.companyName}.`);
    } catch (error) {
      setClientCrmNotice(error?.message || 'Failed to fetch logo from the LinkedIn scraper.');
    } finally {
      setFetchingLogoClientId(null);
    }
  };

  const handleSetAccountOwner = async (accountId, ownerName) => {
    if (!recruiterUser?.uid || !accountId) return;
    // persist owner on all matching opportunities for this company
    const portfolioClient = clientPortfolio.find((c) => c.id === accountId);
    if (!portfolioClient) return;
    const companyKey = normalizeText(portfolioClient.companyName);
    const nowIso = new Date().toISOString();
    const nextOpportunities = clientOpportunities.map((o) =>
      normalizeText(o.companyName || '') === companyKey
        ? { ...o, accountOwner: ownerName, updatedAtIso: nowIso }
        : o
    );
    const nextJobs = jobs.map((job) => normalizeText(getJobCompanyName(job)) === companyKey
      ? { ...job, accountOwner: ownerName, ownerName: ownerName || job.ownerName || '', updatedAtIso: nowIso }
      : job);
    await setDoc(userDocRef(recruiterUser.uid), { jobPortfolio: nextJobs, updatedAt: serverTimestamp() }, { merge: true });
    setJobs(nextJobs);
    await setDoc(recruiterCompanyDoc(recruiterUser.uid, createCompanyRecordId(portfolioClient.companyName)), {
      id: createCompanyRecordId(portfolioClient.companyName),
      name: portfolioClient.companyName,
      normalizedName: companyKey,
      accountOwnerName: ownerName || '',
      accountOwnerUpdatedAtIso: nowIso,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    await persistClientOpportunities(nextOpportunities, `Account owner set to ${ownerName || '(cleared)'}.`);
    await recordActivity(recruiterUser.uid, 'account_owner_changed', `Account owner for ${portfolioClient.companyName} set to ${ownerName || 'unassigned'}.`, { company: portfolioClient.companyName, accountId });
  };

  // ── CRM Automation rule handlers ─────────────────────────────────────────
  const handleSaveCrmAutomationRule = async (ruleData) => {
    if (!recruiterUser?.uid) return;
    const ruleId = `rule-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    await setDoc(recruiterCrmAutomationRuleDoc(recruiterUser.uid, ruleId), {
      ...ruleData,
      id: ruleId,
      createdAtIso: new Date().toISOString(),
    });
  };

  const handleDeleteCrmAutomationRule = async (ruleId) => {
    if (!recruiterUser?.uid || !ruleId) return;
    await deleteDoc(recruiterCrmAutomationRuleDoc(recruiterUser.uid, ruleId));
  };

  const handleToggleCrmAutomationRule = async (ruleId, enabled) => {
    if (!recruiterUser?.uid || !ruleId) return;
    await setDoc(recruiterCrmAutomationRuleDoc(recruiterUser.uid, ruleId), { enabled }, { merge: true });
  };

  const handleDismissAutomation = async (pendingId) => {
    const next = [...automationDismissed, pendingId];
    setAutomationDismissed(next);
    await setDoc(userDocRef(recruiterUser.uid), { automationDismissed: next, updatedAt: serverTimestamp() }, { merge: true });
  };

  const handleExecuteAutomation = async (pending) => {
    if (!recruiterUser?.uid || !pending) return;
    const { rule, client } = pending;
    if (rule?.action === 'create_task') {
      const taskTitle = (rule.actionParams?.title || 'Follow up').replace('{company}', client?.companyName || '');
      const taskId = `crmt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await setDoc(recruiterCrmTaskDoc(recruiterUser.uid, taskId), {
        id: taskId,
        title: taskTitle,
        companyName: client?.companyName || '',
        priority: rule.actionParams?.priority || 'normal',
        status: 'open',
        source: 'automation',
        ruleId: rule.id,
        createdAtIso: new Date().toISOString(),
        dueDate: new Date(Date.now() + 3 * 86400000).toISOString().slice(0, 10),
      });
    } else if (rule?.action === 'send_cta' && client) {
      handleSendClientResponseEmail(client);
    }
    await handleDismissAutomation(pending.id);
    setClientCrmNotice(`Automation executed: ${rule?.name}`);
  };

  // ── Cadence enrollment handlers ──────────────────────────────────────────
  const handleEnrollContactInCadence = async (contactId, cadenceId) => {
    if (!recruiterUser?.uid || !contactId || !cadenceId) return;
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact) return;
    const cadence = BUILTIN_CADENCES.find((c) => c.id === cadenceId);
    if (!cadence) return;
    const enrollId = `enrol-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    const firstStep = cadence.steps[0];
    const nextStepDueIso = new Date(Date.now() + (firstStep?.day || 0) * 86400000).toISOString();
    await setDoc(recruiterCrmCadenceEnrollmentDoc(recruiterUser.uid, enrollId), {
      id: enrollId,
      contactId,
      contactName: contact.name || contact.email || contactId,
      companyName: contact.companyName || contact.company || '',
      cadenceId,
      cadenceName: cadence.name,
      enrolledAtIso: new Date().toISOString(),
      currentStep: 0,
      totalSteps: cadence.steps.length,
      nextStepDueIso,
      status: 'active',
      stepsSent: {},
    });
  };

  const handleAdvanceCadenceEnrollment = async (enrollmentId) => {
    if (!recruiterUser?.uid || !enrollmentId) return;
    const enrollment = crmCadenceEnrollments.find((e) => e.id === enrollmentId);
    if (!enrollment) return;
    const cadence = BUILTIN_CADENCES.find((c) => c.id === enrollment.cadenceId);
    const totalSteps = cadence?.steps.length || 0;
    const nextStep = (enrollment.currentStep || 0) + 1;
    if (nextStep >= totalSteps) {
      await setDoc(recruiterCrmCadenceEnrollmentDoc(recruiterUser.uid, enrollmentId), { status: 'completed', completedAtIso: new Date().toISOString() }, { merge: true });
      return;
    }
    const step = cadence.steps[nextStep];
    const nextStepDueIso = new Date(Date.now() + (step?.day || 0) * 86400000).toISOString();
    await setDoc(recruiterCrmCadenceEnrollmentDoc(recruiterUser.uid, enrollmentId), {
      currentStep: nextStep,
      nextStepDueIso,
      lastAdvancedAtIso: new Date().toISOString(),
    }, { merge: true });
  };

  const handlePauseCadenceEnrollment = async (enrollmentId) => {
    if (!recruiterUser?.uid || !enrollmentId) return;
    const enrollment = crmCadenceEnrollments.find((e) => e.id === enrollmentId);
    if (!enrollment) return;
    const newStatus = enrollment.status === 'paused' ? 'active' : 'paused';
    await setDoc(recruiterCrmCadenceEnrollmentDoc(recruiterUser.uid, enrollmentId), { status: newStatus }, { merge: true });
  };

  // ── SMTP settings ────────────────────────────────────────────────────────
  const handleSaveSmtpSettings = async () => {
    if (!recruiterUser?.uid) return;
    if (!requireAccess('integrations.manage', 'Only admins or owners can change outbound email integration settings.')) return;
    setSmtpSaving(true);
    setSmtpNotice('');
    try {
      const cleaned = {
        host: smtpDraft.host.trim(),
        port: Number(smtpDraft.port) || 587,
        secure: smtpDraft.secure === true,
        user: smtpDraft.user.trim(),
        pass: smtpDraft.pass,
        fromName: smtpDraft.fromName.trim(),
        fromEmail: smtpDraft.fromEmail.trim(),
      };
      await setDoc(userDocRef(recruiterUser.uid), { smtpSettings: cleaned, updatedAt: serverTimestamp() }, { merge: true });
      setSmtpSettings(cleaned);
      setSmtpNotice('Settings saved.');
    } catch (err) {
      setSmtpNotice('Failed to save: ' + err.message);
    } finally {
      setSmtpSaving(false);
    }
  };

  const handleTestSmtpSettings = async () => {
    if (!recruiterUser?.uid) return;
    setSmtpTestSending(true);
    setSmtpNotice('');
    try {
      const fn = httpsCallable(functionsClient, 'sendTestEmail');
      await fn({ appId });
      setSmtpNotice('Test email queued — check your inbox.');
    } catch (err) {
      setSmtpNotice('Test failed: ' + err.message);
    } finally {
      setSmtpTestSending(false);
    }
  };

  // ── Cadence email send + tracking ─────────────────────────────────────────
  const handleSendCadenceEmail = async (contact, { subject, body, stepIndex, enrollmentId }) => {
    if (!recruiterUser?.uid || !contact?.email) return null;
    if (!smtpSettings?.host) {
      setSmtpSettingsOpen(true);
      return null;
    }
    try {
      const fn = httpsCallable(functionsClient, 'sendCadenceEmail');
      const result = await fn({
        appId,
        to: contact.email,
        subject,
        body,
        enrollmentId: enrollmentId || null,
        stepIndex: stepIndex != null ? stepIndex : null,
        contactId: contact.id,
        contactName: contact.name || '',
        companyName: contact.companyName || contact.company || '',
      });
      setClientCrmNotice(`Email sent to ${contact.email}`);
      return result.data?.trackingToken || null;
    } catch (err) {
      setClientCrmNotice('Send failed: ' + err.message);
      return null;
    }
  };

  const handleMarkEmailReplied = async (trackingToken) => {
    if (!recruiterUser?.uid || !trackingToken) return;
    try {
      const fn = httpsCallable(functionsClient, 'markCadenceEmailReplied');
      await fn({ appId, trackingToken });
      setClientCrmNotice('Reply recorded — cadence auto-advanced.');
    } catch (err) {
      setClientCrmNotice('Failed: ' + err.message);
    }
  };

  // ── Contact custom field handlers ────────────────────────────────────────
  const handleSaveContactCustomField = async (contactId, key, value) => {
    if (!recruiterUser?.uid || !contactId || !key) return;
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact) return;
    const existing = Array.isArray(contact.customFields) ? contact.customFields : [];
    const next = existing.find((f) => f.key === key)
      ? existing.map((f) => f.key === key ? { ...f, value } : f)
      : [...existing, { key, value }];
    await setDoc(recruiterContactDoc(recruiterUser.uid, contactId), { customFields: next, updatedAtIso: new Date().toISOString() }, { merge: true });
  };

  const handleDeleteContactCustomField = async (contactId, key) => {
    if (!recruiterUser?.uid || !contactId || !key) return;
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact) return;
    const next = (Array.isArray(contact.customFields) ? contact.customFields : []).filter((f) => f.key !== key);
    await setDoc(recruiterContactDoc(recruiterUser.uid, contactId), { customFields: next, updatedAtIso: new Date().toISOString() }, { merge: true });
  };

  const handleUpdateContact = async (contactId, fields) => {
    if (!recruiterUser?.uid || !contactId) return;
    await setDoc(recruiterContactDoc(recruiterUser.uid, contactId), { ...fields, updatedAtIso: new Date().toISOString() }, { merge: true });
  };

  const handleDeleteClients = async (clientIds) => {
    if (!recruiterUser?.uid || !clientIds || clientIds.size === 0) return;
    if (!requireAccess('clients.delete', 'You need client delete permission to remove company records.')) return;
    setClientCrmDeleting(true);
    try {
      // Build normalised company keys for each selected client id
      const selectedClients = clientPortfolio.filter((c) => clientIds.has(c.id));
      const companyKeys = new Set(selectedClients.map((c) => normalizeText(c.companyName)));
      const companyNames = new Set(selectedClients.map((c) => c.companyName));

      // 1. Remove jobs
      const nextJobs = jobs.filter((j) => !companyKeys.has(normalizeText(getJobCompanyName(j) || '')));
      await setDoc(userDocRef(recruiterUser.uid), { jobPortfolio: nextJobs, updatedAt: serverTimestamp() }, { merge: true });
      setJobs(nextJobs);

      // 2. Remove client opportunities
      const nextOpportunities = clientOpportunities.filter((o) => !companyKeys.has(normalizeText(o.companyName || '')));
      await setDoc(userDocRef(recruiterUser.uid), { clientOpportunities: nextOpportunities, updatedAt: serverTimestamp() }, { merge: true });
      setClientOpportunities(nextOpportunities);

      // 3. Remove contacts belonging to these companies
      const contactsToDelete = contacts.filter((c) => {
        const cKey = normalizeText(c.company || c.companyName || '');
        return [...companyKeys].some((k) => cKey === k || cKey.includes(k) || k.includes(cKey));
      });
      await Promise.all(contactsToDelete.map((c) => deleteDoc(recruiterContactDoc(recruiterUser.uid, c.id))));

      // 4. Remove CRM tasks for these companies
      const tasksToDelete = crmTasks.filter((t) => companyKeys.has(normalizeText(t.companyName || '')));
      await Promise.all(tasksToDelete.map((t) => deleteDoc(recruiterCrmTaskDoc(recruiterUser.uid, t.id))));

      // 5. Activity log entries (best-effort — large collections may have many; delete matched)
      const activityToDelete = activityLog.filter((a) => {
        const co = a?.metadata?.company || a?.metadata?.companyName || a?.metadata?.clientCompany || '';
        return companyKeys.has(normalizeText(co));
      });
      await Promise.all(activityToDelete.map((a) => deleteDoc(recruiterActivityDoc(recruiterUser.uid, a.id))));

      setClientCrmSelectedIds(new Set());
      setClientCrmDeleteConfirm(false);
      const names = [...companyNames].join(', ');
      setClientCrmNotice(`Deleted ${clientIds.size} client${clientIds.size !== 1 ? 's' : ''}: ${names}`);
    } catch (error) {
      console.error('[handleDeleteClients]', error?.message);
      setClientCrmNotice('Delete failed. Please try again.');
    } finally {
      setClientCrmDeleting(false);
    }
  };

  const handleAddCompanyContact = async (companyName, contactData) => {
    if (!recruiterUser?.uid || !companyName || !String(contactData?.name || '').trim()) return;
    const contactId = createContactRecordId(companyName, contactData.email || contactData.name);
    const companyId = createCompanyRecordId(companyName);
    setIsSavingCompanyContact(true);
    try {
      await setDoc(recruiterContactDoc(recruiterUser.uid, contactId), {
        id: contactId,
        companyId,
        companyName,
        name: String(contactData.name || '').trim(),
        email: String(contactData.email || '').trim(),
        phone: String(contactData.phone || '').trim(),
        role: contactData.role || 'Hiring Manager',
        linkedIn: String(contactData.linkedIn || '').trim(),
        notes: String(contactData.notes || '').trim(),
        source: 'manual',
        createdAtIso: new Date().toISOString(),
        updatedAtIso: new Date().toISOString(),
      }, { merge: true });
    } catch (error) {
      console.error('[handleAddCompanyContact]', error?.message);
    } finally {
      setIsSavingCompanyContact(false);
    }
  };

  const handleUpdateCompanyContact = async (contactId, updates) => {
    if (!recruiterUser?.uid || !contactId) return;
    setIsSavingCompanyContact(true);
    try {
      await setDoc(recruiterContactDoc(recruiterUser.uid, contactId), {
        ...updates,
        updatedAtIso: new Date().toISOString(),
      }, { merge: true });
    } catch (error) {
      console.error('[handleUpdateCompanyContact]', error?.message);
    } finally {
      setIsSavingCompanyContact(false);
    }
  };

  const handleRemoveCompanyContact = async (contactId) => {
    if (!recruiterUser?.uid || !contactId) return;
    try {
      await deleteDoc(recruiterContactDoc(recruiterUser.uid, contactId));
    } catch (error) {
      console.error('[handleRemoveCompanyContact]', error?.message);
    }
  };

  // ── Tags: add/remove tag on a contact ──────────────────────────────────
  const handleTagContact = async (contactId, tag, remove = false) => {
    if (!recruiterUser?.uid || !contactId || !String(tag).trim()) return;
    const contact = contacts.find((c) => c.id === contactId);
    if (!contact) return;
    const currentTags = Array.isArray(contact.tags) ? contact.tags : [];
    const nextTags = remove ? currentTags.filter((t) => t !== tag) : currentTags.includes(tag) ? currentTags : [...currentTags, tag.trim()];
    await handleUpdateCompanyContact(contactId, { tags: nextTags });
  };

  // ── Tags: add/remove tag on a candidate ────────────────────────────────
  const handleTagCandidate = async (candidateId, tag, remove = false) => {
    if (!recruiterUser?.uid || !candidateId || !String(tag).trim()) return;
    const candidate = candidates.find((c) => c.id === candidateId);
    if (!candidate) return;
    const currentTags = Array.isArray(candidate.tags) ? candidate.tags : [];
    const nextTags = remove ? currentTags.filter((t) => t !== tag) : currentTags.includes(tag) ? currentTags : [...currentTags, tag.trim()];
    try {
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), { tags: nextTags, updatedAtIso: new Date().toISOString() }, { merge: true });
      setCandidates((prev) => prev.map((c) => c.id === candidateId ? { ...c, tags: nextTags } : c));
    } catch (error) {
      console.error('[handleTagCandidate]', error?.message);
    }
  };

  const handleUpdateCandidateCommercialProfile = async (candidateId, updates = {}) => {
    if (!recruiterUser?.uid || !candidateId || !canAccess('pipeline.write')) return;
    const nowIso = new Date().toISOString();
    const payload = {
      ...updates,
      commercialProfileUpdatedAtIso: nowIso,
      updatedAtIso: nowIso,
      updatedAt: serverTimestamp(),
    };
    try {
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), payload, { merge: true });
      setCandidates((prev) => prev.map((candidate) => candidate.id === candidateId ? { ...candidate, ...payload } : candidate));
      await recordActivity(recruiterUser.uid, 'candidate_commercial_profile_updated', `Candidate commercial profile updated.`, { candidateId, fields: Object.keys(updates) });
    } catch (error) {
      console.error('[handleUpdateCandidateCommercialProfile]', error?.message);
      setPipelineNotice('Failed to update the candidate commercial profile.');
    }
  };

  const handleSaveInterviewCapture = async (candidateId, capture = {}) => {
    if (!recruiterUser?.uid || !candidateId || !canAccess('pipeline.write')) {
      throw new Error('Pipeline write access is required.');
    }
    if (!capture?.consentConfirmed) {
      throw new Error('Confirm candidate recording consent before saving.');
    }

    const candidate = candidates.find((item) => item.id === candidateId);
    if (!candidate) throw new Error('Candidate record not found.');

    const nowIso = new Date().toISOString();
    const recordingId = `interview-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let recordingUrl = '';
    let contentType = '';

    if (capture.blob && storage) {
      contentType = String(capture.blob.type || 'audio/webm');
      const fileRef = storageRef(storage, `interviews/${recruiterUser.uid}/${candidateId}/${recordingId}.webm`);
      await uploadBytes(fileRef, capture.blob, { contentType });
      recordingUrl = await getDownloadURL(fileRef);
    }

    const transcript = String(capture.transcript || '').trim().slice(0, 32000);
    let aiNote = null;
    if (transcript.length >= 60) {
      const summarizeInterviewTranscript = httpsCallable(functionsClient, 'summarizeInterviewTranscript');
      const result = await summarizeInterviewTranscript({
        appId,
        candidateId,
        candidateName: candidate.name || 'Candidate',
        jobTitle: candidate.matchedJobTitle || candidate.role || '',
        transcript,
      });
      aiNote = result?.data?.note || null;
    }

    const recording = {
      id: recordingId,
      url: recordingUrl,
      contentType,
      transcript: transcript.slice(0, 12000),
      transcriptChars: transcript.length,
      transcriptSource: capture.transcriptSource || '',
      consentConfirmed: true,
      consentConfirmedAtIso: nowIso,
      recordedAtIso: nowIso,
      recordedByUid: recruiterUser.uid,
      durationMs: Math.max(0, Number(capture.durationMs || 0)),
    };
    const noteRecord = aiNote ? {
      id: `${recordingId}-ai-note`,
      text: [
        'AI interview notes',
        aiNote.summary ? `Summary: ${aiNote.summary}` : '',
        Array.isArray(aiNote.highlights) && aiNote.highlights.length ? `Highlights:\n- ${aiNote.highlights.join('\n- ')}` : '',
        Array.isArray(aiNote.concerns) && aiNote.concerns.length ? `Concerns:\n- ${aiNote.concerns.join('\n- ')}` : '',
        Array.isArray(aiNote.nextSteps) && aiNote.nextSteps.length ? `Next steps:\n- ${aiNote.nextSteps.join('\n- ')}` : '',
      ].filter(Boolean).join('\n\n'),
      authorName: 'Vision AI Notetaker',
      authorUid: recruiterUser.uid,
      createdAt: nowIso,
      source: aiNote.source || 'ai',
      recordingId,
      transcriptChars: transcript.length,
    } : null;
    const payload = {
      interviewRecordings: arrayUnion(recording),
      ...(noteRecord ? { candidateNotes: arrayUnion(noteRecord) } : {}),
      updatedAtIso: nowIso,
      updatedAt: serverTimestamp(),
    };

    await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), payload, { merge: true });
    setCandidates((current) => current.map((item) => item.id === candidateId ? {
      ...item,
      interviewRecordings: [...(Array.isArray(item.interviewRecordings) ? item.interviewRecordings : []), recording],
      ...(noteRecord ? { candidateNotes: [...(Array.isArray(item.candidateNotes) ? item.candidateNotes : []), noteRecord] } : {}),
      updatedAtIso: nowIso,
    } : item));
    await recordActivity(recruiterUser.uid, 'interview_recording_saved', `${candidate.name || 'Candidate'} interview recording saved.`, {
      candidateId,
      recordingId,
      hasTranscript: Boolean(transcript),
      hasAiNote: Boolean(noteRecord),
    });

    return { recording, aiNote: noteRecord };
  };

  const handleAssignCandidateOwner = async (candidateId, ownerRecruiterId) => {
    if (!recruiterUser?.uid || !candidateId || !canAccess('pipeline.write')) return;
    const member = agencyMembers.find((item) => item.id === ownerRecruiterId);
    const nowIso = new Date().toISOString();
    const payload = {
      ownerRecruiterId,
      ownerRecruiterName: member?.name || member?.email || (ownerRecruiterId === recruiterUser?.uid ? profile.name : ''),
      commercialProfileUpdatedAtIso: nowIso,
      updatedAtIso: nowIso,
      updatedAt: serverTimestamp(),
    };
    const historyEntry = {
      ownerRecruiterId,
      ownerName: member?.name || member?.email || '',
      changedBy: recruiterUser.uid,
      changedAtIso: nowIso,
    };
    try {
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
        ...payload,
      ownershipHistory: arrayUnion({
        ownerRecruiterId,
        ownerName: member?.name || member?.email || '',
          changedBy: recruiterUser.uid,
          changedAtIso: nowIso,
      }),
      }, { merge: true });
      setCandidates((prev) => prev.map((candidate) => candidate.id === candidateId ? {
        ...candidate,
        ...payload,
        ownershipHistory: [...(Array.isArray(candidate.ownershipHistory) ? candidate.ownershipHistory : []), historyEntry],
      } : candidate));
      await recordActivity(recruiterUser.uid, 'candidate_owner_changed', `Candidate owner changed to ${payload.ownerRecruiterName || 'a recruiter'}.`, { candidateId, ownerRecruiterId });
    } catch (error) {
      console.error('[handleAssignCandidateOwner]', error?.message);
      setPipelineNotice('Failed to update candidate owner.');
    }
  };

  const handleCreateTalentList = async () => {
    if (!recruiterUser?.uid || !String(talentListDraft.name || '').trim()) return;
    if (!requireAccess('pipeline.write', 'You need candidate write access to create talent lists.')) return;
    const nowIso = new Date().toISOString();
    const list = {
      id: `talent-list-${Date.now()}`,
      name: talentListDraft.name.trim(),
      description: talentListDraft.description.trim(),
      candidateIds: [],
      ownerRecruiterId: recruiterUser.uid,
      ownerName: profile.name || recruiterUser.email || 'Recruiter',
      createdAtIso: nowIso,
      updatedAtIso: nowIso,
    };
    const nextLists = [list, ...talentLists];
    setTalentLists(nextLists);
    setSelectedTalentListId(list.id);
    setTalentListDraft({ name: '', description: '' });
    await setDoc(userDocRef(recruiterUser.uid), { talentLists: nextLists, updatedAt: serverTimestamp() }, { merge: true });
    await recordActivity(recruiterUser.uid, 'talent_list_created', `Talent list "${list.name}" created.`, { listId: list.id });
  };

  const handleAddCandidateToTalentList = async (candidateId, listId) => {
    if (!recruiterUser?.uid || !candidateId || !listId) return;
    if (!requireAccess('pipeline.write', 'You need candidate write access to update talent lists.')) return;
    const candidate = candidates.find((item) => item.id === candidateId);
    const nextLists = talentLists.map((list) => list.id === listId
      ? { ...list, candidateIds: Array.from(new Set([...(Array.isArray(list.candidateIds) ? list.candidateIds : []), candidateId])), updatedAtIso: new Date().toISOString() }
      : list);
    setTalentLists(nextLists);
    await setDoc(userDocRef(recruiterUser.uid), { talentLists: nextLists, updatedAt: serverTimestamp() }, { merge: true });
    await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
      talentListIds: arrayUnion(listId),
      crmTimeline: arrayUnion(createCrmEvent('talent_list_added', `${candidate?.name || 'Candidate'} added to a talent list.`, { listId })),
      updatedAt: serverTimestamp(),
      updatedAtIso: new Date().toISOString(),
    }, { merge: true });
  };

  const handleApplyCandidateSavedView = (view) => {
    if (!view) return;
    setCandidateDbOpen(true);
    setCandidateDbSection('saved-searches');
    setCandidateDbSearch(view.search || '');
    setCandidateDbFilters({ company: '', role: '', status: '', tag: '', source: '', ...(view.filters || {}) });
    setCandidateDbSort(view.sort || 'name');
  };

  // ── Saved views: persist to agencyDetails ──────────────────────────────
  const handleSaveView = async (entity, name, filters, sort, search) => {
    if (!recruiterUser?.uid || !name.trim()) return;
    const view = { id: `view-${Date.now()}`, name: name.trim(), entity, filters, sort, search, createdAt: new Date().toISOString() };
    const nextViews = entity === 'contacts' ? [...savedContactViews, view] : [...savedCandidateViews, view];
    if (entity === 'contacts') setSavedContactViews(nextViews);
    else setSavedCandidateViews(nextViews);
    try {
      const key = entity === 'contacts' ? 'savedContactViews' : 'savedCandidateViews';
      await setDoc(userDocRef(recruiterUser.uid), { [key]: nextViews, updatedAt: serverTimestamp() }, { merge: true });
    } catch (error) { console.error('[handleSaveView]', error?.message); }
  };

  const handleDeleteSavedView = async (entity, viewId) => {
    if (!recruiterUser?.uid) return;
    const nextViews = entity === 'contacts'
      ? savedContactViews.filter((v) => v.id !== viewId)
      : savedCandidateViews.filter((v) => v.id !== viewId);
    if (entity === 'contacts') setSavedContactViews(nextViews);
    else setSavedCandidateViews(nextViews);
    try {
      const key = entity === 'contacts' ? 'savedContactViews' : 'savedCandidateViews';
      await setDoc(userDocRef(recruiterUser.uid), { [key]: nextViews, updatedAt: serverTimestamp() }, { merge: true });
    } catch (error) { console.error('[handleDeleteSavedView]', error?.message); }
  };

  // ── Dedup: dismiss a pair ───────────────────────────────────────────────
  const handleDedupDismiss = async (key) => {
    const next = [...dedupDismissed, key];
    setDedupDismissed(next);
    try {
      await setDoc(userDocRef(recruiterUser.uid), { dedupDismissed: next, updatedAt: serverTimestamp() }, { merge: true });
    } catch (error) { console.error('[handleDedupDismiss]', error?.message); }
  };

  // ── Dedup: merge two contacts ───────────────────────────────────────────
  const handleMergeContacts = async (primary, secondary, fieldChoices) => {
    if (!recruiterUser?.uid) return;
    setDedupIsMerging(true);
    try {
      const CONTACT_MERGE_FIELDS = ['name', 'email', 'phone', 'role', 'linkedIn', 'notes', 'companyName', 'sentiment', 'tags'];
      const merged = { ...primary };
      CONTACT_MERGE_FIELDS.forEach((f) => {
        const choice = fieldChoices[f] || 'primary';
        const val = choice === 'secondary' ? secondary[f] : primary[f];
        if (f === 'tags') {
          const pa = Array.isArray(primary.tags) ? primary.tags : [];
          const sa = Array.isArray(secondary.tags) ? secondary.tags : [];
          merged.tags = [...new Set([...pa, ...sa])];
        } else {
          merged[f] = val !== undefined ? val : (primary[f] !== undefined ? primary[f] : secondary[f]);
        }
      });
      merged.mergedFromId = secondary.id;
      merged.updatedAtIso = new Date().toISOString();
      await setDoc(recruiterContactDoc(recruiterUser.uid, primary.id), merged, { merge: false });
      await deleteDoc(recruiterContactDoc(recruiterUser.uid, secondary.id));
      const key = [primary.id, secondary.id].sort().join('__');
      const nextDismissed = [...dedupDismissed, key];
      setDedupDismissed(nextDismissed);
      await setDoc(userDocRef(recruiterUser.uid), { dedupDismissed: nextDismissed, updatedAt: serverTimestamp() }, { merge: true });
      setDedupMergeModal(null);
      setDedupMergeFieldChoices({});
      await recordActivity(recruiterUser.uid, 'contact_merged', `Contact "${secondary.name || secondary.email}" merged into "${primary.name || primary.email}".`, { primaryId: primary.id, secondaryId: secondary.id });
    } catch (error) {
      console.error('[handleMergeContacts]', error?.message);
    } finally {
      setDedupIsMerging(false);
    }
  };

  // ── Dedup: merge two candidates ─────────────────────────────────────────
  const handleMergeCandidates = async (primary, secondary, fieldChoices) => {
    if (!recruiterUser?.uid) return;
    setDedupIsMerging(true);
    try {
      const CANDIDATE_MERGE_FIELDS = ['name', 'email', 'phone', 'role', 'company', 'location', 'notes', 'linkedIn', 'tags', 'source', 'rating'];
      const merged = { ...primary };
      CANDIDATE_MERGE_FIELDS.forEach((f) => {
        const choice = fieldChoices[f] || 'primary';
        if (f === 'tags') {
          const pa = Array.isArray(primary.tags) ? primary.tags : [];
          const sa = Array.isArray(secondary.tags) ? secondary.tags : [];
          merged.tags = [...new Set([...pa, ...sa])];
        } else if (f === 'rating') {
          merged.rating = Math.max(Number(primary.rating || 0), Number(secondary.rating || 0)) || primary.rating;
        } else {
          const val = choice === 'secondary' ? secondary[f] : primary[f];
          merged[f] = val !== undefined && val !== '' ? val : (primary[f] !== undefined ? primary[f] : secondary[f]);
        }
      });
      merged.mergedFromId = secondary.id;
      merged.updatedAtIso = new Date().toISOString();
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, primary.id), merged, { merge: false });
      await deleteDoc(recruiterCandidateDoc(recruiterUser.uid, secondary.id));
      setCandidates((prev) => prev.filter((c) => c.id !== secondary.id).map((c) => c.id === primary.id ? merged : c));
      const key = [primary.id, secondary.id].sort().join('__');
      const nextDismissed = [...dedupDismissed, key];
      setDedupDismissed(nextDismissed);
      await setDoc(userDocRef(recruiterUser.uid), { dedupDismissed: nextDismissed, updatedAt: serverTimestamp() }, { merge: true });
      setDedupMergeModal(null);
      setDedupMergeFieldChoices({});
      await recordActivity(recruiterUser.uid, 'candidate_merged', `Candidate "${secondary.name || secondary.email}" merged into "${primary.name || primary.email}".`, { primaryId: primary.id, secondaryId: secondary.id });
    } catch (error) {
      console.error('[handleMergeCandidates]', error?.message);
    } finally {
      setDedupIsMerging(false);
    }
  };

  const handleAddCrmTask = async (job, taskData) => {
    if (!recruiterUser?.uid || !job || !String(taskData?.title || '').trim()) return;
    const companyName = getJobCompanyName(job) || '';
    const taskId = `crmt-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setIsSavingCrmTask(true);
    try {
      await setDoc(recruiterCrmTaskDoc(recruiterUser.uid, taskId), {
        id: taskId,
        companyName,
        jobId: job.id,
        jobTitle: job.title || '',
        type: taskData.type || 'follow_up_call',
        title: String(taskData.title || '').trim(),
        priority: taskData.priority || 'Medium',
        dueDate: taskData.dueDate || '',
        note: String(taskData.note || '').trim(),
        contactName: String(taskData.contactName || '').trim(),
        status: 'open',
        createdAtIso: new Date().toISOString(),
        updatedAtIso: new Date().toISOString(),
      });
      await recordActivity(recruiterUser.uid, 'crm_task_created', `Task created: "${String(taskData.title).trim()}" for ${companyName}.`, { company: companyName, jobId: job.id, taskId });
      if (taskData.dueDate) {
        await pushNotification('task_reminder', `Task due: ${String(taskData.title || '').trim()}`, `${companyName}${taskData.contactName ? ` · ${taskData.contactName}` : ''} — due ${taskData.dueDate}`, { taskId, companyName, jobId: job.id, dueDate: taskData.dueDate, type: taskData.type || 'follow_up_call' });
      }
    } catch (error) {
      console.error('[handleAddCrmTask]', error?.message);
    } finally {
      setIsSavingCrmTask(false);
    }
  };

  const handleCompleteCrmTask = async (taskId) => {
    if (!recruiterUser?.uid || !taskId) return;
    try {
      await setDoc(recruiterCrmTaskDoc(recruiterUser.uid, taskId), {
        status: 'done',
        completedAtIso: new Date().toISOString(),
        updatedAtIso: new Date().toISOString(),
      }, { merge: true });
      const task = crmTasks.find((t) => t.id === taskId);
      if (task?.companyName) {
        await recordActivity(recruiterUser.uid, 'crm_task_completed', `Task completed: "${task.title || task.type}" for ${task.companyName}.`, { company: task.companyName, jobId: task.jobId, taskId });
      }
    } catch (error) {
      console.error('[handleCompleteCrmTask]', error?.message);
    }
  };

  const handleSnoozeCrmTask = async (taskId, days) => {
    if (!recruiterUser?.uid || !taskId || !days) return;
    try {
      const task = crmTasks.find((t) => t.id === taskId);
      const base = task?.dueDate ? new Date(task.dueDate) : new Date();
      base.setDate(base.getDate() + days);
      const newDueDate = base.toISOString().slice(0, 10);
      await setDoc(recruiterCrmTaskDoc(recruiterUser.uid, taskId), {
        dueDate: newDueDate,
        updatedAtIso: new Date().toISOString(),
      }, { merge: true });
    } catch (error) {
      console.error('[handleSnoozeCrmTask]', error?.message);
    }
  };

  const handleRemoveCrmTask = async (taskId) => {
    if (!recruiterUser?.uid || !taskId) return;
    try {
      await deleteDoc(recruiterCrmTaskDoc(recruiterUser.uid, taskId));
    } catch (error) {
      console.error('[handleRemoveCrmTask]', error?.message);
    }
  };

  const handleSetJobStage = async (jobId, newStage) => {
    if (!recruiterUser?.uid || !jobId || !newStage) return;
    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;
    const stageUpdatedAt = new Date().toISOString();
    const nextJobs = jobs.map((j) => j.id === jobId ? { ...j, lifecycleStage: newStage, stageUpdatedAt } : j);
    try {
      await setDoc(userDocRef(recruiterUser.uid), { jobPortfolio: nextJobs, updatedAt: serverTimestamp() }, { merge: true });
      setJobs(nextJobs);
      const stageMeta = JOB_LIFECYCLE_STAGES.find((s) => s.id === newStage);
      await recordActivity(recruiterUser.uid, 'job_stage_changed', `${job.title} moved to ${stageMeta?.label || newStage} stage.`, { jobId, company: getJobCompanyName(job), stage: newStage });
      await pushNotification('assignment', `Stage updated: ${job.title}`, `${getJobCompanyName(job)} — moved to ${stageMeta?.label || newStage}`, { jobId, stage: newStage });
    } catch (error) {
      console.error('[handleSetJobStage]', error?.message);
    }
  };

  const handleToggleJobBlocked = async (jobId, blocked, reason) => {
    if (!recruiterUser?.uid || !jobId) return;
    const job = jobs.find((j) => j.id === jobId);
    if (!job) return;
    const nextJobs = jobs.map((j) => j.id === jobId ? { ...j, isBlocked: !!blocked, blockedReason: reason || '' } : j);
    try {
      await setDoc(userDocRef(recruiterUser.uid), { jobPortfolio: nextJobs, updatedAt: serverTimestamp() }, { merge: true });
      setJobs(nextJobs);
      await recordActivity(recruiterUser.uid, blocked ? 'job_blocked' : 'job_unblocked', `${job.title} was ${blocked ? 'blocked' : 'unblocked'}${reason ? `: ${reason}` : ''}.`, { jobId, company: getJobCompanyName(job) });
      if (blocked) await pushNotification('system', `Job blocked: ${job.title}`, `${getJobCompanyName(job)}${reason ? ` · ${reason}` : ''}`, { jobId });
    } catch (error) {
      console.error('[handleToggleJobBlocked]', error?.message);
    }
  };

  const pushNotification = async (type, title, body, metadata = {}) => {
    if (!recruiterUser?.uid) return;
    return pushNotificationForUser(recruiterUser.uid, type, title, body, metadata);
  };

  const pushNotificationForUser = async (userId, type, title, body, metadata = {}) => {
    if (!userId) return;
    const notifId = `notif-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    try {
      await setDoc(recruiterNotificationDoc(userId, notifId), {
        id: notifId, type, title, body, metadata, read: false,
        createdAtIso: new Date().toISOString(),
        createdAt: serverTimestamp(),
      });
      const sendNotificationEmail = httpsCallable(functionsClient, 'sendInAppNotificationEmail');
      sendNotificationEmail({
        appId,
        targetUid: userId,
        notification: { id: notifId, type, title, body, metadata },
      }).catch((emailErr) => {
        console.warn('[sendInAppNotificationEmail]', emailErr?.message || emailErr);
      });
    } catch (err) {
      console.error('[pushNotification]', err?.message);
    }
  };

  const handleMarkNotificationRead = async (notifId) => {
    if (!recruiterUser?.uid || !notifId) return;
    try {
      await setDoc(recruiterNotificationDoc(recruiterUser.uid, notifId), { read: true }, { merge: true });
    } catch (err) {
      console.error('[handleMarkNotificationRead]', err?.message);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    if (!recruiterUser?.uid) return;
    const unread = notifications.filter((n) => !n.read);
    try {
      await Promise.all(unread.map((n) => setDoc(recruiterNotificationDoc(recruiterUser.uid, n.id), { read: true }, { merge: true })));
    } catch (err) {
      console.error('[handleMarkAllNotificationsRead]', err?.message);
    }
  };

  const handleOpenNotification = (notification) => {
    const metadata = notification?.metadata || {};
    const nextTab = metadata.tab || metadata.workspaceTab || metadata.targetTab;
    if (nextTab) handleWorkspaceNavigation(nextTab);
    if (metadata.agencySection) setAgencySection(metadata.agencySection);
    if (metadata.candidateId) setSelectedCandidateId(metadata.candidateId);
    if (metadata.jobId) setSelectedJobPanelId(metadata.jobId);
    if (metadata.companyId || metadata.accountId) setSelectedAccountId(metadata.companyId || metadata.accountId);
    if (metadata.contactId) setSelectedContactId(metadata.contactId);
    if (!nextTab && notification?.type === 'team_message') {
      handleWorkspaceNavigation('agency');
      setAgencySection('team');
    }
  };

  const [meetingModalState, setMeetingModalState] = useState({ open: false, companyName: '', jobId: '', prefillEmail: '', busy: false });

  const handleOpenMeetingModal = (contact, job) => {
    setMeetingModalState({
      open: true,
      companyName: getJobCompanyName(job || selectedJobPanel) || '',
      jobId: (job || selectedJobPanel)?.id || '',
      prefillEmail: contact?.email || '',
      busy: false,
    });
  };

  const handleScheduleMeeting = async ({ title, startIso, endIso, attendeeEmails, description, companyName: cName, jobId: jId }) => {
    if (!recruiterUser?.uid) return;
    setMeetingModalState((s) => ({ ...s, busy: true }));
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const scheduleClientMeeting = httpsCallable(functionsClient, 'scheduleClientMeeting');
      const resp = await scheduleClientMeeting({ appId, title, startIso, endIso, attendeeEmails, description, companyName: cName, jobId: jId });
      if (resp?.data?.calendarEventLink) {
        setClientCrmNotice(`Meeting scheduled. ${resp.data.calendarSource === 'google_calendar' ? 'Google Calendar event created.' : 'Logged to timeline (reconnect Gmail to enable calendar events).'}`);
      }
    } catch (err) {
      setClientCrmNotice(err?.message || 'Failed to schedule meeting.');
    } finally {
      setMeetingModalState((s) => ({ ...s, open: false, busy: false }));
    }
  };

  const handleAddAccountTimelineEntry = async (job, type, message) => {
    if (!recruiterUser?.uid || !job || !String(message || '').trim()) return;
    const companyName = getJobCompanyName(job) || '';
    const activityType = `client_${type}_logged`;
    const typeLabel = type === 'call' ? 'Call' : type === 'email' ? 'Email' : type === 'meeting' ? 'Meeting' : 'Note';
    setIsSavingTimelineEntry(true);
    try {
      await recordActivity(recruiterUser.uid, activityType, String(message).trim(), {
        company: companyName,
        companyName,
        clientCompany: companyName,
        jobId: job.id,
        jobTitle: job.title || '',
        contact: job.clientContact || '',
      });
      if (['call', 'email', 'meeting'].includes(type)) {
        const now = new Date().toISOString();
        const nextJobs = jobs.map((j) => j.id === job.id ? { ...j, lastOutreach: now } : j);
        await setDoc(userDocRef(recruiterUser.uid), {
          jobPortfolio: nextJobs,
          updatedAt: serverTimestamp(),
        }, { merge: true });
        setJobs(nextJobs);
      }
    } catch (error) {
      console.error('[handleAddAccountTimelineEntry]', error?.message);
    } finally {
      setIsSavingTimelineEntry(false);
    }
  };

  const handleSendClientResponseEmail = async (client) => {
    if (!recruiterUser?.uid || !client?.companyName) return;
    const emailMatch = String(client.hiringContact || '').match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
    const fallbackOpportunity = clientCrmOpportunities.find((opportunity) => normalizeText(opportunity.companyName) === normalizeText(client.companyName));
    const fallbackJob = jobs.find((job) => normalizeText(getJobCompanyName(job)) === normalizeText(client.companyName));
    let to = emailMatch ? emailMatch[0] : String(fallbackOpportunity?.contactEmail || '').trim();
    if (!to && typeof window !== 'undefined') {
      to = window.prompt(`Client email for ${client.companyName}`) || '';
    }
    to = String(to || '').trim().toLowerCase();
    if (!to) {
      setClientCrmNotice(`Add a client email before sending a response-button email to ${client.companyName}.`);
      return;
    }
    const stateKey = client.id || normalizeText(client.companyName);
    setClientResponseEmailState((current) => ({ ...current, [stateKey]: true }));
    setClientCrmNotice('');
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const sendClientResponseEmail = httpsCallable(functionsClient, 'sendClientResponseEmail');
      const response = await sendClientResponseEmail({
        appId,
        to,
        companyName: client.companyName,
        contactName: client.hiringContact && !emailMatch ? client.hiringContact : fallbackOpportunity?.contactName || '',
        jobTitle: fallbackOpportunity?.title || fallbackJob?.title || client.roleTitles?.[0] || 'your hiring need',
        jobId: fallbackJob?.id || '',
        opportunityId: fallbackOpportunity?.id || '',
        message: `I wanted to check how you would like to move forward with ${fallbackOpportunity?.title || fallbackJob?.title || 'this hiring need'}.`,
      });
      setClientCrmNotice(response?.data?.message || `Response-button email queued for ${client.companyName}.`);
    } catch (error) {
      setClientCrmNotice(error?.message || 'Failed to queue response-button email.');
    } finally {
      setClientResponseEmailState((current) => {
        const next = { ...current };
        delete next[stateKey];
        return next;
      });
    }
  };

  const handleStartGmailConnection = async () => {
    if (!recruiterUser?.uid) return;
    setGmailIntegrationBusy(true);
    setClientCrmNotice('');
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const startGmailOAuth = httpsCallable(functionsClient, 'startGmailOAuth');
      const response = await startGmailOAuth({ appId });
      const authUrl = response?.data?.authUrl || '';
      if (authUrl && typeof window !== 'undefined') {
        window.location.href = authUrl;
      } else {
        setClientCrmNotice('Gmail authorization URL was not returned.');
      }
    } catch (error) {
      setClientCrmNotice(error?.message || 'Gmail integration is pending verification.');
    } finally {
      setGmailIntegrationBusy(false);
    }
  };

  const handleSyncGmailClientActivity = async () => {
    if (!recruiterUser?.uid) return;
    setGmailIntegrationBusy(true);
    setClientCrmNotice('');
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const syncGmailClientActivity = httpsCallable(functionsClient, 'syncGmailClientActivity');
      const response = await syncGmailClientActivity({ appId, maxResults: 20 });
      setClientCrmNotice(`Gmail sync complete: ${response?.data?.createdActivities || 0} CRM activities created from ${response?.data?.matched || 0} matched emails.`);
      if ((response?.data?.createdActivities || 0) > 0) {
        await pushNotification('gmail_sync', 'Gmail sync complete', `${response.data.createdActivities} new email activities logged from ${response.data.matched} matched threads.`, { source: 'gmail_sync' });
      }
      await refreshGmailIntegrationStatus();
    } catch (error) {
      setClientCrmNotice(error?.message || 'Failed to sync Gmail activity.');
    } finally {
      setGmailIntegrationBusy(false);
    }
  };

  const handleDisconnectGmailIntegration = async () => {
    if (!recruiterUser?.uid) return;
    setGmailIntegrationBusy(true);
    setClientCrmNotice('');
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const disconnectGmailIntegration = httpsCallable(functionsClient, 'disconnectGmailIntegration');
      await disconnectGmailIntegration({ appId });
      setClientCrmNotice('Gmail disconnected from Vision.');
      await refreshGmailIntegrationStatus();
    } catch (error) {
      setClientCrmNotice(error?.message || 'Failed to disconnect Gmail.');
    } finally {
      setGmailIntegrationBusy(false);
    }
  };



  const recordActivity = async (userId, type, summary, metadata = {}) => {

    if (!db || !userId) return;

    const activityRef = recruiterActivityDoc(userId, `activity-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

    await setDoc(activityRef, {

      type,

      summary,

      metadata,

      createdAt: serverTimestamp(),

      createdAtIso: new Date().toISOString(),

      createdAtMs: Date.now(),

    });

  };

  const handleWorkspaceNavigation = (tabId) => {
    const nextTab = WORKSPACE_PAGE_CONFIG[tabId] ? tabId : 'pipeline';
    const permission = RBAC_PAGE_PERMISSIONS[nextTab];
    if (permission && !canAccess(permission)) {
      const fallbackTab = getFirstAllowedWorkspaceTab(currentWorkspaceRole, currentAgencyMember?.customPermissions);
      setActiveTab(fallbackTab);
      setAgencyNotice(`Access to ${WORKSPACE_PAGE_CONFIG[nextTab]?.label || nextTab} is restricted for ${RBAC_ROLE_PRESETS[currentWorkspaceRole]?.label || currentWorkspaceRole}.`);
      return;
    }
    if (isDemoWorkspace && nextTab !== activeTab && !directDemoWalkthroughOpen && !importDemoWalkthroughOpen) {
      setDemoTabSwitchCount((current) => {
        const nextCount = current + 1;
        if (nextCount % 2 === 0) setDemoAccountAdOpen(true);
        return nextCount;
      });
    }
    setActiveTab(nextTab);
    if (directDemoWalkthroughOpen) {
      if (nextTab === 'pipeline') {
        if (directDemoWalkthroughStep === 0) markDirectDemoStepComplete(0);
        if (directDemoCompletedSteps.includes(4)) markDirectDemoStepComplete(5);
      }
      if (nextTab === 'jobs') markDirectDemoStepComplete(1);
    }
    if (importDemoWalkthroughOpen) {
      if (nextTab === 'jobs' && importDemoWalkthroughStep === 0) markImportDemoStepComplete(0);
      if (nextTab === 'pipeline' && importDemoWalkthroughStep === 2) markImportDemoStepComplete(2);
    }
  };



  useEffect(() => {

    const handlePopState = () => {

      const params = new URLSearchParams(window.location.search);

      setScreeningLinkId(params.get('screening') || '');

      setJobIntakeLinkId(params.get('job-intake') || '');

      setCandidateJobIntakeLinkId(params.get('candidate-intake') || '');

      const routeTab = getWorkspaceTabFromPath(window.location.pathname);
      if (routeTab) setActiveTab(routeTab);

    };

    window.addEventListener('popstate', handlePopState);

    return () => window.removeEventListener('popstate', handlePopState);

  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const routeTab = getWorkspaceTabFromPath(window.location.pathname);
    if (routeTab && routeTab !== activeTab) {
      setActiveTab(routeTab);
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const config = WORKSPACE_PAGE_CONFIG[activeTab];
    if (!config) return;
    const permission = RBAC_PAGE_PERMISSIONS[activeTab];
    if (permission && recruiterUser?.uid && !canAccess(permission)) {
      setActiveTab(getFirstAllowedWorkspaceTab(currentWorkspaceRole, currentAgencyMember?.customPermissions));
      return;
    }
    document.title = `${config.title} | Vision`;
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    if (currentPath !== config.path && (currentPath === '/' || currentPath === '/workspace' || currentPath.startsWith('/workspace/'))) {
      const nextUrl = new URL(window.location.href);
      nextUrl.pathname = config.path;
      window.history.pushState({}, '', nextUrl.toString());
    }
  }, [activeTab, currentWorkspaceRole, currentAgencyMember?.customPermissions, recruiterUser?.uid]);



  useEffect(() => {

    setMembershipPreviewEnabled(false);

  }, [recruiterUser?.uid]);

  useEffect(() => {
    if (activeTab !== 'jobs' || !recruiterUser?.uid || websiteLeadsLoaded) return;
    loadWebsiteLeads(false);
  }, [activeTab, recruiterUser?.uid, websiteLeadsLoaded]);

  const refreshGmailIntegrationStatus = async () => {
    if (!recruiterUser?.uid) return;
    setGmailIntegrationStatus((current) => ({ ...current, loading: true }));
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const getGmailIntegrationStatus = httpsCallable(functionsClient, 'getGmailIntegrationStatus');
      const response = await getGmailIntegrationStatus({ appId });
      setGmailIntegrationStatus({ loading: false, ...(response?.data || {}) });
    } catch (error) {
      setGmailIntegrationStatus((current) => ({ ...current, loading: false, error: error?.message || 'Failed to load Gmail integration status.' }));
    }
  };

  useEffect(() => {
    if (activeTab !== 'jobs' || !recruiterUser?.uid) return;
    refreshGmailIntegrationStatus();
  }, [activeTab, recruiterUser?.uid]);



  useEffect(() => {

    if (typeof window === 'undefined' || !recruiterUser?.uid) return;

    const url = new URL(window.location.href);

    const billingState = url.searchParams.get('billing');

    if (!billingState) return;

    if (billingState === 'success') {

      setAgencyNotice('Billing request received. Vision is preparing your custom PayFlex invoice.');

    } else if (billingState === 'cancelled') {

      setAgencyNotice('Billing checkout was cancelled. No subscription change was applied.');

    }

    url.searchParams.delete('billing');

    window.history.replaceState({}, '', url.toString());

  }, [recruiterUser?.uid]);



  useEffect(() => {

    if ((!screeningLinkId && !jobIntakeLinkId && !candidateJobIntakeLinkId) || auth?.isLoading || auth?.user) return;

    auth?.loginAnonymously?.().catch((error) => {

      console.error('Anonymous public-flow sign-in failed', error);

    });

  }, [auth, candidateJobIntakeLinkId, jobIntakeLinkId, screeningLinkId]);



  useEffect(() => {

    if (isDemoWorkspace) {
      const seed = buildDemoWorkspaceSeed(5);
      setIsLoadingWorkspace(true);
      setProfile(seed.profile);
      setAgencyDetails(seed.agencyDetails);
      setAgencyMembers(seed.agencyMembers);
      setAgencySettings((current) => ({
        ...current,
        seatLimit: String(seed.agencyDetails.seatLimit),
        billingContactEmail: seed.profile.email,
        lawfulUseAccepted: true,
      }));
      setJobs(seed.jobs);
      setCandidates([]);
      setScreeningResponses({});
      setSubmissions([]);
      setCompanies(seed.companies);
      setContacts(seed.contacts);
      setClientOpportunities(seed.clientOpportunities);
      setPlacements([]);
      setActivityLog(seed.activityLog);
      setCandidateReviewQueue([]);
      setScreeningLinks({});
      setSourcingInbox([]);
      setExternalProfiles([]);
      setSequenceRuns([]);
      setSequenceTasks([]);
      setSequenceSuppressions([]);
      setCrmTasks([]);
      setNotifications([]);
      setTeamMessages([
        { id: 'demo-team-message-1', authorName: 'Aisha Patel', message: 'The demo pipeline is empty until the walkthrough moves a sourced candidate into it.', createdAtIso: new Date().toISOString() },
      ]);
      if (directDemoWalkthroughOpen) {
        setDirectDemoBaseline({ jobs: seed.jobs.length, candidates: 0, placements: 0 });
        setDirectDemoCompletedSteps([]);
        setDirectDemoWalkthroughStep(0);
      }
      if (importDemoWalkthroughOpen) {
        setImportDemoCompletedSteps([]);
        setImportDemoWalkthroughStep(0);
      }
      setOwnerIntroCompleted(true);
      setMembershipPreviewEnabled(false);
      setPipelineNotice('Demo mode: the pipeline starts empty. The walkthrough will add the only demo candidate when you move a sourced prospect into the pipeline.');
      setIsLoadingWorkspace(false);
      return undefined;
    }

    if (!db || !recruiterUser?.uid) {

      setCandidates([]);

      setScreeningResponses({});

      setCandidateReviewQueue([]);

      setScreeningLinks({});

      setActivityLog([]);

      setJobs([]);

      setSourcingInbox([]);
      setClientOpportunities([]);
      setCompanies([]);
      setContacts([]);
      setSubmissions([]);
      setPlacements([]);
      setClientOpportunityForm(DEFAULT_CLIENT_OPPORTUNITY_FORM);
      setClientCrmNotice('');
      setClientResponseEmailState({});
      setGmailIntegrationStatus({ loading: false, ready: false, connected: false, pendingVerification: true, emailAddress: '', lastSyncAtIso: '' });
      setGmailIntegrationBusy(false);
      setMigrationPreview(null);
      setMigrationNotice('');
      setMigrationBatches([]);
      setSavedCandidateViews([]);
      setSavedContactViews([]);
      setTalentLists([]);
      setCandidateOwnerDrafts({});

      setExternalProfiles([]);

      setAgencyDetails(null);

      setAgencyMembers([]);

      setSequenceRuns([]);

      setSequenceTasks([]);

      setSequenceSuppressions([]);

      setCrmTasks([]);

      setNotifications([]);
      setShowNotifPanel(false);
      setOwnerIntroCompleted(true);
      setOwnerIntroStep(0);
      setOwnerIntroPreparing(false);
      setOwnerIntroSaving(false);

      setSequenceControls(DEFAULT_SEQUENCE_CONTROLS);

      setSequenceNotice('');

      setActiveSequenceApiTaskId('');

      setAgencySettings({
        seatLimit: '5',
        billingContactEmail: '',
        reportingScheduleEnabled: false,
        reportingScheduleFrequency: 'weekly',
        reportingScheduleDay: 'monday',
        reportingScheduleRecipients: '',
        forecastMonthlyHireTarget: '10',
        costPerHireBudget: '0',
        monthlyRevenueTarget: '0',
        governanceAgentMetricKeys: ['candidate_changes', 'job_changes', 'stage_moves', 'screenings_done', 'time_to_fill', 'offer_acceptance', 'funnel_overview', 'velocity_overview', 'source_quality_overview', 'source_trend_wow', 'conversion_rates', 'dei_funnel', 'agency_performance', 'segmentation'],
        ...DEFAULT_GOVERNANCE_CONTROLS,
      });

      setAgencyNotice('');

      setProfile(DEFAULT_RECRUITER_PROFILE);

      setIsLoadingWorkspace(false);

      return undefined;

    }



    let cancelled = false;

    setIsLoadingWorkspace(true);



    const bootstrapWorkspace = async () => {

      const profileRef = userDocRef(recruiterUser.uid);

      const existingProfile = await getDoc(profileRef);

      const existingData = existingProfile.exists() ? existingProfile.data() : {};

      const agencyFallback = existingData.agency || authForm.agency || '';
      const storedJobs = sanitizeJobPortfolio(Array.isArray(existingData.jobPortfolio) ? existingData.jobPortfolio : [], agencyFallback);

      // Load agency brand
      if (existingData.brand && typeof existingData.brand === 'object') {
        setAgencyBrand({ ...DEFAULT_AGENCY_BRAND, ...existingData.brand });
      }

      const storedSourcingInbox = Array.isArray(existingData.sourcingInbox) ? existingData.sourcingInbox : [];
      const storedClientOpportunities = Array.isArray(existingData.clientOpportunities) ? existingData.clientOpportunities : [];
      const storedMigrationBatches = Array.isArray(existingData.migrationBatches) ? existingData.migrationBatches : [];

      const baseProfile = {

        email: recruiterUser.email || authForm.email || '',

        role: 'recruiter',

        name: existingData.name || authForm.name || DEFAULT_RECRUITER_PROFILE.name,

        agency: agencyFallback,

        location: existingData.location || authForm.location || DEFAULT_RECRUITER_PROFILE.location,

        title: existingData.title || DEFAULT_RECRUITER_PROFILE.title,

        phone: existingData.phone || DEFAULT_RECRUITER_PROFILE.phone,

        agencySecret: existingData.agencySecret || authForm.agencySecret || DEFAULT_RECRUITER_PROFILE.agencySecret,

        agencyKey: existingData.agencyKey || normalizeText(agencyFallback),

        accountRole: existingData.accountRole || authForm.accountRole || DEFAULT_RECRUITER_PROFILE.accountRole,

        seatAssigned: existingData.seatAssigned ?? DEFAULT_RECRUITER_PROFILE.seatAssigned,

      };

      const shouldShowOwnerIntro = (baseProfile.accountRole === 'owner') && existingData.ownerIntroCompleted !== true;
      if (!cancelled) {
        setOwnerIntroCompleted(!shouldShowOwnerIntro);
        setOwnerIntroStep(0);
        setOwnerIntroPreparing(false);
        setOwnerIntroSaving(false);
      }



      await setDoc(profileRef, {

        email: baseProfile.email,

        name: baseProfile.name,

        agency: baseProfile.agency,

        location: baseProfile.location,

        title: baseProfile.title,

        phone: baseProfile.phone,

        jobPortfolio: storedJobs,

        sourcingInbox: storedSourcingInbox,
        clientOpportunities: storedClientOpportunities,
        migrationBatches: storedMigrationBatches,
        ownerIntroCompleted: existingData.ownerIntroCompleted === true,

        updatedAt: serverTimestamp(),

        createdAt: existingData.createdAt || serverTimestamp(),

      }, { merge: true });



      if (!existingData.workspaceInitialized) {

        const batch = writeBatch(db);

        for (const candidate of INITIAL_CANDIDATES) {

          const linkId = createScreeningLinkId(recruiterUser.uid, candidate.id);

          batch.set(recruiterCandidateDoc(recruiterUser.uid, candidate.id), {

            ...candidate,

            recruiterId: recruiterUser.uid,

            screeningLinkId: linkId,

            createdAt: serverTimestamp(),

            updatedAt: serverTimestamp(),

          }, { merge: true });

        }

        batch.set(profileRef, {

          email: baseProfile.email,

          name: baseProfile.name,

          agency: baseProfile.agency,

          location: baseProfile.location,

          title: baseProfile.title,

          phone: baseProfile.phone,

          jobPortfolio: storedJobs,

          sourcingInbox: storedSourcingInbox,
          clientOpportunities: storedClientOpportunities,
          migrationBatches: storedMigrationBatches,

          workspaceInitialized: true,

          updatedAt: serverTimestamp(),

          createdAt: existingData.createdAt || serverTimestamp(),

        }, { merge: true });

        await batch.commit();

      }

    };



    bootstrapWorkspace().catch((error) => {

      console.error('Failed to bootstrap recruiter workspace', error);

      if (!cancelled) {

        setPipelineNotice('Failed to load recruiter workspace.');

        setIsLoadingWorkspace(false);

      }

    });



    const unsubscribers = [

      onSnapshot(userDocRef(recruiterUser.uid), (snapshot) => {

        if (!snapshot.exists()) return;

        const data = snapshot.data();

        setProfile({

          name: data.name || DEFAULT_RECRUITER_PROFILE.name,

          agency: data.agency || DEFAULT_RECRUITER_PROFILE.agency,

          location: data.location || DEFAULT_RECRUITER_PROFILE.location,

          title: data.title || DEFAULT_RECRUITER_PROFILE.title,

          phone: data.phone || DEFAULT_RECRUITER_PROFILE.phone,

          agencySecret: data.agencySecret || DEFAULT_RECRUITER_PROFILE.agencySecret,

          agencyKey: data.agencyKey || normalizeText(data.agency || DEFAULT_RECRUITER_PROFILE.agency),

          accountRole: data.accountRole || DEFAULT_RECRUITER_PROFILE.accountRole,

          seatAssigned: data.seatAssigned ?? DEFAULT_RECRUITER_PROFILE.seatAssigned,

          email: data.email || recruiterUser.email || '',

        });

        const snapshotRole = data.accountRole || DEFAULT_RECRUITER_PROFILE.accountRole;
        setOwnerIntroCompleted(snapshotRole === 'owner' ? data.ownerIntroCompleted === true : true);

        setSequenceControls({

          hourlyEmailCap: Number(data.sequenceControls?.hourlyEmailCap || DEFAULT_SEQUENCE_CONTROLS.hourlyEmailCap),

          dailyEmailCap: Number(data.sequenceControls?.dailyEmailCap || DEFAULT_SEQUENCE_CONTROLS.dailyEmailCap),

          minDomainCooldownMinutes: Number(data.sequenceControls?.minDomainCooldownMinutes || DEFAULT_SEQUENCE_CONTROLS.minDomainCooldownMinutes),

          whatsappTemplateName: String(data.sequenceControls?.whatsappTemplateName || DEFAULT_SEQUENCE_CONTROLS.whatsappTemplateName),

          whatsappTemplateLanguage: String(data.sequenceControls?.whatsappTemplateLanguage || DEFAULT_SEQUENCE_CONTROLS.whatsappTemplateLanguage),

        });

        const rawJobPortfolio = Array.isArray(data.jobPortfolio) ? data.jobPortfolio : [];

        const sanitizedJobPortfolio = sanitizeJobPortfolio(rawJobPortfolio, data.agency || DEFAULT_RECRUITER_PROFILE.agency);

        setJobs(sanitizedJobPortfolio);

        setJobPortfolioCleanupDraft(

          serializeJobPortfolioForComparison(rawJobPortfolio) === serializeJobPortfolioForComparison(sanitizedJobPortfolio)

            ? null

            : sanitizedJobPortfolio

        );

        setSourcingInbox(Array.isArray(data.sourcingInbox) ? data.sourcingInbox : []);
        setClientOpportunities(Array.isArray(data.clientOpportunities) ? data.clientOpportunities : []);
        setMigrationBatches(Array.isArray(data.migrationBatches) ? data.migrationBatches : []);
        setSavedContactViews(Array.isArray(data.savedContactViews) ? data.savedContactViews : []);
        setSavedCandidateViews(Array.isArray(data.savedCandidateViews) ? data.savedCandidateViews : []);
        setDedupDismissed(Array.isArray(data.dedupDismissed) ? data.dedupDismissed : []);
        setTalentLists(Array.isArray(data.talentLists) ? data.talentLists : []);

      }),

      onSnapshot(query(recruiterCandidatesCollection(recruiterUser.uid)), (snapshot) => {

        const nextCandidates = snapshot.docs.map((candidateDoc) => ({ ...candidateDoc.data(), id: candidateDoc.id }));

        setCandidates(sortCandidates(nextCandidates));

        setIsLoadingWorkspace(false);

      }),

      onSnapshot(query(recruiterCompaniesCollection(recruiterUser.uid)), (snapshot) => {

        const nextCompanies = snapshot.docs
          .map((companyDoc) => ({ id: companyDoc.id, ...companyDoc.data() }))
          .sort((left, right) => String(left.name || '').localeCompare(String(right.name || '')));

        setCompanies(nextCompanies);

      }),

      onSnapshot(query(recruiterContactsCollection(recruiterUser.uid)), (snapshot) => {

        const nextContacts = snapshot.docs
          .map((contactDoc) => ({ id: contactDoc.id, ...contactDoc.data() }))
          .sort((left, right) => String(left.name || left.email || '').localeCompare(String(right.name || right.email || '')));

        setContacts(nextContacts);

      }),

      onSnapshot(query(recruiterSubmissionsCollection(recruiterUser.uid)), (snapshot) => {

        const nextSubmissions = snapshot.docs
          .map((submissionDoc) => ({ id: submissionDoc.id, ...submissionDoc.data() }))
          .sort((left, right) => new Date(right.submittedAtIso || right.createdAtIso || 0).getTime() - new Date(left.submittedAtIso || left.createdAtIso || 0).getTime());

        setSubmissions(nextSubmissions);

      }),

      onSnapshot(query(recruiterApplicationsCollection(recruiterUser.uid)), (snapshot) => {

        const nextApplications = snapshot.docs
          .map((applicationDoc) => ({ id: applicationDoc.id, ...applicationDoc.data() }))
          .sort((left, right) => new Date(right.updatedAtIso || right.createdAtIso || 0).getTime() - new Date(left.updatedAtIso || left.createdAtIso || 0).getTime());

        setApplications(nextApplications);

      }),

      onSnapshot(query(recruiterPlacementsCollection(recruiterUser.uid)), (snapshot) => {
        const nextPlacements = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
        setPlacements(nextPlacements);
      }),

      onSnapshot(query(recruiterReviewQueueCollection(recruiterUser.uid)), (snapshot) => {

        const nextReviewItems = snapshot.docs

          .map((reviewDoc) => ({ ...reviewDoc.data(), id: reviewDoc.id }))

          .filter((item) => item.reviewState !== 'rejected' && !item.rejectedAt)

          .sort((left, right) => new Date(right.submittedAt || right.createdAtIso || 0).getTime() - new Date(left.submittedAt || left.createdAtIso || 0).getTime());

        setCandidateReviewQueue(nextReviewItems);

      }),

      onSnapshot(query(recruiterExternalProfilesCollection(recruiterUser.uid)), (snapshot) => {

        const nextProfiles = snapshot.docs.map((profileDoc) => ({ id: profileDoc.id, ...profileDoc.data() }));

        setExternalProfiles(nextProfiles);

      }),

      onSnapshot(query(recruiterResponsesCollection(recruiterUser.uid)), (snapshot) => {

        const nextResponses = {};

        snapshot.docs.forEach((responseDoc) => {

          nextResponses[responseDoc.id] = responseDoc.data();

        });

        setScreeningResponses(nextResponses);

      }),

      onSnapshot(query(recruiterSequenceRunsCollection(recruiterUser.uid)), (snapshot) => {

        const nextRuns = snapshot.docs

          .map((runDoc) => ({ id: runDoc.id, ...runDoc.data() }))

          .sort((left, right) => new Date(right.startedAtIso || 0).getTime() - new Date(left.startedAtIso || 0).getTime());

        setSequenceRuns(nextRuns);

      }),

      onSnapshot(query(recruiterSequenceTasksCollection(recruiterUser.uid)), (snapshot) => {

        const nextTasks = snapshot.docs

          .map((taskDoc) => ({ id: taskDoc.id, ...taskDoc.data() }))

          .sort((left, right) => new Date(right.updatedAtIso || right.createdAtIso || 0).getTime() - new Date(left.updatedAtIso || left.createdAtIso || 0).getTime());

        setSequenceTasks(nextTasks);

      }),

      onSnapshot(query(recruiterSequenceSuppressionsCollection(recruiterUser.uid)), (snapshot) => {

            const nextSuppressions = snapshot.docs

              .map((suppressionDoc) => ({ id: suppressionDoc.id, ...suppressionDoc.data() }))

              .sort((left, right) => new Date(right.updatedAtIso || right.lastTriggeredAtIso || 0).getTime() - new Date(left.updatedAtIso || left.lastTriggeredAtIso || 0).getTime());

            setSequenceSuppressions(nextSuppressions);

          }, (error) => {

            console.warn('Failed to load sequence suppressions snapshot', error);

            setSequenceSuppressions([]);

          }),

      onSnapshot(query(recruiterActivityCollection(recruiterUser.uid)), (snapshot) => {

        const nextActivity = snapshot.docs

          .map((activityDoc) => ({ id: activityDoc.id, ...activityDoc.data() }))

          .sort((left, right) => new Date(right.createdAtIso || 0).getTime() - new Date(left.createdAtIso || 0).getTime());

        setActivityLog(nextActivity);

      }),

      onSnapshot(query(recruiterCrmTasksCollection(recruiterUser.uid)), (snapshot) => {
        const nextTasks = snapshot.docs
          .map((taskDoc) => ({ id: taskDoc.id, ...taskDoc.data() }))
          .sort((a, b) => {
            const priorityOrder = { Urgent: 0, High: 1, Medium: 2, Low: 3 };
            const pd = (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2);
            if (pd !== 0) return pd;
            return new Date(a.dueDate || '9999').getTime() - new Date(b.dueDate || '9999').getTime();
          });
        setCrmTasks(nextTasks);
      }),

      onSnapshot(query(recruiterCrmAutomationRulesCollection(recruiterUser.uid)), (snap) => {
        setCrmAutomationRules(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }),

      onSnapshot(query(recruiterCrmCadenceEnrollmentsCollection(recruiterUser.uid)), (snap) => {
        setCrmCadenceEnrollments(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }),

      onSnapshot(query(recruiterCrmEmailTrackingCollection(recruiterUser.uid), orderBy('sentAtIso', 'desc'), limit(300)), (snap) => {
        setCrmEmailTracking(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }),

      onSnapshot(query(recruiterNotificationsCollection(recruiterUser.uid), orderBy('createdAt', 'desc'), limit(40)), (snap) => {
        setNotifications(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      }),

    ];



    return () => {

      cancelled = true;

      unsubscribers.forEach((unsubscribe) => unsubscribe());

    };

  }, [authForm.agency, authForm.email, authForm.location, authForm.name, isDemoWorkspace, recruiterUser?.uid]);



  useEffect(() => {

    if (!db || !recruiterUser?.uid || isLoadingWorkspace || candidates.length === 0) return undefined;

    const migrationKey = `candidate-cv-backfill-v1:${recruiterUser.uid}`;

    if (typeof window !== 'undefined' && window.localStorage?.getItem(migrationKey) === 'done') return undefined;

    const candidateBackfills = candidates
      .map((candidate) => {
        const resumeUrl = getCandidateResumeUrl(candidate);
        if (!resumeUrl || (typeof candidate?.resumeUrl === 'string' && candidate.resumeUrl.trim())) return null;

        const fileName = getCandidateResumeFileName(candidate, resumeUrl);

        return {
          id: candidate.id,
          resumeUrl,
          fileName,
        };
      })
      .filter(Boolean);

    if (candidateBackfills.length === 0) {
      if (typeof window !== 'undefined') window.localStorage?.setItem(migrationKey, 'done');
      return undefined;
    }

    let cancelled = false;

    const backfillCandidateCvUrls = async () => {

      try {

        for (let index = 0; index < candidateBackfills.length; index += 400) {

          const chunk = candidateBackfills.slice(index, index + 400);

          const batch = writeBatch(db);

          chunk.forEach(({ id, resumeUrl, fileName }) => {

            batch.set(recruiterCandidateDoc(recruiterUser.uid, id), {
              resumeUrl,
              ...(fileName ? { fileName } : {}),
              updatedAt: serverTimestamp(),
            }, { merge: true });

          });

          await batch.commit();

        }

        if (cancelled) return;

        const backfilledById = new Map(candidateBackfills.map((candidate) => [candidate.id, candidate]));

        setCandidates((previousCandidates) => sortCandidates(previousCandidates.map((candidate) => {
          const backfill = backfilledById.get(candidate.id);
          if (!backfill) return candidate;
          return {
            ...candidate,
            resumeUrl: backfill.resumeUrl,
            fileName: candidate.fileName || backfill.fileName || '',
          };
        })));

        setPipelineNotice(`${candidateBackfills.length} existing candidate CV record${candidateBackfills.length === 1 ? '' : 's'} backfilled.`);

        if (typeof window !== 'undefined') window.localStorage?.setItem(migrationKey, 'done');

      } catch (error) {

        console.error('Failed to backfill candidate CV URLs', error);

        if (!cancelled) {
          setPipelineNotice('Failed to backfill legacy candidate CV records.');
        }

      }

    };

    backfillCandidateCvUrls();

    return () => {
      cancelled = true;
    };

  }, [candidates, isLoadingWorkspace, recruiterUser?.uid]);

  useEffect(() => {

    if (!db || !recruiterUser?.uid || isLoadingWorkspace || candidates.length === 0 || jobs.length === 0) return undefined;

    const syncKey = `candidate-submissions-graph-v1:${recruiterUser.uid}`;

    const candidateSubmissions = candidates
      .map((candidate) => {
        const matchedJob = jobs.find((job) => job.id === candidate.matchedJobId) || jobs.find((job) => normalizeText(job.title) === normalizeText(candidate.matchedJobTitle) && normalizeText(getJobCompanyName(job)) === normalizeText(candidate.company));
        const companyName = candidate.company || (matchedJob ? getJobCompanyName(matchedJob) : '');
        if (!candidate.id || (!candidate.matchedJobId && !candidate.matchedJobTitle && !companyName)) return null;
        const jobId = candidate.matchedJobId || matchedJob?.id || '';
        const latestSubmittal = Array.isArray(candidate.submittals) && candidate.submittals.length > 0
          ? candidate.submittals[candidate.submittals.length - 1]
          : null;
        return {
          id: createSubmissionRecordId(candidate.id, jobId || candidate.matchedJobTitle, companyName),
          candidateId: candidate.id,
          candidateName: candidate.name || 'Candidate',
          jobId,
          jobTitle: candidate.matchedJobTitle || matchedJob?.title || candidate.role || '',
          companyName,
          contactId: latestSubmittal?.contactId || '',
          contactName: latestSubmittal?.contactName || matchedJob?.clientContact || '',
          submittedBy: latestSubmittal?.submittedBy || '',
          outcome: latestSubmittal?.outcome || 'pending',
          feedback: latestSubmittal?.feedback || '',
          submittalCount: Array.isArray(candidate.submittals) ? candidate.submittals.length : 0,
          stage: candidate.status || 'submitted',
          screeningStatus: screeningResponses[candidate.id]?.answers?.length || candidate.screeningSubmittedAt ? 'completed' : 'pending',
          screeningLinkId: candidate.screeningLinkId || '',
          revenue: Number(candidate.placementFee || 0),
          submittedAtIso: candidate.submittedAt || candidate.createdAtIso || candidate.lastActivityAt || new Date().toISOString(),
          updatedAtIso: candidate.lastActivityAt || new Date().toISOString(),
          source: candidate.source || 'pipeline',
        };
      })
      .filter(Boolean);

    if (candidateSubmissions.length === 0) return undefined;

    const syncSignature = JSON.stringify(candidateSubmissions.map((submission) => [submission.id, submission.stage, submission.screeningStatus, submission.revenue, submission.jobId, submission.companyName]));

    if (typeof window !== 'undefined' && window.sessionStorage?.getItem(syncKey) === syncSignature) return undefined;

    let cancelled = false;

    const syncSubmissions = async () => {
      try {
        for (let index = 0; index < candidateSubmissions.length; index += 400) {
          const chunk = candidateSubmissions.slice(index, index + 400);
          const batch = writeBatch(db);
          chunk.forEach((submission) => {
            batch.set(recruiterSubmissionDoc(recruiterUser.uid, submission.id), {
              ...submission,
              recruiterId: recruiterUser.uid,
              updatedAt: serverTimestamp(),
            }, { merge: true });
          });
          await batch.commit();
        }
        if (!cancelled && typeof window !== 'undefined') window.sessionStorage?.setItem(syncKey, syncSignature);
      } catch (error) {
        console.warn('Failed to sync candidate submissions graph', error);
      }
    };

    syncSubmissions();

    return () => {
      cancelled = true;
    };

  }, [candidates, isLoadingWorkspace, jobs, recruiterUser?.uid, screeningResponses]);

  useEffect(() => {

    if (!db || !recruiterUser?.uid || isLoadingWorkspace || candidates.length === 0 || jobs.length === 0) return undefined;

    const candidateApplications = candidates
      .map((candidate) => buildApplicationRecordFromCandidate(candidate, jobs, screeningResponses))
      .filter(Boolean);

    if (candidateApplications.length === 0) return undefined;

    const syncKey = `candidate-applications-v1:${recruiterUser.uid}`;
    const syncSignature = JSON.stringify(candidateApplications.map((application) => [
      application.id,
      application.stage,
      application.screeningStatus,
      application.matchScore,
      application.revenue,
      application.submittedAtIso,
      application.feedback,
    ]));

    if (typeof window !== 'undefined' && window.sessionStorage?.getItem(syncKey) === syncSignature) return undefined;

    let cancelled = false;

    const syncApplications = async () => {
      try {
        for (let index = 0; index < candidateApplications.length; index += 400) {
          const chunk = candidateApplications.slice(index, index + 400);
          const batch = writeBatch(db);
          chunk.forEach((application) => {
            batch.set(recruiterApplicationDoc(recruiterUser.uid, application.id), {
              ...application,
              recruiterId: recruiterUser.uid,
              nextAction: getApplicationNextAction(application),
              risk: getApplicationRisk(application).label,
              updatedAt: serverTimestamp(),
            }, { merge: true });
          });
          await batch.commit();
        }
        if (!cancelled && typeof window !== 'undefined') window.sessionStorage?.setItem(syncKey, syncSignature);
      } catch (error) {
        console.warn('Failed to sync candidate applications graph', error);
      }
    };

    syncApplications();

    return () => {
      cancelled = true;
    };

  }, [candidates, isLoadingWorkspace, jobs, recruiterUser?.uid, screeningResponses]);


  useEffect(() => {

    if (!db || !recruiterUser?.uid || isLoadingWorkspace || candidates.length === 0) return undefined;

    const candidatesNeedingScreeningResponse = candidates
      .filter((candidate) => {
        if (!candidate?.id || screeningResponses[candidate.id]) return false;
        const answers = candidate.screeningAnswers;
        if (!answers || typeof answers !== 'object' || Array.isArray(answers)) return false;
        return Object.keys(answers).some((question) => String(question || '').trim());
      })
      .slice(0, 400);

    if (candidatesNeedingScreeningResponse.length === 0) return undefined;

    let cancelled = false;

    const backfillApplicationScreeningResponses = async () => {

      try {

        const batch = writeBatch(db);

        candidatesNeedingScreeningResponse.forEach((candidate) => {

          const qaEntries = Object.entries(candidate.screeningAnswers || {})
            .map(([question, answer]) => [String(question || '').trim(), String(answer ?? '')])
            .filter(([question]) => question);

          if (!qaEntries.length) return;

          batch.set(recruiterResponseDoc(recruiterUser.uid, candidate.id), {
            candidateId: candidate.id,
            recruiterId: recruiterUser.uid,
            screeningLinkId: candidate.screeningLinkId || createScreeningLinkId(recruiterUser.uid, candidate.id),
            candidateName: candidate.name || '',
            role: candidate.role || candidate.matchedJobTitle || '',
            company: candidate.company || '',
            questions: qaEntries.map(([question]) => question),
            answers: qaEntries.map(([, answer]) => answer),
            submittedAt: candidate.submittedAt || candidate.createdAtIso || new Date().toISOString(),
            source: candidate.source || 'candidate_application',
            updatedAt: serverTimestamp(),
          }, { merge: true });

        });

        await batch.commit();

      } catch (error) {

        if (!cancelled) console.error('Failed to backfill application screening responses', error);

      }

    };

    backfillApplicationScreeningResponses();

    return () => {
      cancelled = true;
    };

  }, [candidates, isLoadingWorkspace, recruiterUser?.uid, screeningResponses]);



  useEffect(() => {

    if (!db || !jobIntakeLinkId) {

      setPublicJobIntakeLink(null);

      return undefined;

    }

    const unsubscribe = onSnapshot(publicJobIntakeLinkDoc(jobIntakeLinkId), (snapshot) => {

      setPublicJobIntakeLink(snapshot.exists() ? { id: jobIntakeLinkId, ...snapshot.data() } : null);

    }, (error) => {

      console.error('Failed to load public job intake link', error);

      setPublicJobIntakeLink(null);

    });

    return () => unsubscribe();

  }, [jobIntakeLinkId]);





  useEffect(() => {

    if (!db || !candidateJobIntakeLinkId) {

      setPublicCandidateJobIntakeLink(null);

      return undefined;

    }

    const unsubscribe = onSnapshot(publicCandidateJobIntakeLinkDoc(candidateJobIntakeLinkId), (snapshot) => {

      setPublicCandidateJobIntakeLink(snapshot.exists() ? { id: candidateJobIntakeLinkId, ...snapshot.data() } : null);

    }, (error) => {

      console.error('Failed to load public candidate application link', error);

      setPublicCandidateJobIntakeLink(null);

    });

    return () => unsubscribe();

  }, [candidateJobIntakeLinkId]);





  useEffect(() => {

    const publicJobs = Array.isArray(publicCandidateJobIntakeLink?.jobs) ? publicCandidateJobIntakeLink.jobs : [];

    if (!publicJobs.length) {

      setSelectedPublicCandidateJobId('');

      return;

    }

    if (!publicJobs.some((job) => job.id === selectedPublicCandidateJobId)) {

      setSelectedPublicCandidateJobId(publicJobs[0].id);

    }

  }, [publicCandidateJobIntakeLink, selectedPublicCandidateJobId]);



  useEffect(() => {

    setLinkedInBackfillRequested(false);

  }, [recruiterUser?.uid]);



  useEffect(() => {

    if (!db || !recruiterUser?.uid || linkedInBackfillRequested) return;

    if (!jobs.some((job) => shouldBackfillLinkedInImportedJob(job))) {

      setLinkedInBackfillRequested(true);

      return;

    }



    let cancelled = false;

    setLinkedInBackfillRequested(true);



    const backfillLinkedInJobs = async () => {

      try {

        const functionsClient = getFunctions(app, 'us-central1');

        const backfillLinkedInImportedJobs = httpsCallable(functionsClient, 'backfillLinkedInImportedJobs');

        const result = await backfillLinkedInImportedJobs({ appId });

        const updatedJobsCount = Number(result?.data?.updatedJobsCount || 0);

        if (!cancelled && updatedJobsCount > 0) {

          setJobPortfolioNotice(`Vision repaired ${updatedJobsCount} older LinkedIn imports with source links and summaries.`);

        }

      } catch (error) {

        console.error('Failed to backfill old LinkedIn imported jobs', error);

        if (!cancelled) setLinkedInBackfillRequested(false);

      }

    };



    backfillLinkedInJobs();

    return () => {

      cancelled = true;

    };

  }, [db, jobs, linkedInBackfillRequested, recruiterUser?.uid]);

  useEffect(() => {

    if (!recruiterUser?.uid || !jobPortfolioCleanupDraft) return;



    let cancelled = false;

    const persistCleanedPortfolio = async () => {

      try {

        await setDoc(userDocRef(recruiterUser.uid), {

          jobPortfolio: jobPortfolioCleanupDraft,

          updatedAt: serverTimestamp(),

        }, { merge: true });

        if (!cancelled) {

          setJobPortfolioNotice(`Vision cleaned ${jobPortfolioCleanupDraft.length} valid jobs in the portfolio and removed corrupted rows.`);

          setJobPortfolioCleanupDraft(null);

        }

      } catch (error) {

        console.error('Failed to persist cleaned job portfolio', error);

      }

    };



    persistCleanedPortfolio();

    return () => {

      cancelled = true;

    };

  }, [jobPortfolioCleanupDraft, recruiterUser?.uid]);

  // Reset retry counter when the actual identity changes (not on retry key bumps)
  useEffect(() => {
    agencyPermissionRetries.current = 0;
    setAgencyListenersKey(0);
  }, [profile.agencyKey, recruiterUser?.uid]);

  useEffect(() => {

    if (!db || !recruiterUser?.uid || !profile.agencyKey) {

      setAgencyDetails(null);

      setAgencyMembers([]);
      setTeamMessages([]);
      setTeamMessageDraft('');
      setTeamMessageSending(false);

      return undefined;

    }

    const retryAfterTokenRefresh = () => {
      if (agencyPermissionRetries.current >= 2) return;
      agencyPermissionRetries.current++;
      Promise.resolve(recruiterUser?.getIdToken?.(true) ?? Promise.resolve())
        .catch(() => {})
        .finally(() => { setTimeout(() => setAgencyListenersKey((k) => k + 1), 1500); });
    };

    const unsubscribers = [

      onSnapshot(agencyDocRef(profile.agencyKey), (snapshot) => {

        setAgencyDetails(snapshot.exists() ? snapshot.data() : null);

      }, (error) => {

        console.warn('Failed to load agency snapshot', error);

        setAgencyDetails(null);

        if (error?.code === 'permission-denied') retryAfterTokenRefresh();

      }),

      onSnapshot(query(agencyMembersCollection(profile.agencyKey)), (snapshot) => {

        const members = snapshot.docs.map((memberDoc) => ({ id: memberDoc.id, ...memberDoc.data() }));

        setAgencyMembers(members);

      }, (error) => {

        console.warn('Failed to load agency members snapshot', error);

        setAgencyMembers([]);

        if (error?.code === 'permission-denied') {

          setPipelineNotice('Agency team controls are restricted until your Vision membership record is available.');

          retryAfterTokenRefresh();

        }

      }),

      onSnapshot(query(agencyTeamMessagesCollection(profile.agencyKey), orderBy('createdAt', 'desc'), limit(50)), (snapshot) => {

        setTeamMessages(snapshot.docs.map((messageDoc) => ({ id: messageDoc.id, ...messageDoc.data() })));

      }, (error) => {

        console.warn('Failed to load agency team messages snapshot', error);

        setTeamMessages([]);

        if (error?.code === 'permission-denied') retryAfterTokenRefresh();

      }),

    ];



    return () => unsubscribers.forEach((unsubscribe) => unsubscribe());

  }, [profile.agencyKey, recruiterUser?.uid, agencyListenersKey]);





  useEffect(() => {

    if (!agencyDetails) {

      setAgencySettings((current) => ({

        ...current,

        billingContactEmail: profile.email || recruiterUser?.email || '',

        reportingScheduleEnabled: false,

        reportingScheduleFrequency: 'weekly',

        reportingScheduleDay: 'monday',

        reportingScheduleRecipients: '',

        forecastMonthlyHireTarget: '10',

        lawfulUseAccepted: false,

        governanceAgentMetricKeys: ['candidate_changes', 'job_changes', 'stage_moves', 'screenings_done', 'time_to_fill', 'offer_acceptance', 'funnel_overview', 'velocity_overview', 'source_quality_overview', 'source_trend_wow', 'conversion_rates', 'dei_funnel', 'agency_performance', 'segmentation'],

      }));

      setTimeManagement(DEFAULT_TIME_MANAGEMENT);

      return;

    }

    setAgencySettings({

      seatLimit: String(agencyDetails.seatLimit || 5),

      billingContactEmail: agencyDetails.billingContactEmail || profile.email || recruiterUser?.email || '',

      reportingScheduleEnabled: Boolean(agencyDetails.reportingSettings?.schedule?.enabled),

      reportingScheduleFrequency: String(agencyDetails.reportingSettings?.schedule?.frequency || 'weekly'),

      reportingScheduleDay: String(agencyDetails.reportingSettings?.schedule?.day || 'monday'),

      reportingScheduleRecipients: String(agencyDetails.reportingSettings?.schedule?.recipients || ''),

      forecastMonthlyHireTarget: String(agencyDetails.reportingSettings?.forecasting?.monthlyHireTarget || '10'),

      governanceAgentMetricKeys: Array.isArray(agencyDetails.reportingSettings?.governance?.agentMetricKeys) && agencyDetails.reportingSettings?.governance?.agentMetricKeys.length > 0
        ? agencyDetails.reportingSettings.governance.agentMetricKeys
        : ['candidate_changes', 'job_changes', 'stage_moves', 'screenings_done', 'time_to_fill', 'offer_acceptance', 'funnel_overview', 'velocity_overview', 'source_quality_overview', 'source_trend_wow'],

      webhookUrl: String(agencyDetails.reportingSettings?.webhook?.url || ''),
      webhookSecret: String(agencyDetails.reportingSettings?.webhook?.secret || ''),
      webhookEvents: Array.isArray(agencyDetails.reportingSettings?.webhook?.events) ? agencyDetails.reportingSettings.webhook.events : [],
      lawfulUseAccepted: Boolean(agencyDetails.compliance?.lawfulUseAccepted),

    });
    setWebhookUrl(String(agencyDetails.reportingSettings?.webhook?.url || ''));
    setWebhookSecret(String(agencyDetails.reportingSettings?.webhook?.secret || ''));
    setWebhookEvents(Array.isArray(agencyDetails.reportingSettings?.webhook?.events) ? agencyDetails.reportingSettings.webhook.events : []);

    setSavedReports(Array.isArray(agencyDetails.savedReports) ? agencyDetails.savedReports : []);
    setAutomationDismissed(Array.isArray(agencyDetails.automationDismissed) ? agencyDetails.automationDismissed : []);
    const smtp = agencyDetails.smtpSettings || {};
    setSmtpSettings(smtp);
    setSmtpDraft({ host: smtp.host || '', port: String(smtp.port || '587'), secure: smtp.secure || false, user: smtp.user || '', pass: smtp.pass || '', fromName: smtp.fromName || '', fromEmail: smtp.fromEmail || '' });
    setTimeManagement({

      calendarDate: agencyDetails.timeManagement?.calendarDate || DEFAULT_TIME_MANAGEMENT.calendarDate,

      scheduleItems: agencyDetails.timeManagement?.scheduleItems || DEFAULT_TIME_MANAGEMENT.scheduleItems,

      reminders: agencyDetails.timeManagement?.reminders || DEFAULT_TIME_MANAGEMENT.reminders,

      dailyGoals: agencyDetails.timeManagement?.dailyGoals || DEFAULT_TIME_MANAGEMENT.dailyGoals,

    });

  }, [agencyDetails, profile.email, recruiterUser?.email]);



  useEffect(() => {

    if (!recruiterUser?.uid || !profile.agencyKey || !agencyDetails || agencyMembers.length === 0) return;

    const currentMember = agencyMembers.find((member) => member.id === recruiterUser.uid);

    const ownerViewingMembership = currentMember?.role === 'owner' && agencyDetails?.ownerUid === recruiterUser.uid;

    if (!currentMember) {

      setAuthError('This user no longer has agency membership in Vision.');

      setPipelineNotice('Agency membership was removed. Please contact your agency owner.');

      auth?.logout?.();

      return;

    }

    if (!ownerViewingMembership && agencyDetails.membershipRequired && (!currentMember.seatAssigned || currentMember.seatStatus !== 'active')) {

      setAuthError('Your paid seat is no longer active. Contact the agency owner.');

      setPipelineNotice('Your paid seat is no longer active. Contact the agency owner.');

      auth?.logout?.();

    }

  }, [agencyDetails, agencyMembers, profile.agencyKey, recruiterUser?.uid]);



  useEffect(() => {

    const email = String(recruiterUser?.email || profile.email || '').trim().toLowerCase();

    const agencyKey = profile.agencyKey || normalizeText(profile.agency);

    if (!recruiterUser?.uid || !email || !agencyKey || !agencyDetails) return;

    const ownerViewingMembership = (currentAgencyMember?.role || profile.accountRole) === 'owner' && agencyDetails?.ownerUid === recruiterUser.uid;

    if (!ownerViewingMembership || isAgencySubscriptionActive(agencyDetails)) return;

    const attemptKey = `${email}:${agencyKey}`;

    if (whopRedemptionAttempts.current.has(attemptKey)) return;

    whopRedemptionAttempts.current.add(attemptKey);

    let cancelled = false;

    (async () => {

      try {

        const redeemWhopEntitlement = httpsCallable(functionsClient, 'redeemWhopEntitlement');

        const result = await redeemWhopEntitlement({ appId, agencyKey, email });

        if (!cancelled && result?.data?.redeemed) {

          setPipelineNotice('Your Whop payment was detected and your Vision workspace is active. Refreshing membership status...');

        }

      } catch (error) {

        console.warn('Whop entitlement auto-redemption did not apply', error);

      }

    })();

    return () => { cancelled = true; };

  }, [agencyDetails, currentAgencyMember?.role, functionsClient, profile.accountRole, profile.agency, profile.agencyKey, profile.email, recruiterUser?.email, recruiterUser?.uid]);



  useEffect(() => {

    if (!db || !screeningLinkId) {

      setIsLoadingScreeningCandidate(false);

      return undefined;

    }



    setIsLoadingScreeningCandidate(true);

    const unsubscribe = onSnapshot(publicScreeningLinkDoc(screeningLinkId), (snapshot) => {

      setScreeningLinks((current) => ({

        ...current,

        [screeningLinkId]: snapshot.exists() ? { id: screeningLinkId, ...snapshot.data() } : null,

      }));

      setIsLoadingScreeningCandidate(false);

    }, (error) => {

      console.error('Failed to load screening link', error);

      setScreeningLinks((current) => ({ ...current, [screeningLinkId]: null }));

      setIsLoadingScreeningCandidate(false);

    });



    return () => unsubscribe();

  }, [screeningLinkId]);



  const stageBuckets = useMemo(

    () => PIPELINE_STAGES.map((stage) => ({ stage, items: candidates.filter((candidate) => candidate.status === stage.id) })),

    [candidates]

  );



  const filteredTableCandidates = useMemo(() => {

    const nextCandidates = candidates.filter((candidate) => {

      const stageMatch = !pipelineFilters.stage || candidate.status === pipelineFilters.stage;

      const companyMatch = !pipelineFilters.company || String(candidate.company || '').toLowerCase().includes(pipelineFilters.company.toLowerCase());

      const roleMatch = !pipelineFilters.role || String(candidate.role || '').toLowerCase().includes(pipelineFilters.role.toLowerCase());

      return stageMatch && companyMatch && roleMatch;

    });



    return [...nextCandidates].sort((left, right) => {

      if (pipelineSortKey === 'fit') {

        return Number(right.rating || 0) - Number(left.rating || 0);

      }

      if (pipelineSortKey === 'activity') {

        return new Date(right.lastActivityAt || right.submittedAt || 0).getTime() - new Date(left.lastActivityAt || left.submittedAt || 0).getTime();

      }

      const leftIndex = PIPELINE_STAGES.findIndex((stage) => stage.id === left.status);

      const rightIndex = PIPELINE_STAGES.findIndex((stage) => stage.id === right.status);

      if (leftIndex !== rightIndex) return leftIndex - rightIndex;

      return String(left.name || '').localeCompare(String(right.name || ''));

    });

  }, [candidates, pipelineFilters.company, pipelineFilters.role, pipelineFilters.stage, pipelineSortKey]);



  const intakeAnalytics = useMemo(() => {

    const counts = importSummary.reduce((accumulator, item) => {

      accumulator[item.action] = (accumulator[item.action] || 0) + 1;

      return accumulator;

    }, { accepted: 0, deleted: 0, failed: 0 });

    const crmTrackedCandidates = candidates.filter((candidate) => candidate.rawText || candidate.intakeBatchId || candidate.source !== 'Demo workspace seed');

    const recentTrackedCandidates = [...crmTrackedCandidates].sort((left, right) => new Date(right.lastActivityAt || right.submittedAt || 0).getTime() - new Date(left.lastActivityAt || left.submittedAt || 0).getTime());

    return {

      accepted: counts.accepted || 0,

      deleted: counts.deleted || 0,

      failed: counts.failed || 0,

      crmTrackedCount: crmTrackedCandidates.length,

      automationReadyCount: crmTrackedCandidates.filter((candidate) => candidate.automationState?.vacancyValidated && candidate.automationState?.duplicateChecked).length,

      screeningReadyCount: crmTrackedCandidates.filter((candidate) => Boolean(candidate.screeningLinkId)).length,

      recentTrackedCandidates: recentTrackedCandidates.slice(0, 6),

    };

  }, [candidates, importSummary]);



  useEffect(() => {

    if (!jobs.length) {

      setSelectedJobId('');

      setSelectedJobPanelId('');

      return;

    }

    if (!jobs.some((job) => job.id === selectedJobId)) {

      setSelectedJobId(jobs[0].id);

    }

    if (selectedJobPanelId && !jobs.some((job) => job.id === selectedJobPanelId)) {

      setSelectedJobPanelId('');

    }

  }, [jobs, selectedJobId, selectedJobPanelId]);



  const selectedJob = jobs.find((job) => job.id === selectedJobId) || jobs[0] || null;

  const selectedJobPanel = jobs.find((job) => job.id === selectedJobPanelId) || null;

  const effectiveBooleanQuery = booleanSearchInput || selectedJob?.booleanQuery || createBooleanQueryFromJob(selectedJob);

  const sourcedCandidates = useMemo(() => {

    if (activeTab !== 'jobs' && !selectedJobPanelId) return [];

    return candidates

      .map((candidate) => ({

        candidate,

        match: scoreBooleanMatch(candidate, effectiveBooleanQuery),

      }))

      .filter((item) => item.match.matched)

      .sort((left, right) => right.match.score - left.match.score)

      .slice(0, 12);

  }, [activeTab, candidates, effectiveBooleanQuery, selectedJobPanelId]);

  const linkedInSourcingCommand = selectedJob ? createLinkedInSourcingCommand(selectedJob, effectiveBooleanQuery) : '';



  useEffect(() => {

    if (isLoadingWorkspace || (activeTab !== 'jobs' && !selectedJobPanelId)) {

      setRankedSourceLeads([]);

      return;

    }

    if (!recruiterUser?.uid || !selectedJob) {

      setRankedSourceLeads([]);

      return;

    }

    setIsRankingSourceLeads(true);

    const manualRanked = sourcingInbox

      .map((lead) => {

        const profile = buildSourceLeadProfile(lead, selectedJob);

        const match = scoreBooleanMatch({ ...profile, email: '', phone: '', matchedJobTitle: selectedJob?.title }, effectiveBooleanQuery);

        return { lead, profile, match, sourceType: 'manual', itemId: lead.id };

      });

    const externalRanked = externalProfiles

      .map((profile) => {

        const portfolioMatch = findBestProspectJob(profile, jobs);

        const enrichedProfile = {

          ...profile,

          matchedJobId: portfolioMatch.matchedJobId,

          matchedJobTitle: portfolioMatch.matchedJobTitle,

          matchedCompanyName: portfolioMatch.matchedCompanyName,

          rating: portfolioMatch.rating,

        };

        return {

          lead: enrichedProfile,

          profile: enrichedProfile,

          match: {

            matched: Boolean(portfolioMatch.matchedJobId),

            score: portfolioMatch.fitScore,

          },

          sourceType: 'external',

          itemId: profile.id,

        };

      });

    setRankedSourceLeads([...externalRanked, ...manualRanked].sort((left, right) => {

      const leftCreated = new Date(left?.lead?.createdAtIso || left?.lead?.addedAtIso || 0).getTime();

      const rightCreated = new Date(right?.lead?.createdAtIso || right?.lead?.addedAtIso || 0).getTime();

      if (leftCreated !== rightCreated) return rightCreated - leftCreated;

      return (right?.match?.score || 0) - (left?.match?.score || 0);

    }));

    setIsRankingSourceLeads(false);

  }, [activeTab, effectiveBooleanQuery, externalProfiles, isLoadingWorkspace, jobs, recruiterUser?.uid, selectedJob, selectedJobPanelId, sourcingInbox, sourcingRefreshNonce]);



  const selectedScrapedJob = scrapedJobs.find((listing) => listing.id === selectedScrapedJobId) || scrapedJobs[0] || null;

  const recruiterCompanyMessage = useMemo(() => buildRecruiterCompanyMessage(selectedScrapedJob, profile.name, profile.agency, outreachTone), [outreachTone, profile.agency, profile.name, selectedScrapedJob]);

  const getHistoricalPlacementProbability = (jobTypeOrTitle = '') => {
    const queryText = normalizeText(jobTypeOrTitle);
    const relevantJobs = jobs.filter((job) => {
      const haystack = normalizeText([job.type, job.title, job.department].join(' '));
      return queryText ? haystack.includes(queryText) || queryText.includes(normalizeText(job.type || '')) : true;
    });
    const relevantJobIds = new Set(relevantJobs.map((job) => job.id));
    const relevantCandidates = candidates.filter((candidate) => relevantJobIds.has(candidate.matchedJobId) || normalizeText(candidate.role).includes(queryText));
    if (relevantCandidates.length < 4) return 0.35;
    const hired = relevantCandidates.filter((candidate) => String(candidate.status || '').toLowerCase() === 'hired' || Number(candidate.placementFee || 0) > 0).length;
    return Math.max(0.12, Math.min(0.82, hired / relevantCandidates.length));
  };

  const clientCrmTouchpoints = useMemo(() => {
    const items = [];
    activityLog.forEach((item) => {
      const company = item?.metadata?.company || item?.metadata?.companyName || item?.metadata?.clientCompany || '';
      if (!company) return;
      items.push({
        id: item.id,
        companyName: company,
        type: item.type || 'activity',
        summary: item.summary || 'Client activity recorded.',
        createdAtIso: item.createdAtIso || '',
      });
    });
    sequenceTasks.forEach((task) => {
      const company = task?.target?.company || task?.companyName || '';
      if (!company) return;
      items.push({
        id: task.id,
        companyName: company,
        type: task.channel || 'sequence',
        summary: task.draftMessage || task.instructions || 'Client sequence touchpoint.',
        createdAtIso: task.updatedAtIso || task.createdAtIso || '',
      });
    });
    jobs.forEach((job) => {
      if (!job.lastOutreach) return;
      items.push({
        id: `${job.id}-last-outreach`,
        companyName: getJobCompanyName(job),
        type: 'last_outreach',
        summary: `${job.title} outreach recorded.`,
        createdAtIso: job.lastOutreach,
      });
    });
    return items.sort((left, right) => new Date(right.createdAtIso || 0).getTime() - new Date(left.createdAtIso || 0).getTime());
  }, [activityLog, jobs, sequenceTasks]);

  const clientCrmOpportunities = useMemo(() => clientOpportunities.map((opportunity) => {
    const probability = Number(opportunity.probability || getHistoricalPlacementProbability(opportunity.jobType || opportunity.title));
    const fee = Number(String(opportunity.fee || '').replace(/[^0-9.]/g, '')) || 0;
    const stage = normalizeClientOpportunityStage(opportunity.stage);
    return {
      ...opportunity,
      stage,
      probability,
      weightedValue: Math.round(fee * probability),
      forecastMonth: opportunity.forecastMonth || getOpportunityForecastMonth(opportunity),
    };
  }), [clientOpportunities, candidates, jobs]);

  const contactCommercialRollups = useMemo(() => {
    if (activeTab !== 'jobs' && !selectedContactId && !selectedJobPanelId && !selectedAccountId) return {};
    return contacts.reduce((accumulator, contact) => {
      accumulator[contact.id] = buildContactCommercialRollup(contact, {
        opportunities: clientCrmOpportunities,
        jobs,
        submissions,
        placements,
        touchpoints: clientCrmTouchpoints,
        activityLog,
        candidates,
      });
      return accumulator;
    }, {});
  }, [activeTab, activityLog, candidates, clientCrmOpportunities, clientCrmTouchpoints, contacts, jobs, placements, selectedAccountId, selectedContactId, selectedJobPanelId, submissions]);

  const candidateIntelligenceIndex = useMemo(() => {
    if (activeTab !== 'pipeline' && activeTab !== 'jobs' && !selectedCandidateId && !selectedJobPanelId) return {};
    return candidates.reduce((accumulator, candidate) => {
      accumulator[candidate.id] = buildCandidateIntelligenceProfile(candidate, screeningResponses[candidate.id]);
      return accumulator;
    }, {});
  }, [activeTab, candidates, screeningResponses, selectedCandidateId, selectedJobPanelId]);

  const candidateCommercialIndex = useMemo(() => candidates.reduce((accumulator, candidate) => {
    const candidateSubmissions = submissions.filter((submission) => submission.candidateId === candidate.id || normalizeText(submission.candidateName) === normalizeText(candidate.name));
    const candidatePlacements = placements.filter((placement) => placement.candidateId === candidate.id || normalizeText(placement.candidateName) === normalizeText(candidate.name));
    const candidateApplications = applications.filter((application) => application.candidateId === candidate.id || normalizeText(application.candidateName) === normalizeText(candidate.name));
    const timeline = [
      ...(Array.isArray(candidate.crmTimeline) ? candidate.crmTimeline : []),
      ...(Array.isArray(candidate.stageHistory) ? candidate.stageHistory.map((item) => ({ ...item, type: 'stage_move', createdAt: item.movedAt })) : []),
      ...candidateSubmissions.map((item) => ({ ...item, type: 'client_submission', createdAt: item.submittedAtIso || item.createdAtIso })),
      ...candidatePlacements.map((item) => ({ ...item, type: 'placement', createdAt: item.createdAtIso || item.invoiceDate })),
    ].sort((left, right) => new Date(right.createdAt || 0).getTime() - new Date(left.createdAt || 0).getTime());
    const lastContactedAt = candidate.lastContactedAt || candidate.lastActivityAt || timeline.find((item) => /email|call|message|contact|submission/i.test(String(item.type || item.eventType || '')))?.createdAt || '';
    const lifetimeRevenue = candidatePlacements.reduce((sum, placement) => sum + (Number(placement.directFee || placement.placementFee || placement.fee || 0) || 0), 0) + (Number(candidate.placementFee || 0) || 0);
    const reusableSignals = [
      Number(candidate.rating || 0) >= 70,
      Boolean(candidate.email || candidate.phone),
      Boolean(candidate.rawText || candidate.parsedProfile),
      !['hired', 'rejected'].includes(String(candidate.status || '').toLowerCase()),
      timeline.length > 1,
    ].filter(Boolean).length;
    accumulator[candidate.id] = {
      ownerRecruiterId: candidate.ownerRecruiterId || candidate.recruiterId || recruiterUser?.uid || '',
      ownerName: candidate.ownerRecruiterName || agencyMembers.find((member) => member.id === (candidate.ownerRecruiterId || candidate.recruiterId))?.name || profile.name || 'Unassigned',
      relationshipStatus: candidate.relationshipStatus || (lastContactedAt ? 'Warm relationship' : 'Untouched'),
      lastContactedAt,
      lifetimeApplications: Math.max(candidateApplications.length, candidate.matchedJobId ? 1 : 0),
      lifetimeSubmissions: Math.max(candidateSubmissions.length, Array.isArray(candidate.submittals) ? candidate.submittals.length : 0),
      lifetimePlacements: Math.max(candidatePlacements.length, String(candidate.status || '').toLowerCase() === 'hired' ? 1 : 0),
      lifetimeRevenue,
      reusableScore: Math.min(100, Math.round(reusableSignals * 18 + Math.min(timeline.length * 3, 10))),
      marketabilityScore: Math.min(100, Math.round(Number(candidate.rating || 0) * 0.55 + (candidateIntelligenceIndex[candidate.id]?.parseabilityScore || 50) * 0.25 + reusableSignals * 4)),
      timeline,
    };
    return accumulator;
  }, {}), [agencyMembers, applications, candidateIntelligenceIndex, candidates, placements, profile.name, recruiterUser?.uid, submissions]);

  const candidateSmartCollections = useMemo(() => {
    const now = Date.now();
    const openJobIds = new Set(jobs.filter((job) => !['closed', 'removed'].includes(String(job.status || '').toLowerCase())).map((job) => job.id));
    const stale = candidates.filter((candidate) => {
      const lastMs = new Date(candidate.lastActivityAt || candidate.submittedAt || candidate.updatedAtIso || 0).getTime();
      return lastMs && now - lastMs > 30 * 86400000 && !['hired', 'rejected'].includes(String(candidate.status || '').toLowerCase());
    });
    const parsedNoScreening = candidates.filter((candidate) => (candidate.rawText || candidate.parsedProfile) && !screeningResponses[candidate.id] && !candidate.screeningSubmittedAt);
    const bestForOpenJobs = candidates.filter((candidate) => openJobIds.has(candidate.matchedJobId) && Number(candidate.rating || 0) >= 70);
    const missingSalary = candidates.filter((candidate) => !candidate.salaryExpectation && !candidate.offerSalary && !candidate.expectedSalary);
    const needsNarrative = candidates.filter((candidate) => candidateIntelligenceIndex[candidate.id]?.scope?.needs?.length);
    const underused = candidates.filter((candidate) => Number(candidate.rating || 0) >= 70 && !candidate.submittals?.length && !['hired', 'rejected'].includes(String(candidate.status || '').toLowerCase()));
    return [
      { id: 'best-open', label: 'Best matches for open jobs', count: bestForOpenJobs.length, tone: 'cyan', description: 'High-fit candidates already tied to active job orders.' },
      { id: 'stale', label: 'High-value candidates going cold', count: stale.length, tone: 'amber', description: 'Candidates without recent activity for 30+ days.' },
      { id: 'parsed-no-screening', label: 'Parsed CV, no screening', count: parsedNoScreening.length, tone: 'violet', description: 'CV intelligence exists, but screening is not complete.' },
      { id: 'missing-salary', label: 'Missing salary expectation', count: missingSalary.length, tone: 'rose', description: 'Records missing compensation data before submission.' },
      { id: 'narrative', label: 'Narrative needs leveling', count: needsNarrative.length, tone: 'emerald', description: 'CV language needs quantified scope or ownership proof.' },
      { id: 'underused', label: 'Strong but unused', count: underused.length, tone: 'blue', description: 'Strong profiles not yet submitted to a client.' },
    ];
  }, [candidateIntelligenceIndex, candidates, jobs, screeningResponses]);

  // ── Contact Database: filtered + sorted view ────────────────────────────
  const filteredContactDb = useMemo(() => {
    const q = contactDbSearch.toLowerCase().trim();
    return contacts.filter((c) => {
      if (q && !String(c.name || '').toLowerCase().includes(q) && !String(c.email || '').toLowerCase().includes(q) && !String(c.company || c.companyName || '').toLowerCase().includes(q) && !String(c.role || '').toLowerCase().includes(q) && !String(c.phone || '').toLowerCase().includes(q)) return false;
      if (contactDbFilters.company && normalizeText(c.company || c.companyName || '') !== normalizeText(contactDbFilters.company)) return false;
      if (contactDbFilters.role && c.role !== contactDbFilters.role) return false;
      if (contactDbFilters.sentiment && (c.sentiment || 'Neutral') !== contactDbFilters.sentiment) return false;
      if (contactDbFilters.source && (c.source || 'manual') !== contactDbFilters.source) return false;
      if (contactDbFilters.tag) { const tags = Array.isArray(c.tags) ? c.tags : []; if (!tags.includes(contactDbFilters.tag)) return false; }
      return true;
    }).sort((a, b) => {
      if (contactDbSort === 'name') return String(a.name || '').localeCompare(String(b.name || ''));
      if (contactDbSort === 'company') return String(a.companyName || '').localeCompare(String(b.companyName || ''));
      if (contactDbSort === 'recent') return new Date(b.updatedAtIso || b.createdAtIso || 0).getTime() - new Date(a.updatedAtIso || a.createdAtIso || 0).getTime();
      if (contactDbSort === 'sentiment') return String(a.sentiment || '').localeCompare(String(b.sentiment || ''));
      return 0;
    });
  }, [contacts, contactDbSearch, contactDbFilters, contactDbSort]);

  // ── Candidate Database: filtered + sorted view ──────────────────────────
  const filteredCandidateDb = useMemo(() => {
    const q = candidateDbSearch.trim();
    return candidates.map((candidate) => ({
      candidate,
      semantic: scoreSemanticCandidateSearch(candidate, q, screeningResponses[candidate.id]),
    })).filter(({ candidate: c, semantic }) => {
      if (q && !semantic.matched) return false;
      if (candidateDbFilters.company && !String(c.company || '').toLowerCase().includes(candidateDbFilters.company.toLowerCase())) return false;
      if (candidateDbFilters.role && !String(c.role || '').toLowerCase().includes(candidateDbFilters.role.toLowerCase())) return false;
      if (candidateDbFilters.status && c.status !== candidateDbFilters.status) return false;
      if (candidateDbFilters.source && (c.source || '') !== candidateDbFilters.source) return false;
      if (candidateDbFilters.tag) { const tags = Array.isArray(c.tags) ? c.tags : []; if (!tags.includes(candidateDbFilters.tag)) return false; }
      return true;
    }).sort((a, b) => {
      if (q) return b.semantic.score - a.semantic.score || Number(b.candidate.rating || 0) - Number(a.candidate.rating || 0);
      const left = a.candidate;
      const right = b.candidate;
      if (candidateDbSort === 'name') return String(left.name || '').localeCompare(String(right.name || ''));
      if (candidateDbSort === 'company') return String(left.company || '').localeCompare(String(right.company || ''));
      if (candidateDbSort === 'recent') return new Date(right.lastActivityAt || right.createdAtIso || 0).getTime() - new Date(left.lastActivityAt || left.createdAtIso || 0).getTime();
      if (candidateDbSort === 'rating') return Number(right.rating || 0) - Number(left.rating || 0);
      if (candidateDbSort === 'marketability') return Number(candidateCommercialIndex[right.id]?.marketabilityScore || 0) - Number(candidateCommercialIndex[left.id]?.marketabilityScore || 0);
      if (candidateDbSort === 'reusable') return Number(candidateCommercialIndex[right.id]?.reusableScore || 0) - Number(candidateCommercialIndex[left.id]?.reusableScore || 0);
      return 0;
    }).map(({ candidate, semantic }) => ({ ...candidate, semanticSearchScore: semantic.score, semanticEvidence: semantic.evidence, semanticGaps: semantic.gaps, intelligenceProfile: semantic.profile, commercialProfile: candidateCommercialIndex[candidate.id] || {} }));
  }, [candidateCommercialIndex, candidates, candidateDbSearch, candidateDbFilters, candidateDbSort, screeningResponses]);

  // ── All unique tags across contacts + candidates ─────────────────────────
  const allContactTags = useMemo(() => {
    const s = new Set();
    contacts.forEach((c) => (Array.isArray(c.tags) ? c.tags : []).forEach((t) => s.add(t)));
    return [...s].sort();
  }, [contacts]);
  const allCandidateTags = useMemo(() => {
    const s = new Set();
    candidates.forEach((c) => (Array.isArray(c.tags) ? c.tags : []).forEach((t) => s.add(t)));
    return [...s].sort();
  }, [candidates]);

  // ── Deduplication groups ─────────────────────────────────────────────────
  const contactDupGroups = useMemo(() => computeContactDuplicates(contacts, dedupDismissed), [contacts, dedupDismissed]);
  const candidateDupGroups = useMemo(() => computeCandidateDuplicates(candidates, dedupDismissed), [candidates, dedupDismissed]);
  const totalDupCount = contactDupGroups.length + candidateDupGroups.length;

  const clientPortfolio = useMemo(() => {

    if (activeTab !== 'jobs' && !selectedJobPanelId && !selectedAccountId) return [];

    const clients = jobs.reduce((accumulator, job) => {

      const companyName = getJobCompanyName(job) || 'Unknown company';

      const key = normalizeText(companyName) || job.id;

      if (!accumulator[key]) {

        accumulator[key] = {

          id: key,

          companyName,

          hiringContact: job.clientContact || '',

          relationshipStatus: job.relationshipStatus || 'Active client',

          lastOutreach: job.lastOutreach || '',

          openRoles: 0,

          totalJobOrders: 0,
          submittedCandidates: 0,
          hires: 0,
          opportunities: [],
          weightedPipeline: 0,
          touchpoints: [],
          responseTimeHours: null,
          emailFrequency: 0,
          placementCount: 0,
          lifetimeRevenue: 0,
          forecastValue: 0,
          activeOpportunityCount: 0,
          accountOwner: job.accountOwner || job.ownerName || job.recruiter || '',
          accountTier: job.accountTier || (job.relationshipStatus === 'Active client' ? 'Tier 1' : 'Tier 2'),
          billingTerms: job.billingTerms || '',
          complianceNeeds: job.complianceNeeds || '',
          riskLevel: 'Healthy',
          riskReason: 'Recent activity and open job context are available.',
          salesQueueScore: 0,

          roleTitles: [],

        };

      }

      accumulator[key].totalJobOrders += 1;
      accumulator[key].openRoles += ['closed', 'removed'].includes(String(job.status || '').toLowerCase()) ? 0 : 1;

      accumulator[key].roleTitles.push(job.title);

      if (!accumulator[key].companyLogoUrl && job.companyLogoUrl) accumulator[key].companyLogoUrl = job.companyLogoUrl;

      if (!accumulator[key].linkedInUrl && job.companyLinkedInUrl) accumulator[key].linkedInUrl = job.companyLinkedInUrl;

      if (!accumulator[key].hiringContact && job.clientContact) accumulator[key].hiringContact = job.clientContact;

      if (!accumulator[key].lastOutreach && job.lastOutreach) accumulator[key].lastOutreach = job.lastOutreach;
      if (!accumulator[key].billingTerms && job.billingTerms) accumulator[key].billingTerms = job.billingTerms;
      if (!accumulator[key].complianceNeeds && job.complianceNeeds) accumulator[key].complianceNeeds = job.complianceNeeds;
      if (!accumulator[key].accountOwner && (job.accountOwner || job.ownerName || job.recruiter)) accumulator[key].accountOwner = job.accountOwner || job.ownerName || job.recruiter;

      return accumulator;

    }, {});

    clientCrmOpportunities.forEach((opportunity) => {
      const companyName = opportunity.companyName || 'Unknown company';
      const key = normalizeText(companyName) || opportunity.id;
      if (!clients[key]) {
        clients[key] = {
          id: key,
          companyName,
          hiringContact: opportunity.contactName || opportunity.contactEmail || '',
          relationshipStatus: 'Prospect',
          lastOutreach: '',
          openRoles: 0,
          totalJobOrders: 0,
          submittedCandidates: 0,
          hires: 0,
          opportunities: [],
          weightedPipeline: 0,
          touchpoints: [],
          responseTimeHours: null,
          emailFrequency: 0,
          placementCount: 0,
          lifetimeRevenue: 0,
          forecastValue: 0,
          activeOpportunityCount: 0,
          accountOwner: opportunity.accountOwner || opportunity.ownerName || '',
          accountTier: opportunity.accountTier || 'Prospect',
          billingTerms: opportunity.billingTerms || '',
          complianceNeeds: opportunity.complianceNeeds || '',
          riskLevel: 'Needs attention',
          riskReason: 'Prospect has no active job order yet.',
          salesQueueScore: 0,
          roleTitles: [],
        };
      }
      clients[key].opportunities.push(opportunity);
      clients[key].weightedPipeline += Number(opportunity.weightedValue || 0);
      clients[key].forecastValue += Number(opportunity.weightedValue || 0);
      if (!['Closed-Won', 'Closed-Lost'].includes(normalizeClientOpportunityStage(opportunity.stage))) clients[key].activeOpportunityCount += 1;
      if (!clients[key].hiringContact && (opportunity.contactName || opportunity.contactEmail)) clients[key].hiringContact = opportunity.contactName || opportunity.contactEmail;
      if (!clients[key].accountOwner && (opportunity.accountOwner || opportunity.ownerName)) clients[key].accountOwner = opportunity.accountOwner || opportunity.ownerName;
      if (!clients[key].billingTerms && opportunity.billingTerms) clients[key].billingTerms = opportunity.billingTerms;
      if (!clients[key].complianceNeeds && opportunity.complianceNeeds) clients[key].complianceNeeds = opportunity.complianceNeeds;
    });

    candidates.forEach((candidate) => {
      const companyName = candidate.company || '';
      const key = normalizeText(companyName);
      if (!key || !clients[key]) return;
      if (candidate.matchedJobId || candidate.matchedJobTitle) clients[key].submittedCandidates += 1;
      if (String(candidate.status || '').toLowerCase() === 'hired' || Number(candidate.placementFee || 0) > 0) clients[key].hires += 1;
    });

    clientCrmTouchpoints.forEach((touchpoint) => {
      const key = normalizeText(touchpoint.companyName);
      if (!key || !clients[key]) return;
      clients[key].touchpoints.push(touchpoint);
      if (!clients[key].lastOutreach || new Date(touchpoint.createdAtIso || 0) > new Date(clients[key].lastOutreach || 0)) {
        clients[key].lastOutreach = touchpoint.createdAtIso;
      }
    });

    placements.forEach((placement) => {
      const companyName = placement.clientName || placement.companyName || placement.company || '';
      const key = normalizeText(companyName);
      if (!key || !clients[key]) return;
      clients[key].placementCount += 1;
      clients[key].lifetimeRevenue += Number(placement.invoiceTotal || placement.directFee || placement.placementFee || placement.fee || 0) || 0;
    });

    return Object.values(clients).map((client) => {
      const submittalToHireRatio = client.hires > 0 ? `${client.submittedCandidates}:${client.hires}` : `${client.submittedCandidates}:0`;
      const emailFrequency = client.touchpoints.filter((touchpoint) => /email|sequence|outreach/i.test(touchpoint.type)).length;
      const intelligence = computeRelationshipIntelligence(client, contacts, crmTasks, jobs);
      // Logo: prefer what was scraped from the job, fall back to a company-type contact record
      const companyKey = normalizeText(client.companyName);
      const companyRecord = contacts.find((c) => {
        if (c.type !== 'company') return false;
        const cKey = normalizeText(c.name || '');
        return cKey === companyKey || cKey.includes(companyKey) || companyKey.includes(cKey);
      });
      const companyLogoUrl = client.companyLogoUrl || companyRecord?.companyLogoUrl || '';
      const linkedInUrl = client.linkedInUrl || companyRecord?.linkedInUrl || '';
      const storedCompany = companies.find((company) => normalizeText(company.name || company.companyName) === companyKey);
      const lastTouchAt = client.lastOutreach || storedCompany?.lastTouchAt || '';
      const relationshipHealth = client.riskLevel || intelligence.riskLevel || 'Healthy';
      return {
        ...client,
        accountOwner: client.accountOwner || storedCompany?.accountOwnerName || storedCompany?.accountOwner || '',
        accountTier: storedCompany?.accountTier || client.accountTier || (client.lifetimeRevenue > 0 ? 'Tier 1' : client.openRoles > 0 ? 'Tier 2' : 'Prospect'),
        industry: storedCompany?.industry || client.industry || '',
        status: client.openRoles > 0 ? 'Active' : client.activeOpportunityCount > 0 ? 'Sales pipeline' : client.lifetimeRevenue > 0 ? 'Past client' : 'Prospect',
        lastTouchAt,
        forecastValue: client.forecastValue || client.weightedPipeline || 0,
        relationshipHealth,
        nextAction: intelligence.nextBestAction,
        companyLogoUrl,
        linkedInUrl,
        submittalToHireRatio,
        emailFrequency,
        ...intelligence,
      };
    }).sort((left, right) => right.salesQueueScore - left.salesQueueScore || left.companyName.localeCompare(right.companyName));

  }, [activeTab, candidates, clientCrmOpportunities, clientCrmTouchpoints, companies, contacts, crmTasks, jobs, placements, selectedAccountId, selectedJobPanelId]);

  // ── Automation engine: detect fired rules against current portfolio ───────
  const pendingAutomations = useMemo(() => {
    if (!crmAutomationRules.length || !clientPortfolio.length) return [];
    const dismissed = new Set(automationDismissed);
    const fired = [];
    crmAutomationRules.filter((r) => r.enabled).forEach((rule) => {
      if (rule.trigger === 'no_touch_days') {
        const days = Number(rule.triggerValue || 30);
        clientPortfolio.forEach((client) => {
          if ((client.daysSinceTouch ?? 999) >= days) {
            const pendingId = `${rule.id}__${client.id}`;
            if (!dismissed.has(pendingId)) {
              fired.push({ id: pendingId, rule, client, firedAtIso: new Date().toISOString() });
            }
          }
        });
      } else if (rule.trigger === 'risk_level_is') {
        clientPortfolio.forEach((client) => {
          if (client.riskLevel === rule.triggerValue) {
            const pendingId = `${rule.id}__${client.id}`;
            if (!dismissed.has(pendingId)) {
              fired.push({ id: pendingId, rule, client, firedAtIso: new Date().toISOString() });
            }
          }
        });
      }
    });
    return fired;
  }, [crmAutomationRules, clientPortfolio, automationDismissed]);

  // ── Overdue cadence steps: any active enrollment past its nextStepDueIso ──
  const overdueCadenceItems = useMemo(() => {
    const now = Date.now();
    return crmCadenceEnrollments
      .filter((e) => e.status === 'active' && e.nextStepDueIso && new Date(e.nextStepDueIso).getTime() < now)
      .map((e) => ({
        ...e,
        daysOverdue: Math.floor((now - new Date(e.nextStepDueIso).getTime()) / 86400000),
      }))
      .sort((a, b) => b.daysOverdue - a.daysOverdue);
  }, [crmCadenceEnrollments]);

  const organizationalGraph = useMemo(() => {

    if (activeTab !== 'jobs' && !selectedJobPanelId && !selectedAccountId) {
      return { companies: [], contacts: [], smartCollections: [], stats: { companies: 0, contacts: 0, roleVelocity: 0, submittedCandidates: 0, submissions: 0, completedScreenings: 0, jobOrders: 0, generatedRevenue: 0 } };
    }

    const companyMap = new Map();

    const ensureCompany = (companyName, seed = {}) => {
      const name = String(companyName || seed.name || 'Unknown company').trim() || 'Unknown company';
      const key = normalizeText(name) || seed.id || createCompanyRecordId(name);
      if (!companyMap.has(key)) {
        companyMap.set(key, {
          id: seed.id || createCompanyRecordId(name),
          name,
          relationshipStatus: seed.relationshipStatus || 'Tracked',
          jobs: [],
          contacts: [],
          candidates: [],
          submissions: [],
          opportunities: [],
          touchpoints: [],
          revenue: 0,
          weightedPipeline: 0,
          source: seed.source || 'synthesized',
          ...seed,
        });
      }
      return companyMap.get(key);
    };

    companies.forEach((company) => ensureCompany(company.name || company.companyName, company));

    jobs.forEach((job) => {
      const company = ensureCompany(getJobCompanyName(job), { relationshipStatus: job.relationshipStatus || 'Active client' });
      company.jobs.push(job);
      if (job.clientContact) {
        company.contacts.push({
          id: createContactRecordId(company.name, job.clientContact),
          companyId: company.id,
          companyName: company.name,
          name: String(job.clientContact || '').replace(extractEmailFromText(job.clientContact), '').trim() || job.clientContact,
          email: extractEmailFromText(job.clientContact),
          role: 'Hiring Manager',
          source: 'job_order',
        });
      }
    });

    clientCrmOpportunities.forEach((opportunity) => {
      const company = ensureCompany(opportunity.companyName, { relationshipStatus: 'Prospect' });
      company.opportunities.push(opportunity);
      company.weightedPipeline += Number(opportunity.weightedValue || 0);
      if (opportunity.contactName || opportunity.contactEmail) {
        company.contacts.push({
          id: createContactRecordId(company.name, opportunity.contactEmail || opportunity.contactName),
          companyId: company.id,
          companyName: company.name,
          name: opportunity.contactName || opportunity.contactEmail,
          email: extractEmailFromText(opportunity.contactEmail),
          role: 'Economic Buyer',
          source: 'opportunity',
        });
      }
    });

    contacts.forEach((contact) => {
      if (contact.type === 'company') {
        ensureCompany(contact.name || contact.companyName, {
          ...contact,
          relationshipStatus: contact.relationshipStatus || 'Tracked',
        });
        return;
      }
      const contactCompanyName = contact.company || contact.companyName;
      const company = ensureCompany(contactCompanyName, { id: contact.companyId || createCompanyRecordId(contactCompanyName), relationshipStatus: 'Tracked' });
      company.contacts.push(contact);
    });

    const syntheticSubmissions = candidates
      .map((candidate) => {
        const job = jobs.find((item) => item.id === candidate.matchedJobId);
        const companyName = candidate.company || (job ? getJobCompanyName(job) : '');
        if (!candidate.id || (!candidate.matchedJobId && !candidate.matchedJobTitle && !companyName)) return null;
        return {
          id: createSubmissionRecordId(candidate.id, candidate.matchedJobId || candidate.matchedJobTitle, companyName),
          candidateId: candidate.id,
          candidateName: candidate.name || 'Candidate',
          jobId: candidate.matchedJobId || job?.id || '',
          jobTitle: candidate.matchedJobTitle || job?.title || candidate.role || '',
          companyName,
          contactName: job?.clientContact || '',
          stage: candidate.status || 'submitted',
          screeningStatus: screeningResponses[candidate.id]?.answers?.length || candidate.screeningSubmittedAt ? 'completed' : 'pending',
          revenue: Number(candidate.placementFee || 0),
          submittedAtIso: candidate.submittedAt || candidate.createdAtIso || '',
          source: 'candidate_graph',
        };
      })
      .filter(Boolean);

    const submissionRecords = Array.from(new Map([...syntheticSubmissions, ...submissions].map((submission) => [submission.id || createSubmissionRecordId(submission.candidateId, submission.jobId || submission.jobTitle, submission.companyName), submission])).values());

    submissionRecords.forEach((submission) => {
      const job = jobs.find((item) => item.id === submission.jobId);
      const companyName = submission.companyName || (job ? getJobCompanyName(job) : '');
      if (!companyName) return;
      const company = ensureCompany(companyName);
      company.submissions.push(submission);
    });

    candidates.forEach((candidate) => {
      const job = jobs.find((item) => item.id === candidate.matchedJobId);
      const companyName = candidate.company || (job ? getJobCompanyName(job) : '');
      if (!companyName) return;
      const company = ensureCompany(companyName);
      company.candidates.push(candidate);
      company.revenue += Number(candidate.placementFee || 0);
    });

    clientCrmTouchpoints.forEach((touchpoint) => {
      const company = ensureCompany(touchpoint.companyName);
      company.touchpoints.push(touchpoint);
    });

    const graphCompanies = Array.from(companyMap.values()).map((company) => {
      const normalizedCompany = normalizeText(company.name);
      const companyContacts = Array.from(new Map(company.contacts.map((contact) => [contact.id || createContactRecordId(company.name, contact.email || contact.name), contact])).values());
      const departmentMix = getDepartmentMix(company.jobs);
      const roleVelocity = getRoleVelocity(company.jobs);
      const openRoles = company.jobs.filter((job) => !['closed', 'removed'].includes(String(job.status || '').toLowerCase())).length;
      const submissionList = company.submissions.length ? company.submissions : company.candidates.filter((candidate) => candidate.matchedJobId || candidate.matchedJobTitle);
      const hires = company.candidates.filter((candidate) => String(candidate.status || '').toLowerCase() === 'hired' || Number(candidate.placementFee || 0) > 0);
      const completedScreenings = submissionList.filter((submission) => submission.screeningStatus === 'completed' || screeningResponses[submission.candidateId]?.answers?.length).length;
      const contactsWithSignals = companyContacts.map((contact) => {
        const touchpoints = company.touchpoints.filter((item) => normalizeText(item.metadata?.contact || item.summary || '').includes(normalizeText(contact.email || contact.name)));
        const relevantTouchpoints = touchpoints.length ? touchpoints : company.touchpoints;
        return {
          ...contact,
          companyName: contact.companyName || company.name,
          sentiment: inferContactSentiment(relevantTouchpoints),
          vibe: contact.vibe && contact.vibe !== 'Balanced Vibe' ? contact.vibe : inferContactVibe(contact, relevantTouchpoints, company.candidates),
          generatedRevenue: company.revenue,
          submittedCandidates: submissionList.length,
        };
      });

      return {
        ...company,
        normalizedCompany,
        contacts: contactsWithSignals,
        departmentMix,
        roleVelocity,
        openRoles,
        submittedCandidates: submissionList.length,
        completedScreenings,
        hires: hires.length,
        submittalToHireRatio: hires.length > 0 ? `${submissionList.length}:${hires.length}` : `${submissionList.length}:0`,
        liquiditySignal: roleVelocity >= 1.5 ? 'Hiring surge' : openRoles > 0 ? 'Active market' : 'Dormant',
      };
    }).sort((left, right) => (right.roleVelocity + right.openRoles + right.weightedPipeline / 10000) - (left.roleVelocity + left.openRoles + left.weightedPipeline / 10000));

    const graphContacts = graphCompanies.flatMap((company) => company.contacts.map((contact) => ({ ...contact, companyName: company.name })));
    const atRiskCompanies = clientPortfolio.filter((client) => ['Watch', 'Needs attention', 'At risk', 'Critical'].includes(client.riskLevel));
    const highLiquidityCompanies = graphCompanies.filter((company) => company.liquiditySignal === 'Hiring surge' || company.openRoles >= 2);
    const ghostedContacts = graphContacts.filter((contact) => contact.sentiment === 'Ghosted');
    const revenueContacts = graphContacts.filter((contact) => Number(contact.generatedRevenue || 0) > 0);
    const autoEmailReady = graphCompanies.filter((company) => company.openRoles > 0 && company.contacts.some((contact) => contact.email) && company.submittedCandidates > 0);

    return {
      companies: graphCompanies,
      contacts: graphContacts,
      smartCollections: [
        { id: 'at-risk', label: 'At-risk client queue', count: atRiskCompanies.length, tone: 'rose', description: 'Top or aging accounts that need outreach now.' },
        { id: 'liquidity', label: 'Hiring surge accounts', count: highLiquidityCompanies.length, tone: 'cyan', description: 'Companies adding roles fastest by department velocity.' },
        { id: 'ghosted', label: 'Ghosted contacts', count: ghostedContacts.length, tone: 'amber', description: 'Contacts with weak or stale response patterns.' },
        { id: 'revenue', label: 'Revenue contacts', count: revenueContacts.length, tone: 'emerald', description: 'Contacts tied to generated placement revenue.' },
        { id: 'automation', label: 'Auto-email ready', count: autoEmailReady.length, tone: 'violet', description: 'New jobs with contacts and submitted/matching talent context.' },
      ],
      stats: {
        companies: graphCompanies.length,
        contacts: graphContacts.length,
        roleVelocity: graphCompanies.reduce((sum, company) => sum + company.roleVelocity, 0),
        submittedCandidates: graphCompanies.reduce((sum, company) => sum + company.submittedCandidates, 0),
        submissions: graphCompanies.reduce((sum, company) => sum + company.submissions.length, 0),
        completedScreenings: graphCompanies.reduce((sum, company) => sum + company.completedScreenings, 0),
        jobOrders: graphCompanies.reduce((sum, company) => sum + company.jobs.length, 0),
        generatedRevenue: graphCompanies.reduce((sum, company) => sum + Number(company.revenue || 0), 0),
      },
    };

  }, [activeTab, candidates, clientCrmOpportunities, clientCrmTouchpoints, clientPortfolio, companies, contacts, jobs, screeningResponses, selectedAccountId, selectedJobPanelId, submissions]);



  const visibleJobs = useMemo(() => {

    if (activeTab !== 'jobs' && !selectedJobPanelId) return [];

    const filtered = jobs.filter((job) => {

      const companyName = getJobCompanyName(job);

      const searchHaystack = normalizeText([job.title, companyName, job.location, job.summary, job.requirements].join(' '));

      if (jobPortfolioFilters.search && !searchHaystack.includes(normalizeText(jobPortfolioFilters.search))) return false;

      if (String(job.status || '').toLowerCase() === 'removed' && normalizeText(jobPortfolioFilters.status) !== 'removed') return false;

      if (jobPortfolioFilters.status && normalizeText(job.status) !== normalizeText(jobPortfolioFilters.status)) return false;

      if (jobPortfolioFilters.type && normalizeText(job.type) !== normalizeText(jobPortfolioFilters.type)) return false;

      if (jobPortfolioFilters.company && normalizeText(companyName) !== normalizeText(jobPortfolioFilters.company)) return false;

      if (jobPortfolioFilters.owner && normalizeText(job.ownerName || '') !== normalizeText(jobPortfolioFilters.owner)) return false;

      if (jobPortfolioFilters.stage && (job.lifecycleStage || 'brief') !== jobPortfolioFilters.stage) return false;

      return true;

    });



    return [...filtered].sort((left, right) => {

      if (jobPortfolioFilters.sort === 'title') return left.title.localeCompare(right.title);

      if (jobPortfolioFilters.sort === 'company') return getJobCompanyName(left).localeCompare(getJobCompanyName(right));

      if (jobPortfolioFilters.sort === 'status') return String(left.status || '').localeCompare(String(right.status || '')) || left.title.localeCompare(right.title);

      if (jobPortfolioFilters.sort === 'oldest') return new Date(left.createdAtIso || 0).getTime() - new Date(right.createdAtIso || 0).getTime();

      return new Date(right.createdAtIso || 0).getTime() - new Date(left.createdAtIso || 0).getTime();

    });

  }, [activeTab, jobPortfolioFilters, jobs, selectedJobPanelId]);



  const jobPortfolioCompanies = useMemo(() => Array.from(new Set(jobs.map((job) => getJobCompanyName(job)).filter(Boolean))).sort((left, right) => left.localeCompare(right)), [jobs]);



  const jobCardMetrics = useMemo(() => {

    if (activeTab !== 'jobs' && !selectedJobPanelId) return {};

    return Object.fromEntries(jobs.map((job) => {

      const companyName = getJobCompanyName(job);

      const matchedCandidates = candidates.filter((candidate) => candidate.matchedJobId === job.id || (normalizeText(candidate.matchedJobTitle) === normalizeText(job.title) && normalizeText(candidate.company) === normalizeText(companyName)));

      return [job.id, {

        matchedCandidatesCount: matchedCandidates.length,

        prospectsCount: externalProfiles.filter((profile) => profile.matchedJobId === job.id).length,

        screeningCompletedCount: matchedCandidates.filter((candidate) => Boolean(screeningResponses[candidate.id]?.answers?.length || candidate.screeningSubmittedAt)).length,

      }];

    }));

  }, [activeTab, candidates, jobs, screeningResponses, selectedJobPanelId]);



  const selectedJobAtsSnapshot = useMemo(() => {

    if (!selectedJobPanel) {

      return null;

    }

    const companyName = getJobCompanyName(selectedJobPanel);

    const matchedCandidates = candidates

      .filter((candidate) => candidate.matchedJobId === selectedJobPanel.id || (normalizeText(candidate.matchedJobTitle) === normalizeText(selectedJobPanel.title) && normalizeText(candidate.company) === normalizeText(companyName)))

      .map((candidate) => ({
        ...candidate,
        aiMatch: scoreCandidateForJob(candidate, selectedJobPanel, screeningResponses[candidate.id]),
      }))

      .sort((left, right) => new Date(right.lastActivityAt || right.submittedAt || 0).getTime() - new Date(left.lastActivityAt || left.submittedAt || 0).getTime());

    const matchedCandidateIds = new Set(matchedCandidates.map((candidate) => candidate.id));

    const rediscoveredCandidates = candidates
      .filter((candidate) => !matchedCandidateIds.has(candidate.id) && !['hired', 'rejected'].includes(String(candidate.status || '').toLowerCase()))
      .map((candidate) => ({
        ...candidate,
        aiMatch: scoreCandidateForJob(candidate, selectedJobPanel, screeningResponses[candidate.id]),
      }))
      .filter((candidate) => candidate.aiMatch.overall >= 58)
      .sort((left, right) => right.aiMatch.overall - left.aiMatch.overall)
      .slice(0, 8);

    const prospects = externalProfiles

      .map((profile) => {

        const portfolioMatch = profile.matchedJobId ? {

          matchedJobId: profile.matchedJobId,

          matchedJobTitle: profile.matchedJobTitle,

          matchedCompanyName: profile.matchedCompanyName,

          rating: profile.rating,

        } : findBestProspectJob(profile, jobs);

        return {

          ...profile,

          matchedJobId: portfolioMatch.matchedJobId,

          matchedJobTitle: portfolioMatch.matchedJobTitle,

          matchedCompanyName: portfolioMatch.matchedCompanyName,

          rating: portfolioMatch.rating,

        };

      })

      .filter((profile) => profile.matchedJobId === selectedJobPanel.id)

      .sort((left, right) => new Date(right.createdAtIso || right.addedAtIso || 0).getTime() - new Date(left.createdAtIso || left.addedAtIso || 0).getTime());

    const screeningCompleted = matchedCandidates.filter((candidate) => Boolean(screeningResponses[candidate.id]?.answers?.length || candidate.screeningSubmittedAt));

    const screeningPending = matchedCandidates.filter((candidate) => !screeningResponses[candidate.id] && ['screening', 'interview'].includes(candidate.status));

    const jobApplications = applications
      .filter((application) => application.jobId === selectedJobPanel.id || (normalizeText(application.jobTitle) === normalizeText(selectedJobPanel.title) && normalizeText(application.companyName) === normalizeText(companyName)))
      .sort((left, right) => new Date(right.updatedAtIso || right.createdAtIso || 0).getTime() - new Date(left.updatedAtIso || left.createdAtIso || 0).getTime());

    const submittedApplications = jobApplications.filter((application) => application.submittedAtIso);
    const awaitingFeedbackApplications = submittedApplications.filter((application) => !application.feedback && String(application.stage || '').toLowerCase() !== 'hired');
    const readyToSubmitApplications = jobApplications.filter((application) => ['screening', 'interview'].includes(String(application.stage || '').toLowerCase()) && !application.submittedAtIso);

    const pipelineCounts = matchedCandidates.reduce((accumulator, candidate) => {

      accumulator[candidate.status] = (accumulator[candidate.status] || 0) + 1;

      return accumulator;

    }, {});

    const candidateIds = new Set(matchedCandidates.map((candidate) => candidate.id));

    const matchedOutreachActivity = activityLog

      .filter((item) => item?.metadata?.jobId === selectedJobPanel.id || candidateIds.has(item?.metadata?.candidateId) || normalizeText(item.summary).includes(normalizeText(selectedJobPanel.title)) || (companyName && normalizeText(item.summary).includes(normalizeText(companyName))));

    const hiddenLegacySequenceFailures = matchedOutreachActivity.filter((item) => isLegacySequenceAuthFailureActivity(item)).length;

    const outreachActivity = matchedOutreachActivity

      .filter((item) => !isLegacySequenceAuthFailureActivity(item))

      .slice(0, 8);

    return {

      matchedCandidates,
      rediscoveredCandidates,
      applications: jobApplications,
      submittedApplications,
      awaitingFeedbackApplications,
      readyToSubmitApplications,

      prospects,

      screeningCompleted,

      screeningPending,

      pipelineCounts,

      outreachActivity,

      hiddenLegacySequenceFailures,

      clientOpenRoles: jobs.filter((item) => normalizeText(getJobCompanyName(item)) === normalizeText(companyName) && !['closed', 'removed'].includes(String(item.status || '').toLowerCase())).length,

    };

  }, [activityLog, applications, candidates, externalProfiles, jobs, screeningResponses, selectedJobPanel]);

  const selectedJobPublishing = useMemo(() => {
    if (!selectedJobPanel) return null;
    return {
      careersUrl: getPublicCareerUrl(agencyBrand, profile, recruiterUser),
      clientBoardUrl: getClientBoardUrl(selectedJobPanel, agencyBrand, profile, recruiterUser),
      jobUrl: getPublicJobUrl(selectedJobPanel, agencyBrand, profile, recruiterUser),
      health: buildJobPublishingHealth(selectedJobPanel, selectedJobAtsSnapshot, agencyBrand),
    };
  }, [agencyBrand, profile, recruiterUser, selectedJobAtsSnapshot, selectedJobPanel]);



  useEffect(() => {

    if (!selectedJobPanel) {

      setJobNotesDraft('');

      return;

    }

    setJobNotesDraft(selectedJobPanel.notes || '');

  }, [selectedJobPanel]);



  const selectedJobSequenceRuns = useMemo(() => {

    if (!selectedJobPanel?.id) return [];

    return sequenceRuns

      .filter((run) => run?.jobSnapshot?.id === selectedJobPanel.id)

      .filter((run) => !isLegacySequenceAuthFailureRun(run));

  }, [selectedJobPanel, sequenceRuns]);



  const selectedJobSequenceAnalytics = useMemo(

    () => buildSequenceAnalytics(selectedJobSequenceRuns),

    [selectedJobSequenceRuns],

  );



  const selectedJobSequenceSuppressions = useMemo(() => {

    if (!selectedJobPanel?.id || !selectedJobAtsSnapshot) return [];

    const targetIds = new Set();

    const targetEmails = new Set();

    const targetPhones = new Set();

    const targetLinkedInUrls = new Set();

    const pushTarget = (target = {}) => {

      if (!target) return;

      if (target.id) targetIds.add(String(target.id));

      const email = String(target.email || '').trim().toLowerCase();

      if (email) targetEmails.add(email);

      const phone = normalizeSequencePhone(target.phone, target.location);

      if (phone) targetPhones.add(phone);

      const linkedInUrl = normalizeLinkedInProfileUrl(target.linkedInUrl);

      if (linkedInUrl) targetLinkedInUrls.add(linkedInUrl.replace(/\/+$/, '').toLowerCase());

    };

    selectedJobSequenceRuns.forEach((run) => pushTarget(run?.target));

    selectedJobAtsSnapshot.matchedCandidates.forEach((candidate) => pushTarget(buildSequenceTargetFromCandidate(candidate)));

    selectedJobAtsSnapshot.prospects.forEach((prospect) => pushTarget(buildSequenceTargetFromProspect(prospect)));



    return sequenceSuppressions.filter((suppression) => {

      const keyType = String(suppression?.keyType || '').toLowerCase();

      const keyValue = String(suppression?.keyValue || '').trim().toLowerCase();

      if (!keyType || !keyValue) return false;

      if (keyType === 'target') return targetIds.has(String(suppression.keyValue));

      if (keyType === 'email') return targetEmails.has(keyValue);

      if (keyType === 'phone') return targetPhones.has(String(suppression.keyValue).trim());

      if (keyType === 'linkedin') return targetLinkedInUrls.has(keyValue.replace(/\/+$/, ''));

      return false;

    });

  }, [selectedJobAtsSnapshot, selectedJobPanel, selectedJobSequenceRuns, sequenceSuppressions]);



  const selectedJobCompanyContacts = useMemo(() => {
    if (!selectedJobPanel) return [];
    const key = normalizeText(getJobCompanyName(selectedJobPanel));
    const graphCompany = organizationalGraph.companies.find((c) => normalizeText(c.name) === key);
    return graphCompany?.contacts || [];
  }, [organizationalGraph, selectedJobPanel]);

  const selectedJobAccountTimeline = useMemo(() => {
    if (!selectedJobPanel) return [];
    const companyName = getJobCompanyName(selectedJobPanel);
    const entries = [];

    // Activity log entries already filtered to this job/company
    if (selectedJobAtsSnapshot) {
      selectedJobAtsSnapshot.outreachActivity.forEach((item) => {
        entries.push({
          id: item.id,
          type: item.type || 'activity',
          summary: item.summary || 'Activity recorded.',
          contact: item.metadata?.contact || '',
          createdAtIso: item.createdAtIso || '',
        });
      });
    }

    // Client CRM touchpoints for this company not already captured above
    const activityIds = new Set(entries.map((e) => e.id));
    clientCrmTouchpoints
      .filter((tp) => normalizeText(tp.companyName) === normalizeText(companyName))
      .filter((tp) => !activityIds.has(tp.id))
      .forEach((tp) => {
        entries.push({
          id: tp.id,
          type: tp.type || 'activity',
          summary: tp.summary || 'Client touchpoint.',
          contact: '',
          createdAtIso: tp.createdAtIso || '',
        });
      });

    // Candidate submission and screening events
    if (selectedJobAtsSnapshot) {
      selectedJobAtsSnapshot.matchedCandidates.forEach((candidate) => {
        if (candidate.submittedAt || candidate.createdAtIso) {
          entries.push({
            id: `submission-${candidate.id}`,
            type: 'submission',
            summary: `${candidate.name || 'Candidate'} submitted for ${selectedJobPanel.title}.`,
            contact: candidate.name || '',
            createdAtIso: candidate.submittedAt || candidate.createdAtIso || '',
          });
        }
        const hasScreening = screeningResponses[candidate.id]?.answers?.length || candidate.screeningSubmittedAt;
        if (hasScreening) {
          entries.push({
            id: `screening-${candidate.id}`,
            type: 'screening_completed',
            summary: `Screening completed by ${candidate.name || 'candidate'}.`,
            contact: candidate.name || '',
            createdAtIso: candidate.screeningSubmittedAt || screeningResponses[candidate.id]?.submittedAt || '',
          });
        }
      });
    }

    // Sequence run starts and reply detections
    selectedJobSequenceRuns.forEach((run) => {
      if (run.startedAtIso) {
        entries.push({
          id: `seq-start-${run.id}`,
          type: 'sequence_started',
          summary: `Sequence started for ${run.target?.name || 'target'} \u2014 ${run.templateName || 'outreach run'}.`,
          contact: run.target?.name || '',
          createdAtIso: run.startedAtIso,
        });
      }
      if (run.replyDetectedAtIso) {
        entries.push({
          id: `seq-reply-${run.id}`,
          type: 'reply_detected',
          summary: `Reply detected from ${run.replyFrom || run.target?.name || 'target'}.`,
          contact: run.replyFrom || run.target?.name || '',
          createdAtIso: run.replyDetectedAtIso,
        });
      }
    });

    // Deduplicate and sort newest first
    const seenIds = new Set();
    return entries
      .filter((entry) => {
        if (!entry.id || seenIds.has(entry.id)) return false;
        seenIds.add(entry.id);
        return true;
      })
      .sort((a, b) => new Date(b.createdAtIso || 0).getTime() - new Date(a.createdAtIso || 0).getTime());
  }, [clientCrmTouchpoints, screeningResponses, selectedJobAtsSnapshot, selectedJobPanel, selectedJobSequenceRuns]);

  const selectedJobCrmTasks = useMemo(() => {
    if (!selectedJobPanel?.id) return [];
    return crmTasks.filter((t) => t.jobId === selectedJobPanel.id || normalizeText(t.companyName) === normalizeText(getJobCompanyName(selectedJobPanel)));
  }, [crmTasks, selectedJobPanel]);

  const selectedJobSequenceTasks = useMemo(() => {

    if (!selectedJobPanel?.id) return [];

    return sequenceTasks.filter((task) => task?.jobSnapshot?.id === selectedJobPanel.id && String(task?.status || '').toLowerCase() === 'open');

  }, [selectedJobPanel, sequenceTasks]);



  const selectedJobHiddenSequenceRunCount = useMemo(() => {

    if (!selectedJobPanel?.id) return 0;

    return sequenceRuns

      .filter((run) => run?.jobSnapshot?.id === selectedJobPanel.id)

      .filter((run) => isLegacySequenceAuthFailureRun(run))

      .length;

  }, [selectedJobPanel, sequenceRuns]);



  const selectedScreeningCandidate = candidates.find((candidate) => candidate.id === selectedScreeningCandidateId) || null;

  const selectedScreeningResponse = selectedScreeningCandidateId ? screeningResponses[selectedScreeningCandidateId] || null : null;

  const screeningLinkRecord = screeningLinkId ? screeningLinks[screeningLinkId] || null : null;

  const candidateScreeningResponse = screeningLinkRecord?.candidateId ? screeningResponses[screeningLinkRecord.candidateId] || null : null;



  const persistScreeningLink = async (recruiterId, candidate, deadlineAt) => {

    if (!db || !recruiterId || !candidate) return candidate?.screeningLinkId || '';

    if (!hasAcceptedLawfulUse) {

      setPipelineNotice('Confirm lawful recruitment and data-processing use in Agency Settings before creating public screening links.');

      return candidate?.screeningLinkId || '';

    }

    const candidateId = candidate.id || candidate.candidateId;

    const linkId = candidate.screeningLinkId || createScreeningLinkId(recruiterId, candidateId);

    const screeningQuestions = normalizeScreeningQuestions(candidate.screeningQuestions);



    await setDoc(recruiterCandidateDoc(recruiterId, candidateId), {

      screeningDeadlineAt: deadlineAt,

      screeningLinkId: linkId,

      screeningQuestions,

      recruiterId,

      updatedAt: serverTimestamp(),

    }, { merge: true });



    const linkPayload = {

      screeningLinkId: linkId,

      recruiterId,

      candidateId,

      name: candidate.name,

      role: candidate.role,

      company: candidate.company,

      location: candidate.location || '',

      rating: candidate.rating,

      screeningQuestions,

      screeningDeadlineAt: deadlineAt,

      privacyNotice: PUBLIC_PRIVACY_NOTICE,

      lawfulUseNotice: PUBLIC_LAWFUL_USE_NOTICE,

      updatedAt: serverTimestamp(),

    };



    await setDoc(publicScreeningLinkDoc(linkId), linkPayload, { merge: true });



    if (linkId !== candidateId) {

      await setDoc(publicScreeningLinkDoc(candidateId), {

        ...linkPayload,

        legacyAliasFor: linkId,

      }, { merge: true });

    }



    return linkId;

  };



  const handleStartDefaultSequence = async (target, sourceType = 'candidate', jobOverride = selectedJobPanel || selectedJob, options = {}) => {

    const { processAfterStart = false } = options;

    if (!recruiterUser?.uid) return;

    const activeJob = jobOverride || selectedJobPanel || selectedJob;

    if (!activeJob) {

      setSequenceNotice('Open a vacancy before starting a sequence.');

      return;

    }



    const normalizedTarget = sourceType === 'prospect'

      ? buildSequenceTargetFromProspect(target)

      : buildSequenceTargetFromCandidate(target);

    const contactReadiness = buildSequenceContactReadiness(normalizedTarget);

    const premiumReadyTarget = contactReadiness.normalizedTarget;

    const targetKey = `${sourceType}:${premiumReadyTarget.id}`;

    const experiment = selectStarterSequenceExperiment(premiumReadyTarget, activeJob);

    const steps = buildStarterSequenceSteps(premiumReadyTarget, activeJob, profile, experiment);

    if (steps.length === 0) {

      const blockedReasons = Object.values(contactReadiness.channels)

        .filter((channel) => !channel.isUsable)

        .map((channel) => channel.message);

      setSequenceNotice(`No usable outreach channels were found for ${premiumReadyTarget.name}. ${blockedReasons.join(' ')}`);

      return;

    }



    setActiveSequenceTargetKey(targetKey);

    setSequenceNotice('');



    try {

      const functionsClient = getFunctions(app, 'us-central1');

      const upsertSequenceTemplate = httpsCallable(functionsClient, 'upsertSequenceTemplate');

      const startSequenceRun = httpsCallable(functionsClient, 'startSequenceRun');

      const templateId = `vision-multichannel-starter-${experiment.personaKey}-${experiment.variantKey}`;

      const templateName = `Vision Multichannel Starter | ${experiment.personaLabel} ${experiment.variantLabel}`;

      await upsertSequenceTemplate({

        appId,

        templateId,

        name: templateName,

        goal: 'Candidate re-engagement',

        audienceType: 'candidate',

        status: 'active',

        experiment,

        steps,

      });

      const startResult = await startSequenceRun({

        appId,

        templateId,

        selectedJobId: activeJob.id,

        experiment,

        target: premiumReadyTarget,

        jobSnapshot: {

          id: activeJob.id,

          title: activeJob.title,

          companyName: getJobCompanyName(activeJob),

          location: activeJob.location || '',

        },

      });

      if (!processAfterStart) {

        setSequenceNotice(`Started a ${steps.length}-step sequence for ${premiumReadyTarget.name} using ${experiment.personaLabel} ${experiment.variantLabel}.${contactReadiness.warningSummary ? ` ${contactReadiness.warningSummary}` : ''}`);

      }

      await recordActivity(recruiterUser.uid, 'sequence_started_from_jobs', `Started a multichannel sequence for ${premiumReadyTarget.name}.`, {

        sourceType,

        targetId: premiumReadyTarget.id,

        runId: startResult?.data?.runId || '',

        jobId: activeJob.id,

        personaKey: experiment.personaKey,

        variantKey: experiment.variantKey,

      });

      if (processAfterStart) {

        await handleProcessSequenceRuns();

      }

    } catch (error) {

      console.error('Failed to start sequence run', error);

      setSequenceNotice(error?.message || 'Failed to start the sequence.');

    } finally {

      setActiveSequenceTargetKey('');

    }

  };



  const handleStartOrAdvanceSequence = async (target, sourceType = 'candidate', jobOverride = selectedJobPanel || selectedJob) => {

    if (!recruiterUser?.uid) return;

    const activeJob = jobOverride || selectedJobPanel || selectedJob;

    if (!activeJob) {

      setSequenceNotice('Open a vacancy before starting a sequence.');

      return;

    }



    const normalizedTarget = sourceType === 'prospect'

      ? buildSequenceTargetFromProspect(target)

      : buildSequenceTargetFromCandidate(target);

    const targetKey = `${sourceType}:${normalizedTarget.id}`;

    const matchingRun = findLatestSequenceRunForTarget(sequenceRuns, activeJob.id, normalizedTarget.id);

    if (!isActionableSequenceRun(matchingRun)) {

      await handleStartDefaultSequence(target, sourceType, activeJob, { processAfterStart: true });

      return;

    }



    setActiveSequenceTargetKey(targetKey);

    setSequenceNotice('');

    try {

      await handleProcessSequenceRuns();

    } finally {

      setActiveSequenceTargetKey('');

    }

  };



  const handleCompleteSequenceTask = async (task, outcome = 'completed') => {

    if (!recruiterUser?.uid || !task?.id || !task?.runId || !task?.stepId) return;

    setActiveSequenceTaskId(task.id);

    setSequenceNotice('');

    try {

      const functionsClient = getFunctions(app, 'us-central1');

      const completeSequenceTask = httpsCallable(functionsClient, 'completeSequenceTask');

      const notes = String(sequenceTaskNotes[task.id] || '').trim();

      await completeSequenceTask({

        appId,

        taskId: task.id,

        runId: task.runId,

        stepId: task.stepId,

        outcome,

        notes,

      });

      setSequenceTaskNotes((current) => {

        const next = { ...current };

        delete next[task.id];

        return next;

      });

      const optionLabel = getSequenceTaskOutcomeOptions(task.channel).find((option) => option.id === outcome)?.label || outcome;

      setSequenceNotice(`${task.title || 'Sequence task'} marked ${optionLabel.toLowerCase()}.`);

    } catch (error) {

      console.error('Failed to complete sequence task', error);

      setSequenceNotice(error?.message || 'Failed to update the sequence task.');

    } finally {

      setActiveSequenceTaskId('');

    }

  };



  const handleSendSequenceWhatsAppTask = async (task) => {

    if (!recruiterUser?.uid || !task?.id || !task?.runId || !task?.stepId) return;

    setActiveSequenceApiTaskId(task.id);

    setSequenceNotice('');

    try {

      const functionsClient = getFunctions(app, 'us-central1');

      const sendSequenceWhatsAppTask = httpsCallable(functionsClient, 'sendSequenceWhatsAppTask');

      const result = await sendSequenceWhatsAppTask({

        appId,

        taskId: task.id,

        runId: task.runId,

        stepId: task.stepId,

      });

      const providerStatus = String(result?.data?.providerStatus || 'queued').toLowerCase();

      setSequenceNotice(`${task.title || 'WhatsApp task'} ${providerStatus === 'queued' ? 'queued in WhatsApp API' : `updated to ${providerStatus}`}.`);

    } catch (error) {

      console.error('Failed to send WhatsApp task', error);

      setSequenceNotice(error?.message || 'Failed to send the WhatsApp task.');

    } finally {

      setActiveSequenceApiTaskId('');

    }

  };



  const handleProcessSequenceRuns = async () => {

    if (!recruiterUser?.uid) return;

    setIsProcessingSequenceRuns(true);

    setSequenceNotice('');

    try {

      const functionsClient = getFunctions(app, 'us-central1');

      const processSequenceRuns = httpsCallable(functionsClient, 'processSequenceRuns');

      const result = await processSequenceRuns({ appId });

      const summary = result?.data?.summary || {};

      const advanced = Number(summary.advanced || 0);

      const waitingManual = Number(summary.waitingManual || 0);

      const completed = Number(summary.completed || 0);

      const failed = Number(summary.failed || 0);

      const blocked = Number(summary.blocked || 0);

      const total = Number(summary.total || result?.data?.processed || 0);

      const syncedReplies = Number(result?.data?.replySync?.syncedRuns || 0);

      const topIssue = Array.isArray(summary.issues) && summary.issues.length > 0 ? summary.issues[0] : null;

      if (total === 0) {

        const baseNotice = buildNoDueSequenceNotice(selectedJobSequenceRuns, selectedJobSequenceTasks);

        setSequenceNotice(syncedReplies > 0 ? `${baseNotice} ${syncedReplies} inbox repl${syncedReplies === 1 ? 'y was' : 'ies were'} detected and closed automatically.` : baseNotice);

      } else if (failed > 0 || blocked > 0) {

        const issueLabel = topIssue?.message ? `${topIssue.count} ${topIssue.channel === 'email_send' ? 'email step' : topIssue.channel === 'linkedin_task' ? 'LinkedIn step' : topIssue.channel === 'whatsapp_task' ? 'WhatsApp step' : topIssue.channel === 'call_task' ? 'call step' : 'sequence step'}${topIssue.count === 1 ? '' : 's'} ${failed > 0 ? 'failed' : 'blocked'}: ${topIssue.message}.` : `${failed + blocked} step${failed + blocked === 1 ? '' : 's'} need attention.`;

        setSequenceNotice(`Processed ${total} due step${total === 1 ? '' : 's'}: ${advanced + completed} advanced, ${waitingManual} task${waitingManual === 1 ? '' : 's'} created.${syncedReplies > 0 ? ` ${syncedReplies} inbox repl${syncedReplies === 1 ? 'y was' : 'ies were'} detected.` : ''} ${issueLabel}`);

      } else {

        setSequenceNotice(`Processed ${total} due step${total === 1 ? '' : 's'}: ${advanced + completed} advanced and ${waitingManual} task${waitingManual === 1 ? '' : 's'} created.${syncedReplies > 0 ? ` ${syncedReplies} inbox repl${syncedReplies === 1 ? 'y was' : 'ies were'} detected.` : ''}`);

      }

    } catch (error) {

      console.error('Failed to process sequence runs', error);

      setSequenceNotice(getCallableErrorMessage(error, 'Failed to advance due sequence steps.'));

    } finally {

      setIsProcessingSequenceRuns(false);

    }

  };



  const handleSaveSequenceControls = async () => {

    if (!recruiterUser?.uid) return;

    setIsSavingSequenceControls(true);

    setSequenceNotice('');

    try {

      const nextControls = {

        hourlyEmailCap: Math.max(1, Number(sequenceControls.hourlyEmailCap || DEFAULT_SEQUENCE_CONTROLS.hourlyEmailCap)),

        dailyEmailCap: Math.max(1, Number(sequenceControls.dailyEmailCap || DEFAULT_SEQUENCE_CONTROLS.dailyEmailCap)),

        minDomainCooldownMinutes: Math.max(0, Number(sequenceControls.minDomainCooldownMinutes || DEFAULT_SEQUENCE_CONTROLS.minDomainCooldownMinutes)),

        whatsappTemplateName: String(sequenceControls.whatsappTemplateName || DEFAULT_SEQUENCE_CONTROLS.whatsappTemplateName).trim(),

        whatsappTemplateLanguage: String(sequenceControls.whatsappTemplateLanguage || DEFAULT_SEQUENCE_CONTROLS.whatsappTemplateLanguage).trim() || DEFAULT_SEQUENCE_CONTROLS.whatsappTemplateLanguage,

      };

      await setDoc(userDocRef(recruiterUser.uid), {

        sequenceControls: nextControls,

        updatedAt: serverTimestamp(),

      }, { merge: true });

      setSequenceControls(nextControls);

      setSequenceNotice('Sequence controls saved for this recruiter workspace.');

    } catch (error) {

      console.error('Failed to save sequence controls', error);

      setSequenceNotice(error?.message || 'Failed to save sequence controls.');

    } finally {

      setIsSavingSequenceControls(false);

    }

  };



  const handleClearSequenceSuppression = async (suppressionId) => {

    if (!recruiterUser?.uid || !suppressionId) return;

    setActiveSequenceSuppressionId(suppressionId);

    setSequenceNotice('');

    try {

      await deleteDoc(recruiterSequenceSuppressionDoc(recruiterUser.uid, suppressionId));

      setSequenceNotice('Sequence suppression cleared.');

    } catch (error) {

      console.error('Failed to clear sequence suppression', error);

      setSequenceNotice(error?.message || 'Failed to clear the suppression.');

    } finally {

      setActiveSequenceSuppressionId('');

    }

  };



  const handleRenewScreeningLink = async (candidateId) => {

    if (!recruiterUser?.uid) return;

    const candidate = candidates.find((item) => item.id === candidateId);

    if (!candidate) return;

    const deadlineAt = new Date(Date.now() + SCREENING_WINDOW_MINUTES * 60 * 1000).toISOString();

    try {

      await persistScreeningLink(recruiterUser.uid, candidate, deadlineAt);

      await recordActivity(recruiterUser.uid, 'screening_link_renewed', `Renewed screening link for ${candidate.name}.`, { candidateId, stage: 'screening' });

      setPipelineNotice(`Screening link renewed for ${candidate.name}.`);

    } catch (error) {

      console.error('Failed to renew screening link', error);

      setPipelineNotice('Failed to renew screening link.');

    }

  };



  const handleSaveScreeningQuestions = async (candidateId, questions) => {

    const candidate = candidates.find((item) => item.id === candidateId);

    if (!candidate) throw new Error('Candidate not found.');

    const screeningQuestions = normalizeScreeningQuestions(questions);

    const deadlineAt = candidate.screeningDeadlineAt || ensureScreeningDeadline(candidate);

    if (isDemoWorkspace) {
      setIsSavingScreeningQuestions(true);
      try {
        const savedAtIso = new Date().toISOString();
        setCandidates((current) => current.map((item) => (
          item.id === candidateId
            ? {
                ...item,
                screeningQuestions,
                screeningDeadlineAt: deadlineAt,
                screeningLinkId: item.screeningLinkId || `demo-direct-screening-${candidateId}`,
                screeningQuestionsUpdatedAt: savedAtIso,
                lastActivityAt: savedAtIso,
                crmTimeline: [
                  ...(Array.isArray(item.crmTimeline) ? item.crmTimeline : []),
                  createCrmEvent('screening_questions_updated', `Updated screening questions for ${item.name || 'candidate'}.`, { candidateId, questionCount: screeningQuestions.length }),
                ],
              }
            : item
        )));
        setPipelineNotice(`${candidate.name || 'Candidate'} screening questions saved.`);
        if (directDemoWalkthroughOpen) {
          setSelectedScreeningCandidateId(null);
          markDirectDemoStepComplete(6);
        }
        if (importDemoWalkthroughOpen) {
          setSelectedScreeningCandidateId(null);
          markImportDemoStepComplete(6);
        }
      } finally {
        setIsSavingScreeningQuestions(false);
      }
      return;
    }

    if (!recruiterUser?.uid || !candidateId || !db) throw new Error('Recruiter or candidate context is missing.');

    setIsSavingScreeningQuestions(true);

    try {

      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {

        screeningQuestions,

        screeningDeadlineAt: deadlineAt,

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await persistScreeningLink(recruiterUser.uid, {

        ...candidate,

        screeningQuestions,

      }, deadlineAt);

      await recordActivity(recruiterUser.uid, 'screening_questions_updated', `Updated screening questions for ${candidate.name}.`, {

        candidateId,

        questionCount: screeningQuestions.length,

      });

    } finally {

      setIsSavingScreeningQuestions(false);

    }

  };



  const handleOpenScreeningCandidate = async (candidateId) => {

    const candidate = candidates.find((item) => item.id === candidateId);

    if (!candidate) {
      setPipelineNotice('Vision could not find that candidate in the pipeline.');
      return;
    }

    const deadlineAt = ensureScreeningDeadline(candidate);

    const screeningEvent = createCrmEvent('screening_opened', `Opened screening review for ${candidate.name}.`, { candidateId, stage: 'screening' });

    if (isDemoWorkspace) {
      setCandidates((current) => current.map((item) => (
        item.id === candidateId
          ? {
              ...item,
              status: item.status || 'screening',
              screeningDeadlineAt: deadlineAt,
              screeningLinkId: item.screeningLinkId || `demo-direct-screening-${candidateId}`,
              screeningQuestions: normalizeScreeningQuestions(item.screeningQuestions),
              crmStatus: 'screening_requested',
              lastActivityAt: screeningEvent.createdAtIso,
              automationState: {
                ...(item.automationState || {}),
                vacancyValidated: true,
                duplicateChecked: true,
                screeningLinkLive: true,
              },
              crmTimeline: [...(Array.isArray(item.crmTimeline) ? item.crmTimeline : []), screeningEvent],
            }
          : item
      )));
      setSelectedScreeningCandidateId(candidateId);
      setPipelineNotice(`${candidate.name || 'Candidate'} screening review opened.`);
      return;
    }

    if (!recruiterUser?.uid) {
      setPipelineNotice('Vision is still preparing the workspace. Try Screening again in a moment.');
      return;
    }

    try {

      await persistScreeningLink(recruiterUser.uid, candidate, deadlineAt);

      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {

        screeningDeadlineAt: deadlineAt,

        crmStatus: 'screening_requested',

        lastActivityAt: screeningEvent.createdAtIso,

        automationState: {

          vacancyValidated: true,

          duplicateChecked: true,

          screeningLinkLive: true,

        },

        crmTimeline: arrayUnion(screeningEvent),

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'screening_opened', `Opened screening review for ${candidate.name}.`, { candidateId, stage: 'screening' });

      setSelectedScreeningCandidateId(candidateId);

    } catch (error) {

      console.error('Failed to persist screening link', error);

      setPipelineNotice('Failed to create screening link.');

    }

  };



  const handleDragStartCard = (event, candidateId) => {

    event.dataTransfer.effectAllowed = 'move';

    event.dataTransfer.setData('text/plain', candidateId);

    setDraggedCandidateId(candidateId);

    setPipelineNotice('');

  };



  const handleDragOverStage = (event, stageId) => {

    if (!draggedCandidateId) return;

    event.preventDefault();

    setDragOverStageId(stageId);

  };



  const updateCandidateStage = async (candidateId, targetStageId, options = {}) => {

    if (!candidateId) return;

    const candidate = candidates.find((item) => item.id === candidateId);

    if (!candidate || candidate.status === targetStageId) return;



    const currentIndex = PIPELINE_STAGES.findIndex((stage) => stage.id === candidate.status);

    const targetIndex = PIPELINE_STAGES.findIndex((stage) => stage.id === targetStageId);

    if (targetIndex === -1) return;



    const nextCandidate = {

      ...candidate,

      status: targetStageId,

      screeningDeadlineAt: targetStageId === 'screening' ? ensureScreeningDeadline(candidate) : candidate.screeningDeadlineAt || null,

    };

    const stageLabel = PIPELINE_STAGES[targetIndex].label;

    const relatedJobId = String(candidate.selectedJobId || candidate.jobId || candidate.matchedJobId || '');

    const stageTransitionMeta = {

      candidateId,

      jobId: relatedJobId,

      recruiterId: recruiterUser?.uid || 'demo-user',

      fromStage: candidate.status,

      toStage: targetStageId,

      stageId: targetStageId,

      createdAtIso: new Date().toISOString(),

      createdAtMs: Date.now(),

    };

    const stageEvent = createCrmEvent('candidate_stage_moved', `${candidate.name} moved to ${stageLabel}.`, stageTransitionMeta);

    if (isDemoWorkspace) {
      setCandidates((current) => current.map((item) => (
        item.id === candidateId
          ? {
              ...nextCandidate,
              crmStatus: `stage_${targetStageId}`,
              lastActivityAt: stageEvent.createdAtIso,
              stageHistory: [
                ...(Array.isArray(item.stageHistory) ? item.stageHistory : []),
                {
                  stageId: targetStageId,
                  stageLabel,
                  movedAt: stageEvent.createdAtIso,
                  movedAtMs: stageEvent.createdAtMs,
                },
              ],
              crmTimeline: [...(Array.isArray(item.crmTimeline) ? item.crmTimeline : []), stageEvent],
            }
          : item
      )));
      setPipelineNotice(`${candidate.name} moved to ${stageLabel}.`);
      if (directDemoWalkthroughOpen && directDemoWalkthroughStep === 8 && targetStageId === 'interview') {
        markDirectDemoStepComplete(8);
      }
      if (importDemoWalkthroughOpen && importDemoWalkthroughStep === 5 && targetStageId === 'screening') {
        markImportDemoStepComplete(5);
      }
      if (importDemoWalkthroughOpen && importDemoWalkthroughStep === 8 && targetStageId === 'interview') {
        markImportDemoStepComplete(8);
      }
      return;
    }

    if (!recruiterUser?.uid) return;



    try {

      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {

        status: nextCandidate.status,

        screeningDeadlineAt: nextCandidate.screeningDeadlineAt,

        crmStatus: `stage_${targetStageId}`,

        lastActivityAt: stageEvent.createdAtIso,

        stageHistory: arrayUnion({

          stageId: targetStageId,

          stageLabel,

          movedAt: stageEvent.createdAtIso,

          movedAtMs: stageEvent.createdAtMs,

        }),

        crmTimeline: arrayUnion(stageEvent),

        updatedAt: serverTimestamp(),

      }, { merge: true });

      if (targetStageId === 'screening') {

        await persistScreeningLink(recruiterUser.uid, nextCandidate, nextCandidate.screeningDeadlineAt);

      }

      await recordActivity(recruiterUser.uid, 'candidate_stage_moved', `${candidate.name} moved to ${stageLabel}.`, stageTransitionMeta);

      setPipelineNotice(`${candidate.name} moved to ${stageLabel}.`);

    } catch (error) {

      console.error('Failed to move candidate', error);

      setPipelineNotice('Failed to update candidate pipeline stage.');

    }

  };



  const handleDropStage = async (event, targetStageId) => {

    event.preventDefault();

    const candidateId = event.dataTransfer.getData('text/plain') || draggedCandidateId;

    setDragOverStageId(null);

    setDraggedCandidateId(null);

    if (targetStageId === 'hired') {
      setPlacementCandidateId(candidateId);
      setPlacementDraft({ ...DEFAULT_PLACEMENT_DRAFT, offerSalary: '', feePercent: '', startDate: '' });
      await updateCandidateStage(candidateId, 'hired');
      return;
    }

    await updateCandidateStage(candidateId, targetStageId);

  };



  const handleTableStageChange = async (candidateId, targetStageId) => {

    await updateCandidateStage(candidateId, targetStageId, { allowAnyStage: true });

  };



  
  const handleAddCandidateNote = async (candidateId, text) => {
    if (!recruiterUser?.uid || !text.trim()) return;
    setIsSavingCandidateNote(true);
    try {
      const note = {
        text: text.trim(),
        authorName: profile.name || recruiterUser.email || 'Recruiter',
        authorUid: recruiterUser.uid,
        createdAt: new Date().toISOString(),
      };
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
        candidateNotes: arrayUnion(note),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCandidateNoteDraft('');
    } catch (err) {
      console.error('Failed to save note', err);
    } finally {
      setIsSavingCandidateNote(false);
    }
  };

  const handleSavePlacement = async (candidateId, draft) => {
    if (!recruiterUser?.uid) return;
    if (!requireAccess('revenue.write', 'You need finance access to create or edit placements.')) return;
    setIsSavingPlacement(true);
    try {
      const cand = candidates.find((c) => c.id === candidateId);
      const salary = Number(String(draft.offerSalary).replace(/[^0-9.]/g, '')) || 0;
      const feePercent = Number(String(draft.feePercent).replace(/[^0-9.]/g, '')) || 0;
      const directFee = Number(String(draft.directFee).replace(/[^0-9.]/g, '')) || 0;
      const placementFee = draft.usePercent
        ? (salary > 0 && feePercent > 0 ? Math.round(salary * feePercent / 100) : 0)
        : directFee;
      const marginPct = Number(String(draft.marginPercent).replace(/[^0-9.]/g, '')) || 0;
      const margin = marginPct > 0 && placementFee > 0 ? Math.round(placementFee * marginPct / 100) : 0;
      const commissionSplit = Number(String(draft.commissionSplit).replace(/[^0-9.]/g, '')) || 0;
      const taxPercent = Number(String(draft.taxPercent).replace(/[^0-9.]/g, '')) || 0;
      const taxAmount = placementFee > 0 && taxPercent > 0 ? Math.round(placementFee * taxPercent / 100) : 0;
      const invoiceTotal = placementFee + taxAmount;
      const currency = PLACEMENT_CURRENCIES.some((item) => item.code === draft.currency) ? draft.currency : 'ZAR';
      const invoiceDueDays = Number(String(draft.invoiceDueDays).replace(/[^0-9]/g, '')) || 30;
      const invoiceDate = draft.invoiceDate || new Date().toISOString().slice(0, 10);
      const invoiceDueDate = new Date(new Date(invoiceDate).getTime() + invoiceDueDays * 86400000).toISOString().slice(0, 10);
      const placementId = `placement-${candidateId}-${Date.now()}`;

      // Save to candidate doc
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
        offerSalary: salary || null,
        placementFeePercent: draft.usePercent ? (feePercent || null) : null,
        placementFee: placementFee || null,
        placementCurrency: currency,
        taxPercent: taxPercent || null,
        taxAmount: taxAmount || null,
        invoiceTotal: invoiceTotal || null,
        marginPercent: marginPct || null,
        margin: margin || null,
        commissionSplit: commissionSplit || null,
        startDate: draft.startDate || null,
        hiredAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      }, { merge: true });

      // Save dedicated placement revenue record
      await setDoc(recruiterPlacementDoc(recruiterUser.uid, placementId), {
        id: placementId,
        candidateId,
        candidateName: cand?.name || '',
        jobId: cand?.matchedJobId || '',
        jobTitle: cand?.matchedJobTitle || cand?.role || '',
        clientName: cand?.company || '',
        contactName: (Array.isArray(cand?.submittals) && cand.submittals.length > 0 ? cand.submittals[cand.submittals.length - 1].contactName : '') || '',
        contactId: (Array.isArray(cand?.submittals) && cand.submittals.length > 0 ? cand.submittals[cand.submittals.length - 1].contactId : '') || '',
        currency,
        placementFee,
        taxPercent,
        taxAmount,
        invoiceTotal,
        offerSalary: salary || null,
        feePercent: draft.usePercent ? feePercent : null,
        marginPercent: marginPct || null,
        margin: margin || null,
        commissionSplit: commissionSplit || null,
        billedBy: String(draft.billedBy || '').trim() || profile?.name || '',
        startDate: draft.startDate || null,
        invoiceNumber: String(draft.invoiceNumber || '').trim() || '',
        invoiceDate,
        invoiceDueDate,
        invoiceStatus: draft.invoiceStatus || 'pending',
        paymentStatus: draft.paymentStatus || 'unpaid',
        paidAt: null,
        notes: '',
        createdAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
        recruiterId: recruiterUser.uid,
      });

      if (cand?.email) {
        try {
          const notifyCandidate = httpsCallable(getFunctions(app, 'us-central1'), 'notifyCandidatePipelineStatus');
          notifyCandidate({
            toEmail: cand.email,
            toName: cand.name || '',
            jobTitle: cand.matchedJobTitle || cand.role || '',
            companyName: cand.company || '',
            agencyName: agencySettings?.agencyName || '',
            status: 'hired',
            startDate: draft.startDate || '',
          }).catch((err) => console.error('[notify] hired email failed:', err?.message));
        } catch (_e) { console.error('[notify] hired setup error', _e); }
      }
      setPlacementCandidateId(null);
      setPlacementDraft(DEFAULT_PLACEMENT_DRAFT);
      if (directDemoWalkthroughOpen) markDirectDemoStepComplete(10);
    } catch (err) {
      console.error('Failed to save placement', err);
    } finally {
      setIsSavingPlacement(false);
    }
  };
const handleSetInterviewDetails = async (candidateId, details) => {
    if (!recruiterUser?.uid) return;
    await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
      interviewDate: details.interviewDate || null,
      interviewType: details.interviewType || null,
      interviewerName: details.interviewerName || null,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    if (details.interviewDate) {
      const cand = candidates.find((c) => c.id === candidateId);
      if (cand?.email) {
        try {
          const notifyCandidate = httpsCallable(getFunctions(app, 'us-central1'), 'notifyCandidatePipelineStatus');
          notifyCandidate({
            toEmail: cand.email,
            toName: cand.name || '',
            jobTitle: cand.matchedJobTitle || cand.role || '',
            companyName: cand.company || '',
            agencyName: agencySettings?.agencyName || '',
            status: 'interview',
            interviewDate: details.interviewDate,
            interviewType: details.interviewType || '',
            interviewerName: details.interviewerName || '',
          }).catch((err) => console.error('[notify] interview email failed:', err?.message));
        } catch (_e) { console.error('[notify] interview setup error', _e); }
      }
    }
  };

  const handleSubmitToClient = async (candidateId, clientName, note) => {
    if (!clientName.trim()) {
      setPipelineNotice('Enter the client/company name before logging the submittal.');
      return;
    }
    const cand = candidates.find((c) => c.id === candidateId);
    if (!cand) {
      setPipelineNotice('Vision could not find that candidate in the pipeline.');
      return;
    }
    const matchedJob = jobs.find((j) => j.id === submittalJobId) || jobs.find((j) => j.id === cand.matchedJobId);
    const record = {
      id: typeof globalThis.crypto?.randomUUID === 'function' ? globalThis.crypto.randomUUID() : `submittal-${Date.now()}`,
      clientName: clientName.trim(),
      jobId: submittalJobId || cand.matchedJobId || '',
      jobTitle: matchedJob?.title || cand.matchedJobTitle || cand.role || '',
      contactId: submittalContactId || '',
      contactName: submittalContactName.trim() || '',
      submittedBy: submittalSubmittedBy.trim() || profile?.name || 'Demo recruiter',
      outcome: submittalOutcome || 'pending',
      feedback: submittalFeedback.trim(),
      note: note.trim(),
      submittedAt: new Date().toISOString(),
    };

    if (isDemoWorkspace) {
      setIsSavingSubmittal(true);
      try {
        setCandidates((current) => current.map((item) => {
          if (item.id !== candidateId) return item;
          const existing = Array.isArray(item.submittals) ? item.submittals : [];
          return {
            ...item,
            submittals: [...existing, record],
            lastSubmittedAt: record.submittedAt,
            lastActivityAt: record.submittedAt,
            crmStatus: 'submitted_to_client',
            crmTimeline: [
              ...(Array.isArray(item.crmTimeline) ? item.crmTimeline : []),
              createCrmEvent('client_submittal_logged', `${item.name || 'Candidate'} submitted to ${record.clientName}.`, { candidateId, jobId: record.jobId, submittalId: record.id }),
            ],
          };
        }));
        setSubmittalCandidateId(null);
        setSubmittalClientName('');
        setSubmittalContactId('');
        setSubmittalContactName('');
        setSubmittalJobId('');
        setSubmittalSubmittedBy('');
        setSubmittalOutcome('pending');
        setSubmittalFeedback('');
        setSubmittalNote('');
        setPipelineNotice(`${cand.name || 'Candidate'} submitted to ${record.clientName}.`);
        if (directDemoWalkthroughOpen) markDirectDemoStepComplete(7);
        if (importDemoWalkthroughOpen) markImportDemoStepComplete(7);
      } finally {
        setIsSavingSubmittal(false);
      }
      return;
    }

    if (!recruiterUser?.uid) {
      setPipelineNotice('Vision is still preparing the workspace. Try Log Submittal again in a moment.');
      return;
    }
    if (!requireAccess('pipeline.write', 'You need recruiter pipeline write access to submit candidates to clients.')) return;
    setIsSavingSubmittal(true);
    try {
      const existing = Array.isArray(cand.submittals) ? cand.submittals : [];
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
        submittals: [...existing, record],
        lastSubmittedAt: record.submittedAt,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setSubmittalCandidateId(null);
      setSubmittalClientName('');
      setSubmittalContactId('');
      setSubmittalContactName('');
      setSubmittalJobId('');
      setSubmittalSubmittedBy('');
      setSubmittalOutcome('pending');
      setSubmittalFeedback('');
      setSubmittalNote('');
      if (directDemoWalkthroughOpen) markDirectDemoStepComplete(7);
    } finally {
      setIsSavingSubmittal(false);
    }
  };

  const handleCreateShortlist = async (draft) => {
    const selectedIds = Array.isArray(draft.candidateIds) ? draft.candidateIds : [];
    if (selectedIds.length === 0) {
      setPipelineNotice('Select at least one candidate before creating a shortlist.');
      return;
    }
    const buildShortlistSnapshot = () => selectedIds.map((id) => {
      const c = candidates.find((x) => x.id === id);
      if (!c) return null;
      return {
        id: c.id,
        name: c.name || '',
        role: c.matchedJobTitle || c.role || '',
        company: c.company || '',
        skills: Array.isArray(c.skills) ? c.skills.slice(0, 6) : [],
        rating: c.rating || 0,
        summary: c.summary || c.headline || '',
        location: c.location || '',
        source: c.source || '',
      };
    }).filter(Boolean);

    if (isDemoWorkspace) {
      setShortlistSaving(true);
      try {
        const token = `demo-shortlist-${Date.now()}`;
        const snapshot = buildShortlistSnapshot();
        const url = `${window.location.origin}/?shortlist=${token}`;
        setShortlistCreatedUrl(url);
        setShortlistLinkCopied(false);
        setPipelineNotice(`Demo shortlist created for ${draft.clientName || 'the client'} with ${snapshot.length} candidate${snapshot.length === 1 ? '' : 's'}.`);
      } finally {
        setShortlistSaving(false);
      }
      return;
    }

    if (!recruiterUser?.uid) {
      setPipelineNotice('Vision is still preparing the workspace. Try Create Shortlist again in a moment.');
      return;
    }
    if (!hasAcceptedLawfulUse) {
      setShortlistCreatedUrl('');
      setPipelineNotice('Confirm lawful recruitment and data-processing use in Agency Settings before creating public shortlist links.');
      return;
    }
    setShortlistSaving(true);
    try {
      const token = crypto.randomUUID();
      const snapshot = buildShortlistSnapshot();
      await setDoc(publicShortlistDoc(token), {
        token,
        recruiterId: recruiterUser.uid,
        clientName: draft.clientName || '',
        jobTitle: draft.jobTitle || '',
        candidates: snapshot,
        createdAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
        privacyNotice: PUBLIC_PRIVACY_NOTICE,
        lawfulUseNotice: PUBLIC_LAWFUL_USE_NOTICE,
      });
      const url = `${window.location.origin}/?shortlist=${token}`;
      setShortlistCreatedUrl(url);
      setShortlistLinkCopied(false);
    } catch (err) {
      console.error('Failed to create shortlist', err);
    } finally {
      setShortlistSaving(false);
    }
  };

  const handleDeleteAllCandidates = async () => {
    if (!recruiterUser?.uid) return;
    if (!window.confirm('Delete ALL candidates? This cannot be undone.')) return;
    setIsDeletingAllCandidates(true);
    try {
      const batchSize = 400;
      const col = recruiterCandidatesCollection(recruiterUser.uid);
      const { getDocs, writeBatch } = await import('firebase/firestore');
      const snap = await getDocs(col);
      const chunks = [];
      for (let i = 0; i < snap.docs.length; i += batchSize) {
        chunks.push(snap.docs.slice(i, i + batchSize));
      }
      for (const chunk of chunks) {
        const batch = writeBatch(db);
        chunk.forEach((d) => batch.delete(d.ref));
        await batch.commit();
      }
      setPipelineNotice('All candidates deleted.');
    } catch (err) {
      console.error('Failed to delete all candidates', err);
      setPipelineNotice('Failed to delete candidates.');
    } finally {
      setIsDeletingAllCandidates(false);
    }
  };

  const handleDeleteCandidate = async (candidateId) => {

    if (!recruiterUser?.uid) return;
    if (!requireAccess('pipeline.delete', 'You need delete permission to remove candidates.')) return;

    const candidate = candidates.find((item) => item.id === candidateId);

    try {

      await deleteDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId));

      try {
        await deleteDoc(recruiterResponseDoc(recruiterUser.uid, candidateId));
      } catch (cleanupError) {
        console.warn('Candidate screening response cleanup skipped', cleanupError);
      }

      const linkId = candidate?.screeningLinkId || createScreeningLinkId(recruiterUser.uid, candidateId);

      try {
        const screeningLinkRef = publicScreeningLinkDoc(linkId);
        const screeningLinkSnapshot = await getDoc(screeningLinkRef);
        if (screeningLinkSnapshot.exists() && screeningLinkSnapshot.data()?.recruiterId === recruiterUser.uid) {
          await deleteDoc(screeningLinkRef);
        }
      } catch (cleanupError) {
        console.warn('Public screening link cleanup skipped', cleanupError);
      }

      await recordActivity(recruiterUser.uid, 'candidate_deleted', candidate ? `${candidate.name} removed from the pipeline.` : 'Candidate removed from the pipeline.', { candidateId });

      setPipelineNotice(candidate ? `${candidate.name} removed from the pipeline.` : 'Candidate removed from the pipeline.');

      if (selectedScreeningCandidateId === candidateId) {

        setSelectedScreeningCandidateId(null);

      }

    } catch (error) {

      console.error('Failed to delete candidate', error);

      setPipelineNotice('Failed to delete candidate from the pipeline.');

    }

  };



  const handleAcceptReviewedCandidate = async (reviewId) => {

    if (!recruiterUser?.uid) return;

    const reviewItem = candidateReviewQueue.find((item) => item.id === reviewId);

    if (!reviewItem) return;

    // candidateId may be stored as a separate field or the doc id itself
    const candidateId = reviewItem.candidateId || reviewItem.id;

    if (!candidateId) return;

    const acceptedAtIso = new Date().toISOString();

    const acceptedEvent = createCrmEvent('reviewed_candidate_accepted', `${reviewItem.name} was accepted from recruiter review into the pipeline.`, { candidateId, reviewId, jobId: reviewItem.matchedJobId || null, company: reviewItem.company || '' });

    // Sanitize aiFitAnalysis — strips any undefined values that Firestore rejects
    const safeAiFitAnalysis = reviewItem.aiFitAnalysis ? JSON.parse(JSON.stringify(reviewItem.aiFitAnalysis)) : null;

    try {

      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {

        id: candidateId,

        recruiterId: recruiterUser.uid,

        screeningLinkId: reviewItem.screeningLinkId || createScreeningLinkId(recruiterUser.uid, candidateId),

        name: reviewItem.name,

        email: reviewItem.email || '',

        phone: reviewItem.phone || '',

        role: reviewItem.role || '',

        company: reviewItem.company || '',

        location: reviewItem.location || '',

        skills: Array.isArray(reviewItem.skills) ? reviewItem.skills : [],

        status: 'applied',

        screeningDeadlineAt: null,

        rating: Number(reviewItem.rating || reviewItem.aiFitAnalysis?.score || 0),

        yearsExperience: Number(reviewItem.yearsExperience || 0),

        submittedAt: reviewItem.submittedAt || acceptedAtIso,

        lastActivityAt: acceptedAtIso,

        intakeDecision: 'accepted',

        intakeDecisionReason: 'Recruiter accepted a partial-fit application from the review queue.',

        matchedJobId: reviewItem.matchedJobId || null,

        matchedJobTitle: reviewItem.matchedJobTitle || '',

        source: reviewItem.source || 'Candidate jobs link review queue',

        rawText: reviewItem.rawText || '',

        resumeUrl: reviewItem.resumeUrl || '',

        fileName: reviewItem.fileName || '',

        crmStatus: 'new_intake',

        automationState: {

          vacancyValidated: true,

          duplicateChecked: true,

          directCandidateLink: true,

          aiFitAnalyzed: true,

          recruiterReviewed: true,

        },

        stageHistory: [{

          stageId: 'applied',

          stageLabel: 'New Applied',

          movedAt: acceptedAtIso,

          source: 'candidate_review_queue',

        }],

        crmTimeline: [acceptedEvent],

        aiFitAnalysis: safeAiFitAnalysis,

        updatedAt: serverTimestamp(),

        createdAt: serverTimestamp(),

      }, { merge: true });

      // Write screening answers (submitted on application form) into the screeningResponses
      // collection so they appear in the Screening Review modal under "Submitted Answers".
      if (reviewItem.screeningAnswers && typeof reviewItem.screeningAnswers === 'object' && !Array.isArray(reviewItem.screeningAnswers)) {

        const qaEntries = Object.entries(reviewItem.screeningAnswers).filter(([q]) => String(q).trim());

        if (qaEntries.length > 0) {

          await setDoc(recruiterResponseDoc(recruiterUser.uid, candidateId), {

            candidateId,

            recruiterId: recruiterUser.uid,

            screeningLinkId: reviewItem.screeningLinkId || `screen-${recruiterUser.uid}-${candidateId}`,

            candidateName: reviewItem.name,

            role: reviewItem.role || '',

            company: reviewItem.company || '',

            questions: qaEntries.map(([q]) => q),

            answers: qaEntries.map(([, a]) => String(a || '')),

            submittedAt: reviewItem.submittedAt || acceptedAtIso,

            source: 'careers_page_application',

            updatedAt: serverTimestamp(),

          }, { merge: true });

        }

      }

      await deleteDoc(recruiterReviewQueueDoc(recruiterUser.uid, reviewId));

      // Optimistically remove from local state so the card disappears immediately
      setCandidateReviewQueue((prev) => prev.filter((item) => item.id !== reviewId));

      await recordActivity(recruiterUser.uid, 'candidate_review_accepted', `${reviewItem.name} was accepted from the review queue.`, { reviewId, candidateId, jobId: reviewItem.matchedJobId || null, company: reviewItem.company || '' });

      // Notify candidate of pipeline admission via SMTP email
      try {

        const functionsClient = getFunctions(app, 'us-central1');

        const notifyCandidate = httpsCallable(functionsClient, 'notifyCandidatePipelineStatus');

        if (reviewItem.email) {
          console.log('[email] Sending accepted notification to:', reviewItem.email);
          notifyCandidate({ toEmail: reviewItem.email, toName: reviewItem.name, jobTitle: reviewItem.matchedJobTitle || reviewItem.role || '', companyName: reviewItem.company || '', agencyName: '', status: 'accepted' })
            .then((res) => console.log('[email] Accepted notification result:', res?.data))
            .catch((err) => console.error('[email] Accepted notification failed:', err?.message || err));
        } else {
          console.warn('[email] No email on reviewItem — skipping pipeline notification. reviewItem:', JSON.stringify({ id: reviewItem.id, name: reviewItem.name, email: reviewItem.email }));
        }

      } catch (_emailErr) { console.error('[notifyCandidatePipelineStatus] setup error:', _emailErr); }

      setPipelineNotice(`${reviewItem.name} was accepted into the pipeline.`);

    } catch (error) {

      console.error('Failed to accept reviewed candidate', error);

      setPipelineNotice('Failed to accept this reviewed candidate into the pipeline.');

    }

  };

  const resolveCandidateResumeUrl = useCallback(async (candidate) => {
    const resumeUrl = getCandidateResumeUrl(candidate);
    if (!resumeUrl) throw new Error('This candidate does not have a CV URL.');

    const functionsClient = getFunctions(app, 'us-central1');
    const getCandidateResumeDownloadUrl = httpsCallable(functionsClient, 'getCandidateResumeDownloadUrl');
    const response = await getCandidateResumeDownloadUrl({
      resumeUrl,
      recruiterId: candidate?.recruiterId || recruiterUser?.uid || '',
      appId,
    });
    return response?.data?.url || resumeUrl;
  }, [recruiterUser?.uid]);

  const handleOpenCandidateResume = async (candidate) => {
    const resumeWindow = window.open('', '_blank');
    if (!resumeWindow) {
      setPipelineNotice('Please allow pop-ups to open the candidate CV.');
      return;
    }
    try {
      const readableUrl = await resolveCandidateResumeUrl(candidate);
      resumeWindow.location.replace(readableUrl);
    } catch (error) {
      console.error('Failed to open candidate CV', error);
      resumeWindow.document.body.textContent = 'Unable to open this CV. Return to the recruiter workspace and try again.';
      setPipelineNotice('Unable to open this CV. Please try again or re-upload the document.');
    }
  };



  const handleRejectReviewedCandidate = async (reviewId) => {

    if (!recruiterUser?.uid) return;

    const reviewItem = candidateReviewQueue.find((item) => item.id === reviewId);

    try {

      await deleteDoc(recruiterReviewQueueDoc(recruiterUser.uid, reviewId));

      // Optimistically remove from local state so the card disappears immediately
      setCandidateReviewQueue((prev) => prev.filter((item) => item.id !== reviewId));

      const rejectedCandidateId = reviewItem?.candidateId || reviewItem?.id || null;
      await recordActivity(recruiterUser.uid, 'candidate_review_rejected', reviewItem ? `${reviewItem.name} was rejected from the review queue.` : 'Candidate review item rejected.', { reviewId, ...(rejectedCandidateId ? { candidateId: rejectedCandidateId } : {}), jobId: reviewItem?.matchedJobId || null, company: reviewItem?.company || '' });

      // Notify candidate of rejection via SMTP email
      if (reviewItem?.email) {
        try {
          const functionsClient = getFunctions(app, 'us-central1');
          const notifyCandidate = httpsCallable(functionsClient, 'notifyCandidatePipelineStatus');
          console.log('[email] Sending rejected notification to:', reviewItem.email);
          notifyCandidate({ toEmail: reviewItem.email, toName: reviewItem.name, jobTitle: reviewItem.matchedJobTitle || reviewItem.role || '', companyName: reviewItem.company || '', agencyName: '', status: 'rejected' })
            .then((res) => console.log('[email] Rejected notification result:', res?.data))
            .catch((err) => console.error('[email] Rejected notification failed:', err?.message || err));
        } catch (_emailErr) { console.error('[email] Rejected notification setup error:', _emailErr); }
      } else {
        console.warn('[email] No email on reviewItem — skipping rejection notification. reviewItem:', JSON.stringify({ id: reviewItem?.id, name: reviewItem?.name, email: reviewItem?.email }));
      }

      setPipelineNotice(reviewItem ? `${reviewItem.name} was rejected from the review queue.` : 'Candidate review item removed.');

    } catch (error) {

      console.error('Failed to reject reviewed candidate', error);

      setPipelineNotice('Failed to reject this reviewed candidate.');

    }

  };



  const handleSubmitScreening = async (candidate, answers) => {

    const candidateId = candidate?.candidateId || candidate?.id;

    const recruiterId = candidate?.recruiterId;

    const screeningLinkRef = candidate?.screeningLinkId || candidate?.id;

    const screeningQuestions = normalizeScreeningQuestions(candidate?.screeningQuestions);

    if (!candidateId || !recruiterId || !db) {

      throw new Error('Missing recruiter or candidate identifier for screening submission.');

    }

    const screeningSubmittedAt = new Date().toISOString();

    const screeningEvent = createCrmEvent('screening_submitted', `${candidate.name} submitted screening responses.`, { candidateId, company: candidate.company, role: candidate.role });

    setIsSubmittingScreening(true);

    try {

      await setDoc(

        recruiterResponseDoc(recruiterId, candidateId),

        {

          candidateId,

          recruiterId,

          screeningLinkId: screeningLinkRef,

          candidateName: candidate.name,

          role: candidate.role,

          company: candidate.company,

          answers,

          questions: screeningQuestions,

          submittedAt: screeningSubmittedAt,

          updatedAt: serverTimestamp(),

        },

        { merge: true }

      );

      await setDoc(recruiterCandidateDoc(recruiterId, candidateId), {

        crmStatus: 'screening_submitted',

        screeningSubmittedAt,

        lastActivityAt: screeningEvent.createdAtIso,

        crmTimeline: arrayUnion(screeningEvent),

        automationState: {

          vacancyValidated: true,

          duplicateChecked: true,

          screeningLinkLive: true,

          screeningSubmitted: true,

        },

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterId, 'screening_submitted', `${candidate.name} submitted screening responses.`, { candidateId, company: candidate.company, role: candidate.role });

    } catch (error) {

      console.error('Failed to store screening response', error);

      throw new Error('Failed to store screening responses in Firebase.');

    } finally {

      setIsSubmittingScreening(false);

    }

  };



  const handleBulkCvUpload = async (event) => {

    if (isDemoWorkspace && importDemoWalkthroughOpen) {
      const importedAtIso = new Date().toISOString();
      const primaryJob = jobs[0] || {
        id: 'demo-import-job-fallback',
        title: 'Imported Account Executive',
        companyName: 'Imported Client',
        location: 'Hybrid',
      };
      const parsedCandidate = {
        id: `demo-cv-candidate-${Date.now()}`,
        recruiterId: recruiterUser?.uid || 'demo-user',
        screeningLinkId: `demo-cv-screening-${Date.now()}`,
        name: 'Lina Jacobs',
        email: 'lina.jacobs@example.com',
        phone: '+27 82 555 0121',
        role: primaryJob.title,
        company: getJobCompanyName(primaryJob) || primaryJob.companyName || 'Imported Client',
        location: primaryJob.location || 'Johannesburg',
        skills: ['CV parsing', 'Client coordination', 'CRM updates', 'Candidate care'],
        status: 'applied',
        rating: 91,
        yearsExperience: 6,
        source: 'Demo bulk CV upload',
        submittedAt: importedAtIso,
        createdAtIso: importedAtIso,
        lastActivityAt: importedAtIso,
        matchedJobId: primaryJob.id,
        matchedJobTitle: primaryJob.title,
        rawText: `${primaryJob.title} CV parsed from demo upload and matched to ${getJobCompanyName(primaryJob) || primaryJob.companyName || 'Imported Client'}.`,
        crmStatus: 'cv_imported',
        intakeDecision: 'accepted',
        intakeDecisionReason: 'Demo CV explicitly matched the imported job title and company.',
        stageHistory: [{ stageId: 'applied', stageLabel: 'New Applied', movedAt: importedAtIso, source: 'demo_bulk_cv_upload' }],
        crmTimeline: [createCrmEvent('cv_imported', 'Lina Jacobs parsed from demo CV upload.', { jobId: primaryJob.id })],
      };
      setCandidates((current) => [parsedCandidate, ...current.filter((candidate) => !String(candidate.id || '').startsWith('demo-cv-candidate-'))]);
      setImportSummary([
        { fileName: 'lina-jacobs-cv.pdf', action: 'accepted', candidateName: parsedCandidate.name, role: parsedCandidate.role, company: parsedCandidate.company, aiScore: parsedCandidate.rating, reason: parsedCandidate.intakeDecisionReason, automation: 'Demo AI CV parsing' },
        { fileName: 'unmatched-cv.pdf', action: 'deleted', reason: 'No exact active vacancy title found in the demo CV.', automation: 'Strict vacancy validation' },
      ]);
      setPipelineNotice('Demo CV upload parsed 1 accepted candidate and rejected 1 unmatched CV.');
      if (importDemoWalkthroughStep === 4) markImportDemoStepComplete(4);
      if (event?.target) event.target.value = '';
      return;
    }

    const files = Array.from(event.target.files || []).filter((file) => /pdf$/i.test(file.name));

    if (files.length === 0 || !recruiterUser?.uid) return;



    const activeJobs = jobs.filter((job) => job.status === 'Active');

    const functionsClient = getFunctions(app, 'us-central1');

    const rankRecruiterCvMatches = httpsCallable(functionsClient, 'rankRecruiterCvMatches');

    const intakeBatchId = `intake-${Date.now()}`;

    setIsImporting(true);

    setPipelineNotice('Analyzing uploaded CVs against explicit vacancy signals and AI matching...');



    const results = [];

    const seenKeys = new Map(

      candidates.map((candidate) => [

        `${normalizeText(candidate.email)}|${normalizeText(candidate.name)}|${normalizeText(candidate.role)}`,

        candidate,

      ])

    );



    for (const file of files) {

      try {

        const rawText = await extractPdfText(file);

        const name = extractName(rawText);

        const email = extractEmail(rawText);

        const phone = extractPhone(rawText);

        const location = extractLocation(rawText);

        const skills = extractSkills(rawText);

        const yearsExperience = extractYearsExperience(rawText);

        const candidateProfile = {

          role: titleCaseFromEmail(email) || name || 'Candidate',

          company: '',

          skills,

          rawText,

          email,

          phone,

          location,

        };

        const explicitMatches = findExplicitJobMatches(rawText, activeJobs);



        let matchedJob = null;

        let matchedCompany = '';

        let fitScore = 0;

        let fitSummary = '';

        let automationMode = 'AI + boolean vacancy match';

        let intakeDecision = 'accepted';

        let reviewPayload = null;



        if (explicitMatches.length > 1) {

          results.push({ fileName: file.name, action: 'deleted', reason: 'CV references multiple active vacancies. Intake requires one primary vacancy target.', automation: 'Rejected by vacancy validation' });

          continue;

        }



        if (explicitMatches.length === 1) {

          matchedJob = explicitMatches[0];

          matchedCompany = getJobCompanyName(matchedJob) || '';

          const scoreResult = scoreAgainstJob({ role: matchedJob.title, company: matchedCompany, skills, rawText }, matchedJob);

          if (scoreResult.skillHits < 2 || scoreResult.score < 60) {

            results.push({ fileName: file.name, action: 'deleted', role: matchedJob.title, company: matchedCompany, reason: 'Exact vacancy target found, but the CV is too weak against the vacancy requirements.', automation: 'Rejected by fit scoring' });

            continue;

          }

          fitScore = scoreResult.score;

          fitSummary = `Exact match: ${matchedCompany} / ${matchedJob.title} (${scoreResult.score})`;

          automationMode = 'Exact vacancy targeting + fit scoring';

        } else {

          const localRankedMatches = rankCvJobsLocally(candidateProfile, activeJobs);

          let inferredMatch = null;

          try {

            const rankedPayload = localRankedMatches.slice(0, 6).map((entry) => ({

              id: entry.job.id,

              title: entry.job.title,

              companyName: getJobCompanyName(entry.job) || '',

              location: entry.job.location || '',

              summary: entry.job.summary || '',

              requirements: entry.job.requirements || '',

              booleanQuery: entry.job.booleanQuery || createBooleanQueryFromJob(entry.job),

            }));

            if (rankedPayload.length > 0) {

              const rankingResult = await rankRecruiterCvMatches({

                appId,

                candidateProfile: {

                  role: candidateProfile.role,

                  location: candidateProfile.location,

                  email: candidateProfile.email,

                  phone: candidateProfile.phone,

                  yearsExperience,

                  skills: candidateProfile.skills,

                  rawText: candidateProfile.rawText,

                },

                jobs: rankedPayload,

              });

              const rankedData = rankingResult?.data || null;

              if (rankedData?.job?.id) {

                const matchedFromActiveJobs = activeJobs.find((job) => job.id === rankedData.job.id) || null;

                inferredMatch = {

                  job: matchedFromActiveJobs || rankedData.job,

                  aiScore: { score: Number(rankedData.aiScore || rankedData.finalScore || 0) },

                  booleanScore: localRankedMatches.find((entry) => entry.job.id === rankedData.job.id)?.booleanScore || { score: 0 },

                  blendedScore: Number(rankedData.finalScore || rankedData.aiScore || 0),

                  recommendation: String(rankedData.recommendation || ''),

                  summary: String(rankedData.summary || ''),

                  concerns: Array.isArray(rankedData.concerns) ? rankedData.concerns : [],

                  strengths: Array.isArray(rankedData.strengths) ? rankedData.strengths : [],

                  source: String(rankedData.source || 'server_fallback'),

                };

              }

            }

          } catch (rankingError) {

            console.error('rankRecruiterCvMatches failed', rankingError);

          }



          inferredMatch = inferredMatch || findBestCvJobMatch(candidateProfile, activeJobs);

          if (!inferredMatch || !inferredMatch.job) {

            results.push({ fileName: file.name, action: 'deleted', reason: 'Vision could not infer a credible active vacancy from the CV.', automation: 'Rejected by vacancy ranking' });

            continue;

          }



          matchedJob = inferredMatch.job;

          matchedCompany = getJobCompanyName(matchedJob) || '';

          fitScore = Number(inferredMatch.blendedScore || inferredMatch.aiScore?.score || 0);

          fitSummary = inferredMatch.summary || `Vision matched ${matchedJob.title} at ${matchedCompany} as the strongest active vacancy for this CV.`;

          automationMode = inferredMatch.source === 'openai' ? 'AI vacancy ranking' : 'Server vacancy ranking';



          if (inferredMatch.recommendation === 'reject' || (inferredMatch.aiScore?.score < 30 && (inferredMatch.booleanScore?.score || 0) < 20)) {

            results.push({ fileName: file.name, action: 'deleted', role: matchedJob.title, company: matchedCompany, reason: fitSummary || 'Vision could not produce a strong enough vacancy match from this CV.', automation: automationMode });

            continue;

          }



          if (inferredMatch.recommendation === 'review' || fitScore < 64) {

            intakeDecision = 'review';

            reviewPayload = {

              aiFitAnalysis: {

                score: fitScore,

                summary: fitSummary,

                concerns: Array.isArray(inferredMatch.concerns) ? inferredMatch.concerns : [],

                strengths: Array.isArray(inferredMatch.strengths) ? inferredMatch.strengths : [],

              },

            };

            automationMode = `${automationMode} review queue`;

          }

        }



        const duplicateKey = `${normalizeText(email)}|${normalizeText(name)}|${normalizeText(matchedJob.title)}`;

        const additionalJobMatches = rankCvJobsLocally(candidateProfile, activeJobs)
          .filter((entry) => entry.job.id !== matchedJob.id && entry.blendedScore >= 45)
          .slice(0, 5)
          .map((entry) => ({
            jobId: entry.job.id,
            jobTitle: entry.job.title,
            companyName: getJobCompanyName(entry.job),
            score: entry.blendedScore,
          }));

        const parsedProfile = buildCandidateIntelligenceProfile({
          name,
          email,
          phone,
          role: matchedJob.title,
          matchedJobTitle: matchedJob.title,
          company: matchedCompany,
          location,
          skills,
          yearsExperience,
          rawText,
          rating: fitScore,
        });
        const matchEvidence = scoreCandidateForJob({
          name,
          email,
          phone,
          role: matchedJob.title,
          matchedJobTitle: matchedJob.title,
          company: matchedCompany,
          location,
          skills,
          yearsExperience,
          rawText,
          rating: fitScore,
        }, matchedJob);

        try {
          const safeFileName = createStorageSafeFileName(file.name || `${name || 'candidate-cv'}.pdf`);

          const cvFileRef = storageRef(storage, `public/recruiter-candidate-cvs/${recruiterUser.uid}/${intakeBatchId}/${Date.now()}-${safeFileName}`);

          await uploadBytes(cvFileRef, file, { contentType: file.type || 'application/pdf' });

          var resumeUrl = await getDownloadURL(cvFileRef);

          console.log('[CV Upload] Successfully uploaded CV for', name, 'resumeUrl:', resumeUrl);
        } catch (uploadError) {
          console.error('[CV Upload] Failed to upload CV for', name, uploadError);
          var resumeUrl = '';
        }

        const existingCandidate = seenKeys.get(duplicateKey);



        if (existingCandidate) {

          const enrichmentEvent = createCrmEvent('candidate_enriched', `Existing candidate record refreshed from ${file.name}.`, { candidateId: existingCandidate.id, source: file.name });

          await setDoc(recruiterCandidateDoc(recruiterUser.uid, existingCandidate.id), {

            phone: existingCandidate.phone || phone,

            location: existingCandidate.location || location,

            yearsExperience: existingCandidate.yearsExperience || yearsExperience,

            skills: existingCandidate.skills?.length ? existingCandidate.skills : skills,

            resumeUrl: existingCandidate.resumeUrl || resumeUrl,

            parsedProfile: {
              seniority: parsedProfile.seniority,
              yearsExperience: parsedProfile.yearsExperience,
              skills: parsedProfile.skills,
              scopeLabel: parsedProfile.scope.label,
              scopeNeeds: parsedProfile.scope.needs,
              parseabilityScore: parsedProfile.parseabilityScore,
            },

            semanticSearchText: parsedProfile.searchText,

            latestMatchEvidence: matchEvidence,

            additionalJobMatches,

            fileName: existingCandidate.fileName || file.name,

            intakeBatchId,

            lastActivityAt: enrichmentEvent.createdAtIso,

            automationState: {

              vacancyValidated: explicitMatches.length === 1,

              blendedVacancyMatched: explicitMatches.length === 0,

              duplicateChecked: true,

              enrichmentApplied: true,

            },

            crmTimeline: arrayUnion(enrichmentEvent),

            updatedAt: serverTimestamp(),

          }, { merge: true });

          results.push({ fileName: file.name, action: 'accepted', candidateName: existingCandidate.name || name, role: existingCandidate.role || matchedJob.title, company: existingCandidate.company || matchedCompany, aiScore: fitScore || Number(existingCandidate.rating || 0) || 0, reason: location ? `Existing candidate updated with parsed location: ${location}` : 'Duplicate CV matched an existing candidate record.', automation: 'CRM enrichment' });

          continue;

        }



        const candidateId = `cv-${Date.now()}-${results.length}`;

        const screeningLinkRef = createScreeningLinkId(recruiterUser.uid, candidateId);

        const acceptedEvent = createCrmEvent('intake_accepted', `${name} accepted from ${file.name}.`, { candidateId, company: matchedCompany, role: matchedJob.title, fitScore });

        if (intakeDecision === 'review') {

          const reviewId = `review-${candidateId}`;

          await setDoc(recruiterReviewQueueDoc(recruiterUser.uid, reviewId), {

            id: reviewId,

            candidateId,

            recruiterId: recruiterUser.uid,

            screeningLinkId: screeningLinkRef,

            name,

            email,

            phone,

            role: matchedJob.title,

            company: matchedCompany,

            location,

            skills,

            rating: fitScore,

            yearsExperience,

            submittedAt: acceptedEvent.createdAtIso,

            matchedJobId: matchedJob.id,

            matchedJobTitle: matchedJob.title,

            source: file.name,

            fileName: file.name,

            resumeUrl,

            rawText,

            parsedProfile: {
              seniority: parsedProfile.seniority,
              yearsExperience: parsedProfile.yearsExperience,
              skills: parsedProfile.skills,
              scopeLabel: parsedProfile.scope.label,
              scopeNeeds: parsedProfile.scope.needs,
              parseabilityScore: parsedProfile.parseabilityScore,
            },

            semanticSearchText: parsedProfile.searchText,

            latestMatchEvidence: matchEvidence,

            duplicateKey,

            intakeDecision: 'review',

            intakeDecisionReason: fitSummary,

            automationState: {

              vacancyValidated: false,

              blendedVacancyMatched: true,

              duplicateChecked: true,

              aiFitAnalyzed: true,

              recruiterReviewRequired: true,

            },

            crmStatus: 'review_required',

            crmTimeline: [acceptedEvent],

            aiFitAnalysis: reviewPayload?.aiFitAnalysis || null,

            additionalJobMatches,

            createdAtIso: acceptedEvent.createdAtIso,

            createdAt: serverTimestamp(),

            updatedAt: serverTimestamp(),

          }, { merge: true });

          results.push({ fileName: file.name, action: 'review', candidateName: name, role: matchedJob.title, company: matchedCompany, aiScore: fitScore, fitScore, reason: fitSummary, automation: automationMode });

          continue;

        }



        const candidateRecord = {

          id: candidateId,

          recruiterId: recruiterUser.uid,

          screeningLinkId: screeningLinkRef,

          name,

          email,

          phone,

          role: matchedJob.title,

          company: matchedCompany,

          location,

          skills,

          status: 'applied',

          screeningDeadlineAt: null,

          rating: fitScore,

          yearsExperience,

          submittedAt: acceptedEvent.createdAtIso,

          lastActivityAt: acceptedEvent.createdAtIso,

          intakeBatchId,

          intakeDecision: 'accepted',

          intakeDecisionReason: fitSummary,

          matchedJobId: matchedJob.id,

          matchedJobTitle: matchedJob.title,

          additionalJobMatches,

          source: file.name,

          fileName: file.name,

          resumeUrl,

          rawText,

          parsedProfile: {
            seniority: parsedProfile.seniority,
            yearsExperience: parsedProfile.yearsExperience,
            skills: parsedProfile.skills,
            scopeLabel: parsedProfile.scope.label,
            scopeNeeds: parsedProfile.scope.needs,
            parseabilityScore: parsedProfile.parseabilityScore,
          },

          semanticSearchText: parsedProfile.searchText,

          latestMatchEvidence: matchEvidence,

          duplicateKey,

          crmStatus: 'new_intake',

          automationState: {

            vacancyValidated: explicitMatches.length === 1,

            blendedVacancyMatched: explicitMatches.length === 0,

            duplicateChecked: true,

            screeningLinkLive: true,

            fitScored: true,

            booleanMatched: explicitMatches.length === 0,

          },

          stageHistory: [{

            stageId: 'applied',

            stageLabel: 'New Applied',

            movedAt: acceptedEvent.createdAtIso,

            source: 'agency_bulk_cv',

          }],

          crmTimeline: [acceptedEvent],

          updatedAt: serverTimestamp(),

          createdAt: serverTimestamp(),

        };

        await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), candidateRecord, { merge: true });

        seenKeys.set(duplicateKey, candidateRecord);

        results.push({ fileName: file.name, action: 'accepted', candidateName: name, role: matchedJob.title, company: matchedCompany, aiScore: fitScore, fitScore, reason: fitSummary, automation: automationMode });

      } catch (error) {

        results.push({ fileName: file.name, action: 'failed', reason: `Could not parse PDF: ${error.message || error}`, automation: 'Parser failure' });

      }

    }



    const acceptedCount = results.filter((item) => item.action === 'accepted').length;

    const reviewCount = results.filter((item) => item.action === 'review').length;

    await recordActivity(recruiterUser.uid, 'cv_intake_batch', acceptedCount > 0 || reviewCount > 0 ? `Processed CV batch with ${acceptedCount} accepted candidates and ${reviewCount} queued for review.` : 'Processed CV batch with no viable candidate matches.', { acceptedCount, reviewCount, totalFiles: files.length });

    setPipelineNotice(acceptedCount > 0 || reviewCount > 0 ? `${acceptedCount} candidate CVs accepted${reviewCount > 0 ? ` and ${reviewCount} queued for recruiter review` : ''}.` : 'No uploaded CVs produced a credible vacancy match.');

    setImportSummary(results);

    setIsImporting(false);

    event.target.value = '';

  };



  const clearAuthFormSensitiveFields = ({ keepEmail = false } = {}) => {

    setAuthForm((current) => ({

      ...current,

      email: keepEmail ? current.email : '',

      password: '',

      agencySecret: '',

    }));

  };



  const handleAuthFormChange = (event) => {

    const { name, value, type, checked } = event.target;

    setAuthForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));

  };

  const sendAuthOtpForSession = async (purpose = 'login') => {

    const sendAuthOtp = httpsCallable(functionsClient, 'sendAuthOtp');

    await sendAuthOtp({ appId, purpose });

    setAuthOtpVerified(false);

    setAuthOtpCode('');

    setAuthOtpError('');

    setAuthOtpNotice(`A 6-digit code was sent to ${auth?.user?.email || authForm.email}.`);

    if (authOtpSessionKey) {

      try { window.sessionStorage.removeItem(authOtpSessionKey); } catch (_error) {}

    }

  };

  const handleVerifyAuthOtp = async (event) => {

    event.preventDefault();

    if (!rawRecruiterUser?.uid || authOtpCode.length !== 6) return;

    setIsVerifyingAuthOtp(true);

    setAuthOtpError('');

    try {

      const verifyAuthOtp = httpsCallable(functionsClient, 'verifyAuthOtp');

      await verifyAuthOtp({ appId, code: authOtpCode });

      if (authOtpSessionKey) {

        try { window.sessionStorage.setItem(authOtpSessionKey, 'verified'); } catch (_error) {}

      }

      setAuthOtpVerified(true);

      setAuthOtpCode('');

      setAuthOtpNotice('');

    } catch (error) {

      setAuthOtpError(error?.message || 'Verification failed.');

    } finally {

      setIsVerifyingAuthOtp(false);

    }

  };

  const handleResendAuthOtp = async () => {

    if (!rawRecruiterUser?.uid || isResendingAuthOtp) return;

    setIsResendingAuthOtp(true);

    setAuthOtpError('');

    try {

      await sendAuthOtpForSession(authMode === 'signup' ? 'signup' : 'login');

    } catch (error) {

      setAuthOtpError(error?.message || 'Could not send a new code.');

    } finally {

      setIsResendingAuthOtp(false);

    }

  };

  const handleAuthOtpLogout = async () => {

    if (authOtpSessionKey) {

      try { window.sessionStorage.removeItem(authOtpSessionKey); } catch (_error) {}

    }

    setAuthOtpVerified(false);

    setAuthOtpCode('');

    await auth?.logout?.();

  };



  const handleAuthSubmit = async (event) => {

    event.preventDefault();

    setAuthError('');

    setIsSubmittingAuth(true);

    const normalizedAgency = normalizeText(authForm.agency);

    const agencySecret = String(authForm.agencySecret || '').trim();

    try {

      if (!normalizedAgency || !agencySecret) {

        throw new Error('Agency and Vision profile password are required.');

      }

      if (authMode === 'signup' && !authForm.lawfulUseAccepted) {

        throw new Error('You must confirm lawful recruitment and data-processing use before creating an account.');

      }



      if (authMode === 'signup') {

        const user = await auth?.signUpWithEmail?.(authForm.email, authForm.password, 'recruiter');

        if (user?.uid && db) {

          let workspaceProvisioned = false;

          try {

            const accountRole = authForm.accountRole || 'agent';

            if (accountRole === 'owner') {

              const createOwnerAgency = httpsCallable(functionsClient, 'createOwnerAgency');

              await createOwnerAgency({

                appId,

                agencyKey: normalizedAgency,

                agencyName: authForm.agency,

                agencySecret,

                email: authForm.email,

                name: authForm.name,

                location: authForm.location,

                title: DEFAULT_RECRUITER_PROFILE.title,

                lawfulUseAccepted: true,

              });
              workspaceProvisioned = true;

            } else {

              const joinAgencyWithSecret = httpsCallable(functionsClient, 'joinAgencyWithSecret');

              await joinAgencyWithSecret({

                appId,

                agencyKey: normalizedAgency,

                agencySecret,

                email: authForm.email,

                name: authForm.name,

                location: authForm.location,

                title: DEFAULT_RECRUITER_PROFILE.title,

              });
              workspaceProvisioned = true;

            }



            await setDoc(userDocRef(user.uid), {

              email: authForm.email,

              name: authForm.name,

              agency: authForm.agency,

              location: authForm.location,

              title: DEFAULT_RECRUITER_PROFILE.title,

              phone: DEFAULT_RECRUITER_PROFILE.phone,

              updatedAt: serverTimestamp(),

              createdAt: serverTimestamp(),

            }, { merge: true });

            try {

              const redeemWhopEntitlement = httpsCallable(functionsClient, 'redeemWhopEntitlement');

              const redeemResult = await redeemWhopEntitlement({ appId, agencyKey: normalizedAgency, email: authForm.email });

              if (redeemResult?.data?.redeemed) {

                setPipelineNotice('Your Whop payment was detected and your Vision workspace is active.');

              }

            } catch (redeemError) {

              console.warn('No redeemable Whop entitlement found for this signup', redeemError);

            }

            await recordActivity(user.uid, authForm.accountRole === 'owner' ? 'owner_signup' : 'agent_signup', `${authForm.name || authForm.email} created a Vision ${authForm.accountRole === 'owner' ? 'agency owner' : 'agent'} profile.`, { agency: authForm.agency, accountRole: authForm.accountRole || 'agent' });

            await sendAuthOtpForSession('signup');

            clearAuthFormSensitiveFields({ keepEmail: false });

          } catch (signupError) {

            if (!workspaceProvisioned) {

              try {

                await deleteUser(user);

              } catch (cleanupError) {

                console.error('Failed to remove incomplete auth user', cleanupError);

              }

            }

            throw signupError;

          }

        }

      } else {

        const user = await auth?.signInWithEmail?.(authForm.email, authForm.password);

        const profileSnapshot = await getDoc(userDocRef(user.uid));

        if (!profileSnapshot.exists()) {

          await auth?.logout?.();

          throw new Error('Vision profile not found for this agent.');

        }

        const profileData = profileSnapshot.data();

        const agencyKey = profileData.agencyKey || normalizeText(profileData.agency);

        const memberSnapshot = await getDoc(agencyMemberDoc(agencyKey, user.uid));

        if (!memberSnapshot.exists()) {

          await auth?.logout?.();

          throw new Error('This user does not have official agency membership in Vision.');

        }

        let memberData = memberSnapshot.data();

        let agencyData = null;

        let effectiveAgencyData = {};

        if (['owner', 'admin'].includes(memberData.role || profileData.accountRole)) {

          const agencySnapshot = await getDoc(agencyDocRef(agencyKey));

          if (!agencySnapshot.exists()) {

            await auth?.logout?.();

            throw new Error('This user does not have official agency membership in Vision.');

          }

          agencyData = agencySnapshot.data();

          if (agencyKey !== normalizedAgency || String(agencyData.agencySecret || '') !== agencySecret) {

            await auth?.logout?.();

            throw new Error('Agency or Vision profile password is incorrect.');

          }

          effectiveAgencyData = agencyData;

        } else {

          const joinAgencyWithSecret = httpsCallable(functionsClient, 'joinAgencyWithSecret');

          const joinResult = await joinAgencyWithSecret({

            appId,

            agencyKey,

            agencySecret,

            email: user.email || authForm.email,

            name: profileData.name || authForm.name || '',

            location: profileData.location || authForm.location || '',

            title: profileData.title || DEFAULT_RECRUITER_PROFILE.title,

          });

          const joined = joinResult?.data || {};

          memberData = {

            ...memberData,

            role: joined.role || memberData.role || 'agent',

            seatAssigned: joined.seatAssigned ?? memberData.seatAssigned,

            seatStatus: joined.seatAssigned ? 'active' : memberData.seatStatus,

          };

          agencyData = { agencyName: joined.agencyName || profileData.agency || authForm.agency };

        }

        if ((memberData.role || profileData.accountRole) === 'owner' && !isAgencySubscriptionActive(effectiveAgencyData)) {

          try {

            const redeemWhopEntitlement = httpsCallable(functionsClient, 'redeemWhopEntitlement');

            const searchParams = new URLSearchParams(window.location.search || '');

            const redeemResult = await redeemWhopEntitlement({
              appId,
              agencyKey,
              email: user.email || authForm.email,
              receiptId: searchParams.get('receipt_id') || '',
              paymentId: searchParams.get('payment_id') || '',
              stateId: searchParams.get('state_id') || '',
              checkoutStatus: searchParams.get('checkout_status') || searchParams.get('status') || '',
            });

            if (redeemResult?.data?.redeemed) {

              const refreshedAgencySnapshot = await getDoc(agencyDocRef(agencyKey));

              if (refreshedAgencySnapshot.exists()) {

                effectiveAgencyData = refreshedAgencySnapshot.data();

              }

              setPipelineNotice('Your Whop payment was detected and your Vision workspace is active.');

            }

          } catch (redeemError) {

            console.warn('Whop entitlement redemption during sign-in did not apply', redeemError);

          }

        }

        if (!memberData.seatAssigned || memberData.seatStatus !== 'active') {

          await auth?.logout?.();

          throw new Error('Access is restricted because this user does not have an active paid seat.');

        }

        if (Number(effectiveAgencyData.activeSeatCount || 0) > Number(effectiveAgencyData.seatLimit || 0)) {

          await auth?.logout?.();

          throw new Error('Agency seat capacity has been exceeded. Contact the agency owner.');

        }

        await setDoc(userDocRef(user.uid), {

          agency: effectiveAgencyData.agencyName || profileData.agency || authForm.agency,

          updatedAt: serverTimestamp(),

        }, { merge: true });

        await setDoc(agencyMemberDoc(agencyKey, user.uid), {

          name: profileData.name || authForm.name || '',

          email: user.email || authForm.email,

          updatedAt: serverTimestamp(),

        }, { merge: true });

        await recordActivity(user.uid, 'agent_signin', `${profileData.name || authForm.email} signed in to Vision.`, { agency: agencyData.agencyName || authForm.agency, accountRole: memberData.role || profileData.accountRole || 'agent' });

        await sendAuthOtpForSession('login');

        clearAuthFormSensitiveFields({ keepEmail: false });

      }

    } catch (error) {

      const authErrorMessages = {
        'auth/email-already-in-use': 'This email already has a Vision account. Sign in instead or use password reset.',
        'auth/invalid-email': 'Enter a valid work email address.',
        'auth/invalid-credential': 'Email or password is incorrect. Check that you are signing in with the account you created.',
        'auth/user-not-found': 'No Vision login exists for this email. Create an account first or ask the agency owner to add you.',
        'auth/wrong-password': 'Email or password is incorrect.',
        'auth/weak-password': 'Use a stronger password with at least 6 characters.',
        'auth/too-many-requests': 'Too many failed attempts. Wait a few minutes, then try again or reset your password.',
        'auth/user-disabled': 'This login has been disabled. Contact the agency owner.',
        'auth/api-key-not-valid': 'Firebase rejected this app API key. Check the Firebase web configuration.',
        'auth/invalid-api-key': 'Firebase rejected this app API key. Check the Firebase web configuration.',
        'auth/unauthorized-domain': 'This domain is not authorized for Firebase Authentication. Add it in Firebase Console.',
        'auth/operation-not-allowed': 'Email/password signup is disabled in Firebase Authentication. Enable it in Firebase Console.',
        'auth/network-request-failed': 'Firebase could not be reached. Check your connection and try again.',
        'functions/already-exists': 'This agency already has an owner profile in Vision. Sign in for that agency or use a different agency name.',
      };

      const nextError = authErrorMessages[error?.code] || error?.message || 'Authentication failed.';

      if (error?.code === 'auth/email-already-in-use') {

        setAuthMode('signin');

        clearAuthFormSensitiveFields({ keepEmail: true });

      }

      setAuthError(nextError);

      if (auth?.user && !authOtpVerified) {

        await auth?.logout?.();

      }

    } finally {

      setIsSubmittingAuth(false);

    }

  };



  const handleJobFormChange = (event) => {

    const { name, value } = event.target;

    setJobForm((current) => ({ ...current, [name]: value }));
    if (jobFormErrors[name]) {
      setJobFormErrors((current) => {
        const next = { ...current };
        delete next[name];
        return next;
      });
    }

  };



  const handleSourceLeadFormChange = (event) => {

    const { name, value } = event.target;

    setSourceLeadForm((current) => ({ ...current, [name]: value }));

  };



  const handleScrapedJobSearchChange = (event) => {

    const { name, value } = event.target;

    setScrapedJobSearch((current) => ({ ...current, [name]: value }));

  };



  const handleAddSourceLead = async (event) => {

    event.preventDefault();

    if (!recruiterUser?.uid || !app) return;

    if (!sourceLeadForm.linkedInUrl.trim() && !sourceLeadForm.name.trim() && !sourceLeadForm.headline.trim()) {

      setJobPortfolioNotice('Add at least a LinkedIn URL, a name, or a headline to store a sourcing lead.');

      return;

    }

    setIsSavingSourceLead(true);

    try {

      const functionsClient = getFunctions(app, 'us-central1');

      const trimmedLinkedInUrl = sourceLeadForm.linkedInUrl.trim();

      if (trimmedLinkedInUrl) {

        const ingestExternalProfile = httpsCallable(functionsClient, 'ingestExternalProfile');

        const response = await ingestExternalProfile({

          appId,

          provider: 'linkedin_external_provider',

          linkedInUrl: trimmedLinkedInUrl,

          name: sourceLeadForm.name,

          headline: sourceLeadForm.headline,

          location: sourceLeadForm.location,

          notes: sourceLeadForm.notes,

        });

        const createdProfile = response?.data?.profile;

        if (createdProfile) {

          setExternalProfiles((current) => {

            const remaining = current.filter((item) => item.id !== createdProfile.id);

            return [createdProfile, ...remaining];

          });

        }

        setSourceLeadForm(DEFAULT_SOURCE_LEAD_FORM);

        setSourcingRefreshNonce((current) => current + 1);

        setJobPortfolioNotice('LinkedIn profile ingested through the Vision scraper.');

      } else {

        const ingestSourceLead = httpsCallable(functionsClient, 'ingestSourceLead');

        const response = await ingestSourceLead({

          appId,

          linkedInUrl: '',

          name: sourceLeadForm.name,

          headline: sourceLeadForm.headline,

          location: sourceLeadForm.location,

          notes: sourceLeadForm.notes,

          selectedJobId: selectedJob?.id || '',

        });

        setSourcingInbox(Array.isArray(response?.data?.sourcingInbox) ? response.data.sourcingInbox : sourcingInbox);

        setSourcingRefreshNonce((current) => current + 1);

        setSourceLeadForm(DEFAULT_SOURCE_LEAD_FORM);

        setJobPortfolioNotice('Lead added to the sourcing inbox.');

      }

    } catch (error) {

      console.error('Failed to add sourcing lead', error);

      const message = String(error?.message || 'Failed to add the sourcing lead.');

      if (sourceLeadForm.linkedInUrl.trim() && message.includes('Vision LinkedIn scraper service is not configured')) {

        setJobPortfolioNotice('The Vision LinkedIn scraper is not configured on the backend yet. Add the scraper URL and token, redeploy functions, then try this profile again.');

      } else {

        setJobPortfolioNotice(message);

      }

    } finally {

      setIsSavingSourceLead(false);

    }

  };



  const handleScrapeLinkedInJobs = async (event) => {

    event.preventDefault();

    setJobPortfolioNotice('LinkedIn job scraping is paused for now. Use the Vision Chrome extension or CSV import until this returns.');

  };



  const handleClientJobFormChange = (event) => {

    const { name, value } = event.target;

    setHasSubmittedClientJobForm(false);

    setClientJobForm((current) => ({ ...current, [name]: value }));

  };



  const handleCopyClientJobFormLink = async () => {

    if (!recruiterUser?.uid) return;

    if (!hasAcceptedLawfulUse) {

      setJobPortfolioNotice('Confirm lawful recruitment and data-processing use in Agency Settings before creating public intake links.');

      return;

    }

    const linkId = createJobIntakeLinkId(recruiterUser.uid);

    const linkPayload = {

      recruiterId: recruiterUser.uid,

      agencyKey: profile.agencyKey || normalizeText(profile.agency),

      agencyName: profile.agency || 'Your agency',

      recruiterName: profile.name || 'Recruiter',

      createdAt: serverTimestamp(),

      createdAtIso: new Date().toISOString(),

      createdAtMs: Date.now(),

      status: 'active',

      privacyNotice: PUBLIC_PRIVACY_NOTICE,

      lawfulUseNotice: PUBLIC_LAWFUL_USE_NOTICE,

    };

    try {

      await setDoc(publicJobIntakeLinkDoc(linkId), linkPayload, { merge: true });

      const url = new URL(window.location.href);

      url.searchParams.delete('screening');

      url.searchParams.set('job-intake', linkId);

      await navigator.clipboard.writeText(url.toString());

      setJobPortfolioNotice('Client vacancy link copied. Clients can open it and submit vacancies directly into your portfolio.');

    } catch (error) {

      console.error('Failed to create client job intake link', error);

      setJobPortfolioNotice('Failed to create the client vacancy link.');

    }

  };



  const handleSubmitClientJobForm = async (event) => {

    event.preventDefault();

    if (!app || !jobIntakeLinkId || !publicJobIntakeLink) return;

    setIsSubmittingClientJobForm(true);

    setClientJobFormNotice('');

    try {

      const functionsClient = getFunctions(app, 'us-central1');

      const submitClientJobOffer = httpsCallable(functionsClient, 'submitClientJobOffer');

      const response = await submitClientJobOffer({

        appId,

        linkId: jobIntakeLinkId,

        ...clientJobForm,

      });

      setClientJobForm(DEFAULT_CLIENT_JOB_FORM);

      setHasSubmittedClientJobForm(true);

      setClientJobFormNotice(response?.data?.message || `Your vacancy was shared with ${publicJobIntakeLink.agencyName}.`);

    } catch (error) {

      console.error('Failed to submit client job form', error);

      setClientJobFormNotice(error?.message || 'Failed to submit this vacancy.');

    } finally {

      setIsSubmittingClientJobForm(false);

    }

  };





  const handleCandidateApplicationFormChange = (event) => {

    const { name, value } = event.target;

    setHasSubmittedCandidateApplication(false);

    setCandidateApplicationForm((current) => ({ ...current, [name]: value }));

  };



  const handleCandidateCvFileChange = (event) => {

    setHasSubmittedCandidateApplication(false);

    setCandidateApplicationNotice('');

    setCandidateCvFile(event.target.files?.[0] || null);

  };



  const handleCopyCandidateJobApplicationLink = async () => {

    if (!recruiterUser?.uid) return;

    if (!hasAcceptedLawfulUse) {

      setJobPortfolioNotice('Confirm lawful recruitment and data-processing use in Agency Settings before creating public candidate links.');

      return;

    }

    const publicJobs = jobs

      .filter((job) => String(job.status || '').toLowerCase() !== 'closed')

      .map((job) => ({

        id: job.id,

        title: job.title,

        companyName: getJobCompanyName(job),

        location: job.location || '',

        summary: job.summary || '',

        requirements: job.requirements || '',

        type: job.type || 'Full-time',

        status: job.status || 'Active',

        customScreeningQuestions: Array.isArray(job.customScreeningQuestions) ? job.customScreeningQuestions.filter(Boolean) : [],

      }));

    if (!publicJobs.length) {

      setJobPortfolioNotice('There are no active jobs available to share with candidates right now.');

      return;

    }

    const linkId = createCandidateJobIntakeLinkId(recruiterUser.uid);

    const linkPayload = {

      recruiterId: recruiterUser.uid,

      agencyKey: profile.agencyKey || normalizeText(profile.agency),

      agencyName: profile.agency || 'Your agency',

      recruiterName: profile.name || 'Recruiter',

      jobs: publicJobs,

      brandColor: agencyBrand.brandColor || '#6366f1',

      portalTheme: agencyBrand.portalTheme || DEFAULT_AGENCY_BRAND.portalTheme,

      portalHeadline: agencyBrand.portalHeadline || '',

      logoUrl: agencyBrand.logoUrl || '',

      heroImageUrl: agencyBrand.heroImageUrl || '',

      aboutImageUrl: agencyBrand.aboutImageUrl || '',

      teamImageUrl: agencyBrand.teamImageUrl || '',

      tagline: agencyBrand.tagline || '',

      agencyAbout: agencyBrand.agencyAbout || '',

      candidatePromise: agencyBrand.candidatePromise || '',

      hiringProcess: agencyBrand.hiringProcess || '',

      contactEmail: agencyBrand.contactEmail || '',

      linkedInUrl: agencyBrand.linkedInUrl || '',

      agencyWebsite: agencyBrand.agencyWebsite || '',

      agencySlug: agencyBrand.agencySlug || '',

      createdAt: serverTimestamp(),

      createdAtIso: new Date().toISOString(),

      createdAtMs: Date.now(),

      status: 'active',

      privacyNotice: PUBLIC_PRIVACY_NOTICE,

      lawfulUseNotice: PUBLIC_LAWFUL_USE_NOTICE,

    };

    try {

      await setDoc(publicCandidateJobIntakeLinkDoc(linkId), linkPayload, { merge: true });

      const careersSlug = agencyBrand.agencySlug || recruiterUser.uid;

      const careersUrl = `${getPublicAppBaseUrl()}/careers/${careersSlug}`;

      await navigator.clipboard.writeText(careersUrl);

      setJobPortfolioNotice(`Careers page link copied: ${careersUrl}`);

    } catch (error) {

      console.error('Failed to create candidate jobs link', error);

      setJobPortfolioNotice('Failed to create the candidate jobs link.');

    }

  };



  const handleSubmitCandidateJobApplication = async (event) => {

    event.preventDefault();

    const selectedJob = Array.isArray(publicCandidateJobIntakeLink?.jobs) ? publicCandidateJobIntakeLink.jobs.find((job) => job.id === selectedPublicCandidateJobId) || publicCandidateJobIntakeLink.jobs[0] : null;

    if (!app || !candidateJobIntakeLinkId || !publicCandidateJobIntakeLink || !candidateCvFile || !selectedJob) return;

    setIsSubmittingCandidateApplication(true);

    setCandidateApplicationNotice('');

    try {

      if (!auth?.user) {

        await auth?.loginAnonymously?.();

      }

      const rawText = await extractPdfText(candidateCvFile);

      const cvName = extractName(rawText);
      const cvEmail = extractEmail(rawText);
      const cvPhone = extractPhone(rawText);
      const cvLocation = extractLocation(rawText);
      const mismatches = getCandidateDetailMismatches(candidateApplicationForm, {
        name: cvName === 'Unknown Candidate' ? '' : cvName,
        email: cvEmail,
        phone: cvPhone,
        location: cvLocation,
      });
      if (mismatches.length) {
        setCandidateApplicationNotice(`Please correct the details before submitting. ${mismatches.join(' ')}`);
        return;
      }
      const name = cvName && cvName !== 'Unknown Candidate' ? cvName : candidateApplicationForm.name.trim();
      const email = cvEmail || candidateApplicationForm.email.trim();
      const phone = cvPhone || candidateApplicationForm.phone.trim();
      const location = cvLocation || candidateApplicationForm.location.trim();

      const skills = extractSkills(rawText);

      const yearsExperience = extractYearsExperience(rawText);

      const fit = scoreAgainstJob({

        role: selectedJob.title || '',

        company: selectedJob.companyName || '',

        skills,

        rawText,

      }, {

        title: selectedJob.title || '',

        requirements: selectedJob.requirements || selectedJob.summary || '',

      });

      let resumeUrl = '';

      if (storage) {

        const recruiterId = String(publicCandidateJobIntakeLink.recruiterId || '').trim() || 'public';

        const fileExt = candidateCvFile.name && candidateCvFile.name.toLowerCase().endsWith('.pdf') ? '.pdf' : '';

        const safeFileName = createStorageSafeFileName(candidateCvFile.name || `${name || 'candidate-cv'}${fileExt || '.pdf'}`);

        const cvFileRef = storageRef(storage, `public/candidate-cvs/${recruiterId}/${candidateJobIntakeLinkId}/${Date.now()}-${safeFileName}`);

        await uploadBytes(cvFileRef, candidateCvFile, { contentType: candidateCvFile.type || 'application/pdf' });

        resumeUrl = await getDownloadURL(cvFileRef);

      }

      const functionsClient = getFunctions(app, 'us-central1');

      const submitCandidateJobApplication = httpsCallable(functionsClient, 'submitCandidateJobApplication');

      const response = await submitCandidateJobApplication({

        appId,

        linkId: candidateJobIntakeLinkId,

        selectedJobId: selectedJob.id,

        fileName: candidateCvFile.name,

        resumeUrl,

        rawText,

        name,

        email,

        phone,

        location,

        skills,

        yearsExperience,

        fitScore: fit.score,

        screeningAnswers: candidateScreeningAnswers,

      });

      setCandidateApplicationForm(DEFAULT_CANDIDATE_APPLICATION_FORM);

      setCandidateCvFile(null);

      setCandidateScreeningAnswers({});

      setHasSubmittedCandidateApplication(true);

      setCandidateApplicationNotice(response?.data?.message || `Your CV was shared with ${publicCandidateJobIntakeLink.agencyName}.`);

    } catch (error) {

      console.error('Failed to submit candidate CV', error);

      setCandidateApplicationNotice(error?.message || 'Failed to submit this CV.');

    } finally {

      setIsSubmittingCandidateApplication(false);

    }

  };



  const handleCopyJobOutreachMessage = async (job) => {

    if (!job) return;

    const message = buildJobClientOutreachMessage(job, profile.name, profile.agency);

    try {

      await navigator.clipboard.writeText(message);

      setJobPortfolioNotice(`AI outreach message copied for ${job.title}.`);

    } catch (error) {

      console.error('Failed to copy job outreach message', error);

      setJobPortfolioNotice('Could not copy the AI outreach message on this device.');

    }

  };



  const handleCopyRecruiterCompanyMessage = async () => {

    if (!recruiterCompanyMessage) return;

    try {

      await navigator.clipboard.writeText(recruiterCompanyMessage);

      setJobPortfolioNotice('Recruiter outreach message copied.');

    } catch (error) {

      console.error('Failed to copy recruiter message', error);

      setJobPortfolioNotice('Could not copy the outreach message on this device.');

    }

  };



  const handleOpenJobAts = (jobId) => {

    setSelectedJobId(jobId);

    setSelectedJobPanelId(jobId);
    if (isDemoWorkspace && directDemoWalkthroughOpen) {
      const demoJob = jobs.find((job) => job.id === jobId);
      if (demoJob) {
        const walkthroughProspect = buildDemoProspectForJob(demoJob);
        setExternalProfiles((current) => {
          const hasProspectForJob = current.some((profile) => profile.matchedJobId === demoJob.id);
          if (hasProspectForJob) return current;
          return [walkthroughProspect, ...current];
        });
      }
    }
    if (directDemoWalkthroughOpen) markDirectDemoStepComplete(3);

  };



  const handleCloseJobAts = () => {

    setSelectedJobPanelId('');

  };



  const handleOpenJobPipelineView = (job) => {

    if (!job) return;

    setActiveTab('pipeline');

    setPipelineView('table');

    setPipelineFilters({

      stage: '',

      company: getJobCompanyName(job),

      role: job.title,

    });

    setSelectedJobPanelId('');

  };



  const handleSaveJobNotes = async () => {

    if (!recruiterUser?.uid || !selectedJobPanel) return;

    setIsSavingJobNotes(true);

    const nextJobs = jobs.map((job) => (job.id === selectedJobPanel.id ? { ...job, notes: jobNotesDraft.trim() } : job));

    try {

      await setDoc(userDocRef(recruiterUser.uid), {

        jobPortfolio: nextJobs,

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'job_notes_updated', `Recruiter notes updated for ${selectedJobPanel.title}.`, { jobId: selectedJobPanel.id, company: getJobCompanyName(selectedJobPanel) });

      setJobs(nextJobs);

      setJobPortfolioNotice(`Recruiter notes saved for ${selectedJobPanel.title}.`);

    } catch (error) {

      console.error('Failed to save recruiter notes', error);

      setJobPortfolioNotice('Failed to save recruiter notes for this role.');

    } finally {

      setIsSavingJobNotes(false);

    }

  };

  const handleUpdateJobPublishing = async (jobId, updates = {}, notice = '') => {
    if (!recruiterUser?.uid || !jobId) return;
    if (Object.keys(updates || {}).length === 0) {
      if (notice) setJobPortfolioNotice(notice);
      return;
    }
    if (!requireAccess('jobs.publish', 'You need job publishing access to update public board settings.')) return;
    const cleanedUpdates = Object.fromEntries(Object.entries(updates).map(([key, value]) => [key, typeof value === 'string' ? value.trim() : value]));
    const nextJobs = jobs.map((job) => (job.id === jobId ? { ...job, ...cleanedUpdates, publishingUpdatedAt: new Date().toISOString() } : job));
    const updatedJob = nextJobs.find((job) => job.id === jobId);
    try {
      await setDoc(userDocRef(recruiterUser.uid), {
        jobPortfolio: nextJobs,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setJobs(nextJobs);
      await recordActivity(recruiterUser.uid, 'job_publishing_updated', `Publishing settings updated for ${updatedJob?.title || 'job'}.`, { jobId, updates: cleanedUpdates });
      setJobPortfolioNotice(notice || `Publishing settings updated for ${updatedJob?.title || 'this role'}.`);
    } catch (error) {
      console.error('Failed to update job publishing settings', error);
      setJobPortfolioNotice('Failed to update the public publishing settings.');
    }
  };

  const handleRemoveJob = async (jobId) => {
    if (!recruiterUser?.uid || !jobId) return;
    if (!requireAccess('jobs.delete', 'You need job delete access to remove roles from the portfolio.')) return;
    const removedJob = jobs.find((job) => job.id === jobId);
    const nextJobs = jobs.map((job) => (job.id === jobId ? { ...job, status: 'Removed', removedAt: new Date().toISOString() } : job));
    try {
      await setDoc(userDocRef(recruiterUser.uid), {
        jobPortfolio: nextJobs,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setJobs(nextJobs);
      setSelectedJobPanelId('');
      setRemoveJobConfirmId('');
      await recordActivity(recruiterUser.uid, 'job_removed', `${removedJob?.title || 'Role'} removed from job portfolio.`, { jobId, company: getJobCompanyName(removedJob || {}) });
      setJobPortfolioNotice(`${removedJob?.title || 'Role'} removed from portfolio.`);
    } catch (error) {
      console.error('Failed to remove job', error);
      setJobPortfolioNotice('Failed to remove this role from the portfolio.');
    }
  };

  const [removeAllJobsConfirm, setRemoveAllJobsConfirm] = useState(false);

  const handleRemoveAllJobs = async () => {
    if (!recruiterUser?.uid) return;
    if (!requireAccess('jobs.delete', 'You need job delete access to remove roles from the portfolio.')) return;
    const removedAt = new Date().toISOString();
    const nextJobs = jobs.map((job) =>
      ['removed', 'closed'].includes(String(job.status || '').toLowerCase())
        ? job
        : { ...job, status: 'Removed', removedAt }
    );
    try {
      await setDoc(userDocRef(recruiterUser.uid), { jobPortfolio: nextJobs, updatedAt: serverTimestamp() }, { merge: true });
      setJobs(nextJobs);
      setSelectedJobPanelId('');
      setRemoveAllJobsConfirm(false);
      await recordActivity(recruiterUser.uid, 'jobs_all_removed', `All active roles removed from job portfolio.`, {});
      setJobPortfolioNotice('All active roles have been removed from the portfolio.');
    } catch (error) {
      console.error('Failed to remove all jobs', error);
      setJobPortfolioNotice('Failed to remove all roles. Please try again.');
    }
  };

  const handleReassignCandidateJob = async (candidateId, newJobId) => {
    if (!recruiterUser?.uid || !candidateId || !newJobId) return;
    const newJob = jobs.find((j) => j.id === newJobId);
    if (!newJob) return;
    const newMatchedCompany = getJobCompanyName(newJob);
    const updatedAt = new Date().toISOString();
    try {
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
        matchedJobId: newJob.id,
        matchedJobTitle: newJob.title,
        role: newJob.title,
        company: newMatchedCompany,
        lastActivityAt: updatedAt,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCandidates((prev) => prev.map((c) => c.id === candidateId
        ? { ...c, matchedJobId: newJob.id, matchedJobTitle: newJob.title, role: newJob.title, company: newMatchedCompany, lastActivityAt: updatedAt }
        : c));
      await recordActivity(recruiterUser.uid, 'candidate_job_reassigned', `${candidates.find((c) => c.id === candidateId)?.name || 'Candidate'} reassigned to ${newJob.title} at ${newMatchedCompany}.`, { candidateId, jobId: newJob.id });
    } catch (error) {
      console.error('Failed to reassign candidate job', error);
    }
  };

  const handleRequestJobMatches = (candidateId) => {
    const candidate = candidates.find((c) => c.id === candidateId);
    if (!candidate) return;
    const activeJobs = jobs.filter((j) => !['closed', 'removed'].includes(String(j.status || '').toLowerCase()));
    const profile = {
      role: candidate.role || candidate.matchedJobTitle || '',
      skills: candidate.skills || [],
      rawText: candidate.rawText || candidate.semanticSearchText || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      location: candidate.location || '',
      company: candidate.company || '',
    };
    const ranked = rankCvJobsLocally(profile, activeJobs);
    const results = ranked.map(({ job, blendedScore, aiScore }) => {
      const scored = scoreCandidateForJob(candidate, job, screeningResponses[candidateId]);
      return {
        jobId: job.id,
        jobTitle: job.title,
        companyName: getJobCompanyName(job),
        overall: scored.overall,
        blendedScore,
        aiScore: aiScore.score,
        dimensions: scored.dimensions,
        evidence: scored.evidence,
        missing: scored.missing,
        reason: buildJobMatchReason(scored.overall, blendedScore, scored.dimensions, scored.missing, scored.evidence, scored.profile, job, candidate),
        isPrimary: job.id === candidate.matchedJobId,
      };
    });
    setCandidateJobMatchResults((prev) => ({ ...prev, [candidateId]: results }));
  };



  const handlePipelineReject = async (candidateId, reason, sendEmail, emailBody) => {
    if (!recruiterUser?.uid || !candidateId || !reason) return;
    setIsSavingReject(true);
    try {
      const rejectedAt = new Date().toISOString();
      const cand = candidates.find((c) => c.id === candidateId);
      const reasonLabel = PIPELINE_REJECT_REASON_LABELS[reason] || reason;
      const trimmedEmailBody = String(emailBody || '').trim();
      const feedbackStatus = sendEmail && cand?.email && trimmedEmailBody ? 'email_prepared' : sendEmail ? 'email_missing' : 'internal_only';
      const feedbackRecord = {
        id: `feedback-${Date.now()}`,
        type: 'candidate_outcome',
        stageId: 'rejected',
        reason,
        reasonLabel,
        channel: sendEmail ? 'email' : 'internal',
        status: feedbackStatus,
        createdAtIso: rejectedAt,
        createdBy: profile?.name || recruiterUser.email || 'Recruiter',
      };
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
        status: 'rejected',
        pipelineRejected: true,
        pipelineRejectReason: reason,
        pipelineRejectReasonLabel: reasonLabel,
        pipelineRejectEmailSent: sendEmail,
        pipelineFeedbackChannel: sendEmail ? 'email' : 'internal',
        pipelineFeedbackStatus: feedbackStatus,
        pipelineRejectEmailBody: trimmedEmailBody || '',
        pipelineRejectedAt: rejectedAt,
        candidateFeedbackHistory: arrayUnion(feedbackRecord),
        stageHistory: arrayUnion({
          stageId: 'rejected',
          stageLabel: 'Not Selected',
          movedAt: rejectedAt,
          source: 'candidate_feedback_reason',
          reason,
          reasonLabel,
        }),
        stageId: 'rejected',
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCandidates((prev) => prev.map((c) => c.id === candidateId
        ? {
            ...c,
            status: 'rejected',
            pipelineRejected: true,
            pipelineRejectReason: reason,
            pipelineRejectReasonLabel: reasonLabel,
            pipelineRejectEmailSent: sendEmail,
            pipelineFeedbackChannel: sendEmail ? 'email' : 'internal',
            pipelineFeedbackStatus: feedbackStatus,
            pipelineRejectEmailBody: trimmedEmailBody || '',
            pipelineRejectedAt: rejectedAt,
            stageId: 'rejected',
            candidateFeedbackHistory: [...(Array.isArray(c.candidateFeedbackHistory) ? c.candidateFeedbackHistory : []), feedbackRecord],
            stageHistory: [...(Array.isArray(c.stageHistory) ? c.stageHistory : []), {
              stageId: 'rejected',
              stageLabel: 'Not Selected',
              movedAt: rejectedAt,
              source: 'candidate_feedback_reason',
              reason,
              reasonLabel,
            }],
          }
        : c
      ));
      await recordActivity(recruiterUser.uid, 'pipeline_rejected',
        `${cand?.name || 'Candidate'} was marked not selected. Candidate feedback reason: ${reasonLabel}.`,
        { candidateId, reason, reasonLabel, emailSent: sendEmail, feedbackStatus }
      );
      if (sendEmail && trimmedEmailBody) {
        try {
          if (cand?.email) {
            const functionsClient = getFunctions(app, 'us-central1');
            const notifyCandidate = httpsCallable(functionsClient, 'notifyCandidatePipelineStatus');
            notifyCandidate({
              toEmail: cand.email,
              toName: cand.name || '',
              jobTitle: cand.matchedJobTitle || cand.role || '',
              companyName: cand.company || '',
              agencyName: agencySettings?.agencyName || '',
              status: 'rejected',
              customBody: trimmedEmailBody,
            }).catch((err) => console.error('[email] Pipeline reject notification failed:', err?.message || err));
          }
        } catch (_e) { console.error('[email] Pipeline reject email setup error:', _e); }
      }
      setRejectPipelineCandidateId(null);
      setRejectReason('');
      setRejectSendEmail(false);
      setRejectEmailBody('');
    } catch (err) {
      console.error('Failed to reject pipeline candidate', err);
    } finally {
      setIsSavingReject(false);
    }
  };

  const handleSendOffer = async (candidateId, draft) => {
    if (!candidateId) return;
    setOfferError('');
    setIsSavingOffer(true);
    try {
      const offerCand = candidates.find((c) => c.id === candidateId);
      if (!offerCand) throw new Error('Candidate not found.');
      const compensation = draft.amount
        ? `${draft.currency || 'USD'} ${Number(String(draft.amount).replace(/[^0-9.]/g, '') || 0).toLocaleString()}`
        : '';
      const sentDate = draft.sentDate || new Date().toISOString().slice(0, 10);
      const expiryDate = draft.expiryDate || new Date(Date.now() + 5 * 24 * 3600 * 1000).toISOString().slice(0, 10);
      const offerRecord = {
        amount: draft.amount ? Number(String(draft.amount).replace(/[^0-9.]/g, '')) : null,
        currency: draft.currency || 'USD',
        sentDate,
        expiryDate,
        note: (draft.note || '').trim(),
        status: 'pending', // pending | accepted | declined
        sentAt: new Date().toISOString(),
      };

      if (isDemoWorkspace) {
        const demoOfferRecord = {
          ...offerRecord,
          amount: offerRecord.amount || 85000,
          currency: offerRecord.currency || 'USD',
          note: offerRecord.note || 'Demo offer packet sent from the walkthrough.',
        };
        setCandidates((prev) => prev.map((c) => c.id === candidateId ? {
          ...c,
          status: 'offer',
          offer: demoOfferRecord,
          lastActivityAt: demoOfferRecord.sentAt,
          crmStatus: 'offer_sent',
          crmTimeline: [
            ...(Array.isArray(c.crmTimeline) ? c.crmTimeline : []),
            createCrmEvent('offer_sent', `Offer packet sent for ${c.name || 'candidate'}.`, { candidateId, amount: demoOfferRecord.amount, currency: demoOfferRecord.currency, expiryDate: demoOfferRecord.expiryDate }),
          ],
        } : c));
        setPipelineNotice(`Offer packet sent for ${offerCand.name || 'candidate'}.`);
        setSendOfferCandidateId(null);
        setOfferDraft((current) => ({ ...current, amount: '', sentDate: '', expiryDate: '', note: '', emailBody: '', customNegotiation: '' }));
        if (directDemoWalkthroughOpen) markDirectDemoStepComplete(10);
        if (importDemoWalkthroughOpen) markImportDemoStepComplete(10);
        return;
      }

      if (!recruiterUser?.uid) {
        setPipelineNotice('Vision is still preparing the workspace. Try Send Offer Packet again in a moment.');
        return;
      }
      if (!offerCand?.email) throw new Error('Add the candidate email before sending a client hire confirmation link.');
      if (!draft.clientEmail?.trim()) throw new Error('Client email is required.');
      if (!draft.clientLegalName?.trim() || !draft.clientAddress?.trim()) throw new Error('Client legal name and address are required for the PDF.');
      if (!draft.startDate?.trim() || !draft.workLocation?.trim() || !draft.workingHours?.trim()) throw new Error('Start date, work location, and working hours are required.');
      if (!compensation || !draft.payFrequency?.trim() || !draft.leaveTerms?.trim() || !draft.noticeTerms?.trim()) throw new Error('Compensation, pay frequency, leave terms, and notice terms are required.');
      const sendOfferNegotiationEmail = httpsCallable(functionsClient, 'sendOfferNegotiationEmail');
      const negotiationResult = await sendOfferNegotiationEmail({
        appId,
        candidateId,
        clientEmail: draft.clientEmail,
        clientName: draft.clientName || draft.clientLegalName,
        clientLegalName: draft.clientLegalName,
        clientAddress: draft.clientAddress,
        jobTitle: offerCand.matchedJobTitle || offerCand.role || '',
        startDate: draft.startDate,
        workLocation: draft.workLocation,
        employmentType: draft.employmentType,
        workingHours: draft.workingHours,
        compensation,
        payFrequency: draft.payFrequency,
        benefits: draft.benefits,
        leaveTerms: draft.leaveTerms,
        overtimeTerms: draft.overtimeTerms,
        deductions: draft.deductions,
        noticeTerms: draft.noticeTerms,
        sectorTerms: draft.sectorTerms,
        customNegotiation: draft.customNegotiation,
        emailBody: draft.emailBody,
        expiryDate,
      });
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
        offer: offerRecord,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCandidates((prev) => prev.map((c) => c.id === candidateId ? { ...c, offer: offerRecord } : c));
      await recordActivity(recruiterUser.uid, 'offer_sent',
        `Offer sent to ${candidates.find((c) => c.id === candidateId)?.name || 'candidate'}${offerRecord.amount ? ` — ${offerRecord.currency} ${Number(offerRecord.amount).toLocaleString()}` : ''}.`,
        { candidateId, amount: offerRecord.amount, currency: offerRecord.currency, expiryDate }
      );
      setPipelineNotice(negotiationResult?.data?.message || `Offer negotiation PDF sent to ${draft.clientEmail}.`);
      setSendOfferCandidateId(null);
      setOfferDraft((current) => ({ ...current, amount: '', sentDate: '', expiryDate: '', note: '', emailBody: '', customNegotiation: '' }));
    } catch (err) {
      console.error('Failed to save offer', err);
      setOfferError(err?.message || 'Failed to send offer negotiation email.');
      setPipelineNotice(err?.message || 'Failed to send offer negotiation email.');
    } finally {
      setIsSavingOffer(false);
    }
  };

  const handleDraftOfferNegotiationEmail = async (candidate, draft) => {
    if (!candidate) return;
    setIsDraftingOfferEmail(true);
    try {
      const compensation = draft.amount ? `${draft.currency || 'USD'} ${Number(String(draft.amount).replace(/[^0-9.]/g, '') || 0).toLocaleString()}` : 'the proposed offer';
      const draftOfferNegotiationEmail = httpsCallable(functionsClient, 'draftOfferNegotiationEmail');
      const response = await draftOfferNegotiationEmail({
        clientName: draft.clientName || draft.clientLegalName || 'client',
        candidateName: candidate.name || 'candidate',
        jobTitle: candidate.matchedJobTitle || candidate.role || 'role',
        compensation,
        customNegotiation: draft.customNegotiation,
      });
      setOfferDraft((current) => ({ ...current, emailBody: response?.data?.body || current.emailBody }));
    } catch (error) {
      setPipelineNotice(error?.message || 'Failed to draft offer negotiation email.');
    } finally {
      setIsDraftingOfferEmail(false);
    }
  };

  const handleAcceptOffer = async (candidateId) => {
    if (!recruiterUser?.uid || !candidateId) return;
    const acceptedAt = new Date().toISOString();
    await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
      'offer.status': 'accepted',
      'offer.acceptedAt': acceptedAt,
      updatedAt: serverTimestamp(),
    }, { merge: true });
    setCandidates((prev) => prev.map((c) => c.id === candidateId
      ? { ...c, offer: { ...c.offer, status: 'accepted', acceptedAt } }
      : c
    ));
    await recordActivity(recruiterUser.uid, 'offer_accepted',
      `Offer accepted by ${candidates.find((c) => c.id === candidateId)?.name || 'candidate'}.`,
      { candidateId }
    );
  };

  const handleMarkOfferDeclined = async (candidateId, category, note) => {
    if (!recruiterUser?.uid || !candidateId || !category) return;
    setIsSavingOfferDecline(true);
    try {
      const declinedAt = new Date().toISOString();
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
        offerDeclineCategory: category,
        offerDeclineNote: note || '',
        offerDeclinedAt: declinedAt,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCandidates((prev) => prev.map((c) => c.id === candidateId
        ? { ...c, offerDeclineCategory: category, offerDeclineNote: note || '', offerDeclinedAt: declinedAt }
        : c
      ));
      await recordActivity(recruiterUser.uid, 'offer_declined',
        `Offer declined by ${candidates.find((c) => c.id === candidateId)?.name || 'candidate'}: ${OFFER_DECLINE_CATEGORY_LABELS[category] || category}.`,
        { candidateId, category, note: note || '' }
      );
      setOfferDeclineCandidateId(null);
      setOfferDeclineCategory('');
      setOfferDeclineNote('');
    } catch (err) {
      console.error('Failed to record offer decline', err);
    } finally {
      setIsSavingOfferDecline(false);
    }
  };

  const handleSaveEeocSelfId = async () => {
    if (!recruiterUser?.uid || !eeocEditCandidateId) return;
    setIsSavingEeoc(true);
    try {
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, eeocEditCandidateId), {
        eeocSelfId: eeocDraft,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCandidates((prev) => prev.map((c) => c.id === eeocEditCandidateId ? { ...c, eeocSelfId: eeocDraft } : c));
      setEeocEditCandidateId(null);
      setEeocDraft({});
    } catch (err) {
      console.error('EEOC self-ID save error', err);
    } finally {
      setIsSavingEeoc(false);
    }
  };

  const handleSaveQoh = async () => {
    if (!recruiterUser?.uid || !qohEditCandidateId) return;
    setIsSavingQoh(true);
    try {
      const reviewedAt = new Date().toISOString();
      const score90 = qohDraft.score90 !== '' ? parseInt(qohDraft.score90, 10) : null;
      const score180 = qohDraft.score180 !== '' ? parseInt(qohDraft.score180, 10) : null;
      await setDoc(recruiterCandidateDoc(recruiterUser.uid, qohEditCandidateId), {
        qohScore90: score90,
        qohScore180: score180,
        qohNotes: qohDraft.notes || '',
        qohReviewedAt: reviewedAt,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setCandidates((prev) => prev.map((c) => c.id === qohEditCandidateId
        ? { ...c, qohScore90: score90, qohScore180: score180, qohNotes: qohDraft.notes || '', qohReviewedAt: reviewedAt }
        : c
      ));
      setQohEditCandidateId(null);
      setQohDraft({ score90: '', score180: '', notes: '' });
    } catch (err) {
      console.error('QoH save error', err);
    } finally {
      setIsSavingQoh(false);
    }
  };

  const handleSaveSavedReport = async () => {
    if (!recruiterUser?.uid || !profile.agencyKey || !savedReportDraft.name.trim()) return;
    setIsSavingSavedReport(true);
    try {
      const now = new Date().toISOString();
      let updated;
      if (savedReportEditId) {
        updated = savedReports.map((r) =>
          r.id === savedReportEditId
            ? { ...r, name: savedReportDraft.name.trim(), sections: savedReportDraft.sections, schedule: savedReportDraft.schedule, updatedAt: now }
            : r
        );
      } else {
        const newId = 'rpt-' + Date.now();
        const shareToken = newId + '-' + Math.random().toString(36).slice(2, 8);
        updated = [...savedReports, {
          id: newId,
          shareToken,
          name: savedReportDraft.name.trim(),
          sections: savedReportDraft.sections,
          schedule: savedReportDraft.schedule,
          createdAt: now,
          createdBy: recruiterUser.uid,
        }];
      }
      await setDoc(agencyDocRef(profile.agencyKey), { savedReports: updated, updatedAt: serverTimestamp() }, { merge: true });
      setSavedReports(updated);
      setSavedReportsPanelOpen(false);
      setSavedReportEditId(null);
    } catch (err) {
      console.error('Save report error', err);
    } finally {
      setIsSavingSavedReport(false);
    }
  };

  const handleDeleteSavedReport = async (reportId) => {
    if (!recruiterUser?.uid || !profile.agencyKey || !reportId) return;
    const updated = savedReports.filter((r) => r.id !== reportId);
    try {
      await setDoc(agencyDocRef(profile.agencyKey), { savedReports: updated, updatedAt: serverTimestamp() }, { merge: true });
      setSavedReports(updated);
    } catch (err) {
      console.error('Delete report error', err);
    }
  };

  // ── Scheduled email digest handlers ────────────────────────────────────────
  const loadReportSchedules = async () => {
    if (!profile.agencyKey) return;
    setSchedulesLoading(true);
    try {
      const q = query(
        collection(db, 'artifacts', appId, 'agencies', profile.agencyKey, 'report_schedules'),
        orderBy('createdAt', 'desc'),
        limit(20)
      );
      const snap = await getDocs(q);
      setReportSchedules(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('loadReportSchedules error', err);
    } finally {
      setSchedulesLoading(false);
    }
  };

  const handleOpenNewSchedule = () => {
    setScheduleEditId(null);
    setScheduleDraft({
      label: '',
      cadence: 'weekly',
      timeframe: '30d',
      recruiterFilter: 'all',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'Etc/UTC',
      preferredRunHour: 9,
      deliveryMode: 'attachment_and_link',
      recipients: [],
      status: 'active',
    });
    setSchedulesPanelOpen(true);
  };

  const handleEditSchedule = (sched) => {
    setScheduleEditId(sched.id);
    setScheduleDraft({
      label: sched.label || '',
      cadence: sched.cadence || 'weekly',
      timeframe: sched.timeframe || '30d',
      recruiterFilter: sched.recruiterFilter || 'all',
      timeZone: sched.timeZone || 'Etc/UTC',
      preferredRunHour: sched.preferredRunHour ?? 9,
      deliveryMode: sched.deliveryMode || 'attachment_and_link',
      recipients: Array.isArray(sched.recipients) ? sched.recipients : [],
      status: sched.status || 'active',
    });
    setSchedulesPanelOpen(true);
  };

  const handleSaveSchedule = async () => {
    if (!recruiterUser?.uid || !profile.agencyKey || !scheduleDraft?.label?.trim()) return;
    setIsSavingSchedule(true);
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const upsertSchedule = httpsCallable(functionsClient, 'upsertAgencyReportSchedule');
      const payload = {
        agencyId: profile.agencyKey,
        schedule: {
          ...(scheduleEditId ? { id: scheduleEditId } : {}),
          label: scheduleDraft.label.trim(),
          cadence: scheduleDraft.cadence,
          timeframe: scheduleDraft.timeframe,
          recruiterFilter: scheduleDraft.recruiterFilter,
          timeZone: scheduleDraft.timeZone,
          preferredRunHour: Number(scheduleDraft.preferredRunHour),
          deliveryMode: scheduleDraft.deliveryMode,
          recipients: scheduleDraft.recipients,
          status: scheduleDraft.status,
        },
      };
      await upsertSchedule(payload);
      setSchedulesPanelOpen(false);
      setScheduleDraft(null);
      setScheduleEditId(null);
      await loadReportSchedules();
    } catch (err) {
      console.error('handleSaveSchedule error', err);
    } finally {
      setIsSavingSchedule(false);
    }
  };

  const handleDeleteSchedule = async (scheduleId) => {
    if (!recruiterUser?.uid || !profile.agencyKey || !scheduleId) return;
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const deleteSchedule = httpsCallable(functionsClient, 'deleteAgencyReportSchedule');
      await deleteSchedule({ agencyId: profile.agencyKey, scheduleId });
      await loadReportSchedules();
    } catch (err) {
      console.error('handleDeleteSchedule error', err);
    }
  };

  const handleRunScheduleNow = async (scheduleId) => {
    if (!recruiterUser?.uid || !profile.agencyKey || !scheduleId) return;
    setRunningScheduleId(scheduleId);
    setScheduleRunResult((prev) => ({ ...prev, [scheduleId]: null }));
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const runNow = httpsCallable(functionsClient, 'runAgencyReportScheduleNow');
      await runNow({ agencyId: profile.agencyKey, scheduleId });
      setScheduleRunResult((prev) => ({ ...prev, [scheduleId]: 'sent' }));
      await loadReportSchedules();
      setTimeout(() => setScheduleRunResult((prev) => ({ ...prev, [scheduleId]: null })), 5000);
    } catch (err) {
      console.error('handleRunScheduleNow error', err);
      setScheduleRunResult((prev) => ({ ...prev, [scheduleId]: 'error' }));
    } finally {
      setRunningScheduleId(null);
    }
  };

  const handleImportScrapedJob = async (listing) => {

    if (!recruiterUser?.uid || !listing) return;

    const nextJob = sanitizeJobRecord({

      id: `job-${Date.now()}`,

      agencyId: INITIAL_AGENCIES.find((agency) => normalizeText(agency.name) === normalizeText(listing.company || profile.agency))?.id || '',

      companyName: listing.company || profile.agency || '',

      title: listing.title || 'Imported role',

      department: 'Imported',

      type: listing.type || 'Full-time',

      applicants: 0,

      status: listing.status || 'Active',

      location: listing.location || 'Remote',

      salary: listing.salary || 'Compensation on request',

      recruiter: profile.name || 'Recruiter',

      summary: listing.summary || `Imported from LinkedIn Jobs niche search for ${listing.query || 'the selected market'}.`,

      requirements: listing.requirements || listing.summary || listing.title || 'Imported from LinkedIn Jobs.',

      createdAtIso: new Date().toISOString(),

      sourceUrl: listing.url || '',

      url: listing.url || '',

      job_url: listing.job_url || '',

      applyUrl: listing.applyUrl || '',

      apply_url: listing.apply_url || '',

      job_apply_link: listing.job_apply_link || '',

      job_google_link: listing.job_google_link || '',

      job_posted_link: listing.job_posted_link || '',

      job_offer_link: listing.job_offer_link || '',

      detailUrl: listing.detailUrl || '',

      sourceLabel: listing.source || 'LinkedIn Jobs',

    }, profile.agency || '');

    if (!nextJob) {

      setJobPortfolioNotice('Vision could not extract a clean role, company, and description from that scraped listing.');

      return;

    }

    nextJob.booleanQuery = createBooleanQueryFromJob(nextJob);



    const nextJobs = sanitizeJobPortfolio([nextJob, ...jobs], profile.agency || '');

    try {

      await setDoc(userDocRef(recruiterUser.uid), {

        jobPortfolio: nextJobs,

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'scraped_job_imported', `${nextJob.title} was imported from ${nextJob.sourceLabel}.`, { jobId: nextJob.id, company: nextJob.companyName, sourceUrl: nextJob.sourceUrl });

      setJobs(nextJobs);

      setSelectedJobId(nextJob.id);

      setBooleanSearchInput(nextJob.booleanQuery);

      setJobPortfolioNotice(`${nextJob.title} imported into the job portfolio.`);

    } catch (error) {

      console.error('Failed to import scraped job', error);

      setJobPortfolioNotice('Failed to import that scraped job into the portfolio.');

    }

  };



  const handlePushLeadToPipeline = async (lead, sourceType = 'manual', targetJob = selectedJob) => {

    const activeJob = targetJob || selectedJob;

    if (!activeJob || !lead) {
      setJobPortfolioNotice('Select a job and prospect before moving a sourced candidate into the pipeline.');
      return;
    }

    if (sourceType === 'external') {

      if (isDemoWorkspace) {
        const matchedJob = jobs.find((job) => job.id === lead.matchedJobId) || activeJob;
        const profileLead = buildSourceLeadProfile(lead, matchedJob);
        const candidateId = `demo-direct-candidate-${Date.now()}`;
        const nowIso = new Date().toISOString();
        const demoCandidate = {
          id: candidateId,
          recruiterId: recruiterUser?.uid || 'demo-user',
          screeningLinkId: `demo-direct-screening-${Date.now()}`,
          name: profileLead.name,
          email: 'maya.naidoo@example.com',
          phone: '',
          role: matchedJob.title,
          company: getJobCompanyName(matchedJob),
          location: profileLead.location,
          skills: profileLead.skills,
          status: 'applied',
          rating: profileLead.rating || lead.rating || 90,
          yearsExperience: 6,
          submittedAt: nowIso,
          lastActivityAt: nowIso,
          matchedJobId: matchedJob.id,
          matchedJobTitle: matchedJob.title,
          source: lead.source || 'Vision demo direct sourcing',
          rawText: lead.notes || profileLead.rawText || '',
          crmStatus: 'stage_applied',
          intakeDecision: 'sourced',
          intakeDecisionReason: 'Moved from sourced prospect during direct demo walkthrough.',
          screeningQuestions: Array.isArray(matchedJob.customScreeningQuestions) && matchedJob.customScreeningQuestions.length ? matchedJob.customScreeningQuestions : DEFAULT_DEMO_SCREENING_QUESTIONS,
          screeningDeadlineAt: null,
          stageHistory: [{ stageId: 'applied', stageLabel: 'New Applied', movedAt: nowIso, source: 'direct_demo' }],
          crmTimeline: [createCrmEvent('lead_pushed_to_pipeline', `${profileLead.name} was moved into the demo pipeline.`, { jobId: matchedJob.id, leadId: lead.id })],
          createdAtIso: nowIso,
        };
        setCandidates((current) => [
          demoCandidate,
          ...current.filter((candidate) => !(candidate.source === 'Vision demo direct sourcing' && candidate.matchedJobId === matchedJob.id)),
        ]);
        setExternalProfiles((current) => current.filter((profile) => profile.id !== lead.id));
        setSelectedJobPanelId('');
        setJobPortfolioNotice(`${profileLead.name} moved into the demo pipeline for ${matchedJob.title}.`);
        if (directDemoWalkthroughOpen) markDirectDemoStepComplete(4);
        return;
      }

      if (!recruiterUser?.uid) {
        setJobPortfolioNotice('Vision is still preparing the workspace. Try Move to pipeline again in a moment.');
        return;
      }

      try {

        const functionsClient = getFunctions(app, 'us-central1');

        const pushExternalProfileToPipeline = httpsCallable(functionsClient, 'pushExternalProfileToPipeline');

        const matchedJob = jobs.find((job) => job.id === lead.matchedJobId) || activeJob;

        await pushExternalProfileToPipeline({

          appId,

          externalProfileId: lead.id,

          selectedJob: matchedJob,

        });

        setSourcingRefreshNonce((current) => current + 1);

        setJobPortfolioNotice(`${lead.name || 'External profile'} pushed into the pipeline for ${(matchedJob && matchedJob.title) || activeJob.title}.`);
        if (directDemoWalkthroughOpen) markDirectDemoStepComplete(4);

      } catch (error) {

        console.error('Failed to push external profile into pipeline', error);

        setJobPortfolioNotice(error?.message || 'Failed to push the external profile into the pipeline.');

      }

      return;

    }

    if (!recruiterUser?.uid) {
      setJobPortfolioNotice('Vision is still preparing the workspace. Try Move to pipeline again in a moment.');
      return;
    }

    const profileLead = buildSourceLeadProfile(lead, activeJob);

    const candidateId = `lead-candidate-${Date.now()}`;

    const screeningLinkId = createScreeningLinkId(recruiterUser.uid, candidateId);

    const timelineEvent = createCrmEvent('lead_pushed_to_pipeline', `${profileLead.name} was pushed from sourcing inbox into the pipeline.`, { jobId: activeJob.id, leadId: lead.id });

    try {

      await setDoc(recruiterCandidateDoc(recruiterUser.uid, candidateId), {

        id: candidateId,

        recruiterId: recruiterUser.uid,

        screeningLinkId,

        name: profileLead.name,

        email: '',

        phone: '',

        role: activeJob.title,

        company: getJobCompanyName(activeJob),

        location: profileLead.location,

        skills: profileLead.skills,

        status: 'applied',

        screeningDeadlineAt: null,

        rating: profileLead.rating,

        yearsExperience: 0,

        submittedAt: timelineEvent.createdAtIso,

        lastActivityAt: timelineEvent.createdAtIso,

        matchedJobId: activeJob.id,

        matchedJobTitle: activeJob.title,

        source: lead.linkedInUrl ? `LinkedIn lead: ${lead.linkedInUrl}` : 'Sourcing inbox',

        rawText: `${lead.headline || ''} ${lead.notes || ''}`.trim(),

        crmStatus: 'sourced_lead',

        intakeDecision: 'sourced',

        intakeDecisionReason: 'Pushed manually from sourcing inbox.',

        automationState: {

          booleanMatched: true,

          sourcedFromLinkedIn: Boolean(lead.linkedInUrl),

          recruiterReviewed: true,

        },

        stageHistory: [{ stageId: 'applied', stageLabel: 'New Applied', movedAt: timelineEvent.createdAtIso, source: 'sourcing_inbox' }],

        crmTimeline: [timelineEvent],

        linkedInUrl: lead.linkedInUrl || '',

        createdAt: serverTimestamp(),

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'source_lead_pushed', `${profileLead.name} pushed from sourcing inbox into ${activeJob.title}.`, { jobId: activeJob.id, leadId: lead.id, candidateId });

      setSourcingRefreshNonce((current) => current + 1);

      setJobPortfolioNotice(`${profileLead.name} pushed into the pipeline for ${activeJob.title}.`);
      if (directDemoWalkthroughOpen) markDirectDemoStepComplete(4);

    } catch (error) {

      console.error('Failed to push sourcing lead into pipeline', error);

      setJobPortfolioNotice('Failed to push this sourcing lead into the pipeline.');

    }

  };



  const handleAddJobOffer = async (event) => {

    event?.preventDefault?.();

    const requiredFields = [
      { name: 'title', label: 'Role title' },
      { name: 'companyName', label: 'Company' },
      { name: 'location', label: 'Location' },
      { name: 'recruiter', label: 'Recruiter owner' },
      { name: 'requirements', label: 'Requirements' },
    ];
    const missingFields = requiredFields.filter((field) => !String(jobForm[field.name] || '').trim());
    if (missingFields.length > 0) {
      const nextErrors = missingFields.reduce((acc, field) => ({ ...acc, [field.name]: `${field.label} is required.` }), {});
      setJobFormErrors(nextErrors);
      setJobPortfolioNotice(`Complete required job fields: ${missingFields.map((field) => field.label).join(', ')}.`);
      const firstMissing = missingFields[0];
      window.setTimeout(() => {
        const el = document.querySelector(`[data-job-field="${firstMissing.name}"]`);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
          window.setTimeout(() => el.focus({ preventScroll: true }), 250);
        }
      }, 0);
      return;
    }

    setIsSavingJob(true);
    const resolvedTitle = jobForm.title.trim() || 'Untitled role';
    const resolvedCompanyName = (jobForm.companyName || profile.agency || 'Client').trim();
    const resolvedSummary = jobForm.summary.trim() || `${resolvedTitle} role created manually.`;
    const resolvedRequirements = jobForm.requirements.trim() || 'Requirements can be added later.';

    const nextJob = {

      id: `job-${Date.now()}`,

      agencyId: INITIAL_AGENCIES.find((agency) => normalizeText(agency.name) === normalizeText(resolvedCompanyName))?.id || '',

      companyName: resolvedCompanyName,

      title: resolvedTitle,

      department: jobForm.department.trim() || 'General',

      type: jobForm.type,

      applicants: 0,

      status: jobForm.status,

      location: jobForm.location.trim() || 'Remote',

      salary: jobForm.salary.trim() || 'Compensation on request',

      recruiter: jobForm.recruiter.trim() || profile.name || 'Recruiter',

      clientContact: jobForm.clientContact.trim(),

      relationshipStatus: jobForm.relationshipStatus || 'Active client',

      lastOutreach: jobForm.lastOutreach ? new Date(jobForm.lastOutreach).toISOString() : '',

      summary: resolvedSummary,

      requirements: resolvedRequirements,

      booleanQuery: (jobForm.booleanQuery || createBooleanQueryFromJob({ ...jobForm, companyName: jobForm.companyName || profile.agency })).trim(),

      createdAtIso: new Date().toISOString(),

      customScreeningQuestions: (jobForm.customScreeningQuestions || []).filter((q) => q.trim()),
      sourceLabel: 'Manual job',
      ownerName: (jobForm.ownerName || '').trim() || profile.name || '',
      lifecycleStage: jobForm.lifecycleStage || 'brief',
      stageUpdatedAt: new Date().toISOString(),
      targetFillDate: jobForm.targetFillDate || '',
      fee: (jobForm.fee || '').trim(),
      isBlocked: false,
      blockedReason: '',
      publishStatus: jobForm.publishStatus || 'Live',
      applicationRoutingMode: jobForm.applicationRoutingMode || 'pipeline',
      applicationRoutingOwner: (jobForm.applicationRoutingOwner || jobForm.ownerName || jobForm.recruiter || profile.name || '').trim(),
      showSalaryPublic: jobForm.showSalaryPublic !== false,
      showClientNamePublic: jobForm.showClientNamePublic !== false,
      trackingCampaign: (jobForm.trackingCampaign || '').trim(),
    };



    const nextJobs = sanitizeJobPortfolio([nextJob, ...jobs], profile.agency || '');
    const savedJob = nextJobs.find((job) => job.id === nextJob.id);

    if (isDemoWorkspace) {
      const demoJob = savedJob || {
        ...nextJob,
        title: nextJob.title || 'Untitled role',
        companyName: nextJob.companyName || 'Client',
        summary: nextJob.summary || 'Manual job card.',
        requirements: nextJob.requirements || 'Requirements can be added later.',
      };
      const demoJobs = savedJob ? nextJobs : [demoJob, ...jobs.filter((job) => job.id !== demoJob.id)];

      setJobs(demoJobs);
      const walkthroughProspect = buildDemoProspectForJob(demoJob);
      setExternalProfiles((current) => {
        const remaining = current.filter((profile) => profile.id !== walkthroughProspect.id && profile.matchedJobId !== demoJob.id);
        return [walkthroughProspect, ...remaining];
      });
      setSelectedJobId(demoJob.id);
      setBooleanSearchInput(demoJob.booleanQuery);
      setJobForm(DEFAULT_JOB_FORM);
      setJobFormErrors({});
      setJobPortfolioNotice(`${demoJob.title} added to the demo portfolio. Boolean sourcing is ready.`);
      if (directDemoWalkthroughOpen) markDirectDemoStepComplete(2);
      setIsSavingJob(false);
      return;
    }

    if (!recruiterUser?.uid) {
      setIsSavingJob(false);
      setJobPortfolioNotice('Vision is still preparing the workspace. Try Add job offer again in a moment.');
      return;
    }

    if (!savedJob) {

      const fallbackJob = {
        ...nextJob,
        title: nextJob.title || 'Untitled role',
        companyName: nextJob.companyName || 'Client',
        summary: nextJob.summary || 'Manual job card.',
        requirements: nextJob.requirements || 'Requirements can be added later.',
      };
      const fallbackJobs = [fallbackJob, ...jobs.filter((job) => job.id !== fallbackJob.id)];

      await setDoc(userDocRef(recruiterUser.uid), {

        jobPortfolio: fallbackJobs,

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'job_offer_added', `${fallbackJob.title} was added to the job portfolio.`, { jobId: fallbackJob.id, company: fallbackJob.companyName, booleanQuery: fallbackJob.booleanQuery });
      await upsertCompanyAndContact({
        companyName: fallbackJob.companyName,
        contactName: fallbackJob.clientContact,
        contactEmail: fallbackJob.clientContact,
        job: fallbackJob,
        source: 'manual_job_order',
      });

      setJobs(fallbackJobs);

      setSelectedJobId(fallbackJob.id);

      setBooleanSearchInput(fallbackJob.booleanQuery);

      setJobForm(DEFAULT_JOB_FORM);
      setJobFormErrors({});

      setJobPortfolioNotice(`${fallbackJob.title} added to the portfolio. Boolean sourcing is ready.`);
      if (directDemoWalkthroughOpen) markDirectDemoStepComplete(2);

      setIsSavingJob(false);

      return;

    }

    try {

      await setDoc(userDocRef(recruiterUser.uid), {

        jobPortfolio: nextJobs,

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'job_offer_added', `${nextJob.title} was added to the job portfolio.`, { jobId: nextJob.id, company: nextJob.companyName, booleanQuery: nextJob.booleanQuery });
      await upsertCompanyAndContact({
        companyName: nextJob.companyName,
        contactName: nextJob.clientContact,
        contactEmail: nextJob.clientContact,
        job: nextJob,
        source: 'manual_job_order',
      });

      setJobs(nextJobs);

      setSelectedJobId(savedJob.id);

      setBooleanSearchInput(savedJob.booleanQuery);

      setJobForm(DEFAULT_JOB_FORM);
      setJobFormErrors({});

      setJobPortfolioNotice(`${savedJob.title} added to the portfolio. Boolean sourcing is ready.`);
      if (directDemoWalkthroughOpen) markDirectDemoStepComplete(2);

    } catch (error) {

      console.error('Failed to add job offer', error);

      setJobPortfolioNotice('Failed to add the new job offer.');

    } finally {

      setIsSavingJob(false);

    }

  };



  const handleImportPipelineCsv = async () => {

    if (isDemoWorkspace && importDemoWalkthroughOpen) {
      const importedAtIso = new Date().toISOString();
      const primaryJob = jobs[0] || {
        id: 'demo-import-job-fallback',
        title: 'Imported Account Executive',
        companyName: 'Imported Client',
        location: 'Hybrid',
      };
      const demoCandidates = [
        {
          id: `demo-import-candidate-${Date.now()}-1`,
          recruiterId: recruiterUser?.uid || 'demo-user',
          screeningLinkId: `demo-import-screening-${Date.now()}-1`,
          name: 'Kabelo Maseko',
          email: 'kabelo.maseko@example.com',
          phone: '+27 82 555 0134',
          role: primaryJob.title,
          company: getJobCompanyName(primaryJob) || primaryJob.companyName || 'Imported Client',
          location: primaryJob.location || 'Johannesburg',
          skills: ['Client communication', 'Pipeline management', 'CRM hygiene', 'Shortlisting'],
          status: 'applied',
          rating: 88,
          yearsExperience: 5,
          source: 'Demo CSV import',
          submittedAt: importedAtIso,
          createdAtIso: importedAtIso,
          lastActivityAt: importedAtIso,
          matchedJobId: primaryJob.id,
          matchedJobTitle: primaryJob.title,
          crmStatus: 'csv_imported',
          intakeDecision: 'imported',
          stageHistory: [{ stageId: 'applied', stageLabel: 'New Applied', movedAt: importedAtIso, source: 'demo_csv_import' }],
          crmTimeline: [createCrmEvent('csv_imported', 'Kabelo Maseko imported from demo candidate CSV.', { jobId: primaryJob.id })],
        },
        {
          id: `demo-import-candidate-${Date.now()}-2`,
          recruiterId: recruiterUser?.uid || 'demo-user',
          screeningLinkId: `demo-import-screening-${Date.now()}-2`,
          name: 'Ava Williams',
          email: 'ava.williams@example.com',
          phone: '+27 82 555 0198',
          role: primaryJob.title,
          company: getJobCompanyName(primaryJob) || primaryJob.companyName || 'Imported Client',
          location: 'Cape Town',
          skills: ['Candidate sourcing', 'Interview coordination', 'Reporting'],
          status: 'applied',
          rating: 82,
          yearsExperience: 4,
          source: 'Demo CSV import',
          submittedAt: importedAtIso,
          createdAtIso: importedAtIso,
          lastActivityAt: importedAtIso,
          matchedJobId: primaryJob.id,
          matchedJobTitle: primaryJob.title,
          crmStatus: 'csv_imported',
          intakeDecision: 'imported',
          stageHistory: [{ stageId: 'applied', stageLabel: 'New Applied', movedAt: importedAtIso, source: 'demo_csv_import' }],
          crmTimeline: [createCrmEvent('csv_imported', 'Ava Williams imported from demo candidate CSV.', { jobId: primaryJob.id })],
        },
      ];
      setCandidates((current) => [...demoCandidates, ...current.filter((candidate) => !String(candidate.id || '').startsWith('demo-import-candidate-'))]);
      setPipelineNotice('Demo candidate CSV imported: 2 candidates mapped to the active job.');
      if (importDemoWalkthroughStep === 3) markImportDemoStepComplete(3);
      return;
    }

    if (!recruiterUser?.uid) return;
    if (!requireAccess('pipeline.write', 'You need candidate write access to import pipeline CSV files.')) return;

    const file = await openCsvPicker();

    if (!file) return;

    try {

      const rows = parseCsvText(await file.text());

      const candidatesToImport = rows

        .map((row, index) => {

          const name = row.name || row.candidate || '';

          const role = row.role || row.job_title || '';

          const company = row.company || row.organization || '';

          if (!name || !role) return null;

          const stage = normalizePipelineStage(row.stage || row.status);

          const matchedJob = jobs.find((job) => normalizeText(job.title) === normalizeText(role) && (!company || normalizeText(getJobCompanyName(job)) === normalizeText(company))) || jobs.find((job) => normalizeText(job.title) === normalizeText(role));

          const createdAtIso = new Date().toISOString();

          const candidateId = `csv-candidate-${Date.now()}-${index}`;

          return {

            id: candidateId,

            recruiterId: recruiterUser.uid,

            screeningLinkId: createScreeningLinkId(recruiterUser.uid, candidateId),

            name,

            role,

            company: company || getJobCompanyName(matchedJob) || profile.agency || '',

            location: row.location || row.city || '',

            email: row.email || '',

            phone: row.phone || '',

            status: stage,

            rating: Number(row.fit_score || row.fit || 70) || 70,

            yearsExperience: Number(row.years_experience || 0) || 0,

            submittedAt: createdAtIso,

            lastActivityAt: createdAtIso,

            matchedJobId: matchedJob?.id || '',

            matchedJobTitle: matchedJob?.title || role,

            source: 'CSV import',

            rawText: '',

            crmStatus: 'csv_imported',

            intakeDecision: 'imported',

            intakeDecisionReason: 'Imported from recruiter CSV.',

            automationState: {

              recruiterReviewed: true,

              importedFromCsv: true,

            },

            stageHistory: [{ stageId: stage, stageLabel: PIPELINE_STAGES.find((item) => item.id === stage)?.label || stage, movedAt: createdAtIso, source: 'csv_import' }],

            crmTimeline: [createCrmEvent('csv_imported', `${name} imported from candidate CSV.`, { candidateId, role, company })],

            createdAt: serverTimestamp(),

            updatedAt: serverTimestamp(),

          };

        })

        .filter(Boolean);

      if (!candidatesToImport.length) {

        setPipelineNotice('No valid candidate rows were found in that CSV. Required fields: name and role.');

        return;

      }

      const batch = writeBatch(db);

      candidatesToImport.forEach((candidate) => {

        batch.set(recruiterCandidateDoc(recruiterUser.uid, candidate.id), candidate, { merge: true });

      });

      await batch.commit();

      await recordActivity(recruiterUser.uid, 'pipeline_csv_imported', `Imported ${candidatesToImport.length} candidates into the pipeline from CSV.`, { count: candidatesToImport.length });

      setPipelineNotice(`Imported ${candidatesToImport.length} candidates into the pipeline.`);

    } catch (error) {

      console.error('Failed to import candidate CSV', error);

      setPipelineNotice('Failed to import candidate CSV.');

    }

  };



  const handleExportPipelineCsv = async () => {
    if (!requireAccess('pipeline.export', 'You need export permission to download candidate data.')) return;

    const rows = [

      ['name', 'role', 'company', 'location', 'email', 'phone', 'stage', 'fit_score', 'last_activity'],

      ...filteredTableCandidates.map((candidate) => [

        candidate.name || '',

        candidate.role || '',

        candidate.company || '',

        candidate.location || '',

        candidate.email || '',

        candidate.phone || '',

        candidate.status || '',

        candidate.rating || 0,

        candidate.lastActivityAt || candidate.submittedAt || '',

      ]),

    ];

    downloadCsv('vision-pipeline-table.csv', rows);

    if (recruiterUser?.uid) {

      await recordActivity(recruiterUser.uid, 'pipeline_csv_exported', `Exported ${filteredTableCandidates.length} pipeline rows to CSV.`, { count: filteredTableCandidates.length });

    }

    setPipelineNotice(`Exported ${filteredTableCandidates.length} pipeline rows.`);

  };



  // buildReportingXls replaced by downloadXlsxReport() in services/exportReport.js (SheetJS multi-sheet XLSX)

  const buildReportingRows = (kpiDeltas, activityTrendBuckets, sourceQualityItems, stageDistItems, scopeLabel, timeframeLabel) => {
    const rows = [
      ['Meta', 'Scope', scopeLabel],
      ['Meta', 'Timeframe', timeframeLabel],
    ];
    (kpiDeltas || []).forEach((k) => rows.push(['KPI', k.label, k.displayValue]));
    (stageDistItems || []).forEach((s) => rows.push(['Stage Distribution', s.label, s.count]));
    (sourceQualityItems || []).forEach((s) => {
      rows.push(['Source Quality', s.source + ' — candidates', s.count]);
      rows.push(['Source Quality', s.source + ' — screened %', Math.round(s.screeningRate) + '%']);
      rows.push(['Source Quality', s.source + ' — hired %', Math.round(s.hireRate) + '%']);
    });
    (activityTrendBuckets || []).forEach((b) => rows.push(['Activity Trend', b.label, b.value]));
    return rows;
  };

  const handleExportReportingXls = () => {
    downloadXlsxReport({
      kpiDeltasFull,
      stageVisuals,
      sourceQualityItems,
      sourceRoiItems,
      activityTrendBuckets,
      activityMixItems,
      conversionFunnel,
      recruiterLeaderboard,
      scopeLabel: reportingRecruiterFilter || 'All',
      timeframeLabel: reportingRangeDays + 'd',
    });
  };

  const handleExportReportingPdf = (kpiDeltas, activityTrendBuckets, sourceQualityItems, stageDistItems, scopeLabel, timeframeLabel) => {
    const win = window.open('', '_blank');
    if (!win) return;
    const stamp = new Date().toLocaleString();
    const esc = (v) => String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const rows = buildReportingRows(kpiDeltas, activityTrendBuckets, sourceQualityItems, stageDistItems, scopeLabel, timeframeLabel);
    const trs = rows.map(([sec, lbl, val]) =>
      '<tr><td class="sec">' + esc(sec) + '</td><td>' + esc(lbl) + '</td><td>' + esc(String(val)) + '</td></tr>'
    ).join('');
    const html = '<!DOCTYPE html><html><head><meta charset="utf-8"/><title>Vision Report</title><style>' +
      '*{box-sizing:border-box;margin:0;padding:0;}body{font-family:Arial,sans-serif;font-size:11px;color:#111;padding:24px;}' +
      'h1{font-size:17px;font-weight:700;margin-bottom:3px;}.sub{color:#555;margin-bottom:18px;}' +
      'table{border-collapse:collapse;width:100%;}th{background:#1a2744;color:#fff;padding:6px 10px;text-align:left;font-size:10px;text-transform:uppercase;}' +
      'td{padding:5px 10px;border-bottom:1px solid #e5e9f0;}td.sec{background:#f4f6fa;font-weight:600;color:#1a2744;}' +
      '@media print{@page{margin:15mm;size:A4;}}' +
      '</style></head><body>' +
      '<h1>Vision Recruiting — Analytics Report</h1>' +
      '<p class="sub">Generated: ' + esc(stamp) + ' &nbsp;·&nbsp; Scope: ' + esc(scopeLabel) + '</p>' +
      '<table><thead><tr><th>Section</th><th>Metric</th><th>Value</th></tr></thead><tbody>' + trs + '</tbody></table>' +
      '</body></html>';
    win.document.open();
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => { try { win.print(); } catch (_) {} }, 400);
  };



  const handleImportJobsCsv = async () => {

    if (isDemoWorkspace && importDemoWalkthroughOpen) {
      const importedAtIso = new Date().toISOString();
      const demoImportedJobs = sanitizeJobPortfolio([
        {
          id: `demo-import-job-${Date.now()}-1`,
          title: 'Imported Account Executive',
          companyName: 'Atlas Growth Partners',
          department: 'Sales',
          type: 'Permanent',
          applicants: 0,
          status: 'Active',
          publishStatus: 'Draft',
          location: 'Johannesburg / Hybrid',
          salary: 'ZAR 720k - 900k',
          recruiter: profile.name || 'Demo Recruiter',
          clientContact: 'Nomsa Dube',
          relationshipStatus: 'New client',
          lastOutreach: importedAtIso,
          summary: 'Imported from a demo jobs CSV: account executive role for a growth-stage client.',
          requirements: 'B2B sales, enterprise pipeline, CRM discipline, discovery calls, closing experience.',
          notes: 'Demo import row representing jobs received from a website, spreadsheet, or client email.',
          createdAtIso: importedAtIso,
          sourceLabel: 'Demo CSV import',
        },
        {
          id: `demo-import-job-${Date.now()}-2`,
          title: 'Imported Talent Coordinator',
          companyName: 'Northstar Fintech',
          department: 'People',
          type: 'Contract',
          applicants: 0,
          status: 'Active',
          publishStatus: 'Draft',
          location: 'Remote',
          salary: 'Market related',
          recruiter: profile.name || 'Demo Recruiter',
          clientContact: 'Lerato Mokoena',
          relationshipStatus: 'Active client',
          lastOutreach: importedAtIso,
          summary: 'Imported from a demo jobs CSV: recruiting coordinator role with scheduling and candidate care.',
          requirements: 'Scheduling, candidate communication, ATS updates, interview coordination.',
          notes: 'Demo import row representing jobs received from existing channels.',
          createdAtIso: importedAtIso,
          sourceLabel: 'Demo CSV import',
        },
      ].map((job) => ({ ...job, booleanQuery: createBooleanQueryFromJob(job) })), profile.agency || '');
      setJobs(sanitizeJobPortfolio([...demoImportedJobs, ...jobs], profile.agency || ''));
      setSelectedJobId(demoImportedJobs[0]?.id || selectedJobId);
      setJobPortfolioNotice(`Demo jobs CSV imported: ${demoImportedJobs.length} jobs added to the portfolio.`);
      if (importDemoWalkthroughStep === 1) markImportDemoStepComplete(1);
      return;
    }

    if (!recruiterUser?.uid) return;
    if (!requireAccess('jobs.write', 'You need job write access to import jobs.')) return;

    const file = await openCsvPicker();

    if (!file) return;

    try {

      const rows = parseCsvText(await file.text());

      const importedJobs = rows

        .map((row, index) => {

          const normalizedRow = Object.fromEntries(Object.entries(row || {}).map(([key, value]) => [normalizeText(key).replace(/\s+/g, '_'), value]));

          const job = sanitizeJobRecord({

            id: `job-csv-${Date.now()}-${index}`,

            agencyId: INITIAL_AGENCIES.find((agency) => normalizeText(agency.name) === normalizeText(normalizedRow.company_name || normalizedRow.company || normalizedRow.organization || profile.agency))?.id || '',

            companyName: normalizedRow.company_name || normalizedRow.company || normalizedRow.organization || normalizedRow.primary_description || profile.agency || '',

            title: normalizedRow.title || normalizedRow.role || normalizedRow.job_title || '',

            department: normalizedRow.department || normalizedRow.team || 'General',

            type: normalizedRow.type || normalizedRow.employment_type || 'Full-time',

            applicants: 0,

            status: normalizedRow.status || normalizedRow.job_state || 'Active',

            publishStatus: 'Draft',

            location: normalizedRow.location || normalizedRow.city || 'Remote',

            salary: normalizedRow.salary || 'Compensation on request',

            recruiter: profile.name || 'Recruiter',

            clientContact: normalizedRow.hiring_contact || normalizedRow.client_contact || normalizedRow.poster_id || '',

            relationshipStatus: normalizedRow.relationship_status || 'Active client',

            lastOutreach: normalizedRow.last_outreach || normalizedRow.created_at || '',

            summary: normalizedRow.summary || normalizedRow.description || normalizedRow.primary_description || normalizedRow.insight || '',

            requirements: normalizedRow.requirements || normalizedRow.description || normalizedRow.primary_description || normalizedRow.skill || normalizedRow.skills || '',

            notes: normalizedRow.notes || normalizedRow.insight || '',

            createdAtIso: normalizedRow.created_at || new Date().toISOString(),

            sourceLabel: 'CSV import',

            sourceUrl: normalizedRow.detail_url || normalizedRow.aboutlink || normalizedRow.about_link || '',

            url: normalizedRow.url || normalizedRow.job_url || '',

            job_url: normalizedRow.job_url || '',

            detailUrl: normalizedRow.detail_url || normalizedRow.detailurl || '',

            applyUrl: normalizedRow.apply_url || normalizedRow.applyurl || '',

            apply_url: normalizedRow.apply_url || normalizedRow.applyurl || '',

            job_apply_link: normalizedRow.job_apply_link || '',

            job_google_link: normalizedRow.job_google_link || '',

            job_posted_link: normalizedRow.job_posted_link || '',

            job_offer_link: normalizedRow.job_offer_link || '',

            linkedInJobId: normalizedRow.jobid || normalizedRow.job_id || '',

          }, profile.agency || '');

          if (!job) return null;

          job.booleanQuery = createBooleanQueryFromJob(job);

          return job;

        })

        .filter(Boolean);

      if (!importedJobs.length) {

        setJobPortfolioNotice('No valid job rows were found in that CSV. Vision now removes rows that do not resolve to a believable title, company, and summary.');

        return;

      }

      const nextJobs = sanitizeJobPortfolio([...importedJobs, ...jobs], profile.agency || '');

      await setDoc(userDocRef(recruiterUser.uid), {

        jobPortfolio: nextJobs,

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'jobs_csv_imported', `Imported ${importedJobs.length} jobs into the portfolio from CSV.`, { count: importedJobs.length });

      setJobs(nextJobs);

      setJobPortfolioNotice(`Imported ${importedJobs.length} jobs into the portfolio.`);

    } catch (error) {

      console.error('Failed to import jobs CSV', error);

      setJobPortfolioNotice('Failed to import jobs CSV.');

    }

  };



  const handleExportJobAtsCsv = async (job, snapshot) => {

    if (!job || !snapshot) return;
    if (!requireAccess('pipeline.export', 'You need export permission to download ATS candidate data.')) return;

    const rows = [

      ['name', 'role', 'company', 'location', 'email', 'phone', 'stage', 'fit_score', 'last_activity'],

      ...snapshot.matchedCandidates.map((candidate) => [

        candidate.name || '',

        candidate.role || '',

        candidate.company || '',

        candidate.location || '',

        candidate.email || '',

        candidate.phone || '',

        candidate.status || '',

        candidate.rating || 0,

        candidate.lastActivityAt || candidate.submittedAt || '',

      ]),

    ];

    downloadCsv(`vision-${normalizeText(job.title).replace(/\s+/g, '-')}-ats-candidates.csv`, rows);

    if (recruiterUser?.uid) {

      await recordActivity(recruiterUser.uid, 'job_ats_csv_exported', `Exported ${snapshot.matchedCandidates.length} ATS candidates for ${job.title}.`, { jobId: job.id, count: snapshot.matchedCandidates.length });

    }

    setJobPortfolioNotice(`Exported ${snapshot.matchedCandidates.length} ATS candidates for ${job.title}.`);

  };



  const renderPipeline = () => {

    const scheduleItems = String(timeManagement.scheduleItems || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean);

    const reminders = String(timeManagement.reminders || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean);

    const dailyGoals = String(timeManagement.dailyGoals || '').split(/\r?\n/).map((item) => item.trim()).filter(Boolean);



    return (

    <div className="space-y-5">

      <div className="overflow-hidden rounded-shell border border-cyan-400/20 bg-[linear-gradient(135deg,rgba(14,116,144,0.24),rgba(15,23,42,0.94)_42%,rgba(2,6,23,0.98))] p-4 shadow-[0_18px_60px_rgba(8,47,73,0.22)]">

        <div className="grid gap-3 xl:grid-cols-[1.15fr_0.85fr_0.8fr]">

          <div className="rounded-card border border-white/10 bg-slate-950/45 p-4">

            <div className="text-xs font-black uppercase tracking-[0.3em] text-cyan-300">Time Management</div>

            <h1 className="mt-2 text-xl font-black leading-tight text-white sm:text-2xl">Agency execution rhythm.</h1>

            <p className="mt-2 max-w-2xl text-xs leading-5 text-slate-300 sm:text-sm">The owner-defined schedule now sits on the pipeline itself so recruiters can see today's operating cadence, reminders, and priorities before moving candidates.</p>

            <div className="mt-4 flex flex-wrap gap-2">

              <div className="rounded-card border border-white/10 bg-white/5 px-3 py-2.5">

                <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Calendar day</div>

                <div className="mt-1.5 text-sm font-bold text-white">{timeManagement.calendarDate ? new Date(`${timeManagement.calendarDate}T12:00:00`).toLocaleDateString() : 'No date set'}</div>

              </div>

              <div className="rounded-card border border-white/10 bg-white/5 px-3 py-2.5">

                <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Open roles</div>

                <div className="mt-1.5 text-sm font-bold text-white">{jobs.filter((job) => job.status === 'Active').length}</div>

              </div>

            </div>

          </div>

          <div className="rounded-card border border-white/10 bg-slate-950/45 p-4">

            <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-200">Today's Schedule</div>

            <div className="mt-4 space-y-3">

              {scheduleItems.length === 0 && <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No schedule blocks set yet.</div>}

              {scheduleItems.slice(0, 3).map((item, index) => (

                <div key={`${item}-${index}`} className="rounded-card bg-white/5 px-3 py-2 text-xs text-slate-100 sm:text-sm">{item}</div>

              ))}

            </div>

          </div>

          <div className="grid gap-4">

            <div className="rounded-card border border-white/10 bg-slate-950/45 p-4">

              <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-200">Reminders</div>

              <div className="mt-3 space-y-2">

                {reminders.length === 0 && <div className="text-sm text-slate-500">No reminders set yet.</div>}

                {reminders.slice(0, 2).map((item, index) => (

                  <div key={`${item}-${index}`} className="rounded-card bg-white/5 px-3 py-2 text-xs text-slate-200 sm:text-sm">{item}</div>

                ))}

              </div>

            </div>

            <div className="rounded-card border border-cyan-400/15 bg-cyan-500/10 p-4">

              <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-200">Priority Goals</div>

              <div className="mt-3 space-y-2">

                {dailyGoals.length === 0 && <div className="text-sm text-cyan-50/70">No daily goals set yet.</div>}

                {dailyGoals.slice(0, 2).map((item, index) => (

                  <div key={`${item}-${index}`} className="rounded-card bg-slate-950/50 px-3 py-2 text-xs text-cyan-50 sm:text-sm">{item}</div>

                ))}

              </div>

            </div>

          </div>

        </div>

      </div>



      <div className={`rounded-shell border border-white/10 bg-white/5 p-4 sm:p-5 ${isDirectDemoHighlight(1) || (isDirectDemoHighlight(6) && !selectedScreeningCandidateId) ? directDemoHighlightClass : ''}`}>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">

          <div>

            <div className="text-sm font-black uppercase tracking-[0.22em] text-white">Candidate Pipeline</div>

            <div className="mt-1 text-xs text-slate-400">Board keeps the drag-and-drop kanban. Table gives recruiters a spreadsheet-style view with filters, sort, and inline actions.</div>

          </div>

          <div className="flex flex-wrap items-center gap-2">

            <div className="rounded-full bg-white/5 px-3 py-2 text-meta uppercase tracking-[0.16em] text-slate-300">

              {candidates.length} active candidates

            </div>

            {canAccess('pipeline.write') && <button type="button" data-direct-demo-target="import-pipeline-csv-button" onClick={handleImportPipelineCsv} className={`rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white ${isDirectDemoTarget('import-pipeline-csv-button') ? directDemoHighlightClass : ''}`}>Import CSV</button>}

            {canAccess('pipeline.export') && <button type="button" onClick={handleExportPipelineCsv} className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white">Export CSV</button>}

            {canAccess('pipeline.write') && <button type="button" data-direct-demo-target="share-shortlist-button" onClick={() => {
              setShortlistModalOpen(true);
              setShortlistCreatedUrl('');
              setShortlistLinkCopied(false);
              if (isDemoWorkspace) {
                const shortlistCandidate = candidates.find((candidate) => Array.isArray(candidate.submittals) && candidate.submittals.length > 0)
                  || candidates.find((candidate) => candidate.source === 'Vision demo direct sourcing')
                  || candidates[0];
                setShortlistDraft({
                  clientName: shortlistCandidate?.company || getJobCompanyName(directDemoGuideJob || {}) || 'Demo Client',
                  jobTitle: shortlistCandidate?.matchedJobTitle || shortlistCandidate?.role || directDemoGuideJob?.title || '',
                  candidateIds: shortlistCandidate?.id ? [shortlistCandidate.id] : [],
                });
              } else {
                setShortlistDraft({ clientName: '', jobTitle: '', candidateIds: [] });
              }
            }} className={`rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-cyan-200 transition hover:bg-cyan-500/20 ${isDirectDemoTarget('share-shortlist-button') ? directDemoHighlightClass : ''}`}>Share Shortlist</button>}

            <div className="flex items-center gap-1 rounded-full border border-white/10 bg-slate-950/60 p-1">

              {PIPELINE_VIEW_OPTIONS.map((view) => (

                <button key={view} type="button" onClick={() => setPipelineView(view)} className={`rounded-full px-3 py-2 text-micro font-black uppercase tracking-[0.16em] transition ${pipelineView === view ? 'bg-cyan-500 text-slate-950' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}>

                  {view}

                </button>

              ))}

            </div>

          </div>

        </div>

        <div className="mt-4">

          {pipelineView === 'board' ? (

            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">

              {stageBuckets.map(({ stage, items }) => (

                <StageColumn

                  key={stage.id}

                  stage={stage}

                  items={items}

                  onDragOverStage={handleDragOverStage}

                  onDropStage={handleDropStage}

                  onDragStartCard={handleDragStartCard}

                  onDeleteCandidate={canAccess('pipeline.delete') ? handleDeleteCandidate : null}

                  onDeclineOffer={(candidateId) => {
                    setOfferDeclineCandidateId(candidateId);
                    setOfferDeclineCategory('');
                    setOfferDeclineNote('');
                  }}

                  onOpenScreeningCandidate={handleOpenScreeningCandidate}

                  onOpenCandidate={setSelectedCandidateId}

                  onSetInterviewDetails={handleSetInterviewDetails}

                  onSaveInterviewCapture={handleSaveInterviewCapture}

                  onSubmitToClient={(candidateId) => {
                    const cand = candidates.find((c) => c.id === candidateId);
                    setSubmittalCandidateId(candidateId);
                    setSubmittalClientName(cand?.company || '');
                    setSubmittalJobId(cand?.matchedJobId || '');
                    setSubmittalContactId('');
                    setSubmittalContactName('');
                    setSubmittalSubmittedBy(profile?.name || '');
                    setSubmittalOutcome('pending');
                    setSubmittalFeedback('');
                    setSubmittalNote('');
                  }}

                  onSendOffer={(candidateId, existingOffer) => {
                    const cand = candidates.find((candidate) => candidate.id === candidateId);
                    const matchedJob = jobs.find((job) => job.id === cand?.matchedJobId);
                    const latestSubmittal = Array.isArray(cand?.submittals) ? cand.submittals[cand.submittals.length - 1] : null;
                    const matchingContact = contacts.find((contact) => contact.id === latestSubmittal?.contactId)
                      || contacts.find((contact) => normalizeText(contact.email) === normalizeText(latestSubmittal?.contactEmail))
                      || contacts.find((contact) => normalizeText(contact.companyName) === normalizeText(cand?.company) && contact.email);
                      const clientEmail = latestSubmittal?.contactEmail || matchingContact?.email || extractEmailFromText(matchedJob?.clientContact || '') || '';
                    const demoStartDate = new Date(Date.now() + 14 * 24 * 3600 * 1000).toISOString().slice(0, 10);
                    setSendOfferCandidateId(candidateId);
                    setOfferError('');
                    setOfferDraft({
                      amount: existingOffer?.amount ? String(existingOffer.amount) : isDemoWorkspace ? '85000' : '',
                      currency: existingOffer?.currency || 'USD',
                      sentDate: existingOffer?.sentDate || new Date().toISOString().slice(0, 10),
                      expiryDate: existingOffer?.expiryDate || new Date(Date.now() + 5 * 24 * 3600 * 1000).toISOString().slice(0, 10),
                      note: existingOffer?.note || '',
                      clientEmail: clientEmail || (isDemoWorkspace ? 'hiring.manager@example.com' : ''),
                      clientName: latestSubmittal?.contactName || matchingContact?.name || (isDemoWorkspace ? 'Hiring Manager' : ''),
                      clientLegalName: cand?.company || getJobCompanyName(matchedJob || {}) || (isDemoWorkspace ? 'Demo Client' : ''),
                      clientAddress: isDemoWorkspace ? 'Demo client address' : '',
                      startDate: isDemoWorkspace ? demoStartDate : '',
                      workLocation: matchedJob?.location || cand?.location || '',
                      employmentType: matchedJob?.type || 'Permanent',
                      workingHours: isDemoWorkspace ? 'Monday to Friday, 08:00-17:00' : '',
                      payFrequency: 'Monthly',
                      benefits: isDemoWorkspace ? 'Standard company benefits' : '',
                      leaveTerms: isDemoWorkspace ? 'Annual leave according to company policy' : '',
                      overtimeTerms: '',
                      deductions: '',
                      noticeTerms: isDemoWorkspace ? '30 days notice' : '',
                      sectorTerms: '',
                      customNegotiation: existingOffer?.note || '',
                      emailBody: isDemoWorkspace ? 'Please review the attached offer packet for this candidate.' : '',
                    });
                  }}

                  onAcceptOffer={handleAcceptOffer}

                  onLogPlacement={(candidate) => {
                    setPlacementCandidateId(candidate.id);
                    setPlacementDraft({
                      ...DEFAULT_PLACEMENT_DRAFT,
                      offerSalary: String(candidate.offerSalary || ''),
                      feePercent: String(candidate.placementFeePercent || candidate.feePercent || ''),
                      directFee: String(candidate.placementFee || ''),
                      usePercent: Boolean(candidate.placementFeePercent || candidate.feePercent),
                      startDate: String(candidate.startDate || candidate.offerNegotiation?.terms?.startDate || ''),
                      currency: candidate.placementCurrency || 'ZAR',
                      billedBy: profile?.name || '',
                      invoiceDate: new Date().toISOString().slice(0, 10),
                    });
                  }}

                  loggedCandidateIds={new Set(placements.map((p) => p.candidateId).filter(Boolean))}
                  directDemoGuideTargets={{
                    screeningDrop: stage.id === 'screening' && isDirectDemoTarget('pipeline-screening-column'),
                    interviewDrop: stage.id === 'interview' && isDirectDemoTarget('pipeline-interview-column'),
                    offerDrop: stage.id === 'offer' && isDirectDemoTarget('pipeline-offer-column'),
                    screening: isDirectDemoTarget('candidate-screening-button'),
                    sendClient: isDirectDemoTarget('candidate-send-client-button'),
                    placement: isDirectDemoTarget('candidate-offer-placement'),
                  }}

                  onRejectCandidate={(candidateId) => {
                    const cand = candidates.find((c) => c.id === candidateId);
                    setRejectPipelineCandidateId(candidateId);
                    setRejectReason('');
                    setRejectSendEmail(!!(cand?.email));
                    setRejectEmailBody('');
                  }}

                  isActiveDropTarget={dragOverStageId === stage.id}

                  agingThresholdDays={Number(agencySettings.agingThresholdDays || 14)}

                />

              ))}

            </div>

          ) : (

            <PipelineTableView

              candidates={filteredTableCandidates}

              jobs={jobs}

              sortKey={pipelineSortKey}

              filters={pipelineFilters}

              onSortChange={setPipelineSortKey}

              onFilterChange={(field, value) => setPipelineFilters((current) => ({ ...current, [field]: value }))}

              onStageChange={handleTableStageChange}

              onOpenScreeningCandidate={handleOpenScreeningCandidate}

              onDeleteCandidate={canAccess('pipeline.delete') ? handleDeleteCandidate : null}

              onReassignJob={canAccess('pipeline.write') ? handleReassignCandidateJob : null}

              onDeclineOffer={(candidateId) => {
                setOfferDeclineCandidateId(candidateId);
                setOfferDeclineCategory('');
                setOfferDeclineNote('');
              }}

            />

          )}

        </div>

      </div>



      {pipelineNotice && (

        <div className="rounded-card border border-cyan-500/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">

          {pipelineNotice}

        </div>

      )}

      <div className="rounded-shell border border-cyan-400/15 bg-cyan-500/[0.04] p-5 sm:p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Candidate Intelligence Database</div>
            <div className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">
              Semantic search, parsed CV intelligence, match evidence, and recruiter next actions now sit inside the existing pipeline database.
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCandidateDbOpen((value) => !value)}
            className="rounded-full border border-cyan-400/25 bg-cyan-500/10 px-4 py-2.5 text-micro font-black uppercase tracking-[0.16em] text-cyan-100 transition hover:bg-cyan-500/20"
          >
            {candidateDbOpen ? 'Hide database' : 'Open database'}
          </button>
        </div>

        <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
          {candidateSmartCollections.map((collection) => {
            const toneClass = collection.tone === 'rose' ? 'border-rose-400/20 bg-rose-500/10 text-rose-200'
              : collection.tone === 'amber' ? 'border-amber-400/20 bg-amber-500/10 text-amber-200'
                : collection.tone === 'violet' ? 'border-violet-400/20 bg-violet-500/10 text-violet-200'
                  : collection.tone === 'emerald' ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200'
                    : 'border-cyan-400/20 bg-cyan-500/10 text-cyan-200';
            return (
              <button
                key={collection.id}
                type="button"
                onClick={() => {
                  setCandidateDbOpen(true);
                  if (collection.id === 'parsed-no-screening') setCandidateDbSearch('parsed cv no screening');
                  else if (collection.id === 'missing-salary') setCandidateDbSearch('missing salary expectation');
                  else if (collection.id === 'narrative') setCandidateDbSearch('leadership scope quantified ownership');
                  else if (collection.id === 'underused') setCandidateDbSearch('strong unused high fit');
                  else setCandidateDbSearch('');
                }}
                className={`rounded-card border px-4 py-3 text-left transition ${toneClass}`}
              >
                <div className="text-2xl font-black text-white">{collection.count}</div>
                <div className="mt-1 text-micro font-black uppercase tracking-[0.14em]">{collection.label}</div>
                <div className="mt-2 text-micro leading-4 text-slate-400">{collection.description}</div>
              </button>
            );
          })}
        </div>

        {candidateDbOpen && (
          <div className="mt-5 space-y-4">
            <div className="flex flex-wrap gap-2 rounded-full border border-white/10 bg-slate-950/60 p-1">
              {[
                ['talent-bank', 'Talent Bank'],
                ['saved-searches', 'Saved Searches'],
                ['talent-lists', 'Talent Lists'],
                ['duplicates', `Duplicates ${candidateDupGroups.length ? `(${candidateDupGroups.length})` : ''}`],
              ].map(([id, label]) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setCandidateDbSection(id)}
                  className={`rounded-full px-4 py-2 text-micro font-black uppercase tracking-[0.16em] transition ${candidateDbSection === id ? 'bg-cyan-500 text-slate-950' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="grid gap-2 lg:grid-cols-[1fr_auto_auto_auto_auto]">
              <input
                value={candidateDbSearch}
                onChange={(event) => setCandidateDbSearch(event.target.value)}
                placeholder="Semantic search: senior backend engineer with payments, AWS, leadership..."
                className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition placeholder:text-slate-600 focus:border-cyan-400"
              />
              <select value={candidateDbFilters.status} onChange={(event) => setCandidateDbFilters((current) => ({ ...current, status: event.target.value }))} className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-3 text-xs text-white outline-none focus:border-cyan-400">
                <option value="">All stages</option>
                {PIPELINE_STAGES.map((stage) => <option key={stage.id} value={stage.id}>{stage.label}</option>)}
              </select>
              <select value={candidateDbFilters.tag} onChange={(event) => setCandidateDbFilters((current) => ({ ...current, tag: event.target.value }))} className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-3 text-xs text-white outline-none focus:border-cyan-400">
                <option value="">All tags</option>
                {allCandidateTags.map((tag) => <option key={tag}>{tag}</option>)}
              </select>
              <select value={candidateDbSort} onChange={(event) => setCandidateDbSort(event.target.value)} className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-3 text-xs text-white outline-none focus:border-cyan-400">
                <option value="name">Sort: Name</option>
                <option value="recent">Sort: Recent</option>
                <option value="rating">Sort: Fit</option>
                <option value="company">Sort: Company</option>
                <option value="marketability">Sort: Marketability</option>
                <option value="reusable">Sort: Reusable</option>
              </select>
              <button type="button" onClick={() => { setCandidateDbSearch(''); setCandidateDbFilters({ company: '', role: '', status: '', tag: '', source: '' }); }} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.14em] text-slate-300 transition hover:text-white">Clear</button>
            </div>

            {candidateDbSection === 'saved-searches' && (
              <div className="grid gap-3 lg:grid-cols-[1fr_auto]">
                <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                  <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-400">Saved candidate searches</div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {savedCandidateViews.length === 0 && <div className="text-sm text-slate-500">No saved searches yet. Save the current search to create one.</div>}
                    {savedCandidateViews.map((view) => (
                      <button key={view.id} type="button" onClick={() => handleApplyCandidateSavedView(view)} className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.12em] text-cyan-100 transition hover:bg-cyan-500/20">
                        {view.name}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => { setSaveViewTarget('candidates'); setSaveViewName(''); setSaveViewModalOpen(true); }}
                  className="rounded-card border border-white/10 bg-white/5 px-4 py-3 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-cyan-400/30 hover:text-white"
                >
                  Save current search
                </button>
                {saveViewModalOpen && saveViewTarget === 'candidates' && (
                  <div className="lg:col-span-2 flex gap-2">
                    <input autoFocus value={saveViewName} onChange={(event) => setSaveViewName(event.target.value)} placeholder="Search name..." className="flex-1 rounded-card border border-cyan-400/40 bg-slate-950/70 px-4 py-2 text-sm text-white outline-none focus:border-cyan-400" />
                    <button type="button" onClick={() => { handleSaveView('candidates', saveViewName, candidateDbFilters, candidateDbSort, candidateDbSearch); setSaveViewModalOpen(false); setSaveViewName(''); }} className="rounded-full bg-cyan-400 px-4 py-2 text-xs font-black text-slate-950 hover:bg-cyan-300">Save</button>
                    <button type="button" onClick={() => { setSaveViewModalOpen(false); setSaveViewName(''); }} className="rounded-full border border-white/10 px-4 py-2 text-xs font-black text-slate-400 hover:text-white">Cancel</button>
                  </div>
                )}
              </div>
            )}

            {candidateDbSection === 'talent-lists' && (
              <div className="grid gap-4 xl:grid-cols-[0.9fr_1.1fr]">
                <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                  <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-400">Create reusable talent list</div>
                  <div className="mt-3 grid gap-2">
                    <input value={talentListDraft.name} onChange={(event) => setTalentListDraft((current) => ({ ...current, name: event.target.value }))} placeholder="Cape Town Senior Engineers" className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400" />
                    <input value={talentListDraft.description} onChange={(event) => setTalentListDraft((current) => ({ ...current, description: event.target.value }))} placeholder="Reusable shortlist, market map, or talent pool" className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400" />
                    <button type="button" onClick={handleCreateTalentList} className="rounded-full bg-cyan-500 px-4 py-3 text-micro font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-400">Create list</button>
                  </div>
                </div>
                <div className="grid gap-2">
                  {talentLists.length === 0 && <div className="rounded-card border border-dashed border-white/10 p-5 text-sm text-slate-500">No talent lists yet.</div>}
                  {talentLists.map((list) => (
                    <button key={list.id} type="button" onClick={() => setSelectedTalentListId(list.id)} className={`rounded-card border p-4 text-left transition ${selectedTalentListId === list.id ? 'border-cyan-400/40 bg-cyan-500/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="font-black text-white">{list.name}</div>
                        <div className="rounded-full border border-white/10 bg-slate-950/60 px-3 py-1 text-micro text-slate-300">{(list.candidateIds || []).length} candidates</div>
                      </div>
                      {list.description && <div className="mt-1 text-xs text-slate-400">{list.description}</div>}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {candidateDbSection === 'duplicates' && (
              <div className="rounded-card border border-amber-400/15 bg-amber-500/5 p-4">
                <div className="text-meta font-black uppercase tracking-[0.18em] text-amber-200">Candidate duplicate handling</div>
                <div className="mt-2 text-sm text-slate-300">{candidateDupGroups.length} suggested duplicate pair{candidateDupGroups.length === 1 ? '' : 's'} based on email, phone, name, company, and profile similarity.</div>
                <div className="mt-3 grid gap-2">
                  {candidateDupGroups.slice(0, 4).map((group) => (
                    <div key={group.key} className="flex flex-col gap-3 rounded-card border border-white/10 bg-slate-950/60 p-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-sm text-slate-300">
                        <span className="font-bold text-white">{group.primary.name || group.primary.email}</span>
                        <span className="text-slate-500"> may duplicate </span>
                        <span className="font-bold text-white">{group.secondary.name || group.secondary.email}</span>
                        <span className="ml-2 text-micro uppercase tracking-[0.14em] text-amber-300">{Math.round(group.score)}%</span>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setDedupMergeModal({ primary: group.primary, secondary: group.secondary, entity: 'candidates', score: group.score })} className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-cyan-100">Review merge</button>
                        <button type="button" onClick={() => handleDedupDismiss(group.key)} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-300">Dismiss</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid gap-3 xl:grid-cols-2">
              {filteredCandidateDb.length === 0 && (
                <div className="xl:col-span-2 rounded-card border border-dashed border-white/10 px-4 py-6 text-sm text-slate-500">No candidates match the current semantic search.</div>
              )}
              {filteredCandidateDb.slice(0, 12).map((candidate) => {
                const intel = candidate.intelligenceProfile || candidateIntelligenceIndex[candidate.id] || buildCandidateIntelligenceProfile(candidate, screeningResponses[candidate.id]);
                const score = candidateDbSearch ? candidate.semanticSearchScore : Number(candidate.rating || 0);
                const scoreClass = score >= 75 ? 'text-emerald-300 bg-emerald-500/10 border-emerald-400/20' : score >= 55 ? 'text-amber-300 bg-amber-500/10 border-amber-400/20' : 'text-slate-300 bg-white/5 border-white/10';
                return (
                  <div key={candidate.id} className="rounded-shell border border-white/10 bg-slate-950/60 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <button type="button" onClick={() => setSelectedCandidateId(candidate.id)} className="truncate text-left text-sm font-black text-white hover:text-cyan-200">{candidate.name || 'Candidate'}</button>
                        <div className="mt-1 truncate text-xs text-slate-400">{candidate.role || candidate.matchedJobTitle || 'Unknown role'}{candidate.company ? ` · ${candidate.company}` : ''}</div>
                        <div className="mt-1 text-micro text-slate-500">{intel.seniority} · {intel.yearsExperience || 'n/a'} yrs · Parse {intel.parseabilityScore}/100 · Owner {candidate.commercialProfile?.ownerName || 'Unassigned'}</div>
                      </div>
                      <div className={`shrink-0 rounded-full border px-3 py-1 text-micro font-black uppercase tracking-[0.12em] ${scoreClass}`}>{Math.round(score || 0)}</div>
                    </div>
                    <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                      {[
                        ['Marketability', candidate.commercialProfile?.marketabilityScore || 0],
                        ['Reusable', candidate.commercialProfile?.reusableScore || 0],
                        ['Submissions', candidate.commercialProfile?.lifetimeSubmissions || 0],
                        ['Revenue', formatMoney(candidate.commercialProfile?.lifetimeRevenue || 0, candidate.placementCurrency || 'ZAR')],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-2">
                          <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">{label}</div>
                          <div className="mt-1 text-sm font-black text-white">{value}</div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {intel.skills.slice(0, 6).map((skill) => (
                        <span key={skill} className="rounded-full border border-cyan-400/15 bg-cyan-500/10 px-2 py-0.5 text-micro font-bold text-cyan-200">{skill}</span>
                      ))}
                      <span className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-micro font-bold text-slate-300">{intel.scope.label}</span>
                    </div>
                    {candidate.semanticEvidence?.length > 0 && (
                      <div className="mt-3 rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-2 text-meta leading-5 text-slate-300">
                        {candidate.semanticEvidence[0]}
                      </div>
                    )}
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="text-micro text-slate-500">{intel.scope.needs[0] || 'Profile is recruiter-ready.'}</div>
                      <div className="flex flex-wrap gap-2">
                        <select
                          value={candidateOwnerDrafts[candidate.id] ?? (candidate.ownerRecruiterId || candidate.recruiterId || '')}
                          onChange={(event) => setCandidateOwnerDrafts((current) => ({ ...current, [candidate.id]: event.target.value }))}
                          className="rounded-full border border-white/10 bg-slate-950 px-3 py-1.5 text-micro font-bold text-slate-300 outline-none"
                        >
                          <option value="">Owner</option>
                          {agencyMembers.map((member) => <option key={member.id} value={member.id}>{member.name || member.email || member.id}</option>)}
                        </select>
                        <button type="button" onClick={() => handleAssignCandidateOwner(candidate.id, candidateOwnerDrafts[candidate.id] || candidate.ownerRecruiterId || candidate.recruiterId || recruiterUser?.uid)} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-300 transition hover:text-white">Set owner</button>
                        {selectedTalentListId && <button type="button" onClick={() => handleAddCandidateToTalentList(candidate.id, selectedTalentListId)} className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-emerald-200">Add to list</button>}
                        <button type="button" onClick={() => setSelectedCandidateId(candidate.id)} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-300 transition hover:text-white">Open</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>



      <div className="rounded-shell border border-white/10 bg-white/5 p-5">

        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">

          <div>

            <div className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.22em] text-white">

              <Upload size={16} className="text-cyan-300" /> Agency Candidate Intake

            </div>

            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-300">

              Deposit candidate CVs in bulk beneath the live pipeline. Intake is now strict: the CV must explicitly contain one active company name and one exact vacancy title from the job portfolio, otherwise it is rejected and not admitted to recruiter storage.

            </p>

          </div>

          <label data-direct-demo-target="bulk-cv-upload-button" onClick={(event) => { if (isDemoWorkspace && importDemoWalkthroughOpen) { event.preventDefault(); handleBulkCvUpload({ target: { files: [], value: '' } }); } }} className={`inline-flex cursor-pointer items-center gap-2 rounded-full bg-cyan-500 px-4 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-400 ${isDirectDemoTarget('bulk-cv-upload-button') ? directDemoHighlightClass : ''}`}>

            <Upload size={14} />

            {isImporting ? 'Analyzing...' : 'Upload CV PDFs'}

            <input type="file" accept="application/pdf" multiple className="hidden" onChange={handleBulkCvUpload} disabled={isImporting} />

          </label>

        </div>



        <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

          <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

            <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Accepted This Session</div>

            <div className="mt-2 text-2xl font-black text-white">{intakeAnalytics.accepted}</div>

            <div className="mt-2 text-xs leading-5 text-slate-400">Candidates admitted into CRM-tracked recruiter storage.</div>

          </div>

          <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

            <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Rejected Or Deleted</div>

            <div className="mt-2 text-2xl font-black text-white">{intakeAnalytics.deleted}</div>

            <div className="mt-2 text-xs leading-5 text-slate-400">Strict vacancy-validation and fit-scoring rejections.</div>

          </div>

          <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

            <div className="text-meta uppercase tracking-[0.18em] text-slate-500">CRM Tracked Candidates</div>

            <div className="mt-2 text-2xl font-black text-white">{intakeAnalytics.crmTrackedCount}</div>

            <div className="mt-2 text-xs leading-5 text-slate-400">Candidates with intake telemetry, timelines, and automation markers.</div>

          </div>

          <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

            <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Automation Ready</div>

            <div className="mt-2 text-2xl font-black text-white">{intakeAnalytics.automationReadyCount}</div>

            <div className="mt-2 text-xs leading-5 text-slate-400">Vacancy-validated candidates with duplicate checks and live screening links.</div>

          </div>

        </div>



        <div className="mt-4 rounded-card border border-amber-400/20 bg-amber-500/10 p-4">

          <div className="flex flex-wrap items-center justify-between gap-3">

            <div>

              <div className="text-meta font-black uppercase tracking-[0.18em] text-amber-200">CV Waiting For Approval</div>

              <div className="mt-2 text-sm leading-6 text-amber-50/90">Partial-fit public applications waiting for recruiter approval before pipeline admission.</div>

            </div>

            <div className="rounded-full bg-slate-950/50 px-3 py-2 text-xs font-black uppercase tracking-[0.16em] text-amber-100">{candidateReviewQueue.length} awaiting review</div>

          </div>

          {candidateReviewQueue.length === 0 ? (

            <div className="mt-4 rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-300">No CVs are currently waiting for approval. Partial-fit applications from the public jobs link will appear here.</div>

          ) : (

            <div className="mt-4 grid gap-4 lg:grid-cols-2 xl:grid-cols-3">

              {candidateReviewQueue.map((item) => {
                const fit = item.aiFitAnalysis || {};
                const overallScore = fit.score || item.rating || 0;
                const pillars = Array.isArray(fit.pillars) ? fit.pillars : [];
                const trajectory = fit.trajectory || null;
                const matchMatrix = fit.matchMatrix || null;
                const scorecard = fit.scorecard || fit.summary || item.intakeDecisionReason || '';
                const nonNeg = pillars.filter((p) => p.type === 'non_negotiable');
                const niceToHave = pillars.filter((p) => p.type === 'nice_to_have');
                const scoreColor = overallScore >= 72 ? 'text-emerald-300 bg-emerald-500/10' : overallScore >= 50 ? 'text-amber-300 bg-amber-500/10' : 'text-red-300 bg-red-500/10';

                return (
                <div key={item.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4 flex flex-col gap-0">

                  {/* Header */}
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-bold text-white truncate">{item.name}</div>
                      <div className="mt-1 text-xs text-slate-400 truncate">{item.matchedJobTitle} {item.company ? `· ${item.company}` : ''}</div>
                      <div className="mt-1 text-micro text-slate-500">{item.email || ''}{item.location ? ` · ${item.location}` : ''}</div>
                    </div>
                    <div className={`shrink-0 rounded-full px-3 py-1 text-meta font-black uppercase tracking-[0.14em] ${scoreColor}`}>
                      {overallScore}
                      {matchMatrix && <span className="ml-1 text-micro font-normal opacity-60">/ 100</span>}
                    </div>
                  </div>

                  {/* Match Matrix summary */}
                  {matchMatrix && (
                    <div className="mt-3 grid grid-cols-2 gap-2">
                      <div className="rounded-ctl border border-white/8 bg-white/4 px-3 py-2">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Non-Negotiable</div>
                        <div className={`mt-1 text-sm font-black ${matchMatrix.nonNegotiableScore >= 70 ? 'text-emerald-300' : matchMatrix.nonNegotiableScore >= 50 ? 'text-amber-300' : 'text-red-300'}`}>{matchMatrix.nonNegotiableScore}<span className="text-micro font-normal text-slate-500">/100</span></div>
                      </div>
                      <div className="rounded-ctl border border-white/8 bg-white/4 px-3 py-2">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Nice-to-Have</div>
                        <div className={`mt-1 text-sm font-black ${matchMatrix.niceToHaveScore >= 70 ? 'text-cyan-300' : 'text-slate-400'}`}>{matchMatrix.niceToHaveScore}<span className="text-micro font-normal text-slate-500">/100</span></div>
                      </div>
                    </div>
                  )}

                  {/* Recruiter Scorecard narrative */}
                  {scorecard && (
                    <div className="mt-3 rounded-card border border-violet-400/15 bg-violet-500/8 px-4 py-3">
                      <div className="text-micro font-black uppercase tracking-[0.18em] text-violet-400 mb-1">Recruiter Assessment</div>
                      <p className="text-xs leading-5 text-slate-200 italic">{scorecard}</p>
                    </div>
                  )}

                  {/* Pillar breakdown */}
                  {pillars.length > 0 && (
                    <div className="mt-3 space-y-1.5">
                      <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">Match Matrix</div>
                      {nonNeg.map((p, i) => (
                        <div key={`nn-${i}`} className="rounded-ctl border border-white/8 bg-white/3 px-3 py-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-micro font-bold text-white truncate">{p.name}</div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span className="text-micro font-black uppercase tracking-wide text-rose-400/70">Required</span>
                              <span className={`text-meta font-black ${p.score >= 70 ? 'text-emerald-300' : p.score >= 50 ? 'text-amber-300' : 'text-red-300'}`}>{p.score}</span>
                            </div>
                          </div>
                          {p.verdict && <div className="mt-1 text-micro leading-4 text-slate-400">{p.verdict}</div>}
                          {p.evidence && <div className="mt-1 text-micro leading-4 text-slate-500 italic">&ldquo;{p.evidence.slice(0, 120)}{p.evidence.length > 120 ? '…' : ''}&rdquo;</div>}
                        </div>
                      ))}
                      {niceToHave.map((p, i) => (
                        <div key={`nth-${i}`} className="rounded-ctl border border-white/5 bg-white/2 px-3 py-2 opacity-80">
                          <div className="flex items-center justify-between gap-2">
                            <div className="text-micro font-bold text-slate-300 truncate">{p.name}</div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span className="text-micro font-black uppercase tracking-wide text-slate-600">Optional</span>
                              <span className={`text-meta font-black ${p.score >= 70 ? 'text-cyan-300' : 'text-slate-500'}`}>{p.score}</span>
                            </div>
                          </div>
                          {p.verdict && <div className="mt-1 text-micro leading-4 text-slate-500">{p.verdict}</div>}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Trajectory */}
                  {trajectory && (
                    <div className="mt-3 rounded-ctl border border-white/8 bg-white/3 px-3 py-2">
                      <div className="flex items-center gap-2">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Trajectory</div>
                        <div className={`text-micro font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${
                          trajectory.direction === 'ascending' ? 'bg-emerald-500/15 text-emerald-300' :
                          trajectory.direction === 'pivoting' ? 'bg-amber-500/15 text-amber-300' :
                          trajectory.direction === 'declining' ? 'bg-red-500/15 text-red-300' :
                          'bg-slate-500/15 text-slate-400'
                        }`}>{trajectory.direction}</div>
                      </div>
                      {trajectory.relevance && <div className="mt-1 text-micro leading-4 text-slate-400">{trajectory.relevance}</div>}
                    </div>
                  )}

                  {/* Strengths / Concerns — fallback for non-LLM results */}
                  {!scorecard && Array.isArray(fit.concerns) && fit.concerns.length > 0 && (
                    <div className="mt-3 text-xs leading-5 text-slate-400">Concerns: {fit.concerns.slice(0, 2).join(' ')}</div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-4 flex flex-wrap gap-2">
                    {item.resumeUrl && (
                    <button type="button" onClick={() => handleOpenCandidateResume(item)} className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-cyan-200 transition hover:border-cyan-300/40 hover:text-cyan-100">Open CV PDF</button>
                    )}
                    <button type="button" onClick={() => handleAcceptReviewedCandidate(item.id)} className="rounded-full bg-emerald-500 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-emerald-400">Accept into pipeline</button>
                    <button type="button" onClick={() => handleRejectReviewedCandidate(item.id)} className="rounded-full border border-red-400/20 bg-red-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-red-200 transition hover:border-red-300/40 hover:text-red-100">Reject</button>
                  </div>

                </div>
                );
              })}

            </div>

          )}

        </div>



        <div className="mt-4 grid gap-4 xl:grid-cols-[1.1fr_1.1fr_0.8fr]">

          <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

            <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-400">CRM Intake Queue</div>

            <div className="mt-2 text-xs leading-5 text-slate-500">

              Highest-confidence candidates accepted into the recruiter CRM. Each card now carries batch tracking, timeline activity, and automation state behind the scenes.

            </div>

            <div className="mt-3 space-y-3">

              {intakeAnalytics.recentTrackedCandidates.length === 0 && (

                <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-xs text-slate-500">

                  Upload a CV batch to start the intake queue and CRM telemetry.

                </div>

              )}

              {intakeAnalytics.recentTrackedCandidates.map((candidate) => (

                <div key={candidate.id} className="rounded-card border border-white/10 bg-white/5 p-4">

                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">

                    <div>

                      <div className="text-sm font-bold text-white">{candidate.name}</div>

                      <div className="mt-1 text-xs text-slate-400">{candidate.role} · {candidate.company}</div>

                    </div>

                    <div className="rounded-full bg-emerald-500/10 px-3 py-1 text-micro font-bold uppercase tracking-[0.16em] text-emerald-300">

                      Fit {candidate.rating}

                    </div>

                  </div>

                  <div className="mt-2 flex flex-wrap gap-2 text-micro uppercase tracking-[0.16em]">

                    <span className="rounded-full bg-cyan-500/10 px-2 py-1 text-cyan-200">{candidate.crmStatus || 'tracked'}</span>

                    <span className="rounded-full bg-white/5 px-2 py-1 text-slate-300">Batch {candidate.intakeBatchId || 'seeded'}</span>

                    <span className="rounded-full bg-white/5 px-2 py-1 text-slate-300">Screening {candidate.screeningLinkId ? 'ready' : 'pending'}</span>

                  </div>

                  <div className="mt-3 text-meta text-slate-400">Last activity: {candidate.lastActivityAt ? new Date(candidate.lastActivityAt).toLocaleString() : 'Awaiting activity'}</div>

                </div>

              ))}

            </div>

          </div>



          <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

            <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-400">Latest Intake Decisions</div>

            <div className="mt-3 space-y-3">

              {importSummary.length === 0 && (

                <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-xs text-slate-500">

                  Upload a batch of PDF CVs to see Vision intake decisions and AI-fit scores here.

                </div>

              )}

              {importSummary.slice(0, 8).map((item, index) => (

                <div key={`${item.fileName}-${index}`} className="rounded-card border border-white/10 bg-white/5 p-4">

                  <div className="flex items-center justify-between gap-3">

                    <div className="truncate text-sm font-bold text-white">{item.fileName}</div>

                    <div className={`rounded-full px-2 py-1 text-micro font-bold uppercase tracking-[0.16em] ${item.action === 'accepted' ? 'bg-emerald-500/10 text-emerald-300' : item.action === 'review' ? 'bg-cyan-500/10 text-cyan-300' : item.action === 'deleted' ? 'bg-amber-500/10 text-amber-300' : 'bg-red-500/10 text-red-300'}`}>

                      {item.action}

                    </div>

                  </div>

                  {(item.candidateName || item.role || item.company) && (

                    <div className="mt-2 text-meta text-cyan-300">

                      {[item.candidateName, item.role, item.company].filter(Boolean).join(' · ')}

                    </div>

                  )}

                  <div className="mt-2 flex flex-wrap gap-2">

                    {item.automation && (

                      <div className="inline-flex rounded-full bg-white/5 px-2 py-1 text-micro uppercase tracking-[0.16em] text-slate-300">

                        {item.automation}

                      </div>

                    )}

                    {Number.isFinite(Number(item.aiScore)) && Number(item.aiScore) > 0 && (

                      <div className="inline-flex rounded-full bg-cyan-500/10 px-2 py-1 text-micro font-black uppercase tracking-[0.16em] text-cyan-200">

                        AI score {Math.round(Number(item.aiScore))}

                      </div>

                    )}

                  </div>

                  <div className="mt-2 text-xs leading-5 text-slate-400">{item.reason}</div>

                </div>

              ))}

            </div>

          </div>



          <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

            <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-400">Automation Layer</div>

            <div className="mt-3 space-y-3 text-sm text-slate-300">

              <div className="rounded-card bg-white/5 p-4">

                <div className="font-bold text-white">Exact vacancy validation</div>

                <div className="mt-1 text-xs leading-5 text-slate-400">Rejects CVs unless they explicitly name one active company and one exact active role.</div>

              </div>

              <div className="rounded-card bg-white/5 p-4">

                <div className="font-bold text-white">Duplicate enrichment</div>

                <div className="mt-1 text-xs leading-5 text-slate-400">Re-uploads refresh the CRM record with parsed fields instead of creating noisy duplicates.</div>

              </div>

              <div className="rounded-card bg-white/5 p-4">

                <div className="font-bold text-white">Live recruiter timeline</div>

                <div className="mt-1 text-xs leading-5 text-slate-400">Stage moves, screening opens, and screening submissions are appended to each candidate lifecycle.</div>

              </div>

              <div className="rounded-card bg-cyan-500/10 p-4">

                <div className="text-meta uppercase tracking-[0.18em] text-cyan-200">Screening-ready candidates</div>

                <div className="mt-2 text-2xl font-black text-white">{intakeAnalytics.screeningReadyCount}</div>

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>

  );

  };



  const renderJobs = () => {

    const activePortfolioRoles = jobs.filter((job) => !['closed', 'removed'].includes(String(job.status || '').toLowerCase())).length;

    const visibleActiveRoles = visibleJobs.filter((job) => !['closed', 'removed'].includes(String(job.status || '').toLowerCase())).length;

    const activeFiltersCount = [

      jobPortfolioFilters.search,

      jobPortfolioFilters.status,

      jobPortfolioFilters.type,

      jobPortfolioFilters.company,

      jobPortfolioFilters.sort !== 'newest' ? jobPortfolioFilters.sort : '',

    ].filter(Boolean).length;

    const websiteLeadTeamSizeOptions = Array.from(new Set(websiteLeads.map((lead) => String(lead?.teamSize || '').trim()).filter(Boolean)));
    const clientCrmQueue = clientPortfolio.filter((client) => ['Needs attention', 'At risk', 'Critical'].includes(client.riskLevel)).slice(0, 6);
    const clientCrmWeightedPipeline = clientCrmOpportunities.reduce((sum, opportunity) => sum + Number(opportunity.weightedValue || 0), 0);
    const clientCrmOpenOpportunities = clientCrmOpportunities.filter((opportunity) => !['Closed-Won', 'Closed-Lost'].includes(opportunity.stage)).length;
    const clientCrmRevenue = clientPortfolio.reduce((sum, client) => sum + Number(client.lifetimeRevenue || 0), 0);
    const clientCrmPlacementCount = clientPortfolio.reduce((sum, client) => sum + Number(client.placementCount || 0), 0);
    const clientCrmCriticalCount = clientPortfolio.filter((client) => ['At risk', 'Critical'].includes(client.riskLevel)).length;
    const clientCrmForecastCoverage = clientPortfolio.filter((client) => Number(client.forecastValue || 0) > 0).length;
    const topGraphCompanies = organizationalGraph.companies.slice(0, 4);

    const filteredWebsiteLeads = websiteLeads.filter((lead) => {
      const searchNeedle = websiteLeadFilters.search.trim().toLowerCase();
      const matchesSearch = !searchNeedle || [lead.fullName, lead.workEmail, lead.agencyName, lead.monthlyVolume, lead.message]
        .map((value) => String(value || '').toLowerCase())
        .some((value) => value.includes(searchNeedle));
      const matchesTeamSize = !websiteLeadFilters.teamSize || String(lead.teamSize || '') === websiteLeadFilters.teamSize;
      return matchesSearch && matchesTeamSize;
    });



    return (

      <div className="space-y-6">

        <DailyTaskQueue
          tasks={crmTasks.filter((t) => t.status !== 'done')}
          onComplete={handleCompleteCrmTask}
          onSnooze={handleSnoozeCrmTask}
        />

        <div className={`rounded-shell border border-white/10 bg-white/5 p-6 sm:p-7 ${isDirectDemoHighlight(2) ? directDemoHighlightClass : ''}`}>

          <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">

            <div>

              <div className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.22em] text-white">

                <Search size={16} className="text-cyan-300" /> Job Portfolio

              </div>

              <p className="mt-3 max-w-3xl text-sm leading-7 text-slate-300">

                Add new roles manually, attach Boolean sourcing logic, and scan your recruiter-owned candidate base instantly. Vision now treats the Jobs tab like a sourcing command center, not a static vacancy list.

              </p>

            </div>

            <div className="grid gap-3 sm:grid-cols-3 xl:grid-cols-1">

              <div className="rounded-shell border border-white/10 bg-slate-950/60 px-5 py-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Portfolio roles</div>

                <div className="mt-2 text-2xl font-black text-white">{jobs.length}</div>

              </div>

              <div className="rounded-shell border border-white/10 bg-slate-950/60 px-5 py-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Open roles</div>

                <div className="mt-2 text-2xl font-black text-white">{activePortfolioRoles}</div>

              </div>

              <div className="rounded-shell border border-white/10 bg-slate-950/60 px-5 py-4">

                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-500">Tracked companies</div>

                <div className="mt-2 text-2xl font-black text-white">{jobPortfolioCompanies.length}</div>

              </div>

            </div>

          </div>

        </div>



        <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">

          <div className={`rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6 ${isDirectDemoHighlight(3) ? directDemoHighlightClass : ''}`}>

          <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Add Job Offer</div>
          <div className="mt-2 text-xs text-slate-400"><span className="font-black text-rose-300">*</span> Required fields must be completed before Vision creates the job.</div>
          {Object.keys(jobFormErrors).length > 0 && (
            <div className="mt-4 rounded-card border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm leading-6 text-rose-100">
              Complete the highlighted required fields before adding the job: {Object.values(jobFormErrors).map((message) => message.replace(' is required.', '')).join(', ')}.
            </div>
          )}

          <form className="mt-5 grid gap-4 sm:grid-cols-2" onSubmit={handleAddJobOffer} noValidate>

            <label className="block sm:col-span-2">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Role title <span className="text-rose-300">*</span></div>

              <input name="title" data-job-field="title" data-direct-demo-target="job-title-input" aria-invalid={Boolean(jobFormErrors.title)} value={jobForm.title} onChange={handleJobFormChange} className={`w-full rounded-card border ${jobFormErrors.title ? 'border-rose-400/70 bg-rose-950/20' : 'border-white/10 bg-slate-950/70'} px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400 ${isDirectDemoTarget('job-title-input') ? directDemoHighlightClass : ''}`} placeholder="Senior Recruiter" required />
              {jobFormErrors.title && <div className="mt-2 text-xs font-semibold text-rose-300">{jobFormErrors.title}</div>}

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Company <span className="text-rose-300">*</span></div>

              <input name="companyName" data-job-field="companyName" aria-invalid={Boolean(jobFormErrors.companyName)} value={jobForm.companyName} onChange={handleJobFormChange} className={`w-full rounded-card border ${jobFormErrors.companyName ? 'border-rose-400/70 bg-rose-950/20' : 'border-white/10 bg-slate-950/70'} px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400`} placeholder={profile.agency || 'Client'} />
              {jobFormErrors.companyName && <div className="mt-2 text-xs font-semibold text-rose-300">{jobFormErrors.companyName}</div>}

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Department</div>

              <input name="department" value={jobForm.department} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Engineering" />

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Type</div>

              <select name="type" value={jobForm.type} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                <option>Full-time</option>

                <option>Contract</option>

                <option>Part-time</option>

              </select>

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Status</div>

              <select name="status" value={jobForm.status} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                <option>Active</option>

                <option>Paused</option>

                <option>Closed</option>

              </select>

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Location <span className="text-rose-300">*</span></div>

              <input name="location" data-job-field="location" aria-invalid={Boolean(jobFormErrors.location)} value={jobForm.location} onChange={handleJobFormChange} className={`w-full rounded-card border ${jobFormErrors.location ? 'border-rose-400/70 bg-rose-950/20' : 'border-white/10 bg-slate-950/70'} px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400`} placeholder="Johannesburg or Remote" />
              {jobFormErrors.location && <div className="mt-2 text-xs font-semibold text-rose-300">{jobFormErrors.location}</div>}

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Salary band</div>

              <input name="salary" value={jobForm.salary} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="R700k - R900k" />

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Recruiter owner <span className="text-rose-300">*</span></div>

              <input name="recruiter" data-job-field="recruiter" aria-invalid={Boolean(jobFormErrors.recruiter)} value={jobForm.recruiter} onChange={handleJobFormChange} className={`w-full rounded-card border ${jobFormErrors.recruiter ? 'border-rose-400/70 bg-rose-950/20' : 'border-white/10 bg-slate-950/70'} px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400`} placeholder={profile.name || 'Lead Recruiter'} />
              {jobFormErrors.recruiter && <div className="mt-2 text-xs font-semibold text-rose-300">{jobFormErrors.recruiter}</div>}

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Assigned to (owner)</div>

              <input name="ownerName" value={jobForm.ownerName} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder={profile.name || 'Recruiter name'} />

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Hiring contact</div>

              <input name="clientContact" value={jobForm.clientContact} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Hiring manager or HR contact" />

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Relationship status</div>

              <select name="relationshipStatus" value={jobForm.relationshipStatus} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                <option>Active client</option>

                <option>Warm client</option>

                <option>New client</option>

                <option>Dormant client</option>

              </select>

            </label>

            <label className="block sm:col-span-2">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Last outreach</div>

              <input name="lastOutreach" type="datetime-local" value={jobForm.lastOutreach} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Lifecycle stage</div>

              <select name="lifecycleStage" value={jobForm.lifecycleStage} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                {JOB_LIFECYCLE_STAGES.map((s) => <option key={s.id} value={s.id}>{s.label} — {s.desc}</option>)}

              </select>

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Target fill date</div>

              <input name="targetFillDate" type="date" value={jobForm.targetFillDate} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" />

            </label>

            <label className="block">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Placement fee</div>

              <input name="fee" value={jobForm.fee} onChange={handleJobFormChange} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="e.g. 15% or R120,000" />

            </label>

            <label className="block sm:col-span-2">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Summary</div>

              <textarea name="summary" value={jobForm.summary} onChange={handleJobFormChange} rows={3} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Optional. Explain what this role owns and why it matters." />

            </label>

            <label className="block sm:col-span-2">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Requirements <span className="text-rose-300">*</span></div>

              <textarea name="requirements" data-job-field="requirements" aria-invalid={Boolean(jobFormErrors.requirements)} value={jobForm.requirements} onChange={handleJobFormChange} rows={3} className={`w-full rounded-card border ${jobFormErrors.requirements ? 'border-rose-400/70 bg-rose-950/20' : 'border-white/10 bg-slate-950/70'} px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400`} placeholder="Required. Add must-have skills, stack, domain experience, and screening criteria." />
              {jobFormErrors.requirements && <div className="mt-2 text-xs font-semibold text-rose-300">{jobFormErrors.requirements}</div>}

            </label>

            <label className="block sm:col-span-2">

              <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Boolean sourcing query</div>

              <textarea name="booleanQuery" value={jobForm.booleanQuery} onChange={handleJobFormChange} rows={3} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Leave blank and Vision will generate one from the role and requirements." />

            </label>

            {/* ── Screening Questions ── */}
            <div className="sm:col-span-2 rounded-card border border-violet-500/20 bg-violet-500/5 p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs font-black uppercase tracking-[0.2em] text-violet-300">Screening Questions</div>
                <button
                  type="button"
                  disabled={isSuggestingQuestions || !jobForm.title}
                  onClick={async () => {
                    setIsSuggestingQuestions(true);
                    try {
                      const suggest = httpsCallable(functionsClient, 'suggestScreeningQuestions');
                      const res = await suggest({ jobTitle: jobForm.title, requirements: jobForm.requirements || '', companyName: jobForm.companyName || profile.agency, count: 5 });
                      const qs = res.data?.questions || [];
                      if (qs.length) setJobForm((f) => ({ ...f, customScreeningQuestions: qs }));
                    } catch (e) { console.warn('suggestScreeningQuestions', e); }
                    finally { setIsSuggestingQuestions(false); }
                  }}
                  className="rounded-full border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs font-bold text-violet-300 transition hover:bg-violet-500/20 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSuggestingQuestions ? 'Suggesting...' : 'AI Suggest'}
                </button>
              </div>
              <div className="space-y-2">
                {(jobForm.customScreeningQuestions.length ? jobForm.customScreeningQuestions : ['']).map((q, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={q}
                      onChange={(e) => {
                        const updated = [...(jobForm.customScreeningQuestions.length ? jobForm.customScreeningQuestions : [''])];
                        updated[i] = e.target.value;
                        setJobForm((f) => ({ ...f, customScreeningQuestions: updated }));
                      }}
                      placeholder={`Question ${i + 1}`}
                      className="flex-1 rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-400 focus:outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setJobForm((f) => ({ ...f, customScreeningQuestions: (f.customScreeningQuestions.length ? f.customScreeningQuestions : ['']).filter((_, j) => j !== i) }))}
                      className="rounded-full p-1.5 text-slate-500 transition hover:text-red-400"
                      title="Remove"
                    >✕</button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={() => setJobForm((f) => ({ ...f, customScreeningQuestions: [...(f.customScreeningQuestions || []), ''] }))}
                className="mt-2 text-xs font-semibold text-violet-400 hover:text-violet-300 transition"
              >+ Add question</button>
              <div className="mt-2 text-xs text-slate-600">Leave blank to use the 4 default screening questions.</div>
            </div>

            <div className="sm:col-span-2 flex flex-wrap items-center gap-3">

              <button type="button" onClick={handleAddJobOffer} disabled={isSavingJob} className={`rounded-full bg-cyan-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-70 ${isDirectDemoTarget('job-title-input') ? directDemoHighlightClass : ''}`}>

                {isSavingJob ? 'Adding job...' : 'Add job offer'}

              </button>

              <button type="button" onClick={handleCopyClientJobFormLink} className="rounded-full border border-white/10 bg-white/5 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-200 transition hover:border-white/20 hover:text-white">Copy client vacancy link</button>

              {jobPortfolioNotice && <div className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-xs uppercase tracking-[0.16em] text-cyan-200">{jobPortfolioNotice}</div>}

            </div>

          </form>

        </div>



          <div className="rounded-shell border border-cyan-400/20 bg-gradient-to-br from-cyan-500/10 to-slate-950/80 p-8 sm:p-10">

            <div className="text-meta font-black uppercase tracking-[0.28em] text-cyan-400">Chrome Extension</div>

            <h2 className="mt-3 text-3xl font-black leading-tight tracking-tight text-white sm:text-4xl">
              Scrape LinkedIn Jobs<br className="hidden sm:block" /> straight into Vision.
            </h2>

            <p className="mt-4 max-w-xl text-base leading-7 text-slate-400">
              Install the <span className="font-bold text-cyan-300">Vision LinkedIn Scraper</span> Chrome extension. Open any LinkedIn Jobs search, click capture, and Vision imports the listings automatically — no copy-paste required.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">

              <a
                href="https://chromewebstore.google.com/detail/ocmbnbkcilckgcdjbcbhlmibbfecefbe?utm_source=item-share-cband"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full bg-cyan-500 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-slate-950 transition hover:bg-cyan-400"
              >
                Install Vision Scraper
              </a>

              <a
                href="https://www.linkedin.com/jobs/search/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-6 py-3 text-sm font-black uppercase tracking-[0.14em] text-slate-200 transition hover:border-white/25 hover:text-white"
              >
                Open LinkedIn Jobs Search
              </a>

            </div>

          </div>

        </div>



        <div className="rounded-shell border border-white/10 bg-white/5 p-5">

        <div role="button" tabIndex={0} onClick={() => setIsClientsBoardOpen((current) => !current)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setIsClientsBoardOpen((current) => !current); } }} className="flex w-full flex-wrap items-center justify-between gap-3 text-left cursor-pointer">

          <div>

            <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Client CRM</div>

            <div className="mt-2 text-sm text-slate-400">Single source of truth for client companies, contacts, job orders, opportunities, touchpoints, risk, and submittal-to-hire performance.</div>

          </div>

          <div className="flex items-center gap-2">

            <div className="rounded-full border border-white/10 bg-slate-950/60 px-4 py-2 text-meta font-black uppercase tracking-[0.16em] text-slate-300">{clientPortfolio.length} tracked clients</div>

            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowCrmAutomationPanel(true); }}
              className="flex items-center gap-1.5 rounded-full border border-cyan-400/25 bg-cyan-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-cyan-300 hover:bg-cyan-500/20 transition"
            >
              Automation{pendingAutomations.length > 0 && <span className="ml-0.5 rounded-full bg-amber-500 px-1.5 text-micro text-white">{pendingAutomations.length}</span>}
            </button>

            <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200">{isClientsBoardOpen ? 'Hide board' : 'Open board'}</div>

          </div>

        </div>

        {isClientsBoardOpen && (

          <>

          <div className="mt-4 grid gap-3 md:grid-cols-4 xl:grid-cols-8">
            <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Sales queue</div>
              <div className="mt-2 text-2xl font-black text-white">{clientCrmQueue.length}</div>
            </div>
            <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Open opportunities</div>
              <div className="mt-2 text-2xl font-black text-white">{clientCrmOpenOpportunities}</div>
            </div>
            <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Weighted value</div>
              <div className="mt-2 text-2xl font-black text-white">R {clientCrmWeightedPipeline.toLocaleString()}</div>
            </div>
            <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Touchpoints</div>
              <div className="mt-2 text-2xl font-black text-white">{clientCrmTouchpoints.length}</div>
            </div>
            <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Placements</div>
              <div className="mt-2 text-2xl font-black text-white">{clientCrmPlacementCount}</div>
            </div>
            <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Revenue</div>
              <div className="mt-2 text-2xl font-black text-white">R {clientCrmRevenue.toLocaleString()}</div>
            </div>
            <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">At-risk accounts</div>
              <div className="mt-2 text-2xl font-black text-white">{clientCrmCriticalCount}</div>
            </div>
            <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Forecast accounts</div>
              <div className="mt-2 text-2xl font-black text-white">{clientCrmForecastCoverage}</div>
            </div>
          </div>

          {/* ── Overdue cadence steps strip ───────────────────────────────── */}
          {overdueCadenceItems.length > 0 && (
            <div className="mt-4 rounded-card border border-amber-400/20 bg-amber-500/5 px-4 py-3">
              <div className="mb-2 flex items-center gap-2">
                <span className="text-micro font-black uppercase tracking-[0.16em] text-amber-400">Cadence steps overdue</span>
                <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-micro font-black text-amber-300">{overdueCadenceItems.length}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {overdueCadenceItems.slice(0, 6).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => { const c = contacts.find((x) => x.id === item.contactId); if (c) setSelectedContactId(c.id); }}
                    className="flex items-center gap-2 rounded-full border border-amber-400/25 bg-amber-500/10 pl-2 pr-3 py-1.5 text-micro text-amber-200 hover:bg-amber-500/20 transition"
                  >
                    <span className="flex h-4 w-4 items-center justify-center rounded-full bg-amber-500 text-micro font-black text-white">{item.daysOverdue}d</span>
                    <span className="font-semibold">{item.contactName || item.contactId}</span>
                    <span className="text-amber-500/70 text-micro">{item.cadenceName}</span>
                  </button>
                ))}
                {overdueCadenceItems.length > 6 && (
                  <button type="button" onClick={() => setShowCrmAutomationPanel(true)} className="rounded-full border border-amber-400/20 bg-transparent px-3 py-1.5 text-micro text-amber-500 hover:text-amber-300 transition">
                    +{overdueCadenceItems.length - 6} more →
                  </button>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 rounded-shell border border-cyan-400/15 bg-cyan-500/[0.06] p-4 sm:p-5">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Real-Time Organizational Graph</div>
                <div className="mt-1 max-w-3xl text-sm leading-6 text-slate-400">Companies and contacts now share the same operating graph as jobs and candidates. Vision synthesizes role velocity, department demand, contact sentiment, manager CV vibe, candidate submissions, and generated revenue from your live workspace data.</div>
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="rounded-card border border-white/10 bg-slate-950/55 px-4 py-3">
                  <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Companies</div>
                  <div className="mt-1 text-xl font-black text-white">{organizationalGraph.stats.companies}</div>
                </div>
                <div className="rounded-card border border-white/10 bg-slate-950/55 px-4 py-3">
                  <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Contacts</div>
                  <div className="mt-1 text-xl font-black text-white">{organizationalGraph.stats.contacts}</div>
                </div>
                <div className="rounded-card border border-white/10 bg-slate-950/55 px-4 py-3">
                  <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Velocity/mo</div>
                  <div className="mt-1 text-xl font-black text-white">{organizationalGraph.stats.roleVelocity.toFixed(1)}</div>
                </div>
                <div className="rounded-card border border-white/10 bg-slate-950/55 px-4 py-3">
                  <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Revenue</div>
                  <div className="mt-1 text-xl font-black text-white">R {Number(organizationalGraph.stats.generatedRevenue || 0).toLocaleString()}</div>
                </div>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-4">
              <div className="rounded-card border border-white/10 bg-slate-950/55 p-4">
                <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Shared graph</div>
                <div className="mt-2 text-sm font-bold text-white">Company → Contact → Job → Submission → Candidate → Revenue</div>
                <div className="mt-2 text-xs leading-5 text-slate-500">One operating model across sales, recruiting, screening, and placements.</div>
              </div>
              <div className="rounded-card border border-white/10 bg-slate-950/55 p-4">
                <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Job orders</div>
                <div className="mt-2 text-2xl font-black text-white">{organizationalGraph.stats.jobOrders}</div>
                <div className="mt-1 text-xs text-slate-500">Mapped to client companies.</div>
              </div>
              <div className="rounded-card border border-white/10 bg-slate-950/55 p-4">
                <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Submissions</div>
                <div className="mt-2 text-2xl font-black text-white">{organizationalGraph.stats.submissions}</div>
                <div className="mt-1 text-xs text-slate-500">Candidate-to-job links in the graph.</div>
              </div>
              <div className="rounded-card border border-white/10 bg-slate-950/55 p-4">
                <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">Screened</div>
                <div className="mt-2 text-2xl font-black text-white">{organizationalGraph.stats.completedScreenings}</div>
                <div className="mt-1 text-xs text-slate-500">Completed screening results tied to submissions.</div>
              </div>
            </div>
            <div className="mt-4 grid gap-3 md:grid-cols-5">
              {organizationalGraph.smartCollections.map((collectionItem) => (
                <div key={collectionItem.id} className="rounded-card border border-white/10 bg-slate-950/55 p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-400">{collectionItem.label}</div>
                    <div className="rounded-full border border-white/10 bg-white/5 px-2 py-1 text-xs font-black text-white">{collectionItem.count}</div>
                  </div>
                  <div className="mt-2 text-xs leading-5 text-slate-500">{collectionItem.description}</div>
                </div>
              ))}
            </div>
            {topGraphCompanies.length > 0 && (
              <div className="mt-4 grid gap-3 lg:grid-cols-2">
                {topGraphCompanies.map((company) => (
                  <div key={company.id || company.normalizedCompany} className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-black text-white">{company.name}</div>
                      <div className="rounded-full border border-cyan-300/20 bg-cyan-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.14em] text-cyan-100">{company.liquiditySignal}</div>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-400 sm:grid-cols-3">
                      <div><span className="text-slate-500">Roles:</span> {company.openRoles}/{company.jobs.length}</div>
                      <div><span className="text-slate-500">Submittal:</span> {company.submittalToHireRatio}</div>
                      <div><span className="text-slate-500">Velocity:</span> {company.roleVelocity}/mo</div>
                    </div>
                    <div className="mt-3 grid gap-2 text-xs text-slate-400 sm:grid-cols-3">
                      <div><span className="text-slate-500">Submissions:</span> {company.submissions.length}</div>
                      <div><span className="text-slate-500">Screened:</span> {company.completedScreenings}</div>
                      <div><span className="text-slate-500">Revenue:</span> R {Number(company.revenue || 0).toLocaleString()}</div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {company.contacts.slice(0, 3).map((contact) => (
                        <div key={contact.id || `${company.id}-${contact.email || contact.name}`} className="rounded-ctl border border-white/10 bg-white/[0.035] px-3 py-2">
                          <div className="text-xs font-bold text-white">{contact.name || contact.email || 'Contact'}</div>
                          <div className="mt-1 text-micro uppercase tracking-[0.12em] text-slate-500">{contact.vibe} · {contact.sentiment}</div>
                        </div>
                      ))}
                      {company.contacts.length === 0 && <div className="text-xs text-slate-500">No contacts mapped yet.</div>}
                    </div>
                    <div className="mt-3 text-meta leading-5 text-slate-500">Departments: {Object.entries(company.departmentMix).map(([department, count]) => `${department} ${count}`).join(' · ') || 'No department signal yet'}</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {clientCrmNotice && (
            <div className="mt-4 rounded-card border border-cyan-400/25 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">{clientCrmNotice}</div>
          )}

          <div className="mt-4 rounded-shell border border-white/10 bg-slate-950/55 p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Gmail Integration</div>
                <div className="mt-1 text-sm text-slate-400">
                  {gmailIntegrationStatus.pendingVerification
                    ? 'Gmail sync is built but locked while Google verification is pending.'
                    : gmailIntegrationStatus.connected
                      ? `Connected as ${gmailIntegrationStatus.emailAddress || 'Gmail account'}.`
                      : 'Connect Gmail to sync client replies and email activity into the CRM.'}
                </div>
                {gmailIntegrationStatus.lastSyncAtIso && <div className="mt-2 text-xs text-slate-500">Last sync: {new Date(gmailIntegrationStatus.lastSyncAtIso).toLocaleString()}</div>}
              </div>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={refreshGmailIntegrationStatus} disabled={gmailIntegrationStatus.loading || gmailIntegrationBusy} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.14em] text-slate-200 transition hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-60">{gmailIntegrationStatus.loading ? 'Checking...' : 'Refresh'}</button>
                {!gmailIntegrationStatus.connected ? (
                  <button type="button" onClick={handleStartGmailConnection} disabled={!gmailIntegrationStatus.ready || gmailIntegrationBusy} className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.14em] text-cyan-100 transition hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-50">{gmailIntegrationStatus.pendingVerification ? 'Pending verification' : gmailIntegrationBusy ? 'Connecting...' : 'Connect Gmail'}</button>
                ) : (
                  <>
                    <button type="button" onClick={handleSyncGmailClientActivity} disabled={gmailIntegrationBusy} className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.14em] text-emerald-100 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60">{gmailIntegrationBusy ? 'Working...' : 'Sync now'}</button>
                    <button type="button" onClick={handleDisconnectGmailIntegration} disabled={gmailIntegrationBusy} className="rounded-full border border-rose-400/30 bg-rose-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.14em] text-rose-100 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-60">Disconnect</button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Health distribution summary */}
          {clientPortfolio.length > 0 && (() => {
            const levelOrder = ['Critical', 'At risk', 'Needs attention', 'Watch', 'Healthy'];
            return (
              <div className="mt-5 grid grid-cols-5 gap-2">
                {levelOrder.map((level) => {
                  const count = clientPortfolio.filter((c) => c.riskLevel === level).length;
                  const s = RISK_LEVEL_STYLES[level] || RISK_LEVEL_STYLES['Healthy'];
                  return (
                    <div key={level} className={`rounded-card border px-3 py-3 text-center ${count > 0 ? `${s.border} ${s.bg}` : 'border-white/5 bg-white/[0.02]'}`}>
                      <div className={`text-xl font-black ${count > 0 ? s.badge.split(' ').find((c2) => c2.startsWith('text-')) || 'text-white' : 'text-slate-700'}`}>{count}</div>
                      <div className="mt-0.5 text-micro font-black uppercase tracking-[0.1em] text-slate-500">{level}</div>
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {(() => {
            const clientCrmQuery = clientCrmSearch.trim().toLowerCase();
            const filteredClients = clientCrmQuery
              ? clientPortfolio.filter((c) => [
                  c.companyName, c.industry, c.riskLevel,
                  ...(c.contacts || []).map((ct) => ct.name),
                  ...(c.contacts || []).map((ct) => ct.email),
                ].some((v) => String(v || '').toLowerCase().includes(clientCrmQuery)))
              : clientPortfolio;
            const allSelected = filteredClients.length > 0 && filteredClients.every((c) => clientCrmSelectedIds.has(c.id));
            const someSelected = clientCrmSelectedIds.size > 0;
            const toggleSelectAll = () => {
              if (allSelected) {
                setClientCrmSelectedIds((prev) => { const next = new Set(prev); filteredClients.forEach((c) => next.delete(c.id)); return next; });
              } else {
                setClientCrmSelectedIds((prev) => { const next = new Set(prev); filteredClients.forEach((c) => next.add(c.id)); return next; });
              }
            };
            return (
              <>
                {/* Search + select-all toolbar */}
                <div className="mt-4 flex items-center gap-3">
                  <button
                    type="button"
                    onClick={toggleSelectAll}
                    className={`shrink-0 h-5 w-5 rounded border flex items-center justify-center transition ${allSelected ? 'bg-cyan-500 border-cyan-400' : someSelected ? 'bg-cyan-500/30 border-cyan-400/60' : 'border-white/20 bg-white/5 hover:border-cyan-400/50'}`}
                    title={allSelected ? 'Deselect all' : 'Select all'}
                  >
                    {(allSelected || someSelected) && <Check size={11} className="text-slate-950 font-black" />}
                  </button>
                  <input
                    value={clientCrmSearch}
                    onChange={(e) => setClientCrmSearch(e.target.value)}
                    placeholder="Search clients by name, industry, risk level, or contact…"
                    className="flex-1 rounded-card border border-white/10 bg-slate-950/70 px-4 py-2.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-400"
                  />
                  {clientCrmSearch && (
                    <button type="button" onClick={() => setClientCrmSearch('')} className="shrink-0 rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-slate-400 hover:text-white transition">Clear</button>
                  )}
                  {clientCrmSearch && (
                    <span className="shrink-0 text-xs text-slate-500">{filteredClients.length} of {clientPortfolio.length}</span>
                  )}
                </div>

                {/* Bulk-action bar */}
                {someSelected && (
                  <div className="mt-3 flex items-center gap-3 rounded-card border border-rose-500/20 bg-rose-500/5 px-4 py-2.5">
                    <span className="flex-1 text-xs font-semibold text-rose-200">{clientCrmSelectedIds.size} client{clientCrmSelectedIds.size !== 1 ? 's' : ''} selected</span>
                    <button type="button" onClick={() => setClientCrmSelectedIds(new Set())} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-slate-400 hover:text-white transition">Deselect all</button>
                    <button
                      type="button"
                      onClick={() => setClientCrmDeleteConfirm(true)}
                      className="inline-flex items-center gap-1.5 rounded-full border border-rose-400/40 bg-rose-500/15 px-4 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-rose-200 transition hover:bg-rose-500/25"
                    >
                      <AlertTriangle size={11} /> Delete {clientCrmSelectedIds.size} client{clientCrmSelectedIds.size !== 1 ? 's' : ''}
                    </button>
                  </div>
                )}

                {/* Confirm delete modal */}
                {clientCrmDeleteConfirm && (
                  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
                    <div className="w-full max-w-sm rounded-shell border border-rose-500/30 bg-slate-900 shadow-2xl flex flex-col max-h-[80vh]">
                      <div className="p-5 pb-0">
                        <div className="flex items-center gap-3 mb-3">
                          <AlertTriangle className="h-5 w-5 text-rose-400 shrink-0" />
                          <span className="text-sm font-black text-white">Delete {clientCrmSelectedIds.size} client{clientCrmSelectedIds.size !== 1 ? 's' : ''}?</span>
                        </div>
                        <p className="text-xs text-slate-400 leading-5">
                          This will permanently remove all associated jobs, contacts, CRM tasks, and activity history. This cannot be undone.
                        </p>
                      </div>
                      <div className="px-5 mt-3 overflow-y-auto flex-1 min-h-0">
                        <div className="rounded-ctl border border-rose-500/20 bg-rose-500/5 px-3 py-2.5">
                          {clientPortfolio.filter((c) => clientCrmSelectedIds.has(c.id)).map((c) => (
                            <div key={c.id} className="text-xs font-semibold text-rose-300 py-0.5 truncate">{c.companyName}</div>
                          ))}
                        </div>
                      </div>
                      <div className="p-5 pt-4 flex gap-3 shrink-0">
                        <button type="button" disabled={clientCrmDeleting} onClick={() => setClientCrmDeleteConfirm(false)} className="flex-1 rounded-full border border-white/10 bg-white/5 py-2 text-xs font-black uppercase tracking-[0.12em] text-slate-300 hover:text-white transition disabled:opacity-50">Cancel</button>
                        <button
                          type="button"
                          disabled={clientCrmDeleting}
                          onClick={() => handleDeleteClients(clientCrmSelectedIds)}
                          className="flex-1 rounded-full border border-rose-400/40 bg-rose-500/20 py-2 text-xs font-black uppercase tracking-[0.12em] text-rose-200 transition hover:bg-rose-500/30 disabled:opacity-50"
                        >
                          {clientCrmDeleting ? 'Deleting…' : 'Delete permanently'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Sales forecast summary bar ── */}
                {(() => {
                  const openOpps = clientCrmOpportunities.filter((o) => !['Closed-Won', 'Closed-Lost'].includes(o.stage));
                  if (openOpps.length === 0) return null;
                  const qMap = {};
                  openOpps.forEach((o) => {
                    const d = new Date(o.closeDate || o.expiryDate || o.updatedAtIso || o.createdAtIso || Date.now());
                    const qLabel = `Q${Math.ceil((d.getMonth() + 1) / 3)} ${d.getFullYear()}`;
                    if (!qMap[qLabel]) qMap[qLabel] = { label: qLabel, weighted: 0, count: 0, ms: d.getTime() };
                    qMap[qLabel].weighted += Number(o.weightedValue || 0);
                    qMap[qLabel].count += 1;
                  });
                  const quarters = Object.values(qMap).sort((a, b) => a.ms - b.ms).slice(0, 6);
                  const maxQ = Math.max(...quarters.map((q) => q.weighted), 1);
                  const totalWeighted = openOpps.reduce((s, o) => s + Number(o.weightedValue || 0), 0);
                  return (
                    <div className="mt-4 rounded-card border border-cyan-400/15 bg-cyan-500/5 px-5 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="text-micro font-black uppercase tracking-[0.2em] text-cyan-400">Sales forecast — weighted pipeline by quarter</div>
                        <div className="text-xs font-black text-cyan-300">Total: {Number(totalWeighted).toLocaleString()}</div>
                      </div>
                      <div className="flex items-end gap-3">
                        {quarters.map((q) => (
                          <div key={q.label} className="flex flex-col items-center gap-1.5 flex-1">
                            <div className="text-micro font-bold text-cyan-300">{Number(q.weighted).toLocaleString()}</div>
                            <div className="w-full rounded-t-lg bg-cyan-400 opacity-70 hover:opacity-100 transition" style={{ height: `${Math.max((q.weighted / maxQ) * 48, 4)}px` }} />
                            <div className="text-micro text-slate-500">{q.label}</div>
                            <div className="text-micro text-slate-600">{q.count} opp{q.count !== 1 ? 's' : ''}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* ── Sales pipeline board ─────────────────────────────── */}
                {clientCrmOpportunities.length > 0 && (() => {
                  const accountByCompany = new Map(clientPortfolio.map((client) => [normalizeText(client.companyName), client]));
                  const activeStages = CLIENT_OPPORTUNITY_STAGES;
                  const maxStageValue = Math.max(1, ...activeStages.map((stage) => clientCrmOpportunities.filter((opportunity) => normalizeClientOpportunityStage(opportunity.stage) === stage).reduce((sum, opportunity) => sum + Number(opportunity.weightedValue || 0), 0)));
                  return (
                    <div className="mt-4 rounded-shell border border-white/10 bg-slate-950/55 p-4 sm:p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div>
                          <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Client Sales Pipeline</div>
                          <div className="mt-1 text-sm text-slate-400">Deals stay attached to companies and contacts. Closed-Won still generates the ATS job order automatically.</div>
                        </div>
                        <div className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-300">{clientCrmOpenOpportunities} open deals</div>
                      </div>
                      <div className="mt-4 grid gap-3 xl:grid-cols-7">
                        {activeStages.map((stage) => {
                          const stageOpps = clientCrmOpportunities
                            .filter((opportunity) => normalizeClientOpportunityStage(opportunity.stage) === stage)
                            .sort((left, right) => Number(right.weightedValue || 0) - Number(left.weightedValue || 0));
                          const stageValue = stageOpps.reduce((sum, opportunity) => sum + Number(opportunity.weightedValue || 0), 0);
                          const widthPct = Math.max(5, Math.round((stageValue / maxStageValue) * 100));
                          return (
                            <div key={stage} className="rounded-card border border-white/10 bg-white/[0.035] p-3">
                              <div className="flex items-center justify-between gap-2">
                                <div className="text-micro font-black uppercase tracking-[0.12em] text-slate-300">{stage}</div>
                                <div className="rounded-full bg-slate-950/80 px-2 py-0.5 text-micro font-black text-white">{stageOpps.length}</div>
                              </div>
                              <div className="mt-2 text-sm font-black text-cyan-100">R {Number(stageValue || 0).toLocaleString()}</div>
                              <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
                                <div className="h-full rounded-full bg-cyan-400/70" style={{ width: `${widthPct}%` }} />
                              </div>
                              <div className="mt-3 space-y-2">
                                {stageOpps.slice(0, 4).map((opportunity) => {
                                  const account = accountByCompany.get(normalizeText(opportunity.companyName));
                                  const action = getOpportunityNextAction(opportunity, account);
                                  const isStale = new Date(opportunity.updatedAtIso || opportunity.createdAtIso || 0).getTime() < Date.now() - 7 * 86400000;
                                  return (
                                    <div key={opportunity.id} className={`rounded-ctl border px-3 py-2 ${isStale && !['Closed-Won', 'Closed-Lost'].includes(stage) ? 'border-amber-400/25 bg-amber-500/10' : 'border-white/10 bg-slate-950/70'}`}>
                                      <div className="truncate text-xs font-black text-white">{opportunity.title || 'Opportunity'}</div>
                                      <div className="mt-1 truncate text-micro text-slate-400">{opportunity.companyName || 'Unknown company'}</div>
                                      <div className="mt-2 flex items-center justify-between gap-2">
                                        <div className="text-micro font-black text-cyan-200">R {Number(opportunity.weightedValue || 0).toLocaleString()}</div>
                                        <div className="text-micro text-slate-500">{Math.round(Number(opportunity.probability || 0) * 100)}%</div>
                                      </div>
                                      <div className="mt-2 text-micro leading-4 text-slate-400">{action}</div>
                                    </div>
                                  );
                                })}
                                {stageOpps.length > 4 && <div className="text-micro text-slate-500">+{stageOpps.length - 4} more deals</div>}
                                {stageOpps.length === 0 && <div className="rounded-ctl border border-dashed border-white/10 px-3 py-4 text-micro text-slate-600">No deals</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}

                <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {filteredClients.length === 0 && clientPortfolio.length === 0 && (
                    <div className="md:col-span-2 xl:col-span-3 rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No client companies are tied to the portfolio yet.</div>
                  )}
                  {filteredClients.length === 0 && clientPortfolio.length > 0 && (
                    <div className="md:col-span-2 xl:col-span-3 rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No clients match &ldquo;{clientCrmSearch}&rdquo;.</div>
                  )}
                  {filteredClients.map((client) => (
                    <RelationshipRiskCard
                      key={client.id}
                      client={client}
                      selected={clientCrmSelectedIds.has(client.id)}
                      onSelect={(id) => setClientCrmSelectedIds((prev) => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; })}
                      onLogEmail={(c) => handleLogClientTouchpoint(c, 'email')}
                      onLogCall={(c) => handleLogClientTouchpoint(c, 'call')}
                      onLogMeeting={(c) => handleLogClientTouchpoint(c, 'meeting')}
                      onSendCta={handleSendClientResponseEmail}
                      sendingCta={Boolean(clientResponseEmailState[client.id || normalizeText(client.companyName)])}
                      onDelete={(c) => { setClientCrmSelectedIds(new Set([c.id])); setClientCrmDeleteConfirm(true); }}
                      onOpenAccount={(id) => setSelectedAccountId(id)}
                      onFetchLogo={handleFetchClientLogo}
                      fetchingLogo={fetchingLogoClientId === client.id}
                    />
                  ))}
                </div>
              </>
            );
          })()}

          {/* ── Contact Database ──────────────────────────────────────── */}
          <div className="mt-6 rounded-shell border border-white/10 bg-slate-950/55 p-4 sm:p-5">
            <button className="flex w-full items-center justify-between gap-3" onClick={() => setContactDbOpen((p) => !p)}>
              <div className="flex items-center gap-3">
                <span className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Contact Database</span>
                <span className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-micro font-black text-cyan-300">{contacts.length}</span>
                {totalDupCount > 0 && (
                  <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-micro font-black text-amber-300">{totalDupCount} dup{totalDupCount !== 1 ? 's' : ''}</span>
                )}
              </div>
              <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${contactDbOpen ? 'rotate-90' : ''}`} />
            </button>

            {contactDbOpen && (
              <div className="mt-4 space-y-4">
                {/* Saved views pills */}
                {savedContactViews.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {savedContactViews.map((view) => (
                      <div key={view.id} className="flex items-center gap-1 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-1 text-micro font-black text-cyan-200">
                        <button onClick={() => { setContactDbSearch(view.search || ''); setContactDbFilters(view.filters || { company: '', role: '', sentiment: '', tag: '', source: '' }); setContactDbSort(view.sort || 'name'); }}>{view.name}</button>
                        <button onClick={() => handleDeleteSavedView('contacts', view.id)} className="ml-1 text-slate-400 hover:text-rose-400">✕</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Search + filters row */}
                <div className="flex flex-wrap gap-2">
                  <input
                    value={contactDbSearch}
                    onChange={(e) => setContactDbSearch(e.target.value)}
                    placeholder="Search contacts…"
                    className="flex-1 min-w-[160px] rounded-card border border-white/10 bg-slate-950/70 px-4 py-2.5 text-sm text-white outline-none transition focus:border-cyan-400"
                  />
                  <select value={contactDbFilters.role} onChange={(e) => setContactDbFilters((p) => ({ ...p, role: e.target.value }))} className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400">
                    <option value="">All roles</option>
                    {CONTACT_ROLES.map((r) => <option key={r}>{r}</option>)}
                  </select>
                  <select value={contactDbFilters.sentiment} onChange={(e) => setContactDbFilters((p) => ({ ...p, sentiment: e.target.value }))} className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400">
                    <option value="">All sentiments</option>
                    {Object.keys(CONTACT_SENTIMENT_STYLES).map((s) => <option key={s}>{s}</option>)}
                  </select>
                  {allContactTags.length > 0 && (
                    <select value={contactDbFilters.tag} onChange={(e) => setContactDbFilters((p) => ({ ...p, tag: e.target.value }))} className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400">
                      <option value="">All tags</option>
                      {allContactTags.map((t) => <option key={t}>{t}</option>)}
                    </select>
                  )}
                  <select value={contactDbSort} onChange={(e) => setContactDbSort(e.target.value)} className="rounded-card border border-white/10 bg-slate-950/70 px-3 py-2 text-xs text-white outline-none focus:border-cyan-400">
                    <option value="name">Sort: Name</option>
                    <option value="company">Sort: Company</option>
                    <option value="recent">Sort: Recent</option>
                    <option value="sentiment">Sort: Sentiment</option>
                  </select>
                  {(contactDbSearch || contactDbFilters.role || contactDbFilters.sentiment || contactDbFilters.tag || contactDbFilters.source) && (
                    <button
                      onClick={() => {
                        if (saveViewModalOpen && saveViewTarget === 'contacts') {
                          handleSaveView('contacts', saveViewName, contactDbFilters, contactDbSort, contactDbSearch);
                          setSaveViewModalOpen(false); setSaveViewName('');
                        } else {
                          setSaveViewTarget('contacts'); setSaveViewModalOpen(true);
                        }
                      }}
                      className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-cyan-200 hover:bg-cyan-500/20"
                    >Save view</button>
                  )}
                </div>

                {/* Save view name input */}
                {saveViewModalOpen && saveViewTarget === 'contacts' && (
                  <div className="flex gap-2">
                    <input autoFocus value={saveViewName} onChange={(e) => setSaveViewName(e.target.value)} placeholder="View name…" className="flex-1 rounded-card border border-cyan-400/40 bg-slate-950/70 px-4 py-2 text-sm text-white outline-none focus:border-cyan-400" />
                    <button onClick={() => { handleSaveView('contacts', saveViewName, contactDbFilters, contactDbSort, contactDbSearch); setSaveViewModalOpen(false); setSaveViewName(''); }} className="rounded-full bg-cyan-400 px-4 py-2 text-xs font-black text-slate-950 hover:bg-cyan-300">Save</button>
                    <button onClick={() => { setSaveViewModalOpen(false); setSaveViewName(''); }} className="rounded-full border border-white/10 px-4 py-2 text-xs font-black text-slate-400 hover:text-white">Cancel</button>
                  </div>
                )}

                {/* Contact rows */}
                <div className="space-y-2">
                  {filteredContactDb.length === 0 ? (
                    <div className="rounded-card border border-dashed border-white/10 px-4 py-4 text-sm text-slate-500">No contacts match the current filters.</div>
                  ) : filteredContactDb.map((contact) => {
                    const sentStyle = CONTACT_SENTIMENT_STYLES[contact.sentiment] || CONTACT_SENTIMENT_STYLES['Neutral'];
                    const isEditingTags = tagEditContactId === contact.id;
                    const contactTags = Array.isArray(contact.tags) ? contact.tags : [];
                    const commercial = contactCommercialRollups[contact.id] || buildContactCommercialRollup(contact, {
                      opportunities: clientCrmOpportunities,
                      jobs,
                      submissions,
                      placements,
                      touchpoints: clientCrmTouchpoints,
                      activityLog,
                      candidates,
                    });
                    const healthTone = commercial.health === 'Revenue risk' || commercial.health === 'At risk'
                      ? 'border-rose-400/25 bg-rose-500/10 text-rose-300'
                      : commercial.health === 'Needs feedback'
                        ? 'border-amber-400/25 bg-amber-500/10 text-amber-300'
                        : commercial.health === 'Active'
                          ? 'border-cyan-400/25 bg-cyan-500/10 text-cyan-300'
                          : 'border-emerald-400/25 bg-emerald-500/10 text-emerald-300';
                    return (
                      <div key={contact.id} className="rounded-card border border-white/[0.07] bg-white/[0.03] p-3 hover:bg-white/[0.05] transition">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex flex-wrap items-center gap-2 min-w-0">
                            <span className="font-semibold text-sm text-white truncate max-w-[180px]">{contact.name || '—'}</span>
                            {(contact.companyName || contact.company) && <span className="text-xs text-slate-400 truncate max-w-[140px]">{contact.company || contact.companyName}</span>}
                            {contact.role && <span className="rounded-full bg-white/5 px-2 py-0.5 text-micro text-slate-400">{contact.role}</span>}
                            <span className={`rounded-full px-2 py-0.5 text-micro font-bold ${sentStyle.bg} ${sentStyle.text}`}>{contact.sentiment || 'Neutral'}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <span className="text-micro text-slate-500">{contact.lastInteractionAtIso ? new Date(contact.lastInteractionAtIso).toLocaleDateString() : (contact.updatedAtIso ? new Date(contact.updatedAtIso).toLocaleDateString() : '—')}</span>
                            <button onClick={() => setSelectedContactId(contact.id)} className="rounded-full border border-violet-400/30 bg-violet-500/10 px-2.5 py-1 text-micro font-bold text-violet-300 hover:bg-violet-500/20 transition">Open</button>
                            <button onClick={() => { setTagEditContactId(isEditingTags ? null : contact.id); setTagEditInput(''); }} className="rounded-full border border-white/10 px-2 py-1 text-micro text-slate-400 hover:text-cyan-300 hover:border-cyan-400/30">🏷 Tags</button>
                          </div>
                        </div>

                        <div className="mt-2 grid gap-2 sm:grid-cols-[1fr_auto] sm:items-center">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <span className={`rounded-full border px-2 py-0.5 text-micro font-black uppercase tracking-[0.1em] ${healthTone}`}>{commercial.health}</span>
                            <span className="rounded-full border border-white/10 bg-slate-950/60 px-2 py-0.5 text-micro font-bold text-slate-300">{commercial.submissions.length} submissions</span>
                            <span className="rounded-full border border-white/10 bg-slate-950/60 px-2 py-0.5 text-micro font-bold text-slate-300">{commercial.openOpportunities.length} open deals</span>
                            {commercial.totalRevenue > 0 && <span className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-2 py-0.5 text-micro font-bold text-emerald-300">{summarizeMoneyByCurrency(commercial.placements, 'invoiceTotal')}</span>}
                            {commercial.openWeightedPipeline > 0 && <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-2 py-0.5 text-micro font-bold text-cyan-300">Pipeline {formatMoney(commercial.openWeightedPipeline, 'ZAR')}</span>}
                          </div>
                          <div className="truncate text-micro font-semibold text-slate-400 sm:max-w-[260px]">{commercial.nextAction}</div>
                        </div>

                        {/* Tag chips */}
                        {(contactTags.length > 0 || isEditingTags) && (
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            {contactTags.map((tag) => (
                              <span key={tag} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-micro font-bold ${getTagColor(tag)}`}>
                                {tag}
                                {isEditingTags && (
                                  <button onClick={() => handleTagContact(contact.id, tag, true)} className="ml-0.5 opacity-60 hover:opacity-100">✕</button>
                                )}
                              </span>
                            ))}
                            {isEditingTags && (
                              <form onSubmit={(e) => { e.preventDefault(); if (tagEditInput.trim()) { handleTagContact(contact.id, tagEditInput.trim()); setTagEditInput(''); } }} className="flex items-center gap-1">
                                <input
                                  autoFocus
                                  value={tagEditInput}
                                  onChange={(e) => setTagEditInput(e.target.value)}
                                  placeholder="Add tag…"
                                  className="w-24 rounded-full border border-white/10 bg-slate-950/70 px-2 py-0.5 text-micro text-white outline-none focus:border-cyan-400"
                                />
                                {allContactTags.filter((t) => !contactTags.includes(t) && t.toLowerCase().includes(tagEditInput.toLowerCase())).slice(0, 4).map((t) => (
                                  <button key={t} type="button" onClick={() => { handleTagContact(contact.id, t); setTagEditInput(''); }} className={`rounded-full px-2 py-0.5 text-micro font-bold ${getTagColor(t)} opacity-70 hover:opacity-100`}>{t}</button>
                                ))}
                                <button type="submit" className="rounded-full bg-cyan-500/20 px-2 py-0.5 text-micro text-cyan-300 hover:bg-cyan-500/30">+</button>
                              </form>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* ── Data Quality Center (Dedup) ───────────────────────────── */}
          {totalDupCount > 0 && (
            <div className="mt-4 rounded-shell border border-amber-500/20 bg-amber-500/5 p-4 sm:p-5">
              <button className="flex w-full items-center justify-between gap-3" onClick={() => setDedupOpen((p) => !p)}>
                <div className="flex items-center gap-3">
                  <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                  <span className="text-meta font-black uppercase tracking-[0.18em] text-amber-300">Data Quality Center</span>
                  <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-micro font-black text-amber-300">{totalDupCount} potential duplicate{totalDupCount !== 1 ? 's' : ''}</span>
                </div>
                <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${dedupOpen ? 'rotate-90' : ''}`} />
              </button>

              {dedupOpen && (
                <div className="mt-4 space-y-4">
                  {/* Contacts dedup */}
                  {contactDupGroups.length > 0 && (
                    <div>
                      <div className="mb-2 text-micro font-black uppercase tracking-[0.15em] text-slate-400">Contacts — {contactDupGroups.length} pair{contactDupGroups.length !== 1 ? 's' : ''}</div>
                      <div className="space-y-3">
                        {contactDupGroups.map(({ primary, secondary, score, key }) => (
                          <div key={key} className="rounded-card border border-white/[0.07] bg-white/[0.03] p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                                <div className="rounded-ctl border border-white/10 bg-slate-950/50 p-3">
                                  <div className="text-xs font-bold text-white">{primary.name || '—'}</div>
                                  <div className="text-micro text-slate-400">{primary.email || ''}</div>
                                  <div className="text-micro text-slate-400">{primary.companyName || ''}</div>
                                  {primary.phone && <div className="text-micro text-slate-500">{primary.phone}</div>}
                                </div>
                                <div className="rounded-ctl border border-white/10 bg-slate-950/50 p-3">
                                  <div className="text-xs font-bold text-white">{secondary.name || '—'}</div>
                                  <div className="text-micro text-slate-400">{secondary.email || ''}</div>
                                  <div className="text-micro text-slate-400">{secondary.companyName || ''}</div>
                                  {secondary.phone && <div className="text-micro text-slate-500">{secondary.phone}</div>}
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2 shrink-0">
                                <div className="text-micro font-black text-amber-300">{score}% match</div>
                                <button onClick={() => { setDedupMergeModal({ primary, secondary, entity: 'contacts', score }); setDedupMergeFieldChoices({}); }} className="rounded-full bg-cyan-400 px-3 py-1.5 text-micro font-black text-slate-950 hover:bg-cyan-300">Merge</button>
                                <button onClick={() => handleDedupDismiss(key)} className="rounded-full border border-white/10 px-3 py-1.5 text-micro text-slate-400 hover:text-white">Not a duplicate</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Candidates dedup */}
                  {candidateDupGroups.length > 0 && (
                    <div>
                      <div className="mb-2 text-micro font-black uppercase tracking-[0.15em] text-slate-400">Candidates — {candidateDupGroups.length} pair{candidateDupGroups.length !== 1 ? 's' : ''}</div>
                      <div className="space-y-3">
                        {candidateDupGroups.map(({ primary, secondary, score, key }) => (
                          <div key={key} className="rounded-card border border-white/[0.07] bg-white/[0.03] p-4">
                            <div className="flex items-start justify-between gap-3">
                              <div className="grid flex-1 gap-3 sm:grid-cols-2">
                                <div className="rounded-ctl border border-white/10 bg-slate-950/50 p-3">
                                  <div className="text-xs font-bold text-white">{primary.name || '—'}</div>
                                  <div className="text-micro text-slate-400">{primary.email || ''}</div>
                                  <div className="text-micro text-slate-400">{primary.company || ''} {primary.role ? `· ${primary.role}` : ''}</div>
                                </div>
                                <div className="rounded-ctl border border-white/10 bg-slate-950/50 p-3">
                                  <div className="text-xs font-bold text-white">{secondary.name || '—'}</div>
                                  <div className="text-micro text-slate-400">{secondary.email || ''}</div>
                                  <div className="text-micro text-slate-400">{secondary.company || ''} {secondary.role ? `· ${secondary.role}` : ''}</div>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-2 shrink-0">
                                <div className="text-micro font-black text-amber-300">{score}% match</div>
                                <button onClick={() => { setDedupMergeModal({ primary, secondary, entity: 'candidates', score }); setDedupMergeFieldChoices({}); }} className="rounded-full bg-cyan-400 px-3 py-1.5 text-micro font-black text-slate-950 hover:bg-cyan-300">Merge</button>
                                <button onClick={() => handleDedupDismiss(key)} className="rounded-full border border-white/10 px-3 py-1.5 text-micro text-slate-400 hover:text-white">Not a duplicate</button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── Merge Modal ───────────────────────────────────────────── */}
          {dedupMergeModal && (() => {
            const { primary, secondary, entity, score } = dedupMergeModal;
            const isContacts = entity === 'contacts';
            const MERGE_FIELDS = isContacts
              ? ['name', 'email', 'phone', 'role', 'companyName', 'linkedIn', 'notes', 'sentiment', 'tags']
              : ['name', 'email', 'phone', 'role', 'company', 'location', 'notes', 'source'];
            return (
              <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
                <div className="w-full max-w-2xl rounded-shell border border-white/10 bg-slate-900 p-6 shadow-2xl overflow-y-auto max-h-[90vh]">
                  <div className="mb-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm font-black text-white">Merge {isContacts ? 'Contacts' : 'Candidates'}</div>
                      <div className="text-xs text-slate-400">{score}% similarity — choose which field value to keep</div>
                    </div>
                    <button onClick={() => setDedupMergeModal(null)} className="rounded-full border border-white/10 p-2 text-slate-400 hover:text-white">✕</button>
                  </div>

                  {/* Column headers */}
                  <div className="mb-2 grid grid-cols-[1fr_auto_1fr] gap-3 text-micro font-black uppercase tracking-[0.15em]">
                    <div className="text-center text-cyan-300">Primary (kept)</div>
                    <div className="w-6" />
                    <div className="text-center text-slate-400">Secondary (removed)</div>
                  </div>

                  {/* Field rows */}
                  <div className="space-y-2">
                    {MERGE_FIELDS.map((field) => {
                      const pVal = field === 'tags' ? (Array.isArray(primary[field]) ? primary[field].join(', ') : '') : String(primary[field] || '');
                      const sVal = field === 'tags' ? (Array.isArray(secondary[field]) ? secondary[field].join(', ') : '') : String(secondary[field] || '');
                      const choice = dedupMergeFieldChoices[field] || 'primary';
                      if (!pVal && !sVal) return null;
                      return (
                        <div key={field} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 rounded-ctl border border-white/[0.06] bg-white/[0.02] p-2">
                          <button
                            onClick={() => setDedupMergeFieldChoices((p) => ({ ...p, [field]: 'primary' }))}
                            className={`rounded-ctl px-3 py-2 text-left text-xs transition ${choice === 'primary' ? 'bg-cyan-500/20 border border-cyan-400/40 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                          >
                            <div className="text-micro font-black uppercase text-slate-500 mb-0.5">{field}</div>
                            {pVal || <span className="italic text-slate-600">empty</span>}
                          </button>
                          <div className="text-micro text-slate-600 w-6 text-center">vs</div>
                          <button
                            onClick={() => setDedupMergeFieldChoices((p) => ({ ...p, [field]: 'secondary' }))}
                            className={`rounded-ctl px-3 py-2 text-left text-xs transition ${choice === 'secondary' ? 'bg-amber-500/20 border border-amber-400/40 text-white' : 'text-slate-500 hover:text-slate-300'}`}
                          >
                            <div className="text-micro font-black uppercase text-slate-500 mb-0.5">&nbsp;</div>
                            {sVal || <span className="italic text-slate-600">empty</span>}
                          </button>
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-5 flex justify-end gap-3">
                    <button onClick={() => setDedupMergeModal(null)} className="rounded-full border border-white/10 px-5 py-2.5 text-xs font-black text-slate-400 hover:text-white">Cancel</button>
                    <button
                      onClick={() => isContacts ? handleMergeContacts(primary, secondary, dedupMergeFieldChoices) : handleMergeCandidates(primary, secondary, dedupMergeFieldChoices)}
                      disabled={dedupIsMerging}
                      className="rounded-full bg-cyan-400 px-5 py-2.5 text-xs font-black text-slate-950 hover:bg-cyan-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {dedupIsMerging ? 'Merging…' : 'Merge & keep primary'}
                    </button>
                  </div>
                </div>
              </div>
            );
          })()}

          <div className="mt-5 grid gap-4 xl:grid-cols-[0.95fr_1.05fr]">
            <form onSubmit={handleSaveClientOpportunity} className="rounded-shell border border-white/10 bg-slate-950/55 p-4 sm:p-5">
              <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Lead / Opportunity Capture</div>
              <div className="mt-1 text-sm text-slate-400">Paste a prospect email, convert it into an opportunity, then close-won it into an ATS job order.</div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <label className="block sm:col-span-2">
                  <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">Prospect email / notes</div>
                  <textarea name="sourceEmail" value={clientOpportunityForm.sourceEmail} onChange={handleClientOpportunityFormChange} rows={4} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Paste an Outlook/Gmail message here to convert it into a lead or opportunity." />
                  <button type="button" onClick={handleConvertEmailToOpportunity} className="mt-2 rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-cyan-100 transition hover:bg-cyan-500/20">Convert email</button>
                </label>
                <input name="companyName" value={clientOpportunityForm.companyName} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Company" required />
                <input name="contactName" value={clientOpportunityForm.contactName} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Client contact" />
                <input name="contactEmail" type="email" value={clientOpportunityForm.contactEmail} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="contact@company.com" />
                <input name="title" value={clientOpportunityForm.title} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Opportunity / job order title" required />
                <select name="stage" value={clientOpportunityForm.stage} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">
                  {CLIENT_OPPORTUNITY_STAGES.map((stage) => <option key={stage}>{stage}</option>)}
                </select>
                <input name="fee" value={clientOpportunityForm.fee} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Fee value e.g. 85000" />
                <input name="jobType" value={clientOpportunityForm.jobType} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Permanent, Contract, Executive" />
                <input name="location" value={clientOpportunityForm.location} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Location" />
                <input name="salary" value={clientOpportunityForm.salary} onChange={handleClientOpportunityFormChange} className="rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Salary band" />
                <textarea name="billingTerms" value={clientOpportunityForm.billingTerms} onChange={handleClientOpportunityFormChange} rows={2} className="sm:col-span-2 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Billing terms, fee %, payment schedule" />
                <textarea name="complianceNeeds" value={clientOpportunityForm.complianceNeeds} onChange={handleClientOpportunityFormChange} rows={2} className="sm:col-span-2 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Compliance needs, checks, contract requirements" />
                <textarea name="requirements" value={clientOpportunityForm.requirements} onChange={handleClientOpportunityFormChange} rows={3} className="sm:col-span-2 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Role requirements carried into the ATS job order" />
              </div>
              <button type="submit" disabled={isSavingClientOpportunity} className="mt-4 rounded-full bg-cyan-400 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70">{isSavingClientOpportunity ? 'Saving...' : 'Save opportunity'}</button>
            </form>

            <div className="rounded-shell border border-white/10 bg-slate-950/55 p-4 sm:p-5">
              <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Opportunity Pipeline</div>
              <div className="mt-1 text-sm text-slate-400">Weighted deal value uses fee multiplied by historical placement probability for the job type.</div>
              <div className="mt-4 space-y-3">
                {clientCrmOpportunities.length === 0 ? (
                  <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No client opportunities yet.</div>
                ) : clientCrmOpportunities.map((opportunity) => (
                  <div key={opportunity.id} className="rounded-card border border-white/10 bg-white/[0.035] p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-bold text-white">{opportunity.title}</div>
                        <div className="mt-1 text-xs text-slate-400">{opportunity.companyName} · {opportunity.contactName || opportunity.contactEmail || 'No contact'}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-black text-white">R {Number(opportunity.weightedValue || 0).toLocaleString()}</div>
                        <div className="text-micro uppercase tracking-[0.14em] text-slate-500">{Math.round(Number(opportunity.probability || 0) * 100)}% probability</div>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      {CLIENT_OPPORTUNITY_STAGES.map((stage) => (
                        <button key={stage} type="button" onClick={() => handleMoveClientOpportunityStage(opportunity.id, stage)} className={`rounded-full border px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] transition ${opportunity.stage === stage ? 'border-cyan-300/40 bg-cyan-500/15 text-cyan-100' : 'border-white/10 bg-white/5 text-slate-400 hover:text-white'}`}>{stage}</button>
                      ))}
                    </div>
                    {opportunity.jobId && <div className="mt-3 rounded-ctl border border-emerald-400/20 bg-emerald-500/10 px-3 py-2 text-xs text-emerald-100">ATS job order generated: {opportunity.jobId}</div>}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-5 rounded-shell border border-cyan-400/20 bg-slate-950/55 p-4 sm:p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">PayFlex Billing Requests</div>
                <div className="mt-1 text-sm text-slate-400">Pending membership plan requests. Create a PayFlex billing plan, paste the payment link here, and Vision emails it to the client.</div>
              </div>
              <button type="button" onClick={() => loadBillingRequests(true)} disabled={billingRequestsLoading} className="rounded-full border border-cyan-300/20 bg-cyan-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-cyan-200 transition hover:border-cyan-300/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-70">{billingRequestsLoading ? 'Loading...' : billingRequestsLoaded ? 'Refresh' : 'Load requests'}</button>
            </div>

            {billingRequestsNotice && (
              <div className="mt-3 rounded-card border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">{billingRequestsNotice}</div>
            )}

            {billingRequestsLoaded && (
              <div className="mt-4 space-y-3">
                {billingRequests.length === 0 ? (
                  <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No billing requests yet.</div>
                ) : billingRequests.map((req) => (
                  <div key={req.id} className={`rounded-card border p-4 ${req.status === 'invoice_sent' ? 'border-emerald-400/20 bg-emerald-500/5' : 'border-amber-400/20 bg-amber-500/5'}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <div className="text-sm font-black text-white">{req.agencyName}</div>
                        <a href={`mailto:${req.ownerEmail}`} className="mt-0.5 block text-xs text-cyan-300 hover:text-cyan-200">{req.ownerEmail}</a>
                        <div className="mt-2 flex flex-wrap gap-2 text-micro">
                          <span className="rounded-full border border-white/10 bg-white/5 px-2 py-1 font-black uppercase tracking-[0.14em] text-slate-300">{req.planLabel || `${req.requestedSeatLimit} seat${req.requestedSeatLimit === 1 ? '' : 's'}`}</span>
                          <span className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-2 py-1 font-black text-emerald-200">USD ${Number(req.monthlyAmountUsd || 0).toFixed(2)}/mo</span>
                          <span className={`rounded-full border px-2 py-1 font-black uppercase tracking-[0.14em] ${req.status === 'invoice_sent' ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/25 bg-amber-500/10 text-amber-200'}`}>{req.status === 'invoice_sent' ? 'Invoice sent' : 'Pending invoice'}</span>
                          {req.adminEmailStatus ? <span className="rounded-full border border-cyan-300/20 bg-cyan-500/10 px-2 py-1 font-black uppercase tracking-[0.14em] text-cyan-100">Admin email: {req.adminEmailStatus.replaceAll('_', ' ')}</span> : null}
                          {req.clientReceiptEmailStatus ? <span className="rounded-full border border-sky-300/20 bg-sky-500/10 px-2 py-1 font-black uppercase tracking-[0.14em] text-sky-100">Receipt: {req.clientReceiptEmailStatus.replaceAll('_', ' ')}</span> : null}
                          {req.clientEmailStatus ? <span className="rounded-full border border-violet-300/20 bg-violet-500/10 px-2 py-1 font-black uppercase tracking-[0.14em] text-violet-100">Client email: {req.clientEmailStatus.replaceAll('_', ' ')}</span> : null}
                        </div>
                        {req.createdAtMs ? <div className="mt-1 text-micro text-slate-500">Requested {new Date(req.createdAtMs).toLocaleString()}</div> : null}
                        {req.status === 'invoice_sent' && req.payflexUrl ? <div className="mt-1 text-micro text-slate-500 break-all">Link: {req.payflexUrl}</div> : null}
                      </div>
                    </div>
                    {req.status !== 'invoice_sent' && (
                      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
                        <input
                          type="url"
                          placeholder="Paste PayFlex payment link (https://...)"
                          value={billingRequestPayflexInputs[req.id] || ''}
                          onChange={(e) => setBillingRequestPayflexInputs((prev) => ({ ...prev, [req.id]: e.target.value }))}
                          className="min-w-0 flex-1 rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400"
                        />
                        <button
                          type="button"
                          onClick={() => handleSendPayflexInvoice(req)}
                          disabled={billingRequestSendingId === req.id || !billingRequestPayflexInputs[req.id]}
                          className="shrink-0 rounded-full bg-cyan-400 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-70"
                        >
                          {billingRequestSendingId === req.id ? 'Sending...' : 'Send invoice to client'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-5 rounded-shell border border-white/10 bg-slate-950/55 p-4 sm:p-5">

            <div className="flex flex-wrap items-center justify-between gap-3">

              <div>

                <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Inbound Website Leads</div>

                <div className="mt-1 text-sm text-slate-400">Leads submitted from the homepage contact modal. Filter by agency/team fit and follow up from your client workflow.</div>

              </div>

              <div className="flex items-center gap-2">

                <div className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200">{filteredWebsiteLeads.length} visible</div>

                <button type="button" onClick={() => loadWebsiteLeads(true)} disabled={websiteLeadsLoading} className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-70">{websiteLeadsLoading ? 'Refreshing...' : 'Refresh'}</button>

              </div>

            </div>

            <div className="mt-4 grid gap-3 md:grid-cols-2">

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Search leads</div>

                <input value={websiteLeadFilters.search} onChange={(event) => setWebsiteLeadFilters((current) => ({ ...current, search: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Search name, email, agency, message" />

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Team size</div>

                <select value={websiteLeadFilters.teamSize} onChange={(event) => setWebsiteLeadFilters((current) => ({ ...current, teamSize: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                  <option value="">All team sizes</option>

                  {websiteLeadTeamSizeOptions.map((teamSize) => <option key={teamSize} value={teamSize}>{teamSize}</option>)}

                </select>

              </label>

            </div>

            {websiteLeadsError && (

              <div className="mt-4 rounded-card border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-100">{websiteLeadsError}</div>

            )}

            {websiteLeadActionNotice && (

              <div className="mt-4 rounded-card border border-cyan-400/30 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">{websiteLeadActionNotice}</div>

            )}

            <div className="mt-4 overflow-x-auto rounded-card border border-white/10 bg-slate-950/70">

              <table className="min-w-full text-left text-sm">

                <thead className="border-b border-white/10 bg-white/[0.03]">

                  <tr className="text-meta font-black uppercase tracking-[0.16em] text-slate-400">

                    <th className="px-4 py-3">Lead</th>

                    <th className="px-4 py-3">Agency</th>

                    <th className="px-4 py-3">Team size</th>

                    <th className="px-4 py-3">Volume</th>

                    <th className="px-4 py-3">Submitted</th>

                    <th className="px-4 py-3">Actions</th>

                  </tr>

                </thead>

                <tbody>

                  {filteredWebsiteLeads.length === 0 ? (

                    <tr>

                      <td colSpan={6} className="px-4 py-6 text-sm text-slate-500">{websiteLeadsLoading ? 'Loading website leads...' : 'No inbound website leads match these filters yet.'}</td>

                    </tr>

                  ) : filteredWebsiteLeads.map((lead) => {

                    const isCreatingClient = Boolean(websiteLeadActionState[`${lead.id}:create_client`]);
                    const isCreatingJob = Boolean(websiteLeadActionState[`${lead.id}:create_job`]);
                    const isCreatingAll = Boolean(websiteLeadActionState[`${lead.id}:create_all`]);
                    const isAssigningOwner = Boolean(websiteLeadActionState[`${lead.id}:assign_owner`]);

                    return (

                    <tr key={lead.id} className="border-t border-white/5">

                      <td className="px-4 py-3 align-top">

                        <div className="font-semibold text-white">{lead.fullName || 'Unnamed lead'}</div>

                        <a href={`mailto:${lead.workEmail}`} className="mt-1 inline-block text-xs text-cyan-200 hover:text-cyan-100">{lead.workEmail || 'No email'}</a>

                        {lead.message && <div className="mt-2 max-w-[320px] text-xs leading-5 text-slate-400">{lead.message.slice(0, 140)}{lead.message.length > 140 ? '...' : ''}</div>}

                        <div className="mt-2 flex flex-wrap gap-2 text-micro font-black uppercase tracking-[0.14em]">
                          {lead.clientCreatedAtMs ? <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2 py-1 text-emerald-200">Client created</span> : null}
                          {lead.jobId ? <span className="rounded-full border border-cyan-400/25 bg-cyan-500/10 px-2 py-1 text-cyan-100">Job created</span> : null}
                          {lead.assignedOwnerUid ? <span className="rounded-full border border-violet-400/25 bg-violet-500/10 px-2 py-1 text-violet-100">Owner: {lead.assignedOwnerName || 'Assigned'}</span> : null}
                        </div>

                      </td>

                      <td className="px-4 py-3 align-top text-slate-300">{lead.agencyName || 'Unknown agency'}</td>

                      <td className="px-4 py-3 align-top text-slate-300">{lead.teamSize || 'n/a'}</td>

                      <td className="px-4 py-3 align-top text-slate-300">{lead.monthlyVolume || 'n/a'}</td>

                      <td className="px-4 py-3 align-top text-slate-500">{lead.createdAtMs ? new Date(lead.createdAtMs).toLocaleString() : 'n/a'}</td>

                      <td className="px-4 py-3 align-top">
                        <div className="flex min-w-[250px] flex-col gap-2">
                          {Boolean(automationSettings?.createAllFromLead?.enabled) && (
                            <button type="button" onClick={() => handleWebsiteLeadAction(lead, 'create_all')} disabled={isCreatingAll || Boolean(lead.jobId)} className="rounded-full border border-emerald-400/30 bg-emerald-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-emerald-100 transition hover:bg-emerald-500/20 disabled:cursor-not-allowed disabled:opacity-60">{isCreatingAll ? 'Creating all...' : (lead.jobId ? 'Already created' : 'Create client + job')}</button>
                          )}
                          <button type="button" onClick={() => handleWebsiteLeadAction(lead, 'create_client')} disabled={isCreatingClient || Boolean(lead.clientCreatedAtMs)} className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-slate-200 transition hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-60">{isCreatingClient ? 'Creating client...' : (lead.clientCreatedAtMs ? 'Client created' : 'Create client')}</button>
                          <button type="button" onClick={() => handleWebsiteLeadAction(lead, 'create_job')} disabled={isCreatingJob || Boolean(lead.jobId)} className="rounded-full border border-cyan-400/30 bg-cyan-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-cyan-100 transition hover:bg-cyan-500/20 disabled:cursor-not-allowed disabled:opacity-60">{isCreatingJob ? 'Creating job...' : (lead.jobId ? 'Job created' : 'Create job from lead')}</button>
                          <button type="button" onClick={() => handleWebsiteLeadAction(lead, 'assign_owner')} disabled={isAssigningOwner} className="rounded-full border border-violet-400/30 bg-violet-500/10 px-3 py-2 text-micro font-black uppercase tracking-[0.14em] text-violet-100 transition hover:bg-violet-500/20 disabled:cursor-not-allowed disabled:opacity-60">{isAssigningOwner ? 'Assigning owner...' : 'Assign owner'}</button>
                        </div>
                      </td>

                    </tr>

                    );
                  })}

                </tbody>

              </table>

            </div>

          </div>

          </>

        )}

        </div>



        <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">

            <div>

              <div className="flex items-center gap-2 text-sm font-black uppercase tracking-[0.22em] text-white">

                <Briefcase size={16} className="text-cyan-300" /> Live Job Portfolio

              </div>

              <div className="mt-2 text-sm leading-6 text-slate-400">Browse the portfolio in a cleaner workspace, then open the ATS for any role from the cards below.</div>

            </div>

            <div className="flex flex-wrap gap-2">

              <button type="button" onClick={handleCopyCandidateJobApplicationLink} className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-emerald-200 transition hover:border-emerald-300/40 hover:text-emerald-100">Copy candidate jobs link</button>

              <button type="button" data-direct-demo-target="import-jobs-csv-button" onClick={handleImportJobsCsv} className={`rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white ${isDirectDemoTarget('import-jobs-csv-button') ? directDemoHighlightClass : ''}`}>Import CSV</button>

              <button type="button" onClick={() => handleExportJobAtsCsv(selectedJobPanel || selectedJob, selectedJobPanel ? selectedJobAtsSnapshot : null)} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white">Export ATS CSV</button>

            </div>

          </div>



          <div className="mt-5 grid gap-3 sm:grid-cols-3">

            <div className="rounded-shell border border-white/10 bg-slate-950/60 px-4 py-4">

              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Visible roles</div>

              <div className="mt-2 text-2xl font-black text-white">{visibleJobs.length}</div>

            </div>

            <div className="rounded-shell border border-white/10 bg-slate-950/60 px-4 py-4">

              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Visible open roles</div>

              <div className="mt-2 text-2xl font-black text-white">{visibleActiveRoles}</div>

            </div>

            <div className="rounded-shell border border-white/10 bg-slate-950/60 px-4 py-4">

              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Active filters</div>

              <div className="mt-2 text-2xl font-black text-white">{activeFiltersCount}</div>

            </div>

          </div>



          {/* Stage Funnel — clickable, filters by stage */}
          <div className="mt-5 grid grid-cols-4 gap-2 sm:grid-cols-7">
            {JOB_LIFECYCLE_STAGES.map((stage) => {
              const count = jobs.filter((j) => (j.lifecycleStage || 'brief') === stage.id).length;
              const isActive = jobPortfolioFilters.stage === stage.id;
              return (
                <button
                  key={stage.id}
                  type="button"
                  onClick={() => setJobPortfolioFilters((c) => ({ ...c, stage: isActive ? '' : stage.id }))}
                  className={`rounded-card border px-2 py-3 text-center transition ${isActive ? `${stage.border} ${stage.textColor} bg-white/[0.06]` : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20 hover:text-white'}`}
                >
                  <div className={`text-xl font-black ${isActive ? stage.textColor : 'text-white'}`}>{count}</div>
                  <div className="mt-1 text-micro font-black uppercase tracking-[0.1em]">{stage.label}</div>
                  {jobs.filter((j) => (j.lifecycleStage || 'brief') === stage.id && j.isBlocked).length > 0 && (
                    <div className="mt-0.5 text-micro text-rose-400 font-black">{jobs.filter((j) => (j.lifecycleStage || 'brief') === stage.id && j.isBlocked).length} blocked</div>
                  )}
                </button>
              );
            })}
          </div>

          <div className="mt-5 rounded-shell border border-white/10 bg-slate-950/50 p-4 sm:p-5">

            <div className="flex flex-wrap items-center justify-between gap-3">

              <div>

                <div className="text-meta font-black uppercase tracking-[0.18em] text-cyan-300">Portfolio filters</div>

                <div className="mt-1 text-sm text-slate-400">Search broadly, then narrow by status, type, company, or sort order.</div>

              </div>

              <button type="button" onClick={() => setJobPortfolioFilters({ search: '', status: '', type: '', company: '', owner: '', stage: '', sort: 'newest' })} className="rounded-full border border-white/10 bg-white/5 px-3 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white">Reset filters</button>

            </div>

            <div className="mt-5 grid gap-4 lg:grid-cols-2 2xl:grid-cols-[1.4fr_0.85fr_0.85fr_0.95fr_0.85fr]">

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Search jobs</div>

                <input value={jobPortfolioFilters.search} onChange={(event) => setJobPortfolioFilters((current) => ({ ...current, search: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Search title, company, location" />

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Status</div>

                <select value={jobPortfolioFilters.status} onChange={(event) => setJobPortfolioFilters((current) => ({ ...current, status: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                  <option value="">All statuses</option>

                  <option value="Active">Active</option>

                  <option value="Paused">Paused</option>

                  <option value="Closed">Closed</option>

                  <option value="Draft">Draft</option>

                  <option value="Removed">Removed</option>

                </select>

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Type</div>

                <select value={jobPortfolioFilters.type} onChange={(event) => setJobPortfolioFilters((current) => ({ ...current, type: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                  <option value="">All types</option>

                  <option value="Full-time">Full-time</option>

                  <option value="Contract">Contract</option>

                  <option value="Part-time">Part-time</option>

                </select>

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Company</div>

                <select value={jobPortfolioFilters.company} onChange={(event) => setJobPortfolioFilters((current) => ({ ...current, company: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                  <option value="">All companies</option>

                  {jobPortfolioCompanies.map((company) => <option key={company} value={company}>{company}</option>)}

                </select>

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Owner</div>

                <select value={jobPortfolioFilters.owner} onChange={(event) => setJobPortfolioFilters((current) => ({ ...current, owner: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                  <option value="">All owners</option>

                  {[...new Set(jobs.map((j) => j.ownerName).filter(Boolean))].sort().map((o) => <option key={o} value={o}>{o}</option>)}

                </select>

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Lifecycle stage</div>

                <select value={jobPortfolioFilters.stage} onChange={(event) => setJobPortfolioFilters((current) => ({ ...current, stage: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                  <option value="">All stages</option>

                  {JOB_LIFECYCLE_STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}

                </select>

              </label>

              <label className="block">

                <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Sort</div>

                <select value={jobPortfolioFilters.sort} onChange={(event) => setJobPortfolioFilters((current) => ({ ...current, sort: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">

                  <option value="newest">Newest first</option>

                  <option value="oldest">Oldest first</option>

                  <option value="title">Title</option>

                  <option value="company">Company</option>

                  <option value="status">Status</option>

                </select>

              </label>

            </div>

          </div>



          <div className="mt-5 flex flex-wrap items-center justify-between gap-3 text-xs text-slate-400">

            <div>{visibleJobs.length} roles visible across {jobPortfolioCompanies.length} tracked companies</div>

            {canAccess('jobs.delete') && visibleJobs.some((j) => !['removed', 'closed'].includes(String(j.status || '').toLowerCase())) && (
              <button
                type="button"
                onClick={() => setRemoveAllJobsConfirm(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-rose-500/25 bg-rose-500/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-rose-400 transition hover:border-rose-400/50 hover:bg-rose-500/15"
              >
                <Trash2 size={10} /> Remove All
              </button>
            )}

          </div>

          <div className="mt-6 grid gap-5 xl:grid-cols-2 2xl:grid-cols-3">

            {visibleJobs.length === 0 && <div className="xl:col-span-2 2xl:col-span-3 rounded-card border border-dashed border-white/10 px-4 py-6 text-sm text-slate-500">No jobs match the current filters.</div>}

            {visibleJobs.map((job) => {
              const jobCo = normalizeText(getJobCompanyName(job) || '');
              const jobContacts = contacts.filter((c) => normalizeText(c.company || c.companyName || '') === jobCo && jobCo);
              return <JobCard key={job.id} job={job} snapshot={jobCardMetrics[job.id]} onOpen={handleOpenJobAts} onCopyAiMessage={handleCopyJobOutreachMessage} onDistribute={(j) => { setDistributeJob(j); setDistributeCopied(''); setIndexingStatus('idle'); setIndexingResults([]); }} jobContacts={jobContacts} directDemoGuideHighlightOpen={isDirectDemoTarget('job-open-button')} />;
            })}

          </div>

        </div>

      </div>

    );

  };



  const handleSaveProfile = async (event) => {

    event.preventDefault();

    if (!recruiterUser?.uid) return;

    setIsSavingProfile(true);

    const agencyKey = profile.agencyKey || normalizeText(profile.agency);

    const currentMember = agencyMembers.find((member) => member.id === recruiterUser.uid);

    const nextRole = currentMember?.role || profile.accountRole || 'agent';

    const nextSeatAssigned = currentMember?.seatAssigned ?? profile.seatAssigned ?? true;

    try {

      await setDoc(userDocRef(recruiterUser.uid), {

        name: profile.name,

        agency: profile.agency,

        location: profile.location,

        title: profile.title,

        phone: profile.phone,

        email: profile.email || recruiterUser.email || '',

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await setDoc(agencyMemberDoc(agencyKey, recruiterUser.uid), {

        uid: recruiterUser.uid,

        agencyKey,

        agencyName: profile.agency,

        name: profile.name,

        email: profile.email || recruiterUser.email || '',

        updatedAt: serverTimestamp(),

      }, { merge: true });

      if (nextRole === 'owner') {

        await setDoc(agencyDocRef(agencyKey), {

          agencyKey,

          agencyName: profile.agency,

          ownerUid: recruiterUser.uid,

          agencySecret: profile.agencySecret,

          billingContactEmail: agencySettings.billingContactEmail || profile.email || recruiterUser.email || '',

          updatedAt: serverTimestamp(),

        }, { merge: true });

      }

      await recordActivity(recruiterUser.uid, 'profile_updated', `${profile.name || 'Agent'} updated their agency work profile.`, { agency: profile.agency, accountRole: nextRole });

      setPipelineNotice('Agency work profile saved.');

    } catch (error) {

      console.error('Failed to save agent profile', error);

      setPipelineNotice('Failed to save agency work profile.');

    } finally {

      setIsSavingProfile(false);

    }

  };

  const handleSaveAgencyBrand = async () => {
    if (!recruiterUser?.uid) return;
    setAgencyBrandSaving(true);
    setAgencyBrandMsg('');
    try {
      const slug = (agencyBrand.agencySlug || '').trim().toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');
      const {
        ownedAudienceEnabled,
        talentHubHeadline,
        talentHubIntro,
        ...brandFields
      } = agencyBrand;
      const brandToSave = { ...brandFields, agencySlug: slug, agencyName: profile.agency };
      await setDoc(userDocRef(recruiterUser.uid), { brand: brandToSave, updatedAt: serverTimestamp() }, { merge: true });
      if (slug) {
        const registerSlug = httpsCallable(functionsClient, 'registerAgencySlug');
        await registerSlug({ appId, agencySlug: slug, agencyName: profile.agency, brand: brandToSave }).catch((err) => {
          if (err?.code === 'functions/already-exists') {
            setAgencyBrandMsg('That slug is already taken. Try a different one.');
            throw err;
          } else {
            console.warn('registerAgencySlug failed', err);
            throw err;
          }
        });
      }
      setAgencyBrand(brandToSave);
      if (!agencyBrandMsg) setAgencyBrandMsg('✓ Brand settings saved.');
    } catch (err) {
      console.error('handleSaveAgencyBrand', err);
      setAgencyBrandMsg('Failed to save brand settings.');
    } finally {
      setAgencyBrandSaving(false);
    }
  };

  const handleAgencyBrandImageUpload = async (fieldName, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file || !recruiterUser?.uid) return;
    if (!file.type.startsWith('image/')) {
      setAgencyBrandMsg('Choose a PNG, JPG, WEBP, or SVG image.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAgencyBrandMsg('Images must be smaller than 5 MB.');
      return;
    }

    setAgencyLogoUploading(true);
    setAgencyBrandMsg('');
    try {
      const extension = (file.name.split('.').pop() || 'png').toLowerCase().replace(/[^a-z0-9]/g, '') || 'png';
      const imageRef = storageRef(storage, `profiles/${recruiterUser.uid}/branding/${fieldName}-${Date.now()}.${extension}`);
      await uploadBytes(imageRef, file, { contentType: file.type });
      const imageUrl = await getDownloadURL(imageRef);
      setAgencyBrand((current) => ({ ...current, [fieldName]: imageUrl }));
      if (fieldName === 'logoUrl') {
        const suggestions = await getLogoColorSuggestions(file);
        setAgencyLogoSuggestions(suggestions);
        setAgencyBrandMsg(suggestions.length ? 'Logo uploaded. Choose a suggested color or save your own.' : 'Logo uploaded. Choose a brand color or save your own.');
      } else {
        setAgencyBrandMsg('Agency image uploaded. Save your brand settings to publish it.');
      }
    } catch (error) {
      console.error(`Failed to upload agency brand image (${fieldName})`, error);
      setAgencyBrandMsg('Failed to upload the agency image.');
    } finally {
      setAgencyLogoUploading(false);
    }
  };

  const handleAgencyLogoUpload = (event) => handleAgencyBrandImageUpload('logoUrl', event);



  const handleSaveAgencySettings = async (event) => {

    event.preventDefault();

    if (!recruiterUser?.uid || !profile.agencyKey || agencyDetails?.ownerUid !== recruiterUser.uid) return;

    const minimumSeatLimit = Math.max(Number(agencyDetails?.activeSeatCount || 0), 1);

    const seatLimit = clampSeatLimit(agencySettings.seatLimit, 1, minimumSeatLimit);

    const currentSeatPlan = clampSeatLimit(agencyDetails?.seatLimit || 1);

    setIsUpdatingAgencySettings(true);

    try {

      if (!agencySettings.lawfulUseAccepted) {

        throw new Error('Confirm lawful recruitment and data-processing use before saving agency settings.');

      }

      await setDoc(agencyDocRef(profile.agencyKey), {

        agencyKey: profile.agencyKey,

        agencyName: profile.agency,

        ownerUid: recruiterUser.uid,

        agencySecret: profile.agencySecret,

        billingProvider: 'payflex',

        billingContactEmail: agencySettings.billingContactEmail || profile.email || recruiterUser.email || '',

        membershipRequired: true,

        compliance: {
          lawfulUseAccepted: true,
          lawfulUseAcceptedAtIso: agencyDetails?.compliance?.lawfulUseAcceptedAtIso || new Date().toISOString(),
          lawfulUseAcceptedByUid: agencyDetails?.compliance?.lawfulUseAcceptedByUid || recruiterUser.uid,
          lawfulUseVersion: 'vision-lawful-use-2026-05-20',
          prohibitedUses: ['unlawful_discrimination', 'spam', 'unauthorized_data_sharing', 'unlawful_scraping', 'processing_without_lawful_basis'],
        },

        reportingSettings: {
          schedule: {
            enabled: Boolean(agencySettings.reportingScheduleEnabled),
            frequency: String(agencySettings.reportingScheduleFrequency || 'weekly'),
            day: String(agencySettings.reportingScheduleDay || 'monday'),
            recipients: String(agencySettings.reportingScheduleRecipients || ''),
          },
          forecasting: {
            monthlyHireTarget: Math.max(1, Number(agencySettings.forecastMonthlyHireTarget || 10)),
          },
          governance: {
            agentMetricKeys: Array.isArray(agencySettings.governanceAgentMetricKeys) && agencySettings.governanceAgentMetricKeys.length > 0
              ? agencySettings.governanceAgentMetricKeys
              : ['candidate_changes', 'job_changes', 'stage_moves', 'screenings_done', 'time_to_fill', 'offer_acceptance', 'funnel_overview', 'velocity_overview', 'source_quality_overview', 'source_trend_wow'],
          },
        },

        updatedAt: serverTimestamp(),

      }, { merge: true });



      if (seatLimit !== currentSeatPlan) {

        await handleBillingPlanRequest({ id: 'settings-seat-change', title: 'Seat Change', checkoutSeats: seatLimit });

        return;

      }



      await setDoc(agencyDocRef(profile.agencyKey), {

        seatLimit,

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'agency_settings_updated', `${profile.name || 'Agency owner'} updated agency seat and billing settings.`, { agencyKey: profile.agencyKey, seatLimit });

      setAgencyNotice('Agency billing and seat settings saved.');

    } catch (error) {

      console.error('Failed to save agency settings', error);

      setAgencyNotice('Failed to save agency billing and seat settings.');

    } finally {

      setIsUpdatingAgencySettings(false);

    }

  };



  const handleToggleMemberSeat = async (member) => {

    if (!recruiterUser?.uid || !profile.agencyKey || !member || agencyDetails?.ownerUid !== recruiterUser.uid) return;

    setActiveMemberActionId(member.id);

    try {

      await runTransaction(db, async (transaction) => {

        const agencyRef = agencyDocRef(profile.agencyKey);

        const memberRef = agencyMemberDoc(profile.agencyKey, member.id);

        const agencySnapshot = await transaction.get(agencyRef);

        const memberSnapshot = await transaction.get(memberRef);

        if (!agencySnapshot.exists() || !memberSnapshot.exists()) {

          throw new Error('Agency member record could not be found.');

        }

        const agencyData = agencySnapshot.data();

        const memberData = memberSnapshot.data();

        const currentlyAssigned = Boolean(memberData.seatAssigned) && memberData.seatStatus === 'active';

        const activeSeatCount = Number(agencyData.activeSeatCount || 0);

        const seatLimit = Number(agencyData.seatLimit || 0);

        // Count actual occupied seats from the live members list as the source of truth
        const occupiedSeats = agencyMembers.filter((m) => m.seatAssigned && m.id !== member.id).length + (currentlyAssigned ? 1 : 0);
        const effectiveSeatCount = Math.max(occupiedSeats, activeSeatCount);

        if (!currentlyAssigned && effectiveSeatCount >= seatLimit) {

          throw new Error('No free paid seats remain for this agency.');

        }

        const nextAssigned = !currentlyAssigned;

        transaction.set(memberRef, {

          seatAssigned: nextAssigned,

          seatStatus: nextAssigned ? 'active' : 'inactive',

          updatedAt: serverTimestamp(),

        }, { merge: true });

        transaction.set(agencyRef, {

          activeSeatCount: Math.max(0, effectiveSeatCount + (nextAssigned ? 1 : -1)),

          updatedAt: serverTimestamp(),

        }, { merge: true });

      });

      if (member.id === recruiterUser.uid && member.seatAssigned) {

        setAgencyNotice('Your seat was revoked. You will be signed out.');

      } else {

        setAgencyNotice(`${member.name || member.email || 'Agency member'} ${member.seatAssigned ? 'lost' : 'received'} a paid seat.`);

      }

      await recordActivity(recruiterUser.uid, 'seat_assignment_updated', `${member.name || member.email || 'Agency member'} ${member.seatAssigned ? 'had their seat revoked' : 'was assigned a paid seat'}.`, { memberId: member.id, nextSeatAssigned: !member.seatAssigned });

    } catch (error) {

      console.error('Failed to update seat assignment', error);

      setAgencyNotice(error?.message || 'Failed to update seat assignment.');

    } finally {

      setActiveMemberActionId('');

    }

  };

  const handleRunMigrationDryRun = async () => {
    if (!recruiterUser?.uid) return;
    const option = MIGRATION_ENTITY_OPTIONS.find((item) => item.id === migrationEntity);
    if (option?.permission && !requireAccess(option.permission, 'You need the right workspace access to import this data.')) return;
    const file = await openCsvPicker();
    if (!file) return;
    setMigrationBusy(true);
    setMigrationNotice('');
    try {
      const rows = parseCsvText(await file.text());
      const preview = buildMigrationPreview(rows, migrationSource, migrationEntity, {
        candidates,
        companies,
        contacts,
        jobs,
        placements,
      });
      setMigrationFileName(file.name || 'migration.csv');
      setMigrationPreview({ ...preview, fileName: file.name || 'migration.csv', createdAtIso: new Date().toISOString() });
      setMigrationNotice(`Dry run complete: ${preview.records.length} importable ${migrationEntity} records detected.`);
    } catch (error) {
      console.error('Migration dry run failed', error);
      setMigrationNotice('Migration dry run failed. Check the CSV format and try again.');
    } finally {
      setMigrationBusy(false);
    }
  };

  const handleCommitMigrationPreview = async () => {
    if (!recruiterUser?.uid || !migrationPreview?.records?.length) return;
    const option = MIGRATION_ENTITY_OPTIONS.find((item) => item.id === migrationPreview.entity);
    if (option?.permission && !requireAccess(option.permission, 'You need the right workspace access to import this data.')) return;
    setMigrationBusy(true);
    setMigrationNotice('');
    const batchId = `mig-${Date.now()}`;
    const importedRecords = [];
    const nowIso = new Date().toISOString();
    try {
      let nextJobs = jobs;
      const firestoreBatch = writeBatch(db);
      migrationPreview.records.slice(0, 450).forEach((record, index) => {
        const base = {
          migrationBatchId: batchId,
          migrationSource: migrationPreview.sourceId,
          migrationSourceLabel: migrationPreview.sourceLabel,
          legacyId: record.legacyId,
          importedAtIso: nowIso,
          importedBy: recruiterUser.uid,
        };
        if (migrationPreview.entity === 'candidates') {
          const existing = record.email ? candidates.find((candidate) => normalizeText(candidate.email) === normalizeText(record.email)) : null;
          const candidateId = existing?.id || `mig-candidate-${batchId}-${index}`;
          firestoreBatch.set(recruiterCandidateDoc(recruiterUser.uid, candidateId), {
            id: candidateId,
            recruiterId: recruiterUser.uid,
            name: record.name,
            email: record.email,
            phone: record.phone,
            role: record.role || record.jobTitle,
            company: record.company,
            status: record.stage || 'applied',
            matchedJobTitle: record.jobTitle || record.role,
            source: `${record.legacySource} migration`,
            crmTimeline: arrayUnion(createCrmEvent('migration_imported', `${record.name} imported from ${record.legacySource}.`, { migrationBatchId: batchId, legacyId: record.legacyId })),
            previousSnapshot: existing || null,
            ...base,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          }, { merge: true });
          importedRecords.push({ type: 'candidate', id: candidateId, action: existing ? 'merged' : 'created', previousSnapshot: existing || null });
        }
        if (migrationPreview.entity === 'companies') {
          const companyId = createCompanyRecordId(record.name || record.company);
          const previous = companies.find((company) => company.id === companyId) || null;
          firestoreBatch.set(recruiterCompanyDoc(recruiterUser.uid, companyId), {
            id: companyId,
            name: record.name || record.company,
            companyName: record.name || record.company,
            source: `${record.legacySource} migration`,
            previousSnapshot: previous,
            ...base,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          }, { merge: true });
          importedRecords.push({ type: 'company', id: companyId, action: previous ? 'merged' : 'created', previousSnapshot: previous });
        }
        if (migrationPreview.entity === 'contacts') {
          const contactId = createContactRecordId(record.companyName || record.company || 'company', record.email || record.name || index);
          const previous = contacts.find((contact) => contact.id === contactId) || null;
          firestoreBatch.set(recruiterContactDoc(recruiterUser.uid, contactId), {
            id: contactId,
            name: record.name,
            email: record.contactEmail || record.email,
            companyName: record.companyName || record.company,
            role: record.role || 'Client contact',
            source: `${record.legacySource} migration`,
            previousSnapshot: previous,
            ...base,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          }, { merge: true });
          importedRecords.push({ type: 'contact', id: contactId, action: previous ? 'merged' : 'created', previousSnapshot: previous });
        }
        if (migrationPreview.entity === 'placements') {
          const placementId = `mig-placement-${batchId}-${index}`;
          firestoreBatch.set(recruiterPlacementDoc(recruiterUser.uid, placementId), {
            id: placementId,
            candidateName: record.candidateName,
            clientName: record.clientName || record.company,
            contactName: record.contactName,
            jobTitle: record.jobTitle || record.role,
            placementFee: Number(record.placementFee || 0),
            currency: 'ZAR',
            invoiceStatus: 'draft',
            paymentStatus: 'pending',
            source: `${record.legacySource} migration`,
            ...base,
            updatedAt: serverTimestamp(),
            createdAt: serverTimestamp(),
          }, { merge: true });
          importedRecords.push({ type: 'placement', id: placementId, action: 'created', previousSnapshot: null });
        }
      });
      if (migrationPreview.entity === 'jobs') {
        const importedJobs = migrationPreview.records.map((record, index) => sanitizeJobRecord({
          id: `mig-job-${batchId}-${index}`,
          title: record.title || record.jobTitle || 'Imported role',
          companyName: record.company,
          department: 'Imported',
          type: 'Full-time',
          status: record.stage === 'hired' ? 'Closed' : 'Active',
          publishStatus: 'Draft',
          location: record.raw?.location || 'Remote',
          salary: record.raw?.salary || 'Compensation on request',
          recruiter: profile.name || 'Recruiter',
          clientContact: record.contactName || record.contactEmail || '',
          summary: record.summary,
          requirements: record.raw?.requirements || record.summary,
          sourceLabel: `${record.legacySource} migration`,
          migrationBatchId: batchId,
          migrationSource: migrationPreview.sourceId,
          legacyId: record.legacyId,
          importedAtIso: nowIso,
          importedBy: recruiterUser.uid,
          createdAtIso: nowIso,
        }, profile.agency || '')).filter(Boolean);
        nextJobs = sanitizeJobPortfolio([...importedJobs, ...jobs], profile.agency || '');
        importedJobs.forEach((job) => importedRecords.push({ type: 'job', id: job.id, action: 'created', previousSnapshot: null }));
      }
      await firestoreBatch.commit();
      const batchRecord = {
        id: batchId,
        sourceId: migrationPreview.sourceId,
        sourceLabel: migrationPreview.sourceLabel,
        entity: migrationPreview.entity,
        fileName: migrationPreview.fileName || migrationFileName,
        status: 'imported',
        readiness: migrationPreview.readiness,
        rowsTotal: migrationPreview.rowsTotal,
        importedCount: importedRecords.length,
        importedRecords,
        previousJobPortfolio: migrationPreview.entity === 'jobs' ? jobs : null,
        duplicateBreakdown: migrationPreview.duplicateBreakdown,
        attachmentRecovery: migrationPreview.attachmentRecovery,
        createdAtIso: nowIso,
        importedBy: recruiterUser.uid,
      };
      const nextBatches = [batchRecord, ...migrationBatches].slice(0, 25);
      await setDoc(userDocRef(recruiterUser.uid), {
        ...(migrationPreview.entity === 'jobs' ? { jobPortfolio: nextJobs } : {}),
        migrationBatches: nextBatches,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      if (migrationPreview.entity === 'jobs') setJobs(nextJobs);
      setMigrationBatches(nextBatches);
      setMigrationPreview(null);
      await recordActivity(recruiterUser.uid, 'migration_imported', `${migrationPreview.sourceLabel} ${migrationPreview.entity} migration imported.`, { migrationBatchId: batchId, count: importedRecords.length });
      setMigrationNotice(`Imported ${importedRecords.length} ${migrationPreview.entity} records. Rollback is available from the migration history.`);
    } catch (error) {
      console.error('Migration import failed', error);
      setMigrationNotice('Migration import failed before completion. No batch history was written.');
    } finally {
      setMigrationBusy(false);
    }
  };

  const handleRollbackMigrationBatch = async (batchRecord) => {
    if (!recruiterUser?.uid || !batchRecord?.id) return;
    if (!requireAccess('settings.write', 'Only admins or owners can rollback migration batches.')) return;
    setMigrationBusy(true);
    try {
      const rollbackBatch = writeBatch(db);
      (batchRecord.importedRecords || []).slice(0, 450).forEach((record) => {
        const ref = record.type === 'candidate' ? recruiterCandidateDoc(recruiterUser.uid, record.id)
          : record.type === 'company' ? recruiterCompanyDoc(recruiterUser.uid, record.id)
          : record.type === 'contact' ? recruiterContactDoc(recruiterUser.uid, record.id)
          : record.type === 'placement' ? recruiterPlacementDoc(recruiterUser.uid, record.id)
          : null;
        if (!ref) return;
        if (record.previousSnapshot) rollbackBatch.set(ref, { ...record.previousSnapshot, rolledBackFromMigrationId: batchRecord.id, updatedAt: serverTimestamp() }, { merge: true });
        else rollbackBatch.delete(ref);
      });
      await rollbackBatch.commit();
      const nextBatches = migrationBatches.map((item) => item.id === batchRecord.id ? { ...item, status: 'rolled_back', rolledBackAtIso: new Date().toISOString() } : item);
      await setDoc(userDocRef(recruiterUser.uid), {
        ...(Array.isArray(batchRecord.previousJobPortfolio) ? { jobPortfolio: batchRecord.previousJobPortfolio } : {}),
        migrationBatches: nextBatches,
        updatedAt: serverTimestamp(),
      }, { merge: true });
      if (Array.isArray(batchRecord.previousJobPortfolio)) setJobs(batchRecord.previousJobPortfolio);
      setMigrationBatches(nextBatches);
      await recordActivity(recruiterUser.uid, 'migration_rolled_back', `${batchRecord.sourceLabel} ${batchRecord.entity} migration rolled back.`, { migrationBatchId: batchRecord.id });
      setMigrationNotice(`Rolled back ${batchRecord.sourceLabel} ${batchRecord.entity} migration.`);
    } catch (error) {
      console.error('Migration rollback failed', error);
      setMigrationNotice('Rollback failed. Review the migration batch and try again.');
    } finally {
      setMigrationBusy(false);
    }
  };

  const handleUpdateMemberRole = async (member, nextRole) => {
    if (!recruiterUser?.uid || !profile.agencyKey || !member) return;
    if (!requireAccess('team.editRoles', 'Only owners can change workspace roles.')) return;
    const normalizedRole = normalizeWorkspaceRole(nextRole);
    if (member.role === 'owner' || normalizedRole === 'owner') {
      setAgencyNotice('Owner role cannot be reassigned from this screen.');
      return;
    }
    setActiveMemberActionId(member.id);
    try {
      await setDoc(agencyMemberDoc(profile.agencyKey, member.id), {
        role: normalizedRole,
        roleLabel: RBAC_ROLE_PRESETS[normalizedRole]?.label || normalizedRole,
        permissions: getRolePermissions(normalizedRole),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      await recordActivity(recruiterUser.uid, 'member_role_updated', `${member.name || member.email || 'Agency member'} was changed to ${RBAC_ROLE_PRESETS[normalizedRole]?.label || normalizedRole}.`, { memberId: member.id, role: normalizedRole });
      setAgencyNotice(`${member.name || member.email || 'Agency member'} role updated to ${RBAC_ROLE_PRESETS[normalizedRole]?.label || normalizedRole}.`);
    } catch (error) {
      console.error('Failed to update member role', error);
      setAgencyNotice('Failed to update member role.');
    } finally {
      setActiveMemberActionId('');
    }
  };



  const handleSaveTimeManagement = async (event) => {

    event.preventDefault();

    if (!recruiterUser?.uid || !profile.agencyKey || agencyDetails?.ownerUid !== recruiterUser.uid) return;

    setIsSavingTimeManagement(true);

    try {

      await setDoc(agencyDocRef(profile.agencyKey), {

        timeManagement: {

          calendarDate: timeManagement.calendarDate || '',

          scheduleItems: timeManagement.scheduleItems || '',

          reminders: timeManagement.reminders || '',

          dailyGoals: timeManagement.dailyGoals || '',

        },

        updatedAt: serverTimestamp(),

      }, { merge: true });

      await recordActivity(recruiterUser.uid, 'time_management_updated', `${profile.name || 'Agency owner'} updated agency time management settings.`, { agencyKey: profile.agencyKey });

      setAgencyNotice('Agency time management saved.');

    } catch (error) {

      console.error('Failed to save time management', error);

      setAgencyNotice('Failed to save agency time management.');

    } finally {

      setIsSavingTimeManagement(false);

    }

  };

  const handleSendTeamMessage = async (event) => {
    event.preventDefault();
    if (!recruiterUser?.uid || !profile.agencyKey) return;
    const message = String(teamMessageDraft || '').trim();
    if (message.length < 2) {
      setAgencyNotice('Write a short team message before posting.');
      return;
    }
    setTeamMessageSending(true);
    try {
      const messageId = `team-msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      await setDoc(agencyTeamMessageDoc(profile.agencyKey, messageId), {
        id: messageId,
        message: message.slice(0, 1200),
        authorUid: recruiterUser.uid,
        authorName: profile.name || recruiterUser.email || 'Recruiter',
        authorEmail: recruiterUser.email || profile.email || '',
        agencyKey: profile.agencyKey,
        createdAt: serverTimestamp(),
        createdAtIso: new Date().toISOString(),
      }, { merge: true });
      setTeamMessageDraft('');
      const memberIds = Array.from(new Set([recruiterUser.uid, ...(agencyMembers || []).map((member) => member.uid || member.id)].filter(Boolean)));
      await Promise.all(memberIds.map((memberUid) => pushNotificationForUser(
        memberUid,
        'team_message',
        `Team message from ${profile.name || 'Recruiter'}`,
        message.slice(0, 180),
        { agencyKey: profile.agencyKey, messageId, tab: 'agency', agencySection: 'team', authorUid: recruiterUser.uid }
      )));
      await recordActivity(recruiterUser.uid, 'team_message_posted', `${profile.name || 'Recruiter'} posted a team message.`, { agencyKey: profile.agencyKey, messageId });
      setAgencyNotice('Team message posted.');
    } catch (error) {
      console.error('Failed to send team message', error);
      setAgencyNotice('Failed to post team message.');
    } finally {
      setTeamMessageSending(false);
    }
  };

  const handleDeleteTeamMessage = async (message) => {
    if (!recruiterUser?.uid || !profile.agencyKey || !message?.id) return;
    const canDeleteMessage = agencyDetails?.ownerUid === recruiterUser.uid || message.authorUid === recruiterUser.uid;
    if (!canDeleteMessage) {
      setAgencyNotice('Only the message author or agency owner can remove this message.');
      return;
    }
    try {
      await deleteDoc(agencyTeamMessageDoc(profile.agencyKey, message.id));
      setAgencyNotice('Team message removed.');
    } catch (error) {
      console.error('Failed to delete team message', error);
      setAgencyNotice('Failed to remove team message.');
    }
  };



  const handleBillingPlanRequest = async (plan) => {

    if (!recruiterUser?.uid || !profile.agencyKey || agencyDetails?.ownerUid !== recruiterUser.uid) return;
    if (!requireAccess('billing.write', 'Only owners can request billing changes.')) return;
    if (plan.id === 'small-agency' && (!Number.isInteger(Number(plan.checkoutSeats)) || Number(plan.checkoutSeats) < 2 || Number(plan.checkoutSeats) > 20)) {
      setAgencyNotice('Specify a Small Agency seat count from 2 to 20 before requesting the plan.');
      return;
    }

    if (!app) {

      setAgencyNotice('Firebase app is not initialized.');

      return;

    }

    const seats = plan.checkoutSeats || plan.seats || 1;

    setBillingRequestPlanId(plan.id);

    setBillingRequestSent(false);

    try {

      const functionsClient = getFunctions(app, 'us-central1');

      const requestBilling = httpsCallable(functionsClient, 'createAgencyBillingCheckout');

      const result = await requestBilling({

        agencyKey: profile.agencyKey,

        seatLimit: seats,

      });

      setBillingRequestSent(true);

      setAgencyNotice(result?.data?.message || `Your ${plan.title} plan request has been received. The Vision team will send your PayFlex invoice to your registered email shortly.`);

      await pushNotification('system', 'Seat change request sent', `Vision received your request for ${seats} paid seat${seats === 1 ? '' : 's'}. Your current workspace access remains active while the plan is updated.`, { tab: 'agency', agencySection: 'profile', agencyKey: profile.agencyKey, requestedSeatLimit: seats });

      await recordActivity(recruiterUser.uid, 'billing_plan_requested', `${profile.name || 'Agency owner'} requested the ${plan.title} plan (${seats} seat${seats === 1 ? '' : 's'}).`, { agencyKey: profile.agencyKey, planId: plan.id, seats });

    } catch (error) {

      console.error('Billing plan request failed', error);

      setAgencyNotice(error?.message || 'Could not send billing request. Please try again.');

    } finally {

      setBillingRequestPlanId(null);

    }

  };

  const handleRequestSubscriptionCancellation = async () => {
    if (!recruiterUser?.uid || !profile.agencyKey || agencyDetails?.ownerUid !== recruiterUser.uid) return;
    if (!requireAccess('billing.write', 'Only owners can request subscription cancellation.')) return;
    const confirmed = window.confirm('Request cancellation for this Vision agency? Vision will email the admin. Your workspace will stay active unless the admin confirms deletion.');
    if (!confirmed) return;
    setIsUpdatingAgencySettings(true);
    try {
      const requestCancellation = httpsCallable(functionsClient, 'requestAgencyCancellation');
      const result = await requestCancellation({ appId, agencyKey: profile.agencyKey });
      await pushNotification('system', 'Cancellation request sent', 'Vision received your cancellation request. Your workspace remains active until the admin confirms the deletion action.', { tab: 'agency', agencySection: 'profile', agencyKey: profile.agencyKey });
      await recordActivity(recruiterUser.uid, 'agency_cancellation_requested', `${profile.name || 'Agency owner'} requested subscription cancellation.`, { agencyKey: profile.agencyKey });
      setAgencyNotice(result?.data?.message || 'Cancellation request sent to Vision. Your access remains active until the admin confirms deletion.');
    } catch (error) {
      console.error('Cancellation request failed', error);
      setAgencyNotice(error?.message || 'Could not request cancellation. Please try again.');
    } finally {
      setIsUpdatingAgencySettings(false);
    }
  };



  const renderMembershipPlans = () => {

    const selectedSeatCount = Number(agencySettings.seatLimit || 1);
    const smallAgencySeatInput = String(agencySettings.seatLimit ?? '').replace(/[^\d]/g, '');
    const parsedSmallAgencySeatCount = Number.parseInt(smallAgencySeatInput, 10);
    const smallAgencySeatIsValid = Number.isInteger(parsedSmallAgencySeatCount) && parsedSmallAgencySeatCount >= 2 && parsedSmallAgencySeatCount <= 20;
    const smallAgencySeatCount = smallAgencySeatIsValid ? parsedSmallAgencySeatCount : null;

    const membershipStatus = String(agencyDetails?.subscriptionStatus || agencyDetails?.status || 'inactive');
    const paymentPlans = [
      {
        id: 'solo-recruiter',
        seats: 1,
        checkoutSeats: 1,
        title: 'Solo Recruiter',
        label: '1 member',
        monthlyPrice: `USD $${SOLO_RECRUITER_PRICE_USD.toFixed(2)} / month`,
        summary: 'For a solo recruiter who wants Vision to feel serious from day one without paying team-seat pricing.',
        cta: 'Request solo plan',
        features: [
          'One recruiter workspace for jobs, candidates, clients, and placements',
          'AI CV intake, parsing, screening, and candidate confidence signals',
          'Candidate portal, public career page, and role-watch alerts',
          'Manual job injection and public application routing that works today',
          'Revenue tracking, invoice-ready placement records, and CSV exports',
          'Upgrade into team controls when you add your next recruiter',
        ],
      },
      {
        id: 'small-agency',
        seats: smallAgencySeatCount || 2,
        checkoutSeats: smallAgencySeatCount,
        title: 'Small Agency Launch',
        label: '2-20 recruiters',
        monthlyPrice: `USD $${PRICE_PER_USER_USD.toFixed(2)} / user / month`,
        perUserPrice: `USD $${PRICE_PER_USER_USD.toFixed(2)} per recruiter`,
        summary: 'Launch with a workspace that makes a small agency feel focused, credible, and ready for every client brief.',
        cta: 'Request agency plan',
        features: [
          'A cleaner recruiter CRM and live hiring pipeline',
          'AI CV intake, parsing, screening, and candidate confidence signals',
          'Candidate portal, branded career pages, and role-watch alerts',
          'Manual job injection and public application routing that works today',
          'Companies, contacts, placements, revenue, invoices, and CSV exports',
          'Owner, admin, recruiter, sales, finance, and limited-access roles',
        ],
      },
      {
        id: 'agency-scale',
        seats: 21,
        title: 'Agency Scale',
        label: 'Bigger agencies',
        locked: true,
        popular: true,
        monthlyPrice: 'Custom quote',
        summary: 'For growing agencies that want Vision to become the rhythm of sales, sourcing, follow-up, and job distribution.',
        cta: 'Reserve scale access',
        features: [
          'Everything in Small Agency Launch',
          'Gmail and Outlook sync for email logging, replies, and contact history',
          'Automated posting to job boards with applications routed back into Vision',
          'Advanced recruiter ownership, sales handoff, and account workflow controls',
          'Higher-volume candidate, job, company, and activity operating limits',
        ],
      },
      {
        id: 'enterprise',
        seats: 51,
        title: 'Enterprise Operating System',
        label: 'Enterprise agencies',
        locked: true,
        monthlyPrice: 'Custom implementation',
        summary: 'For agencies that want to move their history, teams, controls, and reporting into Vision with confidence.',
        cta: 'Plan enterprise rollout',
        features: [
          'Everything in Agency Scale',
          'Migration command center, duplicate handling, rollback, and readiness scoring',
          'Enterprise RBAC, audit controls, finance visibility, and export governance',
          'Dedicated onboarding, data cleanup, and implementation support',
          'Future-ready integration path for email, job boards, reporting, and client workflows',
        ],
      },
    ];

    const membershipPlanPresentation = {
      'solo-recruiter': {
        Icon: User,
        titleLineOne: 'Solo Recruiter:',
        titleLineTwo: 'Focus',
      },
      'small-agency': {
        Icon: Briefcase,
        titleLineOne: 'Small Agency:',
        titleLineTwo: 'Scale',
      },
      'agency-scale': {
        Icon: Sparkles,
        titleLineOne: 'Agency Scale:',
        titleLineTwo: 'Growth',
      },
      enterprise: {
        Icon: ShieldCheck,
        titleLineOne: 'Enterprise OS:',
        titleLineTwo: 'Control',
      },
    };

    const renderMembershipPlanVisual = (planId) => {
      if (planId === 'solo-recruiter') {
        return (
          <div aria-hidden="true" className="relative mx-auto h-24 w-full max-w-[180px]">
            <div className="absolute bottom-4 left-7 right-7 h-2 rounded-full bg-cyan-100/18" />
            <div className="absolute bottom-6 left-9 right-9 h-7 rounded-t border border-cyan-100/20 bg-[#214858]/80" />
            <div className="absolute bottom-2 left-10 h-4 w-1 bg-cyan-100/18" />
            <div className="absolute bottom-2 right-10 h-4 w-1 bg-cyan-100/18" />
            <div className="absolute bottom-6 left-[76px] h-10 w-7 rounded-t-xl border border-white/15 bg-slate-500/55" />
            <div className="absolute bottom-1 left-[88px] h-6 w-px bg-white/20" />
            <div className="absolute bottom-0 left-[72px] h-px w-9 bg-white/20" />
            <div className="absolute bottom-[42px] right-11 h-4 w-8 rounded-sm border border-cyan-100/20 bg-cyan-300/10" />
            <div className="absolute left-8 top-7 h-3 w-1 rounded-full bg-emerald-200/60" />
            <div className="absolute left-7 top-6 h-1 w-3 rotate-45 rounded-full bg-emerald-200/45" />
          </div>
        );
      }

      if (planId === 'small-agency') {
        return (
          <div aria-hidden="true" className="relative mx-auto flex h-24 w-full max-w-[180px] items-end justify-center gap-2 pb-4">
            <div className="absolute bottom-4 left-5 right-5 h-2 rounded-full bg-cyan-100/14" />
            {[['h-11', 'bg-cyan-200/60'], ['h-14', 'bg-slate-200/70'], ['h-16', 'bg-emerald-200/55'], ['h-12', 'bg-sky-200/55']].map(([height, tone], index) => (
              <div key={`${height}-${index}`} className="relative z-10 flex flex-col items-center">
                <span className={`h-4 w-4 rounded-full border border-white/20 ${tone}`} />
                <span className={`mt-1 w-6 rounded-t-xl border border-white/15 bg-[#295365]/80 ${height}`} />
              </div>
            ))}
            <div className="absolute bottom-8 left-8 right-8 h-8 rounded border border-cyan-100/15 bg-cyan-200/[.06]" />
          </div>
        );
      }

      if (planId === 'agency-scale') {
        return (
          <div aria-hidden="true" className="relative mx-auto h-24 w-full max-w-[180px] overflow-hidden rounded-ctl border border-white/10 bg-black/10">
            <div className="absolute inset-0 opacity-70 [background-image:linear-gradient(rgba(186,230,253,.12)_1px,transparent_1px),linear-gradient(90deg,rgba(186,230,253,.12)_1px,transparent_1px)] [background-size:36px_24px]" />
            <div className="absolute bottom-5 left-5 h-px w-8 rotate-[-35deg] bg-cyan-200 shadow-[0_0_14px_rgba(103,232,249,.6)]" />
            <div className="absolute bottom-[40px] left-[44px] h-px w-9 rotate-[18deg] bg-cyan-200 shadow-[0_0_14px_rgba(103,232,249,.6)]" />
            <div className="absolute bottom-[34px] left-[78px] h-px w-8 rotate-[-31deg] bg-cyan-200 shadow-[0_0_14px_rgba(103,232,249,.6)]" />
            <div className="absolute bottom-[53px] left-[107px] h-px w-12 rotate-[-43deg] bg-cyan-200 shadow-[0_0_14px_rgba(103,232,249,.6)]" />
            <div className="absolute bottom-5 left-5 right-5 h-12 bg-gradient-to-t from-cyan-300/14 to-transparent [clip-path:polygon(0_100%,0_70%,24%_25%,47%_45%,67%_8%,100%_0,100%_100%)]" />
          </div>
        );
      }

      return (
        <div aria-hidden="true" className="relative mx-auto h-24 w-full max-w-[180px] overflow-hidden rounded-ctl border border-white/12 bg-[#071620]/80 p-2">
          <div className="flex h-full gap-2">
            <div className="w-8 rounded-ctl border border-white/10 bg-white/[.05] p-1.5">
              <div className="h-1 w-4 rounded bg-cyan-100/30" />
              <div className="mt-2 space-y-1">
                <div className="h-1 rounded bg-white/15" />
                <div className="h-1 rounded bg-white/10" />
                <div className="h-1 rounded bg-white/10" />
              </div>
            </div>
            <div className="min-w-0 flex-1 rounded-ctl border border-cyan-100/12 bg-white/[.04] p-2">
              <div className="grid grid-cols-3 gap-1">
                <span className="h-3 rounded bg-cyan-200/20" />
                <span className="h-3 rounded bg-emerald-200/18" />
                <span className="h-3 rounded bg-white/12" />
              </div>
              <div className="mt-2 flex h-10 items-end gap-1 rounded bg-black/15 p-1">
                {[12, 24, 18, 30, 25].map((height) => (
                  <span key={height} style={{ height: `${height}px` }} className="w-full rounded-sm bg-cyan-200/35" />
                ))}
              </div>
            </div>
          </div>
        </div>
      );
    };



    return (

      <div className="relative min-h-screen overflow-hidden bg-[#06141d] text-white">

        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_82%_25%,rgba(45,212,191,.14),transparent_26%),linear-gradient(135deg,rgba(5,19,28,.98),rgba(6,25,38,.96)_45%,rgba(4,14,23,.98))]" />
        <div className="pointer-events-none absolute inset-0 opacity-60 [background-image:linear-gradient(rgba(125,211,252,.09)_1px,transparent_1px),linear-gradient(90deg,rgba(125,211,252,.09)_1px,transparent_1px)] [background-size:46px_46px]" />
        <div className="pointer-events-none absolute inset-y-0 left-0 w-20 border-r border-cyan-100/10 bg-[#071a25]/80 max-lg:hidden" />
        <div className="pointer-events-none absolute left-4 top-5 hidden h-11 w-11 rounded-b-lg border border-cyan-100/20 bg-[#0b2633]/95 shadow-[0_18px_46px_rgba(0,0,0,.32)] max-lg:hidden lg:grid lg:grid-cols-2 lg:gap-1 lg:p-3">
          <span className="rounded-sm bg-cyan-200/50" />
          <span className="rounded-sm bg-cyan-100/18" />
          <span className="rounded-sm bg-cyan-100/18" />
          <span className="rounded-sm bg-cyan-200/35" />
        </div>

        <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 py-7 sm:px-6 lg:px-8">

          <header className="rounded-card border border-cyan-100/20 bg-[#102a38]/75 p-4 shadow-[inset_0_1px_0_rgba(255,255,255,.08),0_24px_80px_rgba(0,0,0,.35)] backdrop-blur-xl sm:p-5">

            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

              <div className="min-w-0">

                <div className="text-micro font-black uppercase tracking-[0.24em] text-cyan-200/70">Vision Membership Required</div>

                <h1 className="mt-2 max-w-[600px] text-[2rem] font-black leading-[1.02] text-[#e4f3fa] drop-shadow-[0_2px_0_rgba(0,0,0,.38)] sm:text-[2.45rem]">Give your agency a sharper operating room.</h1>

                <p className="mt-2 max-w-2xl text-sm leading-5 text-slate-200 sm:leading-6">

                  Your agency account is ready. Activate the small-agency plan to start running jobs, candidates, clients, and revenue from one calm workspace; scale and enterprise plans add Gmail/Outlook sync and job board distribution as they come online.

                </p>

              </div>

              <div className="flex shrink-0 flex-col gap-3 lg:items-end">

                <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-400">
                  Membership status: <span className="text-emerald-200">{membershipStatus}</span>
                </div>

                <div className="rounded-full border border-cyan-100/15 bg-white/[.06] px-5 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-slate-100 shadow-[inset_0_1px_0_rgba(255,255,255,.08)]">
                  {profile.agency || 'Activate your agency workspace'}
                </div>

                <button type="button" onClick={() => { setMembershipPreviewEnabled(false); clearAuthFormSensitiveFields({ keepEmail: false }); auth?.logout?.(); }} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-200 transition hover:border-white/20 hover:text-white">

                  <LogOut size={14} /> Sign out

                </button>

              </div>

            </div>

          </header>



          <main className="mt-5 flex-1 space-y-5">

            <div className="rounded-card border border-cyan-100/14 bg-[#0e2633]/65 px-4 py-3 text-sm leading-6 text-slate-200 shadow-[inset_0_1px_0_rgba(255,255,255,.05)] backdrop-blur">

              Membership status: <span className="font-black uppercase">{membershipStatus}</span>. Once you request a plan, the Vision team will email you a PayFlex payment link to activate your workspace.

            </div>



            {agencyNotice && (

              <div className="rounded-shell border border-cyan-300/20 bg-cyan-300/10 p-5 text-sm text-cyan-100">{agencyNotice}</div>

            )}





            <section className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">

              {paymentPlans.map((plan) => {

                const selected = selectedSeatCount === plan.seats;
                const featured = Boolean(plan.popular);
                const locked = Boolean(plan.locked);
                const presentation = membershipPlanPresentation[plan.id];
                const PlanIcon = presentation.Icon;

                return (

                  <article key={plan.id} className={`group relative flex min-h-[560px] flex-col overflow-hidden rounded-card border p-4 shadow-[inset_0_1px_0_rgba(255,255,255,.07),0_22px_72px_rgba(0,0,0,.28)] transition duration-300 ${locked ? 'opacity-90' : 'hover:shadow-[inset_0_1px_0_rgba(255,255,255,.08),0_28px_82px_rgba(6,182,212,.17)]'} ${featured ? 'border-cyan-100/35 bg-[linear-gradient(180deg,rgba(31,81,96,.9),rgba(10,30,42,.88))]' : selected && !locked ? 'border-cyan-100/30 bg-[#133444]/88 ring-1 ring-cyan-200/16' : 'border-cyan-100/20 bg-[#102b3a]/78 hover:border-cyan-100/36'}`}>

                    <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(150deg,rgba(255,255,255,.08),transparent_24%),radial-gradient(circle_at_90%_18%,rgba(94,234,212,.14),transparent_27%)] opacity-90" />
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-100/60 to-transparent" />
                    {featured && (
                      <div className="absolute right-3 top-3 z-10 rounded-full border border-cyan-100/24 bg-cyan-200/14 px-2.5 py-1 text-micro font-black uppercase tracking-[0.14em] text-cyan-100">
                        Most Popular
                      </div>
                    )}

                    <div className="relative flex min-h-[147px] flex-col">

                      <div className="flex items-start justify-between gap-3">

                        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-ctl border border-cyan-100/18 bg-white/[.06] text-cyan-50">
                          <PlanIcon size={17} strokeWidth={2.4} />
                        </div>

                        {selected && <span className="rounded-full border border-cyan-200/20 bg-cyan-300/10 px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] text-cyan-100">Selected</span>}

                      </div>

                      <h2 className="mt-3 text-xl font-black leading-[1.02] text-[#e6f5fb] drop-shadow-[0_2px_0_rgba(0,0,0,.38)]">
                        <span className="block">{presentation.titleLineOne}</span>
                        <span className="block">{presentation.titleLineTwo}</span>
                      </h2>

                      <p className="mt-2 text-meta leading-4 text-slate-100">{plan.summary}</p>

                    </div>

                    <div className="relative mt-2 flex h-28 items-center rounded-ctl border border-cyan-100/8 bg-black/[.08] px-2">
                      {renderMembershipPlanVisual(plan.id)}
                    </div>

                    {locked ? (
                      <div className="relative mt-4 rounded-ctl border border-white/10 bg-white/[.035] px-3 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-300">
                        {plan.monthlyPrice}
                      </div>
                    ) : (
                      <div className="relative mt-4 whitespace-pre-line text-xl font-black leading-[1.05] text-white drop-shadow-[0_2px_0_rgba(0,0,0,.38)]">{plan.monthlyPrice.replace(' / ', ' /\n')}</div>
                    )}

                    <div className="relative mt-2 flex flex-wrap items-center gap-2 text-micro font-black uppercase tracking-[0.16em] text-slate-400">
                      <span>{locked ? 'Vision future plan' : 'PayFlex launch plan'}</span>
                      <span className="h-1 w-1 rounded-full bg-cyan-200/50" />
                      <span>{plan.label}</span>
                    </div>

                    {plan.id === 'small-agency' && (
                      <label className="relative mt-4 block rounded-ctl border border-cyan-200/15 bg-slate-950/35 p-3">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <span className="text-micro font-black uppercase tracking-[0.18em] text-cyan-100">Agency seats</span>
                          <span className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">2-20</span>
                        </div>
                        <div className="grid items-center gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
                          <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            aria-label="Small agency seat count"
                            value={smallAgencySeatInput}
                            onChange={(event) => {
                              const nextSeats = event.target.value.replace(/[^\d]/g, '');
                              setAgencySettings((current) => ({ ...current, seatLimit: nextSeats }));
                            }}
                            className="w-full rounded-ctl border border-white/10 bg-slate-950/80 px-3 py-2 text-sm font-black text-white outline-none transition focus:border-cyan-300"
                            placeholder="Type seats"
                          />
                          <div className={`min-w-[5.5rem] rounded-ctl border px-3 py-2 text-center shadow-[inset_0_1px_0_rgba(255,255,255,.08)] ${smallAgencySeatIsValid ? 'border-cyan-100/20 bg-cyan-100/[.08]' : 'border-amber-200/20 bg-amber-200/[.08]'}`}>
                            <div className={`font-serif text-[2rem] font-semibold italic leading-none ${smallAgencySeatIsValid ? 'text-cyan-50' : 'text-amber-100'}`}>{smallAgencySeatInput || '--'}</div>
                            <div className="mt-1 text-micro font-black uppercase tracking-[0.16em] text-slate-400">Seats</div>
                          </div>
                        </div>
                        <div className={`mt-2 text-meta ${smallAgencySeatIsValid || !smallAgencySeatInput ? 'text-slate-400' : 'text-amber-100'}`}>
                          {smallAgencySeatIsValid || !smallAgencySeatInput
                            ? `${plan.perUserPrice}. Specify the number of paid seats for this agency.`
                            : 'Use a seat count from 2 to 20 for the Small Agency plan.'}
                        </div>
                      </label>
                    )}

                    <ul className={`relative mt-4 space-y-2.5 text-meta leading-4 ${locked ? 'text-slate-300' : 'text-slate-100'}`}>

                      {plan.features.map((feature) => (
                        <li key={feature} className="flex items-center gap-3">
                          <span className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-cyan-100">
                            <Check size={15} strokeWidth={2.4} />
                          </span>
                          <span>{feature}</span>
                        </li>
                      ))}

                    </ul>

                    {!locked && billingRequestSent && billingRequestPlanId === null && (
                      <div className="relative mt-5 rounded-ctl border border-emerald-300/20 bg-emerald-300/10 px-4 py-3 text-xs text-emerald-200">
                        Request received — we will send your PayFlex invoice shortly.
                      </div>
                    )}

                    {!locked && (
                      <div className="relative mt-4 flex items-center gap-2 text-micro text-slate-500">
                        <span className="inline-block h-1.5 w-1.5 rounded-full bg-cyan-400" />
                        Billed via <span className="font-black text-cyan-300">PayFlex</span> — we will send your invoice link by email
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={() => locked ? openLeadModal() : handleBillingPlanRequest(plan)}
                      disabled={billingRequestPlanId === plan.id}
                      className={`relative mt-auto w-full rounded-full border px-4 py-4 text-xs font-black text-white shadow-[inset_0_1px_0_rgba(255,255,255,.38),0_16px_35px_rgba(0,0,0,.28)] transition duration-300 disabled:cursor-not-allowed disabled:opacity-70 ${locked ? 'border-white/10 bg-white/[.06] text-slate-200 hover:border-cyan-100/40 hover:text-white' : featured ? 'border-cyan-100/60 bg-gradient-to-r from-sky-200 via-cyan-200 to-cyan-500 text-slate-950 hover:shadow-[0_0_36px_rgba(34,211,238,.42)]' : 'border-white/18 bg-gradient-to-r from-white/22 via-white/14 to-cyan-200/34 hover:border-cyan-100/55 hover:shadow-[0_0_34px_rgba(125,211,252,.24)]'}`}
                    >
                      {billingRequestPlanId === plan.id ? 'Sending request...' : plan.cta}
                    </button>

                  </article>

                );

              })}



              <article className="hidden rounded-shell border border-dashed border-white/15 bg-slate-950/50 p-5 sm:p-6">

                <div className="text-xs font-black uppercase tracking-[0.24em] text-slate-400">Temporary Access</div>

                <h2 className="mt-3 text-2xl font-black text-white">Preview the app</h2>

                <div className="mt-5 text-3xl font-black text-white">No membership</div>

                <div className="mt-2 text-sm text-slate-500">Owner-only test access</div>

                <p className="mt-4 text-sm leading-7 text-slate-300">Use this only to inspect the current workspace. It does not activate membership, reserve seats, or unlock Vision for other agents.</p>

                <ul className="mt-5 space-y-2 text-sm text-slate-300">

                  <li>Temporary session access for the signed-in owner</li>

                  <li>No billing or seat activation</li>

                  <li>Membership gate returns after sign out</li>

                  <li>Production access still requires a paid monthly member plan</li>

                </ul>

                <button type="button" onClick={() => { setMembershipPreviewEnabled(true); setPipelineNotice('Preview mode enabled for this session only. Vision membership is still required for normal access.'); }} className="mt-6 w-full rounded-full border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-cyan-100 transition hover:border-cyan-300/30 hover:bg-cyan-500/20">

                  Enter app for now

                </button>

              </article>

            </section>



            <section className="rounded-shell border border-white/10 bg-[#123258]/70 p-5 shadow-[0_20px_55px_rgba(0,0,0,.18)] sm:p-6">

              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Member Access Policy</div>

              <div className="mt-4 grid gap-4 lg:grid-cols-2">

                <div className="rounded-card bg-slate-950/60 p-4">

                  <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Who gets access</div>

                  <div className="mt-2 text-lg font-bold text-white">Only agencies with an active paid monthly Vision membership</div>

                </div>

                <div className="rounded-card bg-slate-950/60 p-4">

                  <div className="text-meta uppercase tracking-[0.18em] text-slate-500">What happens if unpaid</div>

                  <div className="mt-2 text-lg font-bold text-white">Workspace access stays locked until PayFlex confirms membership activation</div>

                </div>

              </div>

            </section>

          </main>

        </div>

      </div>

    );

  };



  const renderAgency = () => {

    const agencySeatLimit = Number(agencyDetails?.seatLimit || 0);

    const occupiedSeats = agencyMembers.filter((m) => m.seatAssigned).length;
    const agencyActiveSeats = Math.max(occupiedSeats, Number(agencyDetails?.activeSeatCount || 0));

    const agencySeatsAvailable = Math.max(agencySeatLimit - agencyActiveSeats, 0);

    const selectedSeatLimit = clampSeatLimit(agencySettings.seatLimit, Math.max(agencySeatLimit, 1), Math.max(agencyActiveSeats, 1));

    const seatLimitChanged = selectedSeatLimit !== clampSeatLimit(agencySeatLimit || 1);

    const currentMember = agencyMembers.find((member) => member.id === recruiterUser?.uid);

    const isAgencyOwner = Boolean(recruiterUser?.uid) && agencyDetails?.ownerUid === recruiterUser.uid && currentMember?.role === 'owner';

    const agentVisibleMetricKeys = Array.isArray(agencySettings.governanceAgentMetricKeys) && agencySettings.governanceAgentMetricKeys.length > 0
      ? agencySettings.governanceAgentMetricKeys
      : ['candidate_changes', 'job_changes', 'stage_moves', 'screenings_done', 'time_to_fill', 'offer_acceptance', 'funnel_overview', 'velocity_overview', 'source_quality_overview', 'source_trend_wow', 'conversion_rates', 'dei_funnel', 'agency_performance', 'segmentation'];

    const canViewReportingMetric = (metricKey) => isAgencyOwner || agentVisibleMetricKeys.includes(metricKey);

    const reportingRangeOptions = [

      { days: 7, label: '7D' },

      { days: 30, label: '30D' },

      { days: 90, label: '90D' },

    ];

    const selectedRecruiterId = isAgencyOwner ? reportingRecruiterFilter : (recruiterUser?.uid || 'all');

    const selectedRecruiterMember = agencyMembers.find((member) => member.id === selectedRecruiterId);

    const nowMs = Date.now();

    const customStartMs = reportingCustomStart ? new Date(`${reportingCustomStart}T00:00:00`).getTime() : Number.NaN;

    const customEndMs = reportingCustomEnd ? new Date(`${reportingCustomEnd}T23:59:59`).getTime() : Number.NaN;

    const hasCustomRange = reportingUseCustomRange && Number.isFinite(customStartMs) && Number.isFinite(customEndMs);

    const rangeStartMs = hasCustomRange ? Math.min(customStartMs, customEndMs) : nowMs - (reportingRangeDays * 86400000);

    const rangeEndMs = hasCustomRange ? Math.max(customStartMs, customEndMs) : nowMs;

    const rangeSpanMs = Math.max(1, (rangeEndMs - rangeStartMs) + 1);

    const effectiveRangeDays = Math.max(1, Math.ceil(rangeSpanMs / 86400000));

    const previousRangeStartMs = rangeStartMs - rangeSpanMs;

    const previousRangeEndMs = rangeStartMs - 1;



    const getTimeMs = (...values) => {

      for (const value of values) {

        if (typeof value === 'number' && Number.isFinite(value)) return value;

        if (typeof value === 'string') {

          const parsed = Date.parse(value);

          if (Number.isFinite(parsed)) return parsed;

        }

      }

      return null;

    };

    const isWithinCurrentRange = (ms) => Number.isFinite(ms) && ms >= rangeStartMs && ms <= rangeEndMs;

    const isWithinPreviousRange = (ms) => reportingCompareEnabled && Number.isFinite(ms) && ms >= previousRangeStartMs && ms <= previousRangeEndMs;

    const matchesRecruiter = (recruiterId) => selectedRecruiterId === 'all' || recruiterId === selectedRecruiterId;

    const matchesJobRecruiter = (job) => {

      if (selectedRecruiterId === 'all') return true;

      if (job?.recruiterId === selectedRecruiterId) return true;

      if (!selectedRecruiterMember) return false;

      const selectedName = normalizeText(selectedRecruiterMember.name || selectedRecruiterMember.email || '');

      const jobRecruiterName = normalizeText(job?.recruiter || '');

      return Boolean(selectedName) && selectedName === jobRecruiterName;

    };



    const filteredCandidates = candidates.filter((candidate) => {

      if (!matchesRecruiter(candidate.recruiterId || recruiterUser?.uid)) return false;

      const ms = getTimeMs(candidate.lastActivityAt, candidate.submittedAt, candidate.createdAtIso);

      return isWithinCurrentRange(ms);

    });

    const filteredJobs = jobs.filter((job) => {

      if (!matchesJobRecruiter(job)) return false;

      const ms = getTimeMs(job.createdAtIso, job.updatedAtIso, job.lastOutreach);

      return isWithinCurrentRange(ms);

    });

    const filteredActivityLog = activityLog.filter((item) => {

      if (!matchesRecruiter(item.recruiterId || recruiterUser?.uid)) return false;

      const ms = getTimeMs(item.createdAtMs, item.createdAtIso);

      return isWithinCurrentRange(ms);

    });

    const previousCandidates = candidates.filter((candidate) => {

      if (!matchesRecruiter(candidate.recruiterId || recruiterUser?.uid)) return false;

      const ms = getTimeMs(candidate.lastActivityAt, candidate.submittedAt, candidate.createdAtIso);

      return isWithinPreviousRange(ms);

    });

    const previousJobs = jobs.filter((job) => {

      if (!matchesJobRecruiter(job)) return false;

      const ms = getTimeMs(job.createdAtIso, job.updatedAtIso, job.lastOutreach);

      return isWithinPreviousRange(ms);

    });

    const previousActivityLog = activityLog.filter((item) => {

      if (!matchesRecruiter(item.recruiterId || recruiterUser?.uid)) return false;

      const ms = getTimeMs(item.createdAtMs, item.createdAtIso);

      return isWithinPreviousRange(ms);

    });



    const responseList = Object.values(screeningResponses || {});

    const filteredScreeningCompleted = responseList.filter((response) => {

      if (!matchesRecruiter(response?.recruiterId || recruiterUser?.uid)) return false;

      const ms = getTimeMs(response?.submittedAt, response?.createdAtIso, response?.completedAtIso);

      return isWithinCurrentRange(ms);

    });

    const previousScreeningCompleted = responseList.filter((response) => {

      if (!matchesRecruiter(response?.recruiterId || recruiterUser?.uid)) return false;

      const ms = getTimeMs(response?.submittedAt, response?.createdAtIso, response?.completedAtIso);

      return isWithinPreviousRange(ms);

    });



    const stageMovedAtMs = (candidate, stageId) => {

      const history = Array.isArray(candidate?.stageHistory) ? candidate.stageHistory : [];

      const hit = history.find((entry) => entry.stageId === stageId);

      return hit ? getTimeMs(hit.movedAt, hit.createdAtIso) : null;

    };

    const outcomeKpis = (pool, useCurrent) => {

      const ttfSamples = [];

      const acceptedRows = [];

      let offerCount = 0;

      let acceptedOfferCount = 0;

      const inRange = useCurrent ? isWithinCurrentRange : isWithinPreviousRange;

      pool.forEach((candidate) => {

        const submittedMs = getTimeMs(candidate.submittedAt, candidate.createdAtIso);

        const offerMs = stageMovedAtMs(candidate, 'offer');

        const hiredMs = stageMovedAtMs(candidate, 'hired') || (candidate.status === 'hired' ? getTimeMs(candidate.lastActivityAt, candidate.updatedAt, candidate.submittedAt) : null);

        if (offerMs && inRange(offerMs)) offerCount += 1;

        if (hiredMs && offerMs && inRange(hiredMs)) {

          acceptedOfferCount += 1;

          acceptedRows.push({

            id: candidate.id,

            title: candidate.name || 'Candidate',

            subtitle: `${candidate.role || 'Role'}  -  ${candidate.company || 'Company'}`,

            meta: new Date(hiredMs).toLocaleString(),

          });

        }

        if (submittedMs && hiredMs && inRange(hiredMs)) {

          const days = (hiredMs - submittedMs) / 86400000;

          if (days >= 0 && days < 365) {

            ttfSamples.push({

              id: candidate.id,

              days,

              title: candidate.name || 'Candidate',

              subtitle: `${candidate.role || 'Role'}  -  ${candidate.company || 'Company'}`,

              meta: `${days.toFixed(1)}d to hire`,

            });

          }

        }

      });

      const avgTimeToFill = ttfSamples.length ? (ttfSamples.reduce((sum, sample) => sum + sample.days, 0) / ttfSamples.length) : null;

      const offerAcceptanceRate = offerCount > 0 ? (acceptedOfferCount / offerCount) * 100 : null;

      return { avgTimeToFill, offerAcceptanceRate, ttfSamples: ttfSamples.sort((a, b) => a.days - b.days), acceptedRows };

    };

    const currentOutcomeKpis = outcomeKpis(filteredCandidates, true);

    const previousOutcomeKpis = outcomeKpis(previousCandidates, false);



    const candidateChangeCount = filteredActivityLog.filter((item) => String(item.type || '').includes('candidate')).length;

    const previousCandidateChangeCount = previousActivityLog.filter((item) => String(item.type || '').includes('candidate')).length;

    const jobChangeCount = filteredActivityLog.filter((item) => String(item.type || '').includes('job')).length;

    const previousJobChangeCount = previousActivityLog.filter((item) => String(item.type || '').includes('job')).length;

    const stageMoveCount = filteredActivityLog.filter((item) => item.type === 'candidate_stage_moved').length;

    const previousStageMoveCount = previousActivityLog.filter((item) => item.type === 'candidate_stage_moved').length;



    const toDelta = (value, previous) => {

      if (!reportingCompareEnabled) return { deltaPct: 0, deltaLabel: 'Compare off' };

      if (value == null || previous == null) return { deltaPct: 0, deltaLabel: 'No baseline' };

      const deltaPct = previous > 0 ? ((value - previous) / previous) * 100 : (value > 0 ? 100 : 0);

      return { deltaPct, deltaLabel: `${deltaPct >= 0 ? '+' : ''}${Math.round(deltaPct)}%` };

    };



    const kpiDeltas = [

      { key: 'candidate_changes', label: 'Candidate changes', value: candidateChangeCount, displayValue: String(candidateChangeCount), previous: previousCandidateChangeCount },

      { key: 'job_changes', label: 'Job changes', value: jobChangeCount, displayValue: String(jobChangeCount), previous: previousJobChangeCount },

      { key: 'stage_moves', label: 'Stage moves', value: stageMoveCount, displayValue: String(stageMoveCount), previous: previousStageMoveCount },

      { key: 'screenings_done', label: 'Screenings done', value: filteredScreeningCompleted.length, displayValue: String(filteredScreeningCompleted.length), previous: previousScreeningCompleted.length },

      { key: 'time_to_fill', label: 'Time to fill', value: currentOutcomeKpis.avgTimeToFill, displayValue: currentOutcomeKpis.avgTimeToFill == null ? 'N/A' : `${currentOutcomeKpis.avgTimeToFill.toFixed(1)}d`, previous: previousOutcomeKpis.avgTimeToFill },

      { key: 'offer_acceptance', label: 'Offer acceptance', value: currentOutcomeKpis.offerAcceptanceRate, displayValue: currentOutcomeKpis.offerAcceptanceRate == null ? 'N/A' : `${Math.round(currentOutcomeKpis.offerAcceptanceRate)}%`, previous: previousOutcomeKpis.offerAcceptanceRate },

    ].map((item) => ({ ...item, ...toDelta(item.value, item.previous) }));



    const stageOrder = PIPELINE_STAGES.map((stage) => stage.id);

    const stageIndexById = stageOrder.reduce((accumulator, stageId, index) => {

      accumulator[stageId] = index;

      return accumulator;

    }, {});

    const getCandidateStageIndex = (candidate) => {

      const idx = stageIndexById[String(candidate?.status || '').toLowerCase()];

      return Number.isFinite(idx) ? idx : 0;

    };

    const conversionFunnel = PIPELINE_STAGES.map((stage, index) => {

      const count = filteredCandidates.filter((candidate) => getCandidateStageIndex(candidate) >= index).length;

      const previousCount = index === 0 ? count : filteredCandidates.filter((candidate) => getCandidateStageIndex(candidate) >= (index - 1)).length;

      const conversionPct = previousCount > 0 ? (count / previousCount) * 100 : 0;

      const dropoffPct = previousCount > 0 ? Math.max(0, 100 - conversionPct) : 0;

      return {

        stageId: stage.id,

        label: stage.label,

        count,

        conversionPct,

        dropoffPct,

        conversionLabel: `${Math.round(conversionPct)}%`,

        dropoffLabel: `${Math.round(dropoffPct)}%`,

      };

    });

    const funnelTopCount = Math.max(conversionFunnel[0]?.count || 0, 1);

    const candidateStageTimeMs = (candidate, stageId) => {

      if (stageId === 'applied') {

        return getTimeMs(candidate?.submittedAt, candidate?.createdAtIso, candidate?.lastActivityAt);

      }

      const history = Array.isArray(candidate?.stageHistory) ? candidate.stageHistory : [];

      const fromHistory = history

        .filter((entry) => String(entry?.stageId || '').toLowerCase() === stageId)

        .map((entry) => getTimeMs(entry?.movedAt, entry?.createdAtIso, entry?.movedAtMs))

        .filter((ms) => Number.isFinite(ms));

      if (fromHistory.length) return Math.min(...fromHistory);

      return getCandidateStageIndex(candidate) >= (stageIndexById[stageId] || 0)

        ? getTimeMs(candidate?.lastActivityAt, candidate?.updatedAt, candidate?.submittedAt)

        : null;

    };

    const medianDays = (samples) => {

      if (!samples.length) return null;

      const sorted = [...samples].sort((left, right) => left - right);

      const mid = Math.floor(sorted.length / 2);

      return sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];

    };

    const stageVelocity = PIPELINE_STAGES.slice(0, -1).map((fromStage, index) => {

      const toStage = PIPELINE_STAGES[index + 1];

      const samples = [];

      filteredCandidates.forEach((candidate) => {

        const fromMs = candidateStageTimeMs(candidate, fromStage.id);

        const toMs = candidateStageTimeMs(candidate, toStage.id);

        if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs < fromMs) return;

        if (!isWithinCurrentRange(toMs)) return;

        const days = (toMs - fromMs) / 86400000;

        if (days < 0 || days > 180) return;

        samples.push({

          id: `${candidate.id}-${fromStage.id}-${toStage.id}`,

          days,

          title: candidate.name || 'Candidate',

          subtitle: `${candidate.role || 'Role'}  -  ${candidate.company || 'Company'}`,

          meta: `${fromStage.label} -> ${toStage.label}`,

        });

      });

      const median = medianDays(samples.map((sample) => sample.days));

      return {

        key: `${fromStage.id}_to_${toStage.id}`,

        label: `${fromStage.label} -> ${toStage.label}`,

        sampleCount: samples.length,

        medianDays: median,

        medianLabel: median == null ? 'N/A' : `${median.toFixed(1)}d`,

        samples: samples.sort((left, right) => right.days - left.days),

      };

    });

    const velocityMax = Math.max(...stageVelocity.map((item) => item.medianDays || 0), 1);
    const funnelAppliedCount = conversionFunnel[0]?.count || 0;
    const funnelHiredCount = conversionFunnel.find((stage) => stage.stageId === 'hired')?.count || 0;
    const funnelYieldPct = funnelAppliedCount > 0 ? (funnelHiredCount / funnelAppliedCount) * 100 : 0;
    const stageVelocityMedian = medianDays(stageVelocity.map((item) => item.medianDays).filter((value) => Number.isFinite(value)));
    const normalizeCandidateSource = (candidate) => {
      const raw = String(candidate?.source || '').trim();
      if (!raw) return 'Unknown source';
      if (/linkedin/i.test(raw)) return 'LinkedIn';
      if (/csv/i.test(raw)) return 'CSV import';
      if (/agency_bulk_cv/i.test(raw)) return 'Bulk CV upload';
      if (/review queue|candidate jobs link/i.test(raw)) return 'Review queue';
      if (/sourcing inbox/i.test(raw)) return 'Sourcing inbox';
      return raw.length > 42 ? `${raw.slice(0, 42)}...` : raw;
    };
    const sourceQualityMap = filteredCandidates.reduce((accumulator, candidate) => {
      const sourceKey = normalizeCandidateSource(candidate);
      if (!accumulator[sourceKey]) {
        accumulator[sourceKey] = {
          key: sourceKey,
          label: sourceKey,
          count: 0,
          screened: 0,
          hired: 0,
        };
      }
      accumulator[sourceKey].count += 1;
      const stageIndex = getCandidateStageIndex(candidate);
      const isScreened = stageIndex >= (stageIndexById.screening || 1) || Boolean(candidate?.screeningSubmittedAt || candidate?.screeningCompletedAt);
      if (isScreened) accumulator[sourceKey].screened += 1;
      if (String(candidate?.status || '').toLowerCase() === 'hired') accumulator[sourceKey].hired += 1;
      return accumulator;
    }, {});
    const sourceQualityItems = Object.values(sourceQualityMap)
      .map((item) => {
        const screeningRate = item.count > 0 ? (item.screened / item.count) * 100 : 0;
        const hireRate = item.count > 0 ? (item.hired / item.count) * 100 : 0;
        return {
          ...item,
          screeningRate,
          screeningRateLabel: `${Math.round(screeningRate)}%`,
          hireRate,
          hireRateLabel: `${Math.round(hireRate)}%`,
        };
      })
      .sort((left, right) => right.count - left.count)
      .slice(0, 8);
    const sourceTotalCandidates = sourceQualityItems.reduce((sum, item) => sum + item.count, 0);
    const sourceTopShare = sourceTotalCandidates > 0 ? ((sourceQualityItems[0]?.count || 0) / sourceTotalCandidates) * 100 : 0;
    const sourceCoverageCount = sourceQualityItems.length;
    const sourceTopHireRate = sourceQualityItems[0]?.hireRate || 0;

    // ── Source ROI (cost + quality) ──────────────────────────────────────────
    const SOURCE_CPC_ESTIMATES = [
      { pattern: /linkedin/i,                  cpc: 8.5  },
      { pattern: /indeed|ziprecruiter|glassdoor|job.?board/i, cpc: 5.0 },
      { pattern: /referral/i,                  cpc: 0.5  },
      { pattern: /csv|bulk|upload/i,            cpc: 0.5  },
      { pattern: /sourcing.?inbox/i,           cpc: 2.0  },
      { pattern: /review.?queue|candidate.?jobs/i, cpc: 1.0 },
    ];
    const DEFAULT_CHANNEL_CPC = 3.0;
    const cphBudgetNum = Number(agencySettings.costPerHireBudget || 0);
    const sourceRoiItems = sourceQualityItems.map((item) => {
      const match = SOURCE_CPC_ESTIMATES.find((e) => e.pattern.test(item.key));
      const cpc = match ? match.cpc : DEFAULT_CHANNEL_CPC;
      const channelSpend = cphBudgetNum > 0
        ? (item.count / Math.max(1, sourceTotalCandidates)) * cphBudgetNum
        : cpc * item.count;
      const costPerHired = item.hired > 0 ? Math.round(channelSpend / item.hired) : null;
      const qualityScore = Math.round(item.hireRate * 0.6 + item.screeningRate * 0.4);
      // ROI index: higher quality per cost unit = better
      // quality/100 / (costPerHired / 10000) → dimensionless score, higher = better
      const roiIndex = costPerHired != null && costPerHired > 0
        ? Math.round((qualityScore / 100) / (costPerHired / 10000))
        : null;
      return { ...item, cpc, channelSpend, costPerHired, qualityScore, roiIndex };
    });
    const sourceRoiQualityMax = Math.max(...sourceRoiItems.map((r) => r.qualityScore), 1);
    const sourceRoiBestChannel = sourceRoiItems.length > 0
      ? sourceRoiItems.slice().sort((a, b) => (b.roiIndex ?? -1) - (a.roiIndex ?? -1))[0]
      : null;
    const sourceWoWWeekMs = 7 * 86400000;
    const sourceWoWNowMs = Date.now();
    const sourceCurrentWeekStartMs = sourceWoWNowMs - sourceWoWWeekMs;
    const sourcePreviousWeekStartMs = sourceWoWNowMs - (2 * sourceWoWWeekMs);
    const candidateIngestionMs = (candidate) => getTimeMs(candidate?.submittedAt, candidate?.createdAtIso, candidate?.lastActivityAt, candidate?.updatedAt);
    const sourceWoWMap = filteredCandidates.reduce((accumulator, candidate) => {
      const sourceKey = normalizeCandidateSource(candidate);
      if (!accumulator[sourceKey]) {
        accumulator[sourceKey] = {
          key: sourceKey,
          label: sourceKey,
          currentCount: 0,
          previousCount: 0,
          currentHired: 0,
          previousHired: 0,
        };
      }
      const ingestedMs = candidateIngestionMs(candidate);
      if (!Number.isFinite(ingestedMs) || ingestedMs < sourcePreviousWeekStartMs) return accumulator;
      const isHired = String(candidate?.status || '').toLowerCase() === 'hired';
      if (ingestedMs >= sourceCurrentWeekStartMs) {
        accumulator[sourceKey].currentCount += 1;
        if (isHired) accumulator[sourceKey].currentHired += 1;
      } else {
        accumulator[sourceKey].previousCount += 1;
        if (isHired) accumulator[sourceKey].previousHired += 1;
      }
      return accumulator;
    }, {});
    const sourceWoWItems = Object.values(sourceWoWMap)
      .map((item) => {
        const deltaCount = item.currentCount - item.previousCount;
        const deltaPct = item.previousCount > 0 ? (deltaCount / item.previousCount) * 100 : (item.currentCount > 0 ? 100 : 0);
        const currentHireRate = item.currentCount > 0 ? (item.currentHired / item.currentCount) * 100 : 0;
        const previousHireRate = item.previousCount > 0 ? (item.previousHired / item.previousCount) * 100 : 0;
        const hireRateDelta = currentHireRate - previousHireRate;
        return {
          ...item,
          deltaCount,
          deltaPct,
          deltaLabel: `${deltaCount >= 0 ? "+" : ""}${deltaCount}`,
          deltaPctLabel: `${deltaPct >= 0 ? "+" : ""}${Math.round(deltaPct)}%`,
          currentHireRate,
          previousHireRate,
          currentHireRateLabel: `${Math.round(currentHireRate)}%`,
          previousHireRateLabel: `${Math.round(previousHireRate)}%`,
          hireRateDelta,
          hireRateDeltaLabel: `${hireRateDelta >= 0 ? "+" : ""}${Math.round(hireRateDelta)}pp`,
          maxWindowCount: Math.max(item.currentCount, item.previousCount),
        };
      })
      .filter((item) => item.currentCount > 0 || item.previousCount > 0)
      .sort((left, right) => right.maxWindowCount - left.maxWindowCount)
      .slice(0, 8);
    const sourceCurrentWeekTotal = sourceWoWItems.reduce((sum, item) => sum + item.currentCount, 0);
    const sourcePreviousWeekTotal = sourceWoWItems.reduce((sum, item) => sum + item.previousCount, 0);
    const sourceVolumeWoWDelta = sourceCurrentWeekTotal - sourcePreviousWeekTotal;
    const sourceVolumeWoWDeltaPct = sourcePreviousWeekTotal > 0 ? (sourceVolumeWoWDelta / sourcePreviousWeekTotal) * 100 : (sourceCurrentWeekTotal > 0 ? 100 : 0);
    const sourceVolumeWoWDeltaLabel = `${sourceVolumeWoWDelta >= 0 ? "+" : ""}${sourceVolumeWoWDelta}`;
    const sourceVolumeWoWDeltaPctLabel = `${sourceVolumeWoWDeltaPct >= 0 ? "+" : ""}${Math.round(sourceVolumeWoWDeltaPct)}%`;

    const reportingMetricDictionary = {
      candidate_changes: {
        short: 'Count of candidate-related actions in range.',
        formula: 'activity.type contains "candidate" within selected time window and recruiter scope',
        owner: 'Recruiting Ops',
        granularity: 'event',
      },
      job_changes: {
        short: 'Count of job-related actions in range.',
        formula: 'activity.type contains "job" within selected time window and recruiter scope',
        owner: 'Recruiting Ops',
        granularity: 'event',
      },
      stage_moves: {
        short: 'Candidates moved between pipeline stages.',
        formula: 'activity.type == "candidate_stage_moved" within selected filters',
        owner: 'Recruiting Ops',
        granularity: 'event',
      },
      screenings_done: {
        short: 'Completed screening submissions.',
        formula: 'count(screeningResponses submitted in range)',
        owner: 'Talent Ops',
        granularity: 'submission',
      },
      time_to_fill: {
        short: 'Average days from submit to hired.',
        formula: 'avg((hiredAt - submittedAt)/86400000) over hired candidates in range',
        owner: 'Delivery Lead',
        granularity: 'candidate',
      },
      offer_acceptance: {
        short: 'Offers accepted as a percent of offers sent.',
        formula: '(acceptedOffers / offersSent) * 100 in selected period',
        owner: 'Delivery Lead',
        granularity: 'candidate',
      },
      funnel_overview: {
        short: 'Conversion across pipeline stages from applied to hired.',
        formula: 'stage count at N / stage count at N-1, plus overall hired/applied yield',
        owner: 'Recruiting Ops',
        granularity: 'stage',
      },
      velocity_overview: {
        short: 'Median days between adjacent pipeline stage transitions.',
        formula: 'median((toStageAt - fromStageAt)/86400000) per stage transition',
        owner: 'Recruiting Ops',
        granularity: 'transition',
      },
      source_quality_overview: {
        short: 'Source mix and conversion quality across candidate channels.',
        formula: 'group by candidate.source, then compute volume, screening rate, and hire rate',
        owner: 'Recruiting Ops',
        granularity: 'source channel',
      },

      source_trend_wow: {

        title: 'Source trend week over week',

        rows: sourceWoWItems.map((item) => ({
          id: `source-wow-${item.key}`,
          title: item.label,
          subtitle: `This week ${item.currentCount} | Prev ${item.previousCount}`,
          meta: `Volume ${item.deltaPctLabel} | Hire-rate ${item.hireRateDeltaLabel}`,
          value: item.deltaLabel,
        })),

      },
      source_trend_wow: {
        short: 'Week-over-week source channel volume and hire-rate deltas.',
        formula: 'current 7-day source totals and hire rates compared to previous 7-day period',
        owner: 'Recruiting Ops',
        granularity: 'source channel/week',
      },
    };

    const kpiDrilldowns = {

      candidate_changes: {

        title: 'Candidate actions',

        rows: filteredActivityLog.filter((item) => String(item.type || '').includes('candidate')).slice(0, 100).map((item) => ({ id: item.id, title: item.summary || 'Candidate action', subtitle: item.type || '', meta: item.createdAtIso ? new Date(item.createdAtIso).toLocaleString() : '' })),

      },

      job_changes: {

        title: 'Job actions',

        rows: filteredActivityLog.filter((item) => String(item.type || '').includes('job')).slice(0, 100).map((item) => ({ id: item.id, title: item.summary || 'Job action', subtitle: item.type || '', meta: item.createdAtIso ? new Date(item.createdAtIso).toLocaleString() : '' })),

      },

      stage_moves: {

        title: 'Stage moves',

        rows: filteredActivityLog.filter((item) => item.type === 'candidate_stage_moved').slice(0, 100).map((item) => ({ id: item.id, title: item.summary || 'Stage moved', subtitle: item.type || '', meta: item.createdAtIso ? new Date(item.createdAtIso).toLocaleString() : '' })),

      },

      screenings_done: {

        title: 'Completed screenings',

        rows: filteredScreeningCompleted.slice(0, 100).map((response, index) => ({ id: response.id || response.candidateId || `screening-${index}`, title: response.candidateName || response.name || 'Screening completed', subtitle: response.role || response.company || 'Submission', meta: response.submittedAt ? new Date(response.submittedAt).toLocaleString() : '' })),

      },

      time_to_fill: {

        title: 'Time-to-fill samples',

        rows: currentOutcomeKpis.ttfSamples.map((sample) => ({ id: sample.id, title: sample.title, subtitle: sample.subtitle, meta: sample.meta })),

      },

      offer_acceptance: {

        title: 'Offer acceptance outcomes',

        rows: currentOutcomeKpis.acceptedRows,

      },

      funnel_overview: {

        title: 'Conversion funnel',

        rows: conversionFunnel.map((item) => ({
          id: `funnel-${item.stageId}`,
          title: item.label,
          subtitle: `${item.count} candidates`,
          meta: `Conversion ${item.conversionLabel} | Drop-off ${item.dropoffLabel}`,
          value: item.count,
        })),

      },

      velocity_overview: {

        title: 'Stage velocity',

        rows: stageVelocity.map((item) => ({
          id: `velocity-${item.key}`,
          title: item.label,
          subtitle: `${item.sampleCount} transitions`,
          meta: `Median ${item.medianLabel}`,
          value: item.medianLabel,
        })),

      },

      source_quality_overview: {

        title: 'Source quality breakdown',

        rows: sourceQualityItems.map((item) => ({
          id: `source-${item.key}`,
          title: item.label,
          subtitle: `${item.count} candidates`,
          meta: `Screened ${item.screeningRateLabel} | Hired ${item.hireRateLabel}`,
          value: item.hireRateLabel,
        })),

      },

    };

    const activeKpiDrilldown = reportingKpiDrawerKey && canViewReportingMetric(reportingKpiDrawerKey) ? kpiDrilldowns[reportingKpiDrawerKey] : null;



    const reportingMetrics = {

      totalMembers: agencyMembers.length,

      activeSeats: agencyActiveSeats,

      candidatesTracked: filteredCandidates.length,

      openJobs: filteredJobs.filter((job) => String(job.status || '').toLowerCase() !== 'closed').length,

      screeningCompleted: filteredScreeningCompleted.length,

      recentActions: filteredActivityLog.slice(0, 8),

      stageCounts: PIPELINE_STAGES.map((stage) => ({

        ...stage,

        count: filteredCandidates.filter((candidate) => candidate.status === stage.id).length,

      })),

      activityTypeCounts: filteredActivityLog.reduce((accumulator, item) => {

        const key = String(item.type || 'unknown').replace(/_/g, ' ');

        accumulator[key] = (accumulator[key] || 0) + 1;

        return accumulator;

      }, {}),

    };

    const topActivityTypes = Object.entries(reportingMetrics.activityTypeCounts)

      .sort((left, right) => right[1] - left[1])

      .slice(0, 5);

    const reportingStagePalette = ['#22d3ee', '#818cf8', '#a78bfa', '#34d399', '#fb923c', '#f472b6'];

    const activityMixPalette = ['#22d3ee', '#818cf8', '#fbbf24', '#f472b6', '#34d399'];

    const totalStageCandidates = reportingMetrics.stageCounts.reduce((sum, stage) => sum + stage.count, 0);

    const maxStageCount = Math.max(...reportingMetrics.stageCounts.map((stage) => stage.count), 1);

    const stageVisuals = reportingMetrics.stageCounts.map((stage, index) => ({

      ...stage,

      barWidth: maxStageCount > 0 ? (stage.count / maxStageCount) * 100 : 0,

      share: totalStageCandidates > 0 ? Math.round((stage.count / totalStageCandidates) * 100) : 0,

      color: reportingStagePalette[index % reportingStagePalette.length],

    }));

    const activityMixTotal = topActivityTypes.reduce((sum, [, count]) => sum + Number(count || 0), 0);

    const activityMixItems = topActivityTypes.map(([type, count], index) => ({

      id: type,

      label: String(type || 'unknown').replace(/_/g, ' '),

      count,

      share: activityMixTotal > 0 ? Math.round((count / activityMixTotal) * 100) : 0,

      color: activityMixPalette[index % activityMixPalette.length],

    }));

    let activityCursor = 0;

    const activityMixGradient = activityMixItems.length

      ? `conic-gradient(${activityMixItems.map((item) => {

        const start = activityCursor;

        const sweep = activityMixTotal > 0 ? (item.count / activityMixTotal) * 360 : 0;

        activityCursor += sweep;

        return `${item.color} ${start}deg ${activityCursor}deg`;

      }).join(', ')})`

      : 'conic-gradient(#1e293b 0deg 360deg)';

    const activityTrendDays = Math.min(14, Math.max(7, effectiveRangeDays));

    const activityTrendBuckets = Array.from({ length: activityTrendDays }, (_, index) => {

      const bucketStart = rangeEndMs - ((activityTrendDays - 1 - index) * 86400000);

      const bucketDate = new Date(bucketStart);

      const label = `${bucketDate.getMonth() + 1}/${bucketDate.getDate()}`;

      const key = bucketDate.toDateString();

      const value = filteredActivityLog.filter((item) => {

        const ms = getTimeMs(item.createdAtMs, item.createdAtIso);

        return Number.isFinite(ms) && new Date(ms).toDateString() === key;

      }).length;

      return { label, value };

    });

    const activityTrendMax = Math.max(...activityTrendBuckets.map((item) => item.value), 1);

    const trendChartWidth = 100;

    const trendChartHeight = 36;

    const trendPointStep = activityTrendBuckets.length > 1 ? trendChartWidth / (activityTrendBuckets.length - 1) : trendChartWidth;

    const trendLinePath = activityTrendBuckets

      .map((point, index) => {

        const x = index * trendPointStep;

        const y = trendChartHeight - ((point.value / activityTrendMax) * trendChartHeight);

        return `${index === 0 ? 'M' : 'L'} ${x} ${Math.max(2, y)}`;

      })

      .join(' ');

    const trendAreaPath = activityTrendBuckets.length > 1

      ? `${trendLinePath} L ${trendChartWidth} ${trendChartHeight} L 0 ${trendChartHeight} Z`

      : '';

    const reportingTimelineEvents = reportingMetrics.recentActions.slice(0, 3).map((item) => ({

      id: item.id,

      title: item.summary || 'No action summary',

      type: String(item.type || 'unknown').replace(/_/g, ' '),

      stamp: item.createdAtIso ? new Date(item.createdAtIso).toLocaleString() : 'Pending timestamp',

    }));



    const forecastMonthlyHireTarget = Number(agencySettings.forecastMonthlyHireTarget || 10);
    const rangeMonths = Math.max(rangeSpanMs / (30 * 86400000), 1 / 30);
    const projectedMonthlyHires = funnelHiredCount / rangeMonths;
    const forecastAttainmentPct = forecastMonthlyHireTarget > 0 ? (projectedMonthlyHires / forecastMonthlyHireTarget) * 100 : 0;
    const forecastStatusLabel = forecastAttainmentPct >= 100 ? 'On Track' : forecastAttainmentPct >= 70 ? 'At Risk' : 'Behind';

    // ── Cost-per-hire ────────────────────────────────────────────
    const cphBudget = Number(agencySettings.costPerHireBudget || 0);
    const hiresInRange = filteredCandidates.filter((c) => c.status === 'hired').length;
    const previousHiresInRange = previousCandidates ? previousCandidates.filter((c) => c.status === 'hired').length : 0;
    const costPerHireValue = cphBudget > 0 && hiresInRange > 0 ? Math.round(cphBudget / hiresInRange) : null;
    const costPerHirePrev = cphBudget > 0 && previousHiresInRange > 0 ? Math.round(cphBudget / previousHiresInRange) : null;
    const costPerHireDisplay = costPerHireValue == null
      ? (cphBudget > 0 ? 'No hires yet' : 'Budget not set')
      : '$' + costPerHireValue.toLocaleString();
    const costPerHireDelta = costPerHireValue != null && costPerHirePrev != null
      ? Math.round(((costPerHireValue - costPerHirePrev) / costPerHirePrev) * 100)
      : null;

    // ── Placement & Revenue Intelligence ─────────────────────────────────────
    const revPlacedCandidates = filteredCandidates.filter((c) => c.status === 'hired' && Number(c.placementFee || 0) > 0);
    const revPrevPlaced = previousCandidates ? previousCandidates.filter((c) => c.status === 'hired' && Number(c.placementFee || 0) > 0) : [];
    const totalRevenue = revPlacedCandidates.reduce((s, c) => s + Number(c.placementFee || 0), 0);
    const prevRevenue = revPrevPlaced.reduce((s, c) => s + Number(c.placementFee || 0), 0);
    const revenueGrowthPct = prevRevenue > 0 ? Math.round(((totalRevenue - prevRevenue) / prevRevenue) * 100) : null;
    const avgPlacementFee = revPlacedCandidates.length > 0 ? Math.round(totalRevenue / revPlacedCandidates.length) : 0;
    const feePercentSamples = revPlacedCandidates.filter((c) => Number(c.placementFeePercent || 0) > 0);
    const avgFeePercent = feePercentSamples.length > 0
      ? (feePercentSamples.reduce((s, c) => s + Number(c.placementFeePercent || 0), 0) / feePercentSamples.length).toFixed(1)
      : null;
    const rangeMonthsActual = Math.max(rangeSpanMs / (30 * 86400000), 1 / 30);
    const revenueRunRateAnnual = totalRevenue > 0 ? Math.round((totalRevenue / rangeMonthsActual) * 12) : 0;
    const revenueTargetMonthly = Number(agencySettings.monthlyRevenueTarget || 0);
    const revenueAttainmentPct = revenueTargetMonthly > 0
      ? Math.round((totalRevenue / (revenueTargetMonthly * rangeMonthsActual)) * 100)
      : null;
    // 12-month revenue trend (all time, ignores range filter)
    const revMonthTrend = (() => {
      const now = new Date();
      return Array.from({ length: 12 }, (_, i) => {
        const d = new Date(now.getFullYear(), now.getMonth() - (11 - i), 1);
        const shortLabel = d.toLocaleString('default', { month: 'short', year: '2-digit' });
        const start = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
        const monthlyPlaced = candidates.filter((c) => {
          if (c.status !== 'hired' || !Number(c.placementFee || 0)) return false;
          const ts = new Date(c.hiredAt || c.stageUpdatedAt || '').getTime();
          return Number.isFinite(ts) && ts >= start && ts <= end;
        });
        const revenue = monthlyPlaced.reduce((s, c) => s + Number(c.placementFee || 0), 0);
        return { shortLabel, revenue, placements: monthlyPlaced.length };
      });
    })();
    const revMonthMax = Math.max(1, ...revMonthTrend.map((m) => m.revenue));
    // Revenue by client
    const revenueByClient = (() => {
      const map = {};
      revPlacedCandidates.forEach((c) => {
        const co = c.company || c.clientCompany || 'Unknown';
        if (!map[co]) map[co] = { name: co, revenue: 0, placements: 0 };
        map[co].revenue += Number(c.placementFee || 0);
        map[co].placements += 1;
      });
      return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 7);
    })();
    const revenueByClientMax = Math.max(1, ...(revenueByClient.map((c) => c.revenue)));
    // Revenue by recruiter
    const revenueByRecruiter = (() => {
      const map = {};
      revPlacedCandidates.forEach((c) => {
        const recId = c.recruiterId || recruiterUser?.uid || 'unknown';
        const recName = agencyMembers.find((m) => m.id === recId)?.name || 'You';
        if (!map[recId]) map[recId] = { name: recName, revenue: 0, placements: 0 };
        map[recId].revenue += Number(c.placementFee || 0);
        map[recId].placements += 1;
      });
      return Object.values(map).sort((a, b) => b.revenue - a.revenue).slice(0, 6);
    })();
    const revenueByRecruiterMax = Math.max(1, ...(revenueByRecruiter.map((r) => r.revenue)));
    // Revenue by job type
    const revenueByJobType = (() => {
      const map = {};
      revPlacedCandidates.forEach((c) => {
        const jt = c.jobType || 'Unspecified';
        if (!map[jt]) map[jt] = { type: jt, revenue: 0, placements: 0 };
        map[jt].revenue += Number(c.placementFee || 0);
        map[jt].placements += 1;
      });
      return Object.values(map).sort((a, b) => b.revenue - a.revenue);
    })();
    // Top placements
    const topPlacements = [...revPlacedCandidates]
      .sort((a, b) => Number(b.placementFee || 0) - Number(a.placementFee || 0))
      .slice(0, 8);
    // Offer-to-placement conversion
    const offersExtended = filteredCandidates.filter((c) => ['offer', 'hired'].includes(c.status)).length;
    const offerToPlacementRate = offersExtended > 0 ? Math.round((hiresInRange / offersExtended) * 100) : null;
    // Avg days to placement (hired - submitted)
    const daysToPlaceSamples = revPlacedCandidates
      .map((c) => {
        const hired = new Date(c.hiredAt || '').getTime();
        const submitted = new Date(c.submittedAt || c.createdAtIso || '').getTime();
        return Number.isFinite(hired) && Number.isFinite(submitted) && hired > submitted
          ? (hired - submitted) / 86400000 : null;
      })
      .filter((d) => d != null);
    const avgDaysToPlacement = daysToPlaceSamples.length > 0
      ? Math.round(daysToPlaceSamples.reduce((a, b) => a + b, 0) / daysToPlaceSamples.length)
      : null;

    // ── Pipeline aging analysis ────────────────────────────────────
    const agingThreshDays = Number(agencySettings.agingThresholdDays || 14);
    const agingAnalysis = PIPELINE_STAGES.map((stage) => {
      const stageItems = filteredCandidates.filter((c) => c.status === stage.id);
      const stuckItems = stageItems.filter((c) => {
        const history = Array.isArray(c.stageHistory) ? c.stageHistory : [];
        const hit = [...history].reverse().find((e) => e.stageId === stage.id);
        const movedMs = hit ? new Date(hit.movedAt || 0).getTime() : new Date(c.submittedAt || 0).getTime();
        if (!Number.isFinite(movedMs) || movedMs <= 0) return false;
        return (Date.now() - movedMs) / 86400000 >= agingThreshDays;
      });
      const avgDaysArr = stageItems.map((c) => {
        const history = Array.isArray(c.stageHistory) ? c.stageHistory : [];
        const hit = [...history].reverse().find((e) => e.stageId === stage.id);
        const movedMs = hit ? new Date(hit.movedAt || 0).getTime() : new Date(c.submittedAt || 0).getTime();
        return Number.isFinite(movedMs) && movedMs > 0 ? (Date.now() - movedMs) / 86400000 : null;
      }).filter((d) => d != null);
      const avgDays = avgDaysArr.length > 0 ? Math.round(avgDaysArr.reduce((a, b) => a + b, 0) / avgDaysArr.length) : 0;
      return { id: stage.id, label: stage.label, total: stageItems.length, stuck: stuckItems.length, avgDays };
    });
    const totalStuckCount = agingAnalysis.reduce((sum, s) => sum + s.stuck, 0);

    // ── Time-in-stage velocity distribution ────────────────────────
    // Buckets: 0-3d, 4-7d, 8-14d, 15-30d, 31+d
    const TIS_BUCKET_DEFS = [
      { label: '0–3d', max: 3 },
      { label: '4–7d', max: 7 },
      { label: '8–14d', max: 14 },
      { label: '15–30d', max: 30 },
      { label: '31+d', max: Infinity },
    ];
    const pct = (arr, p) => {
      if (!arr.length) return null;
      const sorted = [...arr].sort((a, b) => a - b);
      const idx = Math.max(0, Math.min(sorted.length - 1, Math.floor((p / 100) * sorted.length)));
      return sorted[idx];
    };
    const stageTimeInStage = PIPELINE_STAGES.map((stage) => {
      const daysSamples = [];
      filteredCandidates.forEach((candidate) => {
        const history = Array.isArray(candidate.stageHistory) ? candidate.stageHistory : [];
        // Build full timeline: applied entry + stageHistory entries sorted by time
        const submittedMs = (() => {
          const v = candidate.submittedAt || candidate.createdAtIso || candidate.lastActivityAt;
          if (!v) return null;
          const ms = new Date(v).getTime();
          return Number.isFinite(ms) ? ms : null;
        })();
        const timeline = [];
        if (submittedMs) timeline.push({ stageId: 'applied', ms: submittedMs });
        history.forEach((entry) => {
          const ms = new Date(entry.movedAt || entry.createdAtIso || 0).getTime();
          if (Number.isFinite(ms) && ms > 0) timeline.push({ stageId: String(entry.stageId || ''), ms });
        });
        // Sort chronologically
        timeline.sort((a, b) => a.ms - b.ms);
        // For each timeline entry matching this stage, compute dwell time
        timeline.forEach((entry, i) => {
          if (entry.stageId !== stage.id) return;
          const exitMs = i < timeline.length - 1 ? timeline[i + 1].ms : Date.now();
          const days = (exitMs - entry.ms) / 86400000;
          if (days >= 0 && days <= 365) daysSamples.push(days);
        });
      });
      const buckets = TIS_BUCKET_DEFS.map((def, bi) => ({
        ...def,
        count: daysSamples.filter((d) => d <= def.max && (bi === 0 || d > TIS_BUCKET_DEFS[bi - 1].max)).length,
      }));
      const bucketMax = Math.max(1, ...buckets.map((b) => b.count));
      const sortedSamples = [...daysSamples].sort((a, b) => a - b);
      const medTis = sortedSamples.length ? medianDays(daysSamples) : null;
      const p25Tis = pct(daysSamples, 25);
      const p75Tis = pct(daysSamples, 75);
      const prevSamples = [];
      previousCandidates && previousCandidates.forEach((candidate) => {
        const history = Array.isArray(candidate.stageHistory) ? candidate.stageHistory : [];
        const submittedMs = (() => {
          const v = candidate.submittedAt || candidate.createdAtIso;
          if (!v) return null;
          const ms = new Date(v).getTime();
          return Number.isFinite(ms) ? ms : null;
        })();
        const timeline = [];
        if (submittedMs) timeline.push({ stageId: 'applied', ms: submittedMs });
        history.forEach((entry) => {
          const ms = new Date(entry.movedAt || 0).getTime();
          if (Number.isFinite(ms) && ms > 0) timeline.push({ stageId: String(entry.stageId || ''), ms });
        });
        timeline.sort((a, b) => a.ms - b.ms);
        timeline.forEach((entry, i) => {
          if (entry.stageId !== stage.id) return;
          const exitMs = i < timeline.length - 1 ? timeline[i + 1].ms : Date.now();
          const days = (exitMs - entry.ms) / 86400000;
          if (days >= 0 && days <= 365) prevSamples.push(days);
        });
      });
      const prevMedian = prevSamples.length ? medianDays(prevSamples) : null;
      const deltaPct = medTis != null && prevMedian != null && prevMedian > 0
        ? Math.round(((medTis - prevMedian) / prevMedian) * 100) : null;
      return {
        id: stage.id,
        label: stage.label,
        color: stage.color,
        n: daysSamples.length,
        medianDays: medTis,
        p25: p25Tis,
        p75: p75Tis,
        buckets,
        bucketMax,
        deltaPct,
        prevMedian,
      };
    });

    // ── Interview scorecard analytics ──────────────────────────────
    const scorecardResponses = filteredScreeningCompleted.map((r) => ({
      score: (typeof r.aiScore === 'number' ? r.aiScore : (r.aiScore?.score ?? null)),
      recommendation: String(r.recommendation || '').toLowerCase(),
    }));
    const scorecardWithScores = scorecardResponses.filter((r) => r.score != null);
    const scorecardAvg = scorecardWithScores.length > 0
      ? Math.round(scorecardWithScores.reduce((sum, r) => sum + r.score, 0) / scorecardWithScores.length)
      : null;
    const scorecardPassCount = scorecardResponses.filter((r) =>
      ['advance', 'hire', 'proceed', 'yes', 'shortlist'].includes(r.recommendation)
    ).length;
    const scorecardRejectCount = scorecardResponses.filter((r) =>
      ['reject', 'decline', 'no', 'pass', 'remove'].includes(r.recommendation)
    ).length;
    const scorecardPassRate = scorecardResponses.length > 0
      ? Math.round((scorecardPassCount / scorecardResponses.length) * 100) : null;
    const scorecardBuckets = [
      { label: '0-20', count: 0 }, { label: '21-40', count: 0 },
      { label: '41-60', count: 0 }, { label: '61-80', count: 0 }, { label: '81-100', count: 0 },
    ];
    scorecardWithScores.forEach((r) => { scorecardBuckets[Math.min(4, Math.floor(r.score / 21))].count++; });
    const scorecardBucketMax = Math.max(1, ...scorecardBuckets.map((b) => b.count));

    // ── KPI Breadth: conversion funnel rates ─────────────────────────────────
    const _allCount = filteredCandidates.length;
    const _screeningReached = filteredCandidates.filter((c) => ['screening','interview','offer','hired'].includes(c.status)).length;
    const _interviewReached = filteredCandidates.filter((c) => ['interview','offer','hired'].includes(c.status)).length;
    const _offerReached     = filteredCandidates.filter((c) => ['offer','hired'].includes(c.status)).length;
    // ── Previous-period parallel counts for conversion delta ──────────────────────────────
    const _prevScreenReach = previousCandidates.filter((c) => ['screening','interview','offer','hired'].includes(c.status)).length;
    const _prevInterReach  = previousCandidates.filter((c) => ['interview','offer','hired'].includes(c.status)).length;
    const _prevOfferReach  = previousCandidates.filter((c) => ['offer','hired'].includes(c.status)).length;
    const _prevAll         = previousCandidates.length;
    const _prevRates = {
      apply_to_screen:     _prevAll          > 0 ? Math.round((_prevScreenReach / _prevAll)          * 100) : null,
      screen_to_interview: _prevScreenReach  > 0 ? Math.round((_prevInterReach  / _prevScreenReach)  * 100) : null,
      interview_to_offer:  _prevInterReach   > 0 ? Math.round((_prevOfferReach  / _prevInterReach)   * 100) : null,
      offer_to_hire:       _prevOfferReach   > 0 ? Math.round((previousHiresInRange / _prevOfferReach) * 100) : null,
    };
    const conversionRates = [
      { key:'apply_to_screen',    label:'Applied → Screened',   numerator:_screeningReached, denominator:_allCount,          good:50, ok:30, dropCount:_allCount - _screeningReached,         p50:30, p75:55 },
      { key:'screen_to_interview',label:'Screened → Interview', numerator:_interviewReached, denominator:_screeningReached,  good:60, ok:40, dropCount:_screeningReached - _interviewReached,  p50:40, p75:65 },
      { key:'interview_to_offer', label:'Interview → Offer',    numerator:_offerReached,     denominator:_interviewReached,  good:50, ok:30, dropCount:_interviewReached - _offerReached,      p50:30, p75:55 },
      { key:'offer_to_hire',      label:'Offer → Hire',         numerator:hiresInRange,      denominator:_offerReached,      good:80, ok:60, dropCount:Math.max(0, _offerReached - hiresInRange), p50:70, p75:88 },
    ].map((r) => {
      const value   = r.denominator > 0 ? Math.round((r.numerator / r.denominator) * 100) : null;
      const prevVal = _prevRates[r.key];
      const delta   = value != null && prevVal != null ? value - prevVal : null;
      return {
        ...r,
        value,
        display:  value != null ? value + '%' : 'N/A',
        delta,
        prevVal,
        dropPct: r.denominator > 0 && r.dropCount >= 0 ? Math.round((r.dropCount / r.denominator) * 100) : null,
      };
    });
    const pipelineInflow  = _allCount;
    const withdrawCount   = filteredCandidates.filter((c) => /withdraw|withdrew/i.test(c.status || '')).length;
    const withdrawRate    = _allCount > 0 ? Math.round((withdrawCount / _allCount) * 100) : null;
    const disqualCount    = filteredCandidates.filter((c) => /disqualif|reject/i.test(c.status || '')).length;
    const disqualRate     = _allCount > 0 ? Math.round((disqualCount / _allCount) * 100) : null;

    // ── DEI proxy metrics ─────────────────────────────────────────────────────
    const _deiScores = scorecardWithScores.map((r) => r.score);
    const _deiMean = _deiScores.length > 0 ? _deiScores.reduce((s, v) => s + v, 0) / _deiScores.length : null;
    const deiScoreStdDev = _deiMean != null && _deiScores.length > 1
      ? Math.round(Math.sqrt(_deiScores.reduce((s, v) => s + Math.pow(v - _deiMean, 2), 0) / _deiScores.length))
      : null;
    const deiOverallPassRate = scorecardResponses.length > 0
      ? Math.round((scorecardPassCount / scorecardResponses.length) * 100) : null;
    const deiFunnelBySource = sourceQualityItems.map((item) => {
      const srcResponses = filteredScreeningCompleted.filter((r) => {
        const s = (r.source || '').toLowerCase().trim();
        return s === item.key || s.includes(item.key) || item.key.includes(s);
      });
      const srcPass = srcResponses.filter((r) => (r.overallScore || r.score || 0) >= 60).length;
      const srcPassRate = srcResponses.length > 0 ? Math.round((srcPass / srcResponses.length) * 100) : null;
      const srcAvgScore = srcResponses.length > 0
        ? Math.round(srcResponses.reduce((s, r) => s + (r.overallScore || r.score || 0), 0) / srcResponses.length)
        : null;
      return { source: item.label || item.key, screened: srcResponses.length, passRate: srcPassRate, avgScore: srcAvgScore };
    }).filter((r) => r.screened > 0);
    const deiFlaggedCount = deiFunnelBySource.filter(
      (r) => r.passRate != null && deiOverallPassRate != null && Math.abs(r.passRate - deiOverallPassRate) >= 15
    ).length;

    // ── Agency / staffing KPIs ────────────────────────────────────────────────
    const openJobCount   = filteredJobs.filter((j) => String(j.status || '').toLowerCase() !== 'closed').length;
    const closedJobCount = filteredJobs.filter((j) => String(j.status || '').toLowerCase() === 'closed').length;
    const jobCloseRate   = filteredJobs.length > 0 ? Math.round((closedJobCount / filteredJobs.length) * 100) : null;
    const fillRate       = openJobCount > 0 ? Math.round((hiresInRange / openJobCount) * 100) : null;
    const jobOrderVelocity = effectiveRangeDays >= 7 ? +(filteredJobs.length / (effectiveRangeDays / 7)).toFixed(1) : null;
    const agencyActiveRecruiters = agencyMembers.filter((m) => m.seatAssigned).length;
    const avgCandsPerRecruiter   = agencyActiveRecruiters > 0 ? Math.round(_allCount / agencyActiveRecruiters) : null;
    const recruiterLeaderboard = agencyMembers
      .map((m) => {
        const candsByRec = filteredCandidates.filter((c) => c.recruiterId === m.id);
        const hiresByRec = candsByRec.filter((c) => c.status === 'hired').length;
        const frByRec    = openJobCount > 0 ? Math.round((hiresByRec / openJobCount) * 100) : null;
        return { id: m.id, name: m.name || m.email || 'Unknown', hires: hiresByRec, candidates: candsByRec.length, fillRate: frByRec };
      })
      .sort((a, b) => b.hires - a.hires)
      .slice(0, 8);
    const _jobTitleMap = {};
    filteredCandidates.forEach((c) => {
      const t = (c.matchedJobTitle || c.role || '').trim();
      if (!t) return;
      if (!_jobTitleMap[t]) _jobTitleMap[t] = { count: 0, hires: 0 };
      _jobTitleMap[t].count++;
      if (c.status === 'hired') _jobTitleMap[t].hires++;
    });
    const jobTitleBreakdown = Object.entries(_jobTitleMap)
      .map(([title, d]) => ({ title, count: d.count, hireRate: d.count > 0 ? Math.round((d.hires / d.count) * 100) : null }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    // ── Segmentation ──────────────────────────────────────────────────────────
    const _STAGE_ORDER = ['applied','screening','interview','offer','hired'];
    const _STAGE_COLORS = { applied:'#6366f1', screening:'#06b6d4', interview:'#f59e0b', offer:'#10b981', hired:'#22c55e' };
    const _SEG_COLORS = ['#6366f1','#06b6d4','#f59e0b','#10b981','#22c55e','#f43f5e','#a855f7','#ec4899'];
    const _buildSegment = (key, label, color, count, hires, barMax) => ({
      key, label, color, count,
      pct: _allCount > 0 ? Math.round((count / _allCount) * 100) : 0,
      barWidth: barMax > 0 ? Math.round((count / barMax) * 100) : 0,
      hireRate: count > 0 ? Math.round((hires / count) * 100) : null,
    });
    const _statusCounts = _STAGE_ORDER.map((s, i) => {
      const c = filteredCandidates.filter((fc) => fc.status === s);
      return _buildSegment(s, s.charAt(0).toUpperCase() + s.slice(1), _STAGE_COLORS[s] || _SEG_COLORS[i % _SEG_COLORS.length], c.length, c.filter((fc) => fc.status === 'hired').length, _allCount);
    }).filter((seg) => seg.count > 0);
    const _buildGroupSegment = (groups, limit = 8) => {
      const barMax = Math.max(1, ...Object.values(groups).map((g) => g.count));
      return Object.entries(groups)
        .filter(([k]) => k)
        .map(([k, g], i) => _buildSegment(k, k, _SEG_COLORS[i % _SEG_COLORS.length], g.count, g.hires, barMax))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);
    };
    const _sourceGroups = {};
    filteredCandidates.forEach((c) => {
      const k = normalizeCandidateSource(c) || 'unknown';
      if (!_sourceGroups[k]) _sourceGroups[k] = { count: 0, hires: 0 };
      _sourceGroups[k].count++;
      if (c.status === 'hired') _sourceGroups[k].hires++;
    });
    const _seniorityGroups = {};
    filteredCandidates.forEach((c) => {
      const r = (c.role || '').toLowerCase();
      const k = /director|vp|chief|head|principal|staff/i.test(r) ? 'Director+' :
                /senior|sr|lead/i.test(r) ? 'Senior' :
                /junior|jr|associate|entry/i.test(r) ? 'Junior' :
                /manager|mgr/i.test(r) ? 'Manager' : 'Mid-level';
      if (!_seniorityGroups[k]) _seniorityGroups[k] = { count: 0, hires: 0 };
      _seniorityGroups[k].count++;
      if (c.status === 'hired') _seniorityGroups[k].hires++;
    });
    const segmentData = segmentAxis === 'status' ? _statusCounts
      : segmentAxis === 'source' ? _buildGroupSegment(_sourceGroups)
      : _buildGroupSegment(_seniorityGroups);
    const dropOffAnalysis = _STAGE_ORDER.slice(0, -1).map((s, i) => {
      const nextStage = _STAGE_ORDER[i + 1];
      const total  = filteredCandidates.filter((c) => ['applied','screening','interview','offer','hired'].slice(i).includes(c.status)).length;
      const passed = filteredCandidates.filter((c) => ['applied','screening','interview','offer','hired'].slice(i + 1).includes(c.status)).length;
      return {
        fromId:    s,
        fromLabel: s.charAt(0).toUpperCase() + s.slice(1),
        toLabel:   nextStage.charAt(0).toUpperCase() + nextStage.slice(1),
        total, passed,
        lostCount: Math.max(0, total - passed),
        convRate:  total > 0 ? Math.round((passed / total) * 100) : null,
      };
    }).filter((item) => item.total > 0);

    // ── Offer decline reason analytics ──────────────────────────────────────
    const offerDeclines = filteredCandidates.filter((c) => c.offerDeclinedAt);
    const _offerSent = filteredCandidates.filter((c) => {
      const offerMs = candidateStageTimeMs(c, 'offer');
      return Number.isFinite(offerMs);
    });
    const offerDeclineRate = _offerSent.length > 0 ? (offerDeclines.length / _offerSent.length) * 100 : null;
    const _declineCatCounts = {};
    offerDeclines.forEach((c) => {
      const cat = c.offerDeclineCategory || 'other';
      _declineCatCounts[cat] = (_declineCatCounts[cat] || 0) + 1;
    });
    const offerDeclineCategoryBreakdown = Object.entries(OFFER_DECLINE_CATEGORY_LABELS)
      .map(([key, label]) => ({ key, label, count: _declineCatCounts[key] || 0 }))
      .filter((c) => c.count > 0)
      .sort((a, b) => b.count - a.count);
    // Monthly trend: last 6 months
    const _trendNow = new Date();
    const offerDeclineTrend = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(_trendNow.getFullYear(), _trendNow.getMonth() - (5 - i), 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const shortLabel = d.toLocaleString('default', { month: 'short' });
      const count = offerDeclines.filter((c) => {
        const decMs = new Date(c.offerDeclinedAt).getTime();
        const start = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
        const end   = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
        return Number.isFinite(decMs) && decMs >= start && decMs <= end;
      }).length;
      return { key, shortLabel, count };
    });
    const offerDeclineTrendMax = Math.max(1, ...offerDeclineTrend.map((m) => m.count));

    // ── Interviewer load & capacity ─────────────────────────────────────────
    const PANEL_HOURS_SCREENING = 3;
    const PANEL_HOURS_INTERVIEW = 6;
    const interviewerLoad = agencyMembers.map((m) => {
      const recCands = filteredCandidates.filter((c) => (c.recruiterId || recruiterUser?.uid) === m.id);
      const activeScreening = recCands.filter((c) => c.status === 'screening');
      const activeInterview = recCands.filter((c) => c.status === 'interview');
      const activePanelCount = activeScreening.length + activeInterview.length;
      const estimatedPanelHours = activeScreening.length * PANEL_HOURS_SCREENING + activeInterview.length * PANEL_HOURS_INTERVIEW;

      // Scheduling lag: median days from entering screening → entering interview
      const lagSamples = [];
      recCands.forEach((c) => {
        const scrMs = candidateStageTimeMs(c, 'screening');
        const intMs = candidateStageTimeMs(c, 'interview');
        if (Number.isFinite(scrMs) && Number.isFinite(intMs) && intMs > scrMs) {
          const d = (intMs - scrMs) / 86400000;
          if (d >= 0 && d < 90) lagSamples.push(d);
        }
      });
      const schedulingLagDays = medianDays(lagSamples);

      // Decline / drop-off rate: candidates who reached interview but did NOT progress to offer
      const reachedInterview = recCands.filter((c) => {
        const intMs = candidateStageTimeMs(c, 'interview');
        return Number.isFinite(intMs);
      });
      const progressedToOffer = reachedInterview.filter((c) => {
        const offerMs = candidateStageTimeMs(c, 'offer');
        return Number.isFinite(offerMs);
      });
      const declineRate = reachedInterview.length > 0
        ? ((reachedInterview.length - progressedToOffer.length) / reachedInterview.length) * 100
        : null;

      return {
        id: m.id,
        name: m.name || m.email || 'Unknown',
        activeScreeningCount: activeScreening.length,
        activeInterviewCount: activeInterview.length,
        activePanelCount,
        estimatedPanelHours,
        schedulingLagDays,
        declineRate,
        totalCandidates: recCands.length,
      };
    }).filter((r) => r.totalCandidates > 0 || r.activePanelCount > 0);
    const interviewerLoadMaxHours = Math.max(1, ...interviewerLoad.map((r) => r.estimatedPanelHours));
    const interviewerLoadBusiest = interviewerLoad.length > 0
      ? interviewerLoad.slice().sort((a, b) => b.estimatedPanelHours - a.estimatedPanelHours)[0]
      : null;
    const _loadLagArr = interviewerLoad.map((r) => r.schedulingLagDays).filter((d) => d != null);
    const interviewerLoadAvgLag = _loadLagArr.length > 0 ? _loadLagArr.reduce((s, v) => s + v, 0) / _loadLagArr.length : null;
    const _loadHrsArr = interviewerLoad.map((r) => r.estimatedPanelHours);
    const interviewerLoadAvgHours = _loadHrsArr.length > 0 ? _loadHrsArr.reduce((s, v) => s + v, 0) / _loadHrsArr.length : null;

    // ── Time-to-hire by stage & recruiter ──────────────────────────────────
    const TTH_STAGE_BENCHMARKS = [
      { key: 'applied_to_screening',   label: 'Applied → Screening',  p50: 3,  p75: 1  },
      { key: 'screening_to_interview', label: 'Screening → Interview', p50: 7,  p75: 4  },
      { key: 'interview_to_offer',     label: 'Interview → Offer',     p50: 10, p75: 6  },
      { key: 'offer_to_hired',         label: 'Offer → Hired',         p50: 5,  p75: 3  },
    ];
    const tthByStage = stageVelocity.map((sv) => {
      const bm = TTH_STAGE_BENCHMARKS.find((b) => b.key === sv.key) || { p50: 14, p75: 7 };
      const fastestMs = sv.samples.length > 0 ? Math.min(...sv.samples.map((s) => s.days * 86400000)) : null;
      return {
        key:        sv.key,
        label:      sv.label,
        medianDays: sv.medianDays,
        sampleCount:sv.sampleCount,
        p50:        bm.p50,
        p75:        bm.p75,
        fastestMs,
      };
    });
    const tthStageMax = Math.max(1, ...tthByStage.map((r) => Math.max(r.medianDays || 0, r.p50)));
    const tthOverallP50 = TTH_STAGE_BENCHMARKS.reduce((sum, b) => sum + b.p50, 0);
    const tthOverallMedian = (() => {
      const all = [];
      filteredCandidates.forEach((c) => {
        const applied = candidateStageTimeMs(c, 'applied');
        const hired   = candidateStageTimeMs(c, 'hired');
        if (Number.isFinite(applied) && Number.isFinite(hired) && hired > applied) {
          all.push((hired - applied) / 86400000);
        }
      });
      return medianDays(all);
    })();
    const tthByRecruiter = agencyMembers.map((m) => {
      const recCands = filteredCandidates.filter((c) => (c.recruiterId || recruiterUser?.uid) === m.id);
      const stageTTH = stageVelocity.map((sv) => {
        const bm = TTH_STAGE_BENCHMARKS.find((b) => b.key === sv.key) || { p50: 14, p75: 7 };
        const samples = [];
        const [fromId, toId] = sv.key.split('_to_');
        recCands.forEach((c) => {
          const fromMs = candidateStageTimeMs(c, fromId);
          const toMs   = candidateStageTimeMs(c, toId);
          if (Number.isFinite(fromMs) && Number.isFinite(toMs) && toMs > fromMs) {
            const d = (toMs - fromMs) / 86400000;
            if (d > 0 && d < 180) samples.push(d);
          }
        });
        return { key: sv.key, label: sv.label, median: medianDays(samples), p50: bm.p50 };
      });
      const allDays = [];
      recCands.forEach((c) => {
        const a = candidateStageTimeMs(c, 'applied');
        const h = candidateStageTimeMs(c, 'hired');
        if (Number.isFinite(a) && Number.isFinite(h) && h > a) allDays.push((h - a) / 86400000);
      });
      return {
        id:              m.id,
        name:            m.name || m.email || 'Unknown',
        totalCandidates: recCands.length,
        stageTTH,
        overallMedian:   medianDays(allDays),
      };
    }).filter((r) => r.totalCandidates > 0);

    // ── Quality of Hire ──────────────────────────────────────────────────────
    const qohCandidates = filteredCandidates.filter((c) => c.status === 'hired' && (c.qohScore90 != null || c.qohScore180 != null));
    const _qoh90 = qohCandidates.map((c) => c.qohScore90).filter((v) => v != null);
    const _qoh180 = qohCandidates.map((c) => c.qohScore180).filter((v) => v != null);
    const qohAvg90 = _qoh90.length > 0 ? _qoh90.reduce((s, v) => s + v, 0) / _qoh90.length : null;
    const qohAvg180 = _qoh180.length > 0 ? _qoh180.reduce((s, v) => s + v, 0) / _qoh180.length : null;

    // Extended KPI deltas (vars declared here or above)
    const kpiDeltasExtra = [
      { key: 'hires', label: 'Total Hires', value: hiresInRange, displayValue: String(hiresInRange), previous: previousHiresInRange },
      { key: 'hire_rate', label: 'Hire Rate', value: _allCount > 0 ? Math.round((hiresInRange / _allCount) * 100) : null, displayValue: _allCount > 0 ? (Math.round((hiresInRange / _allCount) * 100) + '%') : 'N/A', previous: previousCandidates.length > 0 ? Math.round((previousHiresInRange / previousCandidates.length) * 100) : null },
      { key: 'avg_tth', label: 'Avg TTH', value: tthOverallMedian, displayValue: tthOverallMedian != null ? (tthOverallMedian.toFixed(1) + 'd') : 'N/A', previous: null },
      { key: 'offer_decline_rate', label: 'Offer Decline Rate', value: offerDeclineRate != null ? Math.round(offerDeclineRate) : null, displayValue: offerDeclineRate != null ? (Math.round(offerDeclineRate) + '%') : 'N/A', previous: null },
      { key: 'qoh_avg_90d', label: 'QoH 90d Avg', value: qohAvg90, displayValue: qohAvg90 != null ? qohAvg90.toFixed(1) : 'N/A', previous: null },
      { key: 'pipeline_velocity', label: 'Pipeline Inflow', value: pipelineInflow, displayValue: String(pipelineInflow), previous: previousCandidates.length },
      { key: 'screen_pass_rate', label: 'Screen Pass Rate', value: deiOverallPassRate, displayValue: deiOverallPassRate != null ? (deiOverallPassRate + '%') : 'N/A', previous: null },
      { key: 'total_revenue', label: 'Total Revenue', value: (() => { const fees = filteredCandidates.map((c) => Number(c.placementFee || 0)).filter((v) => v > 0); return fees.length > 0 ? fees.reduce((s, v) => s + v, 0) : 0; })(), displayValue: (() => { const fees = filteredCandidates.map((c) => Number(c.placementFee || 0)).filter((v) => v > 0); const total = fees.reduce((s, v) => s + v, 0); return total > 0 ? 'R ' + total.toLocaleString() : 'R 0'; })(), previous: (() => { if (!previousCandidates) return 0; const fees = previousCandidates.map((c) => Number(c.placementFee || 0)).filter((v) => v > 0); return fees.reduce((s, v) => s + v, 0); })() },
      { key: 'funnel_health', label: 'Funnel Health', value: (() => { if (!conversionRates || !conversionRates.length) return null; return Math.min(100, Math.round(conversionRates.reduce((s, x) => s + (x.value || 0), 0) / conversionRates.length)); })(), displayValue: (() => { if (!conversionRates || !conversionRates.length) return 'N/A'; return Math.min(100, Math.round(conversionRates.reduce((s, x) => s + (x.value || 0), 0) / conversionRates.length)) + '/100'; })(), previous: null },
    ].map((item) => ({ ...item, ...toDelta(item.value, item.previous) }));
    const kpiDeltasFull = [...kpiDeltas, ...kpiDeltasExtra];
    const qohHired = filteredCandidates.filter((c) => c.status === 'hired').length;
    const qohCoverage = qohHired > 0 ? Math.round((qohCandidates.length / qohHired) * 100) : 0;
    // By source
    const _qohSrcMap = {};
    qohCandidates.forEach((c) => {
      const src = c.source || 'Unknown';
      if (!_qohSrcMap[src]) _qohSrcMap[src] = { scores: [], count: 0 };
      if (c.qohScore90 != null) _qohSrcMap[src].scores.push(c.qohScore90);
      if (c.qohScore180 != null) _qohSrcMap[src].scores.push(c.qohScore180);
      _qohSrcMap[src].count += 1;
    });
    const qohBySource = Object.entries(_qohSrcMap).map(([source, d]) => ({
      source,
      count: d.count,
      avgScore: d.scores.length > 0 ? d.scores.reduce((s, v) => s + v, 0) / d.scores.length : null,
    })).sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0)).slice(0, 6);
    // By recruiter
    const _qohRecMap = {};
    qohCandidates.forEach((c) => {
      const recId = c.recruiterId || recruiterUser?.uid || 'unknown';
      const recName = agencyMembers.find((m) => m.id === recId)?.name || 'You';
      if (!_qohRecMap[recId]) _qohRecMap[recId] = { name: recName, scores: [], count: 0 };
      if (c.qohScore90 != null) _qohRecMap[recId].scores.push(c.qohScore90);
      if (c.qohScore180 != null) _qohRecMap[recId].scores.push(c.qohScore180);
      _qohRecMap[recId].count += 1;
    });
    const qohByRecruiter = Object.values(_qohRecMap).map((d) => ({
      ...d,
      avgScore: d.scores.length > 0 ? d.scores.reduce((s, v) => s + v, 0) / d.scores.length : null,
    })).sort((a, b) => (b.avgScore || 0) - (a.avgScore || 0));
    // 6-month 90d trend
    const _qohNow = new Date();
    const qohTrend = Array.from({ length: 6 }, (_, i) => {
      const d = new Date(_qohNow.getFullYear(), _qohNow.getMonth() - (5 - i), 1);
      const shortLabel = d.toLocaleString('default', { month: 'short' });
      const subset = qohCandidates.filter((c) => {
        const rev = new Date(c.qohReviewedAt || '').getTime();
        const start = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
        const end = new Date(d.getFullYear(), d.getMonth() + 1, 0, 23, 59, 59, 999).getTime();
        return Number.isFinite(rev) && rev >= start && rev <= end;
      });
      const s90 = subset.map((c) => c.qohScore90).filter((v) => v != null);
      return { shortLabel, avg: s90.length > 0 ? s90.reduce((a, b) => a + b, 0) / s90.length : null };
    });

    // ── EEOC reporting ──────────────────────────────────────────────────────
    const eeocCandidates = filteredCandidates.filter((c) => c.eeocSelfId && Object.keys(c.eeocSelfId).length > 0);
    const eeocTotal = eeocCandidates.length;
    const eeocResponseRate = filteredCandidates.length > 0 ? Math.round((eeocTotal / filteredCandidates.length) * 100) : 0;
    const _buildEeocGroup = (key, values) => values.map((v) => {
      const inPipeline = eeocCandidates.filter((c) => (c.eeocSelfId[key] || '') === v).length;
      const hired = eeocCandidates.filter((c) => (c.eeocSelfId[key] || '') === v && c.status === 'hired').length;
      const selectRate = inPipeline > 0 ? Math.round((hired / inPipeline) * 100) : null;
      return { [key]: v, inPipeline, hired, selectRate };
    }).filter((r) => r.inPipeline > 0);
    const GENDER_LABELS = { man: 'Man', woman: 'Woman', nonbinary: 'Non-binary', self_describe: 'Self-describe', not_disclosed: 'Prefer not to answer' };
    const RACE_LABELS   = { hispanic: 'Hispanic / Latino', white: 'White', black: 'Black / African American', native: 'Am. Indian / Alaska Native', asian: 'Asian', pacific: 'Native Hawaiian / Pacific Islander', two_or_more: 'Two or More Races', not_disclosed: 'Prefer not to answer' };
    const eeocGenderBreakdown = _buildEeocGroup('gender', Object.keys(GENDER_LABELS)).map((r) => ({ ...r, label: GENDER_LABELS[r.gender] || r.gender, gender: r.gender }));
    const _raceRows = _buildEeocGroup('race', Object.keys(RACE_LABELS)).map((r) => ({ ...r, race: r.race, label: RACE_LABELS[r.race] || r.race }));
    const _raceSelectMax = Math.max(1, ..._raceRows.map((r) => r.selectRate || 0));
    const eeocAdverseImpact = _raceRows.map((r) => ({
      ...r,
      ratio: _raceSelectMax > 0 && r.selectRate != null ? Math.round((r.selectRate / _raceSelectMax) * 100) : null,
      flagged: _raceSelectMax > 0 && r.selectRate != null && (r.selectRate / _raceSelectMax) < 0.8,
    }));
    const eeocFlaggedGroups = eeocAdverseImpact.filter((r) => r.flagged).length;

    // ── DEI self-ID by source ──────────────────────────────────────────────────
    const deiSelfIdBySource = (() => {
      const srcSet = new Set(filteredCandidates.map(c => c.source || 'Unknown'));
      return Array.from(srcSet).map(src => {
        const srcCands = filteredCandidates.filter(c => (c.source || 'Unknown') === src);
        const eeoc = srcCands.filter(c => c.eeocSelfId && Object.keys(c.eeocSelfId).length > 0);
        const advanced = srcCands.filter(c => ['interview', 'offer', 'hired'].includes(c.status || '')).length;
        const passPct = srcCands.length >= 3 ? Math.round(advanced / srcCands.length * 100) : null;
        const genders = {};
        eeoc.forEach(c => { const g = c.eeocSelfId?.gender || 'not_disclosed'; genders[g] = (genders[g] || 0) + 1; });
        return { source: src, total: srcCands.length, eeocCount: eeoc.length, genders, passPct };
      }).filter(r => r.total >= 1).sort((a, b) => b.total - a.total).slice(0, 8);
    })();

    // ── Industry benchmark comparisons ───────────────────────────────────────
    const _ttfKpi = kpiDeltasFull.find((k) => k.key === 'time_to_fill');
    const _offerKpi = kpiDeltasFull.find((k) => k.key === 'offer_acceptance');
    const benchmarkItems = [
      { label: 'Time to Fill',        unit: 'days', p50: 28,   p75: 45,   yourValue: _ttfKpi?.value != null ? Math.round(_ttfKpi.value) : null,                                 lowerIsBetter: true  },
      { label: 'Apply → Screened',    unit: '%',    p50: 40,   p75: 60,   yourValue: conversionRates.find((r) => r.key === 'apply_to_screen')?.value ?? null,                 lowerIsBetter: false },
      { label: 'Screen → Interview',  unit: '%',    p50: 50,   p75: 70,   yourValue: conversionRates.find((r) => r.key === 'screen_to_interview')?.value ?? null,             lowerIsBetter: false },
      { label: 'Interview → Offer',   unit: '%',    p50: 25,   p75: 40,   yourValue: conversionRates.find((r) => r.key === 'interview_to_offer')?.value ?? null,              lowerIsBetter: false },
      { label: 'Offer → Hire',        unit: '%',    p50: 75,   p75: 90,   yourValue: conversionRates.find((r) => r.key === 'offer_to_hire')?.value ?? null,                  lowerIsBetter: false },
      { label: 'Offer Acceptance',    unit: '%',    p50: 70,   p75: 85,   yourValue: _offerKpi?.value != null ? Math.round(_offerKpi.value) : null,                           lowerIsBetter: false },
      { label: 'Screening Pass Rate', unit: '%',    p50: 45,   p75: 65,   yourValue: scorecardPassRate,                                                                        lowerIsBetter: false },
      { label: 'Cost per Hire',       unit: '$',    p50: 4700, p75: 7200, yourValue: costPerHireValue,                                                                        lowerIsBetter: true  },
    ];

    // ── Forecast gap analysis ─────────────────────────────────────────
    const forecastBarWidth = Math.min(100, Math.round(forecastAttainmentPct));
    const forecastHiresToTarget = Math.max(0, forecastMonthlyHireTarget - hiresInRange);
    const daysElapsed = (Date.now() - rangeStartMs) / 86400000;
    const daysTotal = rangeSpanMs / 86400000;
    const daysRemaining = Math.max(0, daysTotal - daysElapsed);
    const forecastDailyRateNeeded = forecastHiresToTarget > 0 && daysRemaining > 0
      ? (forecastHiresToTarget / daysRemaining).toFixed(2) : '0.00';
    const forecastProgressPct = daysTotal > 0 ? Math.min(100, Math.round((daysElapsed / daysTotal) * 100)) : 0;

    // ── BI / JSON export — flagship v2 ───────────────────────────────────
    const handleExportBiJson = async () => {
      const snapshotPayload = {
        // Schema
        schemaVersion: 'flagship-v2',
        generated: new Date().toISOString(),
        scope: reportingRecruiterFilter || 'All',
        rangeDays: reportingRangeDays,
        timeframeLabel: reportingRangeDays === 7 ? 'Last 7 Days' : reportingRangeDays === 90 ? 'Last 90 Days' : 'Last 30 Days',
        // KPIs with delta vs prior period
        kpis: kpiDeltasFull.map((k) => ({ key: k.key, label: k.label, value: k.value, displayValue: k.displayValue, deltaPct: k.deltaPct, delta: k.delta })),
        // Financial
        costPerHire: { value: costPerHireValue, display: costPerHireDisplay, budget: cphBudget, hires: hiresInRange },
        // Forecast
        forecastAttainment: { pct: Math.round(forecastAttainmentPct), status: forecastStatusLabel, monthlyTarget: forecastMonthlyHireTarget, projectedRate: projectedMonthlyHires },
        // Scorecards
        scorecards: { total: scorecardResponses.length, avgScore: scorecardAvg, passRate: scorecardPassRate, passCount: scorecardPassCount, rejectCount: scorecardRejectCount },
        // Pipeline
        stageDistribution: stageVisuals.map((s) => ({ stage: s.label, count: s.count, share: s.share, color: s.color })),
        conversionFunnel: (conversionFunnel || []).map((s) => ({ stageId: s.stageId, label: s.label, count: s.count, conversionPct: s.conversionPct, dropoffPct: s.dropoffPct })),
        pipelineAging: (agingAnalysis || []).map((s) => ({ stageId: s.stageId, label: s.label, count: s.count, avgDays: s.avgDays })),
        totals: { candidatesTracked: stageVisuals.reduce((s, st) => s + st.count, 0), hires: hiresInRange, openJobs: openJobCount },
        // Sources
        sourceQuality: sourceQualityItems.map((s) => ({ source: s.label, count: s.count, hireRate: Math.round(s.hireRate), screeningRate: Math.round(s.screeningRate) })),
        sourceRoi: (sourceRoiItems || []).map((s) => ({ source: s.label, count: s.count, qualityScore: s.qualityScore, costPerHired: s.costPerHired, roiIndex: s.roiIndex })),
        // Activity
        activityTrend: activityTrendBuckets.map((b) => ({ date: b.label, label: b.label, value: b.value })),
        activityMix: (activityMixItems || []).map((a) => ({ type: a.label, count: a.count, share: a.share })),
        // Recruiters
        recruiterLeaderboard: (recruiterLeaderboard || []).map((r) => ({ name: r.name, candidates: r.candidates, hires: r.hires, fillRate: r.fillRate })),
      };

      // ── Download locally ──────────────────────────────────────────────────
      const json = JSON.stringify(snapshotPayload, null, 2);
      const blob = new Blob([json], { type: 'application/json;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vision-bi-' + new Date().toISOString().slice(0, 10) + '.json';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      // ── Persist to Firestore via saveAnalyticsSnapshot CF ────────────────
      if (!recruiterUser?.uid || !profile?.agencyKey) return;
      setBiSnapshotSaveStatus('saving');
      try {
        const functionsClient = getFunctions(app, 'us-central1');
        const saveSnapshot = httpsCallable(functionsClient, 'saveAnalyticsSnapshot');
        await saveSnapshot({ agencyId: profile.agencyKey, snapshot: snapshotPayload });
        setBiSnapshotSaveStatus('saved');
        setTimeout(() => setBiSnapshotSaveStatus('idle'), 4000);
      } catch (err) {
        console.error('saveAnalyticsSnapshot error', err);
        setBiSnapshotSaveStatus('error');
        setTimeout(() => setBiSnapshotSaveStatus('idle'), 5000);
      }
    };

    const canSeeAgencyAdminSections = isAgencyOwner || ['owner', 'admin'].includes(normalizeWorkspaceRole(currentWorkspaceRole || profile.accountRole));
    const isAgencyMemberAccess = Boolean(profile.agencyKey && recruiterUser?.uid);
    const agencySectionOptions = [
      { id: 'profile', locked: false, reason: '' },
      { id: 'team', locked: !isAgencyMemberAccess, reason: 'Agency membership required' },
      { id: 'revenue', locked: !(canSeeAgencyAdminSections || canAccess('revenue.read')), reason: 'Finance access required' },
      { id: 'reporting', locked: !(canSeeAgencyAdminSections || canAccess('audit.read')), reason: 'Agency analytics access required' },
      { id: 'launch', locked: !(canSeeAgencyAdminSections || canAccess('jobs.publish')), reason: 'Publishing access required' },
      { id: 'migration', locked: !(canSeeAgencyAdminSections || canAccess('settings.write')), reason: 'Admin migration access required' },
      { id: 'danger', locked: !isAgencyOwner, reason: 'Owner destructive access required' },
    ];

    return (
      <div className="space-y-5">

        <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">

            <div>

              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Agency Workspace</div>

              <div className="mt-2 text-2xl font-black text-white">Profile, team, and reporting in one admin surface.</div>

              <div className="mt-2 text-sm text-slate-400">Use the section switcher to move between the agent profile, team controls, and agency-level reporting.</div>

            </div>

            <div className="flex flex-wrap gap-2 rounded-full border border-white/10 bg-slate-950/60 p-1">

              {agencySectionOptions.map((section) => (

                <button

                  key={section.id}

                  type="button"

                  onClick={() => {
                    if (section.locked) {
                      setAgencyNotice(`${section.id.replace(/_/g, ' ')} is locked for your current role. ${section.reason}.`);
                      return;
                    }
                    handleSetAgencySection(section.id);
                  }}

                  title={section.locked ? section.reason : ''}

                  aria-disabled={section.locked}

                  className={`rounded-full px-4 py-2 text-micro font-black uppercase tracking-[0.18em] transition ${agencySection === section.id ? 'bg-cyan-500 text-slate-950' : section.locked ? 'border border-white/5 bg-white/[0.02] text-slate-600' : 'text-slate-300 hover:bg-white/5 hover:text-white'}`}

                >

                  {section.locked ? `${section.id} · locked` : section.id}

                </button>

              ))}

            </div>

          </div>

          {agencyNotice && (

            <div className="mt-4 rounded-card border border-cyan-500/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">{agencyNotice}</div>

          )}

        </div>



        <div id="agency-section-body" className="scroll-mt-6" />

        {agencySection === 'migration' && (
          <div className="space-y-5">
            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Migration Command Center</div>
                  <div className="mt-2 text-2xl font-black text-white">Move an agency into Vision without starting fresh.</div>
                  <div className="mt-2 max-w-3xl text-sm leading-6 text-slate-400">Run a dry migration first, review duplicate risk, recover attachments where possible, score readiness, then import with rollback protection.</div>
                </div>
                <div className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-cyan-100">Rollback protected</div>
              </div>
              {migrationNotice && <div className="mt-4 rounded-card border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-sm text-cyan-100">{migrationNotice}</div>}
              <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1fr_auto]">
                <label className="block">
                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Source platform</div>
                  <select value={migrationSource} onChange={(event) => setMigrationSource(event.target.value)} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">
                    {Object.entries(MIGRATION_SOURCE_PRESETS).map(([id, preset]) => <option key={id} value={id}>{preset.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Entity</div>
                  <select value={migrationEntity} onChange={(event) => setMigrationEntity(event.target.value)} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400">
                    {MIGRATION_ENTITY_OPTIONS.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
                  </select>
                </label>
                <div className="flex items-end">
                  <button type="button" onClick={handleRunMigrationDryRun} disabled={migrationBusy} className="w-full rounded-full bg-cyan-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60">{migrationBusy ? 'Working...' : 'Run dry run'}</button>
                </div>
              </div>
            </div>

            {migrationPreview && (
              <div className="grid gap-5 xl:grid-cols-[0.95fr_1.05fr]">
                <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xs font-black uppercase tracking-[0.2em] text-cyan-300">Dry Run Report</div>
                      <div className="mt-2 text-xl font-black text-white">{migrationPreview.sourceLabel} · {migrationPreview.entity}</div>
                      <div className="mt-1 text-xs text-slate-500">{migrationPreview.fileName || migrationFileName}</div>
                    </div>
                    <div className={`rounded-full border px-4 py-2 text-micro font-black uppercase tracking-[0.14em] ${migrationPreview.readiness >= 85 ? 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200' : migrationPreview.readiness >= 70 ? 'border-amber-400/25 bg-amber-500/10 text-amber-100' : 'border-rose-400/25 bg-rose-500/10 text-rose-100'}`}>{migrationPreview.readiness}% ready</div>
                  </div>
                  <div className="mt-5 grid gap-3 sm:grid-cols-2">
                    {[
                      ['Rows', migrationPreview.rowsTotal],
                      ['Importable', migrationPreview.records.length],
                      ['Duplicates', migrationPreview.duplicates],
                      ['Attachments', `${migrationPreview.attachmentRecovery?.recovered || 0}/${migrationPreview.attachmentRecovery?.detected || 0}`],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{label}</div>
                        <div className="mt-2 text-2xl font-black text-white">{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 space-y-2">
                    {Object.entries(migrationPreview.readinessBreakdown || {}).map(([key, value]) => (
                      <div key={key} className="rounded-card border border-white/10 bg-slate-950/60 px-4 py-3">
                        <div className="flex items-center justify-between gap-3 text-xs">
                          <span className="font-black uppercase tracking-[0.14em] text-slate-400">{key.replace(/([A-Z])/g, ' $1')}</span>
                          <span className="font-black text-white">{value}%</span>
                        </div>
                        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-cyan-400" style={{ width: `${Math.max(4, Math.min(100, value))}%` }} /></div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <button type="button" onClick={handleCommitMigrationPreview} disabled={migrationBusy || migrationPreview.blockers?.length > 0} className="rounded-full bg-emerald-400 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-emerald-300 disabled:cursor-not-allowed disabled:opacity-50">Import now</button>
                    <button type="button" onClick={() => setMigrationPreview(null)} className="rounded-full border border-white/10 bg-white/5 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-200">Clear dry run</button>
                  </div>
                </div>

                <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-cyan-300">Risk Review</div>
                  <div className="mt-4 grid gap-3 md:grid-cols-3">
                    {[
                      ['Exact duplicates', migrationPreview.duplicateBreakdown?.exact || 0],
                      ['Likely duplicates', migrationPreview.duplicateBreakdown?.likely || 0],
                      ['Attachment review', migrationPreview.attachmentRecovery?.needsReview || 0],
                    ].map(([label, value]) => (
                      <div key={label} className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{label}</div>
                        <div className="mt-2 text-xl font-black text-white">{value}</div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 space-y-2">
                    {[...(migrationPreview.blockers || []), ...(migrationPreview.warnings || [])].length === 0 && <div className="rounded-card border border-emerald-400/20 bg-emerald-500/10 px-4 py-3 text-sm text-emerald-100">No blockers found. Vision can import this batch with rollback available.</div>}
                    {[...(migrationPreview.blockers || []), ...(migrationPreview.warnings || [])].map((item, index) => (
                      <div key={`${item}-${index}`} className={`rounded-card border px-4 py-3 text-sm ${index < (migrationPreview.blockers || []).length ? 'border-rose-400/20 bg-rose-500/10 text-rose-100' : 'border-amber-400/20 bg-amber-500/10 text-amber-100'}`}>{item}</div>
                    ))}
                  </div>
                  <div className="mt-5 rounded-card border border-white/10 bg-slate-950/60 p-4">
                    <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Mapped fields</div>
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {Object.entries(migrationPreview.fieldMap || {}).map(([field, header]) => (
                        <div key={field} className="flex items-center justify-between gap-3 rounded-ctl border border-white/10 bg-white/[0.03] px-3 py-2 text-xs">
                          <span className="text-slate-400">{field}</span>
                          <span className={header ? 'font-bold text-cyan-200' : 'text-slate-600'}>{header || 'unmapped'}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-cyan-300">Migration History</div>
              <div className="mt-4 space-y-3">
                {migrationBatches.length === 0 && <div className="rounded-card border border-dashed border-white/10 px-4 py-6 text-sm text-slate-500">No migration batches yet.</div>}
                {migrationBatches.map((batch) => (
                  <div key={batch.id} className="grid gap-3 rounded-card border border-white/10 bg-slate-950/60 p-4 lg:grid-cols-[1fr_auto] lg:items-center">
                    <div>
                      <div className="text-sm font-black text-white">{batch.sourceLabel} · {batch.entity}</div>
                      <div className="mt-1 text-xs text-slate-500">{batch.importedCount || 0} records · readiness {batch.readiness || 0}% · {batch.fileName || 'CSV'} · {batch.status}</div>
                    </div>
                    <button type="button" onClick={() => handleRollbackMigrationBatch(batch)} disabled={migrationBusy || batch.status === 'rolled_back'} className="rounded-full border border-rose-400/20 bg-rose-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-rose-100 transition hover:border-rose-300/40 disabled:cursor-not-allowed disabled:opacity-50">Rollback</button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {agencySection === 'profile' && (

          <div className="grid gap-5 xl:grid-cols-[1.05fr_0.95fr]">

            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Agent Work Profile</div>

              <div className="mt-3 flex flex-wrap items-center gap-3">

                <h2 className="text-2xl font-black text-white">{profile.name || 'Vision Agent'}</h2>

                <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-meta font-black uppercase tracking-[0.16em] text-cyan-200">{RBAC_ROLE_PRESETS[currentWorkspaceRole]?.label || 'Recruiter'}</span>

                <span className={`rounded-full border px-3 py-1 text-meta font-black uppercase tracking-[0.16em] ${profile.seatAssigned ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/20 bg-amber-500/10 text-amber-200'}`}>

                  {profile.seatAssigned ? 'Seat active' : 'Seat missing'}

                </span>

              </div>

              <div className="mt-2 text-sm text-slate-400">Every agent belongs to an agency, signs in with the agency-issued Vision profile password, and must have an assigned paid seat.</div>



              <form className="mt-6 grid gap-4 sm:grid-cols-2" onSubmit={handleSaveProfile}>

                <label className="block sm:col-span-2">

                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Agent name</div>

                  <input value={profile.name || ''} onChange={(event) => setProfile((current) => ({ ...current, name: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Full name" required />

                </label>

                <label className="block">

                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Agency</div>

                  <input value={profile.agency || ''} disabled className="w-full rounded-card border border-white/10 bg-slate-950/50 px-4 py-3 text-sm text-slate-300 outline-none" placeholder="Your agency" />

                  <div className="mt-2 text-xs leading-5 text-slate-500">Agency membership is managed by the agency owner.</div>

                </label>

                <label className="block">

                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Role title</div>

                  <input value={profile.title || ''} onChange={(event) => setProfile((current) => ({ ...current, title: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Senior Recruiter" required />

                </label>

                <label className="block">

                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Account role</div>

                  <input value={RBAC_ROLE_PRESETS[currentWorkspaceRole]?.label || 'Recruiter'} disabled className="w-full rounded-card border border-white/10 bg-slate-950/50 px-4 py-3 text-sm text-slate-300 outline-none" />

                </label>

                <label className="block">

                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Coverage location</div>

                  <input value={profile.location || ''} onChange={(event) => setProfile((current) => ({ ...current, location: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="Johannesburg, South Africa" required />

                </label>

                <label className="block">

                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Phone</div>

                  <input value={profile.phone || ''} onChange={(event) => setProfile((current) => ({ ...current, phone: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="+27 82 123 4567" />

                </label>

                <label className="block sm:col-span-2">

                  <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Vision profile password</div>

                  <input type="password" value={profile.agencySecret || ''} onChange={(event) => setProfile((current) => ({ ...current, agencySecret: event.target.value }))} disabled={!isAgencyOwner} className={`w-full rounded-card border px-4 py-3 text-sm outline-none transition ${isAgencyOwner ? 'border-white/10 bg-slate-950/70 text-white focus:border-cyan-400' : 'border-white/10 bg-slate-950/50 text-slate-300'}`} placeholder="Agency-issued Vision password" required />

                  <div className="mt-2 text-xs leading-5 text-slate-500">{isAgencyOwner ? 'Owners can rotate the agency-wide Vision profile password.' : 'Only the agency owner can change the agency-wide Vision profile password.'}</div>

                </label>

                <div className="sm:col-span-2 flex flex-wrap items-center gap-3">

                  <button type="submit" disabled={isSavingProfile} className="rounded-full bg-cyan-500 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-70">

                    {isSavingProfile ? 'Saving...' : 'Save work profile'}

                  </button>

                  <div className="rounded-full border border-white/10 bg-slate-950/60 px-4 py-3 text-xs uppercase tracking-[0.18em] text-slate-300">{profile.email || recruiterUser?.email}</div>

                </div>

              </form>

            </div>



            <div className="space-y-5">

              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

                <div className="flex flex-wrap items-center justify-between gap-3">

                  <div>

                    <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Agency Membership</div>

                    <div className="mt-2 text-sm text-slate-400">Seat allocation, membership status, and owner visibility for the full agency workspace.</div>

                  </div>

                  <span className={`rounded-full border px-3 py-1 text-meta font-black uppercase tracking-[0.16em] ${isAgencySubscriptionActive(agencyDetails) ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/20 bg-amber-500/10 text-amber-200'}`}>

                    {agencyDetails?.subscriptionStatus || agencyDetails?.status || 'unlinked'}

                  </span>

                </div>

                <div className="mt-4 grid gap-3 sm:grid-cols-2">

                  <div className="rounded-card bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Agency key</div>

                    <div className="mt-2 text-lg font-bold text-white">{profile.agencyKey || 'Not set'}</div>

                  </div>

                  <div className="rounded-card bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Membership</div>

                    <div className="mt-2 text-lg font-bold text-white">{currentMember?.seatStatus === 'active' ? 'Seat assigned' : 'Pending access'}</div>

                  </div>

                  <div className="rounded-card bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Seats in use</div>

                    <div className="mt-2 text-lg font-bold text-white">{agencyActiveSeats}{agencySeatLimit ? ` / ${agencySeatLimit}` : ''}</div>

                  </div>

                  <div className="rounded-card bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Seats available</div>

                    <div className="mt-2 text-lg font-bold text-white">{agencySeatLimit ? agencySeatsAvailable : 'Unknown'}</div>

                  </div>

                </div>

              </div>



              {isAgencyOwner && (
                <div className="rounded-shell border border-violet-500/20 bg-violet-500/5 p-5 sm:p-6">
                  <div className="mb-4">
                    <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Candidate Portal Branding</div>
                    <div className="mt-1 text-xs text-slate-400">Customise the candidate-facing careers and application pages distributed with your jobs.</div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Career Page Slug</label>
                      <div className="flex items-center gap-2">
                        <span className="whitespace-nowrap text-xs text-slate-500">{getPublicAppHost()}/careers/</span>
                        <input
                          type="text"
                          value={agencyBrand.agencySlug}
                          onChange={(e) => setAgencyBrand((b) => ({ ...b, agencySlug: e.target.value }))}
                          placeholder="my-agency"
                          className="flex-1 rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none"
                        />
                      </div>
                      {agencyBrand.agencySlug && (
                        <div className="mt-1 text-xs text-violet-400">
                          <a href={`${getPublicAppBaseUrl()}/careers/${agencyBrand.agencySlug}`} target="_blank" rel="noopener noreferrer" className="underline">
                            {getPublicAppHost()}/careers/{agencyBrand.agencySlug}
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="sm:col-span-2">
                      <div className="mb-2 text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Careers imagery</div>
                      <div className="grid gap-3 sm:grid-cols-3">
                        {[
                          ['heroImageUrl', 'Hero image', 'Wide image for the public careers hero'],
                          ['aboutImageUrl', 'Agency story image', 'Image shown beside your agency story'],
                          ['teamImageUrl', 'Team image', 'Image used in the team spotlight'],
                        ].map(([fieldName, label, description]) => (
                          <div key={fieldName} className="rounded-card border border-white/10 bg-slate-900/60 p-3">
                            <div className="text-xs font-bold text-slate-200">{label}</div>
                            <div className="mt-1 min-h-10 text-[11px] leading-4 text-slate-500">{description}</div>
                            {agencyBrand[fieldName] && <img src={agencyBrand[fieldName]} alt={`${label} preview`} className="mt-2 h-20 w-full rounded-lg border border-white/10 object-cover" />}
                            <label className="mt-3 inline-flex w-full cursor-pointer justify-center rounded-full border border-white/10 bg-white/5 px-3 py-2 text-[11px] font-black uppercase tracking-[0.1em] text-slate-300 transition hover:border-violet-400/50 hover:text-white">
                              {agencyLogoUploading ? 'Uploading...' : agencyBrand[fieldName] ? 'Replace image' : 'Upload image'}
                              <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={(event) => handleAgencyBrandImageUpload(fieldName, event)} disabled={agencyLogoUploading} className="sr-only" />
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Accent Colour</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="color"
                          value={agencyBrand.brandColor}
                          onChange={(e) => setAgencyBrand((b) => ({ ...b, brandColor: e.target.value }))}
                          className="h-9 w-12 cursor-pointer rounded-ctl border border-white/10 bg-transparent"
                        />
                        <input
                          type="text"
                          value={agencyBrand.brandColor}
                          onChange={(e) => setAgencyBrand((b) => ({ ...b, brandColor: e.target.value }))}
                          maxLength={9}
                          className="flex-1 rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm font-mono text-white focus:border-violet-500 focus:outline-none"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Portal Theme</label>
                      <select
                        value={agencyBrand.portalTheme || DEFAULT_AGENCY_BRAND.portalTheme}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, portalTheme: e.target.value }))}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white focus:border-violet-500 focus:outline-none"
                      >
                        <option value="midnight">Midnight</option>
                        <option value="light">Light</option>
                        <option value="executive">Executive</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Logo URL</label>
                      <input
                        type="url"
                        value={agencyBrand.logoUrl}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, logoUrl: e.target.value }))}
                        placeholder="https://example.com/logo.png"
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none"
                      />
                      <label className="mt-2 inline-flex cursor-pointer items-center rounded-full border border-violet-400/25 bg-violet-500/10 px-3 py-2 text-xs font-black uppercase tracking-[0.12em] text-violet-200 transition hover:border-violet-300/50 hover:bg-violet-500/20">
                        {agencyLogoUploading ? 'Uploading logo...' : 'Upload logo image'}
                        <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" onChange={handleAgencyLogoUpload} disabled={agencyLogoUploading} className="sr-only" />
                      </label>
                      {agencyBrand.logoUrl && (
                        <div className="mt-2 flex items-center gap-2 text-xs text-slate-400">
                          <img src={agencyBrand.logoUrl} alt={`${profile.agency || 'Agency'} logo preview`} className="h-10 w-10 rounded-lg border border-white/10 bg-white object-contain p-1" />
                          <span>Shown on the candidate careers link header, agency panel, and footer.</span>
                        </div>
                      )}
                      {agencyLogoSuggestions.length > 0 && (
                        <div className="mt-3 rounded-card border border-violet-400/15 bg-violet-500/[0.06] p-3">
                          <div className="text-xs font-bold text-violet-200">Suggested colors from your logo</div>
                          <div className="mt-2 flex flex-wrap gap-2">
                            {agencyLogoSuggestions.map((color) => (
                              <button
                                key={color}
                                type="button"
                                onClick={() => setAgencyBrand((current) => ({ ...current, brandColor: color }))}
                                className={`flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-xs font-bold transition ${agencyBrand.brandColor.toLowerCase() === color.toLowerCase() ? 'border-white/50 bg-white/10 text-white' : 'border-white/10 bg-slate-900 text-slate-300 hover:border-white/30'}`}
                                title={`Use ${color} as your candidate portal accent`}
                              >
                                <span className="h-4 w-4 rounded-full border border-white/30" style={{ backgroundColor: color }} />
                                {color}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Candidate Contact Email</label>
                      <input
                        type="email"
                        value={agencyBrand.contactEmail}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, contactEmail: e.target.value }))}
                        placeholder="careers@your-agency.com"
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Website URL</label>
                      <input
                        type="url"
                        value={agencyBrand.agencyWebsite}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, agencyWebsite: e.target.value }))}
                        placeholder="https://your-agency.com"
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none"
                      />
                    </div>
                    <div>
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">LinkedIn URL</label>
                      <input
                        type="url"
                        value={agencyBrand.linkedInUrl}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, linkedInUrl: e.target.value }))}
                        placeholder="https://www.linkedin.com/company/your-agency"
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Portal Headline</label>
                      <input
                        type="text"
                        value={agencyBrand.portalHeadline}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, portalHeadline: e.target.value }))}
                        placeholder="Career opportunities represented by your agency"
                        maxLength={110}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Tagline</label>
                      <input
                        type="text"
                        value={agencyBrand.tagline}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, tagline: e.target.value }))}
                        placeholder="We connect top talent with great companies"
                        maxLength={160}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">About (shown on careers page)</label>
                      <textarea
                        value={agencyBrand.agencyAbout}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, agencyAbout: e.target.value }))}
                        rows={3}
                        placeholder="Tell candidates a bit about your agency..."
                        maxLength={600}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none resize-y"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Candidate Promise</label>
                      <textarea
                        value={agencyBrand.candidatePromise}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, candidatePromise: e.target.value }))}
                        rows={3}
                        placeholder="Explain how your agency handles candidate confidentiality, feedback, and recruiter follow-up."
                        maxLength={520}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none resize-y"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1 block text-xs font-bold uppercase tracking-[0.14em] text-slate-500">Hiring Process Notes</label>
                      <textarea
                        value={agencyBrand.hiringProcess}
                        onChange={(e) => setAgencyBrand((b) => ({ ...b, hiringProcess: e.target.value }))}
                        rows={3}
                        placeholder="Example: Apply with your CV, complete screening questions, then a recruiter will contact shortlisted candidates."
                        maxLength={520}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-500 focus:outline-none resize-y"
                      />
                    </div>
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-3">
                    <button
                      type="button"
                      onClick={handleSaveAgencyBrand}
                      disabled={agencyBrandSaving}
                      className="rounded-full bg-violet-500 px-5 py-2.5 text-xs font-black uppercase tracking-[0.18em] text-white transition hover:bg-violet-400 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {agencyBrandSaving ? 'Saving...' : 'Save Brand'}
                    </button>
                    <a href="/candidate-tools" className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-5 py-2.5 text-xs font-black uppercase tracking-[0.18em] text-emerald-100 transition hover:border-emerald-300/40 hover:bg-emerald-500/15 hover:text-white">
                      Candidate Tools
                    </a>
                    {agencyBrandMsg && (
                      <span className={`text-xs font-semibold ${agencyBrandMsg.startsWith('✓') ? 'text-green-400' : 'text-amber-400'}`}>{agencyBrandMsg}</span>
                    )}
                  </div>
                </div>
              )}

              {isAgencyOwner && (

                <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

                  <div className="flex flex-wrap items-center justify-between gap-3">

                    <div>

                      <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Billing And Seats</div>

                      <div className="mt-2 text-sm text-slate-400">Solo recruiter: USD $25 per month. Team seats: USD $60 per user per month. After you submit a seat request, the Vision team will email you a custom PayFlex invoice.</div>

                    </div>

                  </div>

                  <form className="mt-5 grid gap-4 sm:grid-cols-2" onSubmit={handleSaveAgencySettings}>

                    <label className="flex gap-3 rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-xs leading-5 text-slate-300 sm:col-span-2">
                      <input
                        type="checkbox"
                        checked={Boolean(agencySettings.lawfulUseAccepted)}
                        onChange={(event) => setAgencySettings((current) => ({ ...current, lawfulUseAccepted: event.target.checked }))}
                        className="mt-1 h-4 w-4 accent-cyan-400"
                        required
                      />
                      <span>I confirm this agency will use Vision only for lawful recruitment activity, with a lawful basis or consent for personal information, no unlawful discrimination, no spam, no unlawful scraping, and no unauthorized sharing of candidate, client, member, billing, or agency data.</span>
                    </label>

                    <label className="block">

                      <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Paid seat limit</div>

                      <div className="flex items-center overflow-hidden rounded-card border border-white/10 bg-slate-950/70">
                        <button
                          type="button"
                          onClick={() => {
                            const minimum = Math.max(agencyActiveSeats, 1);
                            setAgencySettings((current) => ({ ...current, seatLimit: String(Math.max(minimum, Number(current.seatLimit || minimum) - 1)) }));
                          }}
                          disabled={selectedSeatLimit <= Math.max(agencyActiveSeats, 1)}
                          className="h-12 w-12 border-r border-white/10 text-lg font-black text-cyan-200 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:text-slate-700"
                          aria-label="Decrease paid seat limit"
                        >
                          -
                        </button>
                        <div className="flex min-w-0 flex-1 flex-col items-center justify-center px-4 py-2">
                          <div className="text-2xl font-black text-white">{selectedSeatLimit}</div>
                          <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">paid seats</div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setAgencySettings((current) => ({ ...current, seatLimit: String(Math.min(Number(current.seatLimit || 1) + 1, 250)) }))}
                          disabled={selectedSeatLimit >= 250}
                          className="h-12 w-12 border-l border-white/10 text-lg font-black text-cyan-200 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:text-slate-700"
                          aria-label="Increase paid seat limit"
                        >
                          +
                        </button>
                      </div>
                      <div className="mt-2 text-meta leading-5 text-slate-500">Current paid allowance: {agencySeatLimit || 1}. Active assigned seats: {agencyActiveSeats}. Your workspace stays active while a seat-change invoice is prepared.</div>
                      {seatLimitChanged && (
                        <div className="mt-3 rounded-card border border-cyan-400/20 bg-cyan-500/10 px-4 py-3 text-meta leading-5 text-cyan-100">
                          You selected {selectedSeatLimit} seat{selectedSeatLimit === 1 ? '' : 's'}, which differs from your current allowance. Submit will send a plan-change request instead of directly changing access.
                        </div>
                      )}

                    </label>

                    <label className="block sm:col-span-2">

                      <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Billing contact email</div>

                      <input value={agencySettings.billingContactEmail} onChange={(event) => setAgencySettings((current) => ({ ...current, billingContactEmail: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400" placeholder="finance@agency.com" />

                    </label>

                    <div className="sm:col-span-2 grid gap-3 sm:grid-cols-3 rounded-card border border-white/10 bg-slate-950/40 p-4">
                      <label className="flex items-center gap-2 text-xs text-slate-300">
                        <input type="checkbox" checked={agencySettings.reportingScheduleEnabled} onChange={(event) => setAgencySettings((current) => ({ ...current, reportingScheduleEnabled: event.target.checked }))} className="accent-cyan-400 w-4 h-4" />
                        Enable schedule
                      </label>
                      <select value={agencySettings.reportingScheduleFrequency} onChange={(event) => setAgencySettings((current) => ({ ...current, reportingScheduleFrequency: event.target.value }))} className="rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-xs text-white">
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                      <select value={agencySettings.reportingScheduleDay} onChange={(event) => setAgencySettings((current) => ({ ...current, reportingScheduleDay: event.target.value }))} className="rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-xs text-white">
                        <option value="monday">Monday</option>
                        <option value="wednesday">Wednesday</option>
                        <option value="friday">Friday</option>
                      </select>
                      <input value={agencySettings.reportingScheduleRecipients} onChange={(event) => setAgencySettings((current) => ({ ...current, reportingScheduleRecipients: event.target.value }))} className="sm:col-span-3 rounded-card border border-white/10 bg-slate-900 px-3 py-2 text-xs text-white outline-none transition focus:border-cyan-400" placeholder="ops@agency.com, owner@agency.com" />
                    </div>

                    <div className="sm:col-span-2 grid gap-3 sm:grid-cols-3 rounded-card border border-white/10 bg-slate-950/40 p-4">
                      <label className="block">
                        <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Monthly hire target</div>
                        <input type="number" min="1" value={agencySettings.forecastMonthlyHireTarget} onChange={(event) => setAgencySettings((current) => ({ ...current, forecastMonthlyHireTarget: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-900 px-3 py-2 text-xs text-white" />
                      </label>
                      <label className="block">
                        <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Monthly revenue target (R)</div>
                        <input type="number" min="0" value={agencySettings.monthlyRevenueTarget || '0'} onChange={(event) => setAgencySettings((current) => ({ ...current, monthlyRevenueTarget: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-900 px-3 py-2 text-xs text-white" placeholder="e.g. 500000" />
                      </label>
                      <label className="block">
                        <div className="mb-2 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Agent metric keys (comma-separated)</div>
                        <input value={agencySettings.governanceAgentMetricKeys.join(',')} onChange={(event) => setAgencySettings((current) => ({ ...current, governanceAgentMetricKeys: event.target.value.split(',').map((item) => item.trim()).filter(Boolean) }))} className="w-full rounded-card border border-white/10 bg-slate-900 px-3 py-2 text-xs text-white outline-none transition focus:border-cyan-400" placeholder="candidate_changes,job_changes,funnel_overview" />
                      </label>
                    </div>

                    <div className="sm:col-span-2 flex flex-wrap items-center gap-3">

                      <button type="submit" disabled={isUpdatingAgencySettings || billingRequestPlanId === 'settings-seat-change'} className={`rounded-full px-4 py-3 text-xs font-black uppercase tracking-[0.18em] transition disabled:cursor-not-allowed disabled:opacity-70 ${seatLimitChanged ? 'bg-cyan-500 text-slate-950 hover:bg-cyan-400' : 'border border-cyan-400/20 bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20'}`}>

                        {isUpdatingAgencySettings || billingRequestPlanId === 'settings-seat-change' ? 'Sending...' : seatLimitChanged ? 'Send seat change request' : 'Save billing settings'}

                      </button>

                      <button type="button" onClick={handleRequestSubscriptionCancellation} disabled={isUpdatingAgencySettings} className="rounded-full border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-rose-200 transition hover:bg-rose-500/20 disabled:cursor-not-allowed disabled:opacity-70">
                        Request cancellation
                      </button>

                    </div>

                  </form>

                </div>

              )}

            </div>

          </div>

        )}


        {agencySection === 'revenue' && renderRevenue()}

        {agencySection === 'launch' && renderLaunchReadiness()}



        {agencySection === 'danger' && (
          <div className="rounded-shell border border-rose-500/30 bg-rose-950/20 p-5 sm:p-6">
            <div className="text-xs font-black uppercase tracking-[0.24em] text-rose-400">Danger Zone</div>
            <div className="mt-2 text-sm text-slate-400">These actions are permanent and cannot be undone.</div>
            <div className="mt-6 space-y-4">
              <div className="rounded-card border border-rose-500/20 bg-rose-950/30 p-5">
                <div className="text-sm font-bold text-white mb-1">Delete all candidates</div>
                <div className="text-xs text-slate-400 mb-4">Permanently removes every candidate from your pipeline. Use this to clear imported or test data before going live.</div>
                <button type="button" onClick={handleDeleteAllCandidates} disabled={isDeletingAllCandidates} className="rounded-ctl border border-rose-400/40 bg-rose-500/15 px-5 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-rose-300 transition hover:bg-rose-500/25 disabled:opacity-50">
                  {isDeletingAllCandidates ? 'Deleting…' : 'Delete All Candidates'}
                </button>
              </div>
            </div>
          </div>
        )}

        {agencySection === 'team' && (

          <div className="grid gap-5 xl:grid-cols-[1.05fr_0.95fr]">

            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Agency Members</div>

              <div className="mt-2 text-sm text-slate-400">Seat assignment, membership visibility, and the current team roster for this agency.</div>

              <div className="mt-4 space-y-3">

                {agencyMembers.length === 0 && (

                  <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">

                    No agency member records have been created yet.

                  </div>

                )}

                {agencyMembers.map((member) => (

                  <div key={member.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="flex flex-wrap items-center justify-between gap-3">

                      <div>

                        <div className="text-sm font-bold text-white">{member.name || member.email || member.id}</div>

                        <div className="mt-1 text-xs text-slate-400">{member.email || 'No email recorded'}</div>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {getRolePermissions(member.role, member.customPermissions).slice(0, 6).map((permission) => (
                            <span key={permission} className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-micro font-bold text-slate-400">{permission === '*' ? 'full access' : permission}</span>
                          ))}
                        </div>

                      </div>

                      <div className="flex flex-wrap gap-2">

                        <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.16em] text-cyan-200">{RBAC_ROLE_PRESETS[normalizeWorkspaceRole(member.role)]?.label || member.role || 'Recruiter'}</span>

                        <span className={`rounded-full border px-3 py-1 text-micro font-black uppercase tracking-[0.16em] ${member.seatAssigned ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/20 bg-amber-500/10 text-amber-200'}`}>

                          {member.seatAssigned ? member.seatStatus || 'active' : 'unassigned'}

                        </span>

                        {canAccess('team.editRoles') && member.role !== 'owner' && (
                          <select
                            value={normalizeWorkspaceRole(member.role)}
                            onChange={(event) => handleUpdateMemberRole(member, event.target.value)}
                            disabled={activeMemberActionId === member.id}
                            className="rounded-full border border-white/10 bg-slate-900 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-slate-200 outline-none transition focus:border-cyan-400 disabled:opacity-60"
                          >
                            {Object.entries(RBAC_ROLE_PRESETS).filter(([role]) => role !== 'owner').map(([role, preset]) => (
                              <option key={role} value={role}>{preset.label}</option>
                            ))}
                          </select>
                        )}

                        {isAgencyOwner && member.role !== 'owner' && (

                          <button type="button" onClick={() => handleToggleMemberSeat(member)} disabled={activeMemberActionId === member.id || (!member.seatAssigned && agencySeatsAvailable <= 0)} title={!member.seatAssigned && agencySeatsAvailable <= 0 ? 'No seats available — upgrade your plan to add more members' : undefined} className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white disabled:cursor-not-allowed disabled:opacity-60">

                            {activeMemberActionId === member.id ? 'Updating...' : member.seatAssigned ? 'Revoke seat' : 'Assign seat'}

                          </button>

                        )}

                      </div>

                    </div>

                  </div>

                ))}

              </div>

              <form onSubmit={handleSaveTimeManagement} className="mt-5 rounded-shell border border-cyan-300/15 bg-cyan-500/5 p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-micro font-black uppercase tracking-[0.2em] text-cyan-300">Owner Team Controls</div>
                    <div className="mt-2 text-lg font-black text-white">Time management and reminders</div>
                    <p className="mt-1 text-xs leading-5 text-slate-400">Set the operating cadence recruiters see on the pipeline. This is where the owner controls daily priorities, reminders, and schedule blocks for the team.</p>
                  </div>
                  <span className="rounded-full border border-white/10 bg-slate-950/60 px-3 py-1 text-micro font-black uppercase tracking-[0.14em] text-slate-300">{isAgencyOwner ? 'Owner editable' : 'Owner only'}</span>
                </div>
                <div className="mt-4 grid gap-3">
                  <label className="block">
                    <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-400">Calendar day</div>
                    <input type="date" value={timeManagement.calendarDate} disabled={!isAgencyOwner || isSavingTimeManagement} onChange={(event) => setTimeManagement((current) => ({ ...current, calendarDate: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none transition focus:border-cyan-400 disabled:cursor-not-allowed disabled:opacity-60" />
                  </label>
                  <label className="block">
                    <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-400">Schedule blocks</div>
                    <textarea value={timeManagement.scheduleItems} disabled={!isAgencyOwner || isSavingTimeManagement} onChange={(event) => setTimeManagement((current) => ({ ...current, scheduleItems: event.target.value }))} rows={4} className="w-full resize-y rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm leading-6 text-white outline-none transition focus:border-cyan-400 disabled:cursor-not-allowed disabled:opacity-60" placeholder={'09:00 Pipeline standup\n11:00 Client follow-ups\n15:00 Candidate shortlist review'} />
                  </label>
                  <label className="block">
                    <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-400">Team reminders</div>
                    <textarea value={timeManagement.reminders} disabled={!isAgencyOwner || isSavingTimeManagement} onChange={(event) => setTimeManagement((current) => ({ ...current, reminders: event.target.value }))} rows={4} className="w-full resize-y rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm leading-6 text-white outline-none transition focus:border-cyan-400 disabled:cursor-not-allowed disabled:opacity-60" placeholder={'Follow up dormant clients before 12:00\nMove screening candidates before end of day\nCheck candidate role-watch alerts'} />
                  </label>
                  <label className="block">
                    <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-400">Daily goals</div>
                    <textarea value={timeManagement.dailyGoals} disabled={!isAgencyOwner || isSavingTimeManagement} onChange={(event) => setTimeManagement((current) => ({ ...current, dailyGoals: event.target.value }))} rows={3} className="w-full resize-y rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm leading-6 text-white outline-none transition focus:border-cyan-400 disabled:cursor-not-allowed disabled:opacity-60" placeholder={'10 client touches\n5 CV reviews\n2 shortlist submissions'} />
                  </label>
                  <button type="submit" disabled={!isAgencyOwner || isSavingTimeManagement} className="rounded-full bg-cyan-400 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-300 disabled:cursor-not-allowed disabled:opacity-60">{isSavingTimeManagement ? 'Saving...' : 'Save team controls'}</button>
                </div>
              </form>

            </div>



            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

              <div className="rounded-shell border border-violet-300/15 bg-violet-500/5 p-4">
                <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Team Messages</div>
                <div className="mt-2 text-sm text-slate-400">A shared notice board for recruiters to leave operational messages, handoff notes, and short updates for each other.</div>

                <form onSubmit={handleSendTeamMessage} className="mt-4">
                  <textarea value={teamMessageDraft} onChange={(event) => setTeamMessageDraft(event.target.value)} rows={4} maxLength={1200} className="w-full resize-y rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm leading-6 text-white outline-none transition focus:border-violet-300" placeholder="Leave a message for the team..." />
                  <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                    <div className="text-micro text-slate-500">{teamMessageDraft.length}/1200</div>
                    <button type="submit" disabled={teamMessageSending || teamMessageDraft.trim().length < 2} className="rounded-full bg-violet-400 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-violet-300 disabled:cursor-not-allowed disabled:opacity-60">{teamMessageSending ? 'Posting...' : 'Post message'}</button>
                  </div>
                </form>

                <div className="mt-4 space-y-3">
                  {teamMessages.length === 0 && (
                    <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No team messages yet.</div>
                  )}
                  {teamMessages.slice(0, 8).map((message) => (
                    <div key={message.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-xs font-black text-white">{message.authorName || message.authorEmail || 'Recruiter'}</div>
                          <div className="mt-1 text-micro uppercase tracking-[0.14em] text-slate-500">{message.createdAtIso ? new Date(message.createdAtIso).toLocaleString() : 'Just now'}</div>
                        </div>
                        {(agencyDetails?.ownerUid === recruiterUser?.uid || message.authorUid === recruiterUser?.uid) && (
                          <button type="button" onClick={() => handleDeleteTeamMessage(message)} className="rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] text-slate-400 transition hover:border-rose-300/30 hover:text-rose-200">Remove</button>
                        )}
                      </div>
                      <div className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-200">{message.message}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5">

              <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Recorded Actions</div>

              <div className="mt-2 text-sm text-slate-400">The latest recruiter actions across seat changes, profile updates, screening actions, and job work.</div>

              <div className="mt-4 rounded-card border border-cyan-400/15 bg-cyan-500/5 p-4">
                <div className="text-micro font-black uppercase tracking-[0.18em] text-cyan-300">Access Governance</div>
                <div className="mt-2 text-xs leading-5 text-slate-400">Vision now applies role presets to navigation, exports, deletes, finance actions, integrations, billing, and team controls.</div>
                <div className="mt-3 grid gap-2">
                  {Object.entries(RBAC_ROLE_PRESETS).map(([role, preset]) => (
                    <div key={role} className="rounded-ctl border border-white/10 bg-slate-950/50 px-3 py-2">
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-black text-white">{preset.label}</div>
                        <div className="text-micro font-black uppercase tracking-[0.12em] text-slate-500">{preset.permissions.includes('*') ? 'Full' : `${preset.permissions.length} permissions`}</div>
                      </div>
                      <div className="mt-1 text-micro leading-4 text-slate-500">{preset.description}</div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 space-y-3">

                {activityLog.length === 0 && (

                  <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">

                    Agent actions will appear here as work is done inside Vision.

                  </div>

                )}

                {activityLog.slice(0, 12).map((item) => (

                  <div key={item.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="flex items-center justify-between gap-3">

                      <div className="text-sm font-bold text-white">{item.summary}</div>

                      <div className="text-micro uppercase tracking-[0.16em] text-slate-500">{String(item.type || '').replace(/_/g, ' ')}</div>

                    </div>

                    <div className="mt-2 text-xs text-slate-400">{item.createdAtIso ? new Date(item.createdAtIso).toLocaleString() : 'Pending timestamp'}</div>

                  </div>

                ))}

              </div>

              </div>

            </div>

          </div>

        )}



        {agencySection === 'reporting' && (

          <div className="space-y-5">

            {/* Report Controls */}
            <div className="rounded-shell border border-white/10 bg-white/5 p-4 sm:p-5">
              <div className="text-xs font-black uppercase tracking-[0.2em] text-cyan-300 mb-3">Report Controls</div>
              <div className="flex flex-wrap gap-2 mb-3">
                {reportingRangeOptions.map(opt => (
                  <button key={opt.days} type="button"
                    onClick={() => { setReportingRangeDays(opt.days); setReportingUseCustomRange(false); }}
                    className={`rounded-full px-4 py-1.5 text-xs font-bold border transition-colors ${!reportingUseCustomRange && reportingRangeDays === opt.days ? 'border-cyan-400 bg-cyan-400/20 text-cyan-300' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/25'}`}>
                    {opt.days}D
                  </button>
                ))}
                <button type="button"
                  onClick={() => setReportingUseCustomRange(v => !v)}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold border transition-colors ${reportingUseCustomRange ? 'border-violet-400 bg-violet-400/20 text-violet-300' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/25'}`}>
                  Custom
                </button>
              </div>
              {reportingUseCustomRange && (
                <div className="flex flex-wrap gap-2 mb-3">
                  <label className="flex items-center gap-1.5 text-xs text-slate-400">
                    From
                    <input type="date" value={reportingCustomStart} onChange={e => setReportingCustomStart(e.target.value)}
                      className="rounded-ctl border border-white/10 bg-slate-900 px-2 py-1 text-xs text-white" />
                  </label>
                  <label className="flex items-center gap-1.5 text-xs text-slate-400">
                    To
                    <input type="date" value={reportingCustomEnd} onChange={e => setReportingCustomEnd(e.target.value)}
                      className="rounded-ctl border border-white/10 bg-slate-900 px-2 py-1 text-xs text-white" />
                  </label>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer text-xs text-slate-400 select-none">
                  <input type="checkbox" checked={reportingCompareEnabled} onChange={e => setReportingCompareEnabled(e.target.checked)}
                    className="accent-cyan-400 w-4 h-4" />
                  Compare to previous period
                </label>
                {isAgencyOwner && (
                  <label className="flex items-center gap-2 text-xs text-slate-400">
                    Recruiter
                    <select value={reportingRecruiterFilter} onChange={e => setReportingRecruiterFilter(e.target.value)}
                      className="rounded-ctl border border-white/10 bg-slate-900 px-2 py-1 text-xs text-white">
                      <option value="all">All</option>
                      {agencyMembers.map(m => (
                        <option key={m.uid} value={m.uid}>{m.displayName || m.email}</option>
                      ))}
                    </select>
                  </label>
                )}
              </div>
            </div>

            {/* Export buttons */}
            <div className="flex flex-wrap gap-2 justify-end items-center">
              <button
                onClick={() => setCustomReportOpen((o) => !o)}
                className="rounded-ctl border border-violet-500/50 bg-violet-500/10 px-3 py-1.5 text-violet-200 hover:bg-violet-500/20 transition-colors text-sm font-medium"
                title="Open custom report builder"
              >
                ⋯ Report Builder
              </button>
              <button
                onClick={handleExportReportingXls}
                className="flex items-center gap-1.5 rounded-ctl border border-emerald-500/50 bg-emerald-500/10 px-3 py-1.5 text-emerald-200 hover:bg-emerald-500/20 transition-colors text-sm font-medium"
                title="Export full analytics as multi-sheet Excel workbook (9 sheets)"
              >
                <Download size={14} /> ↓ XLSX
              </button>
              <button
                onClick={() => handleExportReportingPdf(kpiDeltasFull, activityTrendBuckets, sourceQualityItems, stageVisuals, reportingRangeDays + 'd', reportingRecruiterFilter || 'All')}
                className="rounded-ctl border border-rose-500/50 bg-rose-500/10 px-3 py-1.5 text-rose-200 hover:bg-rose-500/20 transition-colors text-sm font-medium"
                title="Export analytics as printable PDF"
              >
                ↓ PDF
              </button>
              <button
                onClick={handleExportBiJson}
                disabled={biSnapshotSaveStatus === 'saving'}
                className={`flex items-center gap-1.5 rounded-ctl border px-3 py-1.5 text-sm font-medium transition-colors disabled:opacity-60 ${biSnapshotSaveStatus === 'saved' ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-300' : biSnapshotSaveStatus === 'error' ? 'border-rose-500/50 bg-rose-500/10 text-rose-300' : 'border-sky-500/50 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20'}`}
                title={biSnapshotSaveStatus === 'saved' ? 'Snapshot saved to Firestore' : biSnapshotSaveStatus === 'error' ? 'Save failed — click to retry' : 'Download JSON + save date-keyed snapshot to Firestore'}
              >
                <Download size={14} />
                {biSnapshotSaveStatus === 'saving' ? 'Saving…' : biSnapshotSaveStatus === 'saved' ? '✓ BI Snapshot Saved' : biSnapshotSaveStatus === 'error' ? '✗ Save Failed' : '↓ BI JSON'}
              </button>
              <button
                type="button"
                onClick={() => setWebhookPanelOpen((p) => !p)}
                className={`rounded-ctl border px-3 py-1.5 text-sm font-medium transition-colors ${webhookPanelOpen ? 'border-violet-400/60 bg-violet-500/15 text-violet-200' : 'border-violet-500/30 bg-violet-500/8 text-violet-300 hover:bg-violet-500/15'}`}
                title="Configure outbound webhook / API"
              >
                ⚡ Webhook
              </button>
            </div>

            {/* ── Webhook / API Config Card ── */}
            {webhookPanelOpen && (
              <div className="rounded-shell border border-violet-500/25 bg-slate-950/70 p-5 sm:p-6">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Outbound Webhook / API</div>
                    <div className="mt-0.5 text-meta text-slate-500">Push reporting events to your BI tool, Zapier, or custom endpoint · HMAC-signed payloads</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {webhookTestResult && (
                      <div className={`rounded-full border px-3 py-1 text-micro font-black uppercase tracking-[0.12em] ${webhookTestResult.ok ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : 'border-rose-500/30 bg-rose-500/10 text-rose-300'}`}>
                        {webhookTestResult.ok ? '✓ Test passed' : '✗ Test failed'}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.14em] text-slate-400 mb-1.5">Endpoint URL</label>
                    <input
                      type="url"
                      value={webhookUrl}
                      onChange={(e) => setWebhookUrl(e.target.value)}
                      placeholder="https://hooks.zapier.com/hooks/catch/..."
                      className="w-full rounded-ctl border border-white/10 bg-slate-900/80 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-400/60 focus:outline-none focus:ring-1 focus:ring-violet-400/30"
                    />
                  </div>
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.14em] text-slate-400 mb-1.5">HMAC Secret <span className="text-slate-600 normal-case font-normal">(optional)</span></label>
                    <input
                      type="password"
                      value={webhookSecret}
                      onChange={(e) => setWebhookSecret(e.target.value)}
                      placeholder="sk_webhook_..."
                      className="w-full rounded-ctl border border-white/10 bg-slate-900/80 px-3 py-2 text-sm text-white placeholder-slate-600 focus:border-violet-400/60 focus:outline-none focus:ring-1 focus:ring-violet-400/30"
                    />
                    <div className="mt-1 text-micro text-slate-600">Sent as X-Vision-Signature header (SHA-256)</div>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-400 mb-2.5">Trigger on events</div>
                  <div className="grid gap-2 sm:grid-cols-3">
                    {[
                      { key: 'stage_move', label: 'Stage Move', desc: 'Whenever a candidate advances' },
                      { key: 'hire', label: 'Hire', desc: 'Candidate marked as hired' },
                      { key: 'offer_accepted', label: 'Offer Accepted', desc: 'Offer accepted by candidate' },
                      { key: 'offer_declined', label: 'Offer Declined', desc: 'Offer declined by candidate' },
                      { key: 'screening_done', label: 'Screening Done', desc: 'AI screening completed' },
                      { key: 'weekly_digest', label: 'Weekly Digest', desc: 'KPI summary every Monday 9am' },
                    ].map((ev) => {
                      const on = webhookEvents.includes(ev.key);
                      return (
                        <button
                          key={ev.key}
                          type="button"
                          onClick={() => setWebhookEvents((prev) => on ? prev.filter((e) => e !== ev.key) : [...prev, ev.key])}
                          className={`rounded-ctl border p-3 text-left transition-colors ${on ? 'border-violet-400/50 bg-violet-500/10' : 'border-white/10 bg-slate-900/40 hover:border-white/20'}`}
                        >
                          <div className={`text-micro font-bold ${on ? 'text-violet-200' : 'text-slate-400'}`}>{ev.label}</div>
                          <div className="mt-0.5 text-micro text-slate-600">{ev.desc}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <details className="mt-4 rounded-ctl border border-white/10 bg-slate-900/40">
                  <summary className="cursor-pointer px-4 py-2.5 text-micro font-black uppercase tracking-[0.12em] text-slate-400 hover:text-slate-300">
                    ▸ Payload schema preview
                  </summary>
                  <pre className="overflow-x-auto px-4 pb-4 pt-2 text-micro text-slate-400 leading-relaxed whitespace-pre-wrap max-h-48">{JSON.stringify({
                    event: 'stage_move',
                    timestamp: new Date().toISOString(),
                    agencyKey: profile?.agencyKey || 'your_agency_key',
                    payload: {
                      candidateId: 'CAND-001',
                      fromStage: 'screening',
                      toStage: 'interview',
                      source: 'LinkedIn',
                      recruiterId: 'REC-001',
                    },
                  }, null, 2)}</pre>
                </details>

                <div className="mt-5 flex flex-wrap items-center gap-3 border-t border-white/10 pt-4">
                  <button
                    type="button"
                    disabled={isSavingWebhook || !webhookUrl.trim()}
                    onClick={async () => {
                      if (!webhookUrl.trim()) return;
                      setIsSavingWebhook(true);
                      try {
                        await setDoc(agencyDocRef(profile.agencyKey), {
                          reportingSettings: {
                            webhook: { url: webhookUrl.trim(), secret: webhookSecret.trim(), events: webhookEvents },
                          },
                        }, { merge: true });
                      } finally { setIsSavingWebhook(false); }
                    }}
                    className="rounded-ctl border border-violet-400/40 bg-violet-500/15 px-4 py-2 text-meta font-black uppercase tracking-[0.12em] text-violet-200 transition-colors hover:bg-violet-500/25 disabled:opacity-40"
                  >
                    {isSavingWebhook ? 'Saving…' : 'Save Config'}
                  </button>
                  <button
                    type="button"
                    disabled={isTestingWebhook || !webhookUrl.trim()}
                    onClick={async () => {
                      if (!webhookUrl.trim()) return;
                      setIsTestingWebhook(true);
                      setWebhookTestResult(null);
                      try {
                        const body = JSON.stringify({ event: 'test', timestamp: new Date().toISOString(), agencyKey: profile?.agencyKey, payload: { message: 'Vision webhook test fire' } });
                        const resp = await fetch(webhookUrl.trim(), {
                          method: 'POST',
                          headers: { 'Content-Type': 'application/json', 'X-Vision-Event': 'test' },
                          body,
                        });
                        setWebhookTestResult({ ok: resp.ok, status: resp.status });
                      } catch (err) {
                        setWebhookTestResult({ ok: false, status: err.message });
                      } finally { setIsTestingWebhook(false); }
                    }}
                    className="rounded-ctl border border-cyan-400/30 bg-cyan-500/10 px-4 py-2 text-meta font-black uppercase tracking-[0.12em] text-cyan-200 transition-colors hover:bg-cyan-500/20 disabled:opacity-40"
                  >
                    {isTestingWebhook ? 'Firing…' : 'Test Fire'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const curl = 'curl -X POST "' + webhookUrl + '" \\\n  -H "Content-Type: application/json" \\\n  -H "X-Vision-Event: test" \\\n  -d \'{"event":"test","timestamp":"' + new Date().toISOString() + '","agencyKey":"' + (profile?.agencyKey || '') + '"}\'';
                      navigator.clipboard?.writeText(curl);
                    }}
                    className="rounded-ctl border border-white/10 bg-slate-900/40 px-4 py-2 text-meta font-black uppercase tracking-[0.12em] text-slate-400 transition-colors hover:border-white/20"
                  >
                    Copy as cURL
                  </button>
                  {webhookTestResult && (
                    <div className={`ml-auto text-micro ${webhookTestResult.ok ? 'text-emerald-400' : 'text-rose-400'}`}>
                      HTTP {webhookTestResult.status}
                    </div>
                  )}
                </div>
              </div>
            )}


            {/* KPI Cards */}
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              {kpiDeltasFull.filter((kpi) => canViewReportingMetric(kpi.key)).map((kpi) => (
                <button key={kpi.key} type="button"
                  onClick={() => setReportingKpiDrawerKey(prev => prev === kpi.key ? '' : kpi.key)}
                  className={`rounded-shell border p-4 sm:p-5 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400/50 ${reportingKpiDrawerKey === kpi.key ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/10 bg-white/5 hover:border-white/20'}`}>
                  <div className="mb-1 flex items-center gap-2">
                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">{kpi.label}</div>
                    <span
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-white/20 text-micro font-black text-slate-400"
                      title={`${reportingMetricDictionary[kpi.key]?.short || ''}\nFormula: ${reportingMetricDictionary[kpi.key]?.formula || ''}\nOwner: ${reportingMetricDictionary[kpi.key]?.owner || ''}\nGrain: ${reportingMetricDictionary[kpi.key]?.granularity || ''}`}
                    >
                      i
                    </span>
                  </div>
                  <div className="text-meta text-slate-400">{reportingMetricDictionary[kpi.key]?.short || ''}</div>
                  <div className="text-2xl font-black text-white">{kpi.displayValue}</div>
                  {reportingCompareEnabled && (
                    <div className={`mt-1 text-meta font-bold ${kpi.deltaDir === 'up' ? 'text-emerald-400' : kpi.deltaDir === 'down' ? 'text-rose-400' : 'text-slate-500'}`}>
                      {kpi.deltaLabel} vs prev {effectiveRangeDays}d
                    </div>
                  )}
                </button>
              ))}            </div>

            <div className="grid gap-4 xl:grid-cols-3">
              <button
                type="button"
                onClick={() => setReportingKpiDrawerKey((prev) => (prev === 'funnel_overview' ? '' : 'funnel_overview'))}
                className={`rounded-shell border bg-white/5 p-5 sm:p-6 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400/50 ${reportingKpiDrawerKey === 'funnel_overview' ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/10 hover:border-white/20'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Conversion Funnel</div>
                  <div className="flex items-center gap-2">
                    <div className="text-meta uppercase tracking-[0.16em] text-slate-500">{funnelHiredCount}/{funnelAppliedCount || 0} hired</div>
                    <button onClick={() => exportFunnelCsv(conversionFunnel)} className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-micro font-bold uppercase tracking-[0.1em] text-cyan-400 hover:bg-cyan-500/20 transition-colors" title="Export conversion funnel CSV">↓ CSV</button>
                  </div>
                </div>
                <div className="mt-4 space-y-3">
                  {conversionFunnel.map((stage) => {
                    const width = Math.max(8, Math.round((stage.count / Math.max(funnelTopCount, 1)) * 100));
                    return (
                      <div key={stage.stageId}>
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <div className="font-bold uppercase tracking-[0.12em] text-white">{stage.label}</div>
                          <div className="text-slate-300">
                            <span className="font-black text-white">{stage.count}</span>
                            <span className="ml-2 text-meta text-slate-500">{stage.conversionLabel}</span>
                          </div>
                        </div>
                        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-900/90">
                          <div className="h-full rounded-full bg-cyan-400/80" style={{ width: `${width}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 text-meta text-slate-400">
                  Overall yield: <span className="font-bold text-cyan-300">{Math.round(funnelYieldPct)}%</span>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setReportingKpiDrawerKey((prev) => (prev === 'velocity_overview' ? '' : 'velocity_overview'))}
                className={`rounded-shell border bg-white/5 p-5 sm:p-6 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400/50 ${reportingKpiDrawerKey === 'velocity_overview' ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/10 hover:border-white/20'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Stage Velocity</div>
                  <div className="text-meta uppercase tracking-[0.16em] text-slate-500">Median {stageVelocityMedian == null ? 'N/A' : `${stageVelocityMedian.toFixed(1)}d`}</div>
                </div>
                <div className="mt-4 space-y-3">
                  {stageVelocity.map((item) => {
                    const width = item.medianDays == null ? 0 : Math.max(8, Math.round((item.medianDays / Math.max(velocityMax, 1)) * 100));
                    return (
                      <div key={item.key}>
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <div className="font-bold uppercase tracking-[0.12em] text-white">{item.label}</div>
                          <div className="text-slate-300">
                            <span className="font-black text-white">{item.medianLabel}</span>
                            <span className="ml-2 text-meta text-slate-500">{item.sampleCount}x</span>
                          </div>
                        </div>
                        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-900/90">
                          <div className="h-full rounded-full bg-emerald-400/80" style={{ width: `${width}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </button>


              {/* ── TIME-TO-HIRE BY STAGE & INTERVIEWER ── */}
              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6 col-span-full">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.24em] text-emerald-300">Time-to-Hire by Stage</div>
                    <div className="mt-1 text-meta text-slate-500">Median days per transition · p50 industry overlay · per-recruiter breakdown</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-emerald-300">
                      Overall {tthOverallMedian != null ? tthOverallMedian.toFixed(1) + 'd' : 'N/A'}
                    </div>
                    {['stages', 'recruiters'].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => setTthView(v)}
                        className={`rounded-full border px-3 py-1 text-micro font-bold uppercase tracking-[0.12em] transition-colors ${tthView === v ? 'border-emerald-400 bg-emerald-400/15 text-emerald-300' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'}`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                </div>

                {tthView === 'stages' ? (
                  <div className="mt-5 space-y-4">
                    {tthByStage.map((row) => {
                      const yourBarPct = row.medianDays != null && tthStageMax > 0
                        ? Math.min(100, Math.round((row.medianDays / tthStageMax) * 100)) : 0;
                      const p50BarPct  = Math.min(100, Math.round((row.p50 / tthStageMax) * 100));
                      const p75BarPct  = Math.min(100, Math.round((row.p75 / tthStageMax) * 100));
                      const isOverP50  = row.medianDays != null && row.medianDays > row.p50;
                      const isOverP75  = row.medianDays != null && row.medianDays > row.p75;
                      const barColor   = isOverP75 ? '#f43f5e' : isOverP50 ? '#f59e0b' : '#10b981';
                      return (
                        <div key={row.key} className="rounded-card border border-white/10 bg-slate-900/40 px-4 py-3.5">
                          <div className="flex items-center justify-between gap-3">
                            <div className="text-xs font-black uppercase tracking-[0.14em] text-white">{row.label}</div>
                            <div className="flex items-center gap-4 shrink-0">
                              <div className="text-micro text-slate-500">p50 <span className="font-semibold text-slate-400">{row.p50}d</span></div>
                              <div className="text-micro text-slate-500">p75 <span className="font-semibold text-slate-400">{row.p75}d</span></div>
                              <div className="text-sm font-black" style={{ color: row.medianDays != null ? barColor : '#475569' }}>
                                {row.medianDays != null ? row.medianDays.toFixed(1) + 'd' : 'No data'}
                              </div>
                              <div className="text-micro text-slate-600">{row.sampleCount}x</div>
                            </div>
                          </div>
                          {/* Stacked bar */}
                          <div className="relative mt-2.5 h-3 overflow-hidden rounded-full bg-slate-800">
                            {/* p50 marker */}
                            <div className="absolute top-0 bottom-0 w-0.5 bg-slate-400/40 z-10" style={{ left: p50BarPct + '%' }} />
                            {/* p75 marker */}
                            <div className="absolute top-0 bottom-0 w-0.5 bg-emerald-500/50 z-10" style={{ left: p75BarPct + '%' }} />
                            {/* your value */}
                            {row.medianDays != null && (
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: yourBarPct + '%', background: barColor + 'cc' }} />
                            )}
                          </div>
                          {/* delta vs p50 */}
                          {row.medianDays != null && (
                            <div className="mt-1.5 flex items-center gap-3">
                              {isOverP50 ? (
                                <div className="text-micro font-bold text-rose-400">+{(row.medianDays - row.p50).toFixed(1)}d above industry p50</div>
                              ) : (
                                <div className="text-micro font-bold text-emerald-400">{(row.p50 - row.medianDays).toFixed(1)}d under p50 &#10003;</div>
                              )}
                              {row.fastestMs != null && (
                                <div className="ml-auto text-micro text-slate-500">Fastest: <span className="text-slate-400 font-semibold">{(row.fastestMs / 86400000).toFixed(1)}d</span></div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    <div className="flex items-center gap-5 pt-1">
                      <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-slate-400/50 rounded-full" /><span className="text-micro text-slate-500">p50 industry</span></div>
                      <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-emerald-500/60 rounded-full" /><span className="text-micro text-slate-500">p75 (best quartile)</span></div>
                      <div className="ml-auto text-micro text-slate-600">Source: SHRM / Gem.com Q1 2026</div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5">
                    {tthByRecruiter.length === 0 ? (
                      <div className="rounded-card border border-dashed border-white/10 px-5 py-8 text-center text-sm text-slate-500">
                        No recruiter TTH data for this range. Recruiters appear once they have candidates who advanced through at least one stage.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="border-b border-white/10">
                              <th className="pb-2 text-left font-black uppercase tracking-[0.14em] text-slate-500 pr-4">Recruiter</th>
                              {PIPELINE_STAGES.slice(0, -1).map((s) => (
                                <th key={s.id} className="pb-2 text-center font-black uppercase tracking-[0.12em] text-slate-500 px-2 min-w-[80px]">
                                  {s.label}→
                                </th>
                              ))}
                              <th className="pb-2 text-right font-black uppercase tracking-[0.14em] text-slate-500 pl-4">Overall</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tthByRecruiter.map((rec) => (
                              <tr key={rec.id} className="border-b border-white/[0.05]">
                                <td className="py-2.5 pr-4">
                                  <div className="font-bold text-white">{rec.name}</div>
                                  <div className="text-micro text-slate-500">{rec.totalCandidates} candidates</div>
                                </td>
                                {rec.stageTTH.map((stg) => (
                                  <td key={stg.key} className="py-2.5 px-2 text-center">
                                    {stg.median != null ? (
                                      <div>
                                        <div className={`font-black ${stg.median > stg.p50 ? 'text-rose-400' : 'text-emerald-300'}`}>
                                          {stg.median.toFixed(1)}d
                                        </div>
                                        <div className="text-micro text-slate-600">{stg.median > stg.p50 ? '▲' : '▼'} p50</div>
                                      </div>
                                    ) : (
                                      <div className="text-slate-700">—</div>
                                    )}
                                  </td>
                                ))}
                                <td className="py-2.5 pl-4 text-right">
                                  <div className={`font-black ${rec.overallMedian != null ? (rec.overallMedian > tthOverallP50 ? 'text-amber-300' : 'text-emerald-300') : 'text-slate-600'}`}>
                                    {rec.overallMedian != null ? rec.overallMedian.toFixed(1) + 'd' : '—'}
                                  </div>
                                </td>
                              </tr>
                            ))}
                            {/* p50 reference row */}
                            <tr className="border-t border-white/10 bg-white/[0.02]">
                              <td className="py-2 pr-4 text-micro font-black uppercase tracking-[0.12em] text-slate-500">Industry p50</td>
                              {tthByStage.map((stg) => (
                                <td key={stg.key} className="py-2 px-2 text-center text-micro font-bold text-slate-500">{stg.p50}d</td>
                              ))}
                              <td className="py-2 pl-4 text-right text-micro font-bold text-slate-500">{tthOverallP50}d</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>


              <button
                type="button"
                onClick={() => setReportingKpiDrawerKey((prev) => (prev === 'source_quality_overview' ? '' : 'source_quality_overview'))}
                className={`rounded-shell border bg-white/5 p-5 sm:p-6 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400/50 ${reportingKpiDrawerKey === 'source_quality_overview' ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/10 hover:border-white/20'}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Source Quality</div>
                  <div className="text-meta uppercase tracking-[0.16em] text-slate-500">{sourceCoverageCount} channels</div>
                </div>
                <div className="mt-4 space-y-3">
                  {sourceQualityItems.slice(0, 4).map((item) => {
                    const width = sourceTotalCandidates > 0 ? Math.max(10, Math.round((item.count / sourceTotalCandidates) * 100)) : 0;
                    return (
                      <div key={item.key}>
                        <div className="flex items-center justify-between gap-3 text-sm">
                          <div className="font-bold text-white truncate">{item.label}</div>
                          <div className="text-slate-300 text-right">
                            <span className="font-black text-white">{item.count}</span>
                            <span className="ml-2 text-meta text-slate-500">{item.hireRateLabel}</span>
                          </div>
                        </div>
                        <div className="mt-1.5 h-2.5 overflow-hidden rounded-full bg-slate-900/90">
                          <div className="h-full rounded-full bg-amber-400/80" style={{ width: `${width}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="mt-4 flex items-center justify-between text-meta text-slate-400">
                  <span>Top-channel share: <span className="font-bold text-cyan-300">{Math.round(sourceTopShare)}%</span></span>
                  <span>Top hire rate: <span className="font-bold text-cyan-300">{Math.round(sourceTopHireRate)}%</span></span>
                </div>
              </button>
              <div className="mt-2 flex justify-end">
                <button onClick={() => exportSourceQualityCsv(sourceQualityItems)} className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-micro font-bold uppercase tracking-[0.1em] text-amber-400 hover:bg-amber-500/20 transition-colors" title="Export source quality CSV">↓ Source Quality CSV</button>
              </div>
            </div>

              {/* ── SOURCE ROI ── */}
              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6 col-span-full">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.24em] text-amber-300">Source ROI</div>
                    <div className="mt-1 text-meta text-slate-500">Cost per channel · quality score · efficiency index</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {sourceRoiBestChannel && (
                      <div className="rounded-full border border-emerald-400/20 bg-emerald-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-emerald-300">
                        Best ROI: {sourceRoiBestChannel.label.length > 18 ? sourceRoiBestChannel.label.slice(0, 18) + '…' : sourceRoiBestChannel.label}
                      </div>
                    )}
                    <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-micro text-slate-400">
                      {cphBudget > 0 ? 'Budget-based cost' : 'Industry CPC est.'}
                    </div>
                    <button onClick={() => exportSourceRoiCsv(sourceRoiItems)} className="rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-micro font-bold uppercase tracking-[0.1em] text-amber-400 hover:bg-amber-500/20 transition-colors" title="Export Source ROI CSV">↓ CSV</button>
                  </div>
                </div>

                {/* Header row */}
                <div className="mt-5 grid grid-cols-[1fr_auto_auto_auto] gap-x-4 gap-y-1 text-micro uppercase tracking-[0.14em] text-slate-500 pb-2 border-b border-white/5">
                  <div>Channel</div>
                  <div className="text-right w-20">Quality</div>
                  <div className="text-right w-24">Cost / hire</div>
                  <div className="text-right w-14">ROI</div>
                </div>

                <div className="mt-3 space-y-3">
                  {sourceRoiItems.map((row) => {
                    const qualityPct = sourceRoiQualityMax > 0 ? Math.min(100, Math.round((row.qualityScore / sourceRoiQualityMax) * 100)) : 0;
                    const roiLabel = row.roiIndex == null ? '—' : row.roiIndex >= 80 ? '★★★' : row.roiIndex >= 40 ? '★★' : '★';
                    const roiColor = row.roiIndex == null ? 'text-slate-500' : row.roiIndex >= 80 ? 'text-emerald-300' : row.roiIndex >= 40 ? 'text-amber-300' : 'text-rose-400';
                    const cphDisplay = row.costPerHired == null
                      ? (row.hired === 0 ? 'No hires' : '—')
                      : row.costPerHired < 1000 ? '$' + row.costPerHired.toLocaleString()
                      : '$' + (row.costPerHired / 1000).toFixed(1) + 'k';
                    return (
                      <div key={row.key} className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-x-4">
                        <div>
                          <div className="text-sm font-bold text-white truncate max-w-[180px]">{row.label}</div>
                          <div className="mt-1 flex items-center gap-2">
                            {/* Quality bar */}
                            <div className="relative flex-1 max-w-[120px] h-2 rounded-full bg-slate-900/90 overflow-hidden">
                              <div className="h-full rounded-full bg-emerald-400/80" style={{ width: `${qualityPct}%` }} />
                            </div>
                            <span className="text-micro text-slate-400">{row.count} cands · {row.hired} hired</span>
                          </div>
                        </div>
                        {/* Quality score */}
                        <div className="text-right w-20">
                          <div className="text-sm font-black text-white">{row.qualityScore}</div>
                          <div className="text-micro text-slate-500">/100</div>
                        </div>
                        {/* Cost per hire */}
                        <div className="text-right w-24">
                          <div className={`text-sm font-black ${row.costPerHired == null ? 'text-slate-500' : row.costPerHired < 2000 ? 'text-emerald-300' : row.costPerHired < 8000 ? 'text-amber-300' : 'text-rose-400'}`}>{cphDisplay}</div>
                          {row.costPerHired != null && (
                            <div className="text-micro text-slate-500">est.</div>
                          )}
                        </div>
                        {/* ROI stars */}
                        <div className={`text-right w-14 text-sm font-black tracking-tighter ${roiColor}`}>{roiLabel}</div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 flex items-center gap-4 text-meta text-slate-500 border-t border-white/5 pt-3 flex-wrap">
                  <span>Quality = <span className="text-slate-400">Hire rate ×0.6 + Screening rate ×0.4</span></span>
                  <span className="ml-auto">ROI: <span className="text-emerald-300">★★★</span> efficient · <span className="text-amber-300">★★</span> moderate · <span className="text-rose-400">★</span> high cost</span>
                </div>
              </div>


              {/* ── INTERVIEWER LOAD & CAPACITY ── */}
              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6 col-span-full">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Interviewer Load &amp; Capacity</div>
                    <div className="mt-1 text-meta text-slate-500">Active panel hours · decline rate · scheduling lag per recruiter</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {interviewerLoadBusiest && (
                      <div className="rounded-full border border-violet-400/20 bg-violet-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-violet-300">
                        Busiest: {interviewerLoadBusiest.name.length > 16 ? interviewerLoadBusiest.name.slice(0, 16) + '…' : interviewerLoadBusiest.name}
                      </div>
                    )}
                    <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-micro text-slate-400">
                      {interviewerLoad.length} active recruiters
                    </div>
                  </div>
                </div>

                {interviewerLoad.length === 0 ? (
                  <div className="mt-6 text-center text-sm text-slate-500">No active panel data in selected range</div>
                ) : (
                  <>
                    {/* Column headers */}
                    <div className="mt-5 hidden sm:grid grid-cols-[1fr_auto_auto_auto_auto] gap-x-6 pb-2 border-b border-white/5 text-micro uppercase tracking-[0.14em] text-slate-500">
                      <div>Recruiter</div>
                      <div className="text-right w-28">Panel hours</div>
                      <div className="text-right w-24">Scheduling lag</div>
                      <div className="text-right w-24">Decline rate</div>
                      <div className="text-right w-16">Status</div>
                    </div>

                    <div className="mt-3 space-y-4">
                      {interviewerLoad.map((rec) => {
                        const barPct = interviewerLoadMaxHours > 0
                          ? Math.min(100, Math.round((rec.estimatedPanelHours / interviewerLoadMaxHours) * 100))
                          : 0;
                        const lagLabel = rec.schedulingLagDays != null ? rec.schedulingLagDays.toFixed(1) + 'd' : '—';
                        const declineLabel = rec.declineRate != null ? Math.round(rec.declineRate) + '%' : '—';
                        const loadStatus = rec.estimatedPanelHours === 0
                          ? { label: 'Idle', color: 'text-slate-500' }
                          : rec.estimatedPanelHours >= 30
                          ? { label: 'Overloaded', color: 'text-rose-400' }
                          : rec.estimatedPanelHours >= 15
                          ? { label: 'Heavy', color: 'text-amber-300' }
                          : { label: 'Normal', color: 'text-emerald-300' };
                        const lagStatus = rec.schedulingLagDays == null ? null
                          : rec.schedulingLagDays > 7 ? 'text-rose-400'
                          : rec.schedulingLagDays > 3 ? 'text-amber-300'
                          : 'text-emerald-300';

                        return (
                          <div key={rec.id} className="grid grid-cols-1 sm:grid-cols-[1fr_auto_auto_auto_auto] items-center gap-x-6 gap-y-2">
                            {/* Name + bar */}
                            <div>
                              <div className="flex items-center justify-between gap-2 mb-1.5">
                                <span className="text-sm font-bold text-white truncate max-w-[180px]">{rec.name}</span>
                                <span className="text-micro text-slate-400 sm:hidden">
                                  {rec.activeScreeningCount}s · {rec.activeInterviewCount}i · {rec.estimatedPanelHours}h
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <div className="flex-1 h-2.5 rounded-full bg-slate-900/90 overflow-hidden">
                                  <div
                                    className={`h-full rounded-full transition-all ${rec.estimatedPanelHours >= 30 ? 'bg-rose-500/80' : rec.estimatedPanelHours >= 15 ? 'bg-amber-400/80' : 'bg-violet-400/80'}`}
                                    style={{ width: `${barPct}%` }}
                                  />
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="rounded-full bg-violet-500/15 border border-violet-400/20 px-2 py-px text-micro font-bold text-violet-300">
                                    {rec.activeScreeningCount} scr
                                  </span>
                                  <span className="rounded-full bg-yellow-500/15 border border-yellow-400/20 px-2 py-px text-micro font-bold text-yellow-300">
                                    {rec.activeInterviewCount} int
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Panel hours */}
                            <div className="hidden sm:block text-right w-28">
                              <div className="text-sm font-black text-white">{rec.estimatedPanelHours}h</div>
                              <div className="text-micro text-slate-500">{rec.activePanelCount} active</div>
                            </div>

                            {/* Scheduling lag */}
                            <div className="hidden sm:block text-right w-24">
                              <div className={`text-sm font-black ${lagStatus || 'text-slate-500'}`}>{lagLabel}</div>
                              <div className="text-micro text-slate-500">scr→int</div>
                            </div>

                            {/* Decline rate */}
                            <div className="hidden sm:block text-right w-24">
                              <div className={`text-sm font-black ${rec.declineRate == null ? 'text-slate-500' : rec.declineRate > 60 ? 'text-rose-400' : rec.declineRate > 35 ? 'text-amber-300' : 'text-emerald-300'}`}>
                                {declineLabel}
                              </div>
                              <div className="text-micro text-slate-500">int drop-off</div>
                            </div>

                            {/* Load status */}
                            <div className="hidden sm:block text-right w-16">
                              <div className={`text-micro font-black uppercase tracking-[0.1em] ${loadStatus.color}`}>{loadStatus.label}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Summary row */}
                    <div className="mt-4 flex items-center gap-6 border-t border-white/5 pt-3 text-meta text-slate-500 flex-wrap">
                      <span>Avg scheduling lag: <span className={`font-bold ${interviewerLoadAvgLag > 7 ? 'text-rose-400' : interviewerLoadAvgLag > 3 ? 'text-amber-300' : 'text-emerald-300'}`}>{interviewerLoadAvgLag != null ? interviewerLoadAvgLag.toFixed(1) + 'd' : '—'}</span></span>
                      <span>Avg panel hours: <span className="font-bold text-violet-300">{interviewerLoadAvgHours != null ? interviewerLoadAvgHours.toFixed(0) + 'h' : '—'}</span></span>
                      <span className="ml-auto text-slate-600">Est. 3h/screening · 6h/interview</span>
                    </div>
                  </>
                )}
              </div>


              {/* ── OFFER DECLINE REASON ANALYTICS ── */}
              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6 col-span-full">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-xs font-black uppercase tracking-[0.24em] text-rose-300">Offer Decline Reasons</div>
                    <div className="mt-1 text-meta text-slate-500">Categorised reasons · trend over time · capture via pipeline</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="rounded-full border border-rose-400/20 bg-rose-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-rose-300">
                      {offerDeclines.length} decline{offerDeclines.length !== 1 ? 's' : ''} recorded
                    </div>
                    {offerDeclineRate != null && (
                      <div className={`rounded-full border px-3 py-1 text-micro font-black uppercase tracking-[0.12em] ${offerDeclineRate > 40 ? 'border-rose-400/20 bg-rose-500/10 text-rose-300' : offerDeclineRate > 20 ? 'border-amber-400/20 bg-amber-500/10 text-amber-300' : 'border-emerald-400/20 bg-emerald-500/10 text-emerald-300'}`}>
                        Decline rate {Math.round(offerDeclineRate)}%
                      </div>
                    )}
                  </div>
                </div>

                {offerDeclines.length === 0 ? (
                  <div className="mt-6 rounded-card border border-white/5 bg-slate-950/40 p-6 text-center">
                    <div className="text-sm text-slate-400">No offer declines captured yet.</div>
                    <div className="mt-1 text-meta text-slate-600">Use the <span className="text-rose-300 font-bold">Decline Offer</span> button on offer-stage candidates in the pipeline to record reasons.</div>
                  </div>
                ) : (
                  <div className="mt-5 grid gap-6 sm:grid-cols-2">

                    {/* Category breakdown */}
                    <div>
                      <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-3">By category</div>
                      <div className="space-y-3">
                        {offerDeclineCategoryBreakdown.map((cat) => {
                          const barPct = offerDeclines.length > 0 ? Math.min(100, Math.round((cat.count / offerDeclines.length) * 100)) : 0;
                          return (
                            <div key={cat.key}>
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-xs font-bold text-white">{cat.label}</span>
                                <span className="text-xs text-slate-400"><span className="font-bold text-white">{cat.count}</span> <span className="text-slate-500">({barPct}%)</span></span>
                              </div>
                              <div className="h-2.5 overflow-hidden rounded-full bg-slate-900/90">
                                <div
                                  className="h-full rounded-full bg-rose-400/80 transition-all"
                                  style={{ width: `${barPct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Trend bars (last 6 months) */}
                    <div>
                      <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-3">Monthly trend</div>
                      {offerDeclineTrend.every((m) => m.count === 0) ? (
                        <div className="text-sm text-slate-500 mt-4">Not enough data for trend</div>
                      ) : (
                        <div className="flex items-end gap-2 h-28">
                          {offerDeclineTrend.map((month) => {
                            const pct = offerDeclineTrendMax > 0 ? Math.max(8, Math.round((month.count / offerDeclineTrendMax) * 100)) : 8;
                            return (
                              <div key={month.key} className="flex flex-col items-center gap-1 flex-1">
                                <div className="text-micro font-bold text-rose-300">{month.count > 0 ? month.count : ''}</div>
                                <div
                                  className={`w-full rounded-t-lg transition-all ${month.count === 0 ? 'bg-slate-800/60' : 'bg-rose-500/70'}`}
                                  style={{ height: `${month.count === 0 ? 8 : pct}%` }}
                                />
                                <div className="text-micro text-slate-500 truncate w-full text-center">{month.shortLabel}</div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Top note if any */}
                      {offerDeclines.some((d) => d.offerDeclineNote) && (
                        <div className="mt-4">
                          <div className="text-micro uppercase tracking-[0.14em] text-slate-500 mb-2">Recent notes</div>
                          <div className="space-y-2">
                            {offerDeclines.filter((d) => d.offerDeclineNote).slice(0, 3).map((d) => (
                              <div key={d.id} className="rounded-ctl border border-white/5 bg-slate-950/50 px-3 py-2">
                                <div className="text-micro font-bold text-rose-300 uppercase tracking-[0.1em]">{OFFER_DECLINE_CATEGORY_LABELS[d.offerDeclineCategory] || d.offerDeclineCategory}</div>
                                <div className="mt-0.5 text-meta text-slate-400 line-clamp-2">{d.offerDeclineNote}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </div>


            <button
              type="button"
              onClick={() => setReportingKpiDrawerKey((prev) => (prev === 'source_trend_wow' ? '' : 'source_trend_wow'))}
              className={`w-full rounded-shell border bg-white/5 p-5 sm:p-6 text-left transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-400/50 ${reportingKpiDrawerKey === 'source_trend_wow' ? 'border-cyan-400 bg-cyan-400/10' : 'border-white/10 hover:border-white/20'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Source Trend WoW</div>
                <div className="text-meta uppercase tracking-[0.16em] text-slate-500">This week {sourceCurrentWeekTotal} | Prev {sourcePreviousWeekTotal}</div>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                {sourceWoWItems.slice(0, 4).map((item) => {
                  const width = Math.max(8, Math.round((item.maxWindowCount / Math.max(1, ...sourceWoWItems.map((entry) => entry.maxWindowCount))) * 100));
                  return (
                    <div key={item.key} className="rounded-card border border-white/10 bg-slate-950/50 p-3">
                      <div className="truncate text-xs font-bold text-white">{item.label}</div>
                      <div className="mt-1 flex items-center justify-between text-meta text-slate-400">
                        <span>{item.currentCount} vs {item.previousCount}</span>
                        <span className={item.deltaCount >= 0 ? 'text-emerald-300' : 'text-rose-300'}>{item.deltaLabel}</span>
                      </div>
                      <div className="mt-2 h-2 overflow-hidden rounded-full bg-slate-900/90">
                        <div className="h-full rounded-full bg-sky-400/80" style={{ width: `${width}%` }} />
                      </div>
                      <div className="mt-2 text-micro text-slate-500">{item.deltaPctLabel} | {item.hireRateDeltaLabel}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 text-meta text-slate-400">
                Total WoW delta: <span className={sourceVolumeWoWDelta >= 0 ? 'font-bold text-emerald-300' : 'font-bold text-rose-300'}>{sourceVolumeWoWDeltaLabel}</span>
                <span className="ml-2">({sourceVolumeWoWDeltaPctLabel})</span>
              </div>
            </button>

            {/* ── Scheduled Email Digests ─────────────────────────────────────────────── */}
            <div className="rounded-shell border border-violet-500/30 bg-violet-950/20 p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Scheduled Email Digests</div>
                  <div className="mt-0.5 text-meta text-slate-500">Automated report emails on your schedule · rich KPI digest · CSV attachment · Firestore-backed</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    type="button"
                    onClick={loadReportSchedules}
                    disabled={schedulesLoading}
                    className="rounded-ctl border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-bold text-slate-400 transition-colors hover:border-white/20 hover:text-white disabled:opacity-40"
                  >
                    {schedulesLoading ? 'Loading…' : '↻ Refresh'}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenNewSchedule}
                    className="rounded-ctl border border-violet-400/40 bg-violet-500/15 px-4 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-violet-200 transition-colors hover:border-violet-300/50 hover:bg-violet-500/20"
                  >
                    + New Schedule
                  </button>
                </div>
              </div>

              {/* Schedule list */}
              {reportSchedules.length === 0 && !schedulesPanelOpen ? (
                <div className="mt-5 rounded-card border border-dashed border-white/10 px-5 py-8 text-center">
                  <div className="text-2xl mb-2">📬</div>
                  <div className="text-sm font-bold text-slate-400">No scheduled digests yet</div>
                  <div className="mt-1 text-meta text-slate-600 max-w-xs mx-auto">Create a schedule to automatically email rich KPI digests — daily, weekly, or monthly — to your team or clients.</div>
                  <button type="button" onClick={loadReportSchedules} className="mt-4 rounded-ctl border border-white/10 bg-white/5 px-4 py-1.5 text-micro font-bold text-slate-400 hover:text-white transition-colors">
                    Load schedules
                  </button>
                </div>
              ) : reportSchedules.length > 0 ? (
                <div className="mt-4 space-y-2">
                  {reportSchedules.map((sched) => {
                    const isRunning = runningScheduleId === sched.id;
                    const runRes = scheduleRunResult[sched.id];
                    const nextRun = sched.nextRunAt?.toDate ? sched.nextRunAt.toDate() : (sched.nextRunAt ? new Date(sched.nextRunAt) : null);
                    const lastRun = sched.lastRunAt?.toDate ? sched.lastRunAt.toDate() : (sched.lastRunAt ? new Date(sched.lastRunAt) : null);
                    const isPaused = sched.status === 'paused';
                    const lastOk   = sched.lastRunStatus === 'success';
                    return (
                      <div key={sched.id} className="rounded-card border border-white/[0.07] bg-slate-900/50 p-4">
                        <div className="flex items-start gap-3 flex-wrap">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-sm font-bold text-slate-100 truncate">{sched.label || 'Untitled'}</span>
                              <span className={`rounded-full border px-2 py-0.5 text-micro font-black uppercase tracking-[0.12em] ${isPaused ? 'border-amber-400/30 bg-amber-400/10 text-amber-300' : 'border-emerald-400/30 bg-emerald-400/10 text-emerald-300'}`}>
                                {isPaused ? 'Paused' : 'Active'}
                              </span>
                              <span className="rounded-full border border-violet-400/20 bg-violet-400/5 px-2 py-0.5 text-micro font-bold text-violet-400 capitalize">{sched.cadence}</span>
                              {sched.lastRunStatus && (
                                <span className={`rounded-full border px-2 py-0.5 text-micro font-bold ${lastOk ? 'border-cyan-400/20 bg-cyan-400/5 text-cyan-400' : 'border-rose-400/30 bg-rose-400/10 text-rose-400'}`}>
                                  {lastOk ? '✓ last sent' : '✕ last failed'}
                                </span>
                              )}
                            </div>
                            <div className="mt-1.5 flex gap-4 flex-wrap">
                              <div className="text-micro text-slate-500">
                                <span className="text-slate-600">Timeframe:</span> {sched.timeframe || '30d'}
                              </div>
                              <div className="text-micro text-slate-500">
                                <span className="text-slate-600">Delivery:</span> {sched.deliveryMode?.replace(/_/g, '+') || 'attachment+link'}
                              </div>
                              {sched.recipients?.length > 0 && (
                                <div className="text-micro text-slate-500">
                                  <span className="text-slate-600">To:</span> {Array.isArray(sched.recipients) ? sched.recipients.slice(0,2).join(', ') + (sched.recipients.length > 2 ? ` +${sched.recipients.length - 2}` : '') : sched.recipients}
                                </div>
                              )}
                              {nextRun && (
                                <div className="text-micro text-slate-500">
                                  <span className="text-slate-600">Next:</span> {nextRun.toLocaleDateString()} {nextRun.toLocaleTimeString([], {hour:'2-digit', minute:'2-digit'})}
                                </div>
                              )}
                              {lastRun && (
                                <div className="text-micro text-slate-500">
                                  <span className="text-slate-600">Last:</span> {lastRun.toLocaleDateString()}
                                </div>
                              )}
                              {sched.runCount > 0 && (
                                <div className="text-micro text-slate-500">
                                  <span className="text-slate-600">Runs:</span> {sched.runCount}
                                </div>
                              )}
                            </div>
                            {sched.lastRunError && (
                              <div className="mt-1.5 rounded-ctl border border-rose-500/20 bg-rose-500/5 px-3 py-1 text-micro text-rose-400 truncate">
                                {sched.lastRunError}
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 shrink-0 flex-wrap">
                            {runRes === 'sent' && (
                              <span className="text-micro font-bold text-emerald-400">✓ Sent!</span>
                            )}
                            {runRes === 'error' && (
                              <span className="text-micro font-bold text-rose-400">Send failed</span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleRunScheduleNow(sched.id)}
                              disabled={isRunning}
                              className="rounded-ctl border border-cyan-500/30 bg-cyan-500/10 px-3 py-1 text-micro font-bold text-cyan-300 transition-colors hover:border-cyan-400/50 hover:bg-cyan-500/15 disabled:opacity-40"
                            >
                              {isRunning ? 'Sending…' : '▶ Send Now'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleEditSchedule(sched)}
                              className="rounded-ctl border border-white/10 bg-white/5 px-2.5 py-1 text-micro font-bold text-slate-400 transition-colors hover:border-white/20 hover:text-white"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteSchedule(sched.id)}
                              className="rounded-ctl border border-rose-500/20 bg-rose-500/5 px-2.5 py-1 text-micro font-bold text-rose-400/70 transition-colors hover:border-rose-400/40 hover:text-rose-300"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}

              {/* Builder panel */}
              {schedulesPanelOpen && scheduleDraft && (
                <div className="mt-5 border-t border-white/10 pt-5">
                  <div className="mb-4 flex items-center justify-between gap-3">
                    <div className="text-xs font-black uppercase tracking-[0.18em] text-violet-300">{scheduleEditId ? 'Edit Schedule' : 'New Schedule'}</div>
                    <button type="button" onClick={() => { setSchedulesPanelOpen(false); setScheduleDraft(null); }} className="rounded-ctl border border-white/10 bg-white/5 px-3 py-1 text-micro font-bold text-slate-400 hover:text-white transition-colors">Cancel</button>
                  </div>

                  {/* Row 1: Label */}
                  <div className="mb-4">
                    <label className="mb-1.5 block text-micro font-black uppercase tracking-[0.14em] text-slate-400">Schedule Name</label>
                    <input
                      type="text"
                      value={scheduleDraft.label}
                      onChange={(e) => setScheduleDraft((d) => ({ ...d, label: e.target.value }))}
                      placeholder="e.g. Weekly Exec Digest"
                      className="w-full rounded-ctl border border-white/10 bg-slate-900/80 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:border-violet-400/50 focus:outline-none"
                    />
                  </div>

                  {/* Row 2: Cadence + Timeframe + Run hour */}
                  <div className="mb-4 grid gap-3 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-micro font-black uppercase tracking-[0.12em] text-slate-500">Cadence</label>
                      <select value={scheduleDraft.cadence} onChange={(e) => setScheduleDraft((d) => ({ ...d, cadence: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-800 px-3 py-2 text-xs text-slate-200 focus:border-violet-400/50 focus:outline-none">
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly</option>
                        <option value="monthly">Monthly</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-micro font-black uppercase tracking-[0.12em] text-slate-500">Timeframe</label>
                      <select value={scheduleDraft.timeframe} onChange={(e) => setScheduleDraft((d) => ({ ...d, timeframe: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-800 px-3 py-2 text-xs text-slate-200 focus:border-violet-400/50 focus:outline-none">
                        <option value="7d">Last 7 days</option>
                        <option value="14d">Last 14 days</option>
                        <option value="30d">Last 30 days</option>
                        <option value="90d">Last 90 days</option>
                        <option value="all">All time</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-micro font-black uppercase tracking-[0.12em] text-slate-500">Run hour (0–23)</label>
                      <input type="number" min="0" max="23" value={scheduleDraft.preferredRunHour} onChange={(e) => setScheduleDraft((d) => ({ ...d, preferredRunHour: Number(e.target.value) }))} className="w-full rounded-ctl border border-white/10 bg-slate-800 px-3 py-2 text-xs text-slate-200 focus:border-violet-400/50 focus:outline-none" />
                    </div>
                  </div>

                  {/* Row 3: Timezone + Delivery mode + Status */}
                  <div className="mb-4 grid gap-3 sm:grid-cols-3">
                    <div>
                      <label className="mb-1 block text-micro font-black uppercase tracking-[0.12em] text-slate-500">Timezone</label>
                      <input type="text" value={scheduleDraft.timeZone} onChange={(e) => setScheduleDraft((d) => ({ ...d, timeZone: e.target.value }))} placeholder="America/New_York" className="w-full rounded-ctl border border-white/10 bg-slate-800 px-3 py-2 text-xs text-slate-200 placeholder-slate-600 focus:border-violet-400/50 focus:outline-none" />
                    </div>
                    <div>
                      <label className="mb-1 block text-micro font-black uppercase tracking-[0.12em] text-slate-500">Delivery</label>
                      <select value={scheduleDraft.deliveryMode} onChange={(e) => setScheduleDraft((d) => ({ ...d, deliveryMode: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-800 px-3 py-2 text-xs text-slate-200 focus:border-violet-400/50 focus:outline-none">
                        <option value="attachment_and_link">Digest + CSV attachment</option>
                        <option value="attachment">CSV attachment only</option>
                        <option value="link">Digest + download link</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1 block text-micro font-black uppercase tracking-[0.12em] text-slate-500">Status</label>
                      <select value={scheduleDraft.status} onChange={(e) => setScheduleDraft((d) => ({ ...d, status: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-800 px-3 py-2 text-xs text-slate-200 focus:border-violet-400/50 focus:outline-none">
                        <option value="active">Active</option>
                        <option value="paused">Paused</option>
                      </select>
                    </div>
                  </div>

                  {/* Row 4: Recipients */}
                  <div className="mb-4">
                    <label className="mb-1.5 block text-micro font-black uppercase tracking-[0.14em] text-slate-400">Recipients <span className="font-normal text-slate-600">(comma-separated emails)</span></label>
                    <input
                      type="text"
                      value={Array.isArray(scheduleDraft.recipients) ? scheduleDraft.recipients.join(', ') : scheduleDraft.recipients}
                      onChange={(e) => setScheduleDraft((d) => ({ ...d, recipients: e.target.value.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean) }))}
                      placeholder="ceo@company.com, hr@company.com"
                      className="w-full rounded-ctl border border-white/10 bg-slate-900/80 px-4 py-2.5 text-sm text-slate-200 placeholder-slate-600 focus:border-violet-400/50 focus:outline-none"
                    />
                    <div className="mt-1 text-micro text-slate-600">{Array.isArray(scheduleDraft.recipients) ? scheduleDraft.recipients.length : 0} recipient{(Array.isArray(scheduleDraft.recipients) ? scheduleDraft.recipients.length : 0) !== 1 ? 's' : ''}</div>
                  </div>

                  {/* What's in the digest */}
                  <div className="mb-5 rounded-card border border-violet-500/10 bg-violet-950/10 p-4">
                    <div className="text-micro font-black uppercase tracking-[0.14em] text-violet-400 mb-2">Email digest includes</div>
                    <div className="flex flex-wrap gap-2">
                      {['📊 5 headline KPIs', '🛢️ Pipeline funnel bars', '🎯 Source quality table', '📈 Activity mix', '👤 Top recruiters', '📎 CSV attachment'].map((item) => (
                        <span key={item} className="rounded-full border border-violet-400/20 bg-violet-400/5 px-2.5 py-1 text-micro text-violet-300">{item}</span>
                      ))}
                    </div>
                  </div>

                  {/* Save */}
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-micro text-slate-600">Schedules run via Cloud Functions · Powered by Gmail / SendGrid</div>
                    <button
                      type="button"
                      disabled={!scheduleDraft.label.trim() || scheduleDraft.recipients.length === 0 || isSavingSchedule}
                      onClick={handleSaveSchedule}
                      className="rounded-ctl border border-violet-400/40 bg-violet-500/15 px-6 py-2.5 text-meta font-black uppercase tracking-[0.14em] text-violet-200 transition-colors hover:border-violet-300/60 hover:bg-violet-500/25 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {isSavingSchedule ? 'Saving…' : (scheduleEditId ? 'Update Schedule' : 'Create Schedule')}
                    </button>
                  </div>
                </div>
              )}

              {/* Info footer */}
              <div className="mt-5 border-t border-white/[0.06] pt-4 flex flex-wrap items-center gap-x-5 gap-y-1">
                <div className="text-micro text-slate-600">⏰ Cron: daily 07:05 · weekly Mon 07:15 · monthly 1st 07:25 (UTC)</div>
                <div className="text-micro text-slate-600">📧 Gmail SMTP or SendGrid</div>
                <div className="text-micro text-slate-600">📂 CSV stored in Firebase Storage · 7-day signed URL</div>
              </div>
            </div>

                        {/* ── Quality of Hire ──────────────────────────────────────────────────── */}
            <div className="rounded-shell border border-emerald-500/30 bg-emerald-950/20 p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-emerald-300">Quality of Hire</div>
                  <div className="mt-0.5 text-meta text-slate-500">90 &amp; 180-day performance scores &middot; Hired candidates only</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {qohCandidates.length === 0 && (
                    <div className="rounded-full border border-amber-400/30 bg-amber-400/10 px-3 py-1 text-meta font-bold text-amber-400">Rate hired candidates to unlock insights</div>
                  )}
                  <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-meta font-bold text-slate-400">Coverage: <span className="text-white">{qohCoverage}%</span> ({qohCandidates.length}/{qohHired})</div>
                </div>
              </div>
            
              {/* KPI row */}
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-card bg-slate-900/60 p-3.5">
                  <div className="text-micro uppercase tracking-[0.18em] text-slate-500">Avg 90-day Score</div>
                  <div className={`mt-1.5 text-3xl font-black ${qohAvg90 != null ? (qohAvg90 >= 7 ? 'text-emerald-300' : qohAvg90 >= 5 ? 'text-amber-300' : 'text-rose-400') : 'text-slate-600'}`}>
                    {qohAvg90 != null ? qohAvg90.toFixed(1) : '\u2014'}<span className="ml-1 text-base font-bold text-slate-500">/10</span>
                  </div>
                </div>
                <div className="rounded-card bg-slate-900/60 p-3.5">
                  <div className="text-micro uppercase tracking-[0.18em] text-slate-500">Avg 180-day Score</div>
                  <div className={`mt-1.5 text-3xl font-black ${qohAvg180 != null ? (qohAvg180 >= 7 ? 'text-emerald-300' : qohAvg180 >= 5 ? 'text-amber-300' : 'text-rose-400') : 'text-slate-600'}`}>
                    {qohAvg180 != null ? qohAvg180.toFixed(1) : '\u2014'}<span className="ml-1 text-base font-bold text-slate-500">/10</span>
                  </div>
                </div>
                <div className="rounded-card bg-slate-900/60 p-3.5">
                  <div className="text-micro uppercase tracking-[0.18em] text-slate-500">Hires Rated</div>
                  <div className="mt-1.5 text-3xl font-black text-white">{qohCandidates.length}</div>
                </div>
                <div className="rounded-card bg-slate-900/60 p-3.5">
                  <div className="text-micro uppercase tracking-[0.18em] text-slate-500">Coverage</div>
                  <div className={`mt-1.5 text-3xl font-black ${qohCoverage >= 70 ? 'text-emerald-300' : qohCoverage >= 30 ? 'text-amber-300' : 'text-rose-400'}`}>{qohCoverage}%</div>
                </div>
              </div>
            
              {qohCandidates.length > 0 && (
                <>
                  {/* 6-month 90d trend */}
                  <div className="mt-5 border-t border-white/10 pt-5">
                    <div className="mb-3 text-micro font-black uppercase tracking-[0.18em] text-slate-500">90-Day Score Trend (6 months)</div>
                    <div className="flex items-end gap-1.5" style={{height:'48px'}}>
                      {qohTrend.map((m) => (
                        <div key={m.shortLabel} className="flex flex-1 flex-col items-center gap-1" style={{height:'100%'}}>
                          <div
                            className={`w-full rounded-sm transition-all ${m.avg != null ? (m.avg >= 7 ? 'bg-emerald-400' : m.avg >= 5 ? 'bg-amber-400' : 'bg-rose-400') : 'bg-slate-700/50'}`}
                            style={{ height: m.avg != null ? Math.round((m.avg / 10) * 40) + 'px' : '2px', marginTop: 'auto' }}
                          />
                          <div className="text-micro text-slate-600">{m.shortLabel}</div>
                        </div>
                      ))}
                    </div>
                  </div>
            
                  {/* By source */}
                  {qohBySource.length > 0 && (
                    <div className="mt-5 border-t border-white/10 pt-5">
                      <div className="mb-3 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Avg Score by Source</div>
                      <div className="space-y-2">
                        {qohBySource.map((s) => (
                          <div key={s.source} className="flex items-center gap-3">
                            <div className="w-28 shrink-0 truncate text-meta text-slate-400">{s.source}</div>
                            <div className="flex-1 h-2 overflow-hidden rounded-full bg-slate-800/90">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${s.avgScore != null && s.avgScore >= 7 ? 'bg-emerald-400' : s.avgScore != null && s.avgScore >= 5 ? 'bg-amber-400' : 'bg-rose-500'}`}
                                style={{ width: s.avgScore != null ? Math.round((s.avgScore / 10) * 100) + '%' : '0%' }}
                              />
                            </div>
                            <div className={`w-8 shrink-0 text-right text-meta font-bold ${s.avgScore != null && s.avgScore >= 7 ? 'text-emerald-300' : s.avgScore != null && s.avgScore >= 5 ? 'text-amber-300' : 'text-rose-400'}`}>
                              {s.avgScore != null ? s.avgScore.toFixed(1) : '\u2014'}
                            </div>
                            <div className="w-7 shrink-0 text-right text-micro text-slate-600">&times;{s.count}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
            
                  {/* By recruiter */}
                  {qohByRecruiter.length > 0 && (
                    <div className="mt-5 border-t border-white/10 pt-5">
                      <div className="mb-3 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Avg Score by Recruiter</div>
                      <div className="space-y-2">
                        {qohByRecruiter.map((r) => (
                          <div key={r.name} className="flex items-center gap-3">
                            <div className="w-28 shrink-0 truncate text-meta text-slate-400">{r.name}</div>
                            <div className="flex-1 h-2 overflow-hidden rounded-full bg-slate-800/90">
                              <div
                                className={`h-full rounded-full transition-all duration-500 ${r.avgScore != null && r.avgScore >= 7 ? 'bg-emerald-400' : r.avgScore != null && r.avgScore >= 5 ? 'bg-amber-400' : 'bg-rose-500'}`}
                                style={{ width: r.avgScore != null ? Math.round((r.avgScore / 10) * 100) + '%' : '0%' }}
                              />
                            </div>
                            <div className={`w-8 shrink-0 text-right text-meta font-bold ${r.avgScore != null && r.avgScore >= 7 ? 'text-emerald-300' : r.avgScore != null && r.avgScore >= 5 ? 'text-amber-300' : 'text-rose-400'}`}>
                              {r.avgScore != null ? r.avgScore.toFixed(1) : '\u2014'}
                            </div>
                            <div className="w-7 shrink-0 text-right text-micro text-slate-600">&times;{r.count}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
            
                  {/* Recent rated hires */}
                  <div className="mt-5 border-t border-white/10 pt-5">
                    <div className="mb-3 text-micro font-black uppercase tracking-[0.18em] text-slate-500">Recent Rated Hires</div>
                    <div className="space-y-2">
                      {qohCandidates.slice(0, 5).map((c) => (
                        <div key={c.id} className="flex items-center justify-between gap-3 rounded-card border border-white/[0.06] bg-slate-900/40 px-4 py-2.5 flex-wrap">
                          <div className="min-w-0">
                            <div className="text-xs font-bold text-slate-200 truncate">{c.name}</div>
                            <div className="text-micro text-slate-500">{c.role || c.matchedJobTitle || 'Unknown role'} &middot; {c.source || 'Unknown source'}</div>
                          </div>
                          <div className="flex items-center gap-3 shrink-0">
                            {c.qohScore90 != null && (
                              <div className="text-center">
                                <div className="text-micro text-slate-600">90d</div>
                                <div className={`text-xs font-black ${c.qohScore90 >= 7 ? 'text-emerald-300' : c.qohScore90 >= 5 ? 'text-amber-300' : 'text-rose-400'}`}>{c.qohScore90}/10</div>
                              </div>
                            )}
                            {c.qohScore180 != null && (
                              <div className="text-center">
                                <div className="text-micro text-slate-600">180d</div>
                                <div className={`text-xs font-black ${c.qohScore180 >= 7 ? 'text-emerald-300' : c.qohScore180 >= 5 ? 'text-amber-300' : 'text-rose-400'}`}>{c.qohScore180}/10</div>
                              </div>
                            )}
                            <button
                              type="button"
                              onClick={() => { setQohEditCandidateId(c.id); setQohDraft({ score90: String(c.qohScore90 ?? ''), score180: String(c.qohScore180 ?? ''), notes: c.qohNotes || '' }); }}
                              className="rounded-ctl border border-white/10 bg-white/5 px-2.5 py-1 text-micro font-bold text-slate-400 transition-colors hover:border-emerald-400/30 hover:text-emerald-300"
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            
              {/* CTA footer */}
              <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
                <div className="text-meta text-slate-500">Score placed candidates after 90 or 180 days to track placement quality across sources and recruiters.</div>
                {filteredCandidates.filter((c) => c.status === 'hired' && c.qohScore90 == null).length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const unrated = filteredCandidates.find((c) => c.status === 'hired' && c.qohScore90 == null);
                      if (unrated) { setQohEditCandidateId(unrated.id); setQohDraft({ score90: '', score180: '', notes: '' }); }
                    }}
                    className="rounded-ctl border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-meta font-bold text-emerald-300 transition-colors hover:bg-emerald-500/15"
                  >
                    Rate next hire ({filteredCandidates.filter((c) => c.status === 'hired' && c.qohScore90 == null).length} unrated)
                  </button>
                )}
              </div>
            </div>
            

            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">

              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Agency members</div>

                <div className="mt-2 text-3xl font-black text-white">{reportingMetrics.totalMembers}</div>

              </div>

              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Active seats</div>

                <div className="mt-2 text-3xl font-black text-white">{reportingMetrics.activeSeats}</div>

              </div>

              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Candidates tracked</div>

                <div className="mt-2 text-3xl font-black text-white">{reportingMetrics.candidatesTracked}</div>

              </div>

              <div className="rounded-shell border border-white/10 bg-white/5 p-5">

                <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Open jobs</div>

                <div className="mt-2 text-3xl font-black text-white">{reportingMetrics.openJobs}</div>

              </div>

            </div>



            <div className="grid gap-5 xl:grid-cols-[1fr_1fr_0.9fr]">

              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Pipeline Stage Distribution</div>
                  <div className="flex items-center gap-2">
                    <div className="text-meta uppercase tracking-[0.16em] text-slate-500">{totalStageCandidates} total</div>
                    <button onClick={() => exportPipelineCsv(stageVisuals)} className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-micro font-bold uppercase tracking-[0.1em] text-cyan-400 hover:bg-cyan-500/20 transition-colors" title="Export pipeline stages CSV">↓ CSV</button>
                  </div>
                </div>

                <div className="mt-4 space-y-4">

                  {stageVisuals.map((stage) => (

                    <div key={stage.id}>
                      <div className="flex items-center justify-between gap-3 text-sm">
                        <div className="font-bold uppercase tracking-[0.12em] text-white">{stage.label}</div>
                        <div className="flex items-center gap-2 text-slate-300">
                          <span className="text-lg font-black text-white">{stage.count}</span>
                          <span className="text-meta font-bold text-slate-500">{stage.share}%</span>
                        </div>
                      </div>
                      <div className="mt-2 h-3 overflow-hidden rounded-full bg-slate-800/90">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${stage.barWidth}%`, backgroundColor: stage.color }}
                        />
                      </div>
                    </div>

                  ))}

                </div>

              </div>



              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Recruiter Activity Mix</div>
                  <div className="flex items-center gap-2">
                    <div className="text-meta uppercase tracking-[0.16em] text-slate-500">{activityMixTotal} actions</div>
                    <button onClick={() => exportActivityMixCsv(activityMixItems)} className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-micro font-bold uppercase tracking-[0.1em] text-cyan-400 hover:bg-cyan-500/20 transition-colors" title="Export activity mix CSV">↓ CSV</button>
                  </div>
                </div>

                {activityMixItems.length === 0 ? (
                  <div className="mt-4 rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No recruiter activity recorded yet.</div>
                ) : (
                  <div className="mt-4 flex flex-col items-center gap-5 lg:flex-row lg:items-start">
                    <div className="relative mx-auto h-32 w-32 shrink-0 rounded-full border border-white/10 bg-slate-950/70 p-3">
                      <div className="h-full w-full rounded-full" style={{ background: activityMixGradient }} />
                      <div className="absolute inset-[22px] flex flex-col items-center justify-center rounded-full bg-slate-900/95 ring-1 ring-white/5">
                        <div className="text-3xl font-black text-white">{activityMixTotal}</div>
                        <div className="text-micro font-black uppercase tracking-[0.18em] text-slate-500">Actions</div>
                      </div>
                    </div>
                    <div className="w-full space-y-3">
                      {activityMixItems.map((item) => (
                        <div key={item.id} className="flex items-center justify-between gap-3 text-sm">
                          <div className="flex min-w-0 items-center gap-3">
                            <span className="h-3 w-3 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
                            <span className="truncate font-bold capitalize text-white">{item.label}</span>
                          </div>
                          <div className="flex shrink-0 items-center gap-3">
                            <span className="font-black text-white">{item.count}</span>
                            <span className="text-meta text-slate-500">{item.share}%</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>



              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">

                <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Reporting Highlights</div>

                <div className="mt-4 grid grid-cols-2 gap-3">

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Screenings done</div>

                    <div className="mt-2 text-2xl font-black text-cyan-300">{reportingMetrics.screeningCompleted}</div>

                  </div>

                  <div className="rounded-card border border-white/10 bg-slate-950/60 p-4">

                    <div className="text-meta uppercase tracking-[0.18em] text-slate-500">Seats free</div>

                    <div className="mt-2 text-2xl font-black text-emerald-300">{agencySeatsAvailable}</div>

                  </div>

                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                  <div className="text-meta uppercase tracking-[0.18em] text-slate-500">{activityTrendDays}-Day Activity Trend</div>
                  <button onClick={() => exportActivityTrendCsv(activityTrendBuckets)} className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-micro font-bold uppercase tracking-[0.1em] text-cyan-400 hover:bg-cyan-500/20 transition-colors" title="Export activity trend CSV">↓ CSV</button>
                </div>
                <div className="mt-3 rounded-shell border border-white/10 bg-slate-950/60 p-3">
                  <svg
                  viewBox={`0 0 ${trendChartWidth} ${trendChartHeight}`}
                  className="h-24 w-full"
                  preserveAspectRatio="none"
                  onMouseLeave={() => setReportingChartHoverIdx(null)}
                >
                  <defs>
                    <linearGradient id="reportingTrendAreaGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.45" />
                      <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.02" />
                    </linearGradient>
                  </defs>
                  {[0, 1, 2, 3].map((grid) => {
                    const y = (trendChartHeight / 3) * grid;
                    return <line key={grid} x1="0" y1={y} x2={trendChartWidth} y2={y} stroke="#1e293b" strokeWidth="0.4" />;
                  })}
                  {trendAreaPath && <path d={trendAreaPath} fill="url(#reportingTrendAreaGradient)" />}
                  {trendLinePath && <path d={trendLinePath} fill="none" stroke="#22d3ee" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />}
                  {activityTrendBuckets.map((point, index) => {
                    const x = index * trendPointStep;
                    const y = trendChartHeight - ((point.value / activityTrendMax) * trendChartHeight);
                    return <circle key={`${point.label}-${index}`} cx={x} cy={Math.max(2, y)} r="1.4" fill="#22d3ee" />;
                  })}
                  {activityTrendBuckets.length > 1 && (
                    <rect x="0" y="0" width={trendChartWidth} height={trendChartHeight} fill="transparent" style={{ cursor: 'crosshair' }}
                      onMouseMove={(e) => {
                        const svgEl = e.currentTarget.closest('svg');
                        if (!svgEl) return;
                        const rect = svgEl.getBoundingClientRect();
                        const relX = (e.clientX - rect.left) / rect.width;
                        const idx = Math.max(0, Math.min(activityTrendBuckets.length - 1, Math.round(relX * (activityTrendBuckets.length - 1))));
                        setReportingChartHoverIdx(idx);
                      }}
                    />
                  )}
                  {reportingChartHoverIdx !== null && activityTrendBuckets[reportingChartHoverIdx] && (() => {
                    const pt = activityTrendBuckets[reportingChartHoverIdx];
                    const hx = reportingChartHoverIdx * trendPointStep;
                    const ptY = Math.max(2, trendChartHeight - ((pt.value / activityTrendMax) * trendChartHeight));
                    const tipX = hx > trendChartWidth * 0.65 ? hx - 26 : hx + 3;
                    const tipY = Math.max(1, ptY - 14);
                    return (
                      <g pointerEvents="none">
                        <line x1={hx} y1={0} x2={hx} y2={trendChartHeight} stroke="#22d3ee" strokeWidth="0.5" strokeDasharray="1.5 1" opacity="0.55" />
                        <circle cx={hx} cy={ptY} r="2.2" fill="#22d3ee" stroke="#0f172a" strokeWidth="0.8" />
                        <rect x={tipX} y={tipY} width="24" height="12" rx="2" fill="#0f172a" stroke="#22d3ee" strokeWidth="0.5" opacity="0.97" />
                        <text x={tipX + 12} y={tipY + 4.5} textAnchor="middle" fontSize="3" fill="#67e8f9" fontFamily="monospace">{pt.label}</text>
                        <text x={tipX + 12} y={tipY + 9.5} textAnchor="middle" fontSize="4" fill="white" fontFamily="monospace" fontWeight="bold">{pt.value}</text>
                      </g>
                    );
                  })()}
                </svg>
                  <div className="mt-2 flex items-center justify-between text-micro uppercase tracking-[0.14em] text-slate-500">
                    <span>{activityTrendBuckets[0]?.label || ''}</span>
                    <span>{activityTrendBuckets[activityTrendBuckets.length - 1]?.label || ''}</span>
                  </div>
                </div>

                <div className="mt-5 text-meta uppercase tracking-[0.18em] text-slate-500">Change Ledger Timeline</div>
                <div className="mt-3 rounded-shell border border-white/10 bg-slate-950/60 p-4">
                  {reportingTimelineEvents.length === 0 ? (
                    <div className="text-sm text-slate-500">No actions yet.</div>
                  ) : (
                    <div className="space-y-4">
                      {reportingTimelineEvents.map((item) => (
                        <div key={item.id} className="flex gap-3">
                          <div className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-cyan-300 shadow-[0_0_14px_rgba(34,211,238,0.7)]" />
                          <div className="min-w-0">
                            <div className="text-sm font-bold leading-6 text-white">{item.title}</div>
                            <div className="mt-2 inline-flex rounded-full border border-cyan-400/15 bg-cyan-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.16em] text-cyan-200">{item.type}</div>
                            <div className="mt-2 text-meta text-slate-500">{item.stamp}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

              </div>

            </div>
            
            {/* Custom Report Builder */}
            {customReportOpen && (
              <div className="rounded-shell border border-violet-500/40 bg-violet-950/30 p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Custom Report Builder</div>
                  <button type="button" onClick={() => setCustomReportOpen(false)} className="rounded-ctl border border-white/10 bg-white/5 px-3 py-1 text-xs font-bold text-slate-400 hover:text-white transition-colors">Close</button>
                </div>
                <div className="mt-4 text-meta text-slate-400">Select sections to include in your export:</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {[
                    { id: 'kpis', label: 'KPI Metrics' },
                    { id: 'pipeline', label: 'Pipeline Distribution' },
                    { id: 'sources', label: 'Source Quality' },
                    { id: 'activity', label: 'Activity Trend' },
                    { id: 'forecast', label: 'Forecast & Attainment' },
                    { id: 'aging', label: 'Pipeline Aging' },
                    { id: 'scorecards', label: 'Interview Scorecards' },
                    { id: 'cph', label: 'Cost-per-Hire' },
                    { id: 'revenue', label: 'Revenue & Placements' },
                  ].map((sec) => (
                    <button
                      key={sec.id}
                      type="button"
                      onClick={() => setCustomReportSections((prev) =>
                        prev.includes(sec.id) ? prev.filter((s) => s !== sec.id) : [...prev, sec.id]
                      )}
                      className={`rounded-full border px-3 py-1 text-xs font-bold transition-colors ${customReportSections.includes(sec.id) ? 'border-violet-400 bg-violet-500/20 text-violet-200' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'}`}
                    >
                      {sec.label}
                    </button>
                  ))}
                </div>
                <div className="mt-5 flex flex-wrap gap-2 border-t border-white/10 pt-4">
                  <div className="text-meta text-slate-400 self-center">{customReportSections.length} sections selected &middot; Exports use current filter &amp; date range</div>
                  <div className="ml-auto flex gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        handleExportReportingXls(kpiDeltasFull, activityTrendBuckets, sourceQualityItems, stageVisuals, reportingRangeDays + 'd', reportingRecruiterFilter || 'All');
                      }}
                      className="rounded-ctl border border-emerald-500/50 bg-emerald-500/10 px-3 py-1.5 text-emerald-200 hover:bg-emerald-500/20 transition-colors text-xs font-bold"
                    >
                      &#8595; XLS
                    </button>
                    <button
                      type="button"
                      onClick={handleExportBiJson}
                      disabled={biSnapshotSaveStatus === 'saving'}
                      className={`rounded-ctl border px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-60 ${biSnapshotSaveStatus === 'saved' ? 'border-emerald-400/60 bg-emerald-500/15 text-emerald-300' : biSnapshotSaveStatus === 'error' ? 'border-rose-500/50 bg-rose-500/10 text-rose-300' : 'border-sky-500/50 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20'}`}
                      title={biSnapshotSaveStatus === 'saved' ? 'Snapshot saved to Firestore' : '↓ Download + save snapshot to Firestore'}
                    >
                      {biSnapshotSaveStatus === 'saving' ? 'Saving…' : biSnapshotSaveStatus === 'saved' ? '✓ Saved' : '&#8595; BI JSON'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* ── FLAGSHIP: Placement & Revenue Intelligence ── */}
            <div className="rounded-shell border border-emerald-500/20 bg-emerald-950/10 p-5 sm:p-6">
              {/* Header */}
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-emerald-400">Placement &amp; Revenue Intelligence</div>
                  <div className="mt-1 text-meta text-slate-500">Billed fees · placement pipeline · client &amp; recruiter breakdown · 12-month trend</div>
                </div>
                {revenueAttainmentPct != null && (
                  <div className={`rounded-full border px-3 py-1.5 text-meta font-black ${revenueAttainmentPct >= 100 ? 'border-emerald-400/40 bg-emerald-500/10 text-emerald-300' : revenueAttainmentPct >= 70 ? 'border-amber-400/40 bg-amber-500/10 text-amber-300' : 'border-rose-400/40 bg-rose-500/10 text-rose-300'}`}>
                    {revenueAttainmentPct}% of target
                  </div>
                )}
              </div>

              {/* KPI Strip */}
              <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
                {[
                  { label: 'Total Billed', value: totalRevenue > 0 ? 'R\u00A0' + totalRevenue.toLocaleString() : 'R\u00A00', sub: revenueGrowthPct != null ? (revenueGrowthPct >= 0 ? '+' : '') + revenueGrowthPct + '% vs prev' : null, subColor: revenueGrowthPct != null ? (revenueGrowthPct >= 0 ? 'text-emerald-400' : 'text-rose-400') : 'text-slate-500', accent: true },
                  { label: 'Placements', value: String(revPlacedCandidates.length), sub: 'with logged fees', subColor: 'text-slate-500' },
                  { label: 'Avg Fee', value: avgPlacementFee > 0 ? 'R\u00A0' + avgPlacementFee.toLocaleString() : '–', sub: avgFeePercent ? avgFeePercent + '% avg' : null, subColor: 'text-slate-500' },
                  { label: 'Annual Run Rate', value: revenueRunRateAnnual > 0 ? 'R\u00A0' + (revenueRunRateAnnual >= 1000000 ? (revenueRunRateAnnual / 1000000).toFixed(1) + 'M' : revenueRunRateAnnual.toLocaleString()) : '–', sub: 'annualised', subColor: 'text-slate-500' },
                  { label: 'Offer→Placed', value: offerToPlacementRate != null ? offerToPlacementRate + '%' : '–', sub: avgDaysToPlacement != null ? 'Avg ' + avgDaysToPlacement + 'd to place' : null, subColor: 'text-slate-500' },
                ].map((kpi) => (
                  <div key={kpi.label} className={`rounded-card border p-3 text-center ${kpi.accent ? 'border-emerald-400/30 bg-emerald-500/10' : 'border-white/10 bg-slate-950/40'}`}>
                    <div className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">{kpi.label}</div>
                    <div className={`mt-1.5 text-xl font-black ${kpi.accent ? 'text-emerald-300' : 'text-white'}`}>{kpi.value}</div>
                    {kpi.sub && <div className={`mt-0.5 text-micro font-bold ${kpi.subColor}`}>{kpi.sub}</div>}
                  </div>
                ))}
              </div>

              {/* 12-Month Revenue Trend */}
              <div className="mt-6">
                <div className="text-meta font-black uppercase tracking-[0.18em] text-slate-400">12-Month Billing Trend</div>
                <div className="mt-3 flex items-end gap-1.5" style={{ height: '80px' }}>
                  {revMonthTrend.map((month, i) => {
                    const barH = Math.max(month.revenue > 0 ? 6 : 2, Math.round((month.revenue / revMonthMax) * 68));
                    const isCurrentMonth = i === 11;
                    return (
                      <div key={month.shortLabel} className="group relative flex flex-1 flex-col items-center gap-1">
                        {month.revenue > 0 && (
                          <div className="absolute bottom-6 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-ctl border border-white/10 bg-slate-950 px-2 py-1 text-micro font-bold text-white group-hover:block z-10 pointer-events-none">
                            R{(month.revenue / 1000).toFixed(0)}k · {month.placements}p
                          </div>
                        )}
                        <div
                          className={`w-full rounded-t transition-all ${isCurrentMonth ? 'bg-emerald-400/80' : month.revenue > 0 ? 'bg-emerald-500/40 group-hover:bg-emerald-400/60' : 'bg-slate-800/60'}`}
                          style={{ height: barH + 'px' }}
                        />
                        <div className={`text-micro ${isCurrentMonth ? 'text-emerald-300 font-bold' : 'text-slate-600'}`}>{month.shortLabel}</div>
                      </div>
                    );
                  })}
                </div>
                {revenueTargetMonthly > 0 && (
                  <div className="mt-3 flex items-center gap-3">
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-slate-800">
                      <div
                        className={`h-full rounded-full transition-all ${revenueAttainmentPct >= 100 ? 'bg-emerald-400' : revenueAttainmentPct >= 70 ? 'bg-amber-400' : 'bg-rose-400'}`}
                        style={{ width: Math.min(100, revenueAttainmentPct || 0) + '%' }}
                      />
                    </div>
                    <div className="text-micro font-black text-slate-400 shrink-0">
                      Target: R{Number(revenueTargetMonthly).toLocaleString()}/mo
                    </div>
                  </div>
                )}
              </div>

              {/* Client & Recruiter Revenue Breakdown */}
              <div className="mt-6 grid gap-5 lg:grid-cols-2">

                {/* Revenue by Client */}
                <div>
                  <div className="mb-3 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Revenue by Client</div>
                  {revenueByClient.length === 0 ? (
                    <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-meta text-slate-500">No placement fees logged yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {revenueByClient.map((row) => (
                        <div key={row.name} className="flex items-center gap-3">
                          <div className="w-28 shrink-0 truncate text-meta font-bold text-slate-200">{row.name}</div>
                          <div className="relative flex-1 h-4 overflow-hidden rounded-full bg-slate-800/70">
                            <div
                              className="absolute inset-y-0 left-0 rounded-full bg-emerald-500/50 transition-all"
                              style={{ width: Math.round((row.revenue / revenueByClientMax) * 100) + '%' }}
                            />
                          </div>
                          <div className="w-20 shrink-0 text-right text-meta font-black text-emerald-300">R{(row.revenue / 1000).toFixed(0)}k</div>
                          <div className="w-6 shrink-0 text-right text-micro text-slate-500">{row.placements}p</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Revenue by Recruiter */}
                <div>
                  <div className="mb-3 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Revenue by Recruiter</div>
                  {revenueByRecruiter.length === 0 ? (
                    <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-meta text-slate-500">No recruiter data yet.</div>
                  ) : (
                    <div className="space-y-2">
                      {revenueByRecruiter.map((row, i) => (
                        <div key={row.name} className="flex items-center gap-3">
                          <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-micro font-black ${i === 0 ? 'bg-emerald-400 text-slate-950' : 'bg-slate-700 text-slate-300'}`}>{i + 1}</div>
                          <div className="w-24 shrink-0 truncate text-meta font-bold text-slate-200">{row.name}</div>
                          <div className="relative flex-1 h-4 overflow-hidden rounded-full bg-slate-800/70">
                            <div
                              className="absolute inset-y-0 left-0 rounded-full bg-cyan-500/50 transition-all"
                              style={{ width: Math.round((row.revenue / revenueByRecruiterMax) * 100) + '%' }}
                            />
                          </div>
                          <div className="w-20 shrink-0 text-right text-meta font-black text-white">R{(row.revenue / 1000).toFixed(0)}k</div>
                          <div className="w-6 shrink-0 text-right text-micro text-slate-500">{row.placements}p</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Revenue by Job Type */}
              {revenueByJobType.length > 0 && (
                <div className="mt-5">
                  <div className="mb-3 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Revenue Mix by Job Type</div>
                  <div className="flex flex-wrap gap-2">
                    {revenueByJobType.map((row) => {
                      const sharePct = totalRevenue > 0 ? Math.round((row.revenue / totalRevenue) * 100) : 0;
                      return (
                        <div key={row.type} className="rounded-card border border-white/10 bg-slate-900/50 px-3 py-2.5">
                          <div className="text-micro font-black uppercase tracking-[0.12em] text-slate-400">{row.type}</div>
                          <div className="mt-1 text-base font-black text-white">R{(row.revenue / 1000).toFixed(0)}k</div>
                          <div className="mt-0.5 text-micro text-slate-500">{sharePct}% · {row.placements} placement{row.placements !== 1 ? 's' : ''}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Top Placements Table */}
              {topPlacements.length > 0 && (
                <div className="mt-5">
                  <div className="mb-3 text-meta font-black uppercase tracking-[0.18em] text-slate-400">Top Placements</div>
                  <div className="overflow-hidden rounded-card border border-white/10">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-white/10 bg-slate-950/60">
                          <th className="px-4 py-2.5 text-left text-micro font-black uppercase tracking-[0.14em] text-slate-500">Candidate</th>
                          <th className="px-4 py-2.5 text-left text-micro font-black uppercase tracking-[0.14em] text-slate-500">Client</th>
                          <th className="px-4 py-2.5 text-right text-micro font-black uppercase tracking-[0.14em] text-slate-500">Fee</th>
                          <th className="px-4 py-2.5 text-right text-micro font-black uppercase tracking-[0.14em] text-slate-500 hidden sm:table-cell">Placed</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topPlacements.map((c, i) => (
                          <tr key={c.id || i} className={`border-b border-white/5 ${i % 2 === 0 ? 'bg-slate-950/20' : ''} hover:bg-emerald-500/5 transition-colors`}>
                            <td className="px-4 py-2.5">
                              <div className="font-bold text-white text-meta truncate max-w-[140px]">{c.name || 'Unnamed'}</div>
                              {c.role && <div className="text-micro text-slate-500 truncate">{c.role}</div>}
                            </td>
                            <td className="px-4 py-2.5">
                              <div className="text-meta text-slate-300 truncate max-w-[120px]">{c.company || c.clientCompany || '–'}</div>
                            </td>
                            <td className="px-4 py-2.5 text-right">
                              <div className="text-meta font-black text-emerald-300">R{Number(c.placementFee).toLocaleString()}</div>
                              {Number(c.placementFeePercent || 0) > 0 && (
                                <div className="text-micro text-slate-500">{c.placementFeePercent}%</div>
                              )}
                            </td>
                            <td className="px-4 py-2.5 text-right hidden sm:table-cell">
                              <div className="text-meta text-slate-400">
                                {c.hiredAt ? new Date(c.hiredAt).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: '2-digit' }) : '–'}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="border-t border-white/10 bg-slate-950/60">
                          <td colSpan={2} className="px-4 py-2.5 text-meta font-black uppercase tracking-[0.12em] text-slate-400">Total (top {topPlacements.length})</td>
                          <td className="px-4 py-2.5 text-right text-meta font-black text-emerald-300">
                            R{topPlacements.reduce((s, c) => s + Number(c.placementFee || 0), 0).toLocaleString()}
                          </td>
                          <td className="hidden sm:table-cell" />
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* Empty state */}
              {revPlacedCandidates.length === 0 && (
                <div className="mt-5 rounded-card border border-dashed border-emerald-400/10 px-5 py-6 text-center">
                  <div className="text-meta font-black uppercase tracking-[0.18em] text-emerald-400/50 mb-1">No Placements Logged Yet</div>
                  <div className="text-meta text-slate-500">Move candidates to <span className="font-bold text-slate-400">Hired</span> and record a placement fee to unlock revenue reporting.</div>
                </div>
              )}
            </div>

            {/* Cost-per-Hire + Scorecards row */}
            <div className="grid gap-4 sm:grid-cols-2">

              {/* Cost-per-Hire */}
              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Cost-per-Hire</div>
                  {costPerHireValue != null && costPerHireDelta != null && (
                    <div className={`text-meta font-bold ${costPerHireDelta <= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {costPerHireDelta > 0 ? '+' : ''}{costPerHireDelta}% vs prev
                    </div>
                  )}
                </div>
                <div className="mt-3 text-4xl font-black text-white">{costPerHireDisplay}</div>
                {cphBudget > 0 ? (
                  <div className="mt-3 grid grid-cols-3 gap-2 border-t border-white/10 pt-3">
                    <div className="text-center">
                      <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Budget</div>
                      <div className="mt-1 text-sm font-black text-white">${Number(cphBudget).toLocaleString()}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Hires</div>
                      <div className="mt-1 text-sm font-black text-white">{hiresInRange}</div>
                    </div>
                    <div className="text-center">
                      <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Remaining</div>
                      <div className="mt-1 text-sm font-black text-amber-300">${Math.max(0, Number(cphBudget) - hiresInRange * (costPerHireValue || 0)).toLocaleString()}</div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-3 text-meta text-slate-500">Set a budget in Settings &#8594; Reporting to track cost-per-hire.</div>
                )}
              </div>

              {/* Interview Scorecard Analytics */}
              <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Interview Scorecards</div>
                  <div className="text-meta uppercase tracking-[0.16em] text-slate-500">{scorecardResponses.length} screened</div>
                </div>
                {scorecardResponses.length === 0 ? (
                  <div className="mt-4 rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No screening responses in this period.</div>
                ) : (
                  <>
                    <div className="mt-4 grid grid-cols-3 gap-2">
                      <div className="rounded-ctl bg-slate-900/60 p-2.5 text-center">
                        <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Avg score</div>
                        <div className="mt-1 text-xl font-black text-white">{scorecardAvg != null ? scorecardAvg : 'N/A'}</div>
                      </div>
                      <div className="rounded-ctl bg-slate-900/60 p-2.5 text-center">
                        <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Pass rate</div>
                        <div className="mt-1 text-xl font-black text-emerald-300">{scorecardPassRate != null ? scorecardPassRate + '%' : 'N/A'}</div>
                      </div>
                      <div className="rounded-ctl bg-slate-900/60 p-2.5 text-center">
                        <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Rejected</div>
                        <div className="mt-1 text-xl font-black text-rose-400">{scorecardRejectCount}</div>
                      </div>
                    </div>
                    {scorecardWithScores.length > 0 && (
                      <div className="mt-4">
                        <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Score distribution</div>
                        <div className="mt-2 flex items-end gap-1 h-10">
                          {scorecardBuckets.map((bucket) => (
                            <div key={bucket.label} className="flex flex-1 flex-col items-center gap-1">
                              <div
                                className="w-full rounded-t bg-cyan-500/60 transition-all"
                                style={{ height: Math.round((bucket.count / scorecardBucketMax) * 36) + 'px' }}
                              />
                              <div className="text-micro text-slate-500">{bucket.label}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Pipeline Aging & Stuck Alerts */}
            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Pipeline Aging &amp; Stuck Alerts</div>
                <div className="flex items-center gap-3">
                  <div className="text-meta uppercase tracking-[0.16em] text-slate-500">Threshold: {agingThreshDays}d</div>
                  {totalStuckCount > 0 && (
                    <div className="inline-flex items-center gap-1.5 rounded-full border border-amber-500/50 bg-amber-500/10 px-3 py-1 text-xs font-black text-amber-300">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3 w-3"><path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.5 1.5 0 0 1-1.299 2.25H2.804a1.5 1.5 0 0 1-1.3-2.25l5.197-9ZM8 5a.75.75 0 0 1 .75.75v2.5a.75.75 0 1 1-1.5 0v-2.5A.75.75 0 0 1 8 5Zm0 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/></svg>
                      {totalStuckCount} stuck
                    </div>
                  )}
                </div>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {agingAnalysis.filter((s) => s.total > 0).map((stage) => {
                  const stuckPct = stage.total > 0 ? Math.round((stage.stuck / stage.total) * 100) : 0;
                  const severity = stage.stuck === 0 ? 'ok' : stuckPct >= 50 ? 'high' : 'mid';
                  return (
                    <div
                      key={stage.id}
                      className={`rounded-card border p-3 ${severity === 'high' ? 'border-amber-500/50 bg-amber-950/20' : severity === 'mid' ? 'border-yellow-500/30 bg-yellow-950/10' : 'border-white/10 bg-slate-950/30'}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="text-xs font-bold uppercase tracking-[0.12em] text-white">{stage.label}</div>
                        <div className="text-meta font-black text-slate-400">{stage.total} total</div>
                      </div>
                      <div className="mt-2 flex items-center gap-2">
                        <div className={`text-xl font-black ${stage.stuck > 0 ? 'text-amber-300' : 'text-emerald-400'}`}>{stage.stuck}</div>
                        <div className="text-meta text-slate-400">stuck &gt;{agingThreshDays}d</div>
                        {stage.stuck > 0 && <div className="ml-auto text-meta font-bold text-amber-400">{stuckPct}%</div>}
                      </div>
                      <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-slate-800">
                        <div
                          className={`h-full rounded-full transition-all ${severity === 'high' ? 'bg-amber-400' : severity === 'mid' ? 'bg-yellow-400' : 'bg-emerald-500'}`}
                          style={{ width: stuckPct + '%' }}
                        />
                      </div>
                      <div className="mt-1.5 text-micro text-slate-500">Avg {stage.avgDays}d in stage</div>
                    </div>
                  );
                })}
              </div>
              {totalStuckCount === 0 && filteredCandidates.length > 0 && (
                <div className="mt-4 text-meta text-emerald-400">&#10003; No candidates stuck beyond threshold. Pipeline is flowing.</div>
              )}
            </div>


            {/* Time-in-Stage Velocity Distribution */}
            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Time-in-Stage Distribution</div>
                  <div className="mt-1 text-meta text-slate-500">How long candidates dwell in each pipeline stage</div>
                </div>
                <div className="flex items-center gap-3 text-micro uppercase tracking-[0.14em] text-slate-500">
                  <span className="flex items-center gap-1"><span className="inline-block h-2 w-2 rounded-sm bg-cyan-500/60" /> Histogram</span>
                  <span>Median · P25–P75</span>
                </div>
              </div>

              <div className="mt-5 space-y-5">
                {stageTimeInStage.map((stage) => {
                  const medLabel = stage.medianDays == null ? 'N/A' : stage.medianDays < 1 ? '<1d' : stage.medianDays.toFixed(1) + 'd';
                  const p25Label = stage.p25 == null ? '–' : stage.p25 < 1 ? '<1d' : stage.p25.toFixed(1) + 'd';
                  const p75Label = stage.p75 == null ? '–' : stage.p75 < 1 ? '<1d' : stage.p75.toFixed(1) + 'd';
                  const deltaPositive = stage.deltaPct != null && stage.deltaPct > 0;
                  const deltaNegative = stage.deltaPct != null && stage.deltaPct < 0;
                  return (
                    <div key={stage.id} className="rounded-card border border-white/10 bg-slate-900/40 p-4">
                      {/* Stage header row */}
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2">
                          <div className={`h-2.5 w-2.5 rounded-full ${stage.color}`} />
                          <div className="text-xs font-black uppercase tracking-[0.16em] text-white">{stage.label}</div>
                          <div className="text-meta text-slate-500">{stage.n} candidates</div>
                        </div>
                        <div className="flex items-center gap-3">
                          {stage.deltaPct != null && (
                            <div className={`flex items-center gap-1 text-meta font-bold ${deltaPositive ? 'text-rose-400' : deltaNegative ? 'text-emerald-400' : 'text-slate-400'}`}>
                              {deltaPositive ? '▲' : deltaNegative ? '▼' : ''}{stage.deltaPct > 0 ? '+' : ''}{stage.deltaPct}% vs prev
                            </div>
                          )}
                          <div className="text-right">
                            <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Median</div>
                            <div className={`text-base font-black ${stage.medianDays == null ? 'text-slate-500' : stage.medianDays <= 3 ? 'text-emerald-300' : stage.medianDays <= 14 ? 'text-amber-300' : 'text-rose-400'}`}>{medLabel}</div>
                          </div>
                        </div>
                      </div>

                      {stage.n === 0 ? (
                        <div className="mt-3 text-meta text-slate-600">No data in this period.</div>
                      ) : (
                        <>
                          {/* Histogram */}
                          <div className="mt-4 flex items-end gap-1.5" style={{ height: '52px' }}>
                            {stage.buckets.map((bucket) => {
                              const barH = Math.max(bucket.count > 0 ? 6 : 0, Math.round((bucket.count / stage.bucketMax) * 44));
                              return (
                                <div key={bucket.label} className="flex flex-1 flex-col items-center gap-1">
                                  {bucket.count > 0 && (
                                    <div className="text-micro font-bold text-slate-400">{bucket.count}</div>
                                  )}
                                  <div
                                    className="w-full rounded-t bg-cyan-500/60 transition-all duration-500"
                                    style={{ height: barH + 'px' }}
                                  />
                                </div>
                              );
                            })}
                          </div>
                          {/* Bucket labels */}
                          <div className="mt-1 flex gap-1.5">
                            {stage.buckets.map((bucket) => (
                              <div key={bucket.label} className="flex-1 text-center text-micro text-slate-500">{bucket.label}</div>
                            ))}
                          </div>

                          {/* Percentile strip */}
                          <div className="mt-3 flex items-center gap-4 border-t border-white/10 pt-3">
                            <div className="text-center">
                              <div className="text-micro uppercase tracking-[0.12em] text-slate-500">P25</div>
                              <div className="mt-0.5 text-xs font-bold text-slate-300">{p25Label}</div>
                            </div>
                            <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/40 via-amber-500/40 to-rose-500/40" />
                            <div className="text-center">
                              <div className="text-micro uppercase tracking-[0.12em] text-slate-500">P50</div>
                              <div className={`mt-0.5 text-xs font-black ${stage.medianDays == null ? 'text-slate-500' : stage.medianDays <= 3 ? 'text-emerald-300' : stage.medianDays <= 14 ? 'text-amber-300' : 'text-rose-400'}`}>{medLabel}</div>
                            </div>
                            <div className="flex-1 h-px bg-gradient-to-r from-amber-500/40 via-orange-500/40 to-rose-500/40" />
                            <div className="text-center">
                              <div className="text-micro uppercase tracking-[0.12em] text-slate-500">P75</div>
                              <div className="mt-0.5 text-xs font-bold text-slate-300">{p75Label}</div>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2 border-t border-white/10 pt-4 text-micro text-slate-500">
                <span className="text-emerald-400 font-bold">≤3d</span> Fast
                <span className="ml-4 text-amber-400 font-bold">4–14d</span> Normal
                <span className="ml-4 text-rose-400 font-bold">15+d</span> Slow &mdash; review this stage
                <span className="ml-auto">&#9650; = slower than prev period &nbsp; &#9660; = faster</span>
              </div>
            </div>


            {/* ── FLAGSHIP: Conversion Funnel Rates ── */}
            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Conversion Funnel</div>
                  <div className="mt-1 text-meta text-slate-500">Stage-to-stage rates · drop-off · benchmarks · δ vs prev period</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="rounded-full border border-white/10 bg-slate-900/60 px-3 py-1 text-micro text-slate-400">
                    {filteredCandidates.length} candidates · {hiresInRange} hired
                  </div>
                  {withdrawCount > 0 && (
                    <div className="rounded-full border border-rose-500/30 bg-rose-500/10 px-3 py-1 text-micro font-bold text-rose-300">
                      {withdrawCount} withdrew
                    </div>
                  )}
                </div>
              </div>

              {/* Visual funnel bars */}
              <div className="mt-5 space-y-1">
                {/* header row */}
                <div className="hidden sm:grid grid-cols-12 gap-2 px-1 text-micro font-black uppercase tracking-[0.12em] text-slate-600 mb-2">
                  <div className="col-span-4">Stage transition</div>
                  <div className="col-span-2 text-center">Rate</div>
                  <div className="col-span-2 text-center">Dropped</div>
                  <div className="col-span-2 text-center">Benchmark</div>
                  <div className="col-span-2 text-center">vs Prev</div>
                </div>
                {conversionRates.map((rate, idx) => {
                  const barW = rate.value != null ? Math.max(4, rate.value) : 0;
                  const color = rate.value == null ? 'bg-slate-700/50'
                    : rate.value >= rate.good ? 'bg-emerald-500/70'
                    : rate.value >= rate.ok   ? 'bg-amber-500/70'
                    : 'bg-rose-500/70';
                  const textColor = rate.value == null ? 'text-slate-600'
                    : rate.value >= rate.good ? 'text-emerald-300'
                    : rate.value >= rate.ok   ? 'text-amber-300'
                    : 'text-rose-400';
                  const deltaColor = rate.delta == null ? '' : rate.delta >= 0 ? 'text-emerald-400' : 'text-rose-400';
                  const isSelected = funnelStageIdx === idx;
                  return (
                    <button
                      key={rate.key}
                      type="button"
                      onClick={() => setFunnelStageIdx(isSelected ? -1 : idx)}
                      className={`w-full rounded-card border p-3.5 text-left transition-colors focus:outline-none ${isSelected ? 'border-cyan-400/50 bg-cyan-950/30' : 'border-white/10 bg-slate-900/40 hover:border-white/20'}`}
                    >
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <div className="col-span-12 sm:col-span-4">
                          <div className="text-micro font-bold uppercase tracking-[0.12em] text-slate-400">{rate.label}</div>
                          <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-900">
                            <div className={`h-full rounded-full transition-all ${color}`} style={{ width: `${barW}%` }} />
                          </div>
                        </div>
                        <div className="col-span-4 sm:col-span-2 text-center">
                          <div className={`text-xl font-black ${textColor}`}>{rate.display}</div>
                          <div className="text-micro text-slate-600">{rate.numerator}/{rate.denominator}</div>
                        </div>
                        <div className="col-span-4 sm:col-span-2 text-center">
                          {rate.dropCount > 0 ? (
                            <>
                              <div className="text-sm font-black text-rose-400">{rate.dropCount}</div>
                              <div className="text-micro text-slate-600">{rate.dropPct != null ? rate.dropPct + '% lost' : 'lost'}</div>
                            </>
                          ) : (
                            <div className="text-sm text-slate-700">—</div>
                          )}
                        </div>
                        <div className="col-span-4 sm:col-span-2 text-center">
                          <div className="flex flex-col items-center gap-0.5">
                            <div className="rounded-sm bg-slate-800 px-1.5 py-0.5 text-micro font-bold text-slate-400">p50 {rate.p50}%</div>
                            <div className="rounded-sm bg-slate-800 px-1.5 py-0.5 text-micro font-bold text-cyan-500/70">p75 {rate.p75}%</div>
                          </div>
                        </div>
                        <div className="col-span-12 sm:col-span-2 text-center">
                          {rate.delta != null ? (
                            <div className={`text-sm font-black ${deltaColor}`}>
                              {rate.delta >= 0 ? '+' : ''}{rate.delta}%
                              <div className="text-micro font-normal text-slate-600">vs prev</div>
                            </div>
                          ) : (
                            <div className="text-micro text-slate-700">no data</div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Stage detail drill-down */}
              {funnelStageIdx >= 0 && funnelStageIdx < conversionRates.length && (() => {
                const rate = conversionRates[funnelStageIdx];
                return (
                  <div className="mt-4 rounded-card border border-cyan-400/20 bg-cyan-950/20 p-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="text-meta font-black uppercase tracking-[0.12em] text-cyan-300">{rate.label} — Detail</div>
                      <div className="flex gap-3 text-micro">
                        <span className="text-slate-400">Passed: <span className="font-black text-white">{rate.numerator}</span></span>
                        <span className="text-slate-400">Dropped: <span className="font-black text-rose-300">{rate.dropCount}</span></span>
                        {rate.prevVal != null && <span className="text-slate-400">Prev: <span className="font-black text-cyan-300">{rate.prevVal}%</span></span>}
                      </div>
                    </div>
                    {(() => {
                      const stageOrder = ['applied','screening','interview','offer','hired'];
                      const fromIdx = [0,1,2,3][funnelStageIdx];
                      const toIdx   = [1,2,3,4][funnelStageIdx];
                      const srcMap = {};
                      filteredCandidates.forEach((c) => {
                        const src2 = (c.source || 'Unknown').trim() || 'Unknown';
                        if (!srcMap[src2]) srcMap[src2] = { reached: 0, advanced: 0 };
                        const statusIdx = stageOrder.indexOf(c.status);
                        if (statusIdx >= fromIdx) {
                          srcMap[src2].reached++;
                          if (statusIdx >= toIdx) srcMap[src2].advanced++;
                        }
                      });
                      const rows = Object.entries(srcMap)
                        .map(([s, d]) => ({ source: s, reached: d.reached, advanced: d.advanced, rate: d.reached > 0 ? Math.round((d.advanced / d.reached) * 100) : null }))
                        .filter((r) => r.reached >= 2)
                        .sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0))
                        .slice(0, 6);
                      if (rows.length === 0) return <div className="text-micro text-slate-600">Insufficient data for source breakdown.</div>;
                      return (
                        <div className="space-y-2">
                          <div className="text-micro uppercase tracking-[0.12em] text-slate-500 mb-1">Conversion by source channel</div>
                          {rows.map((row) => (
                            <div key={row.source} className="flex items-center gap-3">
                              <div className="w-32 truncate text-micro text-slate-400">{row.source}</div>
                              <div className="flex-1 h-1.5 rounded-full bg-slate-900">
                                <div
                                  className={`h-full rounded-full ${row.rate == null ? 'bg-slate-700' : row.rate >= rate.good ? 'bg-emerald-500/60' : row.rate >= rate.ok ? 'bg-amber-500/60' : 'bg-rose-500/60'}`}
                                  style={{ width: `${row.rate ?? 0}%` }}
                                />
                              </div>
                              <div className={`w-10 text-right text-micro font-bold ${row.rate == null ? 'text-slate-600' : row.rate >= rate.good ? 'text-emerald-400' : row.rate >= rate.ok ? 'text-amber-300' : 'text-rose-400'}`}>
                                {row.rate != null ? row.rate + '%' : '—'}
                              </div>
                              <div className="text-micro text-slate-600">{row.reached}</div>
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              })()}

              {/* Inflow / Withdraw / Disqualified row */}
              <div className="mt-4 grid gap-3 grid-cols-3 sm:grid-cols-5 border-t border-white/10 pt-4">
                <div className="col-span-1 rounded-card bg-slate-900/50 p-3 text-center">
                  <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Inflow</div>
                  <div className="mt-1 text-xl font-black text-cyan-300">{pipelineInflow}</div>
                  <div className="text-micro text-slate-600">new</div>
                </div>
                <div className="col-span-1 rounded-card bg-slate-900/50 p-3 text-center">
                  <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Hired</div>
                  <div className="mt-1 text-xl font-black text-emerald-300">{hiresInRange}</div>
                  <div className="text-micro text-slate-600">converted</div>
                </div>
                <div className="col-span-1 rounded-card bg-slate-900/50 p-3 text-center">
                  <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Yield</div>
                  <div className={`mt-1 text-xl font-black ${pipelineInflow > 0 && hiresInRange / pipelineInflow >= 0.1 ? 'text-emerald-300' : 'text-amber-300'}`}>
                    {pipelineInflow > 0 ? Math.round((hiresInRange / pipelineInflow) * 100) + '%' : '—'}
                  </div>
                  <div className="text-micro text-slate-600">end-to-end</div>
                </div>
                <div className="col-span-1 rounded-card bg-slate-900/50 p-3 text-center">
                  <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Withdrew</div>
                  <div className={`mt-1 text-xl font-black ${withdrawCount > 0 ? 'text-rose-400' : 'text-slate-700'}`}>{withdrawCount}</div>
                  <div className="text-micro text-slate-600">{withdrawRate != null ? withdrawRate + '%' : '—'}</div>
                </div>
                <div className="col-span-1 rounded-card bg-slate-900/50 p-3 text-center">
                  <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Disqualified</div>
                  <div className={`mt-1 text-xl font-black ${disqualCount > 0 ? 'text-amber-300' : 'text-slate-700'}`}>{disqualCount}</div>
                  <div className="text-micro text-slate-600">{disqualRate != null ? disqualRate + '%' : '—'}</div>
                </div>
              </div>
            </div>



            {/* ── DEI & Equity Monitoring (enhanced) ── */}
            <div className="rounded-shell border border-cyan-500/20 bg-slate-950/60 p-5 sm:p-6">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">DEI &amp; Equity Monitoring</div>
                  <div className="mt-0.5 text-meta text-slate-500">Source pass-rate parity &middot; self-ID breakdown &middot; EEOC export</div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {deiFlaggedCount > 0 && (<div className="rounded-full border border-amber-500/30 bg-amber-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-amber-300">{deiFlaggedCount} parity flag{deiFlaggedCount !== 1 ? 's' : ''}</div>)}
                  <div className="rounded-full border border-yellow-500/30 bg-yellow-500/10 px-3 py-1 text-micro font-bold uppercase tracking-[0.14em] text-yellow-300">Proxy metrics only</div>
                </div>
              </div>
              <div className="mt-4 flex gap-1 rounded-card border border-white/10 bg-slate-900/60 p-1">
                {[{ id: 'passrate', label: 'Pass-Rate Parity' }, { id: 'selfid', label: 'Self-ID × Source' }, { id: 'eeocexport', label: 'EEOC Export' }].map(tab => (
                  <button key={tab.id} type="button" onClick={() => setDeiTab(tab.id)} className={`flex-1 rounded-ctl px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] transition-colors ${deiTab === tab.id ? 'bg-cyan-500/20 text-cyan-200' : 'text-slate-500 hover:text-slate-300'}`}>{tab.label}</button>
                ))}
              </div>
              {deiTab === 'passrate' && (<div>

              {/* Source pass-rate parity table */}
              <div className="mt-5">
                <div className="text-meta uppercase tracking-[0.16em] text-slate-400 mb-3">Source-level Pass-Rate Parity</div>
                {deiFunnelBySource.length === 0 ? (
                  <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No screening data in this period.</div>
                ) : (
                  <div className="space-y-3">
                    {deiFunnelBySource.map((row) => {
                      const deviation = deiOverallPassRate != null && row.passRate != null
                        ? Math.round(row.passRate - deiOverallPassRate) : null;
                      const flagged = deviation != null && Math.abs(deviation) >= 15;
                      return (
                        <div key={row.source} className={`rounded-card border p-3 ${flagged ? 'border-amber-500/40 bg-amber-950/20' : 'border-white/10 bg-slate-900/40'}`}>
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2">
                              {flagged && (
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" className="h-3.5 w-3.5 text-amber-400 shrink-0"><path fillRule="evenodd" d="M6.701 2.25c.577-1 2.02-1 2.598 0l5.196 9a1.5 1.5 0 0 1-1.299 2.25H2.804a1.5 1.5 0 0 1-1.3-2.25l5.197-9ZM8 5a.75.75 0 0 1 .75.75v2.5a.75.75 0 1 1-1.5 0v-2.5A.75.75 0 0 1 8 5Zm0 6a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clipRule="evenodd"/></svg>
                              )}
                              <div className="text-xs font-bold text-white truncate max-w-[180px]">{row.source}</div>
                              <div className="text-meta text-slate-500">{row.screened} screened</div>
                            </div>
                            <div className="flex items-center gap-4">
                              <div className="text-center">
                                <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Pass rate</div>
                                <div className={`text-sm font-black ${row.passRate == null ? 'text-slate-600' : row.passRate >= 60 ? 'text-emerald-300' : row.passRate >= 40 ? 'text-amber-300' : 'text-rose-400'}`}>
                                  {row.passRate != null ? row.passRate + '%' : 'N/A'}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-micro uppercase tracking-[0.12em] text-slate-500">vs avg</div>
                                <div className={`text-sm font-black ${deviation == null ? 'text-slate-600' : deviation > 0 ? 'text-emerald-400' : deviation < 0 ? 'text-rose-400' : 'text-slate-400'}`}>
                                  {deviation != null ? (deviation >= 0 ? '+' : '') + deviation + 'pp' : '–'}
                                </div>
                              </div>
                              <div className="text-center">
                                <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Avg score</div>
                                <div className="text-sm font-black text-white">{row.avgScore != null ? row.avgScore : '–'}</div>
                              </div>
                            </div>
                          </div>
                          {/* Mini bar */}
                          <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-slate-800">
                            <div
                              className={`h-full rounded-full transition-all ${flagged ? 'bg-amber-400/70' : 'bg-cyan-500/60'}`}
                              style={{ width: (row.passRate || 0) + '%' }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Overall equity summary */}
              {deiOverallPassRate != null && (
                <div className="mt-4 grid gap-3 sm:grid-cols-3 border-t border-white/10 pt-4">
                  <div className="rounded-ctl bg-slate-900/60 p-3 text-center">
                    <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Overall pass rate</div>
                    <div className="mt-1 text-xl font-black text-white">{deiOverallPassRate}%</div>
                  </div>
                  <div className="rounded-ctl bg-slate-900/60 p-3 text-center">
                    <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Score std-dev</div>
                    <div className={`mt-1 text-xl font-black ${deiScoreStdDev == null ? 'text-slate-600' : deiScoreStdDev <= 15 ? 'text-emerald-300' : 'text-amber-300'}`}>
                      {deiScoreStdDev != null ? deiScoreStdDev : '–'}
                    </div>
                  </div>
                  <div className="rounded-ctl bg-slate-900/60 p-3 text-center">
                    <div className="text-micro uppercase tracking-[0.12em] text-slate-500">Flagged sources</div>
                    <div className={`mt-1 text-xl font-black ${deiFlaggedCount > 0 ? 'text-amber-300' : 'text-emerald-300'}`}>
                      {deiFlaggedCount}
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-6">
              {/* ── Score dispersion by source ── */}
              {deiFunnelBySource.length >= 2 && (() => {
                const srcScores = deiFunnelBySource.map((row) => row.avgScore).filter((v) => v != null);
                const allAvg = srcScores.length > 0 ? srcScores.reduce((s, v) => s + v, 0) / srcScores.length : null;
                const giniCoeff = (() => {
                  if (srcScores.length < 2) return null;
                  const sorted = [...srcScores].sort((a, b) => a - b);
                  const n = sorted.length;
                  const mean = sorted.reduce((s, v) => s + v, 0) / n;
                  if (mean === 0) return 0;
                  let num = 0;
                  for (let i = 0; i < n; i++) for (let j = 0; j < n; j++) num += Math.abs(sorted[i] - sorted[j]);
                  return +(num / (2 * n * n * mean)).toFixed(3);
                })();
                return (
                  <div className="mt-5 border-t border-white/10 pt-4">
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="text-meta uppercase tracking-[0.16em] text-slate-400">Score Dispersion by Source</div>
                      {giniCoeff != null && (
                        <div className={`rounded-full border px-3 py-1 text-micro font-black ${giniCoeff < 0.1 ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-300' : giniCoeff < 0.2 ? 'border-amber-500/30 bg-amber-500/10 text-amber-300' : 'border-rose-500/30 bg-rose-500/10 text-rose-300'}`}>
                          Gini {giniCoeff} {giniCoeff < 0.1 ? '· Equitable' : giniCoeff < 0.2 ? '· Moderate disparity' : '· High disparity'}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      {deiFunnelBySource.slice(0, 8).map((row) => {
                        const barW = row.avgScore != null ? Math.max(4, row.avgScore) : 0;
                        const devFromAvg = row.avgScore != null && allAvg != null ? Math.round(row.avgScore - allAvg) : null;
                        return (
                          <div key={'disp-' + row.source} className="flex items-center gap-3">
                            <div className="w-28 truncate text-micro text-slate-400 shrink-0">{row.source}</div>
                            <div className="flex-1 h-1.5 rounded-full bg-slate-900">
                              <div
                                className={`h-full rounded-full ${row.avgScore == null ? 'bg-slate-700' : row.avgScore >= 70 ? 'bg-emerald-500/60' : row.avgScore >= 50 ? 'bg-amber-500/60' : 'bg-rose-500/60'}`}
                                style={{ width: `${barW}%` }}
                              />
                            </div>
                            <div className={`w-8 text-right text-micro font-bold ${row.avgScore == null ? 'text-slate-600' : row.avgScore >= 70 ? 'text-emerald-400' : row.avgScore >= 50 ? 'text-amber-300' : 'text-rose-400'}`}>
                              {row.avgScore ?? '–'}
                            </div>
                            {devFromAvg != null && (
                              <div className={`w-12 text-right text-micro ${devFromAvg >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                                {devFromAvg >= 0 ? '+' : ''}{devFromAvg}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2 text-micro text-slate-600">Avg AI score per source channel · deviation from cohort mean · Gini = score inequality index (0 = perfect equity)</div>
                  </div>
                );
              })()}

              {/* ── Per-stage funnel retention by source ── */}
              {deiFunnelBySource.length >= 2 && (() => {
                const STAGE_ORD = ['applied','screening','interview','offer','hired'];
                const STAGE_LABELS = ['Applied','Screened','Interview','Offer','Hired'];
                const srcTotals = {};
                filteredCandidates.forEach((c) => {
                  const s = (c.source || 'Unknown').trim();
                  if (!srcTotals[s]) srcTotals[s] = Array(5).fill(0);
                  const idx2 = STAGE_ORD.indexOf(c.status);
                  if (idx2 >= 0) srcTotals[s][idx2]++;
                });
                const srcCumulative = {};
                Object.entries(srcTotals).forEach(([s, counts]) => {
                  const cum = Array(5).fill(0);
                  for (let i = 4; i >= 0; i--) {
                    cum[i] = counts[i] + (i < 4 ? cum[i + 1] : 0);
                  }
                  srcCumulative[s] = cum;
                });
                const topSrcs = deiFunnelBySource.slice(0, 5).map((r) => r.source);
                if (topSrcs.length === 0) return null;
                return (
                  <div className="mt-5 border-t border-white/10 pt-4">
                    <div className="text-meta uppercase tracking-[0.16em] text-slate-400 mb-3">Per-Stage Funnel Retention by Source</div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-micro">
                        <thead>
                          <tr>
                            <th className="text-left pb-2 pr-3 text-micro font-black uppercase tracking-[0.1em] text-slate-600">Source</th>
                            {STAGE_LABELS.map((lbl) => (
                              <th key={lbl} className="pb-2 px-2 text-center text-micro font-black uppercase tracking-[0.1em] text-slate-600">{lbl}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {topSrcs.map((src) => {
                            const cum = srcCumulative[src] || Array(5).fill(0);
                            const applied = cum[0] || 1;
                            return (
                              <tr key={'sfunnel-' + src} className="border-t border-white/5">
                                <td className="py-2 pr-3 max-w-[120px] truncate font-medium text-slate-300">{src}</td>
                                {cum.map((count, si) => {
                                  const pct = Math.round((count / applied) * 100);
                                  return (
                                    <td key={si} className="py-2 px-2 text-center">
                                      <div className={`font-black ${si === 0 ? 'text-cyan-300' : pct >= 20 ? 'text-emerald-400' : pct >= 5 ? 'text-amber-300' : 'text-rose-400'}`}>{count}</div>
                                      <div className="text-slate-700">{si > 0 ? pct + '%' : ''}</div>
                                    </td>
                                  );
                                })}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 text-micro text-slate-600">% = candidates remaining vs. total who applied from that source</div>
                  </div>
                );
              })()}

              <div className="mt-3 text-micro text-slate-600 border-t border-white/10 pt-3">
                &#9432; These are process-consistency proxy metrics based on source channel, AI score distributions, and pass rates only. No demographic data is collected or inferred.
              </div>
              </div>
            </div>
            )}
         {/* ── Self-ID by Source tab ── */}
            {deiTab === 'selfid' && (
              <div className="mt-5">
                {deiSelfIdBySource.length === 0 ? (
                  <div className="rounded-card border border-dashed border-white/10 px-4 py-8 text-center">
                    <div className="text-sm font-bold text-slate-500">No EEO self-ID data collected yet</div>
                    <div className="mt-2 text-meta text-slate-600 max-w-xs mx-auto">Use the EEO ID button on candidate cards to collect voluntary self-identification data.</div>
                  </div>
                ) : (
                  <>
                    <div className="mb-3 hidden sm:grid grid-cols-12 gap-1 px-4 text-micro font-black uppercase tracking-[0.12em] text-slate-600">
                      <div className="col-span-4">Source</div>
                      <div className="col-span-2 text-center">Candidates</div>
                      <div className="col-span-2 text-center">Self-ID %</div>
                      <div className="col-span-2 text-center">Top Gender</div>
                      <div className="col-span-2 text-center">Advance %</div>
                    </div>
                    <div className="space-y-2">
                      {deiSelfIdBySource.map(row => {
                        const covPct = row.total > 0 ? Math.round(row.eeocCount / row.total * 100) : 0;
                        const genderEntries = Object.entries(row.genders).sort((a, b) => b[1] - a[1]);
                        const topGenderKey = genderEntries[0]?.[0];
                        const GLBLS = { man: 'Man', woman: 'Woman', nonbinary: 'Non-binary', not_disclosed: '\u2014', self_describe: 'Other' };
                        return (
                          <div key={row.source} className="flex flex-wrap items-center gap-3 rounded-card border border-white/10 bg-slate-900/40 px-4 py-3 sm:grid sm:grid-cols-12 sm:gap-1">
                            <div className="col-span-4 min-w-0 flex-1">
                              <div className="truncate text-xs font-bold text-white">{row.source}</div>
                            </div>
                            <div className="col-span-2 text-center">
                              <div className="text-xs font-black text-slate-300">{row.total}</div>
                              <div className="text-micro text-slate-600">total</div>
                            </div>
                            <div className="col-span-2 text-center">
                              <div className={`text-xs font-black ${covPct >= 60 ? 'text-emerald-300' : covPct >= 30 ? 'text-amber-300' : 'text-rose-400'}`}>{covPct}%</div>
                              <div className="text-micro text-slate-700">{row.eeocCount} ID'd</div>
                            </div>
                            <div className="col-span-2 text-center">
                              <div className="text-meta font-bold text-slate-300">{topGenderKey ? (GLBLS[topGenderKey] || topGenderKey) : '\u2014'}</div>
                              {genderEntries.length > 1 && <div className="text-micro text-slate-700">+{genderEntries.length - 1} more</div>}
                            </div>
                            <div className="col-span-2 text-center">
                              <div className={`text-xs font-black ${row.passPct == null ? 'text-slate-600' : row.passPct >= 30 ? 'text-emerald-300' : row.passPct >= 15 ? 'text-amber-300' : 'text-rose-400'}`}>
                                {row.passPct != null ? row.passPct + '%' : '\u2014'}
                              </div>
                              <div className="text-micro text-slate-700">adv. rate</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-3 text-micro text-slate-600 border-t border-white/10 pt-3">
                      Self-ID is voluntary and confidential. Coverage = % of candidates per source who completed at least one self-ID field. Advance rate = % who reached interview, offer, or hired stage.
                    </div>
                  </>
                )}
              </div>

            )}
            {/* ── EEOC Export tab ── */}
            {deiTab === 'eeocexport' && (
              <div className="mt-5">
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-card border border-white/10 bg-slate-900/40 p-4 text-center">
                    <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Response Rate</div>
                    <div className="mt-2 text-3xl font-black text-white">{eeocResponseRate}%</div>
                    <div className="mt-1 text-meta text-slate-600">{eeocTotal} of {filteredCandidates.length} candidates</div>
                  </div>
                  <div className="rounded-card border border-white/10 bg-slate-900/40 p-4 text-center">
                    <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Adverse Impact Flags</div>
                    <div className={`mt-2 text-3xl font-black ${eeocFlaggedGroups > 0 ? 'text-rose-400' : 'text-emerald-300'}`}>{eeocFlaggedGroups}</div>
                    <div className="mt-1 text-meta text-slate-600">race groups below 80% rule</div>
                  </div>
                  <div className="rounded-card border border-white/10 bg-slate-900/40 p-4 text-center">
                    <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Veteran Disclosures</div>
                    <div className="mt-2 text-3xl font-black text-white">{eeocCandidates.filter(c => (c.eeocSelfId?.veteran || '').toLowerCase() === 'yes').length}</div>
                    <div className="mt-1 text-meta text-slate-600">positive disclosures</div>
                  </div>
                  <div className="rounded-card border border-white/10 bg-slate-900/40 p-4 text-center">
                    <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Disability Disclosures</div>
                    <div className="mt-2 text-3xl font-black text-white">{eeocCandidates.filter(c => c.eeocSelfId?.disability === 'yes').length}</div>
                    <div className="mt-1 text-meta text-slate-600">positive disclosures</div>
                  </div>
                </div>

                <div className="mt-5 grid gap-4 sm:grid-cols-2 border-t border-white/10 pt-5">
                  <div className="rounded-card border border-rose-400/20 bg-rose-950/20 p-4">
                    <div className="text-meta font-black uppercase tracking-[0.12em] text-rose-300">EEO-1 Aggregate Matrix</div>
                    <div className="mt-1.5 text-micro text-slate-500">Gender &times; Race/Ethnicity counts &mdash; no individual names. For federal EEO-1 filing.</div>
                    <button
                      type="button"
                      className="mt-3 rounded-ctl border border-rose-400/30 bg-rose-500/10 px-4 py-2 text-meta font-black uppercase tracking-[0.12em] text-rose-200 transition-colors hover:bg-rose-500/15"
                      onClick={() => {
                        const genders = ['man', 'woman', 'nonbinary', 'self_describe', 'not_disclosed'];
                        const races = ['hispanic', 'white', 'black', 'native', 'asian', 'pacific', 'two_or_more', 'not_disclosed'];
                        const GL = { man: 'Man', woman: 'Woman', nonbinary: 'Non-binary', self_describe: 'Self-describe', not_disclosed: 'Not answered' };
                        const RL = { hispanic: 'Hispanic/Latino', white: 'White', black: 'Black/AA', native: 'Am. Indian', asian: 'Asian', pacific: 'Pacific Isl.', two_or_more: 'Two+ Races', not_disclosed: 'Not answered' };
                        const header = ['Gender / Race', ...races.map(r => RL[r] || r), 'Row Total'];
                        const rows = genders.map(g => {
                          const cells = races.map(r => eeocCandidates.filter(c => (c.eeocSelfId?.gender || 'not_disclosed') === g && (c.eeocSelfId?.race || 'not_disclosed') === r).length);
                          return [GL[g] || g, ...cells, cells.reduce((a, b) => a + b, 0)];
                        });
                        rows.push(['Col Total', ...races.map(r => eeocCandidates.filter(c => (c.eeocSelfId?.race || 'not_disclosed') === r).length), eeocTotal]);
                        const csv = [header, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
                        const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv); a.download = 'eeo1_aggregate_' + new Date().toISOString().slice(0, 10) + '.csv'; a.click();
                      }}
                    >
                      Download EEO-1 CSV
                    </button>
                  </div>
                  <div className="rounded-card border border-sky-400/20 bg-sky-950/20 p-4">
                    <div className="text-meta font-black uppercase tracking-[0.12em] text-sky-300">Anonymized Records Export</div>
                    <div className="mt-1.5 text-micro text-slate-500">Candidate ID (no names), source, stage, all self-ID fields. For internal audit and compliance review.</div>
                    <button
                      type="button"
                      className="mt-3 rounded-ctl border border-sky-400/30 bg-sky-500/10 px-4 py-2 text-meta font-black uppercase tracking-[0.12em] text-sky-200 transition-colors hover:bg-sky-500/15"
                      onClick={() => {
                        const GL = { man: 'Man', woman: 'Woman', nonbinary: 'Non-binary', self_describe: 'Self-describe', not_disclosed: 'Not disclosed' };
                        const RL = { hispanic: 'Hispanic / Latino', white: 'White', black: 'Black / African American', native: 'Am. Indian / Alaska Native', asian: 'Asian', pacific: 'Nat. Hawaiian / Pacific Isl.', two_or_more: 'Two or More Races', not_disclosed: 'Not disclosed' };
                        const header = ['Candidate ID', 'Source', 'Stage', 'Hired', 'Gender', 'Race / Ethnicity', 'Veteran', 'Disability'];
                        const rows = eeocCandidates.map((c, i) => [
                          'CAND-' + String(i + 1).padStart(4, '0'),
                          c.source || 'Unknown',
                          c.status || '',
                          c.status === 'hired' ? 'Yes' : 'No',
                          GL[c.eeocSelfId?.gender || ''] || (c.eeocSelfId?.gender || 'Not disclosed'),
                          RL[c.eeocSelfId?.race || ''] || (c.eeocSelfId?.race || 'Not disclosed'),
                          c.eeocSelfId?.veteran || 'Not disclosed',
                          c.eeocSelfId?.disability || 'Not disclosed',
                        ]);
                        const csv = [header, ...rows].map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
                        const a = document.createElement('a'); a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv); a.download = 'eeoc_records_' + new Date().toISOString().slice(0, 10) + '.csv'; a.click();
                      }}
                    >
                      Download Anonymized CSV
                    </button>
                  </div>
                </div>

                <div className="mt-4 rounded-card border border-white/[0.06] bg-slate-950/40 px-4 py-3 text-micro leading-5 text-slate-600">
                  &#9432; All EEO self-identification is voluntary and confidential per EEOC guidelines (29 CFR Part 1602). Individual records are anonymized — no candidate names are exported. Aggregate matrices are for federal EEO-1 reporting only.
                </div>
              </div>

            )}
            </div>



            {/* ── AGENCY/STAFFING SPECIFICS ── */}
            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Agency Performance</div>
                  <div className="mt-1 text-meta text-slate-500">Fill rate · job order velocity · recruiter workload</div>
                </div>
              </div>

              {/* Top-level agency KPIs */}
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                  <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Fill Rate</div>
                  <div className={`mt-1.5 text-3xl font-black ${fillRate == null ? 'text-slate-600' : fillRate >= 60 ? 'text-emerald-300' : fillRate >= 35 ? 'text-amber-300' : 'text-rose-400'}`}>
                    {fillRate != null ? fillRate + '%' : 'N/A'}
                  </div>
                  <div className="mt-1 text-meta text-slate-500">{hiresInRange} hired / {openJobCount} open jobs</div>
                </div>
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                  <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Job Order Velocity</div>
                  <div className={`mt-1.5 text-3xl font-black ${jobOrderVelocity == null ? 'text-slate-600' : 'text-cyan-300'}`}>
                    {jobOrderVelocity != null ? jobOrderVelocity.toFixed(1) + '/wk' : 'N/A'}
                  </div>
                  <div className="mt-1 text-meta text-slate-500">{filteredJobs.length} jobs in period</div>
                </div>
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                  <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Avg Cands / Recruiter</div>
                  <div className={`mt-1.5 text-3xl font-black ${avgCandsPerRecruiter == null ? 'text-slate-600' : avgCandsPerRecruiter <= 20 ? 'text-emerald-300' : avgCandsPerRecruiter <= 35 ? 'text-amber-300' : 'text-rose-400'}`}>
                    {avgCandsPerRecruiter != null ? avgCandsPerRecruiter : 'N/A'}
                  </div>
                  <div className="mt-1 text-meta text-slate-500">{agencyActiveRecruiters} active recruiters</div>
                </div>
                <div className="rounded-card border border-white/10 bg-slate-900/50 p-4">
                  <div className="text-micro uppercase tracking-[0.14em] text-slate-500">Closed Jobs</div>
                  <div className="mt-1.5 text-3xl font-black text-white">{closedJobCount}</div>
                  <div className="mt-1 text-meta text-slate-500">{jobCloseRate != null ? jobCloseRate + '% closure rate' : '—'}</div>
                </div>
              </div>

              {/* Recruiter Leaderboard */}
              <div className="mt-5">
                <div className="flex items-center justify-between gap-3 mb-3">
                  <div className="text-meta uppercase tracking-[0.16em] text-slate-400">Recruiter Leaderboard</div>
                  <button onClick={() => exportRecruiterLeaderboardCsv(recruiterLeaderboard)} className="rounded-md border border-cyan-500/30 bg-cyan-500/10 px-2 py-0.5 text-micro font-bold uppercase tracking-[0.1em] text-cyan-400 hover:bg-cyan-500/20 transition-colors" title="Export recruiter leaderboard CSV">↓ CSV</button>
                </div>
                {recruiterLeaderboard.length === 0 ? (
                  <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No recruiter data — set candidate recruiterId to track.</div>
                ) : (
                  <div className="space-y-2">
                    {recruiterLeaderboard.map((rec, ri) => (
                      <div key={rec.id} className="flex items-center gap-3 rounded-card border border-white/10 bg-slate-900/40 p-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-meta font-black shrink-0 ${ri === 0 ? 'bg-amber-400 text-slate-900' : ri === 1 ? 'bg-slate-400 text-slate-900' : ri === 2 ? 'bg-amber-700 text-white' : 'bg-slate-800 text-slate-400'}`}>
                          {ri + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs font-bold text-white truncate">{rec.name}</div>
                          <div className="mt-0.5 h-1.5 overflow-hidden rounded-full bg-slate-800">
                            <div className="h-full rounded-full bg-cyan-500/60 transition-all" style={{ width: (recruiterLeaderboard[0].hires > 0 ? Math.round((rec.hires / recruiterLeaderboard[0].hires) * 100) : 0) + '%' }} />
                          </div>
                        </div>
                        <div className="text-right shrink-0">
                          <div className="text-xs font-black text-white">{rec.hires} <span className="text-slate-500 font-normal">hired</span></div>
                          <div className="text-micro text-slate-500">{rec.candidates} cands · {rec.fillRate != null ? rec.fillRate + '% fill' : '—'}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Job title breakdown */}
              <div className="mt-5 border-t border-white/10 pt-5">
                <div className="text-meta uppercase tracking-[0.16em] text-slate-400 mb-3">Top Job Titles by Candidate Volume</div>
                {jobTitleBreakdown.length === 0 ? (
                  <div className="text-meta text-slate-500">No job title data in this period.</div>
                ) : (
                  <div className="space-y-2">
                    {jobTitleBreakdown.slice(0, 6).map((item) => (
                      <div key={item.title} className="flex items-center gap-3">
                        <div className="flex-1 min-w-0 text-xs font-bold text-white truncate">{item.title}</div>
                        <div className="w-28 h-1.5 overflow-hidden rounded-full bg-slate-800">
                          <div className="h-full rounded-full bg-violet-500/60 transition-all" style={{ width: (jobTitleBreakdown[0].count > 0 ? Math.round((item.count / jobTitleBreakdown[0].count) * 100) : 0) + '%' }} />
                        </div>
                        <div className="text-meta font-black text-slate-300 w-8 text-right">{item.count}</div>
                        <div className="text-meta text-emerald-400 w-10 text-right">{item.hireRate != null ? item.hireRate + '%' : '—'}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>



            {/* ── DRILL-DOWN SEGMENTATION: by status, source, role ── */}
            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-cyan-300">Pipeline Segmentation</div>
                  <div className="mt-1 text-meta text-slate-500">Multi-axis segmentation — identify concentrations and drop-off patterns</div>
                </div>
                <div className="flex items-center gap-2">
                  {['status', 'source', 'seniority'].map((seg) => (
                    <button
                      key={seg}
                      type="button"
                      onClick={() => setSegmentAxis(seg)}
                      className={`rounded-full border px-3 py-1 text-micro font-bold uppercase tracking-[0.12em] transition-colors ${segmentAxis === seg ? 'border-cyan-400 bg-cyan-400/15 text-cyan-300' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'}`}
                    >
                      {seg}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {segmentData.map((seg) => (
                  <div key={seg.key} className="flex items-center gap-3 rounded-card border border-white/10 bg-slate-900/40 px-3 py-2.5">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ background: seg.color }} />
                    <div className="flex-1 min-w-0 text-xs font-bold text-white truncate">{seg.label}</div>
                    <div className="flex-1 h-2 overflow-hidden rounded-full bg-slate-800">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: seg.barWidth + '%', background: seg.color + '99' }} />
                    </div>
                    <div className="text-xs font-black text-white w-8 text-right">{seg.count}</div>
                    <div className="text-micro text-slate-500 w-10 text-right">{seg.pct}%</div>
                    <div className="text-micro font-bold text-emerald-400 w-10 text-right">
                      {seg.hireRate != null ? seg.hireRate + '%&#8197;&#x2713;' : ''}
                    </div>
                  </div>
                ))}
              </div>

              {/* Drop-off analysis */}
              {dropOffAnalysis.length > 0 && (
                <div className="mt-5 border-t border-white/10 pt-5">
                  <div className="text-meta uppercase tracking-[0.16em] text-slate-400 mb-3">Biggest Drop-off Points</div>
                  <div className="space-y-2">
                    {dropOffAnalysis.map((item) => (
                      <div key={item.fromId} className="flex items-center gap-3 rounded-card border border-white/10 bg-slate-900/40 px-3 py-2.5">
                        <div className="flex-1 text-xs font-bold text-white">{item.fromLabel} → {item.toLabel}</div>
                        <div className={`text-sm font-black ${item.convRate >= 60 ? 'text-emerald-300' : item.convRate >= 30 ? 'text-amber-300' : 'text-rose-400'}`}>
                          {item.convRate}%
                        </div>
                        <div className="text-micro text-slate-500">{item.passed} / {item.total}</div>
                        <div className={`text-micro font-bold ${item.lostCount > 0 ? 'text-rose-400' : 'text-slate-600'}`}>
                          {item.lostCount > 0 ? '−' + item.lostCount + ' lost' : ''}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>



            {/* ── INDUSTRY BENCHMARKS: p50 / p75 comparison ── */}
            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-violet-300">Industry Benchmarks</div>
                  <div className="mt-1 text-meta text-slate-500">Your metrics vs. talent-market p50 &amp; p75 · Sources: SHRM, LinkedIn Talent, Gem.com</div>
                </div>
                <div className="rounded-full border border-violet-400/20 bg-violet-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-violet-300">Live comparison</div>
              </div>

              <div className="mt-5 space-y-3">
                {benchmarkItems.map((bm) => {
                  const hasValue = bm.yourValue != null;
                  const atP75    = hasValue && (bm.lowerIsBetter ? bm.yourValue <= bm.p50 : bm.yourValue >= bm.p75);
                  const atP50    = hasValue && !atP75 && (bm.lowerIsBetter ? bm.yourValue <= bm.p75 : bm.yourValue >= bm.p50);
                  const belowP50 = hasValue && !atP75 && !atP50;
                  const barPct   = hasValue
                    ? bm.lowerIsBetter
                      ? Math.min(100, Math.round(Math.max(0, (1 - (bm.yourValue - bm.p50) / (bm.p75 * 1.5)) * 100)))
                      : Math.min(100, Math.round((bm.yourValue / (bm.p75 * 1.2)) * 100))
                    : 0;
                  const statusColor = atP75 ? '#22c55e' : atP50 ? '#f59e0b' : '#f43f5e';
                  const statusLabel = atP75 ? 'Top quartile' : atP50 ? 'Median' : 'Below median';
                  return (
                    <div key={bm.label} className="rounded-card border border-white/10 bg-slate-900/40 px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-meta font-bold text-slate-300">{bm.label}</div>
                        <div className="flex items-center gap-3 shrink-0">
                          <div className="text-micro text-slate-500">p50: <span className="text-slate-400 font-semibold">{bm.unit === '$' ? '$' + bm.p50.toLocaleString() : bm.p50 + bm.unit}</span></div>
                          <div className="text-micro text-slate-500">p75: <span className="text-slate-400 font-semibold">{bm.unit === '$' ? '$' + bm.p75.toLocaleString() : bm.p75 + bm.unit}</span></div>
                          <div className="text-sm font-black" style={{ color: hasValue ? statusColor : '#475569' }}>
                            {hasValue ? (bm.unit === '$' ? '$' + bm.yourValue.toLocaleString() : bm.yourValue + bm.unit) : 'No data'}
                          </div>
                        </div>
                      </div>
                      <div className="relative mt-2 h-2 overflow-hidden rounded-full bg-slate-800">
                        {/* p50 marker */}
                        <div className="absolute top-0 bottom-0 w-px bg-slate-500/60" style={{ left: (bm.lowerIsBetter ? 60 : Math.round((bm.p50 / (bm.p75 * 1.2)) * 100)) + '%' }} />
                        {/* p75 marker */}
                        <div className="absolute top-0 bottom-0 w-px bg-emerald-500/60" style={{ left: (bm.lowerIsBetter ? 80 : Math.round((bm.p75 / (bm.p75 * 1.2)) * 100)) + '%' }} />
                        {/* your value bar */}
                        {hasValue && (
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: barPct + '%', background: statusColor + 'aa' }} />
                        )}
                      </div>
                      {hasValue && (
                        <div className="mt-1.5 flex items-center justify-between">
                          <div className="text-micro font-bold" style={{ color: statusColor }}>{statusLabel}</div>
                          {bm.lowerIsBetter && belowP50 && (
                            <div className="text-micro text-rose-400">
                              +{bm.unit === '$' ? '$' + (bm.yourValue - bm.p50).toLocaleString() : (bm.yourValue - bm.p50) + bm.unit} above p50
                            </div>
                          )}
                          {!bm.lowerIsBetter && belowP50 && bm.p50 > 0 && (
                            <div className="text-micro text-rose-400">
                              {bm.p50 - bm.yourValue}{bm.unit} below p50
                            </div>
                          )}
                          {(atP75 || atP50) && !bm.lowerIsBetter && (
                            <div className="text-micro text-emerald-400">
                              {bm.yourValue - bm.p50 >= 0 ? '+' : ''}{bm.yourValue - bm.p50}{bm.unit} vs p50
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 flex items-center gap-4 border-t border-white/10 pt-4">
                <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-slate-500/60 rounded-full" /><span className="text-micro text-slate-500">p50 (median)</span></div>
                <div className="flex items-center gap-1.5"><div className="w-3 h-0.5 bg-emerald-500/60 rounded-full" /><span className="text-micro text-slate-500">p75 (top quartile)</span></div>
                <div className="ml-auto text-micro text-slate-600">Updated Q1 2026</div>
              </div>
            </div>



            {/* ── Cross-Filter Pivot Table ── */}
            {(() => {
              const PIVOT_METRICS = [
                { id: 'count',        label: 'Candidate Count',  fmt: v => String(v),          clr: () => 'text-slate-200' },
                { id: 'hired',        label: 'Hires',             fmt: v => String(v),          clr: () => 'text-emerald-300' },
                { id: 'hire_rate',    label: 'Hire Rate %',        fmt: v => v + '%',            clr: v => v >= 20 ? 'text-emerald-300' : v >= 8 ? 'text-amber-300' : 'text-rose-400' },
                { id: 'advance_rate', label: 'Advance Rate %',     fmt: v => v + '%',            clr: v => v >= 50 ? 'text-emerald-300' : v >= 25 ? 'text-amber-300' : 'text-rose-400' },
                { id: 'avg_score',    label: 'Avg AI Score',       fmt: v => v.toFixed(1),       clr: v => v >= 7 ? 'text-emerald-300' : v >= 5 ? 'text-amber-300' : 'text-rose-400' },
                { id: 'tth_days',     label: 'Avg TTH (days)',     fmt: v => v.toFixed(0) + 'd', clr: v => v <= 21 ? 'text-emerald-300' : v <= 42 ? 'text-amber-300' : 'text-rose-400' },
                { id: 'qoh90',        label: 'QoH 90d Score',      fmt: v => v.toFixed(1),       clr: v => v >= 7 ? 'text-emerald-300' : v >= 5 ? 'text-amber-300' : 'text-rose-400' },
                { id: 'declines',     label: 'Offer Declines',     fmt: v => String(v),          clr: v => v === 0 ? 'text-emerald-300' : v <= 2 ? 'text-amber-300' : 'text-rose-400' },
              ];
            
              const PIVOT_DIMS = [
                { id: 'source',    label: 'Source',    fn: c => c.source || 'Unknown' },
                { id: 'recruiter', label: 'Recruiter', fn: c => agencyMembers.find(m => m.id === (c.recruiterId || recruiterUser?.uid))?.name || 'You' },
                { id: 'status',    label: 'Stage',     fn: c => { const s = c.status || 'unknown'; return s.charAt(0).toUpperCase() + s.slice(1); } },
                { id: 'role',      label: 'Role',      fn: c => (c.matchedJobTitle || c.role || 'Unknown').split(' ').slice(0, 4).join(' ') },
                { id: 'company',   label: 'Company',   fn: c => c.company || 'Unknown' },
                { id: 'month',     label: 'Month',     fn: c => { const d = new Date(c.createdAtIso || c.submittedAt || 0); return isNaN(d.getTime()) ? 'Unknown' : d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0'); } },
              ];
            
              const mDef = PIVOT_METRICS.find(m => m.id === pivotMetric) || PIVOT_METRICS[0];
              const rDef = PIVOT_DIMS.find(d => d.id === pivotRowDim) || PIVOT_DIMS[0];
              const cDef = (pivotColDim !== 'none' && pivotColDim !== pivotRowDim) ? PIVOT_DIMS.find(d => d.id === pivotColDim) : null;
            
              const computeMetric = (arr, mid) => {
                if (!arr.length) return null;
                if (mid === 'count') return arr.length;
                if (mid === 'hired') return arr.filter(c => c.status === 'hired').length;
                if (mid === 'hire_rate') { const h = arr.filter(c => c.status === 'hired').length; return Math.round(h / arr.length * 100); }
                if (mid === 'advance_rate') { const adv = arr.filter(c => ['interview', 'offer', 'hired'].includes(c.status || '')).length; return Math.round(adv / arr.length * 100); }
                if (mid === 'avg_score') { const s = arr.map(c => Number(c.rating)).filter(v => Number.isFinite(v) && v > 0); return s.length ? s.reduce((a, b) => a + b, 0) / s.length : null; }
                if (mid === 'tth_days') {
                  const days = [];
                  arr.forEach(c => { const a = candidateStageTimeMs(c, 'applied'); const h = candidateStageTimeMs(c, 'hired'); if (Number.isFinite(a) && Number.isFinite(h) && h > a) days.push((h - a) / 86400000); });
                  return days.length ? days.reduce((a, b) => a + b, 0) / days.length : null;
                }
                if (mid === 'qoh90') { const s = arr.map(c => c.qohScore90).filter(v => v != null); return s.length ? s.reduce((a, b) => a + b, 0) / s.length : null; }
                if (mid === 'declines') return arr.filter(c => c.offerDeclinedAt).length;
                return arr.length;
              };
            
              // Row values: top 10 by count
              const _rowCounts = {};
              filteredCandidates.forEach(c => { const v = rDef.fn(c); _rowCounts[v] = (_rowCounts[v] || 0) + 1; });
              const rowVals = Object.entries(_rowCounts).sort((a, b) => b[1] - a[1]).slice(0, 10).map(([v]) => v);
              const totalRowVals = Object.keys(_rowCounts).length;
            
              // Col values: top 6 by count
              const _colCounts = cDef ? (() => { const cc = {}; filteredCandidates.forEach(c => { const v = cDef.fn(c); cc[v] = (cc[v] || 0) + 1; }); return cc; })() : {};
              const colVals = cDef ? Object.entries(_colCounts).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([v]) => v) : [];
              const totalColVals = Object.keys(_colCounts).length;
            
              // Build matrix
              const matrix = rowVals.map(rv => {
                const row = filteredCandidates.filter(c => rDef.fn(c) === rv);
                const rowTotal = computeMetric(row, pivotMetric);
                const cells = cDef ? colVals.map(cv => {
                  const sub = row.filter(c => cDef.fn(c) === cv);
                  return { val: computeMetric(sub, pivotMetric), count: sub.length };
                }) : [];
                return { rv, rowTotal, rowCount: row.length, cells };
              });
            
              const colTotals = cDef ? colVals.map(cv => computeMetric(filteredCandidates.filter(c => cDef.fn(c) === cv), pivotMetric)) : [];
              const grandTotal = computeMetric(filteredCandidates, pivotMetric);
            
              // Heatmap: relative intensity
              const allCellVals = (cDef ? matrix.flatMap(r => r.cells.map(c => c.val)) : matrix.map(r => r.rowTotal)).filter(v => v != null && v > 0);
              const maxCellVal = Math.max(1, ...allCellVals);
              const heatCls = v => {
                if (v == null || v === 0) return '';
                const p = v / maxCellVal;
                return p >= 0.75 ? 'bg-sky-500/20' : p >= 0.5 ? 'bg-sky-500/15' : p >= 0.25 ? 'bg-sky-500/10' : 'bg-sky-500/5';
              };
            
              const fmtCell = val => val == null ? '\u2014' : mDef.fmt(val);
              const clrCell = val => val == null ? 'text-slate-600' : mDef.clr(val);
            
              // CSV export
              const exportPivot = () => {
                const hdr = cDef ? [rDef.label, ...colVals, 'Total'] : [rDef.label, mDef.label];
                const rows = matrix.map(r => cDef ? [r.rv, ...r.cells.map(c => c.val ?? ''), r.rowTotal ?? ''] : [r.rv, r.rowTotal ?? '']);
                if (cDef) rows.push(['TOTAL', ...colTotals.map(v => v ?? ''), grandTotal ?? '']);
                const csv = [hdr, ...rows].map(r => r.map(v => '"' + String(v == null ? '' : v).replace(/"/g, '""') + '"').join(',')).join('\n');
                const a = document.createElement('a');
                a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
                a.download = 'pivot_' + pivotMetric + '_' + pivotRowDim + (cDef ? '_x_' + pivotColDim : '') + '.csv';
                a.click();
              };
            
              const simpleMax = Math.max(1, ...matrix.map(r => r.rowTotal ?? 0));
            
              return (
                <div className="rounded-shell border border-sky-500/20 bg-sky-950/20 p-5 sm:p-6">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <div className="text-xs font-black uppercase tracking-[0.24em] text-sky-300">Cross-Filter Pivot</div>
                      <div className="mt-0.5 text-meta text-slate-500">Any metric &times; any dimension on-demand &mdash; {filteredCandidates.length} candidates in scope</div>
                    </div>
                    <button
                      type="button"
                      onClick={exportPivot}
                      className="rounded-ctl border border-sky-400/30 bg-sky-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-sky-300 transition-colors hover:border-sky-300/40 hover:bg-sky-500/15"
                    >
                      Export CSV
                    </button>
                  </div>
            
                  {/* Controls */}
                  <div className="mt-4 grid gap-3 sm:grid-cols-3">
                    <div>
                      <label className="mb-1.5 block text-micro font-black uppercase tracking-[0.14em] text-slate-500">Metric</label>
                      <select
                        value={pivotMetric}
                        onChange={e => setPivotMetric(e.target.value)}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-xs text-slate-200 focus:border-sky-400/40 focus:outline-none"
                      >
                        {PIVOT_METRICS.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-micro font-black uppercase tracking-[0.14em] text-slate-500">Rows</label>
                      <select
                        value={pivotRowDim}
                        onChange={e => setPivotRowDim(e.target.value)}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-xs text-slate-200 focus:border-sky-400/40 focus:outline-none"
                      >
                        {PIVOT_DIMS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-micro font-black uppercase tracking-[0.14em] text-slate-500">
                        Column Split <span className="font-normal normal-case tracking-normal text-slate-600">(optional)</span>
                      </label>
                      <select
                        value={pivotColDim}
                        onChange={e => setPivotColDim(e.target.value)}
                        className="w-full rounded-ctl border border-white/10 bg-slate-900 px-3 py-2 text-xs text-slate-200 focus:border-sky-400/40 focus:outline-none"
                      >
                        <option value="none">None \u2014 single column</option>
                        {PIVOT_DIMS.filter(d => d.id !== pivotRowDim).map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                      </select>
                    </div>
                  </div>
            
                  {/* Active selection badge */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <div className="rounded-full border border-sky-400/30 bg-sky-500/10 px-2.5 py-0.5 text-micro font-bold text-sky-300">{mDef.label}</div>
                    <div className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-micro text-slate-400">by {rDef.label}</div>
                    {cDef && <div className="rounded-full border border-white/10 bg-white/5 px-2.5 py-0.5 text-micro text-slate-400">&times; {cDef.label}</div>}
                  </div>
            
                  {/* Table */}
                  <div className="mt-5">
                    {rowVals.length === 0 ? (
                      <div className="rounded-card border border-dashed border-white/10 px-4 py-6 text-center text-sm text-slate-500">No data for current filters.</div>
                    ) : cDef && colVals.length > 0 ? (
                      /* Cross-tab matrix */
                      <div className="overflow-x-auto -mx-1 px-1">
                        <table className="w-full min-w-[560px] border-collapse">
                          <thead>
                            <tr>
                              <th className="border-b border-white/10 pb-2 pr-3 text-left text-micro font-black uppercase tracking-[0.12em] text-slate-500 min-w-[130px]">{rDef.label}</th>
                              {colVals.map(cv => (
                                <th key={cv} className="border-b border-white/10 pb-2 px-2 text-center min-w-[70px]">
                                  <div className="text-micro font-black uppercase tracking-[0.1em] text-slate-500 truncate max-w-[80px] mx-auto">{cv}</div>
                                </th>
                              ))}
                              <th className="border-b border-white/10 pb-2 px-2 text-center text-micro font-black uppercase tracking-[0.12em] text-slate-400 min-w-[60px]">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {matrix.map(row => (
                              <tr key={row.rv} className="border-b border-white/[0.04] hover:bg-white/[0.02] transition-colors">
                                <td className="py-2.5 pr-3">
                                  <div className="truncate text-meta font-bold text-slate-300 max-w-[150px]">{row.rv}</div>
                                  <div className="text-micro text-slate-700">n={row.rowCount}</div>
                                </td>
                                {row.cells.map((cell, ci) => (
                                  <td key={ci} className={`py-2.5 px-2 text-center rounded transition-colors ${heatCls(cell.val)}`}>
                                    <div className={`text-xs font-black ${clrCell(cell.val)}`}>{fmtCell(cell.val)}</div>
                                    {cell.count > 0 && <div className="text-micro text-slate-700">n={cell.count}</div>}
                                  </td>
                                ))}
                                <td className="py-2.5 px-2 text-center">
                                  <div className={`text-xs font-black ${clrCell(row.rowTotal)}`}>{fmtCell(row.rowTotal)}</div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="border-t border-white/10">
                              <td className="pt-2.5 pr-3 text-micro font-black uppercase tracking-[0.12em] text-slate-500">Total</td>
                              {colTotals.map((v, ci) => (
                                <td key={ci} className="pt-2.5 px-2 text-center">
                                  <div className={`text-xs font-black ${clrCell(v)}`}>{fmtCell(v)}</div>
                                </td>
                              ))}
                              <td className="pt-2.5 px-2 text-center">
                                <div className={`text-xs font-black ${clrCell(grandTotal)}`}>{fmtCell(grandTotal)}</div>
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>
                    ) : (
                      /* Simple sorted bar list */
                      <div className="space-y-2">
                        {matrix.map(row => (
                          <div key={row.rv} className="flex items-center gap-3 rounded-card border border-white/10 bg-slate-900/40 px-4 py-2.5">
                            <div className="w-36 shrink-0 min-w-0">
                              <div className="truncate text-xs font-bold text-slate-300">{row.rv}</div>
                              <div className="text-micro text-slate-700">n={row.rowCount}</div>
                            </div>
                            <div className="flex-1 h-2 overflow-hidden rounded-full bg-slate-800/80">
                              <div
                                className="h-full rounded-full bg-sky-500/60 transition-all duration-500"
                                style={{ width: row.rowTotal != null ? Math.round((row.rowTotal / simpleMax) * 100) + '%' : '0%' }}
                              />
                            </div>
                            <div className={`w-20 shrink-0 text-right text-sm font-black ${clrCell(row.rowTotal)}`}>
                              {fmtCell(row.rowTotal)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
            
                  {/* Footer */}
                  <div className="mt-4 flex flex-wrap items-center justify-between gap-2 border-t border-white/10 pt-3 text-micro text-slate-600">
                    <span>
                      Showing top {rowVals.length} {rDef.label.toLowerCase()} values
                      {cDef ? ' \u00d7 top ' + colVals.length + ' ' + cDef.label.toLowerCase() + ' values' : ''}
                      {(totalRowVals > 10 || (cDef && totalColVals > 6)) ? ' \u2014 export CSV for full dataset' : ''}
                    </span>
                    <span className="text-slate-700">Heatmap = relative intensity per column</span>
                  </div>
                </div>
              );
            })()}
            

            {/* ── EEOC / EEO-1 COMPLIANCE REPORTING ── */}
            <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.24em] text-rose-300">EEO Compliance</div>
                  <div className="mt-1 text-meta text-slate-500">Voluntary self-identification · 4/5ths adverse impact rule · EEOC / OFCCP aligned</div>
                </div>
                <div className="flex items-center gap-2">
                  {eeocFlaggedGroups > 0 && (
                    <div className="rounded-full border border-rose-400/30 bg-rose-500/10 px-3 py-1 text-micro font-black uppercase tracking-[0.12em] text-rose-300">
                      {eeocFlaggedGroups} adverse impact flag{eeocFlaggedGroups !== 1 ? 's' : ''}
                    </div>
                  )}
                  <div className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-micro text-slate-400">
                    {eeocResponseRate}% response rate · {eeocTotal} of {filteredCandidates.length}
                  </div>
                </div>
              </div>

              {eeocTotal === 0 ? (
                <div className="mt-5 rounded-card border border-dashed border-white/10 px-5 py-8 text-center">
                  <div className="text-sm font-bold text-slate-500">No EEO self-ID data collected yet</div>
                  <div className="mt-2 text-meta text-slate-600 max-w-md mx-auto">
                    Use the <span className="text-slate-400 font-semibold">EEO ID</span> button on any candidate card to record voluntary self-identification. All fields are optional and confidential per EEOC guidelines.
                  </div>
                </div>
              ) : (
                <div className="mt-5 grid gap-5 xl:grid-cols-2">

                  {/* Gender funnel */}
                  <div>
                    <div className="text-meta uppercase tracking-[0.16em] text-slate-400 mb-3">Gender — Applied vs. Hired</div>
                    <div className="space-y-2">
                      {eeocGenderBreakdown.map((row) => (
                        <div key={row.gender} className="flex items-center gap-3 rounded-card border border-white/10 bg-slate-900/40 px-3 py-2.5">
                          <div className="flex-1 min-w-0 text-xs font-bold text-white">{row.label}</div>
                          <div className="text-xs text-slate-400 w-16 text-right">{row.inPipeline} applied</div>
                          <div className="text-xs text-emerald-400 w-14 text-right">{row.hired} hired</div>
                          <div className={`text-xs font-black w-12 text-right ${row.selectRate == null ? 'text-slate-600' : row.selectRate >= 30 ? 'text-emerald-300' : row.selectRate >= 15 ? 'text-amber-300' : 'text-rose-400'}`}>
                            {row.selectRate != null ? row.selectRate + '%' : '—'}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Race/Ethnicity adverse impact */}
                  <div>
                    <div className="text-meta uppercase tracking-[0.16em] text-slate-400 mb-3">Race/Ethnicity — 4/5ths (80%) Rule</div>
                    <div className="space-y-2">
                      {eeocAdverseImpact.map((row) => (
                        <div key={row.race} className={`flex items-center gap-3 rounded-card border px-3 py-2.5 ${row.flagged ? 'border-rose-400/30 bg-rose-500/5' : 'border-white/10 bg-slate-900/40'}`}>
                          <div className="flex-1 min-w-0 text-xs font-bold text-white truncate">{row.label}</div>
                          <div className="text-xs text-slate-400 w-12 text-right">{row.inPipeline}</div>
                          <div className={`text-xs font-black w-10 text-right ${row.selectRate == null ? 'text-slate-600' : row.selectRate >= 30 ? 'text-emerald-300' : 'text-amber-300'}`}>
                            {row.selectRate != null ? row.selectRate + '%' : '—'}
                          </div>
                          <div className={`w-10 text-right text-micro font-black ${row.flagged ? 'text-rose-400' : row.ratio != null ? 'text-emerald-400' : 'text-slate-600'}`}>
                            {row.ratio != null ? row.ratio + '%' : '—'}
                          </div>
                          {row.flagged && (
                            <div className="shrink-0 w-2 h-2 rounded-full bg-rose-400" title="Adverse impact flag: selection rate below 80% of majority group" />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 text-micro text-slate-600">Ratio = selection rate vs. highest-selected group. Flag = below 80% threshold.</div>
                  </div>

                </div>
              )}

              {/* Veteran & disability summary */}
              {eeocTotal > 0 && (
                <div className="mt-5 grid gap-3 sm:grid-cols-3 border-t border-white/10 pt-5">
                  {[
                    { label: 'Protected Veteran', key: 'veteran', positiveValues: ['yes', 'active', 'reserve', 'separated'] },
                    { label: 'Disability', key: 'disability', positiveValues: ['yes'] },
                    { label: 'Self-ID Complete', key: '_complete', positiveValues: [] },
                  ].map((row) => {
                    const count = row.key === '_complete'
                      ? eeocTotal
                      : eeocCandidates.filter(c => row.positiveValues.includes((c.eeocSelfId[row.key] || '').toLowerCase())).length;
                    const pct = filteredCandidates.length > 0 ? Math.round((count / filteredCandidates.length) * 100) : 0;
                    return (
                      <div key={row.label} className="rounded-card border border-white/10 bg-slate-900/40 px-4 py-3">
                        <div className="text-micro uppercase tracking-[0.16em] text-slate-500">{row.label}</div>
                        <div className="mt-1.5 text-2xl font-black text-white">{count}</div>
                        <div className="text-meta text-slate-500 mt-0.5">{pct}% of pipeline</div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="mt-4 rounded-card border border-white/[0.06] bg-slate-950/40 px-4 py-3 text-micro leading-5 text-slate-600">
                &#9432; All EEO self-identification is voluntary and confidential. Data is collected for federal reporting purposes only and is not used in hiring decisions per EEOC guidelines (29 CFR Part 1602).
              </div>
            </div>


{/* KPI Drill-down Drawer */}
            {activeKpiDrilldown && (
              <div className="fixed inset-0 z-40 flex">
                <button type="button" aria-label="Close drawer" className="absolute inset-0 bg-black/50 cursor-default" onClick={() => setReportingKpiDrawerKey('')} />
                <div className="absolute right-0 top-0 h-full w-full max-w-xl flex flex-col border-l border-white/10 bg-slate-950/95 shadow-2xl">
                  <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10">
                    <div className="text-sm font-black uppercase tracking-[0.18em] text-cyan-300">{activeKpiDrilldown.title}</div>
                    <button type="button" onClick={() => setReportingKpiDrawerKey('')}
                      className="rounded-ctl border border-white/10 bg-white/5 px-3 py-1.5 text-xs font-bold text-slate-400 hover:text-white transition-colors">
                      Close
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-2">
                    {activeKpiDrilldown.rows.length === 0 && (
                      <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No data for this period.</div>
                    )}
                    {activeKpiDrilldown.rows.map((row) => (
                      <div key={row.id} className="rounded-card border border-white/10 bg-white/5 p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div>
                            <div className="text-sm font-bold text-white">{row.name || row.title || 'Item'}</div>
                            {(row.sub || row.subtitle) && <div className="text-xs text-slate-400 mt-0.5">{row.sub || row.subtitle}</div>}
                          </div>
                          <div className="text-right">
                            {row.meta && <div className="text-meta text-slate-500 mt-1">{row.meta}</div>}
                            {row.value != null && <div className="text-sm font-black text-cyan-300 shrink-0">{row.value}</div>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

          </div>

        )}

      </div>

    );

  };



  const handleRunLaunchChecks = async () => {
    setIsRunningLaunchChecks(true);
    setLaunchCheckResults([]);
    const activeJob = jobs.find((job) => String(job.status || 'Active') === 'Active') || jobs[0];
    const careersId = agencyBrand.agencySlug || recruiterUser?.uid || '';
    const checks = [
      { id: 'robots', label: 'Robots file', path: '/robots.txt', expect: (text) => text.includes('Sitemap:') },
      { id: 'sitemap-index', label: 'Sitemap index', path: '/sitemap-index.xml', expect: (text) => text.includes('<sitemapindex') },
      { id: 'sitemap-static', label: 'Static sitemap', path: '/sitemap.xml', expect: (text) => text.includes('<urlset') && text.includes('<loc>') },
      { id: 'sitemap-jobs', label: 'Jobs sitemap', path: '/sitemap-jobs.xml', expect: (text) => text.includes('<urlset') },
      ...(careersId ? [{ id: 'careers', label: 'Careers page', path: `/careers/${encodeURIComponent(careersId)}`, expect: (text) => text.includes('Open Roles') || text.includes('open role') }] : []),
      ...(activeJob?.id && recruiterUser?.uid ? [{ id: 'job', label: 'Job detail page', path: `/job/${encodeURIComponent(recruiterUser.uid)}/${encodeURIComponent(activeJob.id)}`, expect: (text) => text.includes('JobPosting') || text.includes(activeJob.title || '') }] : []),
    ];

    const results = [];
    for (const check of checks) {
      try {
        const response = await fetch(check.path, { method: 'GET', cache: 'no-store' });
        const text = await response.text();
        const ok = response.ok && check.expect(text);
        results.push({
          ...check,
          ok,
          status: response.status,
          url: `${getPublicAppBaseUrl()}${check.path}`,
          message: ok ? 'Reachable and parseable.' : `Unexpected response from ${check.path}.`,
        });
      } catch (error) {
        results.push({
          ...check,
          ok: false,
          status: 0,
          url: `${getPublicAppBaseUrl()}${check.path}`,
          message: error?.message || 'Request failed.',
        });
      }
    }
    setLaunchCheckResults(results);
    setIsRunningLaunchChecks(false);
  };

  const handleSubmitSupportRequest = async (event) => {
    event.preventDefault();
    setSupportRequestState({ status: 'sending', message: '' });
    try {
      const functionsClient = getFunctions(app, 'us-central1');
      const submitSupportRequest = httpsCallable(functionsClient, 'submitSupportRequest');
      const result = await submitSupportRequest({
        appId,
        type: supportRequestDraft.type,
        priority: supportRequestDraft.priority,
        message: supportRequestDraft.message,
        pageUrl: typeof window !== 'undefined' ? window.location.href : '',
        diagnostics: {
          activeTab,
          route: typeof window !== 'undefined' ? window.location.pathname : '',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          jobsCount: jobs.length,
          candidatesCount: candidates.length,
        },
      });
      setSupportRequestDraft((current) => ({ ...current, message: '' }));
      setSupportRequestState({ status: 'sent', message: `Support request ${result?.data?.requestId || ''} created.` });
    } catch (error) {
      setSupportRequestState({ status: 'error', message: error?.message || 'Failed to send support request.' });
    }
  };

  const renderLaunchReadiness = () => {
    const activeJobs = jobs.filter((job) => String(job.status || 'Active') === 'Active');
    const careersId = agencyBrand.agencySlug || recruiterUser?.uid || '';
    const careersUrl = careersId ? `${getPublicAppBaseUrl()}/careers/${careersId}` : '';
    const jobSitemapUrl = `${getPublicAppBaseUrl()}/sitemap-jobs.xml`;
    const readinessItems = [
      {
        id: 'profile',
        title: 'Agency profile',
        ready: Boolean(profile.name && profile.agency && profile.location && profile.agencyKey),
        detail: profile.agency ? `${profile.agency} profile is connected.` : 'Agency identity is incomplete.',
        action: () => { setActiveTab('agency'); setAgencySection('profile'); },
        actionLabel: 'Open profile',
      },
      {
        id: 'brand',
        title: 'Public Careers Page',
        ready: Boolean(agencyBrand.agencySlug),
        detail: agencyBrand.agencySlug ? careersUrl : 'Set your public careers URL before publishing roles.',
        action: () => { setActiveTab('agency'); setAgencySection('profile'); },
        actionLabel: 'Set Careers URL',
      },
      {
        id: 'jobs',
        title: 'Live jobs',
        ready: activeJobs.length > 0,
        detail: `${activeJobs.length} active role${activeJobs.length === 1 ? '' : 's'} available for public distribution.`,
        action: () => setActiveTab('jobs'),
        actionLabel: 'Open jobs',
      },
      {
        id: 'candidates',
        title: 'Candidate pipeline',
        ready: candidates.length > 0,
        detail: `${candidates.length} candidate${candidates.length === 1 ? '' : 's'} in the workspace.`,
        action: () => setActiveTab('pipeline'),
        actionLabel: 'Open pipeline',
      },
      {
        id: 'send-controls',
        title: 'Sequence send controls',
        ready: Number(sequenceControls.hourlyEmailCap) > 0 && Number(sequenceControls.dailyEmailCap) > 0,
        detail: `${sequenceControls.hourlyEmailCap}/hour and ${sequenceControls.dailyEmailCap}/day caps are set.`,
        action: () => activeJobs[0] ? setSelectedJobPanelId(activeJobs[0].id) : setActiveTab('jobs'),
        actionLabel: 'Open controls',
      },
      {
        id: 'support',
        title: 'Support channel',
        ready: true,
        detail: 'In-app support requests are captured with workspace diagnostics.',
        action: () => {
          const element = typeof document !== 'undefined' ? document.getElementById('support-request-form') : null;
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
        actionLabel: 'Contact support',
      },
    ];
    const readyCount = readinessItems.filter((item) => item.ready).length;
    const allChecksPassed = launchCheckResults.length > 0 && launchCheckResults.every((result) => result.ok);

    return (
      <div className="mx-auto max-w-6xl space-y-5 px-4 pb-32 pt-6">
        <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.24em] text-emerald-300">Launch Readiness</div>
              <div className="mt-2 text-2xl font-black text-white">Make this workspace safe to sell, publish, and support.</div>
              <div className="mt-2 text-sm text-slate-400">{readyCount} of {readinessItems.length} readiness checks are complete.</div>
            </div>
            <div className={`rounded-full border px-4 py-2 text-xs font-black uppercase tracking-[0.18em] ${readyCount === readinessItems.length && allChecksPassed ? 'border-emerald-400/20 bg-emerald-500/10 text-emerald-200' : 'border-amber-400/20 bg-amber-500/10 text-amber-200'}`}>
              {readyCount === readinessItems.length && allChecksPassed ? 'Ready' : 'Needs Review'}
            </div>
          </div>
          <div className="mt-5 h-2 overflow-hidden rounded-full bg-white/10">
            <div className="h-full rounded-full bg-emerald-400" style={{ width: `${Math.round((readyCount / readinessItems.length) * 100)}%` }} />
          </div>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          {readinessItems.map((item) => (
            <div key={item.id} className={`rounded-shell border p-5 ${item.ready ? 'border-emerald-400/20 bg-emerald-500/10' : 'border-amber-400/20 bg-amber-500/10'}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-sm font-black text-white">{item.title}</div>
                  <div className="mt-2 text-xs leading-6 text-slate-300">{item.detail}</div>
                </div>
                <span className={`rounded-full px-3 py-1 text-micro font-black uppercase tracking-[0.14em] ${item.ready ? 'bg-emerald-400 text-slate-950' : 'bg-amber-300 text-slate-950'}`}>{item.ready ? 'Done' : 'Fix'}</span>
              </div>
              <button type="button" onClick={item.action} className="mt-4 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-micro font-black uppercase tracking-[0.16em] text-slate-200 transition hover:border-white/20 hover:text-white">{item.actionLabel}</button>
            </div>
          ))}
        </div>

        <div className="grid gap-5 xl:grid-cols-[1fr_0.8fr]">
          <div className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">Public Indexing</div>
                <div className="mt-2 text-sm text-slate-400">Run this after deploy to prove public crawl URLs respond correctly.</div>
              </div>
              <button type="button" onClick={handleRunLaunchChecks} disabled={isRunningLaunchChecks} className="rounded-full bg-cyan-500 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60">
                {isRunningLaunchChecks ? 'Checking...' : 'Run public checks'}
              </button>
            </div>
            <div className="mt-5 grid gap-3">
              {launchCheckResults.length === 0 && (
                <div className="rounded-card border border-dashed border-white/10 px-4 py-5 text-sm text-slate-500">No checks have run in this session.</div>
              )}
              {launchCheckResults.map((result) => (
                <div key={result.id} className="rounded-card border border-white/10 bg-slate-950/60 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <div className="text-sm font-bold text-white">{result.label}</div>
                      <a href={result.url} target="_blank" rel="noreferrer" className="mt-1 block break-all text-xs text-cyan-300 underline">{result.url}</a>
                    </div>
                    <span className={`rounded-full px-3 py-1 text-micro font-black uppercase tracking-[0.14em] ${result.ok ? 'bg-emerald-400 text-slate-950' : 'bg-rose-400 text-white'}`}>{result.ok ? 'Pass' : 'Fail'}</span>
                  </div>
                  <div className="mt-2 text-xs text-slate-400">HTTP {result.status || 'n/a'} · {result.message}</div>
                </div>
              ))}
            </div>
            <div className="mt-4 grid gap-2 text-xs text-slate-400">
              <a href={`${getPublicAppBaseUrl()}/sitemap-index.xml`} target="_blank" rel="noreferrer" className="underline">Sitemap index</a>
              <a href={jobSitemapUrl} target="_blank" rel="noreferrer" className="underline">Jobs sitemap</a>
              {careersUrl && <a href={careersUrl} target="_blank" rel="noreferrer" className="underline">Careers page</a>}
            </div>
          </div>

          <form id="support-request-form" onSubmit={handleSubmitSupportRequest} className="rounded-shell border border-white/10 bg-white/5 p-5 sm:p-6">
            <div className="text-xs font-black uppercase tracking-[0.22em] text-cyan-300">Support</div>
            <div className="mt-2 text-sm text-slate-400">Send setup, indexing, email, WhatsApp, or candidate-flow issues with diagnostics attached.</div>
            <div className="mt-5 grid gap-3">
              <label className="block">
                <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">Request type</div>
                <select value={supportRequestDraft.type} onChange={(event) => setSupportRequestDraft((current) => ({ ...current, type: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400">
                  <option value="setup">Setup</option>
                  <option value="indexing">Indexing</option>
                  <option value="email">Email sending</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="candidate_flow">Candidate flow</option>
                  <option value="billing">Billing</option>
                </select>
              </label>
              <label className="block">
                <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">Priority</div>
                <select value={supportRequestDraft.priority} onChange={(event) => setSupportRequestDraft((current) => ({ ...current, priority: event.target.value }))} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm text-white outline-none focus:border-cyan-400">
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                  <option value="launch_blocker">Launch blocker</option>
                </select>
              </label>
              <label className="block">
                <div className="mb-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500">What needs attention?</div>
                <textarea value={supportRequestDraft.message} onChange={(event) => setSupportRequestDraft((current) => ({ ...current, message: event.target.value }))} rows={6} required minLength={12} className="w-full rounded-card border border-white/10 bg-slate-950/70 px-4 py-3 text-sm leading-6 text-white outline-none focus:border-cyan-400" placeholder="Describe what is blocked, what you expected, and the candidate or job affected." />
              </label>
              {supportRequestState.message && (
                <div className={`rounded-card border px-4 py-3 text-xs ${supportRequestState.status === 'error' ? 'border-rose-400/20 bg-rose-500/10 text-rose-100' : 'border-emerald-400/20 bg-emerald-500/10 text-emerald-100'}`}>{supportRequestState.message}</div>
              )}
              <button type="submit" disabled={supportRequestState.status === 'sending'} className="rounded-full bg-cyan-500 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-400 disabled:cursor-not-allowed disabled:opacity-60">
                {supportRequestState.status === 'sending' ? 'Sending...' : 'Send support request'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  // ─── Revenue & Finance Dashboard ────────────────────────────────────────────
  const renderRevenue = () => {
    const fmt = (n) => Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    const primaryCurrency = placements[0]?.currency || 'ZAR';

    // ── Hired candidates without a placement record ──────────────────────────
    const loggedCandidateIds = new Set(placements.map((p) => p.candidateId).filter(Boolean));
    const hiredCandidates = candidates.filter((c) => String(c.status || '').toLowerCase() === 'hired');
    const unloggedHires = hiredCandidates.filter((c) => !loggedCandidateIds.has(c.id));

    // Derive a best-effort fee estimate from the candidate record or offer negotiation
    const deriveEstimatedFee = (c) => {
      if (Number(c.placementFee || 0) > 0) return Number(c.placementFee);
      const salary = Number(c.offerSalary || c.salaryExpectation || c.expectedSalary || 0);
      const pct = Number(c.placementFeePercent || c.feePercent || 0);
      if (salary > 0 && pct > 0) return Math.round(salary * pct / 100);
      // Try to parse a numeric value out of offerNegotiation compensation string
      const compStr = String(c.offerNegotiation?.terms?.compensation || c.compensation || '');
      const compNum = Number(compStr.replace(/[^0-9.]/g, ''));
      if (compNum > 0) return compNum;
      return 0;
    };

    const unloggedEstimatedTotal = unloggedHires.reduce((s, c) => s + deriveEstimatedFee(c), 0);
    const totalHires = hiredCandidates.length;
    const loggedHires = hiredCandidates.filter((c) => loggedCandidateIds.has(c.id)).length;
    const coverageRate = totalHires > 0 ? Math.round((loggedHires / totalHires) * 100) : 100;

    // KPI aggregates
    const totalFees = placements.reduce((s, p) => s + Number(p.placementFee || 0), 0);
    const totalMargin = placements.reduce((s, p) => s + Number(p.margin || 0), 0);
    const totalTax = placements.reduce((s, p) => s + Number(p.taxAmount || 0), 0);
    const totalInvoiceValue = placements.reduce((s, p) => s + Number(p.invoiceTotal || p.placementFee || 0), 0);
    const paidFees = placements.filter((p) => p.paymentStatus === 'paid').reduce((s, p) => s + Number(p.placementFee || 0), 0);
    const outstandingFees = placements.filter((p) => p.paymentStatus !== 'paid').reduce((s, p) => s + Number(p.placementFee || 0), 0);
    const overdueCount = placements.filter((p) => p.paymentStatus !== 'paid' && p.invoiceDueDate && new Date(p.invoiceDueDate) < new Date()).length;
    const avgFee = placements.length > 0 ? Math.round(totalFees / placements.length) : 0;

    // Revenue by client
    const byClient = {};
    placements.forEach((p) => {
      const k = p.clientName || 'Unknown Client';
      if (!byClient[k]) byClient[k] = { name: k, fee: 0, count: 0 };
      byClient[k].fee += Number(p.placementFee || 0);
      byClient[k].count += 1;
    });
    const clientRows = Object.values(byClient).sort((a, b) => b.fee - a.fee);

    // Revenue by recruiter (billedBy)
    const byRecruiter = {};
    placements.forEach((p) => {
      const k = p.billedBy || 'Unassigned';
      if (!byRecruiter[k]) byRecruiter[k] = { name: k, fee: 0, count: 0 };
      byRecruiter[k].fee += Number(p.placementFee || 0);
      byRecruiter[k].count += 1;
    });
    const recruiterRows = Object.values(byRecruiter).sort((a, b) => b.fee - a.fee);

    // Revenue by contact
    const byContact = {};
    placements.forEach((p) => {
      const k = p.contactName || '';
      if (!k) return;
      if (!byContact[k]) byContact[k] = { name: k, clientName: p.clientName || '', fee: 0, count: 0 };
      byContact[k].fee += Number(p.placementFee || 0);
      byContact[k].count += 1;
    });
    const contactRows = Object.values(byContact).sort((a, b) => b.fee - a.fee);

    const INVOICE_STATUS_STYLES = {
      pending:  { label: 'Draft',    cls: 'border-slate-600/40 bg-slate-700/20 text-slate-400' },
      sent:     { label: 'Sent',     cls: 'border-amber-400/30 bg-amber-500/10 text-amber-300' },
      disputed: { label: 'Disputed', cls: 'border-rose-400/30 bg-rose-500/10 text-rose-300' },
      cancelled:{ label: 'Cancelled',cls: 'border-slate-600/40 bg-slate-700/20 text-slate-500' },
    };
    const PAYMENT_STATUS_STYLES = {
      unpaid:    { label: 'Unpaid',    cls: 'border-rose-400/30 bg-rose-500/10 text-rose-300' },
      partial:   { label: 'Partial',   cls: 'border-amber-400/30 bg-amber-500/10 text-amber-300' },
      paid:      { label: 'Paid',      cls: 'border-emerald-400/30 bg-emerald-500/10 text-emerald-300' },
      written_off:{ label: 'Written off', cls: 'border-slate-600/40 bg-slate-700/20 text-slate-500' },
    };

    const isOverdue = (p) => p.paymentStatus !== 'paid' && p.invoiceDueDate && new Date(p.invoiceDueDate) < new Date();

    const handleExportPlacementsCsv = () => {
      downloadCsv(`vision-placement-revenue-${new Date().toISOString().slice(0, 10)}.csv`, buildPlacementRevenueCsvRows(placements));
    };

    const handleUpdateInvoice = async (placementId, patch) => {
      if (!recruiterUser?.uid) return;
      setIsSavingRevenueInvoice(true);
      try {
        await setDoc(recruiterPlacementDoc(recruiterUser.uid, placementId), { ...patch, updatedAt: serverTimestamp() }, { merge: true });
        setRevenueInvoiceEditId(null);
        setRevenueInvoiceDraft({});
      } finally {
        setIsSavingRevenueInvoice(false);
      }
    };

    return (
      <div className="mx-auto max-w-7xl space-y-6 px-4 pb-32 pt-6">
        {/* Header */}
        <div>
          <div className="text-meta font-black uppercase tracking-[0.28em] text-emerald-400">Revenue & Finance</div>
          <p className="mt-1 text-sm text-slate-500">Placement fees, margins, commissions, invoices, and payment tracking.</p>
        </div>

        {/* KPI row */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-7">
          {[
            { label: 'Total Fees', value: summarizeMoneyByCurrency(placements, 'placementFee'), sub: `${placements.length} placements`, color: 'text-emerald-300' },
            { label: 'Invoice Total', value: summarizeMoneyByCurrency(placements, 'invoiceTotal'), sub: totalTax > 0 ? `${formatMoney(totalTax, primaryCurrency)} tax/VAT tracked` : 'incl. tax when applied', color: 'text-cyan-300' },
            { label: 'Collected', value: summarizeMoneyByCurrency(placements.filter((p) => p.paymentStatus === 'paid'), 'placementFee'), sub: placements.filter((p) => p.paymentStatus === 'paid').length + ' invoices paid', color: 'text-emerald-300' },
            { label: 'Outstanding', value: summarizeMoneyByCurrency(placements.filter((p) => p.paymentStatus !== 'paid'), 'placementFee'), sub: placements.filter((p) => p.paymentStatus !== 'paid').length + ' unpaid', color: 'text-amber-300' },
            { label: 'Overdue', value: String(overdueCount), sub: 'invoices past due date', color: overdueCount > 0 ? 'text-rose-300' : 'text-slate-500' },
            { label: 'Margin', value: summarizeMoneyByCurrency(placements, 'margin'), sub: 'net agency margin', color: 'text-violet-300' },
            { label: 'Revenue Coverage', value: `${coverageRate}%`, sub: unloggedHires.length > 0 ? `${unloggedHires.length} hire${unloggedHires.length !== 1 ? 's' : ''} unlogged` : 'all hires logged', color: coverageRate === 100 ? 'text-emerald-300' : coverageRate >= 70 ? 'text-amber-300' : 'text-rose-300' },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-card border border-white/10 bg-slate-900/60 p-4">
              <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">{kpi.label}</div>
              <div className={`mt-1 text-lg font-black ${kpi.color}`}>{kpi.value}</div>
              <div className="mt-0.5 text-micro text-slate-600">{kpi.sub}</div>
            </div>
          ))}
        </div>

        {/* Unlogged hires alert */}
        {unloggedHires.length > 0 && (
          <div className="rounded-shell border border-amber-400/25 bg-amber-500/[0.07] overflow-hidden">
            <div className="flex flex-col gap-3 px-6 py-4 border-b border-amber-400/10 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-micro font-black uppercase tracking-[0.2em] text-amber-300">Unlogged Hires — Revenue Gap</div>
                <div className="mt-1 text-xs text-slate-400">
                  {unloggedHires.length} successful hire{unloggedHires.length !== 1 ? 's' : ''} {unloggedHires.length !== 1 ? 'have' : 'has'} no placement record.
                  {unloggedEstimatedTotal > 0 && <> Estimated unlogged value: <span className="font-black text-amber-200">{formatMoney(unloggedEstimatedTotal, primaryCurrency)}</span>.</>}
                  {' '}Log each placement to track invoices and collect revenue.
                </div>
              </div>
              <div className="shrink-0 rounded-full border border-amber-400/20 bg-amber-500/10 px-4 py-2 text-micro font-black uppercase tracking-[0.14em] text-amber-200">
                {totalHires > 0 ? `${loggedHires}/${totalHires} logged` : '0 hires'}
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-sm">
                <thead>
                  <tr className="border-b border-amber-400/10">
                    {['Candidate', 'Client', 'Role', 'Hired', 'Est. Fee / Compensation', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-micro font-black uppercase tracking-[0.16em] text-slate-600 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {unloggedHires.map((c) => {
                    const estFee = deriveEstimatedFee(c);
                    const hiredDate = c.hiredAt || c.clientHiredConfirmedAtIso || c.updatedAt || '';
                    const compHint = c.offerNegotiation?.terms?.compensation || c.compensation || '';
                    return (
                      <tr key={c.id} className="border-b border-white/[0.04] hover:bg-amber-400/[0.03] transition">
                        <td className="px-4 py-3 text-xs font-semibold text-white whitespace-nowrap">{c.name || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-300 whitespace-nowrap">{c.company || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap max-w-[160px] truncate">{c.matchedJobTitle || c.role || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                          {hiredDate ? new Date(hiredDate).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-xs whitespace-nowrap">
                          {estFee > 0
                            ? <span className="font-black text-amber-200">{formatMoney(estFee, c.placementCurrency || primaryCurrency)}</span>
                            : compHint
                              ? <span className="text-slate-400">{compHint.slice(0, 40)}{compHint.length > 40 ? '…' : ''}</span>
                              : <span className="text-slate-600 italic">no fee data</span>
                          }
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => {
                              setPlacementCandidateId(c.id);
                              setPlacementDraft({
                                offerSalary: String(c.offerSalary || ''),
                                feePercent: String(c.placementFeePercent || c.feePercent || ''),
                                directFee: String(c.placementFee || ''),
                                usePercent: Boolean(c.placementFeePercent || c.feePercent),
                                startDate: String(c.startDate || c.offerNegotiation?.terms?.startDate || ''),
                                currency: c.placementCurrency || primaryCurrency,
                                billedBy: profile?.name || '',
                                marginPercent: '',
                                commissionSplit: '',
                                taxPercent: '',
                                invoiceNumber: '',
                                invoiceDate: new Date().toISOString().slice(0, 10),
                                invoiceDueDays: '30',
                                invoiceStatus: 'pending',
                                paymentStatus: 'unpaid',
                              });
                            }}
                            className="rounded-full border border-amber-400/30 bg-amber-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-amber-200 transition hover:bg-amber-500/20 hover:text-white"
                          >
                            Log Placement
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="rounded-shell border border-white/10 bg-slate-900/60 overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-white/[0.06]">
            <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-300">All Placements</div>
            <div className="flex items-center gap-3">
              <div className="text-micro text-slate-600">{placements.length} records</div>
              <button type="button" onClick={handleExportPlacementsCsv} disabled={!placements.length} className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-300 transition hover:border-emerald-400/40 hover:text-white disabled:cursor-not-allowed disabled:opacity-40">Export CSV</button>
            </div>
          </div>
          {placements.length === 0 ? (
            <div className="px-6 py-16 text-center text-sm text-slate-600">No placements logged yet. Use "Log Placement" on a hired candidate in the pipeline.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b border-white/[0.06]">
                    {['Candidate', 'Client', 'Contact', 'Role', 'Fee', 'Tax', 'Total', 'Margin', 'Commission', 'Invoice #', 'Invoice Date', 'Due Date', 'Invoice', 'Payment', 'Billed by', ''].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-micro font-black uppercase tracking-[0.16em] text-slate-600 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {placements.map((p) => {
                    const overdue = isOverdue(p);
                    const isEditing = revenueInvoiceEditId === p.id;
                    const invStyle = INVOICE_STATUS_STYLES[p.invoiceStatus] || INVOICE_STATUS_STYLES.pending;
                    const payStyle = PAYMENT_STATUS_STYLES[p.paymentStatus] || PAYMENT_STATUS_STYLES.unpaid;
                    return (
                      <tr key={p.id} className={`border-b border-white/[0.04] hover:bg-white/[0.02] transition ${overdue ? 'bg-rose-500/5' : ''}`}>
                        <td className="px-4 py-3 text-xs font-semibold text-white whitespace-nowrap">{p.candidateName || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-300 whitespace-nowrap">{p.clientName || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{p.contactName || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap max-w-[140px] truncate">{p.jobTitle || '—'}</td>
                        <td className="px-4 py-3 text-xs font-black text-emerald-300 whitespace-nowrap">{formatMoney(p.placementFee, p.currency || 'ZAR')}</td>
                        <td className="px-4 py-3 text-xs text-cyan-300 whitespace-nowrap">{Number(p.taxPercent || 0) > 0 ? `${p.taxPercent}% · ${formatMoney(p.taxAmount, p.currency || 'ZAR')}` : '—'}</td>
                        <td className="px-4 py-3 text-xs font-black text-white whitespace-nowrap">{formatMoney(p.invoiceTotal || p.placementFee, p.currency || 'ZAR')}</td>
                        <td className="px-4 py-3 text-xs text-violet-300 whitespace-nowrap">{p.margin ? formatMoney(p.margin, p.currency || 'ZAR') : p.marginPercent ? `${p.marginPercent}%` : '—'}</td>
                        <td className="px-4 py-3 text-xs text-violet-300 whitespace-nowrap">{p.commissionSplit ? `${p.commissionSplit}%` : '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-400 whitespace-nowrap">{p.invoiceNumber || '—'}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{p.invoiceDate ? new Date(p.invoiceDate).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}</td>
                        <td className={`px-4 py-3 text-xs whitespace-nowrap font-semibold ${overdue ? 'text-rose-300' : 'text-slate-500'}`}>{p.invoiceDueDate ? new Date(p.invoiceDueDate).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' }) : '—'}{overdue ? ' ⚠' : ''}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {isEditing ? (
                            <select value={revenueInvoiceDraft.invoiceStatus ?? p.invoiceStatus} onChange={(e) => setRevenueInvoiceDraft((d) => ({ ...d, invoiceStatus: e.target.value }))} className="rounded-ctl border border-white/10 bg-slate-800 px-2 py-1 text-meta text-white outline-none">
                              {Object.entries(INVOICE_STATUS_STYLES).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
                            </select>
                          ) : (
                            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-micro font-semibold ${invStyle.cls}`}>{invStyle.label}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {isEditing ? (
                            <select value={revenueInvoiceDraft.paymentStatus ?? p.paymentStatus} onChange={(e) => setRevenueInvoiceDraft((d) => ({ ...d, paymentStatus: e.target.value }))} className="rounded-ctl border border-white/10 bg-slate-800 px-2 py-1 text-meta text-white outline-none">
                              {Object.entries(PAYMENT_STATUS_STYLES).map(([v, s]) => <option key={v} value={v}>{s.label}</option>)}
                            </select>
                          ) : (
                            <span className={`inline-flex rounded-full border px-2.5 py-0.5 text-micro font-semibold ${payStyle.cls}`}>{payStyle.label}</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{p.billedBy || '—'}</td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          {isEditing ? (
                            <div className="flex items-center gap-1.5">
                              <button type="button" onClick={() => handleUpdateInvoice(p.id, revenueInvoiceDraft)} disabled={isSavingRevenueInvoice} className="rounded-ctl bg-emerald-500 px-2.5 py-1 text-micro font-black text-slate-950 disabled:opacity-40">Save</button>
                              <button type="button" onClick={() => { setRevenueInvoiceEditId(null); setRevenueInvoiceDraft({}); }} className="rounded-ctl border border-white/10 px-2.5 py-1 text-micro text-slate-400 hover:text-white">Cancel</button>
                            </div>
                          ) : (
                            <div className="flex items-center gap-1.5">
                              <button type="button" onClick={() => openPlacementInvoicePrint(p, agencySettings)} className="rounded-ctl border border-white/10 bg-white/5 px-2.5 py-1 text-micro text-slate-400 hover:text-white">PDF</button>
                              <button type="button" onClick={() => { setRevenueInvoiceEditId(p.id); setRevenueInvoiceDraft({ invoiceStatus: p.invoiceStatus, paymentStatus: p.paymentStatus }); }} className="rounded-ctl border border-white/10 bg-white/5 px-2.5 py-1 text-micro text-slate-400 hover:text-white">Edit</button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Revenue breakdowns */}
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* By Client */}
          <div className="rounded-shell border border-white/10 bg-slate-900/60 p-6">
            <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-300 mb-4">Revenue by Client</div>
            {clientRows.length === 0 ? <div className="text-xs text-slate-600">No data yet.</div> : (
              <div className="space-y-2.5">
                {clientRows.map((r) => {
                  const pct = totalFees > 0 ? Math.round((r.fee / totalFees) * 100) : 0;
                  return (
                    <div key={r.name}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-semibold text-slate-300 truncate">{r.name}</span>
                        <span className="shrink-0 text-xs font-black text-emerald-300">R {fmt(r.fee)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 rounded-full bg-slate-800 h-1.5 overflow-hidden">
                          <div className="h-full rounded-full bg-emerald-500/60" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-micro text-slate-600 shrink-0">{r.count} placement{r.count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* By Recruiter */}
          <div className="rounded-shell border border-white/10 bg-slate-900/60 p-6">
            <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-300 mb-4">Revenue by Recruiter</div>
            {recruiterRows.length === 0 ? <div className="text-xs text-slate-600">No data yet.</div> : (
              <div className="space-y-2.5">
                {recruiterRows.map((r) => {
                  const pct = totalFees > 0 ? Math.round((r.fee / totalFees) * 100) : 0;
                  return (
                    <div key={r.name}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="text-xs font-semibold text-slate-300 truncate">{r.name}</span>
                        <span className="shrink-0 text-xs font-black text-violet-300">R {fmt(r.fee)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 rounded-full bg-slate-800 h-1.5 overflow-hidden">
                          <div className="h-full rounded-full bg-violet-500/60" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-micro text-slate-600 shrink-0">{r.count} placement{r.count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* By Contact */}
          <div className="rounded-shell border border-white/10 bg-slate-900/60 p-6">
            <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-300 mb-4">Revenue by Contact</div>
            {contactRows.length === 0 ? <div className="text-xs text-slate-600">No contacts linked to placements yet.</div> : (
              <div className="space-y-2.5">
                {contactRows.map((r) => {
                  const pct = totalFees > 0 ? Math.round((r.fee / totalFees) * 100) : 0;
                  return (
                    <div key={r.name}>
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="min-w-0">
                          <span className="text-xs font-semibold text-slate-300 truncate block">{r.name}</span>
                          <span className="text-micro text-slate-600 truncate block">{r.clientName}</span>
                        </div>
                        <span className="shrink-0 text-xs font-black text-violet-300">R {fmt(r.fee)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 rounded-full bg-slate-800 h-1.5 overflow-hidden">
                          <div className="h-full rounded-full bg-violet-500/60" style={{ width: `${pct}%` }} />
                        </div>
                        <span className="text-micro text-slate-600 shrink-0">{r.count} placement{r.count !== 1 ? 's' : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const handleFinishOwnerIntro = async () => {
    if (!recruiterUser?.uid || ownerIntroSaving) return;
    setOwnerIntroSaving(true);
    setOwnerIntroPreparing(true);
    try {
      await setDoc(userDocRef(recruiterUser.uid), {
        ownerIntroCompleted: true,
        ownerIntroCompletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      setOwnerIntroCompleted(true);
      window.setTimeout(() => {
        setOwnerIntroPreparing(false);
        setOwnerIntroSaving(false);
      }, 1200);
    } catch (error) {
      console.error('Failed to complete owner walkthrough', error);
      setOwnerIntroPreparing(false);
      setOwnerIntroSaving(false);
      setPipelineNotice('Failed to save walkthrough completion. Please try again.');
    }
  };

  const renderContent = () => {
    const activePermission = RBAC_PAGE_PERMISSIONS[activeTab];
    if (activePermission && !canAccess(activePermission)) {
      return (
        <div className="rounded-shell border border-amber-400/20 bg-amber-500/10 p-8 text-center">
          <div className="text-xs font-black uppercase tracking-[0.24em] text-amber-200">Restricted Area</div>
          <div className="mt-3 text-2xl font-black text-white">{WORKSPACE_PAGE_CONFIG[activeTab]?.label || 'This page'} is not available for your role.</div>
          <div className="mt-2 text-sm text-slate-300">Current role: {RBAC_ROLE_PRESETS[currentWorkspaceRole]?.label || currentWorkspaceRole}. Ask an owner to change your access in Team Settings.</div>
        </div>
      );
    }

    if (activeTab === 'pipeline') return renderPipeline();

    if (activeTab === 'jobs') return renderJobs();

    if (activeTab === 'revenue') return renderRevenue();

    if (activeTab === 'automations') {
      const mergedSettings = { ...DEFAULT_AUTOMATION_SETTINGS, ...automationSettings };
      const updateSetting = (key, patch) => {
        setAutomationSettings((prev) => ({
          ...prev,
          [key]: { ...(DEFAULT_AUTOMATION_SETTINGS[key] || {}), ...(prev[key] || {}), ...patch },
        }));
      };
      const automationCards = [
        {
          key: 'leadFollowUp',
          title: 'Lead Follow-up',
          plainTitle: 'Remind recruiters to follow up with new leads',
          description: 'When a website lead or prospect is created, Vision can nudge the team if nobody has followed up after the selected days.',
          trigger: 'New lead or prospect enters Vision',
          outcome: 'Creates follow-up reminders before the lead goes cold',
          field: 'followUpDays',
          inputLabel: 'Follow up after these days',
          inputHint: 'Use commas for multiple reminders, for example 3, 7, 14.',
          type: 'text',
          value: (mergedSettings.leadFollowUp.followUpDays || [3, 7, 14]).join(', '),
          tone: 'cyan',
        },
        {
          key: 'candidateAging',
          title: 'Candidate Aging',
          plainTitle: 'Flag candidates stuck in one pipeline stage',
          description: 'Vision watches candidates who have not moved for too long, so recruiters can unblock them, reject them, or push them forward.',
          trigger: 'Candidate remains in the same stage',
          outcome: 'Highlights stale candidates before the pipeline becomes messy',
          field: 'stageThresholdDays',
          inputLabel: 'Flag after this many days',
          inputHint: 'A smaller number makes Vision stricter about pipeline movement.',
          type: 'number',
          value: mergedSettings.candidateAging.stageThresholdDays || 21,
          tone: 'amber',
        },
        {
          key: 'screeningReminder',
          title: 'Screening Reminder',
          plainTitle: 'Remind candidates to complete screening',
          description: 'When a candidate has a screening link but has not completed it, Vision can remind the recruiter to chase it up.',
          trigger: 'Screening link is sent but unanswered',
          outcome: 'Keeps screening results available before shortlist decisions',
          field: 'reminderDays',
          inputLabel: 'Remind after these days',
          inputHint: 'Use commas for repeated reminders, for example 2, 5.',
          type: 'text',
          value: (mergedSettings.screeningReminder.reminderDays || [2, 5]).join(', '),
          tone: 'violet',
        },
        {
          key: 'clientReengagement',
          title: 'Client Re-engagement',
          plainTitle: 'Bring quiet clients back to the top of the queue',
          description: 'If a client has not had activity for a while, Vision can surface them so account managers know who needs attention.',
          trigger: 'No recent client activity',
          outcome: 'Protects revenue by flagging accounts before they drift',
          field: 'dormantDays',
          inputLabel: 'Flag after this many inactive days',
          inputHint: '30 days is a practical default for active agency accounts.',
          type: 'number',
          value: mergedSettings.clientReengagement.dormantDays || 30,
          tone: 'emerald',
        },
        {
          key: 'weeklyDigest',
          title: 'Weekly Digest',
          plainTitle: 'Summarize the agency week',
          description: 'Vision can prepare a weekly operating summary so owners see pipeline movement, client activity, and team priorities in one place.',
          trigger: 'Scheduled weekly workspace review',
          outcome: 'Gives owners a simple weekly health check',
          field: '',
          inputLabel: '',
          inputHint: '',
          type: '',
          value: '',
          tone: 'sky',
        },
        {
          key: 'jobAutoClose',
          title: 'Job Auto-Close',
          plainTitle: 'Mark inactive jobs for cleanup',
          description: 'When a job has been inactive for too long, Vision can flag it so the team can close, refresh, or follow up with the client.',
          trigger: 'Job has no recent activity',
          outcome: 'Keeps the job board and pipeline cleaner',
          field: 'inactiveDays',
          inputLabel: 'Flag inactive jobs after days',
          inputHint: 'This does not delete jobs. It helps you review stale roles.',
          type: 'number',
          value: mergedSettings.jobAutoClose.inactiveDays || 60,
          tone: 'rose',
        },
        {
          key: 'autoQualify',
          title: 'Auto-Qualify',
          plainTitle: 'Suggest candidates who look strong enough to progress',
          description: 'Vision can use candidate score signals to identify profiles that may be ready for recruiter review or shortlist movement.',
          trigger: 'Candidate score reaches the threshold',
          outcome: 'Helps recruiters spot high-fit candidates faster',
          field: 'scoreThreshold',
          inputLabel: 'Minimum score to flag',
          inputHint: 'Higher scores make the automation more selective.',
          type: 'number',
          value: mergedSettings.autoQualify.scoreThreshold || 70,
          tone: 'lime',
        },
        {
          key: 'autoRejectFeedback',
          title: 'Auto-Reject Feedback',
          plainTitle: 'Prepare rejection feedback for weak-fit candidates',
          description: 'Vision can identify low-fit candidates and help prepare a cleaner review path instead of leaving them stuck in the pipeline.',
          trigger: 'Candidate score falls below the threshold',
          outcome: 'Reduces pipeline clutter while keeping decisions explainable',
          field: 'scoreThreshold',
          inputLabel: 'Low-fit score threshold',
          inputHint: 'Lower scores make this automation less aggressive.',
          type: 'number',
          value: mergedSettings.autoRejectFeedback.scoreThreshold || 40,
          tone: 'orange',
        },
        {
          key: 'createAllFromLead',
          title: 'Create Client + Job',
          plainTitle: 'Turn a new lead into a client record and job order',
          description: 'When a prospect submits enough details, Vision can help create the company, contact, opportunity, and job workflow from one lead.',
          trigger: 'Qualified inbound lead arrives',
          outcome: 'Reduces admin work when sales turns into recruitment delivery',
          field: '',
          inputLabel: '',
          inputHint: '',
          type: '',
          value: '',
          tone: 'fuchsia',
        },
      ];
      const toneClasses = {
        cyan: 'border-cyan-300/20 bg-cyan-500/10 text-cyan-200',
        amber: 'border-amber-300/20 bg-amber-500/10 text-amber-200',
        violet: 'border-violet-300/20 bg-violet-500/10 text-violet-200',
        emerald: 'border-emerald-300/20 bg-emerald-500/10 text-emerald-200',
        sky: 'border-sky-300/20 bg-sky-500/10 text-sky-200',
        rose: 'border-rose-300/20 bg-rose-500/10 text-rose-200',
        lime: 'border-lime-300/20 bg-lime-500/10 text-lime-200',
        orange: 'border-orange-300/20 bg-orange-500/10 text-orange-200',
        fuchsia: 'border-violet-300/20 bg-violet-500/10 text-violet-200',
      };
      if (!automationSettingsLoaded) loadAutomationSettings();
      return (
        <div className="mx-auto max-w-6xl space-y-5 px-4 pb-32 pt-6">
          <section className="overflow-hidden rounded-shell border border-white/10 bg-slate-950/70 shadow-2xl shadow-violet-950/10">
            <div className="border-b border-white/10 bg-white/[0.03] p-5 sm:p-6">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                <div>
                  <div className="text-meta font-black uppercase tracking-[0.28em] text-violet-300">Automation Engine</div>
                  <h2 className="mt-3 text-2xl font-black text-white sm:text-3xl">Let Vision watch the repetitive work.</h2>
                  <p className="mt-2 max-w-3xl text-sm leading-7 text-slate-300">These automations do not replace the recruiter. They watch for missed follow-ups, stale candidates, quiet clients, and cleanup moments, then surface the next action so beginners know what is happening and why.</p>
                </div>
                <div className="grid min-w-[240px] grid-cols-2 gap-3">
                  <div className="rounded-card border border-white/10 bg-white/[0.04] p-4">
                    <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Enabled</div>
                    <div className="mt-1 text-2xl font-black text-white">{automationCards.filter((card) => Boolean(mergedSettings[card.key]?.enabled)).length}</div>
                  </div>
                  <div className="rounded-card border border-white/10 bg-white/[0.04] p-4">
                    <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500">Available</div>
                    <div className="mt-1 text-2xl font-black text-white">{automationCards.filter((card) => !LOCKED_AUTOMATION_FEATURES.has(card.key)).length}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="grid gap-4 p-4 sm:p-6 lg:grid-cols-2">
          {automationCards.map((card) => {
            const isLocked = LOCKED_AUTOMATION_FEATURES.has(card.key);
            const enabled = Boolean(mergedSettings[card.key]?.enabled);
            return (
              <div key={card.key} className={`rounded-shell border p-5 transition ${isLocked ? 'border-white/10 bg-slate-900/25 opacity-60' : enabled ? 'border-violet-300/25 bg-slate-900/70 shadow-lg shadow-violet-950/10' : 'border-white/10 bg-slate-900/45'}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.14em] ${toneClasses[card.tone] || toneClasses.violet}`}>{card.title}</span>
                      {isLocked && <span className="rounded-full border border-amber-400/40 bg-amber-500/10 px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] text-amber-200">Locked</span>}
                      <span className={`rounded-full border px-2.5 py-1 text-micro font-black uppercase tracking-[0.12em] ${enabled ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-white/10 bg-white/5 text-slate-400'}`}>{enabled ? 'Watching' : 'Off'}</span>
                    </div>
                    <h3 className="mt-4 text-lg font-black leading-6 text-white">{card.plainTitle}</h3>
                    <p className="mt-2 text-sm leading-6 text-slate-300">{card.description}</p>
                  </div>
                  <button type="button" disabled={isLocked} onClick={() => updateSetting(card.key, { enabled: !enabled })} className={`relative h-6 w-11 rounded-full ${enabled ? 'bg-violet-600' : 'bg-slate-700'} disabled:cursor-not-allowed disabled:opacity-50`}>
                    <span className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white transition-transform ${enabled ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
                <div className="mt-5 grid gap-3 sm:grid-cols-2">
                  <div className="rounded-card border border-white/10 bg-black/20 p-3">
                    <div className="flex items-center gap-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500"><Clock3 size={12} /> Trigger</div>
                    <div className="mt-2 text-xs font-semibold leading-5 text-slate-200">{card.trigger}</div>
                  </div>
                  <div className="rounded-card border border-white/10 bg-black/20 p-3">
                    <div className="flex items-center gap-2 text-micro font-black uppercase tracking-[0.16em] text-slate-500"><Check size={12} /> Result</div>
                    <div className="mt-2 text-xs font-semibold leading-5 text-slate-200">{card.outcome}</div>
                  </div>
                </div>
                {isLocked && <div className="mt-4 rounded-card border border-amber-400/20 bg-amber-500/10 px-4 py-3 text-xs leading-5 text-amber-100">This automation is temporarily locked while reliability fixes are completed.</div>}
                {enabled && card.field && (
                  <div className="mt-5 rounded-card border border-white/10 bg-white/[0.035] p-4">
                    <label className="text-micro font-black uppercase tracking-[0.16em] text-slate-400">{card.inputLabel}</label>
                    <input
                      type={card.type}
                      value={card.value}
                      disabled={isLocked}
                      onChange={(e) => {
                        if (card.type === 'text') {
                          updateSetting(card.key, { [card.field]: e.target.value.split(',').map((v) => parseInt(v.trim(), 10)).filter((v) => Number.isFinite(v) && v > 0) });
                        } else {
                          updateSetting(card.key, { [card.field]: Number(e.target.value) || 0 });
                        }
                      }}
                      className="mt-2 w-full rounded-ctl border border-white/10 bg-slate-950 px-3 py-2 text-sm font-semibold text-white outline-none transition focus:border-violet-300 disabled:cursor-not-allowed disabled:opacity-50"
                    />
                    <div className="mt-2 text-xs leading-5 text-slate-500">{card.inputHint}</div>
                  </div>
                )}
                {enabled && !card.field && (
                  <div className="mt-5 rounded-card border border-white/10 bg-white/[0.035] px-4 py-3 text-xs leading-5 text-slate-400">No timing field is needed for this automation. Vision uses the workspace schedule and activity data.</div>
                )}
              </div>
            );
          })}
            </div>
          </section>
          {automationSettingsNotice && <div className="rounded-ctl border border-white/10 bg-slate-900/40 px-4 py-3 text-sm text-slate-200">{automationSettingsNotice}</div>}
          <button type="button" disabled={automationSettingsSaving} onClick={saveAutomationSettingsFn} className="w-full rounded-card bg-violet-600 py-3 text-sm font-black text-white transition hover:bg-violet-500 disabled:opacity-50">
            {automationSettingsSaving ? 'Saving...' : 'Save Automation Settings'}
          </button>
        </div>
      );
    }

    return renderAgency();

  };



  if (screeningLinkId) {

    return (

      <ScreeningCandidatePage

        candidate={screeningLinkRecord}

        response={candidateScreeningResponse}

        isSubmitting={isSubmittingScreening}

        isLoadingCandidate={isLoadingScreeningCandidate}

        onSubmit={(answers) => handleSubmitScreening(screeningLinkRecord, answers)}

        onExit={() => {

          const url = new URL(window.location.href);

          url.searchParams.delete('screening');

          window.history.pushState({}, '', url.toString());

          setScreeningLinkId('');

        }}

      />

    );

  }



  if (jobIntakeLinkId) {

    return (

      <ClientJobIntakePage

        linkRecord={publicJobIntakeLink}

        form={clientJobForm}

        notice={clientJobFormNotice}

        isSubmitting={isSubmittingClientJobForm}

        hasSubmitted={hasSubmittedClientJobForm}

        onChange={handleClientJobFormChange}

        onSubmit={handleSubmitClientJobForm}

        onExit={() => {

          const url = new URL(window.location.href);

          url.searchParams.delete('job-intake');

          window.history.pushState({}, '', url.toString());

          setJobIntakeLinkId('');

          setPublicJobIntakeLink(null);

          setHasSubmittedClientJobForm(false);

          setClientJobFormNotice('');

          setClientJobForm(DEFAULT_CLIENT_JOB_FORM);

        }}

      />

    );

  }



  if (shortlistLinkId) {
    return (
      <ShortlistPortal
        token={shortlistLinkId}
        onExit={() => {
          const url = new URL(window.location.href);
          url.searchParams.delete('shortlist');
          window.history.pushState({}, '', url.toString());
          setShortlistLinkId('');
        }}
      />
    );
  }

  if (candidateJobIntakeLinkId) {

    return (

      <CandidateJobIntakePage

        linkRecord={publicCandidateJobIntakeLink}

        form={candidateApplicationForm}

        notice={candidateApplicationNotice}

        isSubmitting={isSubmittingCandidateApplication}

        hasSubmitted={hasSubmittedCandidateApplication}

        selectedJobId={selectedPublicCandidateJobId}

        onJobSelect={setSelectedPublicCandidateJobId}

        onChange={handleCandidateApplicationFormChange}

        onFileChange={handleCandidateCvFileChange}

        onSubmit={handleSubmitCandidateJobApplication}

        screeningAnswers={candidateScreeningAnswers}

        onScreeningChange={(q, v) => setCandidateScreeningAnswers((a) => ({ ...a, [q]: v }))}

        onExit={() => {

          const url = new URL(window.location.href);

          url.searchParams.delete('candidate-intake');

          window.history.pushState({}, '', url.toString());

          setCandidateJobIntakeLinkId('');

          setPublicCandidateJobIntakeLink(null);

          setCandidateCvFile(null);

          setSelectedPublicCandidateJobId('');

          setHasSubmittedCandidateApplication(false);

          setCandidateApplicationNotice('');

          setCandidateApplicationForm(DEFAULT_CANDIDATE_APPLICATION_FORM);

        }}

      />

    );

  }



  const handleUseDemoWorkspace = (demoPath = '') => {
    if (typeof window !== 'undefined') {
      const nextUrl = new URL(window.location.href);
      nextUrl.pathname = '/workspace/pipeline';
      nextUrl.search = '';
      window.history.pushState({}, '', nextUrl.toString());
    }
    setActiveTab('pipeline');
    setDemoWorkspaceActive(true);
    setDemoTabSwitchCount(0);
    setDemoAccountAdOpen(false);
    setDirectDemoConfettiOpen(false);
    setPostDemoFeaturePromptOpen(false);
    setPostDemoFeatureDetailsOpen(false);
    setDirectDemoWalkthroughOpen(demoPath === 'direct');
    setImportDemoWalkthroughOpen(demoPath === 'import');
    setDirectDemoWalkthroughStep(0);
    setImportDemoWalkthroughStep(0);
    setDirectDemoCompletedSteps(demoPath === 'direct' ? [] : []);
    setImportDemoCompletedSteps(demoPath === 'import' ? [] : []);
    setDirectDemoBaseline({ jobs: jobs.length, candidates: candidates.length, placements: placements.length });
    setAuthError('');
    setAuthMode('signin');
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_error) {}
  };

  const handleExitDemoWorkspace = () => {
    setDemoWorkspaceActive(false);
    setDemoTabSwitchCount(0);
    setDemoAccountAdOpen(false);
    setDirectDemoConfettiOpen(false);
    setPostDemoFeaturePromptOpen(false);
    setPostDemoFeatureDetailsOpen(false);
    setDirectDemoWalkthroughOpen(false);
    setImportDemoWalkthroughOpen(false);
    setDirectDemoWalkthroughStep(0);
    setImportDemoWalkthroughStep(0);
    setDirectDemoCompletedSteps([]);
    setImportDemoCompletedSteps([]);
    setPipelineNotice('');
    setAgencyNotice('');
    setActiveTab('pipeline');
    clearAuthFormSensitiveFields({ keepEmail: false });
  };

  const handleCreateAccountFromDemo = () => {
    setDemoWorkspaceActive(false);
    setDemoTabSwitchCount(0);
    setDemoAccountAdOpen(false);
    setDirectDemoConfettiOpen(false);
    setPostDemoFeaturePromptOpen(false);
    setPostDemoFeatureDetailsOpen(false);
    setDirectDemoWalkthroughOpen(false);
    setImportDemoWalkthroughOpen(false);
    setDirectDemoWalkthroughStep(0);
    setImportDemoWalkthroughStep(0);
    setDirectDemoCompletedSteps([]);
    setImportDemoCompletedSteps([]);
    setAuthMode('signup');
    setAuthError('');
    setPipelineNotice('');
    setAgencyNotice('');
    clearAuthFormSensitiveFields({ keepEmail: false });
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_error) {}
  };

  const currentPublicUrl = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const currentPublicPath = currentPublicUrl ? currentPublicUrl.pathname.replace(/\/+$/, '') || '/' : '/';
  const currentDemoParam = currentPublicUrl ? String(currentPublicUrl.searchParams.get('demo') || '').toLowerCase() : '';
  const isDemoChoiceLink = (currentPublicPath === '/workspace' || currentPublicPath === '/workspace/pipeline') && ['1', 'true', 'choose', 'options'].includes(currentDemoParam);

  useEffect(() => {
    if (!isDemoChoiceLink || typeof window === 'undefined') return;
    const nextUrl = new URL(window.location.href);
    nextUrl.pathname = '/demo';
    nextUrl.search = '';
    window.history.replaceState({}, '', nextUrl.toString());
  }, [isDemoChoiceLink]);

  const isPublicDemoPage = currentPublicPath === '/demo' || isDemoChoiceLink;

  if (isPublicDemoPage) {
    return <VisionDemoLandingPage onLaunchDemo={handleUseDemoWorkspace} onCreateAccount={handleCreateAccountFromDemo} />;
  }

  if (rawRecruiterUser && !authOtpCheckDone) {
    return <WorkspacePrepSkeleton />;
  }

  if (!rawRecruiterUser && !isDemoWorkspace) {

    return <RecruiterAuthPanel mode={authMode} form={authForm} error={authError} isSubmitting={isSubmittingAuth} onModeChange={(mode) => { setAuthMode(mode); setAuthError(''); clearAuthFormSensitiveFields({ keepEmail: false }); }} onChange={handleAuthFormChange} onSubmit={handleAuthSubmit} onUseDemo={handleUseDemoWorkspace} />;

  }

  if (!isDemoWorkspace && !authOtpVerified) {

    return (
      <OtpGatePanel
        email={rawRecruiterUser.email}
        code={authOtpCode}
        error={authOtpError}
        notice={authOtpNotice}
        isSubmitting={isVerifyingAuthOtp}
        isResending={isResendingAuthOtp}
        onCodeChange={setAuthOtpCode}
        onSubmit={handleVerifyAuthOtp}
        onResend={handleResendAuthOtp}
        onLogout={handleAuthOtpLogout}
      />
    );

  }



  const isAgencyOwner = (currentAgencyMember?.role || profile.accountRole) === 'owner' && agencyDetails?.ownerUid === recruiterUser.uid;

  const isWaitingForOwnerAgencyState = Boolean(recruiterUser?.uid && profile.accountRole === 'owner' && profile.agencyKey && agencyDetails === null);

  const ownerNeedsMembership = isAgencyOwner && !isAgencySubscriptionActive(agencyDetails);
  const shouldShowOwnerIntro = (currentAgencyMember?.role || profile.accountRole) === 'owner' && !ownerIntroCompleted;



  if (isWaitingForOwnerAgencyState) {

    return (

      <div className="min-h-screen bg-[radial-gradient(circle_at_top,#11335f_0%,#020617_42%,#020617_100%)] text-slate-100">

        <div className="mx-auto flex min-h-screen w-full max-w-4xl items-center justify-center px-4 py-6 sm:px-6 lg:px-8">

          <div className="w-full rounded-shell border border-white/10 bg-slate-950/60 p-8 text-center backdrop-blur">

            <img src="/vision-official-logo.jpg" alt="Vision" className="mx-auto w-full max-w-[420px] rounded-shell shadow-2xl shadow-cyan-950/30" />

            <div className="text-xs font-black uppercase tracking-[0.3em] text-cyan-300">Vision Membership</div>

            <h1 className="mt-3 text-3xl font-black text-white">Loading your agency membership access...</h1>

            <p className="mt-3 text-sm leading-7 text-slate-300">Your new agency is being verified before Vision unlocks the correct membership screen.</p>

          </div>

        </div>

      </div>

    );

  }


  if (ownerIntroPreparing) {

    return <WorkspacePrepSkeleton />;

  }


  if (shouldShowOwnerIntro) {

    return (

      <OwnerIntroWalkthrough
        agencyName={agencyDetails?.agencyName || profile.agency}
        ownerName={profile.name || recruiterUser.email}
        step={ownerIntroStep}
        onStep={setOwnerIntroStep}
        onFinish={handleFinishOwnerIntro}
        isFinishing={ownerIntroSaving}
      />

    );

  }



  if (ownerNeedsMembership && !membershipPreviewEnabled) {

    return renderMembershipPlans();

  }

  const directDemoGuideJob = jobs.find((job) => String(job.status || 'Active') === 'Active') || jobs[0] || null;
  const directDemoGuideActive = isDemoWorkspace && directDemoWalkthroughOpen;
  const importDemoGuideActive = isDemoWorkspace && importDemoWalkthroughOpen;
  const directDemoScreeningCandidateReady = candidates.some((candidate) => candidate.source === 'Vision demo direct sourcing' && candidate.status === 'screening');
  const directDemoOfferCandidateReady = candidates.some((candidate) => candidate.source === 'Vision demo direct sourcing' && candidate.status === 'offer');
  const importDemoScreeningCandidateReady = candidates.some((candidate) => ['Demo CSV import', 'Demo bulk CV upload'].includes(candidate.source) && candidate.status === 'screening');
  const importDemoOfferCandidateReady = candidates.some((candidate) => ['Demo CSV import', 'Demo bulk CV upload'].includes(candidate.source) && candidate.status === 'offer');
  const directDemoHighlightClass = 'relative z-[9996] ring-4 ring-cyan-300/80 ring-offset-4 ring-offset-slate-950 shadow-[0_0_0_1px_rgba(103,232,249,.75),0_0_34px_rgba(34,211,238,.45)]';
  const isDirectDemoHighlight = (...steps) => directDemoGuideActive && steps.includes(directDemoWalkthroughStep);
  const isDirectDemoTarget = (targetKey) => {
    const screeningStepTarget = selectedScreeningCandidateId ? 'screening-ai-suggestion-button' : directDemoScreeningCandidateReady ? 'candidate-screening-button' : 'pipeline-screening-column';
    const offerStepTarget = directDemoOfferCandidateReady ? 'candidate-offer-placement' : 'pipeline-offer-column';
    const targetsByStep = ['nav-pipeline', 'nav-jobs', 'job-title-input', 'job-open-button', 'sourced-prospects', 'nav-pipeline', screeningStepTarget, 'candidate-send-client-button', 'pipeline-interview-column', 'share-shortlist-button', offerStepTarget];
    const importScreeningStepTarget = selectedScreeningCandidateId ? 'screening-ai-suggestion-button' : importDemoScreeningCandidateReady ? 'candidate-screening-button' : 'pipeline-screening-column';
    const importOfferStepTarget = importDemoOfferCandidateReady ? 'candidate-offer-placement' : 'pipeline-offer-column';
    const importTargetsByStep = ['nav-jobs', 'import-jobs-csv-button', 'nav-pipeline', 'import-pipeline-csv-button', 'bulk-cv-upload-button', 'pipeline-screening-column', importScreeningStepTarget, 'candidate-send-client-button', 'pipeline-interview-column', 'share-shortlist-button', importOfferStepTarget];
    return (directDemoGuideActive && targetsByStep[directDemoWalkthroughStep] === targetKey)
      || (importDemoGuideActive && importTargetsByStep[importDemoWalkthroughStep] === targetKey);
  };

  const openDirectDemoGuidePipeline = () => {
    setSelectedJobPanelId('');
    setActiveTab('pipeline');
    if (directDemoWalkthroughStep === 0) markDirectDemoStepComplete(0);
    if (directDemoCompletedSteps.includes(4)) markDirectDemoStepComplete(5);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_error) {}
  };

  const openDirectDemoGuideJobs = () => {
    setSelectedJobPanelId('');
    setActiveTab('jobs');
    markDirectDemoStepComplete(1);
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_error) {}
  };

  const openDirectDemoGuideJobWorkspace = () => {
    setActiveTab('jobs');
    markDirectDemoStepComplete(1);
    if (directDemoGuideJob?.id) {
      setSelectedJobPanelId(directDemoGuideJob.id);
      markDirectDemoStepComplete(3);
    }
  };

  const openDirectDemoGuideShortlist = () => {
    setSelectedJobPanelId('');
    setActiveTab('pipeline');
    setShortlistCreatedUrl('');
    setShortlistLinkCopied(false);
    const shortlistCandidate = candidates.find((candidate) => Array.isArray(candidate.submittals) && candidate.submittals.length > 0)
      || candidates.find((candidate) => candidate.source === 'Vision demo direct sourcing')
      || candidates[0];
    setShortlistDraft({
      clientName: shortlistCandidate?.company || getJobCompanyName(directDemoGuideJob || {}) || 'Demo Client',
      jobTitle: shortlistCandidate?.matchedJobTitle || shortlistCandidate?.role || directDemoGuideJob?.title || '',
      candidateIds: shortlistCandidate?.id ? [shortlistCandidate.id] : [],
    });
    setShortlistModalOpen(true);
  };

  const visionDistinctFeatureHighlights = [
    {
      title: 'Candidate Intelligence / Fit Details',
      body: 'Vision does more than show a score. It connects the candidate profile, job requirements, screening context, skills, stage history, and recruiter notes so the recruiter can explain why a person is worth moving forward.',
      detail: 'This helps recruiters defend submissions to clients with evidence instead of relying on memory, scattered notes, or a generic CV summary.',
    },
    {
      title: 'AI Outreach + Sequence Automation',
      body: 'Vision supports structured outreach for prospects and candidates, including AI-assisted messaging and follow-up sequences tied to the job and candidate context.',
      detail: 'The value is speed and consistency: recruiters can start follow-ups without rebuilding messages from scratch or forgetting who needs the next touch.',
    },
    {
      title: 'Screening Answer Review + AI Question Support',
      body: 'Vision keeps screening questions, candidate responses, recruiter review, and AI suggestions directly attached to the pipeline record.',
      detail: 'This makes screening part of the workflow instead of a separate form, email thread, or spreadsheet note that gets lost before client submission.',
    },
    {
      title: 'Interview Recording + AI Notes',
      body: 'Vision can capture interview context and turn it into structured notes that stay connected to the candidate and role.',
      detail: 'Recruiters can preserve what was discussed, reduce manual note-taking, and keep the next person in the process aligned.',
    },
    {
      title: 'Placement Fee / Revenue Tracking',
      body: 'Vision links the recruiting workflow to commercial outcome tracking, including placements, fees, revenue, and performance after the hire.',
      detail: 'This helps agencies see which roles, recruiters, clients, and workflows are actually producing revenue, not just activity.',
    },
  ];

  return (

    <>

      <DirectDemoWalkthrough
        isOpen={isDemoWorkspace && directDemoWalkthroughOpen}
        step={directDemoWalkthroughStep}
        completedSteps={directDemoCompletedSteps}
        onStep={setDirectDemoWalkthroughStep}
        onClose={() => setDirectDemoWalkthroughOpen(false)}
        onOpenPipeline={openDirectDemoGuidePipeline}
        onOpenJobs={openDirectDemoGuideJobs}
        onOpenDemoJob={openDirectDemoGuideJobWorkspace}
        onOpenShortlist={openDirectDemoGuideShortlist}
        jobTitle={directDemoGuideJob?.title || ''}
        candidateCount={candidates.length}
        screeningReviewOpen={Boolean(selectedScreeningCandidateId)}
        screeningCandidateReady={directDemoScreeningCandidateReady}
        offerCandidateReady={directDemoOfferCandidateReady}
      />

      <ImportDemoWalkthrough
        isOpen={isDemoWorkspace && importDemoWalkthroughOpen}
        step={importDemoWalkthroughStep}
        completedSteps={importDemoCompletedSteps}
        screeningReviewOpen={Boolean(selectedScreeningCandidateId)}
        screeningCandidateReady={importDemoScreeningCandidateReady}
        offerCandidateReady={importDemoOfferCandidateReady}
      />

      {isDemoWorkspace && directDemoConfettiOpen && (
        <div className="pointer-events-none fixed inset-0 z-[9999] overflow-hidden bg-slate-950/20" aria-hidden="true">
          <div className="absolute inset-x-0 top-[18%] text-center">
            <div className="inline-flex rounded-full border border-cyan-300/30 bg-slate-950/80 px-5 py-3 text-xs font-black uppercase tracking-[0.2em] text-cyan-100 shadow-2xl shadow-cyan-950/40">Walkthrough complete</div>
          </div>
          {Array.from({ length: 42 }).map((_, index) => {
            const colors = ['#22d3ee', '#a78bfa', '#34d399', '#facc15', '#fb7185', '#ffffff'];
            const left = (index * 17) % 100;
            const delay = (index % 9) * 0.055;
            const drift = ((index % 7) - 3) * 22;
            return (
              <span
                key={`direct-demo-confetti-${index}`}
                className="direct-demo-confetti-piece"
                style={{
                  left: `${left}%`,
                  '--confetti-color': colors[index % colors.length],
                  '--confetti-delay': `${delay}s`,
                  '--confetti-drift': `${drift}px`,
                  '--confetti-spin': `${180 + (index % 6) * 70}deg`,
                }}
              />
            );
          })}
        </div>
      )}

      {isDemoWorkspace && postDemoFeaturePromptOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto bg-slate-950/90 px-3 py-4 text-white sm:px-4 sm:py-6" role="dialog" aria-modal="true" aria-label="Vision feature follow-up">
          <div className="flex max-h-[92vh] w-full max-w-4xl flex-col rounded-shell border border-cyan-300/20 bg-slate-950 shadow-2xl shadow-cyan-950/30">
            {!postDemoFeatureDetailsOpen ? (
              <div className="overflow-y-auto p-5 text-center sm:p-7">
                <div className="mx-auto inline-flex rounded-full border border-cyan-300/30 bg-cyan-300/10 px-4 py-2 text-micro font-black uppercase tracking-[0.22em] text-cyan-100">Demo complete</div>
                <h2 className="mx-auto mt-5 max-w-2xl text-2xl font-black leading-tight text-white sm:text-4xl">Want to know other ways Vision helps you make faster placements?</h2>
                <p className="mx-auto mt-4 max-w-2xl text-sm leading-7 text-slate-300">The walkthrough showed the core direct-sourcing path. Vision also has deeper workflow features that help recruiters reduce admin, improve client submissions, and track revenue.</p>
                <div className="mt-7 flex flex-wrap justify-center gap-3">
                  <button type="button" onClick={() => setPostDemoFeatureDetailsOpen(true)} className="rounded-full bg-cyan-400 px-7 py-4 text-xs font-black uppercase tracking-[0.18em] text-slate-950 transition hover:bg-cyan-300">
                    Now
                  </button>
                  <button type="button" onClick={() => { setPostDemoFeaturePromptOpen(false); setPostDemoFeatureDetailsOpen(false); }} className="rounded-full border border-white/15 bg-white/5 px-7 py-4 text-xs font-black uppercase tracking-[0.18em] text-slate-300 transition hover:bg-white/10 hover:text-white">
                    Later
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex min-h-0 flex-col">
                <div className="shrink-0 border-b border-white/10 p-5 sm:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div>
                    <div className="text-micro font-black uppercase tracking-[0.22em] text-cyan-300">Top Vision placement accelerators</div>
                    <h2 className="mt-3 text-xl font-black text-white sm:text-3xl">5 Vision-distinct features beyond the walkthrough</h2>
                  </div>
                  <button type="button" onClick={() => { setPostDemoFeaturePromptOpen(false); setPostDemoFeatureDetailsOpen(false); }} className="rounded-full border border-white/15 bg-white/5 px-5 py-3 text-micro font-black uppercase tracking-[0.16em] text-slate-300 transition hover:bg-white/10 hover:text-white">
                    Continue demo
                  </button>
                </div>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-5 sm:p-6">
                <div className="grid gap-3">
                  {visionDistinctFeatureHighlights.map((feature, index) => (
                    <div key={feature.title} className="rounded-card border border-white/10 bg-white/[0.04] p-3 sm:p-4">
                      <div className="flex gap-3">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-cyan-400 text-sm font-black text-slate-950">{index + 1}</div>
                        <div>
                          <div className="text-sm font-black text-white">{feature.title}</div>
                          <div className="mt-2 text-xs leading-5 text-slate-300 sm:text-sm sm:leading-6">{feature.body}</div>
                          <div className="mt-2 text-xs leading-5 text-cyan-100/85">{feature.detail}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                </div>
                <div className="shrink-0 border-t border-white/10 p-4 sm:p-5">
                <div className="flex justify-end">
                  <button type="button" onClick={() => { setPostDemoFeaturePromptOpen(false); setPostDemoFeatureDetailsOpen(false); }} className="rounded-full bg-cyan-400 px-6 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950 transition hover:bg-cyan-300">
                    Continue using demo
                  </button>
                </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="min-h-screen bg-[radial-gradient(circle_at_top,#11335f_0%,#020617_42%,#020617_100%)] text-slate-100">

        <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col px-3 pb-28 pt-4 sm:px-6 sm:pt-6 lg:px-8">

          <header data-direct-demo-target="workspace-header" className={`mb-5 rounded-shell border border-white/10 bg-slate-950/60 p-4 backdrop-blur sm:mb-6 sm:p-5 ${isDirectDemoTarget('workspace-header') ? directDemoHighlightClass : ''}`}>

            <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">

              <div>

                <div className="text-xs font-black uppercase tracking-[0.3em] text-cyan-300">Vision</div>

                <div className="mt-2 flex flex-wrap items-center gap-3">

                  <h1 className="text-xl font-black text-white sm:text-2xl">Recruiter Workspace</h1>

                  <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-meta uppercase tracking-[0.18em] text-slate-300">workspace</span>

                  <span className="rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1 text-meta uppercase tracking-[0.18em] text-cyan-200">{RBAC_ROLE_PRESETS[currentWorkspaceRole]?.label || 'Recruiter'}</span>

                  {isDemoWorkspace && (
                    <span className="rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1 text-meta uppercase tracking-[0.18em] text-amber-100">demo mode</span>
                  )}

                </div>

                <div className="mt-2 text-sm text-slate-400">{profile.name} · {profile.agency}</div>

              </div>



              <div className="flex flex-wrap items-center gap-2">

                <NotificationBell
                  notifications={notifications}
                  onMarkRead={handleMarkNotificationRead}
                  onMarkAllRead={handleMarkAllNotificationsRead}
                  onOpenNotification={handleOpenNotification}
                />

                <div className="rounded-full border border-emerald-500/20 bg-emerald-500/10 px-4 py-2 text-xs font-bold uppercase tracking-[0.18em] text-emerald-200">

                  {recruiterUser.email || 'Signed in'}

                </div>

                <button type="button" onClick={() => { if (isDemoWorkspace) { handleExitDemoWorkspace(); return; } setMembershipPreviewEnabled(false); clearAuthFormSensitiveFields({ keepEmail: false }); auth?.logout?.(); }} className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-slate-200 transition hover:border-white/20 hover:text-white">

                  <LogOut size={14} /> {isDemoWorkspace ? 'Exit demo' : 'Sign out'}

                </button>

              </div>

            </div>

          </header>

          {isDemoWorkspace && (
            <button
              type="button"
              onClick={handleCreateAccountFromDemo}
              className="mb-5 w-full rounded-shell border border-cyan-300/25 bg-[radial-gradient(circle_at_20%_0%,rgba(34,211,238,.18),transparent_32%),rgba(8,47,73,.55)] px-5 py-4 text-left shadow-xl shadow-cyan-950/10 transition hover:border-cyan-200/45 hover:bg-cyan-500/15 sm:mb-6"
            >
              <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <div className="text-micro font-black uppercase tracking-[0.22em] text-cyan-200">Ready for the real workspace?</div>
                  <div className="mt-2 text-lg font-black text-white">Switch from demo data to a real Vision account.</div>
                  <div className="mt-1 text-sm leading-6 text-slate-300">Create your agency workspace, invite paid seats, and start using Vision with your own roles, candidates, clients, and reporting.</div>
                </div>
                <span className="inline-flex shrink-0 items-center justify-center rounded-full bg-cyan-400 px-5 py-3 text-xs font-black uppercase tracking-[0.16em] text-slate-950">
                  Create account
                </span>
              </div>
            </button>
          )}

          <main className="flex-1">

            {isLoadingWorkspace ? (

              <div className="flex min-h-[62vh] items-center justify-center rounded-shell border border-white/10 bg-black/80 px-4 py-10 text-sm text-slate-300">

                <div className="grid w-full max-w-2xl justify-items-center gap-5 text-center">

                  <img src="/vision-official-logo.jpg" alt="Vision" className="w-full max-w-[460px] rounded-shell shadow-2xl shadow-cyan-950/30" />

                  <div className="inline-flex items-center gap-3 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-5 py-3 text-xs font-black uppercase tracking-[0.18em] text-cyan-100">

                    <Loader size={18} className="animate-spin" /> Establishing secure workspace

                  </div>
                </div>

              </div>

            ) : renderContent()}

          </main>



          <nav className="fixed bottom-3 left-1/2 z-30 flex w-[calc(100vw-1rem)] max-w-[880px] -translate-x-1/2 items-center justify-center rounded-shell border border-white/10 bg-slate-950/95 px-2 py-2 shadow-2xl backdrop-blur sm:bottom-6 sm:w-[min(94vw,880px)] sm:rounded-full sm:px-3 sm:py-3">

            <div className="flex min-w-0 flex-1 items-center justify-around gap-2">

              {[

                { id: 'pipeline', label: 'Pipeline', icon: Sparkles },

                { id: 'jobs', label: 'Jobs', icon: Briefcase },

                { id: 'agency', label: 'Agency', icon: User },

                { id: 'automations', label: 'Automate', icon: Zap },

              ].filter((tab) => canAccess(RBAC_PAGE_PERMISSIONS[tab.id])).map((tab) => {

                const Icon = tab.icon;

                const active = activeTab === tab.id;
                const guideTarget = tab.id === 'pipeline' ? 'nav-pipeline' : tab.id === 'jobs' ? 'nav-jobs' : undefined;

                return (

                  <button key={tab.id} type="button" data-direct-demo-target={guideTarget} onClick={() => handleWorkspaceNavigation(tab.id)} className={`flex min-w-0 flex-1 flex-col items-center rounded-card px-2 py-2 text-micro font-black uppercase tracking-[0.12em] transition sm:min-w-[78px] sm:flex-none sm:px-3 sm:tracking-[0.16em] ${active ? 'bg-cyan-500/15 text-cyan-300' : 'text-slate-500 hover:bg-white/5 hover:text-slate-200'} ${guideTarget && isDirectDemoTarget(guideTarget) ? directDemoHighlightClass : ''}`}>

                    <Icon size={18} />

                    <span className="mt-1">{tab.label}</span>

                  </button>

                );

              })}

            </div>

          </nav>

        </div>

      </div>

      {isDemoWorkspace && demoAccountAdOpen && (
        <div className="fixed inset-0 z-[9998] overflow-y-auto bg-[#020817] text-white" role="dialog" aria-modal="true" aria-label="Create a real Vision account">
          <div className="pointer-events-none fixed inset-0 overflow-hidden">
            <div className="demo-ad-grid absolute inset-0 opacity-45" />
            <div className="demo-ad-sweep absolute -top-20 left-0 h-[140vh] w-56 bg-gradient-to-r from-transparent via-cyan-200/25 to-transparent blur-sm" />
            <div className="absolute -left-24 top-10 h-96 w-96 rounded-full bg-cyan-400/30 blur-3xl" />
            <div className="absolute right-[-8rem] top-1/4 h-[34rem] w-[34rem] rounded-full bg-emerald-300/25 blur-3xl" />
            <div className="absolute bottom-[-9rem] left-1/3 h-[30rem] w-[30rem] rounded-full bg-violet-400/20 blur-3xl" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_52%_12%,rgba(34,211,238,.28),transparent_26%),linear-gradient(180deg,rgba(2,8,23,.08),rgba(2,8,23,.86))]" />
            <div className="absolute left-0 right-0 top-8 overflow-hidden border-y border-cyan-300/20 bg-cyan-300/10 py-2">
              <div className="demo-ad-ticker flex w-[200%] gap-8 whitespace-nowrap text-micro font-black uppercase tracking-[0.28em] text-cyan-100">
                <span>Live role to client-ready shortlist</span><span>Replace spreadsheet chaos</span><span>Real seats. Real pipeline. Real reporting.</span><span>Vision execution layer</span>
                <span>Live role to client-ready shortlist</span><span>Replace spreadsheet chaos</span><span>Real seats. Real pipeline. Real reporting.</span><span>Vision execution layer</span>
              </div>
            </div>
          </div>

          <div className="relative mx-auto flex min-h-screen w-full max-w-7xl items-center px-4 py-20 sm:px-6 lg:py-16">
            <div className="grid w-full gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:items-stretch">
              <section className="relative overflow-hidden rounded-shell border border-cyan-200/30 bg-slate-950/78 p-6 shadow-[0_30px_90px_rgba(8,145,178,.22)] backdrop-blur sm:p-8 lg:p-10">
                <div className="absolute right-[-3rem] top-[-3rem] h-36 w-36 rounded-full border border-cyan-200/25" />
                <div className="absolute bottom-8 right-8 hidden rounded-full border border-emerald-200/20 bg-emerald-300/10 px-4 py-2 text-micro font-black uppercase tracking-[0.2em] text-emerald-100 sm:block">Ad break</div>
                <div className="relative">
                  <div className="inline-flex rounded-full border border-cyan-300/35 bg-cyan-300/15 px-4 py-1.5 text-micro font-black uppercase tracking-[0.24em] text-cyan-50">Vision demo interruption</div>
                  <h2 className="mt-5 max-w-3xl text-5xl font-black leading-[0.92] text-white sm:text-6xl lg:text-7xl">Stop watching the trailer.</h2>
                  <div className="mt-4 inline-flex -rotate-1 rounded-card bg-cyan-300 px-4 py-2 text-xl font-black uppercase tracking-[0.12em] text-slate-950 shadow-[0_18px_45px_rgba(34,211,238,.35)] sm:text-2xl">Put Vision on your real desk.</div>
                  <p className="mt-6 max-w-2xl text-base leading-8 text-slate-200">The demo has done its job. Now let Vision replace the spreadsheet, email, and follow-up mess around your actual live roles, recruiters, clients, and candidates.</p>
                  <div className="mt-7 grid gap-3 text-sm font-bold text-slate-100 sm:grid-cols-3">
                    {['Your roles', 'Your seats', 'Your shortlist speed'].map((item) => (
                      <div key={item} className="rounded-card border border-white/10 bg-white/[0.06] px-4 py-3">{item}</div>
                    ))}
                  </div>
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <button type="button" onClick={handleCreateAccountFromDemo} className="group relative overflow-hidden rounded-full bg-cyan-300 px-8 py-5 text-sm font-black uppercase tracking-[0.22em] text-slate-950 shadow-[0_0_0_2px_rgba(255,255,255,.35),0_0_36px_rgba(34,211,238,.75),0_24px_70px_rgba(34,211,238,.42)] transition hover:bg-white focus:outline-none focus:ring-4 focus:ring-cyan-300/40">
                      <span className="absolute inset-y-0 -left-12 w-14 rotate-12 bg-white/70 blur-md transition-all duration-700 group-hover:left-[120%]" />
                      <span className="relative">Create real account</span>
                    </button>
                    <button type="button" onClick={() => setDemoAccountAdOpen(false)} className="rounded-full border border-white/20 bg-slate-950/45 px-6 py-4 text-xs font-black uppercase tracking-[0.18em] text-slate-200 transition hover:bg-white/10">
                      Keep exploring demo
                    </button>
                  </div>
                </div>
              </section>

              <section className="relative max-h-none min-h-[620px] overflow-hidden rounded-shell border border-white/10 bg-white/[0.06] p-4 shadow-2xl shadow-black/30 backdrop-blur lg:max-h-[calc(100vh-8rem)]">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_20%,rgba(34,211,238,.22),transparent_35%),radial-gradient(circle_at_80%_80%,rgba(16,185,129,.2),transparent_32%)]" />
                <div className="absolute left-6 top-6 z-10 rounded-full border border-white/10 bg-slate-950/80 px-3 py-1 text-micro font-black uppercase tracking-[0.18em] text-cyan-100">Live background</div>
                <div className="relative h-full overflow-y-auto pr-1 pt-10">
                  <div className="demo-ad-float rounded-shell border border-white/10 bg-slate-950/85 p-5 shadow-2xl shadow-cyan-950/20">
                    <div className="flex items-center justify-between gap-3">
                      <div>
                        <div className="text-micro font-black uppercase tracking-[0.2em] text-cyan-300">Live execution layer</div>
                        <div className="mt-2 text-xl font-black text-white">Senior Java Developer</div>
                        <div className="mt-1 text-sm text-slate-400">Northstar Fintech · Client-ready shortlist</div>
                      </div>
                      <div className="rounded-full bg-emerald-300 px-3 py-1 text-micro font-black uppercase tracking-[0.14em] text-slate-950">Ready</div>
                    </div>
                    <div className="mt-5 grid grid-cols-4 gap-2">
                      {[
                        ['Intake', '1'],
                        ['Screen', '5'],
                        ['Interview', '3'],
                        ['Offer', '2'],
                      ].map(([label, value]) => (
                        <div key={label} className="rounded-card border border-white/10 bg-white/[0.04] p-3">
                          <div className="text-2xl font-black text-white">{value}</div>
                          <div className="mt-1 text-micro font-black uppercase tracking-[0.14em] text-slate-500">{label}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mt-5 grid gap-3">
                    {[
                      ['Role intake locked', 'Brief, criteria, client context'],
                      ['Screening evidence captured', 'Answers and recruiter notes stay attached'],
                      ['Submission pack ready', 'Shortlist rationale prepared for client'],
                      ['Owner reporting wakes up', 'Activity and speed-to-shortlist become visible'],
                      ['ATS handoff stays clean', 'Bullhorn gets updated after the execution work is clear'],
                    ].map(([title, body], index) => (
                      <div key={title} className="flex items-center gap-3 rounded-card border border-white/10 bg-slate-950/75 p-4 shadow-lg shadow-black/10" style={{ transform: `translateX(${(index % 3) * 14}px)` }}>
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-cyan-400/15 text-sm font-black text-cyan-200">{index + 1}</div>
                        <div>
                          <div className="text-sm font-black text-white">{title}</div>
                          <div className="text-xs text-slate-400">{body}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-5 rounded-shell border border-emerald-300/20 bg-emerald-300/10 p-5">
                    <div className="text-micro font-black uppercase tracking-[0.2em] text-emerald-200">Character</div>
                    <div className="mt-2 text-2xl font-black text-white">The shortlist engine is warmed up.</div>
                    <p className="mt-2 text-sm leading-6 text-emerald-50/80">Right now it is rehearsing. Create the account and put it on the real agency floor.</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        </div>
      )}


      {/* ── Submit to Client Modal ── */}
      {false && submittalCandidateId && (() => {
        const subCand = candidates.find((c) => c.id === submittalCandidateId);
        const prevSubmittals = Array.isArray(subCand?.submittals) ? subCand.submittals : [];
        const clientNames = [...new Set((clientPortfolio || []).map((cl) => cl.name).filter(Boolean))];
        const clientMatchedContacts = contacts.filter((ct) => {
          const cn = (ct.companyName || ct.company || '').toLowerCase();
          return submittalClientName.trim() && cn.includes(submittalClientName.trim().toLowerCase());
        });
        const OUTCOME_OPTIONS = [
          { value: 'pending', label: 'Pending — awaiting response' },
          { value: 'interview_requested', label: 'Interview requested' },
          { value: 'shortlisted', label: 'Shortlisted by client' },
          { value: 'rejected', label: 'Rejected by client' },
          { value: 'no_response', label: 'No response' },
          { value: 'placed', label: 'Placed / Hired' },
        ];
        const OUTCOME_COLORS = { pending: 'text-amber-300', interview_requested: 'text-cyan-300', shortlisted: 'text-violet-300', rejected: 'text-rose-300', no_response: 'text-slate-400', placed: 'text-emerald-300' };
        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSubmittalCandidateId(null)}>
            <div className="w-full max-w-lg rounded-shell border border-white/10 bg-slate-900 shadow-2xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between gap-3 px-6 pt-6 pb-4 border-b border-white/[0.06] shrink-0">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-violet-300">Log Submittal</div>
                  {subCand && <div className="mt-1 text-sm font-bold text-white">{subCand.name}</div>}
                  {subCand && (subCand.matchedJobTitle || subCand.role) && (
                    <div className="mt-0.5 text-meta text-slate-400">{subCand.matchedJobTitle || subCand.role}</div>
                  )}
                </div>
                <button type="button" onClick={() => setSubmittalCandidateId(null)} className="rounded-ctl border border-white/10 p-1.5 text-slate-400 hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22z"/></svg>
                </button>
              </div>

              <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
                {/* Previous submittals */}
                {prevSubmittals.length > 0 && (
                  <div className="rounded-card border border-white/[0.08] bg-white/[0.03] px-4 py-3">
                    <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-2">Previously submitted</div>
                    <div className="space-y-2">
                      {[...prevSubmittals].reverse().map((s) => (
                        <div key={s.id} className="flex flex-col gap-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-meta font-semibold text-slate-300">{s.clientName}{s.contactName ? <span className="text-slate-500"> · {s.contactName}</span> : null}</span>
                            <span className="text-micro text-slate-500">{new Date(s.submittedAt).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                          </div>
                          {s.jobTitle && <div className="text-micro text-slate-600">{s.jobTitle}</div>}
                          <div className="flex items-center gap-2 flex-wrap">
                            {s.outcome && s.outcome !== 'pending' && <span className={`text-micro font-semibold ${OUTCOME_COLORS[s.outcome] || 'text-slate-400'}`}>{OUTCOME_OPTIONS.find((o) => o.value === s.outcome)?.label || s.outcome}</span>}
                            {s.submittedBy && <span className="text-micro text-slate-600">by {s.submittedBy}</span>}
                          </div>
                          {s.feedback && <div className="text-micro text-slate-500 italic">"{s.feedback}"</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client / Company */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Client / Company <span className="text-rose-400">*</span></div>
                  <input
                    list="submittal-clients-list"
                    value={submittalClientName}
                    onChange={(e) => { setSubmittalClientName(e.target.value); setSubmittalContactId(''); setSubmittalContactName(''); }}
                    placeholder="e.g. Accenture"
                    className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                  />
                  <datalist id="submittal-clients-list">
                    {clientNames.map((n) => <option key={n} value={n} />)}
                  </datalist>
                </div>

                {/* Contact at client */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Sent to (contact)</div>
                  {clientMatchedContacts.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {clientMatchedContacts.map((ct) => (
                        <button
                          key={ct.id}
                          type="button"
                          onClick={() => { setSubmittalContactId(submittalContactId === ct.id ? '' : ct.id); setSubmittalContactName(submittalContactId === ct.id ? '' : (ct.name || ct.email || '')); }}
                          className={`flex flex-col items-start rounded-ctl border px-3 py-2 text-left transition ${submittalContactId === ct.id ? 'border-violet-400/60 bg-violet-500/10 text-violet-200' : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20'}`}
                        >
                          <span className="text-meta font-semibold truncate w-full">{ct.name || ct.email}</span>
                          {ct.role && <span className="text-micro text-slate-500 truncate w-full">{ct.role}</span>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      value={submittalContactName}
                      onChange={(e) => { setSubmittalContactName(e.target.value); setSubmittalContactId(''); }}
                      placeholder="Contact name (optional)"
                      className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                    />
                  )}
                </div>

                {/* Job */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Job / Role</div>
                  <select
                    value={submittalJobId}
                    onChange={(e) => setSubmittalJobId(e.target.value)}
                    className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white outline-none focus:border-violet-400/60"
                  >
                    <option value="">— select a job (optional) —</option>
                    {jobs.filter((j) => !submittalClientName.trim() || normalizeText(getJobCompanyName(j)).includes(normalizeText(submittalClientName))).map((j) => (
                      <option key={j.id} value={j.id}>{j.title}{getJobCompanyName(j) ? ` · ${getJobCompanyName(j)}` : ''}</option>
                    ))}
                    {jobs.filter((j) => submittalClientName.trim() && !normalizeText(getJobCompanyName(j)).includes(normalizeText(submittalClientName))).length > 0 && (
                      <optgroup label="Other jobs">
                        {jobs.filter((j) => submittalClientName.trim() && !normalizeText(getJobCompanyName(j)).includes(normalizeText(submittalClientName))).map((j) => (
                          <option key={j.id} value={j.id}>{j.title}{getJobCompanyName(j) ? ` · ${getJobCompanyName(j)}` : ''}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                {/* Submitted by */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Submitted by</div>
                  <input
                    value={submittalSubmittedBy}
                    onChange={(e) => setSubmittalSubmittedBy(e.target.value)}
                    placeholder={profile?.name || 'Recruiter name'}
                    className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                  />
                </div>

                {/* Outcome */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Outcome</div>
                  <div className="grid grid-cols-2 gap-2">
                    {OUTCOME_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSubmittalOutcome(opt.value)}
                        className={`rounded-ctl border px-3 py-2 text-left text-meta font-semibold transition ${submittalOutcome === opt.value ? 'border-violet-400/60 bg-violet-500/10 text-violet-200' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Client feedback */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Client feedback (optional)</div>
                  <textarea
                    value={submittalFeedback}
                    onChange={(e) => setSubmittalFeedback(e.target.value)}
                    rows={2}
                    placeholder="e.g. Strong profile but lacks fintech experience"
                    className="w-full resize-none rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                  />
                </div>

                {/* Internal note */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Internal note (optional)</div>
                  <textarea
                    value={submittalNote}
                    onChange={(e) => setSubmittalNote(e.target.value)}
                    rows={2}
                    placeholder="e.g. Sent via email — follow up Friday"
                    className="w-full resize-none rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                  />
                </div>
              </div>

              <div className="flex gap-3 px-6 py-4 border-t border-white/[0.06] shrink-0">
                <button type="button" onClick={() => setSubmittalCandidateId(null)} className="flex-1 rounded-card border border-white/10 bg-white/5 py-2.5 text-xs font-bold text-slate-400 hover:text-white">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!submittalClientName.trim() || isSavingSubmittal}
                  onClick={() => handleSubmitToClient(submittalCandidateId, submittalClientName, submittalNote)}
                  className="flex-1 rounded-card bg-violet-500 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-white transition hover:bg-violet-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSavingSubmittal ? 'Saving…' : 'Log Submittal'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Pipeline Reject Modal ── */}
      {rejectPipelineCandidateId && (() => {
        const rejCand = candidates.find((c) => c.id === rejectPipelineCandidateId);
        const recruiterName = profile?.name || agencySettings?.agencyName || 'Recruiter';
        const autoEmail = rejectReason
          ? PIPELINE_REJECT_EMAIL_TEMPLATES[rejectReason]?.(
              rejCand?.name?.split(' ')[0] || 'there',
              rejCand?.matchedJobTitle || rejCand?.role || 'the role'
            ) + `\n${recruiterName}`
          : '';
        // Auto-populate email body when reason is selected and body is still blank/auto
        const emailBodyValue = rejectEmailBody || autoEmail;
        const hasEmail = !!rejCand?.email;
        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setRejectPipelineCandidateId(null)}>
            <div className="w-full max-w-lg rounded-shell border border-white/10 bg-slate-900 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between gap-3 mb-5">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-rose-300">Candidate Outcome</div>
                  {rejCand && <div className="mt-1 text-sm font-bold text-white">{rejCand.name}</div>}
                  {rejCand && (rejCand.matchedJobTitle || rejCand.role) && (
                    <div className="mt-0.5 text-meta text-slate-400">{rejCand.matchedJobTitle || rejCand.role}</div>
                  )}
                  <div className="mt-2 max-w-sm text-meta leading-5 text-slate-500">Select the recruiter-approved reason before marking this candidate not selected. Vision prepares the update; the recruiter owns the decision.</div>
                </div>
                <button type="button" onClick={() => setRejectPipelineCandidateId(null)} className="rounded-ctl border border-white/10 p-1.5 text-slate-400 hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22z"/></svg>
                </button>
              </div>

              {/* Reason grid */}
              <div>
                <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-3">Feedback reason <span className="text-rose-400">*</span></div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(PIPELINE_REJECT_REASON_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => {
                        setRejectReason(key);
                        // Auto-fill email body with template when reason changes
                        const tmpl = PIPELINE_REJECT_EMAIL_TEMPLATES[key]?.(
                          rejCand?.name?.split(' ')[0] || 'there',
                          rejCand?.matchedJobTitle || rejCand?.role || 'the role'
                        );
                        setRejectEmailBody(tmpl ? tmpl + `\n${recruiterName}` : '');
                      }}
                      className={`rounded-card border px-3 py-2.5 text-left text-meta font-bold transition ${rejectReason === key ? 'border-rose-400 bg-rose-400/15 text-rose-200' : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Send feedback email toggle */}
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => setRejectSendEmail((v) => !v)}
                  className="flex items-center gap-3 w-full"
                >
                  <div className={`relative h-5 w-9 rounded-full border transition ${rejectSendEmail ? 'border-rose-400/60 bg-rose-500/30' : 'border-white/10 bg-white/5'}`}>
                    <div className={`absolute top-0.5 h-4 w-4 rounded-full transition-all ${rejectSendEmail ? 'left-4 bg-rose-400' : 'left-0.5 bg-slate-500'}`} />
                  </div>
                  <span className="text-meta font-bold text-slate-300">
                    Email this reason to the candidate
                    {!hasEmail && <span className="ml-2 text-micro font-normal text-amber-400">(no email on file)</span>}
                  </span>
                </button>
              </div>

              {/* Email body editor */}
              {rejectSendEmail && (
                <div className="mt-3">
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Email body</div>
                  <textarea
                    value={emailBodyValue}
                    onChange={(e) => setRejectEmailBody(e.target.value)}
                    rows={7}
                    className="w-full resize-none rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-meta leading-relaxed text-white placeholder-slate-600 outline-none focus:border-rose-400/50 font-mono"
                    placeholder="Email body will auto-fill when you select a reason above…"
                  />
                  {!hasEmail && (
                    <div className="mt-2 rounded-ctl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-meta text-amber-200">
                      No email address on this candidate's record — email will not send until you add one.
                    </div>
                  )}
                </div>
              )}

              <div className="mt-5 flex gap-3">
                <button type="button" onClick={() => setRejectPipelineCandidateId(null)} className="flex-1 rounded-card border border-white/10 bg-white/5 py-2.5 text-xs font-bold text-slate-400 hover:text-white">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!rejectReason || isSavingReject}
                  onClick={() => handlePipelineReject(rejectPipelineCandidateId, rejectReason, rejectSendEmail, emailBodyValue)}
                  className="flex-1 rounded-card bg-rose-600 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-white transition hover:bg-rose-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSavingReject ? 'Saving…' : rejectSendEmail ? 'Mark Not Selected + Email' : 'Mark Not Selected'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Send Offer Modal ── */}
      {sendOfferCandidateId && (() => {
        const offerCand = candidates.find((c) => c.id === sendOfferCandidateId);
        const isEdit = !!offerCand?.offer;
        const today = new Date().toISOString().slice(0, 10);
        const defaultExpiry = new Date(Date.now() + 5 * 24 * 3600 * 1000).toISOString().slice(0, 10);
        const currencies = ['USD', 'GBP', 'EUR', 'ZAR', 'AUD', 'CAD'];
        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSendOfferCandidateId(null)}>
            <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-shell border border-white/10 bg-slate-900 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between gap-3 mb-5">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-green-300">{isEdit ? 'Edit Offer' : 'Send Offer'}</div>
                  {offerCand && <div className="mt-1 text-sm font-bold text-white">{offerCand.name}</div>}
                  {offerCand && (offerCand.matchedJobTitle || offerCand.role) && (
                    <div className="mt-0.5 text-meta text-slate-400">{offerCand.matchedJobTitle || offerCand.role}</div>
                  )}
                </div>
                <button type="button" onClick={() => setSendOfferCandidateId(null)} className="rounded-ctl border border-white/10 p-1.5 text-slate-400 hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22z"/></svg>
                </button>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
              <div>
                <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Offer Amount <span className="text-rose-400">*</span></div>
                <div className="flex gap-2">
                  <select
                    value={offerDraft.currency}
                    onChange={(e) => setOfferDraft((d) => ({ ...d, currency: e.target.value }))}
                    className="w-24 rounded-card border border-white/10 bg-slate-950/60 px-3 py-3 text-sm text-white outline-none focus:border-green-400/60"
                  >
                    {currencies.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  <input
                    type="number"
                    min="0"
                    placeholder="e.g. 85000"
                    value={offerDraft.amount}
                    onChange={(e) => setOfferDraft((d) => ({ ...d, amount: e.target.value }))}
                    className={`flex-1 rounded-card border bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60 ${offerError && !offerDraft.amount ? 'border-rose-400/60' : 'border-white/10'}`}
                  />
                </div>
              </div>
              <div>
                <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Client email <span className="text-rose-400">*</span></div>
                <input value={offerDraft.clientEmail} onChange={(e) => setOfferDraft((d) => ({ ...d, clientEmail: e.target.value }))} placeholder="hiring.manager@company.com" className={`w-full rounded-card border bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60 ${offerError && !offerDraft.clientEmail.trim() ? 'border-rose-400/60' : 'border-white/10'}`} />
              </div>
              </div>

              {/* Dates */}
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Sent Date</div>
                  <input
                    type="date"
                    value={offerDraft.sentDate || today}
                    onChange={(e) => setOfferDraft((d) => ({ ...d, sentDate: e.target.value }))}
                    className="w-full rounded-card border border-white/10 bg-slate-950/60 px-3 py-3 text-sm text-white outline-none focus:border-green-400/60"
                  />
                </div>
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Expiry Date</div>
                  <input
                    type="date"
                    value={offerDraft.expiryDate || defaultExpiry}
                    onChange={(e) => setOfferDraft((d) => ({ ...d, expiryDate: e.target.value }))}
                    className="w-full rounded-card border border-white/10 bg-slate-950/60 px-3 py-3 text-sm text-white outline-none focus:border-green-400/60"
                  />
                </div>
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Employment start date <span className="text-rose-400">*</span></div>
                  <input type="date" value={offerDraft.startDate} onChange={(e) => setOfferDraft((d) => ({ ...d, startDate: e.target.value }))} className={`w-full rounded-card border bg-slate-950/60 px-3 py-3 text-sm text-white outline-none focus:border-green-400/60 ${offerError && !offerDraft.startDate ? 'border-rose-400/60' : 'border-white/10'}`} />
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Client contact name</div>
                  <input value={offerDraft.clientName} onChange={(e) => setOfferDraft((d) => ({ ...d, clientName: e.target.value }))} placeholder="Hiring manager" className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60" />
                </div>
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Client legal name <span className="text-rose-400">*</span></div>
                  <input value={offerDraft.clientLegalName} onChange={(e) => setOfferDraft((d) => ({ ...d, clientLegalName: e.target.value }))} placeholder="Registered company name" className={`w-full rounded-card border bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60 ${offerError && !offerDraft.clientLegalName.trim() ? 'border-rose-400/60' : 'border-white/10'}`} />
                </div>
                <div className="sm:col-span-2">
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Client address <span className="text-rose-400">*</span></div>
                  <input value={offerDraft.clientAddress} onChange={(e) => setOfferDraft((d) => ({ ...d, clientAddress: e.target.value }))} placeholder="Registered or operating address" className={`w-full rounded-card border bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60 ${offerError && !offerDraft.clientAddress.trim() ? 'border-rose-400/60' : 'border-white/10'}`} />
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Work location <span className="text-rose-400">*</span></div>
                  <input value={offerDraft.workLocation} onChange={(e) => setOfferDraft((d) => ({ ...d, workLocation: e.target.value }))} placeholder="Office, hybrid, remote" className={`w-full rounded-card border bg-slate-950/60 px-3 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60 ${offerError && !offerDraft.workLocation.trim() ? 'border-rose-400/60' : 'border-white/10'}`} />
                </div>
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Employment type</div>
                  <input value={offerDraft.employmentType} onChange={(e) => setOfferDraft((d) => ({ ...d, employmentType: e.target.value }))} placeholder="Permanent" className="w-full rounded-card border border-white/10 bg-slate-950/60 px-3 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60" />
                </div>
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Working hours <span className="text-rose-400">*</span></div>
                  <input value={offerDraft.workingHours} onChange={(e) => setOfferDraft((d) => ({ ...d, workingHours: e.target.value }))} placeholder="Mon-Fri, 08:00-17:00" className={`w-full rounded-card border bg-slate-950/60 px-3 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60 ${offerError && !offerDraft.workingHours.trim() ? 'border-rose-400/60' : 'border-white/10'}`} />
                </div>
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Pay frequency <span className="text-rose-400">*</span></div>
                  <input value={offerDraft.payFrequency} onChange={(e) => setOfferDraft((d) => ({ ...d, payFrequency: e.target.value }))} placeholder="Monthly" className={`w-full rounded-card border bg-slate-950/60 px-3 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60 ${offerError && !offerDraft.payFrequency.trim() ? 'border-rose-400/60' : 'border-white/10'}`} />
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                {[
                  ['Leave terms', 'leaveTerms', 'Annual, sick, family responsibility or applicable policy terms', true],
                  ['Notice terms', 'noticeTerms', 'Notice period or fixed-term end date', true],
                  ['Benefits and other payments', 'benefits', 'Bonus, allowances, medical, equity, in-kind benefits', false],
                  ['Overtime terms', 'overtimeTerms', 'Overtime rate or applicable policy', false],
                  ['Deductions', 'deductions', 'Authorized deductions or none', false],
                  ['Sector or council terms', 'sectorTerms', 'Collective, bargaining council, or sector terms', false],
                ].map(([label, key, placeholder, required]) => (
                  <div key={key}>
                    <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">{label}{required && <span className="text-rose-400 ml-0.5">*</span>}</div>
                    <textarea value={offerDraft[key]} onChange={(e) => setOfferDraft((d) => ({ ...d, [key]: e.target.value }))} rows={2} placeholder={placeholder} className={`w-full resize-y rounded-card border bg-slate-950/60 px-3 py-2.5 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60 ${required && offerError && !offerDraft[key]?.trim() ? 'border-rose-400/60' : 'border-white/10'}`} />
                  </div>
                ))}
              </div>

              <div className="mt-4">
                <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Custom negotiation terms</div>
                <textarea value={offerDraft.customNegotiation} onChange={(e) => setOfferDraft((d) => ({ ...d, customNegotiation: e.target.value, note: e.target.value }))} rows={3} placeholder="Negotiation points for the client PDF and AI email." className="w-full resize-y rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60" />
              </div>

              <div className="mt-4 rounded-card border border-white/10 bg-slate-950/40 p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500">Client email body</div>
                  <button type="button" disabled={isDraftingOfferEmail} onClick={() => handleDraftOfferNegotiationEmail(offerCand, offerDraft)} className="rounded-ctl border border-cyan-400/25 bg-cyan-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.12em] text-cyan-100 disabled:opacity-50">
                    {isDraftingOfferEmail ? 'Drafting...' : 'Write with AI'}
                  </button>
                </div>
                <textarea value={offerDraft.emailBody} onChange={(e) => setOfferDraft((d) => ({ ...d, emailBody: e.target.value }))} rows={6} placeholder="Write the client negotiation email manually or generate a draft with AI." className="mt-2 w-full resize-y rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-green-400/60" />
              </div>

              {offerError && (
                <div className="mt-4 rounded-card border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
                  {offerError}
                </div>
              )}

              <div className="mt-4 flex gap-3">
                <button type="button" onClick={() => { setSendOfferCandidateId(null); setOfferError(''); }} className="flex-1 rounded-card border border-white/10 bg-white/5 py-2.5 text-xs font-bold text-slate-400 hover:text-white">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={isSavingOffer}
                  onClick={() => handleSendOffer(sendOfferCandidateId, offerDraft)}
                  className="flex-1 rounded-card bg-green-600 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-white transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSavingOffer ? 'Sending...' : isEdit ? 'Send Updated Packet' : 'Send Offer Packet'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Submit to Client Modal ── */}
      {submittalCandidateId && (() => {
        const subCand = candidates.find((c) => c.id === submittalCandidateId);
        const prevSubmittals = Array.isArray(subCand?.submittals) ? subCand.submittals : [];
        const clientNames = [...new Set((clientPortfolio || []).map((cl) => cl.name).filter(Boolean))];
        const clientMatchedContacts = contacts.filter((ct) => {
          const cn = (ct.companyName || ct.company || '').toLowerCase();
          return submittalClientName.trim() && cn.includes(submittalClientName.trim().toLowerCase());
        });
        const OUTCOME_OPTIONS = [
          { value: 'pending', label: 'Pending — awaiting response' },
          { value: 'interview_requested', label: 'Interview requested' },
          { value: 'shortlisted', label: 'Shortlisted by client' },
          { value: 'rejected', label: 'Rejected by client' },
          { value: 'no_response', label: 'No response' },
          { value: 'placed', label: 'Placed / Hired' },
        ];
        const OUTCOME_COLORS = { pending: 'text-amber-300', interview_requested: 'text-cyan-300', shortlisted: 'text-violet-300', rejected: 'text-rose-300', no_response: 'text-slate-400', placed: 'text-emerald-300' };
        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setSubmittalCandidateId(null)}>
            <div className="w-full max-w-lg rounded-shell border border-white/10 bg-slate-900 shadow-2xl flex flex-col max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between gap-3 px-6 pt-6 pb-4 border-b border-white/[0.06] shrink-0">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-violet-300">Log Submittal</div>
                  {subCand && <div className="mt-1 text-sm font-bold text-white">{subCand.name}</div>}
                  {subCand && (subCand.matchedJobTitle || subCand.role) && (
                    <div className="mt-0.5 text-meta text-slate-400">{subCand.matchedJobTitle || subCand.role}</div>
                  )}
                </div>
                <button type="button" onClick={() => setSubmittalCandidateId(null)} className="rounded-ctl border border-white/10 p-1.5 text-slate-400 hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22z"/></svg>
                </button>
              </div>

              <div className="overflow-y-auto flex-1 px-6 py-4 space-y-4">
                {/* Previous submittals */}
                {prevSubmittals.length > 0 && (
                  <div className="rounded-card border border-white/[0.08] bg-white/[0.03] px-4 py-3">
                    <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-2">Previously submitted</div>
                    <div className="space-y-2">
                      {[...prevSubmittals].reverse().map((s) => (
                        <div key={s.id} className="flex flex-col gap-0.5">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-meta font-semibold text-slate-300">{s.clientName}{s.contactName ? <span className="text-slate-500"> · {s.contactName}</span> : null}</span>
                            <span className="text-micro text-slate-500">{new Date(s.submittedAt).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' })}</span>
                          </div>
                          {s.jobTitle && <div className="text-micro text-slate-600">{s.jobTitle}</div>}
                          <div className="flex items-center gap-2 flex-wrap">
                            {s.outcome && s.outcome !== 'pending' && <span className={`text-micro font-semibold ${OUTCOME_COLORS[s.outcome] || 'text-slate-400'}`}>{OUTCOME_OPTIONS.find((o) => o.value === s.outcome)?.label || s.outcome}</span>}
                            {s.submittedBy && <span className="text-micro text-slate-600">by {s.submittedBy}</span>}
                          </div>
                          {s.feedback && <div className="text-micro text-slate-500 italic">"{s.feedback}"</div>}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Client / Company */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Client / Company <span className="text-rose-400">*</span></div>
                  <input
                    list="submittal-clients-list-2"
                    value={submittalClientName}
                    onChange={(e) => { setSubmittalClientName(e.target.value); setSubmittalContactId(''); setSubmittalContactName(''); }}
                    placeholder="e.g. Accenture"
                    className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                  />
                  <datalist id="submittal-clients-list-2">
                    {clientNames.map((n) => <option key={n} value={n} />)}
                  </datalist>
                </div>

                {/* Contact at client */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Sent to (contact)</div>
                  {clientMatchedContacts.length > 0 ? (
                    <div className="grid grid-cols-2 gap-2">
                      {clientMatchedContacts.map((ct) => (
                        <button
                          key={ct.id}
                          type="button"
                          onClick={() => { setSubmittalContactId(submittalContactId === ct.id ? '' : ct.id); setSubmittalContactName(submittalContactId === ct.id ? '' : (ct.name || ct.email || '')); }}
                          className={`flex flex-col items-start rounded-ctl border px-3 py-2 text-left transition ${submittalContactId === ct.id ? 'border-violet-400/60 bg-violet-500/10 text-violet-200' : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20'}`}
                        >
                          <span className="text-meta font-semibold truncate w-full">{ct.name || ct.email}</span>
                          {ct.role && <span className="text-micro text-slate-500 truncate w-full">{ct.role}</span>}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      value={submittalContactName}
                      onChange={(e) => { setSubmittalContactName(e.target.value); setSubmittalContactId(''); }}
                      placeholder="Contact name (optional)"
                      className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                    />
                  )}
                </div>

                {/* Job */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Job / Role</div>
                  <select
                    value={submittalJobId}
                    onChange={(e) => setSubmittalJobId(e.target.value)}
                    className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white outline-none focus:border-violet-400/60"
                  >
                    <option value="">— select a job (optional) —</option>
                    {jobs.filter((j) => !submittalClientName.trim() || normalizeText(getJobCompanyName(j)).includes(normalizeText(submittalClientName))).map((j) => (
                      <option key={j.id} value={j.id}>{j.title}{getJobCompanyName(j) ? ` · ${getJobCompanyName(j)}` : ''}</option>
                    ))}
                    {jobs.filter((j) => submittalClientName.trim() && !normalizeText(getJobCompanyName(j)).includes(normalizeText(submittalClientName))).length > 0 && (
                      <optgroup label="Other jobs">
                        {jobs.filter((j) => submittalClientName.trim() && !normalizeText(getJobCompanyName(j)).includes(normalizeText(submittalClientName))).map((j) => (
                          <option key={j.id} value={j.id}>{j.title}{getJobCompanyName(j) ? ` · ${getJobCompanyName(j)}` : ''}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                {/* Submitted by */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Submitted by</div>
                  <input
                    value={submittalSubmittedBy}
                    onChange={(e) => setSubmittalSubmittedBy(e.target.value)}
                    placeholder={profile?.name || 'Recruiter name'}
                    className="w-full rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                  />
                </div>

                {/* Outcome */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Outcome</div>
                  <div className="grid grid-cols-2 gap-2">
                    {OUTCOME_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setSubmittalOutcome(opt.value)}
                        className={`rounded-ctl border px-3 py-2 text-left text-meta font-semibold transition ${submittalOutcome === opt.value ? 'border-violet-400/60 bg-violet-500/10 text-violet-200' : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Client feedback */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Client feedback (optional)</div>
                  <textarea
                    value={submittalFeedback}
                    onChange={(e) => setSubmittalFeedback(e.target.value)}
                    rows={2}
                    placeholder="e.g. Strong profile but lacks fintech experience"
                    className="w-full resize-none rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                  />
                </div>

                {/* Internal note */}
                <div>
                  <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Internal note (optional)</div>
                  <textarea
                    value={submittalNote}
                    onChange={(e) => setSubmittalNote(e.target.value)}
                    rows={2}
                    placeholder="e.g. Sent via email — follow up Friday"
                    className="w-full resize-none rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-violet-400/60"
                  />
                </div>
              </div>

              <div className="flex gap-3 px-6 py-4 border-t border-white/[0.06] shrink-0">
                <button type="button" onClick={() => setSubmittalCandidateId(null)} className="flex-1 rounded-card border border-white/10 bg-white/5 py-2.5 text-xs font-bold text-slate-400 hover:text-white">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!submittalClientName.trim() || isSavingSubmittal}
                  onClick={() => handleSubmitToClient(submittalCandidateId, submittalClientName, submittalNote)}
                  className="flex-1 rounded-card bg-violet-500 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-white transition hover:bg-violet-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSavingSubmittal ? 'Saving…' : 'Log Submittal'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Offer Decline Reason Modal ── */}
      {offerDeclineCandidateId && (() => {
        const decCand = candidates.find((c) => c.id === offerDeclineCandidateId);
        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setOfferDeclineCandidateId(null)}>
            <div className="w-full max-w-md rounded-shell border border-white/10 bg-slate-900 p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
              {/* Header */}
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-rose-300">Decline Offer</div>
                  {decCand && <div className="mt-1 text-sm font-bold text-white">{decCand.name}</div>}
                </div>
                <button type="button" onClick={() => setOfferDeclineCandidateId(null)} className="rounded-ctl border border-white/10 p-1.5 text-slate-400 hover:text-white">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22z"/></svg>
                </button>
              </div>

              <div className="mt-5">
                <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-3">Decline reason</div>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(OFFER_DECLINE_CATEGORY_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setOfferDeclineCategory(key)}
                      className={`rounded-card border px-3 py-2.5 text-left text-xs font-bold transition-colors ${offerDeclineCategory === key ? 'border-rose-400 bg-rose-400/15 text-rose-200' : 'border-white/10 bg-white/5 text-slate-300 hover:border-white/20'}`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4">
                <div className="text-micro uppercase tracking-[0.16em] text-slate-500 mb-2">Note (optional)</div>
                <textarea
                  value={offerDeclineNote}
                  onChange={(e) => setOfferDeclineNote(e.target.value)}
                  rows={3}
                  placeholder="Additional context..."
                  className="w-full resize-none rounded-card border border-white/10 bg-slate-950/60 px-4 py-3 text-sm text-white placeholder-slate-600 outline-none focus:border-rose-400/60"
                />
              </div>

              <div className="mt-4 flex gap-3">
                <button type="button" onClick={() => setOfferDeclineCandidateId(null)} className="flex-1 rounded-card border border-white/10 bg-white/5 py-2.5 text-xs font-bold text-slate-400 hover:text-white">
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!offerDeclineCategory || isSavingOfferDecline}
                  onClick={() => handleMarkOfferDeclined(offerDeclineCandidateId, offerDeclineCategory, offerDeclineNote)}
                  className="flex-1 rounded-card bg-rose-500 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-white transition hover:bg-rose-400 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSavingOfferDecline ? 'Saving…' : 'Record Decline'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}


      {/* ── Distribute Job Modal ── */}
      {distributeJob && (() => {
        const careersBase = getPublicAppBaseUrl();
        const recruiterId = recruiterUser?.uid || '';
        const careersPageUrl = `${careersBase}/careers/${recruiterId}`;
        const jobUrl = `${careersBase}/job/${recruiterId}/${encodeURIComponent(distributeJob.id)}`;
        const indeedFeedUrl = `${careersBase}/feeds/indeed/${recruiterId}.xml`;
        const zipFeedUrl = `${careersBase}/feeds/ziprecruiter/${recruiterId}.xml`;
        const pnetFeedUrl = `${careersBase}/feeds/pnet/${recruiterId}.xml`;
        const careers24FeedUrl = `${careersBase}/feeds/careers24/${recruiterId}.xml`;
        const adzunaFeedUrl = `${careersBase}/feeds/adzuna/${recruiterId}.xml`;
        const exportAgencyName = agencyBrand?.name || agencyBrand?.agencyName || profile?.agency || profile?.name || '';
        const jobBoardExport = buildJobBoardExport(distributeJob, { jobUrl, careersPageUrl }, exportAgencyName);
        const exportFields = [
          { key: 'export-title', label: 'Title', value: jobBoardExport.title },
          { key: 'export-company', label: 'Company', value: jobBoardExport.company },
          { key: 'export-location', label: 'Location', value: jobBoardExport.location },
          { key: 'export-type', label: 'Job Type', value: jobBoardExport.jobType },
          { key: 'export-salary', label: 'Salary', value: jobBoardExport.salary },
          { key: 'export-apply', label: 'Application URL', value: jobBoardExport.applyUrl },
          { key: 'export-summary', label: 'Description', value: jobBoardExport.summary },
          { key: 'export-requirements', label: 'Requirements', value: jobBoardExport.requirements || 'No separate requirements captured.' },
        ];

        const copyLink = (key, text) => {
          navigator.clipboard.writeText(text).then(() => {
            setDistributeCopied(key);
            setTimeout(() => setDistributeCopied(''), 2000);
          });
        };

        const handleRequestGoogleIndexing = async () => {
          setIndexingStatus('submitting');
          setIndexingResults([]);
          try {
            const fnClient = getFunctions(app, 'us-central1');
            const requestGoogleIndexing = httpsCallable(fnClient, 'requestGoogleIndexing');
            const result = await requestGoogleIndexing({ urls: [jobUrl, careersPageUrl] });
            const data = result.data || {};
            setIndexingStatus(data.status || 'done');
            setIndexingResults(data.results || []);
          } catch (error) {
            console.error('requestGoogleIndexing failed', error);
            setIndexingStatus('error');
            setIndexingResults([{ source: 'validation', url: jobUrl, status: 'error', reason: error?.message || 'Callable request failed' }]);
          }
        };

        const directLinks = [
          { key: 'careers', label: 'Careers Page', url: careersPageUrl },
          { key: 'job', label: 'Direct Job Link', url: jobUrl },
        ];

        const boards = [
          { key: 'pnet', label: 'PNet', flag: '🇿🇦', description: 'Manual posting is currently safest — use Job Board Export fields when PNet blocks feed access', url: pnetFeedUrl, submitUrl: 'https://www.pnet.co.za/employers', setupNote: 'If PNet approves XML feeds later, paste this URL in their employer feed setup.' },
          { key: 'careers24', label: 'Careers24', flag: '🇿🇦', description: 'Media24 network — submit feed URL once in Careers24 employer portal', url: careers24FeedUrl, submitUrl: 'https://www.careers24.com/employers', setupNote: 'careers24.com/employers → XML Feed → Paste URL → Save' },
          { key: 'indeed-feed', label: 'Indeed', flag: '🌍', description: 'Manual job entry is expected for most accounts — use Job Board Export and set the application URL to Vision', url: indeedFeedUrl, submitUrl: 'https://employers.indeed.com', setupNote: 'If Indeed approves XML feeds later, paste this URL in their job feed setup.' },
          { key: 'adzuna', label: 'Adzuna', flag: '🌍', description: 'Global aggregator (50+ partner sites) — register at publishers.adzuna.com', url: adzunaFeedUrl, submitUrl: 'https://publishers.adzuna.com', setupNote: 'publishers.adzuna.com → Add Feed → Paste URL → Submit' },
          { key: 'zip-feed', label: 'ZipRecruiter', flag: '🌍', description: 'US/international — submit feed URL in ZipRecruiter employer portal', url: zipFeedUrl, submitUrl: 'https://www.ziprecruiter.com/employers', setupNote: 'ziprecruiter.com/employers → Job Feed → Paste URL → Save' },
          { key: 'linkedin', label: 'LinkedIn Post', flag: '🌍', description: 'Share as a post to your LinkedIn feed — visible to connections & followers (not a LinkedIn Jobs listing)', url: jobUrl, openUrl: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(jobUrl)}` },
        ];

        return (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setDistributeJob(null)}>
            <div className="w-full max-w-4xl rounded-shell border border-white/10 bg-slate-900 p-6 shadow-2xl overflow-y-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="text-xs font-black uppercase tracking-[0.2em] text-violet-300">Distribute Role</div>
                  <div className="mt-1 text-base font-black text-white">{distributeJob.title}</div>
                  <div className="mt-0.5 text-sm text-slate-400">{distributeJob.companyName || distributeJob.company || ''}</div>
                </div>
                <button type="button" onClick={() => setDistributeJob(null)} className="shrink-0 rounded-ctl border border-white/10 bg-white/5 p-1.5 text-slate-400 hover:text-white">
                  <X size={16} />
                </button>
              </div>

              {/* Direct Links */}
              <div className="mt-6">
                <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-500 mb-3">Direct Links</div>
                <div className="space-y-2">
                  {directLinks.map(({ key, label, url }) => (
                    <div key={key} className="flex items-center gap-3 rounded-card border border-white/10 bg-slate-950/60 px-4 py-3">
                      <div className="min-w-0 flex-1">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-400">{label}</div>
                        <div className="mt-1 truncate text-xs text-slate-300">{url}</div>
                      </div>
                      <div className="flex shrink-0 gap-2">
                        <button type="button" onClick={() => copyLink(key, url)} className={`rounded-full px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] transition ${distributeCopied === key ? 'bg-emerald-500 text-white' : 'border border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:text-white'}`}>
                          {distributeCopied === key ? 'Copied!' : 'Copy'}
                        </button>
                        <a href={url} target="_blank" rel="noopener noreferrer" className="rounded-full border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-300 transition hover:border-white/20 hover:text-white">
                          Open
                        </a>
                      </div>
                    </div>
                  ))}

                  <div className="grid gap-3 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
                    {/* Google for Jobs */}
                    <div className="flex items-center gap-3 rounded-card border border-white/10 bg-slate-950/60 px-4 py-3">
                      <Globe size={14} className="shrink-0 text-cyan-300" />
                      <div className="min-w-0 flex-1">
                        <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-400">Google for Jobs + Bing</div>
                        <div className="mt-1 text-xs text-slate-400">Submit the job page to Google Indexing API. Job and careers links go to IndexNow.</div>
                      </div>
                      <button type="button" onClick={handleRequestGoogleIndexing} disabled={indexingStatus === 'submitting'} className="shrink-0 rounded-full border border-cyan-400/20 bg-cyan-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-cyan-200 transition hover:border-cyan-300/40 hover:text-cyan-100 disabled:cursor-not-allowed disabled:opacity-60">
                        {indexingStatus === 'submitting' ? 'Submitting...' : 'Index Now'}
                      </button>
                    </div>

                    {/* Job Board Export */}
                    <div className="rounded-card border border-emerald-400/15 bg-emerald-500/[0.06] px-4 py-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 text-micro font-black uppercase tracking-[0.16em] text-emerald-200">
                            <FileText size={13} /> Job Board Export
                          </div>
                          <div className="mt-1 text-xs text-slate-400">Copy clean fields for manual Indeed, PNet, LinkedIn, and other board entries.</div>
                        </div>
                        <button type="button" onClick={() => copyLink('export-full', jobBoardExport.fullAdvert)} className={`shrink-0 rounded-full px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] transition ${distributeCopied === 'export-full' ? 'bg-emerald-500 text-white' : 'border border-emerald-400/20 bg-emerald-500/10 text-emerald-200 hover:border-emerald-300/40 hover:text-emerald-100'}`}>
                          {distributeCopied === 'export-full' ? 'Copied!' : 'Copy Advert'}
                        </button>
                      </div>
                      <div className="mt-3 grid gap-2 sm:grid-cols-2">
                        {exportFields.map(({ key, label, value }) => (
                          <button key={key} type="button" onClick={() => copyLink(key, value)} className={`min-w-0 rounded-ctl border px-3 py-2 text-left transition ${distributeCopied === key ? 'border-emerald-400/40 bg-emerald-500/15' : 'border-white/10 bg-slate-950/50 hover:border-white/20'}`}>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-micro font-black uppercase tracking-[0.14em] text-slate-500">{label}</span>
                              <span className="text-micro font-black uppercase tracking-[0.14em] text-emerald-200">{distributeCopied === key ? 'Copied' : 'Copy'}</span>
                            </div>
                            <div className="mt-1 truncate text-meta text-slate-300">{value}</div>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                  {indexingStatus !== 'idle' && indexingStatus !== 'submitting' && (
                    <div className={`rounded-card px-4 py-3 text-xs font-semibold ${indexingStatus === 'done' ? 'border border-emerald-500/20 bg-emerald-500/10 text-emerald-200' : indexingStatus === 'error' ? 'border border-rose-500/20 bg-rose-500/10 text-rose-200' : 'border border-amber-500/20 bg-amber-500/10 text-amber-200'}`}>
                      {indexingStatus === 'done' ? 'Job page submitted to Google and discovery links submitted to IndexNow.' : indexingStatus === 'partial' ? 'Some indexing submissions completed, others need attention.' : indexingStatus === 'skipped' ? 'Indexing was skipped because no supported URL was submitted.' : 'Indexing request failed. Try again later.'}
                      {indexingResults.length > 0 && (
                        <ul className="mt-2 space-y-1">
                          {indexingResults.map((r, i) => <li key={i} className="truncate opacity-70">{r.source === 'google' ? 'Google' : r.source === 'indexnow' ? 'IndexNow' : 'Validation'} {r.url || 'request'}: {r.status}{r.reason ? ` (${r.reason})` : ''}</li>)}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Job Boards */}
              <div className="mt-6">
                <div className="text-micro font-black uppercase tracking-[0.2em] text-slate-500 mb-2">Job Boards</div>
                <div className="mb-3 rounded-card border border-violet-500/20 bg-violet-500/[0.07] px-4 py-3">
                  <div className="text-micro font-black uppercase tracking-[0.16em] text-violet-300 mb-1">Manual boards need clean copy</div>
                  <div className="text-meta text-slate-400 leading-relaxed">Use Job Board Export for Indeed and PNet manual forms. Feed URLs remain available for boards that approve XML ingestion.</div>
                </div>
                <div className="space-y-2">
                  {boards.map(({ key, label, flag, description, url, openUrl, submitUrl, setupNote }) => (
                    <div key={key} className="rounded-card border border-white/10 bg-slate-950/60 px-4 py-3">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            {flag && <span className="text-sm">{flag}</span>}
                            <div className="text-micro font-black uppercase tracking-[0.16em] text-slate-300">{label}</div>
                          </div>
                          <div className="mt-0.5 text-meta text-slate-500">{description}</div>
                          <div className="mt-1 truncate text-meta text-slate-400 font-mono">{url}</div>
                        </div>
                        <div className="flex shrink-0 gap-2 pt-0.5">
                          <button type="button" onClick={() => copyLink(key, url)} className={`rounded-full px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] transition ${distributeCopied === key ? 'bg-emerald-500 text-white' : 'border border-white/10 bg-white/5 text-slate-300 hover:border-white/20 hover:text-white'}`}>
                            {distributeCopied === key ? '✓ Copied' : 'Copy URL'}
                          </button>
                          {(openUrl || submitUrl) && (
                            <a href={openUrl || submitUrl} target="_blank" rel="noopener noreferrer" className="rounded-full border border-violet-400/20 bg-violet-500/10 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-violet-200 transition hover:border-violet-300/40 hover:text-violet-100">
                              {openUrl ? 'Share' : 'Submit →'}
                            </a>
                          )}
                        </div>
                      </div>
                      {setupNote && (
                        <div className="mt-2 rounded-ctl border border-white/[0.06] bg-white/[0.03] px-3 py-2 text-micro text-slate-500">
                          <span className="text-slate-400 font-semibold">How: </span>{setupNote}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-5 flex justify-end">
                <button type="button" onClick={() => setDistributeJob(null)} className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-black uppercase tracking-[0.16em] text-slate-400 hover:border-white/20 hover:text-white">
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}


      {/* ── EEOC Self-ID Editor Flyout ── */}
      {eeocEditCandidateId && (
        <div className="fixed inset-0 z-50 flex">
          <button
            type="button"
            aria-label="Close EEO editor"
            className="absolute inset-0 bg-black/60 cursor-default"
            onClick={() => { setEeocEditCandidateId(null); setEeocDraft({}); }}
          />
          <div className="absolute right-0 top-0 h-full w-full max-w-md flex flex-col border-l border-white/10 bg-slate-950/98 shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-white/10 sticky top-0 bg-slate-950/98 z-10">
              <div>
                <div className="text-sm font-black uppercase tracking-[0.18em] text-rose-300">EEO Self-Identification</div>
                <div className="mt-0.5 text-meta text-slate-500">Voluntary · Confidential · EEOC / OFCCP compliant</div>
              </div>
              <button
                type="button"
                onClick={() => { setEeocEditCandidateId(null); setEeocDraft({}); }}
                className="rounded-ctl border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-400 hover:border-white/20 hover:text-slate-200"
              >
                Cancel
              </button>
            </div>

            <div className="flex-1 px-5 py-5 space-y-6">

              <div className="rounded-card border border-white/[0.06] bg-slate-900/40 px-4 py-3 text-micro leading-5 text-slate-500">
                The following questions are voluntary. Your responses will be used solely for EEOC / federal EEO-1 reporting and will not affect hiring decisions (29 CFR Part 1602).
              </div>

              {/* Gender */}
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-300 mb-3">Gender Identity</div>
                <div className="space-y-2">
                  {[
                    { value: 'man', label: 'Man' },
                    { value: 'woman', label: 'Woman' },
                    { value: 'nonbinary', label: 'Non-binary / Non-conforming' },
                    { value: 'self_describe', label: 'I want to self-describe' },
                    { value: 'not_disclosed', label: 'Prefer not to answer' },
                  ].map((opt) => (
                    <label key={opt.value} className={`flex items-center gap-3 cursor-pointer rounded-card border px-4 py-3 transition-colors ${eeocDraft.gender === opt.value ? 'border-rose-400/40 bg-rose-500/10' : 'border-white/10 bg-white/[0.03] hover:border-white/20'}`}>
                      <input
                        type="radio"
                        name="eeoc_gender"
                        value={opt.value}
                        checked={eeocDraft.gender === opt.value}
                        onChange={() => setEeocDraft((d) => ({ ...d, gender: opt.value }))}
                        className="sr-only"
                      />
                      <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 transition-colors ${eeocDraft.gender === opt.value ? 'border-rose-400 bg-rose-400' : 'border-white/20'}`} />
                      <span className="text-sm text-slate-300">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Race / Ethnicity */}
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-300 mb-2">Race / Ethnicity</div>
                <div className="text-meta text-slate-600 mb-3">Please identify with one or more of the following groups per EEO-1 Category definitions.</div>
                <div className="space-y-2">
                  {[
                    { value: 'hispanic', label: 'Hispanic or Latino' },
                    { value: 'white', label: 'White (Not Hispanic or Latino)' },
                    { value: 'black', label: 'Black or African American (Not Hispanic or Latino)' },
                    { value: 'native', label: 'American Indian or Alaska Native (Not Hispanic or Latino)' },
                    { value: 'asian', label: 'Asian (Not Hispanic or Latino)' },
                    { value: 'pacific', label: 'Native Hawaiian or Other Pacific Islander (Not Hispanic or Latino)' },
                    { value: 'two_or_more', label: 'Two or More Races (Not Hispanic or Latino)' },
                    { value: 'not_disclosed', label: 'Prefer not to answer' },
                  ].map((opt) => (
                    <label key={opt.value} className={`flex items-center gap-3 cursor-pointer rounded-card border px-4 py-2.5 transition-colors ${eeocDraft.race === opt.value ? 'border-rose-400/40 bg-rose-500/10' : 'border-white/10 bg-white/[0.03] hover:border-white/20'}`}>
                      <input
                        type="radio"
                        name="eeoc_race"
                        value={opt.value}
                        checked={eeocDraft.race === opt.value}
                        onChange={() => setEeocDraft((d) => ({ ...d, race: opt.value }))}
                        className="sr-only"
                      />
                      <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 transition-colors ${eeocDraft.race === opt.value ? 'border-rose-400 bg-rose-400' : 'border-white/20'}`} />
                      <span className="text-xs text-slate-300 leading-5">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Veteran Status */}
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-300 mb-3">Protected Veteran Status</div>
                <div className="space-y-2">
                  {[
                    { value: 'not_veteran', label: 'I am not a protected veteran' },
                    { value: 'active', label: 'Active Duty Wartime or Campaign Badge Veteran' },
                    { value: 'reserve', label: 'Armed Forces Service Medal Veteran' },
                    { value: 'separated', label: 'Disabled / Recently Separated Veteran' },
                    { value: 'not_disclosed', label: 'Prefer not to answer' },
                  ].map((opt) => (
                    <label key={opt.value} className={`flex items-center gap-3 cursor-pointer rounded-card border px-4 py-2.5 transition-colors ${eeocDraft.veteran === opt.value ? 'border-rose-400/40 bg-rose-500/10' : 'border-white/10 bg-white/[0.03] hover:border-white/20'}`}>
                      <input
                        type="radio"
                        name="eeoc_veteran"
                        value={opt.value}
                        checked={eeocDraft.veteran === opt.value}
                        onChange={() => setEeocDraft((d) => ({ ...d, veteran: opt.value }))}
                        className="sr-only"
                      />
                      <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 transition-colors ${eeocDraft.veteran === opt.value ? 'border-rose-400 bg-rose-400' : 'border-white/20'}`} />
                      <span className="text-xs text-slate-300 leading-5">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Disability Status */}
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-300 mb-3">Disability Status (Section 503)</div>
                <div className="space-y-2">
                  {[
                    { value: 'no', label: 'No, I do not have a disability' },
                    { value: 'yes', label: 'Yes, I have a disability (or have had one in the past)' },
                    { value: 'not_disclosed', label: 'Prefer not to answer' },
                  ].map((opt) => (
                    <label key={opt.value} className={`flex items-center gap-3 cursor-pointer rounded-card border px-4 py-2.5 transition-colors ${eeocDraft.disability === opt.value ? 'border-rose-400/40 bg-rose-500/10' : 'border-white/10 bg-white/[0.03] hover:border-white/20'}`}>
                      <input
                        type="radio"
                        name="eeoc_disability"
                        value={opt.value}
                        checked={eeocDraft.disability === opt.value}
                        onChange={() => setEeocDraft((d) => ({ ...d, disability: opt.value }))}
                        className="sr-only"
                      />
                      <div className={`w-3.5 h-3.5 rounded-full border-2 shrink-0 transition-colors ${eeocDraft.disability === opt.value ? 'border-rose-400 bg-rose-400' : 'border-white/20'}`} />
                      <span className="text-xs text-slate-300">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

            </div>

            {/* Footer */}
            <div className="sticky bottom-0 border-t border-white/10 bg-slate-950/98 px-5 py-4 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={() => { setEeocDraft({}); }}
                className="rounded-ctl border border-white/10 bg-white/5 px-4 py-2 text-meta font-black uppercase tracking-[0.14em] text-slate-400 hover:border-white/20"
              >
                Clear all
              </button>
              <button
                type="button"
                disabled={isSavingEeoc}
                onClick={handleSaveEeocSelfId}
                className="rounded-ctl border border-rose-400/30 bg-rose-500/15 px-5 py-2 text-meta font-black uppercase tracking-[0.14em] text-rose-200 transition hover:border-rose-300/40 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {isSavingEeoc ? 'Saving…' : 'Save Self-ID'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── QoH Score Entry Drawer ── */}
      {qohEditCandidateId && (() => {
        const _qohCand = candidates.find((c) => c.id === qohEditCandidateId);
        return (
          <div className="fixed inset-0 z-50 flex">
            <button
              type="button"
              aria-label="Close QoH editor"
              className="absolute inset-0 cursor-default bg-black/60"
              onClick={() => { setQohEditCandidateId(null); setQohDraft({ score90: '', score180: '', notes: '' }); }}
            />
            <div className="absolute right-0 top-0 flex h-full w-full max-w-sm flex-col overflow-y-auto border-l border-white/10 bg-slate-950/98 shadow-2xl">
              <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-white/10 bg-slate-950/98 px-5 py-4">
                <div>
                  <div className="text-sm font-black uppercase tracking-[0.18em] text-emerald-300">Quality of Hire</div>
                  <div className="mt-0.5 text-meta text-slate-500">{_qohCand?.name || 'Candidate'} &middot; {_qohCand?.role || _qohCand?.matchedJobTitle || 'Unknown role'}</div>
                </div>
                <button
                  type="button"
                  onClick={() => { setQohEditCandidateId(null); setQohDraft({ score90: '', score180: '', notes: '' }); }}
                  className="rounded-ctl border border-white/10 bg-white/5 px-3 py-1.5 text-micro font-black uppercase tracking-[0.14em] text-slate-400 transition-colors hover:border-white/20 hover:text-slate-200"
                >
                  Cancel
                </button>
              </div>
      
              <div className="flex-1 space-y-5 px-5 py-5">
                <div className="rounded-card border border-white/[0.06] bg-slate-900/40 px-4 py-3 text-micro leading-5 text-slate-500">
                  Rate this hire&apos;s on-the-job performance after 90 and/or 180 days. Scores power Quality of Hire analytics across sources and recruiters.
                </div>
      
                {/* 90-day score */}
                <div>
                  <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-300">90-Day Performance Score</div>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setQohDraft((d) => ({ ...d, score90: String(d.score90) === String(n) ? '' : String(n) }))}
                        className={`h-9 w-9 rounded-ctl border text-sm font-black transition-colors ${String(qohDraft.score90) === String(n) ? (n >= 7 ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200' : n >= 5 ? 'border-amber-400 bg-amber-500/20 text-amber-200' : 'border-rose-400 bg-rose-500/20 text-rose-200') : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {qohDraft.score90 && (
                    <div className={`mt-2 text-meta font-bold ${Number(qohDraft.score90) >= 8 ? 'text-emerald-300' : Number(qohDraft.score90) >= 6 ? 'text-emerald-400' : Number(qohDraft.score90) >= 4 ? 'text-amber-300' : 'text-rose-400'}`}>
                      {Number(qohDraft.score90) >= 8 ? 'Excellent' : Number(qohDraft.score90) >= 6 ? 'Good' : Number(qohDraft.score90) >= 4 ? 'Acceptable' : 'Needs improvement'} &middot; {qohDraft.score90}/10
                    </div>
                  )}
                </div>
      
                {/* 180-day score */}
                <div>
                  <div className="mb-3 text-xs font-black uppercase tracking-[0.18em] text-slate-300">
                    180-Day Performance Score <span className="text-micro font-normal normal-case tracking-normal text-slate-600">(optional)</span>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setQohDraft((d) => ({ ...d, score180: String(d.score180) === String(n) ? '' : String(n) }))}
                        className={`h-9 w-9 rounded-ctl border text-sm font-black transition-colors ${String(qohDraft.score180) === String(n) ? (n >= 7 ? 'border-emerald-400 bg-emerald-500/20 text-emerald-200' : n >= 5 ? 'border-amber-400 bg-amber-500/20 text-amber-200' : 'border-rose-400 bg-rose-500/20 text-rose-200') : 'border-white/10 bg-white/5 text-slate-400 hover:border-white/20'}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                  {qohDraft.score180 && (
                    <div className={`mt-2 text-meta font-bold ${Number(qohDraft.score180) >= 8 ? 'text-emerald-300' : Number(qohDraft.score180) >= 6 ? 'text-emerald-400' : Number(qohDraft.score180) >= 4 ? 'text-amber-300' : 'text-rose-400'}`}>
                      {Number(qohDraft.score180) >= 8 ? 'Excellent' : Number(qohDraft.score180) >= 6 ? 'Good' : Number(qohDraft.score180) >= 4 ? 'Acceptable' : 'Needs improvement'} &middot; {qohDraft.score180}/10
                    </div>
                  )}
                </div>
      
                {/* Notes */}
                <div>
                  <div className="mb-2 text-xs font-black uppercase tracking-[0.18em] text-slate-300">
                    Notes <span className="text-micro font-normal normal-case tracking-normal text-slate-600">(optional)</span>
                  </div>
                  <textarea
                    rows={3}
                    value={qohDraft.notes}
                    onChange={(e) => setQohDraft((d) => ({ ...d, notes: e.target.value }))}
                    placeholder="e.g. Promoted at 4 months, high client satisfaction, recommended for senior track&hellip;"
                    className="w-full resize-none rounded-card border border-white/10 bg-slate-900/80 px-4 py-3 text-sm text-slate-200 placeholder-slate-600 transition-colors focus:border-emerald-400/50 focus:outline-none"
                  />
                </div>
              </div>
      
              {/* Footer */}
              <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-white/10 bg-slate-950/98 px-5 py-4">
                <div className="text-micro text-slate-600">{_qohCand?.source ? 'Source: ' + _qohCand.source : ''}</div>
                <button
                  type="button"
                  disabled={(!qohDraft.score90 && !qohDraft.score180) || isSavingQoh}
                  onClick={handleSaveQoh}
                  className="rounded-ctl border border-emerald-400/30 bg-emerald-500/15 px-5 py-2 text-meta font-black uppercase tracking-[0.14em] text-emerald-200 transition hover:border-emerald-300/40 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isSavingQoh ? 'Saving\u2026' : 'Save Score'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      

      <JobAtsModal

        job={selectedJobPanel}

        snapshot={selectedJobAtsSnapshot}

        notesDraft={jobNotesDraft}

        isSavingNotes={isSavingJobNotes}

        sequenceRuns={selectedJobSequenceRuns}

        sequenceAnalytics={selectedJobSequenceAnalytics}

        sequenceControls={sequenceControls}

        sequenceSuppressions={selectedJobSequenceSuppressions}

        sequenceTasks={selectedJobSequenceTasks}

        sequenceTaskNotes={sequenceTaskNotes}

        sequenceNotice={sequenceNotice}

        activeSequenceTargetKey={activeSequenceTargetKey}

        activeSequenceTaskId={activeSequenceTaskId}

        activeSequenceApiTaskId={activeSequenceApiTaskId}

        activeSequenceSuppressionId={activeSequenceSuppressionId}

        isProcessingSequenceRuns={isProcessingSequenceRuns}

        isSavingSequenceControls={isSavingSequenceControls}

        hiddenLegacySequenceRunCount={selectedJobHiddenSequenceRunCount}
        directDemoGuideHighlight={isDirectDemoHighlight(4)}

        onClose={handleCloseJobAts}

        onNotesChange={setJobNotesDraft}

        onSaveNotes={handleSaveJobNotes}

        onPushProspect={(prospect) => handlePushLeadToPipeline(prospect, 'external', selectedJobPanel)}

        onOpenPipeline={() => handleOpenJobPipelineView(selectedJobPanel)}

        onOpenScreening={handleOpenScreeningCandidate}

        onExportCsv={() => handleExportJobAtsCsv(selectedJobPanel, selectedJobAtsSnapshot)}

        onStartCandidateSequence={(candidate) => handleStartOrAdvanceSequence(candidate, 'candidate', selectedJobPanel)}

        onStartProspectSequence={(prospect) => handleStartOrAdvanceSequence(prospect, 'prospect', selectedJobPanel)}

        onCompleteSequenceTask={handleCompleteSequenceTask}

        onSendSequenceWhatsAppTask={handleSendSequenceWhatsAppTask}

        onSequenceTaskNoteChange={(taskId, value) => setSequenceTaskNotes((current) => ({ ...current, [taskId]: value }))}

        onSequenceControlsChange={(field, value) => setSequenceControls((current) => ({ ...current, [field]: value }))}

        onSaveSequenceControls={handleSaveSequenceControls}

        onClearSequenceSuppression={handleClearSequenceSuppression}

        onProcessSequenceRuns={handleProcessSequenceRuns}

        onOpenEeocEdit={(candidateId, currentData) => { setEeocEditCandidateId(candidateId); setEeocDraft(currentData || {}); }}

        accountTimeline={selectedJobAccountTimeline}

        isSavingTimelineEntry={isSavingTimelineEntry}

        onAddTimelineEntry={(type, message) => handleAddAccountTimelineEntry(selectedJobPanel, type, message)}

        companyContacts={selectedJobCompanyContacts}

        isSavingCompanyContact={isSavingCompanyContact}

        onAddContact={(data) => handleAddCompanyContact(getJobCompanyName(selectedJobPanel), data)}

        onUpdateContact={handleUpdateCompanyContact}

        onRemoveContact={handleRemoveCompanyContact}

        onLogContactActivity={(contact, type) => handleAddAccountTimelineEntry(selectedJobPanel, type, `${type === 'call' ? 'Called' : 'Emailed'} ${contact.name || 'contact'} (${contact.role || 'stakeholder'}) at ${getJobCompanyName(selectedJobPanel)}.`)}

        crmTasks={selectedJobCrmTasks}

        isSavingCrmTask={isSavingCrmTask}

        onAddCrmTask={(data) => handleAddCrmTask(selectedJobPanel, data)}

        onCompleteCrmTask={handleCompleteCrmTask}

        onSnoozeCrmTask={handleSnoozeCrmTask}

        onRemoveCrmTask={handleRemoveCrmTask}

        onScheduleMeeting={(contact) => handleOpenMeetingModal(contact, selectedJobPanel)}
        onSetJobStage={(newStage) => selectedJobPanel && handleSetJobStage(selectedJobPanel.id, newStage)}
        onToggleJobBlocked={(blocked, reason) => selectedJobPanel && handleToggleJobBlocked(selectedJobPanel.id, blocked, reason)}
        publishing={selectedJobPublishing}
        canPublish={canAccess('jobs.publish')}
        onUpdatePublishing={(updates, notice) => selectedJobPanel && handleUpdateJobPublishing(selectedJobPanel.id, updates, notice)}
        onRemoveJob={() => selectedJobPanel && setRemoveJobConfirmId(selectedJobPanel.id)}
        canRemoveJob={canAccess('jobs.delete')}

      />

      <ConfirmModal
        open={Boolean(removeJobConfirmId)}
        title="Remove role from portfolio?"
        message={(() => {
          const job = jobs.find((j) => j.id === removeJobConfirmId);
          const activeCandidateCount = candidates.filter((c) => c.matchedJobId === removeJobConfirmId && !['hired', 'rejected'].includes(String(c.status || '').toLowerCase())).length;
          return `${job?.title || 'This role'} will be hidden from your portfolio and excluded from all candidate matching. ${activeCandidateCount > 0 ? `${activeCandidateCount} active pipeline candidate${activeCandidateCount === 1 ? '' : 's'} will remain in their current stages — reassign them before removing if needed. ` : ''}You can restore it by filtering for Removed roles.`;
        })()}
        confirmLabel="Remove from portfolio"
        onConfirm={() => handleRemoveJob(removeJobConfirmId)}
        onClose={() => setRemoveJobConfirmId('')}
      />

      <ConfirmModal
        open={removeAllJobsConfirm}
        title="Remove all active roles from portfolio?"
        message={`This will mark all ${visibleJobs.filter((j) => !['removed','closed'].includes(String(j.status||'').toLowerCase())).length} active role${visibleJobs.filter((j) => !['removed','closed'].includes(String(j.status||'').toLowerCase())).length === 1 ? '' : 's'} as Removed and hide them from the portfolio. Candidates will remain in their current pipeline stages. You can restore individual roles by filtering for Removed.`}
        confirmLabel="Remove all roles"
        onConfirm={handleRemoveAllJobs}
        onClose={() => setRemoveAllJobsConfirm(false)}
      />

      <ScheduleMeetingModal
        isOpen={meetingModalState.open}
        companyName={meetingModalState.companyName}
        jobId={meetingModalState.jobId}
        prefillContactEmail={meetingModalState.prefillEmail}
        onSchedule={handleScheduleMeeting}
        isBusy={meetingModalState.busy}
        onClose={() => setMeetingModalState((s) => ({ ...s, open: false }))}
      />

      <ScreeningReviewModal

        candidate={selectedScreeningCandidate}

        response={selectedScreeningResponse}

        isSavingQuestions={isSavingScreeningQuestions}

        onRenewLink={handleRenewScreeningLink}

        onSaveQuestions={handleSaveScreeningQuestions}

        directDemoGuideHighlightAi={isDirectDemoTarget('screening-ai-suggestion-button')}

        onClose={() => setSelectedScreeningCandidateId(null)}

      />

      {selectedCandidateId && (() => {
        const _panelCandidate = candidates.find((c) => c.id === selectedCandidateId);
        if (!_panelCandidate) return null;
        const _panelCommercialProfile = candidateCommercialIndex[selectedCandidateId] || {};
        return (
          <CandidateFullPanel
            candidate={{ ..._panelCandidate, commercialProfile: _panelCommercialProfile }}
            jobs={jobs}
            stages={PIPELINE_STAGES}
            screeningResponse={screeningResponses[selectedCandidateId]}
            candidateIntelligence={_panelCandidate.matchedJobId ? scoreCandidateForJob(_panelCandidate, jobs.find((job) => job.id === _panelCandidate.matchedJobId) || { title: _panelCandidate.matchedJobTitle || _panelCandidate.role, requirements: '' }, screeningResponses[selectedCandidateId]) : null}
            noteDraft={candidateNoteDraft}
            onNoteDraftChange={setCandidateNoteDraft}
            isSavingNote={isSavingCandidateNote}
            onAddNote={handleAddCandidateNote}
            onStageChange={(cid, sid) => { handleTableStageChange(cid, sid); }}
            onDeleteCandidate={canAccess('pipeline.delete') ? handleDeleteCandidate : null}
            onReassignJob={canAccess('pipeline.write') ? handleReassignCandidateJob : null}
            onRequestJobMatches={handleRequestJobMatches}
            jobMatchResults={candidateJobMatchResults[selectedCandidateId] || null}
            onOpenScreening={handleOpenScreeningCandidate}
            onResolveCandidateResumeUrl={resolveCandidateResumeUrl}
            onClose={() => setSelectedCandidateId(null)}
          />
        );
      })()}

      {selectedAccountId && (() => {
        const _acctClient = clientPortfolio.find((c) => c.id === selectedAccountId);
        if (!_acctClient) return null;
        const _acctCompanyKey = normalizeText(_acctClient.companyName);
        const _acctGraphCompany = organizationalGraph.companies.find((c) => normalizeText(c.name) === _acctCompanyKey);
        const _acctOpps = clientCrmOpportunities.filter((o) => normalizeText(o.companyName || '') === _acctCompanyKey);
        const _acctTouchpoints = clientCrmTouchpoints.filter((t) => normalizeText(t.companyName || '') === _acctCompanyKey);
        return (
          <AccountPanel
            client={_acctClient}
            graphCompany={_acctGraphCompany}
            opportunities={_acctOpps}
            touchpoints={_acctTouchpoints}
            onLogEmail={(c) => handleLogClientTouchpoint(c, 'email')}
            onLogCall={(c) => handleLogClientTouchpoint(c, 'call')}
            onLogMeeting={(c) => handleLogClientTouchpoint(c, 'meeting')}
            onMoveStage={handleMoveClientOpportunityStage}
            onSetOwner={handleSetAccountOwner}
            onClose={() => setSelectedAccountId(null)}
          />
        );
      })()}

      {selectedContactId && (() => {
        const _contact = contacts.find((c) => c.id === selectedContactId);
        if (!_contact) return null;
        const _cKey = normalizeText(_contact.companyName || _contact.company || '');
        const _commercialRollup = contactCommercialRollups[selectedContactId] || buildContactCommercialRollup(_contact, {
          opportunities: clientCrmOpportunities,
          jobs,
          submissions,
          placements,
          touchpoints: clientCrmTouchpoints,
          activityLog,
          candidates,
        });
        const _contactOpps = _commercialRollup.opportunities.length ? _commercialRollup.opportunities : clientCrmOpportunities.filter((o) => normalizeText(o.companyName || '') === _cKey || o.contactEmail === _contact.email);
        const _contactJobs = _commercialRollup.jobs.length ? _commercialRollup.jobs : jobs.filter((j) => normalizeText(getJobCompanyName(j)) === _cKey);
        const _contactTouchpoints = _commercialRollup.touchpoints.length ? _commercialRollup.touchpoints : clientCrmTouchpoints.filter((t) => normalizeText(t.companyName || '') === _cKey);
        const _enrollment = crmCadenceEnrollments.find((e) => e.contactId === selectedContactId && e.status === 'active');
        return (
          <ContactPanel
            contact={_contact}
            touchpoints={_contactTouchpoints}
            opportunities={_contactOpps}
            jobs={_contactJobs}
            commercialRollup={_commercialRollup}
            activityLog={activityLog}
            cadenceEnrollment={_enrollment || null}
            cadenceTemplates={BUILTIN_CADENCES}
            onLogEmail={() => handleLogClientTouchpoint({ companyName: _contact.companyName || _contact.company, hiringContact: _contact.name }, 'email')}
            onLogCall={() => handleLogClientTouchpoint({ companyName: _contact.companyName || _contact.company, hiringContact: _contact.name }, 'call')}
            onLogMeeting={() => handleLogClientTouchpoint({ companyName: _contact.companyName || _contact.company, hiringContact: _contact.name }, 'meeting')}
            onLogWithNote={(c, type, note) => handleLogClientTouchpoint({ companyName: _contact.companyName || _contact.company, hiringContact: _contact.name }, type, note)}
            onSendCadenceEmail={handleSendCadenceEmail}
            onMarkReplied={handleMarkEmailReplied}
            emailTracking={crmEmailTracking.filter((t) => t.contactId === selectedContactId)}
            smtpConfigured={!!smtpSettings?.host}
            onSaveCustomField={handleSaveContactCustomField}
            onDeleteCustomField={handleDeleteContactCustomField}
            onEnrollCadence={handleEnrollContactInCadence}
            onAdvanceCadence={handleAdvanceCadenceEnrollment}
            onPauseCadence={handlePauseCadenceEnrollment}
            onUpdateContact={handleUpdateContact}
            onClose={() => setSelectedContactId(null)}
            onOpenSmtpSettings={() => setSmtpSettingsOpen(true)}
          />
        );
      })()}

      {showCrmAutomationPanel && (
        <CrmAutomationPanel
          rules={crmAutomationRules}
          cadenceEnrollments={crmCadenceEnrollments}
          pendingAutomations={pendingAutomations}
          emailTracking={crmEmailTracking}
          smtpConfigured={!!smtpSettings?.host}
          onOpenSmtpSettings={() => setSmtpSettingsOpen(true)}
          onSaveRule={handleSaveCrmAutomationRule}
          onDeleteRule={handleDeleteCrmAutomationRule}
          onToggleRule={handleToggleCrmAutomationRule}
          onDismissAutomation={handleDismissAutomation}
          onExecuteAutomation={handleExecuteAutomation}
          onClose={() => setShowCrmAutomationPanel(false)}
        />
      )}

      {/* ── SMTP / Outbound email settings modal ── */}
      {smtpSettingsOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/95 px-4">
          <div className="w-full max-w-lg rounded-shell border border-cyan-400/20 bg-slate-950 p-8 shadow-2xl">
            <div className="mb-1 text-micro font-black uppercase tracking-[0.2em] text-cyan-400">Outbound Email — SMTP Settings</div>
            <p className="mb-5 text-xs text-slate-500">Connect your email account so Vision can send cadence emails on your behalf. Use Gmail App Password, Outlook SMTP, or any custom mail server.</p>
            <div className="space-y-3">
              {[
                { key: 'host', label: 'SMTP Host', placeholder: 'smtp.gmail.com' },
                { key: 'port', label: 'Port', placeholder: '587' },
                { key: 'user', label: 'Username / Email', placeholder: 'you@gmail.com' },
                { key: 'pass', label: 'Password / App password', placeholder: '••••••••••••••••', type: 'password' },
                { key: 'fromName', label: 'Your display name', placeholder: 'Jane Smith' },
                { key: 'fromEmail', label: 'From email (if different)', placeholder: 'jane@agency.com' },
              ].map(({ key, label, placeholder, type }) => (
                <div key={key}>
                  <label className="mb-1 block text-micro font-black uppercase tracking-[0.12em] text-slate-500">{label}</label>
                  <input
                    type={type || 'text'}
                    value={smtpDraft[key]}
                    onChange={(e) => setSmtpDraft((d) => ({ ...d, [key]: e.target.value }))}
                    placeholder={placeholder}
                    autoComplete="off"
                    className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-2.5 text-sm text-white outline-none focus:border-cyan-400"
                  />
                </div>
              ))}
              <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
                <input type="checkbox" checked={smtpDraft.secure} onChange={(e) => setSmtpDraft((d) => ({ ...d, secure: e.target.checked }))} className="accent-cyan-400 h-4 w-4" />
                Use SSL/TLS (port 465)
              </label>
            </div>
            {smtpNotice && <p className={`mt-3 text-xs font-bold ${smtpNotice.startsWith('Failed') || smtpNotice.startsWith('Test failed') ? 'text-rose-400' : 'text-emerald-400'}`}>{smtpNotice}</p>}
            <div className="mt-5 flex gap-3 flex-wrap">
              <button disabled={smtpSaving} onClick={handleSaveSmtpSettings} className="flex-1 rounded-full bg-cyan-400 py-2.5 text-meta font-black text-slate-950 hover:bg-cyan-300 disabled:opacity-40 transition">
                {smtpSaving ? 'Saving…' : 'Save settings'}
              </button>
              <button disabled={smtpTestSending} onClick={handleTestSmtpSettings} className="rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-meta font-black text-slate-400 hover:text-white disabled:opacity-40 transition">
                {smtpTestSending ? 'Sending…' : 'Test email'}
              </button>
              <button onClick={() => { setSmtpSettingsOpen(false); setSmtpNotice(''); }} className="rounded-full border border-white/10 bg-white/5 px-5 py-2.5 text-meta font-black text-slate-500 hover:text-white transition">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {shortlistModalOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/95 px-4">
          <div className="w-full max-w-lg rounded-shell border border-cyan-400/20 bg-slate-950 p-8 shadow-2xl">
            <div className="text-micro font-black uppercase tracking-[0.2em] text-cyan-400 mb-2">Create Client Shortlist</div>
            {shortlistCreatedUrl ? (
              <div className="space-y-4">
                <p className="text-sm text-slate-300">Shortlist link created. Share it with your client:</p>
                <div className="rounded-ctl border border-cyan-400/30 bg-cyan-500/5 px-4 py-3 font-mono text-xs text-cyan-300 break-all select-all">{shortlistCreatedUrl}</div>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        await navigator.clipboard?.writeText(shortlistCreatedUrl);
                      } catch (_error) {}
                      setShortlistLinkCopied(true);
                      setPipelineNotice('Shortlist link copied. Close the shortlist window to continue.');
                    }}
                    className="flex-1 rounded-ctl bg-cyan-500 py-3 text-meta font-black uppercase tracking-[0.14em] text-slate-950 transition hover:bg-cyan-400"
                  >
                    {shortlistLinkCopied ? 'Copied' : 'Copy Link'}
                  </button>
                  <button
                    type="button"
                    disabled={isDemoWorkspace && directDemoWalkthroughOpen && !shortlistLinkCopied}
                    onClick={() => {
                      setShortlistModalOpen(false);
                      if (directDemoWalkthroughOpen && shortlistLinkCopied) markDirectDemoStepComplete(9);
                      if (importDemoWalkthroughOpen && shortlistLinkCopied) markImportDemoStepComplete(9);
                    }}
                    className="rounded-ctl border border-white/10 bg-white/5 px-5 py-3 text-meta font-black uppercase tracking-[0.14em] text-slate-400 transition hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Close
                  </button>
                </div>
                {isDemoWorkspace && directDemoWalkthroughOpen && !shortlistLinkCopied && (
                  <div className="rounded-ctl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs font-bold text-amber-100">Copy the shortlist link before closing this step.</div>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Client Name</label>
                  <input type="text" placeholder="e.g. Acme Corp" value={shortlistDraft.clientName} onChange={(e) => setShortlistDraft((d) => ({ ...d, clientName: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-cyan-500/50 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Role / Job Title</label>
                  <input type="text" placeholder="e.g. Senior Developer" value={shortlistDraft.jobTitle} onChange={(e) => setShortlistDraft((d) => ({ ...d, jobTitle: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-cyan-500/50 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Select Candidates ({shortlistDraft.candidateIds.length} selected)</label>
                  <div className="max-h-48 overflow-y-auto space-y-1 rounded-ctl border border-white/10 bg-slate-900/60 p-2">
                    {candidates.map((c) => (
                      <label key={c.id} className="flex items-center gap-2.5 rounded-ctl px-2 py-1.5 hover:bg-white/5 cursor-pointer">
                        <input type="checkbox" checked={shortlistDraft.candidateIds.includes(c.id)} onChange={(e) => setShortlistDraft((d) => ({ ...d, candidateIds: e.target.checked ? [...d.candidateIds, c.id] : d.candidateIds.filter((id) => id !== c.id) }))} className="h-3.5 w-3.5 accent-cyan-400" />
                        <span className="text-xs text-white truncate">{c.name}</span>
                        <span className="ml-auto text-micro text-slate-500 truncate shrink-0">{c.matchedJobTitle || c.role || ''}</span>
                      </label>
                    ))}
                    {candidates.length === 0 && <div className="py-3 text-center text-xs text-slate-500">No candidates in pipeline.</div>}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button type="button" onClick={() => handleCreateShortlist(shortlistDraft)} disabled={shortlistSaving || shortlistDraft.candidateIds.length === 0} className="flex-1 rounded-ctl bg-cyan-500 py-3 text-meta font-black uppercase tracking-[0.14em] text-slate-950 transition hover:bg-cyan-400 disabled:opacity-40">{shortlistSaving ? 'Creating…' : 'Create Shortlist'}</button>
                  <button type="button" onClick={() => setShortlistModalOpen(false)} className="rounded-ctl border border-white/10 bg-white/5 px-5 py-3 text-meta font-black uppercase tracking-[0.14em] text-slate-400 transition hover:text-white">Cancel</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {placementCandidateId && (() => {
        const _pc = candidates.find((c) => c.id === placementCandidateId);
        const calcFee = placementDraft.usePercent
          ? (Number(placementDraft.offerSalary || 0) > 0 && Number(placementDraft.feePercent || 0) > 0 ? Math.round(Number(placementDraft.offerSalary) * Number(placementDraft.feePercent) / 100) : 0)
          : Number(String(placementDraft.directFee).replace(/[^0-9.]/g, '')) || 0;
        const calcMargin = calcFee > 0 && Number(placementDraft.marginPercent || 0) > 0 ? Math.round(calcFee * Number(placementDraft.marginPercent) / 100) : 0;
        const calcTaxPercent = Number(String(placementDraft.taxPercent || '').replace(/[^0-9.]/g, '')) || 0;
        const calcTax = calcFee > 0 && calcTaxPercent > 0 ? Math.round(calcFee * calcTaxPercent / 100) : 0;
        const calcInvoiceTotal = calcFee + calcTax;
        const draftCurrency = placementDraft.currency || 'ZAR';
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/80 backdrop-blur-sm px-4">
            <div className="w-full max-w-xl rounded-shell border border-emerald-400/20 bg-slate-950 shadow-2xl flex flex-col max-h-[92vh]">
              <div className="px-8 pt-8 pb-4 border-b border-white/[0.06] shrink-0">
                <div className="text-micro font-black uppercase tracking-[0.2em] text-emerald-400 mb-2">Log Placement</div>
                <h2 className="text-xl font-black text-white mb-1">{_pc?.name || 'Candidate'}</h2>
                <p className="text-xs text-slate-500">Record placement details to track revenue, invoices, and commission.</p>
              </div>

              <div className="overflow-y-auto flex-1 px-8 py-6 space-y-5">
                {/* Fee mode toggle */}
                <div className="flex rounded-ctl border border-white/10 bg-slate-900 p-1 gap-1">
                  <button type="button" onClick={() => setPlacementDraft((d) => ({ ...d, usePercent: false }))} className={`flex-1 rounded-ctl py-2 text-micro font-black uppercase tracking-[0.14em] transition ${!placementDraft.usePercent ? 'bg-emerald-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}>Once-off Fee</button>
                  <button type="button" onClick={() => setPlacementDraft((d) => ({ ...d, usePercent: true }))} className={`flex-1 rounded-ctl py-2 text-micro font-black uppercase tracking-[0.14em] transition ${placementDraft.usePercent ? 'bg-emerald-500 text-slate-950' : 'text-slate-400 hover:text-white'}`}>Salary + %</button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Currency</label>
                    <select value={placementDraft.currency} onChange={(e) => setPlacementDraft((d) => ({ ...d, currency: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white focus:border-emerald-500/50 focus:outline-none">
                      {PLACEMENT_CURRENCIES.map((currency) => <option key={currency.code} value={currency.code}>{currency.code} - {currency.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Tax / VAT %</label>
                    <input type="number" placeholder="e.g. 15" value={placementDraft.taxPercent} onChange={(e) => setPlacementDraft((d) => ({ ...d, taxPercent: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" />
                  </div>
                </div>

                {/* Fee fields */}
                {!placementDraft.usePercent ? (
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Agency Fee</label>
                    <input type="number" placeholder="e.g. 75000" value={placementDraft.directFee} onChange={(e) => setPlacementDraft((d) => ({ ...d, directFee: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" autoFocus />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Annual Salary</label>
                      <input type="number" placeholder="e.g. 540000" value={placementDraft.offerSalary} onChange={(e) => setPlacementDraft((d) => ({ ...d, offerSalary: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" />
                    </div>
                    <div>
                      <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Agency Fee %</label>
                      <input type="number" placeholder="e.g. 15" value={placementDraft.feePercent} onChange={(e) => setPlacementDraft((d) => ({ ...d, feePercent: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" />
                    </div>
                  </div>
                )}

                {/* Fee preview */}
                {calcFee > 0 && (
                  <div className="rounded-ctl border border-emerald-400/20 bg-emerald-500/5 px-4 py-3 flex items-center gap-4 flex-wrap">
                    <div><div className="text-micro uppercase tracking-[0.16em] text-slate-500">Fee ex tax</div><div className="text-lg font-black text-emerald-300 mt-0.5">{formatMoney(calcFee, draftCurrency)}</div></div>
                    {calcTax > 0 && <div><div className="text-micro uppercase tracking-[0.16em] text-slate-500">Tax / VAT ({calcTaxPercent}%)</div><div className="text-lg font-black text-cyan-300 mt-0.5">{formatMoney(calcTax, draftCurrency)}</div></div>}
                    <div><div className="text-micro uppercase tracking-[0.16em] text-slate-500">Invoice Total</div><div className="text-lg font-black text-white mt-0.5">{formatMoney(calcInvoiceTotal, draftCurrency)}</div></div>
                    {calcMargin > 0 && <div><div className="text-micro uppercase tracking-[0.16em] text-slate-500">Margin ({placementDraft.marginPercent}%)</div><div className="text-lg font-black text-violet-300 mt-0.5">{formatMoney(calcMargin, draftCurrency)}</div></div>}
                  </div>
                )}

                {/* Margin + Commission */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Margin %</label>
                    <input type="number" placeholder="e.g. 25" value={placementDraft.marginPercent} onChange={(e) => setPlacementDraft((d) => ({ ...d, marginPercent: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" />
                    <div className="mt-1 text-micro text-slate-600">Agency gross margin on the fee</div>
                  </div>
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Commission Split %</label>
                    <input type="number" placeholder="e.g. 20" value={placementDraft.commissionSplit} onChange={(e) => setPlacementDraft((d) => ({ ...d, commissionSplit: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" />
                    <div className="mt-1 text-micro text-slate-600">Recruiter's share of the fee</div>
                  </div>
                </div>

                {/* Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Start Date</label>
                    <input type="date" value={placementDraft.startDate} onChange={(e) => setPlacementDraft((d) => ({ ...d, startDate: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white focus:border-emerald-500/50 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Invoice Date</label>
                    <input type="date" value={placementDraft.invoiceDate || new Date().toISOString().slice(0, 10)} onChange={(e) => setPlacementDraft((d) => ({ ...d, invoiceDate: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white focus:border-emerald-500/50 focus:outline-none" />
                  </div>
                </div>

                {/* Invoice details */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Invoice Number</label>
                    <input type="text" placeholder="e.g. INV-0042" value={placementDraft.invoiceNumber} onChange={(e) => setPlacementDraft((d) => ({ ...d, invoiceNumber: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" />
                  </div>
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Payment terms (days)</label>
                    <input type="number" placeholder="30" value={placementDraft.invoiceDueDays} onChange={(e) => setPlacementDraft((d) => ({ ...d, invoiceDueDays: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" />
                  </div>
                </div>

                {/* Invoice + Payment status */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Invoice Status</label>
                    <select value={placementDraft.invoiceStatus} onChange={(e) => setPlacementDraft((d) => ({ ...d, invoiceStatus: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white focus:border-emerald-500/50 focus:outline-none">
                      <option value="pending">Draft</option>
                      <option value="sent">Sent to client</option>
                      <option value="disputed">Disputed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Payment Status</label>
                    <select value={placementDraft.paymentStatus} onChange={(e) => setPlacementDraft((d) => ({ ...d, paymentStatus: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white focus:border-emerald-500/50 focus:outline-none">
                      <option value="unpaid">Unpaid</option>
                      <option value="partial">Partially paid</option>
                      <option value="paid">Paid</option>
                      <option value="written_off">Written off</option>
                    </select>
                  </div>
                </div>

                {/* Billed by */}
                <div>
                  <label className="block text-micro font-black uppercase tracking-[0.16em] text-slate-500 mb-1.5">Billed by (recruiter)</label>
                  <input type="text" placeholder={profile?.name || 'Recruiter name'} value={placementDraft.billedBy} onChange={(e) => setPlacementDraft((d) => ({ ...d, billedBy: e.target.value }))} className="w-full rounded-ctl border border-white/10 bg-slate-900 px-4 py-3 text-sm text-white placeholder-slate-600 focus:border-emerald-500/50 focus:outline-none" />
                </div>
              </div>

              <div className="flex gap-3 px-8 py-5 border-t border-white/[0.06] shrink-0">
                <button type="button" onClick={() => handleSavePlacement(placementCandidateId, placementDraft)} disabled={isSavingPlacement} className="flex-1 rounded-ctl bg-emerald-500 px-4 py-3 text-meta font-black uppercase tracking-[0.14em] text-slate-950 transition hover:bg-emerald-400 disabled:opacity-40">
                  {isSavingPlacement ? 'Saving…' : 'Save Placement'}
                </button>
                <button type="button" onClick={() => setPlacementCandidateId(null)} className="rounded-ctl border border-white/10 bg-white/5 px-4 py-3 text-meta font-black uppercase tracking-[0.14em] text-slate-400 transition hover:text-white">
                  Skip
                </button>
              </div>
            </div>
          </div>
        );
      })()}

    </>

  );

}
